import { useState, useRef, useEffect, useCallback, useMemo, Fragment } from "react";
import { jsPDF } from "jspdf";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TutorialPage from "./TutorialPage";

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const IcoDashboard = () => <Icon d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />;
const IcoMap       = () => <Icon d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />;
const IcoList      = () => <Icon d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 0 2-2h2a2 2 0 0 0 2 2" />;
const IcoUsers     = () => <Icon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />;
const IcoTruck = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:"scaleX(-1)"}}>
    <circle cx="5" cy="17" r="2" />
    <circle cx="18" cy="17" r="2" />
    <path d="M2 17h1" />
    <path d="M7 17h9" />
    <path d="M20 17h2" />
    <path d="M8 17V9H4L2 12v5" />
    <path d="M2 12h6" />
    <path d="M11 9h10l-2 7h-6z" />
    <path d="M9 16l5-11h3" />
    <path d="M16 5v6" />
    <path d="M15 11h2" />
  </svg>
);
const IcoChart     = () => <Icon d="M18 20V10M12 20V4M6 20v-6" />;
const IcoBot       = () => <Icon d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zM9 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM15 14a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />;
const IcoPlus      = () => <Icon d="M12 5v14M5 12h14" />;
const IcoSearch    = () => <Icon d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />;
const IcoBell      = () => <Icon d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />;
const IcoChevron   = () => <Icon d="M9 18l6-6-6-6" />;
const IcoX         = () => <Icon d="M18 6L6 18M6 6l12 12" />;
const IcoEdit      = () => <Icon d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoEye       = () => <Icon d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />;
const IcoDoc       = () => <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8" />;
const IcoCheck     = () => <Icon d="M20 6L9 17l-5-5" />;
const IcoCar = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{transform:"scaleX(-1)"}}>
    <circle cx="5" cy="17" r="2" />
    <circle cx="18" cy="17" r="2" />
    <path d="M2 17h1" />
    <path d="M7 17h9" />
    <path d="M20 17h2" />
    <path d="M8 17V9H4L2 12v5" />
    <path d="M2 12h6" />
    <path d="M11 9h10l-2 7h-6z" />
    <path d="M9 16l5-11h3" />
    <path d="M16 5v6" />
    <path d="M15 11h2" />
  </svg>
);
const IcoDriver    = () => <Icon d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM6 21v-2a6 6 0 0 1 12 0v2" />;
const IcoContainer = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8h18l-2 13H5L3 8z" />
    <path d="M1 8l3-5h16l3 5" />
    <line x1="9" y1="12" x2="8.5" y2="18" />
    <line x1="15" y1="12" x2="15.5" y2="18" />
  </svg>
);
const IcoGear      = () => <Icon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />;
const IcoWrench    = () => <Icon d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />;
const IcoFuel      = () => <Icon d="M3 22V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v17M3 22h10M13 10h2a2 2 0 0 1 2 2v3a2 2 0 0 1 2 2v0a2 2 0 0 0 2-2V9l-3-3" />;
const IcoComercial = () => <Icon d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />;
const IcoCalculator = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="8" y2="10.01" /><line x1="12" y1="10" x2="12" y2="10.01" /><line x1="16" y1="10" x2="16" y2="10.01" /><line x1="8" y1="14" x2="8" y2="14.01" /><line x1="12" y1="14" x2="12" y2="14.01" /><line x1="16" y1="14" x2="16" y2="14.01" /><line x1="8" y1="18" x2="16" y2="18" />
  </svg>
);
const IcoFinanceiro = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);
const IcoTutorial = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    <path d="M12 7v14" />
    <circle cx="18" cy="5.5" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

// ─── Segoe UI font constant ───────────────────────────────────────────────────
const FONT = "'Segoe UI', 'Segoe UI Variable', system-ui, -apple-system, sans-serif";
const MAPBOX_TOKEN = "pk.eyJ1IjoidGhpYWdvZjgwIiwiYSI6ImNtbWZmbnc1MzA4M2MycG9yYXE5cW56MnEifQ.4-pE0tuRHE2Ho7U3XNfuoQ";
// Normalizar texto removendo acentos para busca
const norm = (s) => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase();

// Helper: download autenticado (abre em nova aba)
const downloadAuth = async (url, filename) => {
  const API = process.env.REACT_APP_API || "http://localhost:3001";
  const token = sessionStorage.getItem("controla_token");
  try {
    const res = await fetch(API + url, { headers: { Authorization: "Bearer " + token } });
    if (!res.ok) { alert("Erro ao baixar arquivo"); return; }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = blobUrl; a.download = filename || "arquivo"; a.target = "_blank";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  } catch(e) { alert("Erro: " + e.message); }
};

// Helper: data de hoje no timezone local (evita bug UTC do toISOString)
const hojeLocal = () => { const n=new Date(); return n.getFullYear()+"-"+String(n.getMonth()+1).padStart(2,"0")+"-"+String(n.getDate()).padStart(2,"0"); };

// Diária extra por atraso: retorna { dias, diaria, total } se a locação está atrasada
// (retirada prevista < hoje, status ativo) e tem diaria_extra > 0; senão null.
// opts.incluirCobranca: considera também "Para Cobrar" (atraso contado até a retirada real).
const calcDiariaExtraAtraso = (l, opts) => {
  if (!l) return null;
  const diaria = parseFloat(l.diariaExtra != null ? l.diariaExtra : l.diaria_extra) || 0;
  if (diaria <= 0) return null;
  const statusOk = (opts && opts.incluirCobranca)
    ? ["Colocada", "Para Retirar", "Para Trocar", "Para Cobrar"].includes(l.status)
    : ["Colocada", "Para Retirar", "Para Trocar"].includes(l.status);
  if (!statusOk) return null;
  const dRet = String(l.dataRetiraPrevista || l.data_retirada || l.dataRetirada || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dRet)) return null;
  let fim = hojeLocal();
  if (l.status === "Para Cobrar") {
    const real = String(l.dataRetiradaReal || l.data_retirada_real || "").slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(real)) fim = real;
  }
  if (dRet >= fim) return null;
  const dias = Math.round((Date.parse(fim) - Date.parse(dRet)) / 86400000);
  if (dias <= 0) return null;
  return { dias, diaria, total: dias * diaria };
};

// Ordenação padrão das listagens: por data de criação, MAIS RECENTES primeiro.
// Comparador TOTALMENTE TRANSITIVO: chave primária = created_at (ISO normalizado; ausência
// vira "" e cai pro fim), desempate determinístico pelo id — que é monotônico com a criação
// em todas as entidades (int auto-increment; locações usam id "YYYYMMDDNNNN").
const ordCriacaoDesc = (a, b) => {
  const ak = String((a && a.created_at) || ""), bk = String((b && b.created_at) || "");
  if (ak !== bk) return bk.localeCompare(ak); // desc por created_at
  const ai = a && a.id, bi = b && b.id;       // empate → desempata pelo id
  if (typeof ai === "number" && typeof bi === "number") return bi - ai;
  return String(bi == null ? "" : bi).localeCompare(String(ai == null ? "" : ai), undefined, { numeric: true });
};

// Helper: derivar operação real a partir do status atual da locação
// Ex: locação criada como ENTREGA, mas agora está "Para Retirar" → operação real = RETIRADA
const getOperacao = (loc) => {
  if (!loc) return "ENTREGA";
  const st = loc.status || "";
  if (st === "Para Retirar" || st === "Encerrada" || st === "Para Cobrar") return "RETIRADA";
  if (st === "Para Trocar") return "TROCA";
  // Filha de troca: obs contém "troca da locação #" → é TROCA mesmo que status seja "Para Colocar"
  const obs = loc.obs || loc.observacoes || "";
  if (typeof obs === "string" && (obs.toLowerCase().includes("troca da locação #") || obs.toLowerCase().includes("troca da locacao #"))) return "TROCA";
  if (loc.operacao === "TROCA") return "TROCA";
  return loc.operacao || "ENTREGA";
};

// ─── Global Animations ────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @keyframes fadeSlideUp {
      from { opacity: 0; transform: translateY(22px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0)    scale(1); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes toastIn {
      from { opacity: 0; transform: translateX(80px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes toastOut {
      from { opacity: 1; transform: translateX(0); }
      to   { opacity: 0; transform: translateX(80px); }
    }
    @keyframes countUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes pageEnter {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .modal-overlay { animation: fadeIn 0.18s ease; }
    .modal-box     { animation: fadeSlideUp 0.22s cubic-bezier(.22,1,.36,1); }
    .toast-enter   { animation: toastIn 0.28s cubic-bezier(.22,1,.36,1) forwards; }
    .toast-exit    { animation: toastOut 0.28s ease forwards; }
    .kpi-count     { animation: countUp 0.5s ease; }
    .page-content  { animation: pageEnter 0.3s cubic-bezier(.22,1,.36,1); }
    * { box-sizing: border-box; }
    button { transition: opacity .15s, transform .1s, box-shadow .15s; }
    button:hover { opacity: .88; }
    button:active { transform: scale(0.97); }
    @keyframes loginIn {
      from { opacity:0; transform:translateY(32px) scale(0.96); }
      to   { opacity:1; transform:translateY(0) scale(1); }
    }
    @keyframes bgFloat {
      0%   { transform:translateY(0px) rotate(0deg); }
      50%  { transform:translateY(-18px) rotate(3deg); }
      100% { transform:translateY(0px) rotate(0deg); }
    }
    @keyframes bgFloat2 {
      0%   { transform:translateY(0px) rotate(0deg); }
      50%  { transform:translateY(14px) rotate(-4deg); }
      100% { transform:translateY(0px) rotate(0deg); }
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-12px); }
    }
    @keyframes aiPulse {
      0%   { box-shadow: 0 0 0 0 rgba(139,92,246,.5); }
      70%  { box-shadow: 0 0 0 8px rgba(139,92,246,0); }
      100% { box-shadow: 0 0 0 0 rgba(139,92,246,0); }
    }
    .ai-pulse-btn { animation: aiPulse 2s infinite; }
    .ai-pulse-btn:hover { animation: none; transform: scale(1.05); }
    .login-box  { animation: loginIn 0.5s cubic-bezier(.22,1,.36,1); }
    .float1     { animation: bgFloat  7s ease-in-out infinite; }
    .float2     { animation: bgFloat2 9s ease-in-out infinite; }
    .float3     { animation: bgFloat  11s ease-in-out infinite 1s; }
    input[type="date"]::-webkit-calendar-picker-indicator { cursor: pointer; filter: invert(30%) sepia(90%) saturate(1500%) hue-rotate(200deg) brightness(95%); }
    input[type="date"] { accent-color: #1aae9e; color-scheme: light; }
    select { accent-color: #1aae9e; }
    input[type="checkbox"] { accent-color: #1aae9e; }
    input:focus { outline:none; border-color:#1aae9e !important; box-shadow:0 0 0 3px rgba(26,174,158,.12); }
    .tbl-loc td { border-right: 1px solid #f0f0f0; }
    .tbl-loc td:last-child { border-right: none; }
    .tbl-loc th { border-right: 1px solid #f0f0f0; }
    .tbl-loc th:last-child { border-right: none; }
    .tbl-loc th:first-child { border-right: none; }
    /* ═══════════════════════════════════════════════════════════════════
       MOBILE — visual de aplicativo (≤768px)
       Regras baseadas em CLASSES estáveis (modal-head/body/foot, loc-cards,
       section-head, app-field, data-label). Evitar nth-child sempre que
       possível — seletor posicional quebra quando colunas mudam.
       Desktop (≥769px) permanece 100% intocado.
       ═══════════════════════════════════════════════════════════════════ */
    @media (max-width: 768px) {
      html, body { -webkit-tap-highlight-color: transparent; }
      body { overscroll-behavior-y: contain; }

      /* ── Shell: sidebar vira drawer nativo ── */
      .desktop-sidebar { display: none !important; }
      .desktop-sidebar.mobile-open { display: flex !important; position: fixed !important; top: 0; left: 0; bottom: 0; z-index: 9990; width: 80vw !important; max-width: 300px; box-shadow: 4px 0 28px rgba(0,0,0,.4); border-radius: 0 18px 18px 0; padding-bottom: env(safe-area-inset-bottom); animation: drawerIn .22s ease; overflow-y: auto; }
      @keyframes drawerIn { from { transform: translateX(-35%); opacity: .4; } to { transform: none; opacity: 1; } }
      .mobile-overlay { display: block !important; position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 9989; -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); }
      .mobile-hamburger { display: flex !important; min-width: 42px; min-height: 42px; }
      .desktop-topbar-btns { display: none !important; }

      /* ── Topbar ── */
      .app-topbar { padding: 0 10px !important; height: 54px !important; }
      .app-topbar .topbar-user-name { display: none !important; }
      .ai-pulse-btn { font-size: 11px !important; padding: 6px 10px !important; border-radius: 16px !important; }
      .ai-pulse-btn span { display: none; }

      /* ── Área de conteúdo ── */
      .app-content { padding: 12px 12px calc(20px + env(safe-area-inset-bottom)) 12px !important; }
      .page-content h1 { font-size: 18px !important; }
      .page-content > div:first-child p { font-size: 12px !important; }
      .app-content > .page-content { overflow-x: hidden; }
      .app-content > .page-content > div { max-width: 100%; }
      .boleto-banner { flex-direction: column !important; padding: 8px 12px !important; gap: 4px !important; text-align: center; }

      /* ── Toque: inputs ≥16px (iPhone não dá zoom), alvos confortáveis ── */
      input, select, textarea { font-size: 16px !important; }
      input:not([type="checkbox"]):not([type="radio"]), select { min-height: 44px; }
      textarea { min-height: 72px; }
      input[type="checkbox"], input[type="radio"] { width: 20px; height: 20px; }

      /* ── Login estilo app: tela cheia ── */
      .login-box { margin: 0 !important; max-width: 100% !important; width: 100%; min-height: 100vh; min-height: 100dvh; border-radius: 0 !important; display: flex; flex-direction: column; justify-content: center; box-sizing: border-box; padding-top: env(safe-area-inset-top); padding-bottom: env(safe-area-inset-bottom); }
      .login-box > div { padding-left: 26px !important; padding-right: 26px !important; }
      .login-box img { width: 200px !important; }
      .login-box input { border-radius: 12px !important; }
      .login-box button { min-height: 48px; border-radius: 12px !important; }

      /* ── Modais = bottom sheet (estilo app) ── */
      .modal-overlay { padding: 0 !important; align-items: flex-end !important; }
      .modal-overlay > .modal-box { width: 100% !important; max-width: 100vw !important; margin: 0 !important; border-radius: 18px 18px 0 0 !important; max-height: 92vh !important; max-height: 92dvh !important; height: auto !important; min-height: 42vh; animation: sheetUp .24s ease; }
      @keyframes sheetUp { from { transform: translateY(48px); opacity: .5; } to { transform: none; opacity: 1; } }
      .modal-overlay > .modal-box::before { content: ""; display: block; width: 42px; height: 4px; border-radius: 2px; background: #ddd; margin: 8px auto 0; flex-shrink: 0; }
      .modal-head { padding: 8px 16px 10px !important; }
      .modal-head > div > div:first-child { font-size: 15px !important; }
      .modal-body { padding: 0 14px 14px !important; }
      .modal-foot, .modal-footer-btns { padding: 10px 14px calc(10px + env(safe-area-inset-bottom)) !important; }
      .modal-foot > button, .modal-footer-btns > button { flex: 1; min-height: 46px; font-size: 14px !important; justify-content: center !important; border-radius: 10px !important; }

      /* Detalhe da locação: no bottom-sheet mobile a altura volta a ser flexível */
      .locdetail-body { height: auto !important; flex: 1 1 auto !important; min-height: 0 !important; }

      /* Formulários dentro de modal: grids empilham, campos no dedo */
      .modal-box div[style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
      .modal-box input, .modal-box select, .modal-box textarea { padding: 10px 12px !important; box-sizing: border-box; border-radius: 10px !important; }
      .modal-box label { font-size: 11px !important; }
      .section-head { margin-top: 14px !important; border-radius: 12px 12px 0 0 !important; }
      .section-body { padding: 14px 12px !important; border-radius: 0 0 12px 12px !important; }
      .app-field label { font-size: 11px !important; }

      /* ── LOCAÇÕES: tabela vira cards (estilo app) ── */
      .tbl-wrap:has(.loc-cards) { background: transparent !important; border: none !important; overflow: visible !important; border-radius: 0 !important; }
      .tbl-wrap:has(.loc-cards) > div { background: #fff; border: 1px solid #ececec; border-radius: 12px; }
      .loc-cards { min-width: 0 !important; }
      .loc-cards, .loc-cards tbody { display: block; }
      .loc-cards thead { display: none; }
      .loc-cards tbody tr { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 2px 12px; position: relative; padding: 12px 14px 10px !important; margin: 0 0 10px; background: #fff; border: 1px solid #ececec !important; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
      .loc-cards td { display: block; border: none !important; padding: 2px 0 !important; font-size: 13px !important; }
      .loc-cards td:first-child { order: 0; padding-right: 2px !important; }
      .loc-cards td:nth-child(2) { order: 0; }
      .loc-cards td:nth-child(3) { order: 1; width: 100%; max-width: none !important; padding-right: 34px !important; }
      .loc-cards td:nth-child(4) { order: 2; width: 100%; text-align: left !important; background: #f7faf9; border-radius: 10px; padding: 7px 10px !important; margin-top: 4px; }
      .loc-cards td[data-label] { order: 3; width: calc(50% - 8px); display: flex; flex-direction: column; margin-top: 6px; }
      .loc-cards td[data-label]::before { content: attr(data-label); font-size: 9px; font-weight: 800; color: #9aa0a6; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 1px; }
      .loc-cards td:last-child { position: absolute; top: 10px; right: 8px; padding: 0 !important; }
      .loc-cards td[colspan] { position: static; width: 100%; order: 0; }
      .loc-cards tbody tr:has(td[colspan]) { display: block; }
      .loc-cards td:nth-child(3) > div:first-child { font-size: 14px !important; }
      .loc-cards td[data-label="Preço Total"] { font-size: 15px !important; color: #0d9488; }
      .loc-cards td.loc-col-extra { white-space: normal !important; max-width: none !important; overflow: visible !important; }

      /* ── Tabelas → cards genéricos (Clientes, Caçambas, Tipos, Motoristas, Itinerários, Financeiro) ── */
      .tbl-wrap:has(.mob-cards) { background: transparent !important; border: none !important; overflow: visible !important; }
      .tbl-wrap:has(.mob-cards) > div { background: #fff; border: 1px solid #ececec; border-radius: 12px; }
      .mob-cards { min-width: 0 !important; }
      .mob-cards, .mob-cards tbody { display: block; }
      .mob-cards thead { display: none !important; }
      .mob-cards tbody tr { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 2px 12px; position: relative; padding: 12px 14px !important; margin: 0 0 10px; background: #fff !important; border: 1px solid #ececec !important; border-radius: 14px; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
      .mob-cards td { display: block; border: none !important; padding: 2px 0 !important; font-size: 13px !important; max-width: none !important; white-space: normal !important; text-align: left !important; }
      .mob-cards .mob-title { order: -2; width: calc(100% - 54px); font-size: 14px !important; }
      .mob-cards .mob-thumb { position: absolute; top: 10px; right: 12px; width: auto; padding: 0 !important; }
      .mob-cards .mob-hide { display: none !important; }
      .mob-cards td[data-label] { display: flex; flex-direction: column; width: calc(50% - 8px); margin-top: 6px; }
      .mob-cards td[data-label]::before { content: attr(data-label); font-size: 9px; font-weight: 800; color: #9aa0a6; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 1px; }
      .mob-cards td[data-label="Endereço"], .mob-cards td[data-label="Observação"], .mob-cards td[data-label="Cliente Atual"], .mob-cards td[data-label="Descrição"], .mob-cards td[data-label="Categoria"], .mob-cards td[data-label="Progresso"] { width: 100%; }
      .mob-cards .mob-actions { width: 100% !important; margin-top: 10px; }
      .mob-cards .mob-actions > div { display: flex !important; gap: 8px !important; }
      .mob-cards .mob-actions button { flex: 1; min-height: 40px; border-radius: 10px !important; justify-content: center !important; font-size: 13px !important; }
      .mob-cards td[colspan] { position: static; width: 100%; }
      .mob-cards tbody tr:has(td[colspan]) { display: block; }

      /* ── Plano de Contas: botões não estouram a linha ── */
      .fincat-row { flex-wrap: wrap !important; row-gap: 8px; }
      .fincat-row > div { flex: 1 1 100% !important; }
      .fincat-row button { flex: 1 1 calc(33% - 8px); min-height: 38px; border-radius: 8px !important; }
      .finsub-row { flex-wrap: wrap !important; padding-left: 24px !important; row-gap: 6px; }
      .finsub-row > div { flex: 1 1 100% !important; }
      .finsub-row button { flex: 1 1 calc(50% - 8px); min-height: 36px; border-radius: 8px !important; }

      /* ── Demais tabelas .tbl-loc (relatórios): rolagem suave ── */
      .tbl-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; border-radius: 12px; }
      .tbl-loc:not(.loc-cards) { font-size: 11px !important; min-width: 680px; }
      .tbl-loc:not(.loc-cards) th { font-size: 10px !important; padding: 8px 6px !important; white-space: nowrap; }
      .tbl-loc:not(.loc-cards) td { font-size: 11px !important; padding: 8px 6px !important; }

      /* Tabelas genéricas das páginas: densidade reduzida */
      .page-content table:not(.loc-cards) { font-size: 12px !important; }
      .page-content table:not(.loc-cards) th, .page-content table:not(.loc-cards) td { padding: 8px 7px !important; }

      /* ── Tabs de status: pills com scroll lateral ── */
      .status-tabs-row { gap: 0 !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; flex-wrap: nowrap !important; padding: 0 12px 8px; margin: 0 -12px; scrollbar-width: none; }
      .status-tabs-row::-webkit-scrollbar { display: none; }
      .status-tabs-row button { font-size: 12px !important; padding: 8px 14px !important; white-space: nowrap; flex-shrink: 0; min-height: 38px; border-radius: 18px !important; }

      /* ── Barras de ação e filtros: empilhados, botões cheios ── */
      .page-action-bar { flex-direction: column !important; gap: 8px !important; align-items: stretch !important; }
      .page-action-bar > *, .page-action-bar > div { width: 100% !important; }
      .page-action-bar button { width: 100% !important; justify-content: center !important; min-height: 44px; border-radius: 10px !important; }
      .page-action-bar input { width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; border-radius: 10px !important; }
      .page-action-bar > div > div[style*="display"] { flex-direction: column !important; width: 100% !important; }
      .page-action-bar > div > div[style*="display"] > * { width: 100% !important; }
      .filter-row { flex-direction: column !important; gap: 8px !important; align-items: stretch !important; }
      .filter-row > *, .filter-row > div { width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
      .filter-row select, .filter-row input { width: 100% !important; min-height: 44px; box-sizing: border-box !important; border-radius: 10px !important; }
      .filter-row > div[style*="marginLeft"] { margin-left: 0 !important; width: 100% !important; }
      .filter-row > div[style*="marginLeft"] > div { overflow-x: auto !important; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; }
      .filter-row > div[style*="marginLeft"] button { flex-shrink: 0 !important; white-space: nowrap !important; font-size: 11px !important; padding: 7px 12px !important; }

      /* ── Grids inline das páginas (3 col → 1, 4 col → 2) ── */
      .page-content div[style*="grid-template-columns: 1fr 1fr 1fr"],
      .page-content div[style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
      .page-content div[style*="grid-template-columns: 1fr 1fr 1fr 1fr"],
      .page-content div[style*="grid-template-columns: repeat(4"] { grid-template-columns: 1fr 1fr !important; }

      /* ── Dashboard ── */
      .dash-cards-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
      .dash-cards-grid > div { padding: 12px 10px !important; border-radius: 12px !important; }
      .dash-cards-grid > div > div:nth-child(2) { font-size: 20px !important; }
      .dash-cards-row3 { grid-template-columns: 1fr 1fr 1fr !important; gap: 8px !important; }
      .dash-cards-row3 > div { padding: 12px 10px !important; border-radius: 12px !important; }
      .dash-cards-row3 > div > div:nth-child(2) { font-size: 20px !important; }
      .dash-finance-cards { grid-template-columns: 1fr !important; }
      .dash-finance-cards > div { padding: 16px 18px !important; border-radius: 14px !important; }
      .dash-finance-cards > div > div:nth-child(2) { font-size: 28px !important; line-height: 1 !important; }
      .dash-operations { grid-template-columns: 1fr !important; gap: 8px !important; }
      .dash-operations > div { padding: 14px !important; border-radius: 12px !important; }
      .dash-chart-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
      .dash-chart-grid > div { padding: 14px !important; border-radius: 12px !important; }
      .dash-chart-grid > div > div[style*="display"] { flex-direction: column !important; align-items: center !important; }
      .dash-chart-grid > div > div[style*="display"] > div:first-child { margin-bottom: 12px; }
      .dash-faturamento-chart { padding: 14px !important; max-height: 240px !important; overflow: hidden !important; border-radius: 12px !important; }
      .dashboard-faturamento-bars { max-height: 130px !important; height: 130px !important; overflow: hidden !important; }
      .dash-ultimas-loc-table { font-size: 10px !important; min-width: auto !important; }
      .dash-ultimas-loc-table thead { display: none !important; }
      .dash-ultimas-loc-table tbody { display: block !important; }
      .dash-ultimas-loc-table tbody tr { display: flex !important; flex-wrap: wrap !important; padding: 8px !important; border-bottom: 1px solid #f0f0f0 !important; gap: 4px; }
      .dash-ultimas-loc-table tbody td { display: inline-block !important; padding: 0 !important; border: none !important; font-size: 11px !important; }
      .dash-ultimas-loc-table tbody td:nth-child(1) { width: auto; font-weight: 600 !important; margin-right: 6px; }
      .dash-ultimas-loc-table tbody td:nth-child(2) { width: calc(100% - 60px); }
      .dash-ultimas-loc-table tbody td:nth-child(3), .dash-ultimas-loc-table tbody td:nth-child(4), .dash-ultimas-loc-table tbody td:nth-child(5), .dash-ultimas-loc-table tbody td:nth-child(6) { display: none !important; }

      /* ── KPI cards (Caçambas, Frota, Manutenção, Combustível…) ── */
      .kpi-cards-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 8px !important; }
      .kpi-cards-grid > div { padding: 12px 12px !important; border-radius: 12px !important; }

      /* ── Relatórios ── */
      .relatorio-summary-cards { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
      .relatorio-summary-cards > div { padding: 12px 10px !important; border-radius: 12px !important; }
      .relatorio-period-btns { overflow-x: auto !important; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; padding-bottom: 4px; gap: 4px !important; }
      .relatorio-period-btns button { flex-shrink: 0 !important; white-space: nowrap !important; font-size: 11px !important; padding: 7px 12px !important; border-radius: 16px !important; }
      .relatorio-chart-container { max-height: 240px !important; overflow: hidden !important; padding: 14px !important; }
      .relatorio-chart-bars { max-height: 130px !important; height: 130px !important; overflow: hidden !important; }
      .relatorio-top-clientes-tbl { font-size: 10px !important; }
      .relatorio-top-clientes-tbl thead { display: none !important; }
      .relatorio-top-clientes-tbl tbody { display: block !important; }
      .relatorio-top-clientes-tbl tbody tr { display: flex !important; flex-wrap: wrap !important; padding: 8px !important; border-bottom: 1px solid #f0f0f0 !important; gap: 4px; }
      .relatorio-top-clientes-tbl tbody td { display: inline-block !important; padding: 0 !important; border: none !important; font-size: 11px !important; }
      .relatorio-top-clientes-tbl tbody td:nth-child(1) { width: 100%; margin-bottom: 2px; font-weight: 600 !important; }
      .relatorio-top-clientes-tbl tbody td:nth-child(2) { width: calc(50% - 2px); }
      .relatorio-top-clientes-tbl tbody td:nth-child(3) { width: calc(50% - 2px); }
      .relatorio-top-clientes-tbl tbody td:nth-child(4), .relatorio-top-clientes-tbl tbody td:nth-child(5), .relatorio-top-clientes-tbl tbody td:nth-child(6) { display: none !important; }
      .relatorio-pagos-table { font-size: 10px !important; }
      .relatorio-pagos-table thead { display: none !important; }
      .relatorio-pagos-table tbody tr { display: block !important; padding: 8px !important; border-bottom: 1px solid #f0f0f0 !important; margin-bottom: 4px; }
      .relatorio-pagos-table tbody td { display: inline-block !important; padding: 2px 4px !important; border: none !important; font-size: 11px !important; }
      .relatorio-pagos-table tbody td:nth-child(1) { width: auto; margin-right: 6px; font-weight: 600 !important; }
      .relatorio-pagos-table tbody td:nth-child(2) { width: calc(100% - 100px); }
      .relatorio-pagos-table tbody td:nth-child(3), .relatorio-pagos-table tbody td:nth-child(4), .relatorio-pagos-table tbody td:nth-child(5), .relatorio-pagos-table tbody td:nth-child(6) { display: none !important; }

      /* ── Seletor de cliente (modal) ── */
      .client-selector-tbl thead { display: none !important; }
      .client-selector-tbl tbody tr { display: flex !important; flex-wrap: wrap !important; padding: 12px 14px !important; border-bottom: 1px solid #f0f0f0 !important; gap: 4px; align-items: center; position: relative !important; }
      .client-selector-tbl tbody td { display: block !important; padding: 2px 0 !important; border: none !important; }
      .client-selector-tbl tbody td:nth-child(1) { width: calc(100% - 36px) !important; font-size: 14px !important; }
      .client-selector-tbl tbody td:nth-child(2) { display: none !important; }
      .client-selector-tbl tbody td:nth-child(3) { width: auto; font-size: 12px !important; color: #1aae9e !important; }
      .client-selector-tbl tbody td:nth-child(4) { width: 100% !important; font-size: 11px !important; max-width: none !important; white-space: normal !important; }
      .client-selector-tbl tbody td:nth-child(5) { display: none !important; }
      .client-selector-tbl tbody td:nth-child(6) { position: absolute !important; right: 14px !important; top: 12px !important; }

      /* ── Configurações ── */
      .config-tabs { overflow-x: auto !important; flex-wrap: nowrap !important; -webkit-overflow-scrolling: touch; gap: 0 !important; }
      .config-tabs button { flex-shrink: 0 !important; white-space: nowrap !important; font-size: 12px !important; padding: 8px 14px !important; min-height: 40px; }

      /* ── Itinerários ── */
      /* mobile: nada de altura travada — a página inteira rola, sem scroll interno */
      .itinerario-grid { grid-template-columns: 1fr !important; }
      .itinerario-grid > div:first-child { max-height: none !important; overflow: visible !important; }
      .itinerario-panels { grid-template-columns: 1fr !important; }
      .itinerario-panels > div > div[style*="maxHeight"] { max-height: 300px !important; }
      .itinerario-actions { flex-wrap: wrap !important; }
      .itinerario-actions button { flex: 1 1 auto !important; text-align: center !important; min-height: 42px; }
      .demandas-list { max-height: 340px !important; }
      .demandas-list > div { padding: 10px 12px !important; flex-wrap: wrap; }
      .demandas-list > div > div { flex: 1 1 100% !important; min-width: 0; }
      .demandas-list > div button { flex: 1 1 100%; width: auto !important; margin-top: 8px !important; min-height: 40px; border-radius: 8px !important; }
      .demandas-list span[title^="Arraste"] { display: none !important; }
      /* lista de demandas dentro do modal Novo Itinerário */
      .modal-demandas > div { flex-wrap: wrap !important; row-gap: 4px; }
      .modal-demandas span[title^="Arraste"] { display: none !important; }
      .modal-demandas > div > div[style*="flex"] { min-width: 60% !important; }

      /* ── Mapa operacional ── */
      .mapa-grid { grid-template-columns: 1fr !important; }
      .mapa-grid .mapa-sidebar { max-height: 260px !important; }
      .mapa-grid .mapa-sidebar > div:last-child { max-height: 200px !important; }
      /* mira só o container do Leaflet (height:500px inline) — não o toggle de satélite */
      .mapa-grid > div:first-child > div[style*="height: 500px"] { height: 62vh !important; min-height: 340px !important; }
      .map-confirm-container { height: 280px !important; }
    }

    /* ═══ Telefones pequenos (≤480px) ═══ */
    @media (max-width: 480px) {
      .dash-cards-grid { grid-template-columns: 1fr 1fr !important; gap: 6px !important; }
      .dash-cards-row3 { grid-template-columns: 1fr !important; gap: 6px !important; }
      .kpi-cards-grid { grid-template-columns: 1fr 1fr !important; gap: 6px !important; }
      .relatorio-summary-cards { grid-template-columns: 1fr !important; }
      .status-tabs-row button { font-size: 11px !important; padding: 7px 12px !important; }
      .modal-overlay > .modal-box { max-height: 100vh !important; max-height: 100dvh !important; border-radius: 0 !important; min-height: 100vh; min-height: 100dvh; }
      .modal-overlay > .modal-box::before { display: none; }
      .app-content { padding: 10px 10px calc(16px + env(safe-area-inset-bottom)) 10px !important; }
      .page-content h1 { font-size: 16px !important; }
      .dash-finance-cards > div { padding: 14px 12px !important; }
      .dashboard-faturamento-bars { max-height: 100px !important; height: 100px !important; }
      .relatorio-chart-bars { max-height: 100px !important; height: 100px !important; }
      .relatorio-chart-container { max-height: 200px !important; }
      .loc-cards td[data-label] { width: calc(50% - 8px); }
    }

    /* ═══ Desktop only ═══ */
    @media (min-width: 769px) {
      .mobile-overlay { display: none !important; }
      .mobile-hamburger { display: none !important; }
      .mobile-only { display: none !important; }
    }
    /* ═══ Helpers mobile ═══ */
    @media (max-width: 768px) {
      .desktop-only { display: none !important; }
    }
  `}</style>
);

// ─── Toast Notification ───────────────────────────────────────────────────────
function ToastContainer({ toasts, removeToast }) {
  return (
    <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, display:"flex", flexDirection:"column", gap:10, pointerEvents:"none" }}>
      {toasts.map(t => (
        <div key={t.id} className={t.exiting ? "toast-exit" : "toast-enter"}
          style={{ background:t.color||"#1e2330", color:"#fff", borderRadius:6, padding:"12px 18px", fontSize:13, fontWeight:600, boxShadow:"0 4px 20px rgba(0,0,0,.25)", display:"flex", alignItems:"center", gap:10, minWidth:240, maxWidth:340, fontFamily:FONT, cursor:"pointer", pointerEvents:"all" }}
          onClick={() => removeToast(t.id)}>
          <span style={{ fontSize:22, lineHeight:1 }}>{t.icon||""}</span>
          <div>
            <div style={{ fontSize:13, fontWeight:700 }}>{t.title}</div>
            {t.body && <div style={{ fontSize:11, opacity:.85, marginTop:2 }}>{t.body}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((title, body, icon, color) => {
    const id = Date.now() + Math.random();
    setToasts(p => [...p, { id, title, body, icon, color, exiting: false }]);
    setTimeout(() => {
      setToasts(p => p.map(t => t.id===id ? { ...t, exiting:true } : t));
      setTimeout(() => setToasts(p => p.filter(t => t.id!==id)), 350);
    }, 3500);
  }, []);
  const remove = useCallback((id) => {
    setToasts(p => p.map(t => t.id===id ? { ...t, exiting:true } : t));
    setTimeout(() => setToasts(p => p.filter(t => t.id!==id)), 350);
  }, []);
  return { toasts, add, remove };
}


// ─── Animated Counter ─────────────────────────────────────────────────────────
function AnimatedNumber({ value, prefix = "", suffix = "", duration = 900 }) {
  const [display, setDisplay] = useState(0);
  const isFloat = typeof value === "number" && !Number.isInteger(value);
  const numVal   = typeof value === "number" ? value : 0;
  const isCurrency = prefix === "R$" || String(value).includes("R$");

  useEffect(() => {
    if (isCurrency || typeof value === "string") return; // strings animate via CSS only
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * numVal));
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(numVal);
    };
    requestAnimationFrame(step);
  }, [numVal]);

  if (typeof value === "string") return <span className="kpi-count">{value}</span>;
  return <span className="kpi-count">{display.toLocaleString("pt-BR")}{suffix}</span>;
}

function AnimatedMoney({ value, duration = 900 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(eased * value);
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [value]);
  return <span className="kpi-count">{display.toLocaleString("pt-BR", { style:"currency", currency:"BRL" })}</span>;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────
const INIT_CLIENTES = [];
const INIT_LOCACOES = [];
const INIT_ITINERARIOS = [];
const INIT_MOTORISTAS = [];
const INIT_FROTA = [];
const INIT_CACAMBAS = [];

// ─── Utilities ────────────────────────────────────────────────────────────────
const fmt = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v) || 0;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// Helper: formatar entrada de valor monetário em tempo real (R$ 1.234,56)
const formatCurrencyInput = (raw) => {
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  let cents = parseInt(digits, 10);
  let reais = (cents / 100).toFixed(2);
  // Formatar com separador brasileiro
  const [intPart, decPart] = reais.split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return intFormatted + ',' + decPart;
};
const parseCurrencyToFloat = (formatted) => {
  if (!formatted) return 0;
  // "1.234,56" → 1234.56
  return parseFloat(String(formatted).replace(/\./g, '').replace(',', '.')) || 0;
};
const initCurrencyFromNumber = (num) => {
  if (!num && num !== 0) return '';
  const n = parseFloat(num) || 0;
  if (n === 0) return '';
  const fixed = n.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return intFormatted + ',' + decPart;
};
const formatKmInput = (raw) => {
  let digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('pt-BR');
};
const parseKmToNumber = (formatted) => {
  if (!formatted) return '';
  return String(formatted).replace(/\D/g, '');
};
const initKmFromNumber = (num) => {
  if (!num && num !== 0) return '';
  const n = parseInt(num, 10);
  if (!n && n !== 0) return '';
  return n.toLocaleString('pt-BR');
};

const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v);
  // Handle ISO strings like "2026-03-04T03:00:00.000Z"
  const d = s.includes("T") ? s.split("T")[0] : s;
  if (d.length === 10 && d.includes("-")) {
    const [y, m, day] = d.split("-");
    return day + "/" + m + "/" + y;
  }
  return s;
};

// ─── Formatação CEP ────────────────────────────────────────────────────────────
const formatCep = (value) => {
  const nums = (value || "").replace(/\D/g, "").slice(0, 8);
  if (nums.length <= 5) return nums;
  return nums.replace(/(\d{5})(\d{1,3})/, "$1-$2");
};

const formatPhone = (value) => {
  const nums = (value || "").replace(/\D/g, "").slice(0, 11);
  if (nums.length <= 2) return nums;
  if (nums.length <= 6) return "(" + nums.slice(0,2) + ") " + nums.slice(2);
  if (nums.length <= 10) return "(" + nums.slice(0,2) + ") " + nums.slice(2,6) + "-" + nums.slice(6);
  return "(" + nums.slice(0,2) + ") " + nums.slice(2,7) + "-" + nums.slice(7);
};

// ─── Exportar para Excel (CSV UTF-8 BOM) ──────────────────────────────────────
const exportToExcel = (rows, columns, filename) => {
  const sep = ";";
  const header = columns.map(c => c.label || c.h || "").join(sep);
  const lines = rows.map(r => columns.map(c => {
    const ck = c.key || c.k;
    let v = typeof ck === "function" ? ck(r) : (r[ck] ?? "");
    v = String(v).replace(/"/g, '""');
    if (String(v).includes(sep) || String(v).includes('"') || String(v).includes("\n")) v = '"' + v + '"';
    return v;
  }).join(sep));
  const bom = "\uFEFF";
  const csv = bom + header + "\n" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename + ".csv"; a.click();
  URL.revokeObjectURL(url);
};

// ─── Formatação CPF/CNPJ ───────────────────────────────────────────────────────
const formatCpfCnpj = (value) => {
  const nums = (value || "").replace(/\D/g, "");
  if (nums.length <= 11) {
    // CPF: 000.000.000-00
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  // CNPJ: 00.000.000/0000-00
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const handleCpfCnpjChange = (setter, key) => (e) => {
  const raw = e.target.value.replace(/\D/g, "").slice(0, 14);
  setter(prev => ({ ...prev, [key]: formatCpfCnpj(raw) }));
};

// ─── Autocomplete de Endereço (Google Places) ────────────────────────────────
function AddressAutocomplete({ value, onChange, onSelect, placeholder, style, cidadeHint, ufHint }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const serviceRef = useRef(null);
  const placesRef = useRef(null);
  const dummyRef = useRef(null);

  // Inicializar Google Places services
  useEffect(() => {
    const init = () => {
      if (window.google?.maps?.places) {
        serviceRef.current = new window.google.maps.places.AutocompleteService();
        if (!dummyRef.current) { dummyRef.current = document.createElement("div"); }
        placesRef.current = new window.google.maps.places.PlacesService(dummyRef.current);
      }
    };
    init();
    if (!serviceRef.current) { const t = setInterval(() => { init(); if (serviceRef.current) clearInterval(t); }, 500); return () => clearInterval(t); }
  }, []);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fallback: busca via Mapbox Geocoding (caso Google Places não esteja disponível ou retorne vazio)
  const searchMapbox = async (text) => {
    try {
      const prox = (cidadeHint || ufHint) ? "&proximity=ip" : "";
      const q = text + (cidadeHint ? ", " + cidadeHint : "") + (ufHint ? ", " + ufHint : "");
      const url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q)
        + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=6&language=pt&autocomplete=true"
        + "&types=address,place,locality,neighborhood,poi" + prox;
      const resp = await fetch(url);
      const data = await resp.json();
      if (!data.features || !data.features.length) return [];
      return data.features.map(f => {
        const ctx = f.context || [];
        const findCtx = (pref) => { const c = ctx.find(x => (x.id || "").startsWith(pref)); return c ? c.text : ""; };
        const findCtxShort = (pref) => { const c = ctx.find(x => (x.id || "").startsWith(pref)); return c ? (c.short_code ? c.short_code.replace("BR-", "").toUpperCase() : c.text) : ""; };
        const [lng, lat] = f.center || [null, null];
        return {
          source: "mapbox",
          main: f.text || (f.place_name || "").split(",")[0] || "",
          secondary: (f.place_name || "").split(",").slice(1).join(",").trim(),
          // dados já resolvidos (Mapbox não precisa de getDetails)
          resolved: {
            logradouro: f.text || "",
            numero: f.address || "",
            bairro: findCtx("neighborhood"),
            cidade: findCtx("place") || findCtx("locality"),
            uf: findCtxShort("region"),
            cep: findCtx("postcode"),
            lat, lng,
            placeName: f.place_name || "",
          },
        };
      });
    } catch (e) { return []; }
  };

  const search = (text) => {
    onChange(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (text.length < 2) { setSuggestions([]); setShowDrop(false); return; }
    timerRef.current = setTimeout(() => {
      setLoading(true);
      const usarMapbox = () => {
        searchMapbox(text).then(list => {
          setSuggestions(list);
          setShowDrop(list.length > 0);
          setLoading(false);
        });
      };
      // 1) Tenta Google Places (se disponível)
      if (serviceRef.current) {
        const request = {
          input: text + (cidadeHint ? ", " + cidadeHint : "") + (ufHint ? ", " + ufHint : ""),
          componentRestrictions: { country: "br" },
        };
        try {
          serviceRef.current.getPlacePredictions(request, (predictions, status) => {
            try {
              const ok = window.google?.maps?.places?.PlacesServiceStatus?.OK;
              if (status === ok && predictions && predictions.length) {
                setSuggestions(predictions.map(p => ({
                  source: "google",
                  placeId: p.place_id,
                  main: p.structured_formatting?.main_text || p.description?.split(",")[0] || "",
                  secondary: p.structured_formatting?.secondary_text || "",
                  description: p.description || "",
                })));
                setShowDrop(true);
                setLoading(false);
              } else {
                // Google falhou ou vazio → fallback Mapbox
                usarMapbox();
              }
            } catch (cbErr) { usarMapbox(); }
          });
        } catch (gErr) { usarMapbox(); }
      } else {
        // Google Places não carregou → usa Mapbox direto
        usarMapbox();
      }
    }, 220);
  };

  const pick = (s) => {
    setShowDrop(false);
    setSuggestions([]);
    // Sugestão do Mapbox já vem com tudo resolvido
    if (s.source === "mapbox" && s.resolved) {
      if (onSelect) onSelect(s.resolved);
      else onChange(s.resolved.logradouro || s.main);
      return;
    }
    // Sugestão do Google → busca detalhes
    if (!placesRef.current || !s.placeId) { onChange(s.main); return; }
    placesRef.current.getDetails({ placeId: s.placeId, fields: ["address_components", "geometry", "name", "formatted_address"] }, (place, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) return;
      const comp = place.address_components || [];
      const get = (type) => { const c = comp.find(x => x.types.includes(type)); return c ? c.long_name : ""; };
      const getShort = (type) => { const c = comp.find(x => x.types.includes(type)); return c ? c.short_name : ""; };
      const logradouro = get("route") || place.name || "";
      const numero = get("street_number") || "";
      const bairro = get("sublocality_level_1") || get("sublocality") || get("neighborhood") || "";
      const cidade = get("administrative_area_level_2") || get("locality") || "";
      const uf = getShort("administrative_area_level_1") || "";
      const cep = get("postal_code") || "";
      const lat = place.geometry?.location?.lat() || null;
      const lng = place.geometry?.location?.lng() || null;
      if (onSelect) onSelect({ logradouro, numero, bairro, cidade, uf, cep, lat, lng, placeName: place.formatted_address || "" });
    });
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input style={style} value={value} onChange={e => search(e.target.value)} placeholder={placeholder || "Digite o endereço..."} autoComplete="off" />
      {loading && <div style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", fontSize:10, color:"#999" }}>...</div>}
      {showDrop && suggestions.length > 0 && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff", border:"1px solid #e0e0e0", borderRadius:"0 0 8px 8px", boxShadow:"0 6px 20px rgba(0,0,0,.15)", zIndex:9999, maxHeight:260, overflowY:"auto" }}>
          {suggestions.map((s, i) => (
            <div key={i} onClick={() => pick(s)} style={{ padding:"10px 12px", fontSize:13, cursor:"pointer", borderBottom: i < suggestions.length-1 ? "1px solid #f0f0f0" : "none", transition:"background .15s" }}
              onMouseEnter={e => e.currentTarget.style.background="#f0faf8"}
              onMouseLeave={e => e.currentTarget.style.background="#fff"}>
              <div style={{ fontWeight:600, color:"#1e293b" }}>{s.main}</div>
              <div style={{ fontSize:11, color:"#888", marginTop:2 }}>{s.secondary}</div>
            </div>
          ))}
          <div style={{ padding:"4px 12px 6px", fontSize:9, color:"#bbb", textAlign:"right" }}>powered by Google</div>
        </div>
      )}
    </div>
  );
}

// ─── Gerador de Recibo PDF ─────────────────────────────────────────────────────
async function gerarReciboPDF(locacao, empresaCfg) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210, ph = 297;
  const halfH = ph / 2;
  const m = 6; // margin

  const emp = empresaCfg || {};
  const hoje = new Date();
  const emissao = String(hoje.getDate()).padStart(2,"0")+"/"+String(hoje.getMonth()+1).padStart(2,"0")+"/"+hoje.getFullYear();
  const total = locacao.valor ? Number(locacao.valor).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "0,00";

  const fmtD = (d) => {
    if (!d) return "";
    const s = String(d);
    const p = s.includes("T") ? s.split("T")[0] : s;
    if (p.length !== 10) return p;
    const [y,mm,dd] = p.split("-");
    return dd+"/"+mm+"/"+y;
  };

  const data = {
    empresa: emp.nome || "EMPRESA",
    cnpj: emp.cnpj || "",
    endereco: emp.endereco || "",
    bairro: emp.bairro || "",
    cidade: emp.cidade || "",
    uf: emp.uf || "",
    cep: emp.cep || "",
    telefone: emp.telefone || "",
    email: emp.email || "",
    condicoes: emp.condicoes_aluguel || "",
    num_locacao: locacao.id || "",
    emissao: emissao,
    total: total,
    cli_nome: locacao.cliente || "",
    cli_cpf: locacao.cpfCnpj || locacao.cpf_cnpj || "",
    cli_tel: locacao.contatoTel || locacao.contato_tel || "",
    cli_end: locacao.logradouro ? (locacao.logradouro + (locacao.numero ? ", " + locacao.numero : "")) : (typeof locacao.endereco === "string" ? locacao.endereco : ""),
    cli_cep: locacao.cep || (locacao.endereco?.cep) || "",
    cli_bairro: locacao.bairro || (locacao.endereco?.bairro) || "",
    cli_municipio: (locacao.cidade || locacao.endereco?.cidade || "") + "/" + (locacao.uf || locacao.endereco?.uf || ""),
    tipo_cacamba: (locacao.tipoCacamba || locacao.tipo_cacamba || "") + (locacao.cacamba ? " / " + locacao.cacamba : ""),
    data_entrega: fmtD(locacao.dataEntrega || locacao.data_entrega),
    data_retirada_prev: fmtD(locacao.dataRetiraPrevista || locacao.data_retirada_prevista || locacao.dataRetirada || locacao.data_retirada),
    data_retirada: fmtD(locacao.dataRetiradaReal || locacao.data_retirada_real || ""),
    pagamento: locacao.pagamento || "dinheiro",
  };

  // Load logo and QR images if available
  const API = process.env.REACT_APP_API || "http://localhost:3001";
  let logoImg = null, qrImg = null, assImg = null;
  const token = sessionStorage.getItem("controla_token");
  const fetchAuth = (url) => fetch(url, { headers: { Authorization: "Bearer " + token } });
  if (emp.logo_url) {
    try { const u = emp.logo_url.startsWith("/api/") ? API + emp.logo_url : API + "/api/files/" + emp.logo_url; const r = await fetchAuth(u); const b = await r.blob(); logoImg = await blobToDataURL(b); } catch(e){}
  }
  if (emp.qrpix_url) {
    try { const u = emp.qrpix_url.startsWith("/api/") ? API + emp.qrpix_url : API + "/api/files/" + emp.qrpix_url; const r = await fetchAuth(u); const b = await r.blob(); qrImg = await blobToDataURL(b); } catch(e){}
  }
  if (emp.assinatura_url) {
    try { const u = emp.assinatura_url.startsWith("/api/") ? API + emp.assinatura_url : API + "/api/files/" + emp.assinatura_url; const r = await fetchAuth(u); const b = await r.blob(); assImg = await blobToDataURL(b); } catch(e){}
  }
  const nomeSig = emp.nome_signatario || '';

  function blobToDataURL(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  function drawRecibo(yOffset) {
    const lx = m, rx = pw - m, ty = yOffset, cw = rx - lx;
    let cy = ty;

    // ===== MODELO CLASSICO =====
    const hdrH = 22;
    const rowH = 5.5;

    // Header: Logo | Company Info | Receipt Box
    const logoW = 36, boxW = 48;
    const infoW = cw - logoW - boxW;

    // Logo
    doc.setDrawColor(170);
    doc.setLineWidth(0.15);
    doc.rect(lx, cy, logoW, hdrH);
    if (logoImg) {
      try { doc.addImage(logoImg, "PNG", lx+1, cy+1, logoW-2, hdrH-2); } catch(e){}
    } else {
      doc.setFontSize(5.5); doc.setTextColor(150);
      doc.text("[LOGO]", lx + logoW/2, cy + hdrH/2, { align: "center" });
    }

    // Company info
    doc.rect(lx + logoW, cy, infoW, hdrH);
    doc.setTextColor(0); doc.setFontSize(8); doc.setFont("helvetica","bold");
    doc.text(data.empresa, lx + logoW + 2, cy + 4);
    doc.setFontSize(5.5); doc.setFont("helvetica","normal");
    doc.text(data.endereco, lx + logoW + 2, cy + 8);
    doc.text(data.cidade + "/" + data.uf + " - CEP: " + data.cep, lx + logoW + 2, cy + 11.5);
    doc.text(data.email, lx + logoW + 2, cy + 15);
    doc.text("Fone: " + data.telefone + "   CNPJ: " + data.cnpj, lx + logoW + 2, cy + 18.5);

    // Receipt box
    const bx = rx - boxW;
    doc.setFillColor(44, 62, 80); doc.rect(bx, cy, boxW, 5.5, "FD");
    doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
    doc.text("RECIBO DE LOCAÇÃO", bx + boxW/2, cy + 3.8, { align: "center" });

    const rRows = [["Nº locação", data.num_locacao], ["Emissão", data.emissao], ["Total (R$)", data.total]];
    const rrH = (hdrH - 5.5) / 3;
    rRows.forEach(([lab, val], i) => {
      const ry = cy + 5.5 + i * rrH;
      doc.setDrawColor(170); doc.rect(bx, ry, boxW/2, rrH); doc.rect(bx + boxW/2, ry, boxW/2, rrH);
      doc.setTextColor(0); doc.setFontSize(5.5); doc.setFont("helvetica","bold");
      doc.text(lab, bx + 1.5, ry + rrH/2 + 0.8);
      doc.setFont("helvetica","normal"); doc.setFontSize(6);
      doc.text(val, bx + boxW - 1.5, ry + rrH/2 + 0.8, { align: "right" });
    });
    cy += hdrH + 0.5;

    // CLIENTE header
    doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
    doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
    doc.text("CLIENTE", lx + 2, cy + 3.2);
    cy += 4.5;

    // Cliente rows
    const cr = 5.5;
    const w1a = cw*0.45, w1b = cw*0.22, w1c = cw*0.33;
    doc.setDrawColor(170); doc.setTextColor(0);

    // Row 1
    doc.rect(lx, cy, w1a, cr); doc.rect(lx+w1a, cy, w1b, cr); doc.rect(lx+w1a+w1b, cy, w1c, cr);
    doc.setFontSize(5); doc.setFont("helvetica","normal");
    doc.text("Nome:", lx+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.setFontSize(5.5);
    doc.text(data.cli_nome, lx+12, cy+3.5);
    doc.setFont("helvetica","normal"); doc.setFontSize(5);
    doc.text("CPF/CNPJ:", lx+w1a+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.text(data.cli_cpf, lx+w1a+18, cy+3.5);
    doc.setFont("helvetica","normal"); doc.text("Telefone:", lx+w1a+w1b+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.text(data.cli_tel, lx+w1a+w1b+15, cy+3.5);
    cy += cr;

    // Row 2
    const w2a = cw*0.72, w2b = cw*0.28;
    doc.rect(lx, cy, w2a, cr); doc.rect(lx+w2a, cy, w2b, cr);
    doc.setFont("helvetica","normal"); doc.setFontSize(5);
    doc.text("Endereço:", lx+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.setFontSize(5.5);
    doc.text(data.cli_end, lx+17, cy+3.5);
    doc.setFont("helvetica","normal"); doc.setFontSize(5);
    doc.text("CEP:", lx+w2a+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.text(data.cli_cep, lx+w2a+9, cy+3.5);
    cy += cr;

    // Row 3
    const w3a = cw*0.18, w3b = cw*0.35, w3c = cw*0.47;
    doc.rect(lx, cy, w3a, cr); doc.rect(lx+w3a, cy, w3b, cr); doc.rect(lx+w3a+w3b, cy, w3c, cr);
    doc.setFont("helvetica","normal"); doc.setFontSize(5);
    doc.text("Compl.:", lx+1, cy+3.5);
    doc.text("Bairro:", lx+w3a+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.text(data.cli_bairro, lx+w3a+12, cy+3.5);
    doc.setFont("helvetica","normal"); doc.text("Município/UF:", lx+w3a+w3b+1, cy+3.5);
    doc.setFont("helvetica","bold"); doc.text(data.cli_municipio, lx+w3a+w3b+22, cy+3.5);
    cy += cr + 0.5;

    // LOCAÇÃO header
    doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
    doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
    doc.text("LOCAÇÃO", lx + 2, cy + 3.2);
    cy += 4.5;

    // Locação column headers
    doc.setTextColor(0);
    const cols = [cw*0.22, cw*0.16, cw*0.22, cw*0.18, cw*0.22];
    const heads = ["Tipo da Caçamba", "Data da Entrega", "Data Prev. da Retirada", "Data da Retirada", "Valor da Locação (R$)"];
    let cx = lx;
    doc.setFillColor(240, 240, 240);
    heads.forEach((h, i) => {
      doc.rect(cx, cy, cols[i], 4.5, "FD");
      doc.setFontSize(4.5); doc.setFont("helvetica","bold");
      doc.text(h, cx + 1.5, cy + 3);
      cx += cols[i];
    });
    cy += 4.5;

    // Locação data row
    const vals = [data.tipo_cacamba, data.data_entrega, data.data_retirada_prev, data.data_retirada, data.total];
    cx = lx;
    vals.forEach((v, i) => {
      doc.rect(cx, cy, cols[i], 5.5);
      doc.setFontSize(5.5); doc.setFont("helvetica","normal");
      doc.text(v, cx + 1.5, cy + 3.8);
      cx += cols[i];
    });
    cy += 5.5;

    // Resíduos (se houver)
    let resObj = locacao.residuos || {};
    if (typeof resObj === 'string') { try { resObj = JSON.parse(resObj); } catch(e) { resObj = {}; } }
    const resLines = [];
    if (resObj.classeA?.length) resLines.push("Classe A: " + resObj.classeA.join(", "));
    if (resObj.classeB?.length) resLines.push("Classe B: " + resObj.classeB.join(", "));
    if (resObj.classeC?.length) resLines.push("Classe C: " + resObj.classeC.join(", "));
    if (resObj.classeD?.length) resLines.push("Classe D: " + resObj.classeD.join(", "));
    if (resObj.outros) resLines.push("Outros: " + resObj.outros);
    if (resLines.length > 0) {
      doc.setFillColor(240, 240, 240); doc.rect(lx, cy, cw, 4.5, "FD");
      doc.setTextColor(0); doc.setFontSize(4.5); doc.setFont("helvetica","bold");
      doc.text("Classificação dos Resíduos", lx + 1.5, cy + 3);
      cy += 4.5;
      const resTxt = resLines.join("   |   ");
      const resWrapped = doc.splitTextToSize(resTxt, cw - 4);
      const resH = Math.max(resWrapped.length * 3 + 2, 5);
      doc.rect(lx, cy, cw, resH);
      doc.setFontSize(5); doc.setFont("helvetica","normal");
      doc.text(resWrapped, lx + 1.5, cy + 3);
      cy += resH;
    }

    // Lançamentos
    doc.rect(lx, cy, cw, 6);
    doc.setFontSize(5.5); doc.setFont("helvetica","bold");
    doc.text("Lançamentos", lx + 2, cy + 4);
    cy += 6;

    // Formas de pagamento
    doc.rect(lx, cy, cw*0.72, 7);
    const pag = data.pagamento.toLowerCase();
    const marks = [["dinheiro","Dinheiro"],["boleto","Boleto"],["cartao","Cartão"],["pix","PIX"],["deposito","Depósito"]];
    let ptxt = "Formas de Pagamento: ";
    marks.forEach(([k,v]) => { ptxt += "[ " + (pag===k?"X":" ") + " ] " + v + "   "; });
    doc.setFontSize(5.5); doc.setFont("helvetica","normal");
    doc.text(ptxt, lx + 1.5, cy + 4.5);
    // Total box
    doc.rect(lx+cw*0.72, cy, cw*0.28, 3.5);
    doc.rect(lx+cw*0.72, cy+3.5, cw*0.28, 3.5);
    doc.setFontSize(5); doc.setFont("helvetica","bold");
    doc.text("Total do Recibo (R$)", lx+cw*0.72+1.5, cy+2.5);
    doc.setFontSize(7.5);
    doc.text(data.total, lx+cw*0.72+cw*0.28-2, cy+6.5, { align: "right" });
    cy += 7.5;

    // CONDIÇÕES DO ALUGUEL
    doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
    doc.setTextColor(255); doc.setFontSize(5.5); doc.setFont("helvetica","bold");
    doc.text("CONDIÇÕES DO ALUGUEL", lx + 2, cy + 3.2);
    cy += 4.5;
    doc.setTextColor(0); doc.setFont("helvetica","normal"); doc.setFontSize(5);
    const condLines = doc.splitTextToSize(data.condicoes || "", cw - 4);
    const condH = Math.max(condLines.length * 3.2 + 2, 8);
    doc.rect(lx, cy, cw, condH);
    doc.text(condLines, lx + 2, cy + 3);
    cy += condH + 2;

    // Assinaturas
    const sigW = (cw - 30) / 2;
    doc.setLineWidth(0.2); doc.setDrawColor(0);
    // Assinatura do locador (esquerda) — imagem se disponível
    if (assImg) {
      try {
        const assImgW = 35, assImgH = 14;
        const assX = lx + (sigW + 5) / 2 - assImgW / 2;
        doc.addImage(assImg, "PNG", assX, cy - 1, assImgW, assImgH);
      } catch(e) {}
    }
    doc.line(lx + 5, cy + 12, lx + sigW, cy + 12);
    doc.line(lx + sigW + 8, cy + 12, lx + sigW*2 + 3, cy + 12);
    doc.setFontSize(5);
    const sigLabelEsq = nomeSig ? nomeSig.toUpperCase() : data.empresa.toUpperCase();
    doc.text(sigLabelEsq, lx + (sigW+5)/2, cy + 15, { align: "center" });
    doc.setFontSize(4.5);
    doc.text("ASSINATURA DO LOCADOR", lx + (sigW+5)/2, cy + 18, { align: "center" });
    doc.setFontSize(5);
    doc.text("ASSINATURA DO LOCATÁRIO", lx + sigW + 8 + (sigW-5)/2, cy + 15, { align: "center" });

    // QR Code PIX
    const qrSize = 20;
    const qrX = rx - qrSize - 1;
    if (qrImg) {
      try { doc.addImage(qrImg, "PNG", qrX, cy, qrSize, qrSize); } catch(e){}
    } else {
      doc.setDrawColor(170); doc.rect(qrX, cy, qrSize, qrSize);
      doc.setFontSize(5.5); doc.setTextColor(150); doc.setFont("helvetica","bold");
      doc.text("QR CODE", qrX + qrSize/2, cy + qrSize/2 - 1, { align: "center" });
      doc.text("PIX", qrX + qrSize/2, cy + qrSize/2 + 2.5, { align: "center" });
    }
  }

  // Draw 2 copies
  drawRecibo(4);
  // Dashed separator
  doc.setDrawColor(150); doc.setLineDashPattern([2, 1.5], 0); doc.setLineWidth(0.2);
  doc.line(m, halfH + 0.5, pw - m, halfH + 0.5);
  doc.setLineDashPattern([], 0);
  drawRecibo(halfH + 1.5);

  // Download
  const filename = "RECIBO_" + (locacao.id || "locacao") + ".pdf";
  doc.save(filename);
}

// ─── Gerador de Recibo CONSOLIDADO (múltiplas locações, mesmo cliente) ──────────
async function gerarReciboConsolidadoPDF(locacoes, empresaCfg) {
  if (!locacoes || !locacoes.length) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210, m = 7;
  const lx = m, rx = pw - m, cw = rx - lx;

  const emp = empresaCfg || {};
  const hoje = new Date();
  const emissao = String(hoje.getDate()).padStart(2,"0")+"/"+String(hoje.getMonth()+1).padStart(2,"0")+"/"+hoje.getFullYear();

  const fmtD = (d) => {
    if (!d) return "—";
    const s = String(d);
    const p = s.includes("T") ? s.split("T")[0] : s;
    if (p.length !== 10) return p;
    const [y,mm,dd] = p.split("-");
    return dd+"/"+mm+"/"+y;
  };
  const fmtV = (v) => v ? Number(v).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "0,00";

  const totalGeral = locacoes.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
  const loc0 = locacoes[0];
  const ids = locacoes.map(l => l.id).join(", ");

  // Dados da empresa
  const data = {
    empresa: emp.nome || "EMPRESA",
    cnpj: emp.cnpj || "",
    endereco: emp.endereco || "",
    cidade: emp.cidade || "",
    uf: emp.uf || "",
    cep: emp.cep || "",
    telefone: emp.telefone || "",
    email: emp.email || "",
    condicoes: emp.condicoes_aluguel || "",
  };

  // Dados do cliente (todos do mesmo cliente)
  const cli = {
    nome: loc0.cliente || "",
    cpf: loc0.cpfCnpj || loc0.cpf_cnpj || "",
    tel: loc0.contatoTel || loc0.contato_tel || "",
    end: loc0.logradouro ? (loc0.logradouro + (loc0.numero ? ", " + loc0.numero : "")) : (typeof loc0.endereco === "string" ? loc0.endereco : ""),
    cep: loc0.cep || (loc0.endereco?.cep) || "",
    bairro: loc0.bairro || (loc0.endereco?.bairro) || "",
    municipio: (loc0.cidade || loc0.endereco?.cidade || "") + "/" + (loc0.uf || loc0.endereco?.uf || ""),
  };

  // Load images
  const API = process.env.REACT_APP_API || "http://localhost:3001";
  let logoImg = null, qrImg = null, assImg = null;
  const token = sessionStorage.getItem("controla_token");
  const fetchAuth = (url) => fetch(url, { headers: { Authorization: "Bearer " + token } });
  function blobToDataURL(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }
  if (emp.logo_url) {
    try { const u = emp.logo_url.startsWith("/api/") ? API + emp.logo_url : API + "/api/files/" + emp.logo_url; const r = await fetchAuth(u); const b = await r.blob(); logoImg = await blobToDataURL(b); } catch(e){}
  }
  if (emp.qrpix_url) {
    try { const u = emp.qrpix_url.startsWith("/api/") ? API + emp.qrpix_url : API + "/api/files/" + emp.qrpix_url; const r = await fetchAuth(u); const b = await r.blob(); qrImg = await blobToDataURL(b); } catch(e){}
  }
  if (emp.assinatura_url) {
    try { const u = emp.assinatura_url.startsWith("/api/") ? API + emp.assinatura_url : API + "/api/files/" + emp.assinatura_url; const r = await fetchAuth(u); const b = await r.blob(); assImg = await blobToDataURL(b); } catch(e){}
  }
  const nomeSig = emp.nome_signatario || '';

  let cy = m;
  const rowH = 5.5;

  // ===== HEADER =====
  const hdrH = 22;
  const logoW = 36, boxW = 48;
  const infoW = cw - logoW - boxW;

  // Logo
  doc.setDrawColor(170); doc.setLineWidth(0.15);
  doc.rect(lx, cy, logoW, hdrH);
  if (logoImg) {
    try { doc.addImage(logoImg, "PNG", lx+1, cy+1, logoW-2, hdrH-2); } catch(e){}
  } else {
    doc.setFontSize(5.5); doc.setTextColor(150);
    doc.text("[LOGO]", lx + logoW/2, cy + hdrH/2, { align: "center" });
  }

  // Company info
  doc.rect(lx + logoW, cy, infoW, hdrH);
  doc.setTextColor(0); doc.setFontSize(8); doc.setFont("helvetica","bold");
  doc.text(data.empresa, lx + logoW + 2, cy + 4);
  doc.setFontSize(5.5); doc.setFont("helvetica","normal");
  doc.text(data.endereco, lx + logoW + 2, cy + 8);
  doc.text(data.cidade + "/" + data.uf + " - CEP: " + data.cep, lx + logoW + 2, cy + 11.5);
  doc.text(data.email, lx + logoW + 2, cy + 15);
  doc.text("Fone: " + data.telefone + "   CNPJ: " + data.cnpj, lx + logoW + 2, cy + 18.5);

  // Receipt box
  const bx = rx - boxW;
  doc.setFillColor(44, 62, 80); doc.rect(bx, cy, boxW, 5.5, "FD");
  doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
  doc.text("RECIBO DE LOCAÇÃO", bx + boxW/2, cy + 3.8, { align: "center" });

  const rRows = [["Locações", ids.length > 18 ? locacoes.length + " locações" : ids], ["Emissão", emissao], ["Total (R$)", fmtV(totalGeral)]];
  const rrH = (hdrH - 5.5) / 3;
  rRows.forEach(([lab, val], i) => {
    const ry = cy + 5.5 + i * rrH;
    doc.setDrawColor(170); doc.rect(bx, ry, boxW/2, rrH); doc.rect(bx + boxW/2, ry, boxW/2, rrH);
    doc.setTextColor(0); doc.setFontSize(5.5); doc.setFont("helvetica","bold");
    doc.text(lab, bx + 1.5, ry + rrH/2 + 0.8);
    doc.setFont("helvetica","normal"); doc.setFontSize(5.5);
    doc.text(String(val), bx + boxW - 1.5, ry + rrH/2 + 0.8, { align: "right" });
  });
  cy += hdrH + 0.5;

  // ===== CLIENTE =====
  doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
  doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
  doc.text("CLIENTE", lx + 2, cy + 3.2);
  cy += 4.5;

  const cr = 5.5;
  doc.setDrawColor(170); doc.setTextColor(0);

  // Row 1: Nome | CPF | Tel
  const w1a = cw*0.45, w1b = cw*0.22, w1c = cw*0.33;
  doc.rect(lx, cy, w1a, cr); doc.rect(lx+w1a, cy, w1b, cr); doc.rect(lx+w1a+w1b, cy, w1c, cr);
  doc.setFontSize(5); doc.setFont("helvetica","normal");
  doc.text("Nome:", lx+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.setFontSize(5.5);
  doc.text(cli.nome, lx+12, cy+3.5);
  doc.setFont("helvetica","normal"); doc.setFontSize(5);
  doc.text("CPF/CNPJ:", lx+w1a+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.text(cli.cpf, lx+w1a+18, cy+3.5);
  doc.setFont("helvetica","normal"); doc.text("Telefone:", lx+w1a+w1b+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.text(cli.tel, lx+w1a+w1b+15, cy+3.5);
  cy += cr;

  // Row 2: Endereço | CEP
  const w2a = cw*0.72, w2b = cw*0.28;
  doc.rect(lx, cy, w2a, cr); doc.rect(lx+w2a, cy, w2b, cr);
  doc.setFont("helvetica","normal"); doc.setFontSize(5);
  doc.text("Endereço:", lx+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.setFontSize(5.5);
  doc.text(cli.end, lx+17, cy+3.5);
  doc.setFont("helvetica","normal"); doc.setFontSize(5);
  doc.text("CEP:", lx+w2a+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.text(cli.cep, lx+w2a+9, cy+3.5);
  cy += cr;

  // Row 3: Bairro | Município
  const w3a = cw*0.45, w3b = cw*0.55;
  doc.rect(lx, cy, w3a, cr); doc.rect(lx+w3a, cy, w3b, cr);
  doc.setFont("helvetica","normal"); doc.setFontSize(5);
  doc.text("Bairro:", lx+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.text(cli.bairro, lx+12, cy+3.5);
  doc.setFont("helvetica","normal"); doc.text("Município/UF:", lx+w3a+1, cy+3.5);
  doc.setFont("helvetica","bold"); doc.text(cli.municipio, lx+w3a+22, cy+3.5);
  cy += cr + 0.5;

  // ===== LOCAÇÕES (tabela) =====
  doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
  doc.setTextColor(255); doc.setFontSize(6); doc.setFont("helvetica","bold");
  doc.text("LOCAÇÕES", lx + 2, cy + 3.2);
  cy += 4.5;

  // Verificar se endereços são diferentes
  const enderecos = locacoes.map(l => {
    if (l.logradouro) return l.logradouro + (l.numero ? ", " + l.numero : "");
    if (typeof l.endereco === "string") return l.endereco;
    return "";
  });
  const todosIguais = enderecos.every(e => e === enderecos[0]);

  // Colunas: com ou sem endereço
  let cols, heads;
  if (todosIguais) {
    // Sem coluna endereço
    cols = [cw*0.10, cw*0.13, cw*0.15, cw*0.18, cw*0.22, cw*0.22];
    heads = ["Nº", "Caçamba", "Tipo", "Entrega", "Retirada Prev.", "Valor (R$)"];
  } else {
    // Com coluna endereço
    cols = [cw*0.08, cw*0.10, cw*0.12, cw*0.25, cw*0.14, cw*0.14, cw*0.17];
    heads = ["Nº", "Caçamba", "Tipo", "Endereço", "Entrega", "Ret. Prev.", "Valor (R$)"];
  }

  // Header row
  doc.setTextColor(0);
  let cx = lx;
  doc.setFillColor(240, 240, 240);
  heads.forEach((h, i) => {
    doc.rect(cx, cy, cols[i], 4.5, "FD");
    doc.setFontSize(4.8); doc.setFont("helvetica","bold");
    doc.text(h, cx + 1.5, cy + 3);
    cx += cols[i];
  });
  cy += 4.5;

  // Data rows
  locacoes.forEach((l, idx) => {
    // Verificar se precisa de nova página
    if (cy > 250) {
      doc.addPage();
      cy = m;
    }

    const tipo = (l.tipoCacamba || l.tipo_cacamba || "");
    const cacamba = l.cacamba || "";
    const entrega = fmtD(l.dataEntrega || l.data_entrega);
    const retPrev = fmtD(l.dataRetiraPrevista || l.data_retirada_prevista || l.dataRetirada || l.data_retirada);
    const valor = fmtV(l.valor);
    const end = l.logradouro ? (l.logradouro + (l.numero ? ", " + l.numero : "")) : (typeof l.endereco === "string" ? l.endereco : "");

    let vals;
    if (todosIguais) {
      vals = [String(l.id), cacamba, tipo, entrega, retPrev, valor];
    } else {
      // Truncar endereço se muito longo
      const endShort = end.length > 32 ? end.substring(0, 30) + "…" : end;
      vals = [String(l.id), cacamba, tipo, endShort, entrega, retPrev, valor];
    }

    cx = lx;
    const isAlt = idx % 2 === 1;
    if (isAlt) doc.setFillColor(248, 248, 248);
    vals.forEach((v, i) => {
      if (isAlt) doc.rect(cx, cy, cols[i], rowH, "FD");
      else doc.rect(cx, cy, cols[i], rowH);
      doc.setFontSize(5.5); doc.setFont("helvetica","normal"); doc.setTextColor(0);
      doc.text(v, cx + 1.5, cy + 3.8);
      cx += cols[i];
    });
    cy += rowH;
  });

  // Total row
  doc.setFillColor(44, 62, 80);
  const totalCols = todosIguais ? 5 : 6;
  const totalW = cols.slice(0, totalCols).reduce((s, c) => s + c, 0);
  const valorW = cols[totalCols];
  doc.rect(lx, cy, totalW, 6, "FD");
  doc.rect(lx + totalW, cy, valorW, 6, "FD");
  doc.setTextColor(255); doc.setFontSize(6.5); doc.setFont("helvetica","bold");
  doc.text("TOTAL (" + locacoes.length + " locações)", lx + totalW - 2, cy + 4.2, { align: "right" });
  doc.setFontSize(7.5);
  doc.text("R$ " + fmtV(totalGeral), lx + totalW + valorW - 2, cy + 4.2, { align: "right" });
  cy += 7;

  // Resíduos (consolidados de todas as locações)
  const allRes = [];
  locacoes.forEach(l => {
    let resObj = l.residuos || {};
    if (typeof resObj === 'string') { try { resObj = JSON.parse(resObj); } catch(e) { resObj = {}; } }
    const parts = [];
    if (resObj.classeA?.length) parts.push("A: " + resObj.classeA.join(", "));
    if (resObj.classeB?.length) parts.push("B: " + resObj.classeB.join(", "));
    if (resObj.classeC?.length) parts.push("C: " + resObj.classeC.join(", "));
    if (resObj.classeD?.length) parts.push("D: " + resObj.classeD.join(", "));
    if (resObj.outros) parts.push("Outros: " + resObj.outros);
    if (parts.length) allRes.push("#" + l.id + ": " + parts.join(" | "));
  });
  if (allRes.length > 0) {
    if (cy > 250) { doc.addPage(); cy = m; }
    doc.setFillColor(240, 240, 240); doc.rect(lx, cy, cw, 4.5, "FD");
    doc.setTextColor(0); doc.setFontSize(4.5); doc.setFont("helvetica","bold");
    doc.text("Classificação dos Resíduos", lx + 1.5, cy + 3);
    cy += 4.5;
    const resTxt = allRes.join("   •   ");
    const resWrapped = doc.splitTextToSize(resTxt, cw - 4);
    const resH = Math.max(resWrapped.length * 3 + 2, 5);
    doc.rect(lx, cy, cw, resH);
    doc.setFontSize(5); doc.setFont("helvetica","normal");
    doc.text(resWrapped, lx + 1.5, cy + 3);
    cy += resH;
  }

  // ===== CONDIÇÕES DO ALUGUEL =====
  if (data.condicoes) {
    if (cy > 240) { doc.addPage(); cy = m; }
    doc.setFillColor(44, 62, 80); doc.rect(lx, cy, cw, 4.5, "FD");
    doc.setTextColor(255); doc.setFontSize(5.5); doc.setFont("helvetica","bold");
    doc.text("CONDIÇÕES DO ALUGUEL", lx + 2, cy + 3.2);
    cy += 4.5;
    doc.setTextColor(0); doc.setFont("helvetica","normal"); doc.setFontSize(5);
    const condLines = doc.splitTextToSize(data.condicoes, cw - 4);
    const condH = Math.max(condLines.length * 3.2 + 2, 8);
    doc.rect(lx, cy, cw, condH);
    doc.text(condLines, lx + 2, cy + 3);
    cy += condH + 2;
  }

  // ===== ASSINATURAS =====
  if (cy > 258) { doc.addPage(); cy = m; }
  const sigW = (cw - 30) / 2;
  doc.setDrawColor(0); doc.setLineWidth(0.2);
  if (assImg) {
    try {
      const assImgW = 35, assImgH = 14;
      const assX = lx + (sigW + 5) / 2 - assImgW / 2;
      doc.addImage(assImg, "PNG", assX, cy - 1, assImgW, assImgH);
    } catch(e) {}
  }
  doc.line(lx + 5, cy + 12, lx + sigW, cy + 12);
  doc.line(lx + sigW + 8, cy + 12, lx + sigW*2 + 3, cy + 12);
  doc.setFontSize(5); doc.setTextColor(0);
  const sigLabelEsq = nomeSig ? nomeSig.toUpperCase() : data.empresa.toUpperCase();
  doc.text(sigLabelEsq, lx + (sigW+5)/2, cy + 15, { align: "center" });
  doc.setFontSize(4.5);
  doc.text("ASSINATURA DO LOCADOR", lx + (sigW+5)/2, cy + 18, { align: "center" });
  doc.setFontSize(5);
  doc.text("ASSINATURA DO LOCATÁRIO", lx + sigW + 8 + (sigW-5)/2, cy + 15, { align: "center" });

  // QR Code PIX
  const qrSize = 20;
  const qrX = rx - qrSize - 1;
  if (qrImg) {
    try { doc.addImage(qrImg, "PNG", qrX, cy, qrSize, qrSize); } catch(e){}
  } else {
    doc.setDrawColor(170); doc.rect(qrX, cy, qrSize, qrSize);
    doc.setFontSize(5.5); doc.setTextColor(150); doc.setFont("helvetica","bold");
    doc.text("QR CODE", qrX + qrSize/2, cy + qrSize/2 - 1, { align: "center" });
    doc.text("PIX", qrX + qrSize/2, cy + qrSize/2 + 2.5, { align: "center" });
  }

  // Download
  const filename = "RECIBO_CONSOLIDADO_" + locacoes.map(l => l.id).join("-") + ".pdf";
  doc.save(filename);
}

const dateBadge = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr);
  const d = s.includes("T") ? s.split("T")[0] : s;
  if (d.length !== 10 || !d.includes("-")) return null;
  const [y, m, day] = d.split("-");
  const target = new Date(parseInt(y), parseInt(m) - 1, parseInt(day));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  if (diffDays < 0) {
    return { color: "#fff", bg: "#d9534f", label: "Atrasado " + Math.abs(diffDays) + " dia" + (Math.abs(diffDays)!==1?"s":"") };
  } else if (diffDays === 0) {
    return { color: "#fff", bg: "#f0ad4e", label: "Vence hoje" };
  } else if (diffDays <= 7) {
    return { color: "#fff", bg: "#5cb85c", label: "Vence em " + diffDays + " dia" + (diffDays!==1?"s":"") };
  } else {
    return { color: "#fff", bg: "#3498db", label: "Faltam " + diffDays + " dias" };
  }
};

const DateCell = ({ dateStr, noBadge }) => {
  const badge = noBadge ? null : dateBadge(dateStr);
  const formatted = fmtDate(dateStr);
  if (!badge) return <span style={{ color:"#555", fontSize:12 }}>{formatted}</span>;
  return (
    <div style={{ background:badge.bg+"18", borderLeft:"3px solid "+badge.bg, padding:"6px 8px", borderRadius:2, textAlign:"center" }}>
      <div style={{ color:"#333", fontSize:12, fontWeight:600, marginBottom:3 }}>{formatted}</div>
      <span style={{ fontSize:9.75, color:badge.color, background:badge.bg, padding:"3px 6px", lineHeight:"10px", whiteSpace:"nowrap", display:"inline" }}>{badge.label}</span>
    </div>
  );
};

const statusColor = {
  "No Prazo":     { bg: "#e6f4ea", text: "#1a7a38", dot: "#2ea04c" },
  "Vence Hoje":   { bg: "#fff8e1", text: "#a56800", dot: "#f5a623" },
  "Atraso":       { bg: "#fde8e8", text: "#c0392b", dot: "#e74c3c" },
  "Ativo":        { bg: "#e8f4ff", text: "#1a5fa8", dot: "#1a9e8f" },
  "Inativo":      { bg: "#f5f5f5", text: "#777",    dot: "#bbb" },
  "Manutenção":   { bg: "#fff3e0", text: "#e65100", dot: "#ff6f00" },
  "Em Aberto":    { bg: "#fff3e0", text: "#e65100", dot: "#ff6f00" },
  "Em Andamento": { bg: "#e3f2fd", text: "#0d47a1", dot: "#1976d2" },
  "Concluído":    { bg: "#e8f5e9", text: "#1b5e20", dot: "#388e3c" },
  "Não Atendido": { bg: "#f5f5f5", text: "#666",    dot: "#bbb" },
  "Pendente":     { bg: "#fff8e1", text: "#a56800",  dot: "#f5a623" },
  "Atendido":     { bg: "#e8f5e9", text: "#1b5e20",  dot: "#388e3c" },
};

// ─── Gerador de PDF de Itinerário ─────────────────────────────────────────────
function gerarItinerarioPDF(itinerario, locacoes, empresaNome) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210, ph = 297;
  const mg = 10;
  const uw = pw - 2 * mg;

  const empresa = empresaNome || "Controla Cacambas";
  const motorista = itinerario.motorista || "-";
  const dataIt = fmtDate(itinerario.data) || "-";

  const paradas = (itinerario.paradas || []).map((p, i) => {
    const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {};
    const op = getOperacao(loc);
    const end = typeof loc.endereco === "string" ? loc.endereco : loc.endereco ? [loc.endereco.logradouro, loc.endereco.numero, loc.endereco.complemento, loc.endereco.bairro, loc.endereco.cidade, loc.endereco.uf].filter(Boolean).join(", ") : "-";
    // Para TROCA, buscar a nova locação já criada
    let novaLocId = p.novaLocId || "";
    if (op === "TROCA" && !novaLocId) {
      const childLoc = locacoes.find(l => l.obs && typeof l.obs === "string" && (l.obs.includes("troca da loca\u00e7\u00e3o #" + p.locId) || l.obs.includes("troca da locacao #" + p.locId)));
      if (childLoc) novaLocId = String(childLoc.id);
    }
    // Para TROCA, buscar se a locação filha está paga
    let childPago = false;
    if (op === "TROCA" && novaLocId) {
      const childLoc2 = locacoes.find(l2 => String(l2.id) === novaLocId);
      if (childLoc2) childPago = !!childLoc2.pago;
    }
    return { num: i + 1, op, cliente: loc.cliente || "-", tel: loc.telefone || loc.tel || loc.contatoTel || loc.contato_tel || loc.celular || "", local: end, cacamba: loc.cacamba || loc.tipoCacamba || "Cacamba", loc: String(loc.id || ""), mtr: loc.mtr || "-", pago: !!loc.pago, valor: loc.valor ? "R$ " + Number(loc.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "", status: p.status, novaLocId, childPago };
  });

  const colocarCount = paradas.filter(p => p.op === "ENTREGA").length;
  const retirarCount = paradas.filter(p => p.op === "RETIRADA").length;
  const trocarCount = paradas.filter(p => p.op === "TROCA").length;

  const opLabel = (op) => op === "RETIRADA" ? "RETIRAR" : op === "TROCA" ? "TROCAR" : "COLOCAR";
  let page = 1;
  let totalPages = 1;

  function drawHeader(y) {
    // Titulo centralizado: empresa + ITINERARIO
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text(empresa.toUpperCase(), pw / 2, y + 6, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text("ITINER\u00c1RIO", pw / 2, y + 12, { align: "center" });

    // Linha fina
    y += 15;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(mg, y, mg + uw, y);
    y += 3;

    // Info: Motorista | Data | Resumo
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(50, 50, 50);
    doc.text("Motorista: " + motorista, mg, y + 3);
    doc.text("Data: " + dataIt, mg + 70, y + 3);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(7);
    doc.text("Entregas: " + colocarCount + "  |  Retiradas: " + retirarCount + "  |  Trocas: " + trocarCount + "  |  Total: " + paradas.length, mg + uw, y + 3, { align: "right" });

    y += 6;
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.15);
    doc.line(mg, y, mg + uw, y);
    return y + 3;
  }

  function drawFooter() {
    const fy = ph - 14;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(mg, fy - 2, mg + uw, fy - 2);
    doc.setFontSize(6);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(empresa + "  -  " + dataIt, mg, fy + 1);
    doc.text("P\u00e1gina " + page, mg + uw, fy + 1, { align: "right" });
    // Frase + site centralizado
    doc.setFontSize(5.5);
    doc.setTextColor(170, 170, 170);
    doc.text("Sistema para gerenciamento de Ca\u00e7ambas", pw / 2, fy + 5, { align: "center" });
    doc.setFontSize(5);
    doc.text("www.controlacacambas.com.br", pw / 2, fy + 8.5, { align: "center" });
  }

  function drawPagoBox(x, cy, pago) {
    if (pago) {
      doc.setFillColor(50, 50, 50);
      doc.rect(x, cy + 0.2, 3, 3, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(5);
      doc.text("X", x + 0.7, cy + 2.5);
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(6);
      doc.setFont("helvetica", "bold");
      doc.text("PAGO", x + 4, cy + 2.7);
    } else {
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.2);
      doc.rect(x, cy + 0.2, 3, 3);
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      doc.text("A COBRAR", x + 4, cy + 2.7);
    }
  }

  function drawOpLine(cx, cy, label, p) {
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(label, cx, cy + 3);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(6);
    doc.text(p.cacamba, cx + 20, cy + 3);
    doc.text("Loc. " + p.loc, cx + 55, cy + 3);
    doc.text("MTR: " + p.mtr, cx + 80, cy + 3);
    drawPagoBox(cx + 110, cy, p.pago);
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    if (p.valor) doc.text(p.valor, mg + uw - 2, cy + 3, { align: "right" });
  }

  function drawParada(y, p, idx) {
    const isTroca = p.op === "TROCA";
    const blockH = isTroca ? 38 : 32;

    if (y + blockH > ph - 16) return null;

    // Fundo alternado suave
    if (idx % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(mg, y, uw, blockH, "F");
    }

    // Linha separadora topo
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.15);
    doc.line(mg, y, mg + uw, y);

    // Numero
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(60, 60, 60);
    doc.circle(mg + 5, y + 4, 2.8, "F");
    doc.text(String(p.num), mg + 5, y + 5, { align: "center" });

    // Operacao (texto simples, sem badge colorido)
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(7);
    doc.text(opLabel(p.op), mg + 10, y + 5);

    // Endereco (destaque — negrito e fonte maior)
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const endTxt = p.local.length > 120 ? p.local.substring(0, 120) + "..." : p.local;
    doc.text(endTxt, mg + 30, y + 5.5, { maxWidth: uw - 32 });

    // Cliente + Telefone
    let cy = y + 9;
    doc.setTextColor(30, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(p.cliente.substring(0, 55), mg + 4, cy + 3);
    if (p.tel) {
      doc.setTextColor(100, 100, 100);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.text("Tel: " + p.tel, mg + uw - 2, cy + 3, { align: "right" });
    }

    // Detalhes da operacao
    cy += 7;
    if (isTroca) {
      drawOpLine(mg + 4, cy, "RETIRAR:", p);
      cy += 5;
      // Colocar — nova locação
      doc.setTextColor(60, 60, 60);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.5);
      doc.text("COLOCAR:", mg + 4, cy + 3);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(6);
      doc.text(p.cacamba, mg + 24, cy + 3);
      if (p.novaLocId) doc.text("Loc. " + p.novaLocId, mg + 55, cy + 3);
      // Mostrar status de pagamento da locação filha
      drawPagoBox(mg + 110, cy, p.childPago);
    } else if (p.op === "RETIRADA") {
      drawOpLine(mg + 4, cy, "RETIRAR:", p);
    } else {
      drawOpLine(mg + 4, cy, "COLOCAR:", p);
    }

    // Linha inferior: INF + ATENDIDO + ASSINATURA
    cy = y + blockH - 6;
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(5.5);
    doc.text("OBS: _______________________________________________", mg + 4, cy + 2);

    // Checkbox ATENDIDO
    const atendido = p.status === "Atendido";
    if (atendido) {
      doc.setFillColor(50, 50, 50);
      doc.rect(mg + 88, cy - 0.5, 3, 3, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(5);
      doc.text("X", mg + 88.7, cy + 1.8);
    } else {
      doc.setDrawColor(180, 180, 180);
      doc.setLineWidth(0.2);
      doc.rect(mg + 88, cy - 0.5, 3, 3);
    }
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6);
    doc.text("ATENDIDO", mg + 93, cy + 1.8);

    // Assinatura
    doc.setDrawColor(180, 180, 180);
    doc.line(mg + 130, cy + 1.5, mg + uw - 2, cy + 1.5);
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(4.5);
    doc.text("ASSINATURA RESPONSAVEL", mg + 140, cy + 4);

    return y + blockH;
  }

  // Gerar paginas
  let y = mg;
  y = drawHeader(y);

  for (let i = 0; i < paradas.length; i++) {
    const result = drawParada(y, paradas[i], i);
    if (result === null) {
      drawFooter();
      doc.addPage();
      page++;
      totalPages++;
      y = mg;
      y = drawHeader(y);
      const r2 = drawParada(y, paradas[i], i);
      if (r2 !== null) y = r2;
    } else {
      y = result;
    }
  }

  drawFooter();
  doc.save("Itinerario_" + motorista.replace(/\s+/g, "_") + "_" + (itinerario.data || "").replace(/-/g, "") + ".pdf");
}

function StatusBadge({ label }) {
  const c = statusColor[label] || { bg: "#f0f0f0", text: "#555", dot: "#999" };
  return (
    <span style={{ background: c.bg, color: c.text, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 3, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", fontFamily: FONT }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

const inp = {
  width: "100%", padding: "7px 10px", border: "1px solid #ccc", borderRadius: 3,
  fontSize: 13, color: "#1a1a1a", outline: "none", boxSizing: "border-box",
  fontFamily: FONT, background: "#fff",
};

function SectionHeader({ icon, label }) {
  return (
    <div className="section-head" style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 14px", background: "#f7f7f7", border: "1px solid #e4e4e4", borderRadius: "3px 3px 0 0", marginTop: 18 }}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: "#444", textTransform: "uppercase", letterSpacing: .4 }}>{label}</span>
    </div>
  );
}

function SectionBody({ children }) {
  return (
    <div className="section-body" style={{ border: "1px solid #e4e4e4", borderTop: "none", borderRadius: "0 0 3px 3px", padding: "16px 14px", background: "#fff" }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="app-field">
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4, textTransform: "uppercase", letterSpacing: .3 }}>{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon = "📭", title, subtitle }) {
  return (
    <div style={{ textAlign:"center", padding:"48px 24px", color:"#aaa" }}>
      <div style={{ fontSize:38, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:14, fontWeight:700, color:"#888", marginBottom:4 }}>{title}</div>
      {subtitle && <div style={{ fontSize:12, color:"#bbb" }}>{subtitle}</div>}
    </div>
  );
}

// ─── Success Modal (Locação criada) ──────────────────────────────────────────
function SuccessModal({ title, message, detail, onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT }} onClick={onClose}>
      <div style={{ background:"#fff", borderRadius:12, padding:"32px 36px", maxWidth:400, width:"90%", textAlign:"center", boxShadow:"0 20px 60px rgba(0,0,0,.3)", animation:"fadeInScale .3s ease" }} onClick={e=>e.stopPropagation()}>
        <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px" }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div style={{ fontSize:20, fontWeight:800, color:"#1a1a1a", marginBottom:8 }}>{title || "Sucesso!"}</div>
        <div style={{ fontSize:14, color:"#555", marginBottom:6, lineHeight:1.5 }}>{message || "Operação realizada com sucesso."}</div>
        {detail && <div style={{ fontSize:16, fontWeight:700, color:"#1aae9e", marginBottom:18, padding:"8px 16px", background:"#f0faf8", borderRadius:6, display:"inline-block" }}>{detail}</div>}
        <div style={{ marginTop:18 }}>
          <button onClick={onClose} style={{ padding:"10px 40px", background:"linear-gradient(135deg, #1aae9e 0%, #148f82 100%)", color:"#fff", border:"none", borderRadius:6, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>OK</button>
        </div>
      </div>
      <style>{`@keyframes fadeInScale { from { opacity:0; transform:scale(.8); } to { opacity:1; transform:scale(1); } }`}</style>
    </div>
  );
}

function CheckGroup({ title, items, checked, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 8, borderBottom: "1px solid #eee", paddingBottom: 4 }}>{title}</div>
      {items.map(item => (
        <label key={item} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#444", marginBottom: 5, cursor: "pointer" }}>
          <input type="checkbox" checked={checked.includes(item)} onChange={() => onChange(item)} style={{ accentColor: "#1aae9e" }} />
          {item}
        </label>
      ))}
    </div>
  );
}

// ─── Large Modal ──────────────────────────────────────────────────────────────
function LargeModal({ title, subtitle, onClose, children, onSave, saveLabel = "Salvar", maxWidth = 860, extraFooter, cancelLabel = "Cancelar" }) {
  return (
    <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ background: "#fff", width: "100%", maxWidth: maxWidth, maxHeight: "94vh", overflowY: "auto", borderRadius: 4, boxShadow: "0 12px 48px rgba(0,0,0,.22)", fontFamily: FONT, display: "flex", flexDirection: "column" }}>
        <div className="modal-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 22px", borderBottom: "1px solid #e4e4e4", background: "#fff", position: "sticky", top: 0, zIndex: 10, flexShrink: 0 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a1a" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888", display: "flex", padding: 8, flexShrink: 0, minWidth: 32, minHeight: 32, alignItems: "center", justifyContent: "center" }}><IcoX /></button>
        </div>
        <div className="modal-body" style={{ padding: "0 22px 22px", flex: 1, overflowY: "auto" }}>{children}</div>
        <div className="modal-foot" style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 22px", borderTop: "1px solid #e4e4e4", background: "#fafafa", position: "sticky", bottom: 0, alignItems: "center", flexShrink: 0 }}>
          {cancelLabel !== null && <button onClick={onClose} style={{ padding: "7px 18px", background: "#fff", border: "1px solid #ccc", borderRadius: 3, fontSize: 13, cursor: "pointer", fontWeight: 600, color: "#555", fontFamily: FONT }}>{cancelLabel}</button>}
          {onSave && <button onClick={onSave} style={{ padding: "7px 20px", background: "#1aae9e", border: "none", borderRadius: 3, fontSize: 13, cursor: "pointer", fontWeight: 700, color: "#fff", fontFamily: FONT, display: "flex", alignItems: "center", gap: 6 }}>
            <IcoCheck /> {saveLabel}
          </button>}
          {extraFooter}
        </div>
      </div>
    </div>
  );
}

// ─── Cliente Selector Modal ───────────────────────────────────────────────────
function ClienteSelectorModal({ clientes, locacoes = [], onSelect, onClose }) {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const filtered = clientes.filter(c => {
    if (!search.trim()) return true;
    const s = norm(search);
    if (norm(c.nome).includes(s) || String(c.tel||"").includes(search) || String(c.cpfCnpj||c.cpf_cnpj||"").includes(search)) return true;
    const locsC = locacoes.filter(l => l.cliente === c.nome || l.clienteId === c.id || l.cliente_id === c.id);
    return locsC.some(l => norm(l.logradouro||l.end_logradouro).includes(s) || norm(l.bairro||l.end_bairro).includes(s) || norm(l.cidade||l.end_cidade).includes(s) || norm(typeof l.endereco === "string" ? l.endereco : "").includes(s));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safeP = Math.min(page, totalPages);
  const paged = filtered.slice((safeP - 1) * PER_PAGE, safeP * PER_PAGE);

  const confirm = () => {
    if (!sel) return;
    onSelect(sel);
    onClose();
  };

  const thSt = { padding:"9px 16px", textAlign:"left", fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #ebebeb" };
  const pgBtn = (n, active) => ({ background: active ? "#1a9e8f" : "#f5f5f5", color: active ? "#fff" : "#555", border:"none", borderRadius:3, padding:"5px 10px", fontWeight:600, fontSize:12, cursor:"pointer", minWidth:32 });

  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:950, maxHeight:"90vh", display:"flex", flexDirection:"column", borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.22)", fontFamily:FONT }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 22px", borderBottom:"1px solid #e4e4e4" }}>
          <span style={{ fontWeight:700, fontSize:16, color:"#1a1a1a" }}>Clientes</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:18 }}>✕</button>
        </div>
        {/* Search toolbar */}
        <div style={{ padding:"12px 22px", borderBottom:"1px solid #f0f0f0", display:"flex", gap:10 }}>
          <input
            autoFocus
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Pesquisar por ..."
            style={{ ...inp, flex:1 }}
          />
          <button style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>
            Pesquisar
          </button>
        </div>
        {/* Table */}
        <div style={{ flex:1, overflowY:"auto" }}>
          <table className="client-selector-tbl" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead style={{ background:"#f9f9f9", position:"sticky", top:0 }}>
              <tr>
                <th style={thSt}>Cliente</th>
                <th style={thSt}>Tipo</th>
                <th style={thSt}>Telefone</th>
                <th style={thSt}>Endereço</th>
                <th style={thSt}>Criado em</th>
                <th style={{ ...thSt, width:40 }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr><td colSpan={6}><EmptyState icon="👥" title="Nenhum cliente encontrado" subtitle="Cadastre clientes para selecionar" /></td></tr>
              ) : paged.map(c => {
                const isSelected = sel?.id === c.id;
                const addr = c.enderecos && c.enderecos[0] ? [c.enderecos[0].logradouro, c.enderecos[0].numero, c.enderecos[0].bairro, c.enderecos[0].cidade, c.enderecos[0].uf].filter(Boolean).join(", ") : "—";
                return (
                  <tr key={c.id} onClick={() => setSel(c)}
                    style={{ borderBottom:"1px solid #f4f4f4", cursor:"pointer", background: isSelected ? "#fff8f4" : "", transition:"background .12s" }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background="#fafafa"; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background=""; }}>
                    <td style={{ padding:"10px 16px" }}>
                      <div style={{ fontWeight:700, color:isSelected?"#1aae9e":"#1a9e8f", fontSize:13 }}>{c.nome}</div>
                      {c.cpfCnpj && <div style={{ fontSize:11, color:"#888" }}>CPF/CNPJ: {c.cpfCnpj}</div>}
                    </td>
                    <td style={{ padding:"10px 16px", fontSize:12, color:"#555", textTransform:"uppercase", fontWeight:600 }}>{c.tipo === "PJ" ? "PESSOA JURÍDICA" : "PESSOA FÍSICA"}</td>
                    <td style={{ padding:"10px 16px", fontSize:12, color:"#555" }}>{c.tel || "—"}</td>
                    <td style={{ padding:"10px 16px", fontSize:11, color:"#888", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{addr}</td>
                    <td style={{ padding:"10px 16px", fontSize:12, color:"#888" }}>{c.criadoEm || "—"}</td>
                    <td style={{ padding:"10px 16px", textAlign:"center" }}>
                      {isSelected && <span style={{ color:"#2ecc71", fontSize:18 }}>✅</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div style={{ padding:"10px 22px", borderTop:"1px solid #f0f0f0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, color:"#888" }}>{Math.min((safeP - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(safeP * PER_PAGE, filtered.length)} de {filtered.length} clientes</span>
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safeP <= 1} style={{ ...pgBtn(0, false), opacity: safeP <= 1 ? .4 : 1 }}></button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let n;
              if (totalPages <= 7) n = i + 1;
              else if (safeP <= 4) n = i + 1;
              else if (safeP >= totalPages - 3) n = totalPages - 6 + i;
              else n = safeP - 3 + i;
              return <button key={n} onClick={() => setPage(n)} style={pgBtn(n, n === safeP)}>{n}</button>;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safeP >= totalPages} style={{ ...pgBtn(0, false), opacity: safeP >= totalPages ? .4 : 1 }}></button>
          </div>
        </div>
        {/* Footer */}
        <div className="modal-footer-btns" style={{ padding:"12px 22px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"flex-end", gap:10 }}>
          <button onClick={onClose} style={{ background:"#555", color:"#fff", border:"none", borderRadius:3, padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>
            ✕ Cancelar
          </button>
          {sel && (
            <button onClick={confirm} style={{ background:"#2ecc71", color:"#fff", border:"none", borderRadius:3, padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>
              Selecionar: {sel.nome}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Map Confirm Modal ─────────────────────────────────────────────────────────
function MapConfirmModal({ address, onClose, onConfirm, cidadeHint, ufHint }) {
  const [query, setQuery] = useState(address || "");
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayAddr, setDisplayAddr] = useState(address || "");
  const [addrDetails, setAddrDetails] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const refineCep = async (details) => {
    if (!details.logradouro || !details.cidade || !details.uf) return details;
    try {
      const uf = details.uf.length === 2 ? details.uf : "";
      if (!uf) return details;
      const removeAccents = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const cidade = removeAccents(details.cidade);
      const tryViaCep = async (c, l) => {
        try {
          const r = await fetch("https://viacep.com.br/ws/" + encodeURIComponent(uf) + "/" + encodeURIComponent(c) + "/" + encodeURIComponent(l) + "/json/");
          if (r.ok) { const d = await r.json(); if (Array.isArray(d) && d.length > 0) return d; }
        } catch(e) {}
        return null;
      };
      const words = details.logradouro.split(" ");
      const searches = [
        words.slice(0, 3).join(" "),
        words.length > 1 ? words.slice(1).join(" ") : null,
        words.slice(0, 2).join(" "),
        words.length > 1 ? words[words.length - 1] : null
      ].filter(Boolean);
      let data = null;
      for (const s of searches) {
        data = await tryViaCep(cidade, s);
        if (!data) data = await tryViaCep(details.cidade, s);
        if (data) break;
      }
      if (data && data.length > 0) {
        const logrLower = removeAccents(details.logradouro.toLowerCase());
        const best = data.find(d => removeAccents((d.logradouro||"").toLowerCase()).includes(logrLower.split(",")[0].trim())) || data.find(d => logrLower.includes(removeAccents((d.logradouro||"").toLowerCase().split(" - ")[0]))) || data[0];
        details.cep = best.cep || details.cep;
        if (!details.bairro && best.bairro) details.bairro = best.bairro;
      }
    } catch(e) {}
    return details;
  };

  const formatAddrDisplay = (d) => {
    const parts = [d.logradouro, d.bairro ? "(" + d.bairro + ")" : "", d.cidade, d.uf ? "- " + d.uf : "", d.cep].filter(Boolean);
    return parts.join(", ") + ", Brasil";
  };

  const parseMapboxResult = (feat) => {
    if (!feat) return null;
    const ctx = feat.context || [];
    const get = (id) => { const c = ctx.find(x => x.id && x.id.startsWith(id)); return c ? c.text : ""; };
    const shortCode = (id) => { const c = ctx.find(x => x.id && x.id.startsWith(id)); return c && c.short_code ? c.short_code.replace("BR-","").toUpperCase() : ""; };
    return {
      logradouro: (feat.text || "") + (feat.address ? ", " + feat.address : ""),
      bairro: get("neighborhood") || get("locality") || "",
      cidade: get("place") || "",
      uf: shortCode("region") || get("region") || "",
      cep: get("postcode") || ""
    };
  };

  // Limpar endereço para geocodificação
  const cleanAddr = (raw) => {
    let s = raw.trim();
    s = s.replace(/\s*\([^)]*\)\s*/g, " ");  // Remove (RESIDENCIAL...)
    s = s.replace(/[Nn][º°]:?\s*/g, " ");     // Remove Nº:
    s = s.replace(/\d{5}-?\d{3}/g, "");        // Remove CEP
    s = s.replace(/,\s*,/g, ",").replace(/\s{2,}/g, " ").replace(/,\s*$/,"").trim();
    return s;
  };

  // Extrair cidade do endereço para usar como proximity
  const extractCityForProximity = async (addr) => {
    // 1) Se temos cidadeHint e ufHint (vindos do formulário/CEP), usar direto — mais confiável
    if (cidadeHint && ufHint) {
      try {
        const r = await fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(cidadeHint + ", " + ufHint + ", Brasil") + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&types=place&language=pt");
        const d = await r.json();
        if (d.features && d.features.length > 0) {
          const [lng, lat] = d.features[0].center;
          return lng + "," + lat;
        }
      } catch(e) {}
    }
    // 2) Tentar extrair do texto: "Cidade - UF" (com hífen)
    let match = addr.match(/,\s*([A-Za-zÀ-ú\s]+?)\s*-\s*[A-Z]{2}/);
    // 3) Tentar formato: "Cidade, UF" (com vírgula, UF 2 letras no final)
    if (!match) match = addr.match(/([A-Za-zÀ-ú\s]{2,}),\s*([A-Z]{2})\s*(?:,|$)/);
    if (match) {
      const city = match[1].trim();
      const uf = match[2] ? match[2].trim() : "";
      const q = city + (uf ? ", " + uf : "") + ", Brasil";
      try {
        const r = await fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&types=place&language=pt");
        const d = await r.json();
        if (d.features && d.features.length > 0) {
          const [lng, lat] = d.features[0].center;
          return lng + "," + lat;
        }
      } catch(e) {}
    }
    return null;
  };

  const geocode = async (addr) => {
    try {
      const proximity = await extractCityForProximity(addr);
      const proxParam = proximity ? "&proximity=" + proximity : "";
      const cleaned = cleanAddr(addr);

      // Montar lista de queries para tentar
      const queries = [
        cleaned,                          // Endereço limpo
        cleaned + ", Brasil",             // Com Brasil
        addr,                             // Original
      ];

      // Helper: verificar se feature bate com a cidade esperada
      const matchesCidade = (f) => {
        if (!cidadeHint) return true;
        const ctx = f.context || [];
        const place = ctx.find(c => c.id && c.id.startsWith("place"));
        if (place && place.text && place.text.toLowerCase().includes(cidadeHint.toLowerCase())) return true;
        if (f.place_name && f.place_name.toLowerCase().includes(cidadeHint.toLowerCase())) return true;
        return false;
      };

      let feat = null;
      for (const q of queries) {
        if (feat) break;
        const url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=5&language=pt&types=address,poi,place,neighborhood,locality" + proxParam;
        const r = await fetch(url);
        const d = await r.json();
        if (d.features && d.features.length > 0) {
          // Priorizar resultados que batem com a cidade esperada
          const cityMatch = cidadeHint ? d.features.find(f => f.relevance >= 0.4 && matchesCidade(f)) : null;
          feat = cityMatch || d.features.find(f => f.relevance >= 0.5 && f.place_type?.includes("address")) || d.features.find(f => f.relevance >= 0.4) || d.features[0];
        }
      }

      // Se o resultado não bate com a cidade esperada, buscar centro da cidade como fallback
      if (feat && cidadeHint && !matchesCidade(feat)) {
        const cityQ = cidadeHint + (ufHint ? ", " + ufHint : "") + ", Brasil";
        try {
          const cr = await fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(cityQ) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&types=place&language=pt");
          const cd = await cr.json();
          if (cd.features && cd.features.length > 0) {
            console.log("[MapConfirm] Resultado não bate com " + cidadeHint + ", usando centro da cidade");
            feat = cd.features[0];
          }
        } catch(e) {}
      }

      if (feat) {
        const [lng, lat] = feat.center;
        const addrObj = parseMapboxResult(feat);
        if (feat.properties && feat.properties.category && feat.text) {
          addrObj.nome = feat.text;
          if (!addrObj.logradouro || !addrObj.logradouro.includes(feat.text)) {
            addrObj.logradouro = feat.text + (feat.properties.address ? ", " + feat.properties.address : addrObj.logradouro ? " - " + addrObj.logradouro : "");
          }
        }
        setAddrDetails(addrObj);
        const displayParts = [feat.place_name || formatAddrDisplay(addrObj)];
        return { lat, lng, display: displayParts[0], address: addrObj };
      }
    } catch(e) { console.warn("[MapConfirm] geocode error:", e); }
    return null;
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/" + lng + "," + lat + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&language=pt");
      const data = await r.json();
      if (data.features && data.features.length > 0) {
        const details = parseMapboxResult(data.features[0]);
        setAddrDetails(details);
        return formatAddrDisplay(details);
      }
    } catch(e) {}
    return "";
  };

  const searchAddress = async () => {
    if (!query.trim()) return;
    setLoading(true);
    const result = await geocode(query);
    setLoading(false);
    if (result) {
      setCoords({ lat: result.lat, lng: result.lng });
      setDisplayAddr(result.display);
      setQuery(result.display);
      if (result.address) setAddrDetails(result.address);
      if (mapInstanceRef.current && markerRef.current) {
        const latlng = L.latLng(result.lat, result.lng);
        mapInstanceRef.current.setView(latlng, 17, { animate: true });
        markerRef.current.setLatLng(latlng);
      }
    } else {
      alert("Endereço não encontrado. Tente ser mais específico.");
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;
    const map = L.map(mapContainerRef.current, { zoomControl: true }).setView([-23.55, -46.63], 13);
    L.tileLayer("https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN, {
      attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a>',
      tileSize: 512, zoomOffset: -1, maxZoom: 22
    }).addTo(map);

    const cacambaIcon = L.divIcon({
      className: '',
      iconSize: [64, 48],
      iconAnchor: [32, 48],
      html: '<div style="filter:drop-shadow(0 3px 6px rgba(0,0,0,0.5));cursor:grab"><svg width="64" height="48" viewBox="40 20 380 160" xmlns="http://www.w3.org/2000/svg"><polygon points="110,30 410,30 350,80 50,80" fill="#1A5276"/><polygon points="50,80 110,30 110,160 50,160" fill="#1F618D"/><polygon points="50,80 350,80 350,160 50,160" fill="#2874A6"/><polygon points="350,80 410,30 410,160 350,160" fill="#1F618D"/><line x1="130" y1="80" x2="130" y2="160" stroke="#1F618D" stroke-width="4"/><line x1="210" y1="80" x2="210" y2="160" stroke="#1F618D" stroke-width="4"/><line x1="290" y1="80" x2="290" y2="160" stroke="#1F618D" stroke-width="4"/><text x="200" y="132" font-family="Arial,sans-serif" font-weight="bold" font-size="30" fill="#fff" text-anchor="middle" letter-spacing="2">CONTROLA</text></svg></div>',
    });
    const marker = L.marker([-23.55, -46.63], { draggable: true, icon: cacambaIcon }).addTo(map);
    marker.on("dragend", async function() {
      const pos = marker.getLatLng();
      setCoords({ lat: pos.lat, lng: pos.lng });
      const addr = await reverseGeocode(pos.lat, pos.lng);
      if (addr) { setDisplayAddr(addr); setQuery(addr); }
    });

    mapInstanceRef.current = map;
    markerRef.current = marker;

    // Geocode initial address
    if (address) {
      geocode(address).then(result => {
        if (result) {
          const latlng = L.latLng(result.lat, result.lng);
          map.setView(latlng, 17);
          marker.setLatLng(latlng);
          setCoords({ lat: result.lat, lng: result.lng });
          setDisplayAddr(result.display);
          setQuery(result.display);
          if (result.address) setAddrDetails(result.address);
        }
      });
    }

    return () => { map.remove(); mapInstanceRef.current = null; };
  }, []);

  const handleConfirm = () => {
    if (onConfirm && coords) {
      const refined = addrDetails ? {...addrDetails} : null;
      // Fechar modal imediatamente, refinar CEP em background
      onConfirm(coords, displayAddr, refined);
      if (refined) {
        refineCep(refined).then(r => {
          if (r && r.cep && onConfirm) onConfirm(coords, displayAddr, r);
        }).catch(() => {});
      }
    }
    onClose();
  };

  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:1200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:960, borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.3)", fontFamily:FONT, overflow:"hidden" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 22px", borderBottom:"1px solid #e4e4e4" }}>
          <span style={{ fontWeight:700, fontSize:15, color:"#1a1a1a" }}>📍 Confirmar Localização</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:18 }}>✕</button>
        </div>
        <div style={{ padding:"14px 22px", borderBottom:"1px solid #f0f0f0", display:"flex", gap:10 }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key==="Enter" && searchAddress()}
            style={{ ...inp, flex:1 }} placeholder="Digite o endereço para buscar..." />
          <button onClick={searchAddress} disabled={loading} style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT, opacity:loading?.6:1 }}>
            {loading ? "Buscando..." : "Pesquisar"}
          </button>
        </div>
        {displayAddr && (
          <div style={{ padding:"8px 22px", background:"#f0f8ff", fontSize:12, color:"#333", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontWeight:700, color:"#1aae9e" }}>📍</span> {displayAddr}
            {coords && <span style={{ marginLeft:"auto", fontSize:10, color:"#888" }}>({coords.lat.toFixed(6)}, {coords.lng.toFixed(6)})</span>}
          </div>
        )}
        <div className="map-confirm-container" style={{ height:420, position:"relative" }}>
          <div ref={mapContainerRef} style={{ width:"100%", height:"100%" }} />
          <div style={{ position:"absolute", bottom:10, left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,.7)", color:"#fff", padding:"6px 14px", borderRadius:20, fontSize:11, fontWeight:600, pointerEvents:"none", zIndex:999 }}>
            Arraste o pin para ajustar a localização exata
          </div>
        </div>
        <div style={{ padding:"12px 22px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"flex-end", gap:10 }}>
          <button onClick={onClose} style={{ background:"#888", color:"#fff", border:"none", borderRadius:3, padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>✕ Cancelar</button>
          <button onClick={handleConfirm} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>Confirmar Localização</button>
        </div>
      </div>
    </div>
  );
}

// ─── Endereço Selector Modal ──────────────────────────────────────────────────
function EnderecoSelectorModal({ enderecos, onSelect, onClose }) {
  const [sel, setSel] = useState(enderecos.length === 1 ? 0 : null);
  const confirm = () => { if (sel === null) return; onSelect(enderecos[sel]); onClose(); };
  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:1200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:760, borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.3)", fontFamily:FONT, overflow:"hidden" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"13px 20px", borderBottom:"1px solid #e4e4e4" }}>
          <span style={{ fontWeight:700, fontSize:15 }}>Selecione um endereço do cliente</span>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:18 }}>✕</button>
        </div>
        <div style={{ maxHeight:360, overflowY:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead style={{ background:"#f9f9f9", position:"sticky", top:0 }}>
              <tr>
                {["Nome do Endereço","CEP","Endereço",""].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 16px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {enderecos.map((e, i) => {
                const isSel = sel === i;
                const addr = [e.logradouro, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(" - ");
                const nomeEnd = e.nomeEndereco || e.nome_endereco || "";
                return (
                  <tr key={i} onClick={() => setSel(i)} style={{ cursor:"pointer", borderBottom:"1px solid #f4f4f4", background:isSel?"#f0f4ff":"" }}>
                    <td style={{ padding:"12px 16px", fontWeight:600, fontSize:12, color:nomeEnd?"#1a1a1a":"#bbb" }}>{nomeEnd || "—"}</td>
                    <td style={{ padding:"12px 16px", color:"#1aae9e", fontWeight:600, fontSize:12 }}>{e.cep || "—"}</td>
                    <td style={{ padding:"12px 16px" }}>{addr}</td>
                    <td style={{ padding:"12px 16px", textAlign:"center", width:40 }}>
                      {isSel && <span style={{ color:"#1aae9e", fontSize:16 }}>✅</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding:"12px 20px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"flex-end", gap:10 }}>
          <button onClick={onClose} style={{ background:"#888", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>✕ Cancelar</button>
          {sel !== null && (
            <button onClick={confirm} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>Usar este endereço</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Endereço de FATURAMENTO do cliente (opcional; NÃO é endereço de obra) ───
// Campos estruturados em `clientes`: fat_cep, fat_uf, fat_cidade, fat_bairro,
// fat_logradouro, fat_numero, fat_complemento. Frontend usa camelCase (fatCep…).
const FAT_VAZIO = { fatCep:"", fatUf:"", fatCidade:"", fatBairro:"", fatLogradouro:"", fatNumero:"", fatComplemento:"" };
// Lê do objeto cliente (camel + snake) → estado do form
const fatFromCliente = (c) => {
  if (!c) return { ...FAT_VAZIO };
  return {
    fatCep:         c.fatCep         || c.fat_cep         || "",
    fatUf:          c.fatUf          || c.fat_uf          || "",
    fatCidade:      c.fatCidade      || c.fat_cidade      || "",
    fatBairro:      c.fatBairro      || c.fat_bairro      || "",
    fatLogradouro:  c.fatLogradouro  || c.fat_logradouro  || "",
    fatNumero:      c.fatNumero      || c.fat_numero      || "",
    fatComplemento: c.fatComplemento || c.fat_complemento || "",
  };
};
// Formata "Logradouro, Nº, Compl — Bairro — Cidade/UF — CEP". Vazio → "".
const fmtEnderecoFaturamento = (c) => {
  const f = fatFromCliente(c);
  const linha1 = [f.fatLogradouro, f.fatNumero, f.fatComplemento].filter(Boolean).join(", ");
  const cidUf = f.fatCidade ? f.fatCidade + (f.fatUf ? "/" + f.fatUf : "") : (f.fatUf || "");
  const cep = f.fatCep ? "CEP " + f.fatCep : "";
  return [linha1, f.fatBairro, cidUf, cep].filter(Boolean).join(" — ");
};
// Caixa de formulário reutilizável (NovoClienteModal e aba "Cadastrar Cliente" da locação)
function EnderecoFaturamentoBox({ fat, setFat, compact }) {
  const [loading, setLoading] = useState(false);
  const s = (k, v) => setFat(p => ({ ...p, [k]: v }));
  const fetchCepFat = async (cep) => {
    const clean = String(cep || "").replace(/\D/g, "");
    if (clean.length !== 8) return;
    setLoading(true);
    try {
      let d = null;
      try { const r = await fetch("https://viacep.com.br/ws/" + clean + "/json/"); if (r.ok) { d = await r.json(); if (d.erro) d = null; } } catch(e) {}
      if (!d) { try { const r2 = await fetch("https://brasilapi.com.br/api/cep/v2/" + clean); if (r2.ok) { const b = await r2.json(); d = { logradouro: b.street||"", bairro: b.neighborhood||"", localidade: b.city||"", uf: b.state||"" }; } } catch(e) {} }
      if (d) { s("fatLogradouro", d.logradouro||""); s("fatBairro", d.bairro||""); s("fatCidade", d.localidade||""); s("fatUf", d.uf||""); }
    } catch(e) {}
    setLoading(false);
  };
  const pad = compact ? "12px 14px" : 16;
  return (
    <div style={{ border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden", marginTop:14 }}>
      <div style={{ padding:"10px 16px", background:"#fafafa", borderBottom:"1px solid #e8e8e8", fontWeight:700, fontSize:13 }}>
        Endereço de Faturamento <span style={{ fontWeight:400, color:"#aaa", fontSize:12 }}>(Opcional — endereço fiscal/cobrança, não é endereço de obra)</span>
      </div>
      <div style={{ padding: pad }}>
        <div style={{ display:"grid", gridTemplateColumns:"150px 90px 1fr 1fr", gap:10, marginBottom:10 }}>
          <Field label="CEP"><input style={{ ...inp, background: loading ? "#f5f5f5" : inp.background }} value={fat.fatCep} onChange={e => { const v = formatCep(e.target.value); s("fatCep", v); fetchCepFat(v); }} placeholder="00000-000" /></Field>
          <Field label="UF"><input style={inp} value={fat.fatUf} maxLength={2} onChange={e => s("fatUf", e.target.value.toUpperCase())} /></Field>
          <Field label="Cidade"><input style={inp} value={fat.fatCidade} onChange={e => s("fatCidade", e.target.value)} /></Field>
          <Field label="Bairro"><input style={inp} value={fat.fatBairro} onChange={e => s("fatBairro", e.target.value)} /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 110px 1fr", gap:10 }}>
          <Field label="Logradouro"><input style={inp} value={fat.fatLogradouro} onChange={e => s("fatLogradouro", e.target.value)} placeholder="Rua, Avenida..." /></Field>
          <Field label="Número"><input style={inp} value={fat.fatNumero} onChange={e => s("fatNumero", e.target.value)} /></Field>
          <Field label="Complemento"><input style={inp} value={fat.fatComplemento} onChange={e => s("fatComplemento", e.target.value)} placeholder="Sala, Bloco..." /></Field>
        </div>
      </div>
    </div>
  );
}

// ─── Novo Cliente Inline (for use inside NovaLocacaoModal) ───────────────────
function NovoClienteInline({ onSaved }) {
  const [form, setForm] = useState({ nome:"", tipo:"PF", cpfCnpj:"", tel:"", tel2:"", email:"", obs:"" });
  const [fat, setFat] = useState({ ...FAT_VAZIO });
  const [enderecos, setEnderecos] = useState([]);
  const [newEnd, setNewEnd] = useState({ cep:"", uf:"", cidade:"", logradouro:"", numero:"", complemento:"", bairro:"", nomeEndereco:"", pagamento:"" });
  const [endErrors, setEndErrors] = useState({});
  const [mapModal, setMapModal] = useState(false);
  const sf = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const se = (k,v) => { setNewEnd(p => ({ ...p, [k]:v })); setEndErrors(e => ({ ...e, [k]: false })); };
  const fetchCep = async (cep) => {
    const c = cep.replace(/\D/g,""); if(c.length!==8) return;
    try {
      let d = null;
      try { const r = await fetch("https://viacep.com.br/ws/"+c+"/json/"); if(r.ok){ d = await r.json(); if(d.erro) d = null; } } catch(e){}
      if(!d) { try { const r2 = await fetch("https://brasilapi.com.br/api/cep/v2/"+c); if(r2.ok){ const b = await r2.json(); d = { logradouro: b.street||"", bairro: b.neighborhood||"", localidade: b.city||"", uf: b.state||"" }; } } catch(e){} }
      if(d){se("logradouro",d.logradouro||"");se("bairro",d.bairro||"");se("cidade",d.localidade||"");se("uf",d.uf||"");}
    } catch(e){}
  };
  const addEnd = () => {
    const errs = {};
    if (!newEnd.logradouro) errs.logradouro = true;
    if (!newEnd.numero)     errs.numero     = true;
    if (!newEnd.cidade)     errs.cidade     = true;
    if (!newEnd.uf)         errs.uf         = true;
    if (Object.keys(errs).length > 0) { setEndErrors(errs); return; }
    setEnderecos(p=>[...p,{...newEnd,id:Date.now()}]);
    setNewEnd({ cep:"", uf:"", cidade:"", logradouro:"", numero:"", complemento:"", bairro:"", nomeEndereco:"", pagamento:"" });
    setEndErrors({});
  };
  const save = () => {
    if(!form.nome) { alert("Nome do cliente é obrigatório."); return; }
    if(form.tipo==="PJ" && !form.cpfCnpj) { alert("CNPJ obrigatório para Pessoa Jurídica."); return; }
    if(enderecos.length === 0) { alert("Adicione pelo menos um endereço completo antes de salvar."); return; }
    if(enderecos.some(e => !e.logradouro || !e.logradouro.trim())) { alert("Todos os endereços precisam ter um logradouro (rua) válido."); return; }
    onSaved({ ...form, ...fat, enderecos });
  };
  return (
    <div style={{ padding:"4px 0 16px" }}>
      <div style={{ background:"#f0f4ff", border:"1px solid #c7d7fd", borderRadius:4, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#1aae9e", fontWeight:600 }}>
        📌 Preencha os dados do novo cliente. Ao salvar, ele será adicionado à sua base e selecionado automaticamente na locação.
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 200px", gap:12, marginBottom:12 }}>
        <Field label="Nome do Cliente *"><input style={inp} value={form.nome} onChange={e=>sf("nome",e.target.value)} /></Field>
        <Field label={form.tipo==="PJ"?"CNPJ *":"CPF (Opcional)"}><input style={inp} value={form.cpfCnpj} onChange={e=>{const raw=e.target.value.replace(/\D/g,"").slice(0,14); sf("cpfCnpj",formatCpfCnpj(raw));}} placeholder={form.tipo==="PJ"?"00.000.000/0001-00":"000.000.000-00"} /></Field>
        <Field label="Tipo Pessoa"><select style={{...inp,appearance:"none"}} value={form.tipo} onChange={e=>sf("tipo",e.target.value)}><option value="PF">Pessoa Física</option><option value="PJ">Pessoa Jurídica</option></select></Field>
        <Field label="Telefone"><input style={inp} value={form.tel} onChange={e=>sf("tel",formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
        <Field label="E-mail"><input style={inp} value={form.email} onChange={e=>sf("email",e.target.value)} /></Field>
        <Field label="Observação"><input style={inp} value={form.obs} onChange={e=>sf("obs",e.target.value)} /></Field>
      </div>
      <div style={{ fontWeight:700, fontSize:12, color:"#555", marginBottom:10, textTransform:"uppercase", borderTop:"1px solid #eee", paddingTop:14 }}>
        Endereços <span style={{ color:"#e74c3c", fontWeight:600 }}>* Obrigatório — ao menos 1</span>
      </div>
      <div style={{ background:"#fafbff", border:"1px solid #e8ecf4", borderRadius:6, padding:"14px 16px", marginBottom:10 }}>
        <div style={{ display:"grid", gridTemplateColumns:"130px 80px 1fr 1fr", gap:10, marginBottom:10 }}>
          <Field label="CEP"><input style={inp} value={newEnd.cep} onChange={e=>{const v=formatCep(e.target.value);se("cep",v);fetchCep(v);}} placeholder="00000-000" /></Field>
          <Field label={<span>UF <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.uf?"#e74c3c":inp.borderColor, background: endErrors.uf?"#fff5f5":inp.background}} value={newEnd.uf} onChange={e=>se("uf",e.target.value)} /></Field>
          <Field label={<span>Cidade <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.cidade?"#e74c3c":inp.borderColor, background: endErrors.cidade?"#fff5f5":inp.background}} value={newEnd.cidade} onChange={e=>se("cidade",e.target.value)} /></Field>
          <Field label="Bairro"><input style={inp} value={newEnd.bairro} onChange={e=>se("bairro",e.target.value)} /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 100px 1fr", gap:10, marginBottom:12 }}>
          <Field label={<span>Logradouro <span style={{color:"#e74c3c"}}>*</span></span>}><AddressAutocomplete style={{...inp, borderColor: endErrors.logradouro?"#e74c3c":inp.borderColor, background: endErrors.logradouro?"#fff5f5":inp.background}} value={newEnd.logradouro} onChange={v=>se("logradouro",v)} cidadeHint={newEnd.cidade} ufHint={newEnd.uf} placeholder="Rua, Avenida ou nome do local..." onSelect={s => { se("logradouro", s.logradouro); if(s.numero) se("numero", s.numero); if(s.bairro) se("bairro", s.bairro); if(s.cidade) se("cidade", s.cidade); if(s.uf) se("uf", s.uf); if(s.cep) se("cep", s.cep); }} /></Field>
          <Field label={<span>Número <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.numero?"#e74c3c":inp.borderColor, background: endErrors.numero?"#fff5f5":inp.background}} value={newEnd.numero} onChange={e=>se("numero",e.target.value)} /></Field>
          <Field label="Complemento"><input style={inp} value={newEnd.complemento} onChange={e=>se("complemento",e.target.value)} placeholder="Apto, Bloco..." /></Field>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <button type="button" onClick={() => setMapModal(true)} style={{ padding:"8px 14px", background:"none", border:"1px solid #1a9e8f", borderRadius:4, color:"#1a9e8f", fontSize:12, cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", fontFamily:FONT }}>📍 Confirmar no Mapa</button>
          <button onClick={addEnd} style={{ padding:"8px 18px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:4, cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:FONT }}>+ Adicionar Endereço</button>
        </div>
      </div>
      {enderecos.length > 0 && <div style={{ marginBottom:12, fontSize:12, color:"#555" }}>{enderecos.map((e,i)=><div key={i} style={{ padding:"5px 10px", background:"#f5f5f5", borderRadius:3, marginBottom:4 }}>📍 {[e.logradouro,e.numero,e.bairro,e.cidade,e.uf].filter(Boolean).join(", ")}</div>)}</div>}
      <EnderecoFaturamentoBox fat={fat} setFat={setFat} compact />
      <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12 }}>
        <button onClick={save} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 22px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>Salvar Cliente e Usar na Locação</button>
      </div>
      {mapModal && <MapConfirmModal address={[newEnd.logradouro, newEnd.numero, newEnd.bairro, newEnd.cidade, newEnd.uf].filter(Boolean).join(", ")} cidadeHint={newEnd.cidade} ufHint={newEnd.uf} onClose={() => setMapModal(false)} onConfirm={(coords, displayAddr, addrDetails) => { if (addrDetails) { if (addrDetails.cep) se("cep", addrDetails.cep.replace(/\D/g,"").replace(/^(\d{5})(\d{3})$/,"$1-$2")); if (addrDetails.logradouro) { const parts = addrDetails.logradouro.split(","); se("logradouro", parts[0].trim()); if (parts[1]) se("numero", parts[1].trim()); } if (addrDetails.bairro) se("bairro", addrDetails.bairro); if (addrDetails.cidade) se("cidade", addrDetails.cidade); if (addrDetails.uf) se("uf", addrDetails.uf); } }} />}
    </div>
  );
}

// ─── Nova Locação ─────────────────────────────────────────────────────────────
const CLASSE_A = ["Argamassa","Concreto","Material Asfáltico","Material Cerâmico","Pedra","Solo","Vegetação"];
const CLASSE_B = ["Gesso","Madeira","Metal","Papel/Papelão","Plástico","Vidro"];
const CLASSE_C = ["Materiais Não Recicláveis / Outros"];
const CLASSE_D = ["Amianto","Óleos","Solventes","Tintas"];

function NovaLocacaoModal({ onClose, onSave, clientes, locacoes=[], motoristas, cacambas, tiposCacamba=[], onClienteCreated, onEnderecoAdded, apiFetch }) {
  const [innerTab, setInnerTab] = useState("locacao");
  const [form, setForm] = useState({
    cliente: "", clienteId: null, tipoPessoa: "", cpfCnpj: "", tags: "",
    contatoNome: "", contatoTel: "", contatoEmail: "", contatoCargo: "",
    cep: "", uf: "", cidade: "", logradouro: "", numero: "", complemento: "", bairro: "", nomeEndereco: "",
    dataSolicitacao: hojeLocal(), dataEntrega: "", horaEntrega: "", dataRetirada: "",
    tipoCacamba: "", cacamba: "", volumeCacamba: "", mtr: "", gerarMtr: false,
    classeA: [], classeB: [], classeC: [], classeD: [],
    outrosResiduos: "", precoLocacao: "", observacoes: "", pagamento: "", prazoPagamento: "",
    diariaExtra: "",
  });
  const [lancamentos, setLancamentos] = useState([]);
  const [cepLoading, setCepLoading]   = useState(false);
  const [clienteModal, setClienteModal]     = useState(false);
  const [mapModal, setMapModal]             = useState(false);
  const [enderecoModal, setEnderecoModal]   = useState(false);
  const [clienteEnderecos, setClienteEnderecos] = useState([]);
  const [errors, setErrors] = useState({});
  const [multiploModal, setMultiploModal] = useState(false);
  const [qtdeMultiplo, setQtdeMultiplo] = useState("");
  const [cacambasMultiplo, setCacambasMultiplo] = useState([]); // caçamba escolhida por locação no modo múltiplo
  const [validacaoPopup, setValidacaoPopup] = useState(null);
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(e => ({ ...e, [k]: false })); };

  // Pré-preencher diária extra com o padrão do tenant (empresa_config.diaria_extra_padrao)
  useEffect(() => {
    let vivo = true;
    if (!apiFetch) return undefined;
    apiFetch("/api/config").then(cfg => {
      if (!vivo) return;
      // Aceita DECIMAL string ("30.00"), número ou valor com vírgula ("30,00")
      const raw = cfg && cfg.diaria_extra_padrao != null ? String(cfg.diaria_extra_padrao).replace(",", ".") : "";
      const dx = parseFloat(raw);
      if (dx > 0) setForm(p => (p.diariaExtra === "" || p.diariaExtra == null) ? { ...p, diariaExtra: String(dx) } : p);
    }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const applyEndereco = (e) => {
    set("cep", e.cep || ""); set("logradouro", e.logradouro || "");
    set("numero", e.numero || ""); set("bairro", e.bairro || "");
    set("cidade", e.cidade || ""); set("uf", e.uf || "SP");
    set("complemento", e.complemento || "");
  };

  const selectCliente = (c) => {
    set("cliente", c.nome);
    set("clienteId", c.id || null);
    set("tipoPessoa", c.tipo === "PJ" ? "Pessoa Jurídica" : "Pessoa Física");
    set("cpfCnpj", c.cpfCnpj || c.cpf_cnpj || "");
    set("contatoTel", c.tel || "");
    const ends = c.enderecos || [];
    setClienteEnderecos(ends);
    if (ends.length === 1) {
      applyEndereco(ends[0]);
    } else if (ends.length > 1) {
      setEnderecoModal(true);
    }
  };
  const toggleRes = (cls, item) => set(cls, form[cls].includes(item) ? form[cls].filter(x => x !== item) : [...form[cls], item]);
  const total = (parseFloat(form.precoLocacao) || 0) + lancamentos.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
  // Última locação (não cancelada) do cliente selecionado — sugestão de valor
  const ultimaLocCliente = useMemo(() => {
    const nomeSel = String(form.cliente || "").trim().toLowerCase();
    if (form.clienteId == null && !nomeSel) return null;
    const cand = (locacoes || []).filter(l => {
      if (!l || l.status === "Cancelada") return false;
      if (!(parseFloat(l.valor) > 0)) return false;
      const cid = l.clienteId != null ? l.clienteId : l.cliente_id;
      if (form.clienteId != null && cid != null) return String(cid) === String(form.clienteId);
      const nm = String(l.cliente || l.cliente_nome || "").trim().toLowerCase();
      return !!nomeSel && nm === nomeSel;
    });
    if (!cand.length) return null;
    cand.sort(ordCriacaoDesc);
    return cand[0];
  }, [form.clienteId, form.cliente, locacoes]);
  const validar = () => {
    const errs = {};
    if (!form.cliente)       errs.cliente     = true;
    if (!form.logradouro)    errs.logradouro  = true;
    if (!form.numero)        errs.numero      = true;
    if (!form.cidade)        errs.cidade      = true;
    if (!form.uf)            errs.uf          = true;
    if (!form.dataEntrega)   errs.dataEntrega = true;
    if (!total || total <= 0) errs.valor = true;
    if (form.tipoPessoa === "Pessoa Jurídica" && !form.cpfCnpj) errs.cpfCnpj = true;
    if (!form.tipoCacamba)   errs.tipoCacamba = true;
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const msgs = [];
      if (errs.cliente)     msgs.push("• Selecione um cliente");
      if (errs.logradouro || errs.numero || errs.cidade || errs.uf) msgs.push("• Preencha o endereço completo (Logradouro, Número, Cidade e UF)");
      if (errs.dataEntrega) msgs.push("• Informe a Data Prevista de Entrega");
      if (errs.valor)       msgs.push("• Informe o Valor da Locação (obrigatório)");
      if (errs.cpfCnpj)     msgs.push("• CNPJ obrigatório para Pessoa Jurídica");
      if (errs.tipoCacamba)  msgs.push("• Selecione o Tipo de Caçamba");
      setValidacaoPopup(msgs);
      return false;
    }
    return true;
  };
  const buildPayload = () => ({
    cliente: form.cliente,
    clienteId: form.clienteId || null,
    contatoNome: form.contatoNome || '',
    contatoTel: form.contatoTel || '',
    contatoEmail: form.contatoEmail || '',
    endereco: { logradouro: form.logradouro, numero: form.numero, complemento: form.complemento, bairro: form.bairro, cidade: form.cidade, uf: form.uf, cep: form.cep },
    lat: form.lat || null,
    lng: form.lng || null,
    tipoCacamba: form.tipoCacamba || '',
    cacamba: form.cacamba,
    operacao: form.operacao || 'ENTREGA',
    motorista: form.motorista || '',
    dataSolicitacao: form.dataSolicitacao || null,
    dataEntrega: form.dataEntrega,
    horaEntrega: form.horaEntrega || '',
    dataRetirada: form.dataRetirada || null,
    valor: total,
    pagamento: form.pagamento || 'dinheiro',
    prazoPagamento: form.prazoPagamento || '',
    obs: form.observacoes || '',
    gerarMtr: form.gerarMtr || false,
    mtr: form.mtr || '',
    diariaExtra: (form.diariaExtra === "" || form.diariaExtra == null) ? null : (parseFloat(form.diariaExtra) || 0),
    lancamentos,
    residuos: {
      classeA: form.classeA || [],
      classeB: form.classeB || [],
      classeC: form.classeC || [],
      classeD: form.classeD || [],
      outros: form.outrosResiduos || '',
    },
  });
  // Salva o endereço da locação no cadastro do cliente se ainda não estiver lá.
  // Comparação normalizada por logradouro + número + cidade + UF + CEP (só dígitos).
  const salvarEnderecoSeNovo = async () => {
    try {
      const cid = form.clienteId;
      if (!cid || !apiFetch) return;
      if (!form.logradouro || !form.cidade || !form.uf) return;
      const norm = (s) => String(s||"").toLowerCase().trim().replace(/\s+/g, " ");
      const onlyDigits = (s) => String(s||"").replace(/\D/g, "");
      const buildKey = (e) => [norm(e.logradouro), norm(e.numero), norm(e.cidade), norm(e.uf), onlyDigits(e.cep)].join("|");
      const novaKey = buildKey({ logradouro: form.logradouro, numero: form.numero, cidade: form.cidade, uf: form.uf, cep: form.cep });
      const existe = (clienteEnderecos||[]).some(e => buildKey(e) === novaKey);
      if (existe) return;
      const novo = await apiFetch("/api/clientes/" + cid + "/endereco", {
        method: "POST",
        body: {
          nomeEndereco: form.nomeEndereco || "",
          cep: form.cep || "",
          logradouro: form.logradouro || "",
          numero: form.numero || "",
          complemento: form.complemento || "",
          bairro: form.bairro || "",
          cidade: form.cidade || "",
          uf: form.uf || "SP",
          pagamento: form.pagamento || ""
        }
      });
      setClienteEnderecos(prev => [...(prev||[]), novo]);
      onEnderecoAdded?.(cid, novo);
    } catch (e) { console.warn("[salvarEnderecoSeNovo] erro:", e.message); }
  };

  const save = async () => {
    if (!validar()) return;
    await onSave(buildPayload());
    await salvarEnderecoSeNovo();
    onClose();
  };
  const [salvandoMultiplo, setSalvandoMultiplo] = useState(false);
  const confirmarMultiplo = async () => {
    const n = parseInt(qtdeMultiplo);
    if (!n || n < 1 || n > 10) { alert("Informe uma quantidade válida (1 a 10)"); return; }
    if (salvandoMultiplo) return;
    setSalvandoMultiplo(true);
    try {
      // SEQUENCIAL: aguarda cada locação ser criada e commitada antes da próxima.
      // Evita race condition na geração do número sequencial (IDs duplicados → erro 500).
      // 1ª) A locação ATUAL (do formulário), com a caçamba já selecionada nela.
      await onSave(buildPayload());
      // 2ª..) N locações ADICIONAIS, cada uma com a caçamba escolhida na sua linha.
      for (let i = 0; i < n; i++) {
        await onSave({ ...buildPayload(), cacamba: cacambasMultiplo[i] || "" });
      }
      // Salva o endereço no cadastro do cliente se ainda não estiver lá (1 vez só — todas as locações têm o mesmo endereço).
      await salvarEnderecoSeNovo();
      setMultiploModal(false);
      onClose();
    } catch (e) {
      alert("Erro ao gerar as locações: " + (e?.message || "erro desconhecido"));
    } finally {
      setSalvandoMultiplo(false);
    }
  };

  const fetchCep = async (cep) => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      let data = null;
      try { const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`); if(res.ok){ data = await res.json(); if(data.erro) data = null; } } catch(e){}
      if(!data) { try { const r2 = await fetch(`https://brasilapi.com.br/api/cep/v2/${clean}`); if(r2.ok){ const b = await r2.json(); data = { logradouro: b.street||"", bairro: b.neighborhood||"", localidade: b.city||"", uf: b.state||"" }; } } catch(e){} }
      if (data) {
        set("logradouro", data.logradouro || "");
        set("bairro",     data.bairro     || "");
        set("cidade",     data.localidade || "");
        set("uf",         data.uf         || "");
      }
    } catch(e) {}
    setCepLoading(false);
  };

  // === Sugestão de preço por região (CEP-5 + tipo de caçamba) ===
  const [pricingSugestao, setPricingSugestao] = useState(null);
  const [pricingLoading, setPricingLoading]   = useState(false);
  useEffect(() => {
    const cepClean = (form.cep || "").replace(/\D/g, "");
    const tipo = (form.tipoCacamba || "").trim();
    const cidade = (form.cidade || "").trim();
    // Só consulta se tiver tipo de caçamba e (CEP com 5+ dígitos OU cidade)
    if (!tipo || (cepClean.length < 5 && !cidade)) {
      setPricingSugestao(null);
      return;
    }
    setPricingLoading(true);
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (cepClean) params.set("cep", cepClean);
        params.set("tipoCacamba", tipo);
        if (cidade) params.set("cidade", cidade);
        const data = await apiFetch("/api/pricing/sugerir?" + params.toString());
        setPricingSugestao(data || null);
      } catch (e) {
        // Silencioso — se falhar, card simplesmente não aparece
        setPricingSugestao(null);
      } finally {
        setPricingLoading(false);
      }
    }, 500);
    return () => { clearTimeout(timer); setPricingLoading(false); };
  }, [form.cep, form.tipoCacamba, form.cidade]);

  return (
    <>
    <LargeModal title="Nova Locação" subtitle="Tela de cadastro de uma nova locação" onClose={onClose} onSave={innerTab === "locacao" ? save : null} saveLabel="Salvar Locação" extraFooter={innerTab === "locacao" ? (
      <button onClick={() => { if(validar()) { setQtdeMultiplo(""); setCacambasMultiplo([]); setMultiploModal(true); } }} style={{ padding:"7px 16px", background:"linear-gradient(135deg,#1aae9e,#158a7d)", border:"none", borderRadius:3, fontSize:12, cursor:"pointer", fontWeight:700, color:"#fff", fontFamily:FONT, display:"flex", alignItems:"center", gap:6, marginLeft:8, whiteSpace:"nowrap", boxShadow:"0 2px 6px rgba(26,174,158,.3)" }}>
        Salvar múltiplos pedidos
      </button>
    ) : null}>
      {/* Tab selector: Nova Locação / Cadastrar Cliente */}
      <div style={{ display:"flex", borderBottom:"2px solid #ebebeb", marginBottom:16, marginTop:8 }}>
        {[["locacao","Nova Locação"],["cliente","Cadastrar Cliente"]].map(([k,label]) => (
          <button key={k} onClick={() => setInnerTab(k)} style={{ padding:"9px 18px", border:"none", background:"none", cursor:"pointer", fontWeight:innerTab===k?700:400, color:innerTab===k?"#1aae9e":"#888", borderBottom:innerTab===k?"2px solid #1aae9e":"2px solid transparent", marginBottom:-2, fontFamily:FONT, fontSize:13 }}>
            {label}
          </button>
        ))}
      </div>

      {innerTab === "cliente" && <NovoClienteInline onSaved={async (c) => {
        let novoId = null;
        if(onClienteCreated) { try { novoId = await onClienteCreated(c); } catch(e){} }
        setInnerTab("locacao");
        set("cliente", c.nome);
        set("clienteId", novoId || null);
        set("tipoPessoa", c.tipo==="PJ"?"Pessoa Jurídica":"Pessoa Física");
        set("cpfCnpj", c.cpfCnpj||"");
        set("contatoTel", c.tel||"");
        set("contatoNome", c.nome||"");
        set("contatoEmail", c.email||"");
        const ends = c.enderecos || [];
        setClienteEnderecos(ends);
        if (ends.length >= 1) {
          const e = ends[0];
          set("cep", e.cep||""); set("logradouro", e.logradouro||"");
          set("numero", e.numero||""); set("bairro", e.bairro||"");
          set("cidade", e.cidade||""); set("uf", e.uf||"");
          set("complemento", e.complemento||""); set("nomeEndereco", e.nomeEndereco||"");
        }
        if (ends.length > 1) setEnderecoModal(true);
      }} />}

      {innerTab === "locacao" && <>
      <div style={{ textAlign: "right", fontSize: 11, color: "#1aae9e", paddingTop: 0, paddingBottom: 2 }}>* Campo Obrigatório</div>

      {/* Cliente */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 190px", gap: 12, marginBottom: 10 }}>
        <Field label="Nome do Cliente *">
          <div style={{ display:"flex", gap:6 }}>
            <input style={{ ...inp, flex:1 }} placeholder="Selecione um cliente..." value={form.cliente} onChange={e => set("cliente", e.target.value)} readOnly onClick={() => setClienteModal(true)} />
            <button type="button" onClick={() => setClienteModal(true)} style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, padding:"0 12px", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:FONT }}>
              Buscar
            </button>
          </div>
        </Field>
        <Field label="Tipo Pessoa">
          <select style={{ ...inp, appearance: "none" }} value={form.tipoPessoa} onChange={e => set("tipoPessoa", e.target.value)}>
            <option value="">-- Selecione --</option>
            <option>Pessoa Física</option>
            <option>Pessoa Jurídica</option>
          </select>
        </Field>
        <Field label={form.tipoPessoa === "Pessoa Jurídica" ? "CNPJ *" : "CPF (Opcional)"}>
          <input style={inp} value={form.cpfCnpj} onChange={e=>{const raw=e.target.value.replace(/\D/g,"").slice(0,14); set("cpfCnpj",formatCpfCnpj(raw));}} placeholder={form.tipoPessoa === "Pessoa Jurídica" ? "00.000.000/0001-00" : "000.000.000-00 (opcional)"} />
        </Field>
      </div>
      <Field label="Tags">
        <input style={inp} placeholder="Informe uma ou mais tags" value={form.tags} onChange={e => set("tags", e.target.value)} />
      </Field>

      {/* Contato */}
      <SectionHeader icon="📞" label="Contato da Locação" />
      <SectionBody>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <Field label="Nome"><input style={inp} value={form.contatoNome} onChange={e => set("contatoNome", e.target.value)} /></Field>
          <Field label="Telefone"><input style={inp} value={form.contatoTel} onChange={e => set("contatoTel", formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
          <Field label="E-mail"><input style={inp} value={form.contatoEmail} onChange={e => set("contatoEmail", e.target.value)} /></Field>
          <Field label="Cargo"><input style={inp} value={form.contatoCargo} onChange={e => set("contatoCargo", e.target.value)} /></Field>
        </div>
      </SectionBody>

      {/* Localização */}
      <SectionHeader icon="📍" label="Localização da Entrega" />
      <SectionBody>
        {clienteEnderecos.length > 1 && (
          <div style={{ marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
            <button type="button" onClick={() => setEnderecoModal(true)} style={{ background:"#f0f4ff", color:"#1aae9e", border:"1px solid #c7d7fd", borderRadius:3, padding:"6px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
              Trocar endereço do cliente ({clienteEnderecos.length} disponíveis)
            </button>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "150px 75px 1fr 1fr 90px", gap: 12, marginBottom: 12 }}>
          <Field label="CEP"><input style={inp} value={form.cep} onChange={e => { const v=formatCep(e.target.value); set("cep", v); fetchCep(v); }} placeholder="00000-000" /></Field>
          <Field label={<span>UF <span style={{color:"#1aae9e"}}>*</span></span>}><input style={{...inp, borderColor: errors.uf ? "#1aae9e" : inp.borderColor, background: errors.uf ? "#f0f4ff" : inp.background}} value={form.uf} onChange={e => set("uf", e.target.value)} /></Field>
          <Field label={<span>Cidade <span style={{color:"#1aae9e"}}>*</span></span>}><input style={{...inp, borderColor: errors.cidade ? "#1aae9e" : inp.borderColor, background: errors.cidade ? "#f0f4ff" : inp.background}} value={form.cidade} onChange={e => set("cidade", e.target.value)} /></Field>
          <Field label={<span>Logradouro <span style={{color:"#1aae9e"}}>*</span></span>}><AddressAutocomplete style={{...inp, borderColor: errors.logradouro ? "#1aae9e" : inp.borderColor, background: errors.logradouro ? "#f0f4ff" : inp.background}} value={form.logradouro} onChange={v => set("logradouro", v)} cidadeHint={form.cidade} ufHint={form.uf} placeholder="Rua, Avenida ou nome do local..." onSelect={s => { set("logradouro", s.logradouro); if(s.numero) set("numero", s.numero); if(s.bairro) set("bairro", s.bairro); if(s.cidade) set("cidade", s.cidade); if(s.uf) set("uf", s.uf); if(s.cep) set("cep", s.cep); if(s.lat) set("lat", s.lat); if(s.lng) set("lng", s.lng); }} /></Field>
          <Field label={<span>Número <span style={{color:"#1aae9e"}}>*</span></span>}><input style={{...inp, borderColor: errors.numero ? "#1aae9e" : inp.borderColor, background: errors.numero ? "#f0f4ff" : inp.background}} value={form.numero} onChange={e => set("numero", e.target.value)} /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "end" }}>
          <Field label="Complemento"><input style={inp} value={form.complemento} onChange={e => set("complemento", e.target.value)} /></Field>
          <Field label="Bairro"><input style={inp} value={form.bairro} onChange={e => set("bairro", e.target.value)} /></Field>
          <Field label="Nome do Endereço"><input style={inp} value={form.nomeEndereco} onChange={e => set("nomeEndereco", e.target.value)} /></Field>
          <button type="button" onClick={() => setMapModal(true)} style={{ padding: "7px 11px", background: "none", border: "1px solid #1a9e8f", borderRadius: 3, color: "#1a9e8f", fontSize: 12, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap", fontFamily: FONT, marginBottom: 1 }}>
            📍 Confirmar no Mapa
          </button>
        </div>
      </SectionBody>

      {/* Pedido e Resíduos */}
      <SectionHeader icon="ℹ️" label="Informações do Pedido e Resíduos" />
      <SectionBody>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
          <Field label="Pedido em"><input type="date" style={inp} value={form.dataSolicitacao} onChange={e => set("dataSolicitacao", e.target.value)} /></Field>
          <Field label={<span>Data Prevista de Entrega <span style={{color:"#1aae9e"}}>*</span></span>}><input type="date" style={{...inp, borderColor: errors.dataEntrega ? "#1aae9e" : inp.borderColor, background: errors.dataEntrega ? "#f0f4ff" : inp.background}} value={form.dataEntrega} onChange={e => set("dataEntrega", e.target.value)} /></Field>
          <Field label={<span>Hora Prevista <span style={{color:"#aaa", fontWeight:400, fontSize:11}}>(opcional)</span></span>}><input type="time" style={inp} value={form.horaEntrega} onChange={e => set("horaEntrega", e.target.value)} /></Field>
          <Field label="Data Prevista de Retirada"><input type="date" style={inp} value={form.dataRetirada} min={form.dataEntrega||undefined} onChange={e => set("dataRetirada", e.target.value)} /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 12, alignItems: "end", marginBottom: 18 }}>
          <Field label={<span>Tipo de Caçamba <span style={{color:"#1aae9e"}}>*</span></span>}>
            <select style={{ ...inp, appearance: "none", borderColor: errors.tipoCacamba ? "#1aae9e" : inp.borderColor, background: errors.tipoCacamba ? "#f0f4ff" : inp.background }} value={form.tipoCacamba} onChange={e => set("tipoCacamba", e.target.value)}>
              <option value="">-- Selecione --</option>
              {tiposCacamba.map(t => <option key={t.id} value={t.nome}>{t.nome}{t.cubicagem?" ("+t.cubicagem.replace(/m3/gi,"").trim()+" m³)":""}</option>)}
            </select>
          </Field>
          <Field label="Caçamba">
            <select style={{ ...inp, appearance: "none" }} value={form.cacamba} onChange={e => {
              const cod = e.target.value;
              set("cacamba", cod);
              if (cod) {
                const obj = (cacambas||[]).find(c => c.codigo === cod);
                if (obj) {
                  if (obj.tipo && !form.tipoCacamba) set("tipoCacamba", obj.tipo);
                  set("volumeCacamba", obj.volume || obj.cubicagem || "");
                }
              }
            }}>
              <option value="">-- Selecione --</option>
              {(cacambas||[]).filter(c => (!form.tipoCacamba || !c.tipo || c.tipo === form.tipoCacamba) && (!c.status || c.status === "Disponível")).map(c => <option key={c.id||String(c)} value={c.codigo||""}>{c.codigo||String(c)}{c.tipo?" — "+c.tipo:""}{c.volume?" ("+c.volume+")":""}</option>)}
            </select>
          </Field>
          <Field label="Número do MTR/CTR">
            <input style={inp} value={form.mtr} onChange={e => set("mtr", e.target.value)} placeholder="Preenchido automaticamente com nº da locação" />
            <span style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Se vazio, será usado o número da locação</span>
          </Field>
        </div>
        <div style={{ border: "1px solid #e8e8e8", borderRadius: 3, padding: 14, marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 12 }}>Classificação dos Resíduos</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 18 }}>
            <CheckGroup title="Classe A" items={CLASSE_A} checked={form.classeA} onChange={item => toggleRes("classeA", item)} />
            <CheckGroup title="Classe B" items={CLASSE_B} checked={form.classeB} onChange={item => toggleRes("classeB", item)} />
            <CheckGroup title="Classe C" items={CLASSE_C} checked={form.classeC} onChange={item => toggleRes("classeC", item)} />
            <CheckGroup title="Classe D" items={CLASSE_D} checked={form.classeD} onChange={item => toggleRes("classeD", item)} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#888", display: "block", marginBottom: 4 }}>Outros resíduos não listados? Preencha a seguir:</label>
          <input style={inp} value={form.outrosResiduos} onChange={e => set("outrosResiduos", e.target.value)} />
        </div>
      </SectionBody>

      {/* Precificação */}
      <SectionHeader icon="$" label="Precificação / Lançamentos Adicionais" />
      <SectionBody>
        {/* Sugestão de preço por região (aparece apenas quando há dados suficientes) */}
        {pricingSugestao && pricingSugestao.sugerido && (
          <div style={{
            background: "linear-gradient(135deg, #eafaf6 0%, #d8f4ed 100%)",
            border: "1px solid #1aae9e",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 14,
            fontSize: 12,
            color: "#0e5e55",
            fontFamily: FONT,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#1aae9e", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 700, flexShrink: 0,
            }}>💡</div>
            <div style={{ flex: 1, lineHeight: 1.45 }}>
              <div style={{ fontWeight: 700, color: "#0e5e55", fontSize: 12, marginBottom: 2 }}>
                Preço sugerido para esta região
                {pricingSugestao.base === "cep5" && <span style={{ fontWeight: 500, color: "#4a8a80" }}> · CEP {pricingSugestao.cep5}</span>}
                {pricingSugestao.base === "cidade" && <span style={{ fontWeight: 500, color: "#4a8a80" }}> · {pricingSugestao.cidade}</span>}
              </div>
              <div style={{ color: "#0e5e55", fontSize: 12 }}>
                <strong style={{ fontSize: 14 }}>R$ {Number(pricingSugestao.mediana).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</strong>
                <span style={{ color: "#4a8a80", marginLeft: 8 }}>
                  (faixa R$ {Number(pricingSugestao.p25).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} — R$ {Number(pricingSugestao.p75).toLocaleString("pt-BR", { minimumFractionDigits: 2 })})
                </span>
                <span style={{ color: "#4a8a80", marginLeft: 8, fontSize: 11 }}>
                  · {pricingSugestao.qtd} locações {pricingSugestao.tipoCacamba ? "de " + pricingSugestao.tipoCacamba : ""} nos últimos {pricingSugestao.periodoMeses} meses
                </span>
                {!pricingSugestao.solida && (
                  <span style={{ color: "#d68910", marginLeft: 8, fontSize: 11, fontWeight: 600 }}>
                    · poucos dados — use como referência
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                const valor = Number(pricingSugestao.mediana) || 0;
                const formatted = valor.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                set("precoLocacaoFmt", formatted);
                set("precoLocacao", valor);
              }}
              style={{
                background: "#1aae9e", color: "#fff", border: "none",
                borderRadius: 4, padding: "7px 14px", fontSize: 11, fontWeight: 700,
                cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(26,174,158,.3)",
              }}
              title="Usar o preço sugerido"
            >
              Usar R$ {Number(pricingSugestao.mediana).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </button>
          </div>
        )}
        {pricingLoading && !pricingSugestao && (
          <div style={{ fontSize: 11, color: "#888", marginBottom: 8, fontStyle: "italic" }}>
            Consultando preço médio da região…
          </div>
        )}
        {ultimaLocCliente && (
          <div style={{ fontSize: 11, color: "#888", marginBottom: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>Última locação deste cliente: <strong style={{ color: "#555" }}>{fmt(ultimaLocCliente.valor)}</strong></span>
            <button
              type="button"
              onClick={() => {
                const valor = parseFloat(ultimaLocCliente.valor) || 0;
                const formatted = valor.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
                set("precoLocacaoFmt", formatted);
                set("precoLocacao", valor);
              }}
              style={{ background: "none", border: "1px solid #1a9e8f", borderRadius: 4, color: "#1a9e8f", padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}
              title="Preencher o valor com o da última locação deste cliente"
            >Usar valor</button>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #eee" }}>
              <th style={{ textAlign: "left", padding: "5px 8px", fontSize: 11, color: "#888", fontWeight: 600 }}>Descrição do Item</th>
              <th style={{ textAlign: "right", padding: "5px 8px", fontSize: 11, color: "#888", fontWeight: 600, width: 160 }}>Valor (R$)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "5px 8px", color: "#444" }}>Preço da Locação</td>
              <td style={{ padding: "4px 8px" }}>
                <div style={{ position:"relative" }}>
                  <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#888", fontWeight:600, pointerEvents:"none" }}>R$</span>
                  <input type="text" inputMode="numeric" style={{ ...inp, textAlign: "right", paddingLeft:36 }} value={form.precoLocacaoFmt||""} onChange={e => { const formatted = formatCurrencyInput(e.target.value); set("precoLocacaoFmt", formatted); set("precoLocacao", parseCurrencyToFloat(formatted)); }} placeholder="0,00" />
                </div>
              </td>
            </tr>
            {lancamentos.map((l, i) => (
              <tr key={i}>
                <td style={{ padding: "4px 8px" }}>
                  <input style={inp} value={l.desc} onChange={e => { const a = [...lancamentos]; a[i].desc = e.target.value; setLancamentos(a); }} placeholder="Descrição" />
                </td>
                <td style={{ padding: "4px 8px" }}>
                  <div style={{ position:"relative" }}>
                    <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"#888", fontWeight:600, pointerEvents:"none" }}>R$</span>
                    <input type="text" inputMode="numeric" style={{ ...inp, textAlign: "right", paddingLeft:36 }} value={l.valorFmt||""} onChange={e => { const formatted = formatCurrencyInput(e.target.value); const a = [...lancamentos]; a[i].valorFmt = formatted; a[i].valor = parseCurrencyToFloat(formatted); setLancamentos(a); }} placeholder="0,00" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <button onClick={() => setLancamentos(p => [...p, { desc: "", valor: "" }])} style={{ background: "none", border: "none", cursor: "pointer", color: "#1a9e8f", fontSize: 13, fontWeight: 600, fontFamily: FONT, padding: 0 }}>
            + Novo Lançamento
          </button>
          <span style={{ fontSize: 15, fontWeight: 800, color: "#1a1a1a" }}>Total &nbsp; {fmt(total)}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, paddingTop: 10, borderTop: "1px dashed #eee" }}>
          <label style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>Diária extra (R$/dia) <span style={{ color: "#aaa", fontWeight: 400, fontSize: 11 }}>(opcional — cobrada por dia de atraso na retirada)</span></label>
          <input type="number" min="0" step="0.01" style={{ ...inp, width: 120, textAlign: "right" }} value={form.diariaExtra} onChange={e => set("diariaExtra", e.target.value)} placeholder="0,00" />
        </div>
      </SectionBody>

      {/* Pagamento */}
      <SectionHeader icon="💳" label="Forma de Pagamento" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Forma de Pagamento *">
            <select style={{ ...inp, appearance:"none" }} value={form.pagamento} onChange={e => set("pagamento", e.target.value)}>
              <option value="">-- Selecione --</option>
              <option value="pix">PIX</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="boleto">Boleto</option>
              <option value="faturado">Faturado</option>
            </select>
          </Field>
          {(form.pagamento === "boleto" || form.pagamento === "faturado") && (
            <Field label="Prazo de Pagamento (dias)">
              <input type="number" style={inp} value={form.prazoPagamento} onChange={e => set("prazoPagamento", e.target.value)} placeholder="Ex: 30" min="1" />
            </Field>
          )}
          {(form.pagamento === "boleto" || form.pagamento === "faturado") && form.dataEntrega && form.prazoPagamento && (() => {
            const dataVenc = new Date(form.dataEntrega);
            dataVenc.setDate(dataVenc.getDate() + parseInt(form.prazoPagamento||0));
            const hoje = new Date();
            const vencida = dataVenc < hoje;
            return (
              <Field label="Vencimento previsto">
                <div style={{ padding:"8px 12px", borderRadius:3, border:"1px solid", borderColor: vencida?"#e74c3c":"#c7d7fd", background: vencida?"#fde8e8":"#f0f4ff", color: vencida?"#c0392b":"#1aae9e", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
                  {vencida ? "" : ""} {dataVenc.toLocaleDateString("pt-BR")}
                  {vencida && <span style={{ fontSize:11, color:"#e74c3c", fontWeight:800 }}> — VENCIDO</span>}
                </div>
              </Field>
            );
          })()}
        </div>
      </SectionBody>

      {/* Observações */}
      <div style={{ marginTop: 18 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: "#666", display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: .3 }}>Observações</label>
        <textarea style={{ ...inp, minHeight: 68, resize: "vertical" }} value={form.observacoes} onChange={e => set("observacoes", e.target.value)} />
      </div>
      </>}
    </LargeModal>
    {clienteModal && <ClienteSelectorModal clientes={clientes} locacoes={locacoes} onSelect={selectCliente} onClose={() => setClienteModal(false)} />}
    {enderecoModal && clienteEnderecos.length > 0 && <EnderecoSelectorModal enderecos={clienteEnderecos} onSelect={applyEndereco} onClose={() => setEnderecoModal(false)} />}
    {mapModal && <MapConfirmModal address={[form.logradouro, form.numero, form.bairro, form.cidade, form.uf].filter(Boolean).join(", ")} cidadeHint={form.cidade} ufHint={form.uf} onClose={() => setMapModal(false)} onConfirm={(coords, displayAddr, addrDetails) => { set("lat", coords.lat); set("lng", coords.lng); if (addrDetails) { if (addrDetails.cep) set("cep", addrDetails.cep.replace(/\D/g,"").replace(/^(\d{5})(\d{3})$/,"$1-$2")); if (addrDetails.logradouro) { const parts = addrDetails.logradouro.split(","); set("logradouro", parts[0].trim()); if (parts[1]) set("numero", parts[1].trim()); } if (addrDetails.bairro) set("bairro", addrDetails.bairro); if (addrDetails.cidade) set("cidade", addrDetails.cidade); if (addrDetails.uf) set("uf", addrDetails.uf); } }} />}
    {validacaoPopup && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:3000, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ background:"#fff", borderRadius:14, width:400, maxWidth:"90vw", boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden", fontFamily:FONT, animation:"fadeIn .2s ease" }}>
          <div style={{ background:"linear-gradient(135deg, #ff6b6b, #ee5a24)", padding:"20px 24px", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:"50%", background:"rgba(255,255,255,.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>⚠️</div>
            <div>
              <div style={{ color:"#fff", fontWeight:800, fontSize:16 }}>Campos Obrigatórios</div>
              <div style={{ color:"rgba(255,255,255,.8)", fontSize:11 }}>Preencha os campos abaixo para continuar</div>
            </div>
          </div>
          <div style={{ padding:"20px 24px" }}>
            {validacaoPopup.map((m, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:i < validacaoPopup.length - 1 ? "1px solid #f3f3f3" : "none" }}>
                <div style={{ width:24, height:24, borderRadius:"50%", background:"#fff2f0", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#e74c3c" }} />
                </div>
                <div style={{ fontSize:13, color:"#444", fontWeight:500 }}>{m.replace(/^• /, "")}</div>
              </div>
            ))}
          </div>
          <div style={{ padding:"0 24px 20px", textAlign:"right" }}>
            <button onClick={() => setValidacaoPopup(null)} style={{ padding:"10px 32px", background:"linear-gradient(135deg, #1aae9e, #16a085)", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 2px 8px rgba(26,174,158,.3)" }}>Entendi</button>
          </div>
        </div>
      </div>
    )}
    {multiploModal && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <div style={{ background:"#fff", borderRadius:6, padding:"24px 28px", minWidth:320, boxShadow:"0 8px 32px rgba(0,0,0,.25)", fontFamily:FONT }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:4, color:"#1a1a1a" }}>Salvar Múltiplos Pedidos</div>
          <div style={{ fontSize:11.5, color:"#777", marginBottom:14, lineHeight:1.45, maxWidth:360 }}>
            A <b>locação atual</b> será criada com {form.cacamba ? <>a caçamba <b>{form.cacamba}</b></> : "nenhuma caçamba"}. Informe quantas locações <b>adicionais</b> deseja criar — cada uma com sua própria caçamba.
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ fontSize:12, color:"#555", fontWeight:600, display:"block", marginBottom:6 }}>Locações adicionais</label>
            <select style={{ width:"100%", padding:"8px 12px", border:"1px solid #ccc", borderRadius:3, fontSize:13, fontFamily:FONT, appearance:"auto" }} value={qtdeMultiplo} onChange={e => setQtdeMultiplo(e.target.value)}>
              <option value="">-- Selecione --</option>
              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {(parseInt(qtdeMultiplo) > 0) && (() => {
            const n = parseInt(qtdeMultiplo);
            const disp = (cacambas||[]).filter(c => (!form.tipoCacamba || !c.tipo || c.tipo === form.tipoCacamba) && (!c.status || c.status === "Disponível"));
            return (
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:12, color:"#555", fontWeight:600, display:"block", marginBottom:6 }}>Caçamba de cada locação adicional <span style={{ color:"#aaa", fontWeight:400 }}>(opcional)</span></label>
                <div style={{ maxHeight:240, overflowY:"auto", display:"flex", flexDirection:"column", gap:8, paddingRight:4 }}>
                  {Array.from({ length:n }, (_, i) => {
                    // Exclui a caçamba da locação atual (form.cacamba) e as já escolhidas em outras linhas → evita "caçamba já em uso"
                    const escolhidasOutras = [form.cacamba, ...cacambasMultiplo.filter((cod, j) => j !== i)].filter(Boolean);
                    const opcoes = disp.filter(c => !escolhidasOutras.includes(c.codigo));
                    return (
                      <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:11, color:"#888", fontWeight:700, minWidth:72 }}>Adicional {i+1}</span>
                        <select style={{ flex:1, padding:"7px 10px", border:"1px solid #ccc", borderRadius:3, fontSize:12, fontFamily:FONT, appearance:"auto" }}
                          value={cacambasMultiplo[i] || ""}
                          onChange={e => { const v = e.target.value; setCacambasMultiplo(prev => { const arr = [...prev]; arr[i] = v; return arr; }); }}>
                          <option value="">-- Sem caçamba / definir depois --</option>
                          {opcoes.map(c => <option key={c.id||c.codigo||String(c)} value={c.codigo||""}>{c.codigo||String(c)}{c.tipo?" — "+c.tipo:""}{c.volume?" ("+c.volume+")":""}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
                {disp.length === 0 && <div style={{ fontSize:11, color:"#e67e22", marginTop:6 }}>Nenhuma caçamba disponível para o tipo selecionado — as adicionais serão criadas sem caçamba.</div>}
                <div style={{ fontSize:11.5, color:"#1aae9e", fontWeight:700, marginTop:10 }}>Total: {n+1} locações (1 atual + {n} adicionais)</div>
              </div>
            );
          })()}
          <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:18 }}>
            <button onClick={() => setMultiploModal(false)} style={{ padding:"7px 18px", background:"#fff", border:"1px solid #ccc", borderRadius:3, fontSize:13, cursor:"pointer", fontWeight:600, color:"#555", fontFamily:FONT, display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ color:"#e74c3c", fontWeight:800 }}>✕</span> Cancelar
            </button>
            <button onClick={confirmarMultiplo} style={{ padding:"7px 20px", background:"#27ae60", border:"none", borderRadius:3, fontSize:13, cursor:"pointer", fontWeight:700, color:"#fff", fontFamily:FONT, display:"flex", alignItems:"center", gap:4 }}>
              <span></span> Confirmar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ─── Frota ────────────────────────────────────────────────────────────────────
function VeiculoModal({ onClose, onSave, motoristas = [], editData, empresaCfg, apiFetch }) {
  const fileRef = useRef();
  const isEdit = !!editData;
  const [form, setForm] = useState(editData ? {
    placa: editData.placa||"", modelo: editData.modelo||"", ano: editData.ano||"", cor: editData.cor||"",
    motorista: editData.motorista||"", km: editData.km||"", chassi: editData.chassi||"", renavam: editData.renavam||"",
    combustivel: editData.combustivel||"Diesel", capacidade: editData.capacidade||"",
    vencCNH: editData.vencCNH||editData.venc_cnh ? (editData.vencCNH||editData.venc_cnh||"").split("T")[0] : "",
    vencCRLV: editData.vencCRLV||editData.venc_crlv ? (editData.vencCRLV||editData.venc_crlv||"").split("T")[0] : "",
    vencSeguro: editData.vencSeguro||editData.venc_seguro ? (editData.vencSeguro||editData.venc_seguro||"").split("T")[0] : "",
    status: editData.status||"Ativo", observacoes: editData.observacoes||""
  } : { placa:"", modelo:"", ano:"", cor:"", motorista:"", km:"", chassi:"", renavam:"", combustivel:"Diesel", capacidade:"", vencCNH:"", vencCRLV:"", vencSeguro:"", status:"Ativo", observacoes:"" });
  const [anexos, setAnexos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const permitAnexos = empresaCfg && (empresaCfg.plano === "controla" || empresaCfg.plano === "controla_plus" || empresaCfg.plano === "operacao" || empresaCfg.plano === "escala");

  useEffect(() => {
    if (isEdit && editData.id && apiFetch) {
      apiFetch("/api/documentos?categoria=veiculo_" + editData.id).then(docs => setAnexos(docs||[])).catch(() => {});
    }
  }, []);

  const handleUpload = async (files) => {
    if (!permitAnexos || !apiFetch) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("documento", file);
        fd.append("categoria", "veiculo_" + (editData?.id || "novo"));
        fd.append("descricao", "Documento do veículo " + (form.placa || ""));
        const res = await fetch((process.env.REACT_APP_API || "http://localhost:3001") + "/api/documentos", {
          method: "POST", headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") }, body: fd
        });
        if (res.ok) { const doc = await res.json(); setAnexos(p => [...p, doc]); }
        else { const err = await res.json(); alert(err.error || "Erro ao enviar arquivo"); }
      } catch(e) { alert("Erro ao enviar: " + e.message); }
    }
    setUploading(false);
  };

  const removeAnexo = async (doc) => {
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await apiFetch("/api/documentos/" + doc.id, { method: "DELETE" });
      setAnexos(p => p.filter(d => d.id !== doc.id));
    } catch(e) { alert("Erro: " + e.message); }
  };

  const save = () => {
    if (!form.placa) { alert("Placa é obrigatória."); return; }
    if (!form.modelo) { alert("Modelo é obrigatório."); return; }
    onSave({ ...form, id: editData?.id, km: parseInt(form.km) || 0 });
  };

  return (
    <LargeModal title={isEdit ? "Editar Veículo — " + (editData.placa||"") : "Cadastrar Novo Veículo"} subtitle="Informações e documentos do veículo" onClose={onClose} onSave={save} saveLabel={isEdit ? "Salvar Alterações" : "Salvar Veículo"}>
      <SectionHeader icon="🚗" label="Dados do Veículo" />
      <SectionBody>
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 80px 90px", gap: 12, marginBottom: 12 }}>
          <Field label="Placa *"><input style={inp} value={form.placa} onChange={e => set("placa", e.target.value.toUpperCase())} placeholder="BRA-0A00" maxLength={8} /></Field>
          <Field label="Modelo / Marca *"><input style={inp} value={form.modelo} onChange={e => set("modelo", e.target.value)} placeholder="Ex: Mercedes-Benz Atego 1719" /></Field>
          <Field label="Ano"><input style={inp} value={form.ano} onChange={e => set("ano", e.target.value)} placeholder="2023" maxLength={4} /></Field>
          <Field label="Cor"><input style={inp} value={form.cor} onChange={e => set("cor", e.target.value)} placeholder="Branco" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label="Chassi"><input style={inp} value={form.chassi} onChange={e => set("chassi", e.target.value.toUpperCase())} placeholder="9BWZZZ000XX000000" maxLength={17} /></Field>
          <Field label="RENAVAM"><input style={inp} value={form.renavam} onChange={e => set("renavam", e.target.value)} placeholder="00000000000" maxLength={11} /></Field>
          <Field label="Combustível">
            <select style={{ ...inp, appearance: "none" }} value={form.combustivel} onChange={e => set("combustivel", e.target.value)}>
              <option>Diesel</option><option>Diesel S-10</option><option>Gasolina</option><option>Flex</option><option>GNV</option>
            </select>
          </Field>
          <Field label="Capacidade"><input style={inp} value={form.capacidade} onChange={e => set("capacidade", e.target.value)} placeholder="5m³" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Motorista Vinculado">
            <select style={{ ...inp, appearance: "none" }} value={form.motorista} onChange={e => set("motorista", e.target.value)}>
              <option value="">-- Selecione --</option>
              {motoristas.map(m => <option key={m.id||m.nome} value={m.nome}>{m.nome}</option>)}
            </select>
          </Field>
          <Field label="KM Atual"><input type="number" style={inp} value={form.km} onChange={e => set("km", e.target.value)} placeholder="0" /></Field>
          <Field label="Status">
            <select style={{ ...inp, appearance: "none" }} value={form.status} onChange={e => set("status", e.target.value)}>
              <option>Ativo</option><option>Manutenção</option><option>Inativo</option>
            </select>
          </Field>
        </div>
      </SectionBody>

      <SectionHeader icon="📅" label="Vencimentos" />
      <SectionBody>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Venc. CNH do Motorista"><input type="date" style={inp} value={form.vencCNH} onChange={e => set("vencCNH", e.target.value)} /></Field>
          <Field label="Venc. CRLV"><input type="date" style={inp} value={form.vencCRLV} onChange={e => set("vencCRLV", e.target.value)} /></Field>
          <Field label="Venc. Seguro"><input type="date" style={inp} value={form.vencSeguro} onChange={e => set("vencSeguro", e.target.value)} /></Field>
        </div>
      </SectionBody>

      <SectionHeader icon="📎" label="Documentos e Anexos" />
      <SectionBody>
        {permitAnexos ? (<>
          <div
            style={{ border: "2px dashed #ddd", borderRadius: 4, padding: 18, textAlign: "center", cursor: "pointer", background: uploading ? "#f0f9ff" : "#fafafa" }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleUpload(Array.from(e.dataTransfer.files)); }}>
            <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={e => handleUpload(Array.from(e.target.files))} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
            <div style={{ fontSize: 24, marginBottom: 4 }}>{uploading ? "" : ""}</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>{uploading ? "Enviando..." : "Arraste arquivos ou clique para selecionar"}</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>CRLV, CNH, Seguro, Laudos — PDF, JPG, PNG</div>
          </div>
          {anexos.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {anexos.map(d => (
                <div key={d.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "#f0f7ff", border: "1px solid #c5dff5", borderRadius: 3, padding: "6px 10px", marginBottom: 5, fontSize: 12 }}>
                  <button onClick={() => downloadAuth(d.url||"/api/files/"+d.caminho, d.nome_original||d.nome||"Documento")} style={{ background:"none", border:"none", cursor:"pointer", color: "#1a5fa8", textDecoration: "none", display: "flex", alignItems: "center", gap: 5, fontSize:12 }}>
                    {d.nome_original || d.nome || "Documento"}
                  </button>
                  <button onClick={() => removeAnexo(d)} style={{ background: "none", border: "none", cursor: "pointer", color: "#e74c3c", fontSize: 14, fontWeight: 700 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </>) : (
          <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 4, padding: 14, fontSize: 12, color: "#856404" }}>
            Anexos de documentos estão disponíveis a partir do <b>plano Controla</b>. Faça upgrade para anexar CRLV, CNH, Seguros e outros documentos.
          </div>
        )}
      </SectionBody>

      <div style={{ marginTop: 14 }}>
        <Field label="Observações"><textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.observacoes} onChange={e => set("observacoes", e.target.value)} /></Field>
      </div>
    </LargeModal>
  );
}

// ─── Manutenção ──────────────────────────────────────────────────────────────
function NovaManutencaoModal({ frota, onClose, onSave, editData, empresaCfg, apiFetch }) {
  const [form, setForm] = useState(() => {
    const d = editData || { veiculo_id:"", tipo:"Preventiva", descricao:"", data_manutencao:hojeLocal(), km_veiculo:"", valor:"", oficina:"", observacoes:"" };
    return { ...d, valorFmt: initCurrencyFromNumber(d.valor), kmFmt: initKmFromNumber(d.km_veiculo) };
  });
  const [anexos, setAnexos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const plano = empresaCfg?.plano || "base";
  const permiteAnexos = ["controla","controla_plus","operacao","escala"].includes(plano);

  // Load existing attachments if editing
  useEffect(() => {
    if (editData && editData.id && apiFetch) {
      (async () => {
        try {
          const res = await fetch((process.env.REACT_APP_API || "http://localhost:3001") + "/api/documentos?categoria=manutencao_" + editData.id, {
            headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") }
          });
          if (res.ok) { const data = await res.json(); setAnexos(data); }
        } catch(e) {}
      })();
    }
  }, [editData]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removeNewFile = (idx) => setNewFiles(prev => prev.filter((_, i) => i !== idx));

  const removeExistingAnexo = async (doc) => {
    try {
      const res = await fetch((process.env.REACT_APP_API || "http://localhost:3001") + "/api/documentos/" + doc.id, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") }
      });
      if (res.ok) setAnexos(prev => prev.filter(a => a.id !== doc.id));
    } catch(e) {}
  };

  const handleSave = async () => {
    setUploading(true);
    try {
      const cleanForm = { ...form }; delete cleanForm.valorFmt; delete cleanForm.kmFmt;
      const saved = await onSave(cleanForm);
      const manutId = saved?.id || editData?.id;
      // Upload new files
      if (manutId && newFiles.length > 0) {
        for (const file of newFiles) {
          const fd = new FormData();
          fd.append("documento", file);
          fd.append("categoria", "manutencao_" + manutId);
          fd.append("descricao", "Anexo de manutenção");
          await fetch((process.env.REACT_APP_API || "http://localhost:3001") + "/api/documentos", {
            method: "POST",
            headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") },
            body: fd
          });
        }
      }
    } catch(e) {}
    setUploading(false);
  };

  return (
    <LargeModal title={editData?"Editar Manutenção":"Nova Manutenção"} subtitle="Registre manutenção de veículo" onClose={onClose} onSave={handleSave} saveLabel={uploading?"Salvando...":"Salvar"} maxWidth={700}>
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Veículo *">
            <select style={{...inp,appearance:"none"}} value={form.veiculo_id} onChange={e=>set("veiculo_id",e.target.value)}>
              <option value="">Selecione...</option>
              {frota.map(v=><option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
            </select>
          </Field>
          <Field label="Tipo">
            <select style={{...inp,appearance:"none"}} value={form.tipo} onChange={e=>set("tipo",e.target.value)}>
              <option>Preventiva</option><option>Corretiva</option><option>Revisão</option><option>Inspeção</option>
            </select>
          </Field>
        </div>
        <div style={{ marginBottom:12 }}>
          <Field label="Descrição *"><textarea style={{...inp,minHeight:60,resize:"vertical"}} value={form.descricao} onChange={e=>set("descricao",e.target.value)} placeholder="Descreva a manutenção realizada..." /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Data"><input type="date" style={inp} value={form.data_manutencao} onChange={e=>set("data_manutencao",e.target.value)} /></Field>
          <Field label="KM do Veículo">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#999", fontWeight:600, pointerEvents:"none" }}>km</span>
              <input type="text" inputMode="numeric" style={{...inp, paddingLeft:32, textAlign:"right"}} value={form.kmFmt||""} onChange={e=>{ const fmt = formatKmInput(e.target.value); set("kmFmt", fmt); set("km_veiculo", parseKmToNumber(fmt)); }} placeholder="0" />
            </div>
          </Field>
          <Field label="Valor (R$)">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"#999", fontWeight:600, pointerEvents:"none" }}>R$</span>
              <input type="text" inputMode="numeric" style={{...inp, paddingLeft:36, textAlign:"right"}} value={form.valorFmt||""} onChange={e=>{ const fmt = formatCurrencyInput(e.target.value); set("valorFmt", fmt); set("valor", parseCurrencyToFloat(fmt)); }} placeholder="0,00" />
            </div>
          </Field>
          <Field label="Oficina"><input style={inp} value={form.oficina} onChange={e=>set("oficina",e.target.value)} placeholder="Nome da oficina" /></Field>
        </div>
        <Field label="Observações"><textarea style={{...inp,minHeight:40,resize:"vertical"}} value={form.observacoes} onChange={e=>set("observacoes",e.target.value)} /></Field>

        {/* Anexos - Plan gated */}
        <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid #eee" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#1a1a1a", marginBottom:8 }}>Anexos</div>
          {!permiteAnexos ? (
            <div style={{ background:"#fffbeb", border:"1px solid #fbbf24", borderRadius:6, padding:12, fontSize:12, color:"#92400e" }}>
              Anexos de manutenção disponíveis a partir do plano Controla. Faça upgrade para anexar comprovantes, notas e laudos.
            </div>
          ) : (
            <>
              {/* Existing attachments */}
              {anexos.length > 0 && (
                <div style={{ marginBottom:10 }}>
                  {anexos.map(a => (
                    <div key={a.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"#f9fafb", borderRadius:4, marginBottom:4, fontSize:12 }}>
                      <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.nome_original}</span>
                      <span style={{ color:"#888", fontSize:11 }}>{(a.tamanho/1024).toFixed(0)} KB</span>
                      <button onClick={()=>downloadAuth(a.url, a.nome_original)} style={{ background:"none", border:"none", color:"#1aae9e", cursor:"pointer", fontSize:11 }}>Ver</button>
                      <button onClick={()=>removeExistingAnexo(a)} style={{ background:"none", border:"none", color:"#e74c3c", cursor:"pointer", fontSize:11 }}>Remover</button>
                    </div>
                  ))}
                </div>
              )}
              {/* New files to upload */}
              {newFiles.length > 0 && (
                <div style={{ marginBottom:10 }}>
                  {newFiles.map((f,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"#eef7ff", borderRadius:4, marginBottom:4, fontSize:12 }}>
                      <span style={{ flex:1 }}>{f.name}</span>
                      <span style={{ color:"#888", fontSize:11 }}>{(f.size/1024).toFixed(0)} KB</span>
                      <button onClick={()=>removeNewFile(i)} style={{ background:"none", border:"none", color:"#e74c3c", cursor:"pointer", fontSize:11 }}>Remover</button>
                    </div>
                  ))}
                </div>
              )}
              <label style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 14px", background:"#f0fdf4", border:"1px dashed #86efac", borderRadius:6, cursor:"pointer", fontSize:12, color:"#16a34a" }}>
                + Anexar arquivo (PDF, imagem, Word, Excel)
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt" multiple onChange={handleFileSelect} style={{ display:"none" }} />
              </label>
            </>
          )}
        </div>
      </SectionBody>
    </LargeModal>
  );
}

function ManutencaoPage({ manutencoes, setManutencoes, frota, addToast, apiFetch, empresaCfg }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filterVeiculo, setFilterVeiculo] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterDe, setFilterDe] = useState("");
  const [filterAte, setFilterAte] = useState("");
  const [selected, setSelected] = useState(new Set());

  const fmtDateBR = (ds) => {
    if (!ds) return "—";
    const s = String(ds).split("T")[0];
    if (!s || s.length < 10) return "—";
    const [y,m,d] = s.split("-");
    return d + "/" + m + "/" + y;
  };

  const mesAtual = hojeLocal().slice(0,7);
  const manutMes = manutencoes.filter(m => {
    const d = (m.data_manutencao||""); const ds = String(d).split("T")[0];
    return ds.startsWith(mesAtual);
  });
  const totalGastoMes = manutMes.reduce((s,m) => s + parseFloat(m.valor||0), 0);
  const veiculosEmManut = frota.filter(v => (v.status||"").toLowerCase().includes("manutenção") || (v.status||"").toLowerCase().includes("manutencao")).length;

  const filtered = manutencoes.filter(m => {
    if (filterVeiculo && String(m.veiculo_id) !== filterVeiculo) return false;
    if (filterTipo && m.tipo !== filterTipo) return false;
    const ds = String(m.data_manutencao||"").split("T")[0];
    if (filterDe && ds < filterDe) return false;
    if (filterAte && ds > filterAte) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (m.placa||"").toLowerCase().includes(s) || (m.descricao||"").toLowerCase().includes(s) || (m.oficina||"").toLowerCase().includes(s) || (m.tipo||"").toLowerCase().includes(s) || (m.modelo||"").toLowerCase().includes(s);
  });

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(m=>m.id))); };

  const save = async (data) => {
    try {
      let saved;
      if (editData && editData.id) {
        saved = await apiFetch("/api/manutencoes/" + editData.id, { method: "PUT", body: data });
        setManutencoes(p => p.map(m => m.id === saved.id ? saved : m));
        addToast("Manutenção atualizada!", "success");
      } else {
        saved = await apiFetch("/api/manutencoes", { method: "POST", body: data });
        setManutencoes(p => [saved, ...p]);
        addToast("Manutenção registrada!", "success");
      }
      setModal(false); setEditData(null);
      return saved;
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta manutenção?")) return;
    try {
      await apiFetch("/api/manutencoes/" + id, { method: "DELETE" });
      setManutencoes(p => p.filter(m => m.id !== id));
      setSelected(p => { const n = new Set(p); n.delete(id); return n; });
      addToast("Manutenção excluída!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const handleExport = () => {
    const rows = selected.size > 0 ? filtered.filter(m => selected.has(m.id)) : filtered;
    exportToExcel(rows, [
      { label:"Veículo", key: r => (r.placa||"") + " " + (r.modelo||"") },
      { label:"Tipo", key:"tipo" },
      { label:"Descrição", key:"descricao" },
      { label:"Data", key: r => fmtDateBR(r.data_manutencao) },
      { label:"KM", key:"km_veiculo" },
      { label:"Valor (R$)", key: r => parseFloat(r.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2}) },
      { label:"Oficina", key:"oficina" },
      { label:"Observações", key:"observacoes" }
    ], "manutencoes");
    addToast(selected.size > 0 ? selected.size + " registros exportados" : "Todos exportados", "success");
  };

  const clearFilters = () => { setFilterVeiculo(""); setFilterTipo(""); setFilterDe(""); setFilterAte(""); setSearch(""); };
  const hasFilters = filterVeiculo || filterTipo || filterDe || filterAte || search;

  return (
    <div>
      {/* Cards resumo */}
      <div className="kpi-cards-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"Manutenções no Mês", value:manutMes.length, color:"#1a9e8f" },
          { label:"Total Gasto (Mês)", value:"R$ "+totalGastoMes.toLocaleString("pt-BR",{minimumFractionDigits:2}), color:"#e67e22" },
          { label:"Veículos em Manutenção", value:veiculosEmManut, color:"#e74c3c" },
          { label:"Total Registros", value:manutencoes.length, color:"#2ecc71" },
        ].map((c,i)=>(
          <div key={i} style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"16px 18px", borderLeft:"4px solid "+c.color }}>
            <div style={{ fontSize:11, color:"#888", marginBottom:4, textTransform:"uppercase", fontWeight:600 }}>{c.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:"#1a1a1a" }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:14, marginBottom:14 }}>
        <div className="filter-row" style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:180 }}>
            <IcoSearch />
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por placa, descrição, oficina..." style={{...inp,paddingLeft:32}} />
          </div>
          <select style={{...inp,width:180,appearance:"none"}} value={filterVeiculo} onChange={e=>setFilterVeiculo(e.target.value)}>
            <option value="">Todos os Veículos</option>
            {frota.map(v=><option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
          </select>
          <select style={{...inp,width:140,appearance:"none"}} value={filterTipo} onChange={e=>setFilterTipo(e.target.value)}>
            <option value="">Todos os Tipos</option>
            <option>Preventiva</option><option>Corretiva</option><option>Revisão</option><option>Inspeção</option>
          </select>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:11, color:"#888", fontWeight:600 }}>De:</span>
            <input type="date" style={{...inp,width:140}} value={filterDe} onChange={e=>setFilterDe(e.target.value)} />
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:11, color:"#888", fontWeight:600 }}>Até:</span>
            <input type="date" style={{...inp,width:140}} value={filterAte} onChange={e=>setFilterAte(e.target.value)} />
          </div>
          {hasFilters && <button onClick={clearFilters} style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"7px 12px", fontSize:11, cursor:"pointer", color:"#888", fontFamily:FONT }}>Limpar</button>}
        </div>
        <div className="page-action-bar" style={{ display:"flex", gap:10, alignItems:"center", marginTop:10, justifyContent:"space-between" }}>
          <div style={{ fontSize:12, color:"#888" }}>
            {filtered.length} registro{filtered.length!==1?"s":""} encontrado{filtered.length!==1?"s":""}
            {selected.size > 0 && <span style={{ color:"#1aae9e", fontWeight:600 }}> — {selected.size} selecionado{selected.size!==1?"s":""}</span>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleExport} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap" }}>
              {selected.size > 0 ? "Exportar Selecionados ("+selected.size+")" : "Exportar Todos"}
            </button>
            <button onClick={()=>{setEditData(null);setModal(true);}} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap" }}>+ Nova Manutenção</button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#f9fafb", borderBottom:"2px solid #e5e7eb" }}>
              <th style={{ padding:"10px 10px", width:36 }}>
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
              </th>
              {["Veículo","Tipo","Descrição","Data","KM","Valor (R$)","Oficina","Ações"].map(h=>(
                <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ padding:40, textAlign:"center", color:"#aaa" }}>Nenhuma manutenção encontrada</td></tr>
            ) : filtered.map(m => (
              <tr key={m.id} style={{ borderBottom:"1px solid #f0f0f0", background: selected.has(m.id) ? "#f0fdfa" : "transparent" }}>
                <td style={{ padding:"10px 10px" }}>
                  <input type="checkbox" checked={selected.has(m.id)} onChange={()=>toggleSelect(m.id)} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
                </td>
                <td style={{ padding:"10px 14px", fontWeight:600 }}>{m.placa || "—"}{m.modelo ? <span style={{ color:"#999", fontWeight:400, fontSize:11, marginLeft:4 }}>{m.modelo}</span> : ""}</td>
                <td style={{ padding:"10px 14px" }}><span style={{ background:m.tipo==="Corretiva"?"#fee2e2":m.tipo==="Preventiva"?"#dbeafe":m.tipo==="Inspeção"?"#fef3c7":"#e0e7ff", color:m.tipo==="Corretiva"?"#dc2626":m.tipo==="Preventiva"?"#1aae9e":m.tipo==="Inspeção"?"#92400e":"#4338ca", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600 }}>{m.tipo}</span></td>
                <td style={{ padding:"10px 14px", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.descricao}</td>
                <td style={{ padding:"10px 14px" }}>{fmtDateBR(m.data_manutencao)}</td>
                <td style={{ padding:"10px 14px", fontFamily:"monospace", fontSize:12 }}>{m.km_veiculo ? parseInt(m.km_veiculo).toLocaleString("pt-BR") : "—"}</td>
                <td style={{ padding:"10px 14px", fontWeight:600, fontFamily:"monospace", fontSize:12 }}>R$ {parseFloat(m.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</td>
                <td style={{ padding:"10px 14px" }}>{m.oficina || "—"}</td>
                <td style={{ padding:"10px 14px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={()=>{setEditData(m);setModal(true);}} style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"4px 8px", cursor:"pointer", fontSize:11, color:"#555" }}>Editar</button>
                    <button onClick={()=>remove(m.id)} style={{ background:"none", border:"1px solid #fcc", borderRadius:3, padding:"4px 8px", cursor:"pointer", fontSize:11, color:"#e74c3c" }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <NovaManutencaoModal frota={frota} editData={editData} onClose={()=>{setModal(false);setEditData(null);}} onSave={save} empresaCfg={empresaCfg} apiFetch={apiFetch} />}
    </div>
  );
}

// ─── Combustível ─────────────────────────────────────────────────────────────
function NovoAbastecimentoModal({ frota, onClose, onSave, editData }) {
  const [form, setForm] = useState(() => {
    const d = editData || { veiculo_id:"", data_abastecimento:hojeLocal(), km_veiculo:"", litros:"", valor:"", posto:"", tipo_combustivel:"Diesel", observacoes:"" };
    return { ...d, kmFmt: initKmFromNumber(d.km_veiculo), valorLitroFmt: initCurrencyFromNumber(d.valor), litrosFmt: d.litros ? String(d.litros).replace('.', ',') : "" };
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const litrosNum = parseFloat(String(form.litros||0).replace(',','.')) || 0;
  const valorLitroNum = parseCurrencyToFloat(form.valorLitroFmt) || parseFloat(form.valor||0);
  const valorTotalCalc = litrosNum > 0 && valorLitroNum > 0 ? (valorLitroNum * litrosNum) : 0;
  const valorTotalFmt = valorTotalCalc > 0 ? valorTotalCalc.toLocaleString('pt-BR', { minimumFractionDigits:2, maximumFractionDigits:2 }) : "—";
  return (
    <LargeModal title={editData?"Editar Abastecimento":"Novo Abastecimento"} subtitle="Registre abastecimento de veículo" onClose={onClose} onSave={()=>onSave({...form, valor: form.valorLitroFmt ? parseCurrencyToFloat(form.valorLitroFmt) : form.valor, km_veiculo: parseKmToNumber(form.kmFmt) || form.km_veiculo })} saveLabel="Salvar" maxWidth={700}>
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Veículo *">
            <select style={{...inp,appearance:"none"}} value={form.veiculo_id} onChange={e=>set("veiculo_id",e.target.value)}>
              <option value="">Selecione...</option>
              {frota.map(v=><option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
            </select>
          </Field>
          <Field label="Tipo Combustível">
            <select style={{...inp,appearance:"none"}} value={form.tipo_combustivel} onChange={e=>set("tipo_combustivel",e.target.value)}>
              <option>Diesel</option><option>Diesel S10</option><option>Gasolina</option><option>Etanol</option><option>GNV</option>
            </select>
          </Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Data"><input type="date" style={inp} value={form.data_abastecimento} onChange={e=>set("data_abastecimento",e.target.value)} /></Field>
          <Field label="KM Atual *">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#999", fontWeight:600, pointerEvents:"none" }}>km</span>
              <input type="text" inputMode="numeric" style={{...inp, paddingLeft:32, textAlign:"right"}} value={form.kmFmt||""} onChange={e=>{ const fmt = formatKmInput(e.target.value); set("kmFmt", fmt); set("km_veiculo", parseKmToNumber(fmt)); }} placeholder="0" />
            </div>
          </Field>
          <Field label="Litros *">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"#999", fontWeight:600, pointerEvents:"none" }}>L</span>
              <input type="text" inputMode="decimal" style={{...inp, paddingLeft:24, textAlign:"right"}} value={form.litrosFmt||""} onChange={e=>{ const v = e.target.value.replace(/[^\d,]/g,''); set("litrosFmt", v); set("litros", v.replace(',','.')); }} placeholder="0,00" />
            </div>
          </Field>
          <Field label="Valor/Litro (R$) *">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"#999", fontWeight:600, pointerEvents:"none" }}>R$</span>
              <input type="text" inputMode="numeric" style={{...inp, paddingLeft:36, textAlign:"right"}} value={form.valorLitroFmt||""} onChange={e=>{ const fmt = formatCurrencyInput(e.target.value); set("valorLitroFmt", fmt); set("valor", parseCurrencyToFloat(fmt)); }} placeholder="0,000" />
            </div>
          </Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Posto"><input style={inp} value={form.posto} onChange={e=>set("posto",e.target.value)} placeholder="Nome do posto" /></Field>
          <Field label="Valor Total (calculado)">
            <div style={{...inp, background:"linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", color:"#16a34a", fontWeight:700, display:"flex", alignItems:"center", gap:6}}>
              <span style={{ fontSize:11, color:"#888", fontWeight:400 }}>R$</span> {valorTotalFmt}
            </div>
          </Field>
        </div>
        <Field label="Observações"><textarea style={{...inp,minHeight:40,resize:"vertical"}} value={form.observacoes} onChange={e=>set("observacoes",e.target.value)} /></Field>
      </SectionBody>
    </LargeModal>
  );
}

function CombustivelPage({ abastecimentos, setAbastecimentos, frota, addToast, apiFetch }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [filterVeiculo, setFilterVeiculo] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterDe, setFilterDe] = useState("");
  const [filterAte, setFilterAte] = useState("");
  const [selected, setSelected] = useState(new Set());

  const fmtDateBR = (ds) => {
    if (!ds) return "—";
    const s = String(ds).split("T")[0];
    if (!s || s.length < 10) return "—";
    const [y,m,d] = s.split("-");
    return d + "/" + m + "/" + y;
  };

  const mesAtual = hojeLocal().slice(0,7);
  const abastMes = abastecimentos.filter(a => {
    const d = String(a.data_abastecimento||"").split("T")[0];
    return d.startsWith(mesAtual);
  });
  const totalGastoMes = abastMes.reduce((s,a) => s + parseFloat(a.valor||0), 0);

  const calcConsumo = () => {
    if (abastecimentos.length < 2) return { mediaKmL: "—", custoKm: "—" };
    const porVeiculo = {};
    abastecimentos.forEach(a => {
      const vid = a.veiculo_id;
      if (!porVeiculo[vid]) porVeiculo[vid] = [];
      porVeiculo[vid].push(a);
    });
    let totalKm = 0, totalLitros = 0, totalValor = 0;
    Object.values(porVeiculo).forEach(regs => {
      const sorted = [...regs].sort((a,b) => (a.km_veiculo||0) - (b.km_veiculo||0));
      for (let i = 1; i < sorted.length; i++) {
        const kmDiff = (sorted[i].km_veiculo||0) - (sorted[i-1].km_veiculo||0);
        if (kmDiff > 0) {
          totalKm += kmDiff;
          totalLitros += parseFloat(sorted[i].litros||0);
          totalValor += parseFloat(sorted[i].valor||0);
        }
      }
    });
    return {
      mediaKmL: totalLitros > 0 ? (totalKm / totalLitros).toFixed(2) : "—",
      custoKm: totalKm > 0 ? "R$ " + (totalValor / totalKm).toFixed(2) : "—"
    };
  };
  const consumo = calcConsumo();

  const enriched = abastecimentos.map(a => {
    const vid = a.veiculo_id;
    const anteriores = abastecimentos.filter(x => x.veiculo_id === vid && (x.km_veiculo||0) < (a.km_veiculo||0)).sort((x,y) => (y.km_veiculo||0) - (x.km_veiculo||0));
    const anterior = anteriores[0];
    let kmL = "—";
    if (anterior) {
      const kmDiff = (a.km_veiculo||0) - (anterior.km_veiculo||0);
      if (kmDiff > 0 && a.litros > 0) kmL = (kmDiff / parseFloat(a.litros)).toFixed(2);
    }
    return { ...a, kmL };
  });

  const filtered = enriched.filter(a => {
    if (filterVeiculo && String(a.veiculo_id) !== filterVeiculo) return false;
    if (filterTipo && a.tipo_combustivel !== filterTipo) return false;
    const ds = String(a.data_abastecimento||"").split("T")[0];
    if (filterDe && ds < filterDe) return false;
    if (filterAte && ds > filterAte) return false;
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (a.placa||"").toLowerCase().includes(s) || (a.posto||"").toLowerCase().includes(s) || (a.tipo_combustivel||"").toLowerCase().includes(s);
  });

  const toggleSelect = (id) => setSelected(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map(a=>a.id))); };

  const save = async (data) => {
    try {
      const cleanData = { ...data };
      delete cleanData.kmFmt; delete cleanData.valorLitroFmt; delete cleanData.litrosFmt;
      cleanData.litros = parseFloat(String(cleanData.litros||0).replace(',','.')) || 0;
      cleanData.valor = parseFloat(cleanData.valor||0);
      const payload = { ...cleanData, valor: cleanData.litros && cleanData.valor ? (cleanData.litros * cleanData.valor).toFixed(2) : cleanData.valor };
      if (editData && editData.id) {
        const upd = await apiFetch("/api/abastecimentos/" + editData.id, { method: "PUT", body: payload });
        setAbastecimentos(p => p.map(a => a.id === upd.id ? upd : a));
        addToast("Abastecimento atualizado!", "success");
      } else {
        const novo = await apiFetch("/api/abastecimentos", { method: "POST", body: payload });
        setAbastecimentos(p => [novo, ...p]);
        addToast("Abastecimento registrado!", "success");
      }
      setModal(false); setEditData(null);
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este abastecimento?")) return;
    try {
      await apiFetch("/api/abastecimentos/" + id, { method: "DELETE" });
      setAbastecimentos(p => p.filter(a => a.id !== id));
      setSelected(p => { const n = new Set(p); n.delete(id); return n; });
      addToast("Abastecimento excluído!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const handleExport = () => {
    const rows = selected.size > 0 ? filtered.filter(a => selected.has(a.id)) : filtered;
    exportToExcel(rows, [
      { label:"Veículo", key: r => (r.placa||"") + " " + (r.modelo||"") },
      { label:"Data", key: r => fmtDateBR(r.data_abastecimento) },
      { label:"KM", key:"km_veiculo" },
      { label:"Litros", key: r => parseFloat(r.litros||0).toFixed(1) },
      { label:"Valor Total (R$)", key: r => parseFloat(r.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2}) },
      { label:"R$/L", key: r => r.litros && r.valor ? (parseFloat(r.valor)/parseFloat(r.litros)).toFixed(3) : "—" },
      { label:"Km/L", key:"kmL" },
      { label:"Combustível", key:"tipo_combustivel" },
      { label:"Posto", key:"posto" }
    ], "abastecimentos");
    addToast(selected.size > 0 ? selected.size + " registros exportados" : "Todos exportados", "success");
  };

  const clearFilters = () => { setFilterVeiculo(""); setFilterTipo(""); setFilterDe(""); setFilterAte(""); setSearch(""); };
  const hasFilters = filterVeiculo || filterTipo || filterDe || filterAte || search;

  return (
    <div>
      {/* Cards resumo */}
      <div className="kpi-cards-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"Consumo Médio (Km/L)", value:consumo.mediaKmL, color:"#1a9e8f" },
          { label:"Total Gasto (Mês)", value:"R$ "+totalGastoMes.toLocaleString("pt-BR",{minimumFractionDigits:2}), color:"#e67e22" },
          { label:"Custo por Km", value:consumo.custoKm, color:"#9b59b6" },
          { label:"Abastecimentos (Mês)", value:abastMes.length, color:"#2ecc71" },
        ].map((c,i)=>(
          <div key={i} style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"16px 18px", borderLeft:"4px solid "+c.color }}>
            <div style={{ fontSize:11, color:"#888", marginBottom:4, textTransform:"uppercase", fontWeight:600 }}>{c.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:"#1a1a1a" }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:14, marginBottom:14 }}>
        <div className="filter-row" style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ position:"relative", flex:1, minWidth:160 }}>
            <IcoSearch />
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar por placa, posto..." style={{...inp,paddingLeft:32}} />
          </div>
          <select style={{...inp,width:180,appearance:"none"}} value={filterVeiculo} onChange={e=>setFilterVeiculo(e.target.value)}>
            <option value="">Todos os Veículos</option>
            {frota.map(v=><option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
          </select>
          <select style={{...inp,width:140,appearance:"none"}} value={filterTipo} onChange={e=>setFilterTipo(e.target.value)}>
            <option value="">Combustível</option>
            <option>Diesel</option><option>Diesel S10</option><option>Gasolina</option><option>Etanol</option><option>GNV</option>
          </select>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:11, color:"#888", fontWeight:600 }}>De:</span>
            <input type="date" style={{...inp,width:140}} value={filterDe} onChange={e=>setFilterDe(e.target.value)} />
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:11, color:"#888", fontWeight:600 }}>Até:</span>
            <input type="date" style={{...inp,width:140}} value={filterAte} onChange={e=>setFilterAte(e.target.value)} />
          </div>
          {hasFilters && <button onClick={clearFilters} style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"7px 12px", fontSize:11, cursor:"pointer", color:"#888", fontFamily:FONT }}>Limpar</button>}
        </div>
        <div className="page-action-bar" style={{ display:"flex", gap:10, alignItems:"center", marginTop:10, justifyContent:"space-between" }}>
          <div style={{ fontSize:12, color:"#888" }}>
            {filtered.length} registro{filtered.length!==1?"s":""} encontrado{filtered.length!==1?"s":""}
            {selected.size > 0 && <span style={{ color:"#1aae9e", fontWeight:600 }}> — {selected.size} selecionado{selected.size!==1?"s":""}</span>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={handleExport} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap" }}>
              {selected.size > 0 ? "Exportar Selecionados ("+selected.size+")" : "Exportar Todos"}
            </button>
            <button onClick={()=>{setEditData(null);setModal(true);}} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap" }}>+ Novo Abastecimento</button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:"#f9fafb", borderBottom:"2px solid #e5e7eb" }}>
              <th style={{ padding:"10px 10px", width:36 }}>
                <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
              </th>
              {["Veículo","Data","KM","Litros","Valor (R$)","R$/L","Km/L","Combustível","Posto","Ações"].map(h=>(
                <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={11} style={{ padding:40, textAlign:"center", color:"#aaa" }}>Nenhum abastecimento encontrado</td></tr>
            ) : filtered.map(a => {
              const precoL = a.litros && a.valor ? (parseFloat(a.valor)/parseFloat(a.litros)).toFixed(3) : "—";
              return (
                <tr key={a.id} style={{ borderBottom:"1px solid #f0f0f0", background: selected.has(a.id) ? "#f0fdfa" : "transparent" }}>
                  <td style={{ padding:"10px 10px" }}>
                    <input type="checkbox" checked={selected.has(a.id)} onChange={()=>toggleSelect(a.id)} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
                  </td>
                  <td style={{ padding:"10px 14px", fontWeight:600 }}>{a.placa || "—"}{a.modelo ? <span style={{ color:"#999", fontWeight:400, fontSize:11, marginLeft:4 }}>{a.modelo}</span> : ""}</td>
                  <td style={{ padding:"10px 14px" }}>{fmtDateBR(a.data_abastecimento)}</td>
                  <td style={{ padding:"10px 14px", fontFamily:"monospace", fontSize:12 }}>{a.km_veiculo ? parseInt(a.km_veiculo).toLocaleString("pt-BR") : "—"}</td>
                  <td style={{ padding:"10px 14px" }}>{parseFloat(a.litros||0).toFixed(1)}</td>
                  <td style={{ padding:"10px 14px", fontWeight:600, fontFamily:"monospace", fontSize:12 }}>R$ {parseFloat(a.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</td>
                  <td style={{ padding:"10px 14px", fontFamily:"monospace", fontSize:12 }}>{precoL}</td>
                  <td style={{ padding:"10px 14px" }}><span style={{ fontWeight:700, color:a.kmL!=="—"&&parseFloat(a.kmL)<3?"#e74c3c":"#2ecc71" }}>{a.kmL}</span></td>
                  <td style={{ padding:"10px 14px" }}><span style={{ background:"#f3f4f6", padding:"2px 6px", borderRadius:8, fontSize:11 }}>{a.tipo_combustivel||"—"}</span></td>
                  <td style={{ padding:"10px 14px" }}>{a.posto || "—"}</td>
                  <td style={{ padding:"10px 14px" }}>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>{const valorLitro = a.litros && a.valor ? parseFloat(a.valor)/parseFloat(a.litros) : parseFloat(a.valor||0); setEditData({...a, valor: valorLitro});setModal(true);}} style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"4px 8px", cursor:"pointer", fontSize:11, color:"#555" }}>Editar</button>
                      <button onClick={()=>remove(a.id)} style={{ background:"none", border:"1px solid #fcc", borderRadius:3, padding:"4px 8px", cursor:"pointer", fontSize:11, color:"#e74c3c" }}>Excluir</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && <NovoAbastecimentoModal frota={frota} editData={editData} onClose={()=>{setModal(false);setEditData(null);}} onSave={save} />}
    </div>
  );
}

function Frota({ frota, setFrota, motoristas = [], addToast, apiFetch, empresaCfg }) {
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);

  const isVencendo = (dateStr) => {
    if (!dateStr) return false;
    const d = String(dateStr).split("T")[0];
    const date = new Date(d + "T12:00:00");
    if (isNaN(date)) return false;
    return (date - new Date()) / (1000*60*60*24) <= 30;
  };

  const filtered = frota.filter(v =>
    norm(v.placa).includes(norm(search)) ||
    norm(v.modelo).includes(norm(search)) ||
    norm(v.motorista).includes(norm(search))
  );

  const veiculoSel = sel !== null ? filtered[sel] : null;

  const DateCell = ({ val }) => {
    if (!val) return <span style={{ color:"#aaa" }}>—</span>;
    const d = String(val).split("T")[0];
    const formatted = d.length === 10 ? d.split("-").reverse().join("/") : d;
    return (
      <span style={{ color: isVencendo(val) ? "#e74c3c" : "#444", fontWeight: isVencendo(val) ? 700 : 400, fontSize: 12 }}>
        {formatted} {isVencendo(val) && ""}
      </span>
    );
  };

  const save = async (data) => {
    try {
      if (data.id) {
        const upd = await apiFetch("/api/frota/" + data.id, { method: "PUT", body: data });
        setFrota(p => p.map(v => v.id === upd.id ? { ...upd, vencCNH: upd.venc_cnh||"", vencCRLV: upd.venc_crlv||"", vencSeguro: upd.venc_seguro||"" } : v));
        if (addToast) addToast("Veículo atualizado!", "", "", "#2ecc71");
      } else {
        const novo = await apiFetch("/api/frota", { method: "POST", body: data });
        setFrota(p => [...p, { ...novo, vencCNH: novo.venc_cnh||"", vencCRLV: novo.venc_crlv||"", vencSeguro: novo.venc_seguro||"" }]);
        if (addToast) addToast("Veículo cadastrado!", "", "", "#1aae9e");
      }
      setModal(false); setEditData(null);
    } catch(err) { alert("Erro: " + err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este veículo?")) return;
    try {
      await apiFetch("/api/frota/" + id, { method: "DELETE" });
      setFrota(p => p.filter(v => v.id !== id));
      setSel(null);
      if (addToast) addToast("Veículo excluído!", "", "", "#e74c3c");
    } catch(err) { alert("Erro: " + err.message); }
  };

  return (
    <div>
      <div className="kpi-cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 18 }}>
        {[
          { label: "Total de Veículos", value: frota.length, color: "#1a9e8f", icon: "🚛" },
          { label: "Ativos", value: frota.filter(v => v.status === "Ativo").length, color: "#2ecc71", icon: "✅" },
          { label: "Em Manutenção", value: frota.filter(v => v.status === "Manutenção").length, color: "#f39c12", icon: "🔧" },
          { label: "Docs Vencendo", value: frota.filter(v => isVencendo(v.vencCRLV) || isVencendo(v.vencSeguro)).length, color: "#e74c3c", icon: "⚠️" },
        ].map((c, i) => (
          <div key={i} style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 4, padding: "14px 16px", borderTop: "3px solid " + c.color }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: .3, marginBottom: 5 }}>{c.label}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#1a1a1a" }}>{c.value}</div>
              </div>
              <span style={{ fontSize: 22 }}>{c.icon}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="page-action-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa" }}><IcoSearch /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Placa, modelo ou motorista..." style={{ ...inp, width: 300, paddingLeft: 34 }} />
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => exportToExcel(filtered, [
            {label:"Placa",key:r=>r.placa||""},{label:"Modelo",key:r=>r.modelo||""},{label:"Ano",key:r=>r.ano||""},
            {label:"Motorista",key:r=>r.motorista||""},{label:"KM",key:r=>r.km||""},{label:"Status",key:r=>r.status||""},
            {label:"Venc. CRLV",key:r=>r.vencCRLV||r.venc_crlv||""},{label:"Venc. Seguro",key:r=>r.vencSeguro||r.venc_seguro||""},
          ], "frota")} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
            Exportar Excel
          </button>
          <button onClick={() => { setEditData(null); setModal(true); }} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 3, padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: FONT }}>
            <IcoPlus /> Novo Veículo
          </button>
        </div>
      </div>

      <div className="tbl-wrap" style={{ background: "#fff", border: "1px solid #e8e8e8", borderRadius: 4, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#fafafa" }}>
            <tr>
              {["Placa","Modelo","Ano","Combustível","Motorista","KM","Venc. CRLV","Venc. Seguro","Status","Ações"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "9px 12px", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: .3, borderBottom: "1px solid #ebebeb" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10}><EmptyState icon="🚛" title="Nenhum veículo cadastrado" subtitle="Clique em 'Novo Veículo' para adicionar à frota" /></td></tr>
            ) : filtered.map((v, i) => (
              <tr key={v.id} style={{ borderBottom: "1px solid #f4f4f4" }}
                onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background=""}>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ fontWeight: 800, fontSize: 12, color: "#1a1a1a", background: "#f5f5f5", padding: "3px 7px", borderRadius: 3, letterSpacing: 1, fontFamily: "monospace" }}>{v.placa}</span>
                </td>
                <td style={{ padding: "10px 12px", fontWeight: 600, color: "#333" }}>{v.modelo}</td>
                <td style={{ padding: "10px 12px", color: "#666" }}>{v.ano}</td>
                <td style={{ padding: "10px 12px", color: "#666", fontSize: 12 }}>{v.combustivel||"—"}</td>
                <td style={{ padding: "10px 12px", color: "#444" }}>{v.motorista||"—"}</td>
                <td style={{ padding: "10px 12px", color: "#555", fontSize: 12 }}>{(Number(v.km) || 0).toLocaleString("pt-BR")} km</td>
                <td style={{ padding: "10px 12px" }}><DateCell val={v.vencCRLV||v.venc_crlv} /></td>
                <td style={{ padding: "10px 12px" }}><DateCell val={v.vencSeguro||v.venc_seguro} /></td>
                <td style={{ padding: "10px 12px" }}><StatusBadge label={v.status} /></td>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={() => { setEditData(v); setModal(true); }} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#555", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEdit /> Editar</button>
                    <button onClick={() => remove(v.id)} style={{ background:"#fee", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:600 }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "9px 12px", borderTop: "1px solid #f0f0f0", fontSize: 12, color: "#aaa" }}>{filtered.length} veículo(s)</div>
      </div>

      {modal && <VeiculoModal motoristas={motoristas} editData={editData} empresaCfg={empresaCfg} apiFetch={apiFetch} onClose={() => { setModal(false); setEditData(null); }} onSave={save} />}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ locacoes, clientes, cacambas, motoristas, frota, manutencoes, goToLocacoes, userName }) {
  const hoje = hojeLocal();
  const hojeDate = new Date();
  const monthNames = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const monthFull = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  // ─── Filtro de período ───────────────────────────────────────────────────────
  const [periodo, setPeriodo] = useState("mes");
  const [periodoCustom, setPeriodoCustom] = useState({ de:"", ate:"" });
  const [mesNav, setMesNav] = useState({ y: hojeDate.getFullYear(), m: hojeDate.getMonth() });
  const navegarMes = (delta) => {
    setPeriodo("mes");
    setMesNav(p => { let m = p.m + delta, y = p.y; if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; } return { y, m }; });
  };
  const isMesCorrente = mesNav.y === hojeDate.getFullYear() && mesNav.m === hojeDate.getMonth();

  const getDateRange = () => {
    const now = new Date();
    if (periodo === "hoje") return { de: hoje, ate: hoje };
    if (periodo === "semana") {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate()); const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      const dStr = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
      const domDate = new Date(d); domDate.setDate(domDate.getDate()+6);
      const domStr = domDate.getFullYear()+"-"+String(domDate.getMonth()+1).padStart(2,"0")+"-"+String(domDate.getDate()).padStart(2,"0");
      return { de: dStr, ate: domStr };
    }
    if (periodo === "custom" && periodoCustom.de && periodoCustom.ate) return { de: periodoCustom.de, ate: periodoCustom.ate };
    // Mês (navegável pelas setas): do dia 1 ao ÚLTIMO dia do mês selecionado
    const lastDay = new Date(mesNav.y, mesNav.m + 1, 0).getDate();
    return { de: `${mesNav.y}-${String(mesNav.m+1).padStart(2,"0")}-01`, ate: `${mesNav.y}-${String(mesNav.m+1).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}` };
  };
  const range = getDateRange();
  const inRange = (ds) => ds && ds >= range.de && ds <= range.ate;

  // ─── Cores iguais às abas de Locações ──────────────────────────────────────
  const COR = {
    "Para Colocar":"#1a9e8f", "Colocada":"#2ecc71", "Para Retirar":"#9b59b6",
    "Para Trocar":"#f39c12", "Para Cobrar":"#f59e0b", "Encerrada":"#888", "Cancelada":"#e74c3c",
    atrasada:"#e74c3c", recebido:"#2ecc71", aReceber:"#e74c3c",
  };

  // ─── Contagens por status ──────────────────────────────────────────────────
  // Filha de troca: locação com status "Para Colocar" gerada automaticamente por troca
  const isFilhaTrocaDash = (l) => {
    if (l.status !== "Para Colocar") return false;
    const obs = l.obs || "";
    return typeof obs === "string" && (obs.toLowerCase().includes("troca da locação #") || obs.toLowerCase().includes("troca da locacao #"));
  };
  const countByStatus = (st) => locacoes.filter(l => {
    if (l.status !== st) return false;
    // Não contar filhas de troca no card "Para Colocar"
    if (st === "Para Colocar" && isFilhaTrocaDash(l)) return false;
    return true;
  }).length;
  const statusCards = [
    { label:"Para Colocar", key:"Para Colocar" },
    { label:"Colocada",     key:"Colocada" },
    { label:"Para Retirar", key:"Para Retirar" },
    { label:"Para Trocar",  key:"Para Trocar" },
    { label:"Para Cobrar",  key:"Para Cobrar" },
    { label:"Encerrada",    key:"Encerrada" },
  ];

  // ─── Dados operacionais ────────────────────────────────────────────────────
  const atrasadas = locacoes.filter(l => {
    if (["Cancelada","Encerrada","Para Cobrar"].includes(l.status)) return false;
    const dr = l.status === "Para Colocar" ? (l.dataEntrega||l.data_entrega) : (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada);
    return dr && dr < hoje;
  }).length;
  const cacambasAtivas = locacoes.filter(l => ["Colocada","Para Trocar","Para Retirar"].includes(l.status)).length;

  // ─── Dados financeiros ─────────────────────────────────────────────────────
  const mesAtual = hojeDate.getMonth();
  const anoAtual = hojeDate.getFullYear();
  // locsPeriodo: locações com dataEntrega no período (para contagens operacionais)
  const locsPeriodo = locacoes.filter(l => l.status !== "Cancelada" && inRange(l.dataEntrega || l.data_entrega));
  const faturamentoPeriodo = locsPeriodo.reduce((s, l) => s + (l.valor || 0), 0);
  // Recebido: filtra por DATA DO PAGAMENTO no período (não data de entrega!)
  const recebidoPeriodo = locacoes.filter(l => l.pago && l.status !== "Cancelada" && inRange(l.dataPagamento || l.data_pagamento)).reduce((s, l) => s + (l.valor || 0), 0);
  const recebidoPeriodoQtd = locacoes.filter(l => l.pago && l.status !== "Cancelada" && inRange(l.dataPagamento || l.data_pagamento)).length;
  const aReceberPeriodo = locsPeriodo.filter(l => !l.pago).reduce((s, l) => s + (l.valor || 0), 0);
  const aReceberTotal = locacoes.filter(l => !l.pago && l.status !== "Cancelada").reduce((s, l) => s + (l.valor || 0), 0);
  const aReceberTotalQtd = locacoes.filter(l => !l.pago && l.status !== "Cancelada").length;
  const faturamentoTotal = locacoes.filter(l => l.status !== "Cancelada").reduce((s, l) => s + (l.valor || 0), 0);
  const recebidoTotal = locacoes.filter(l => l.pago && l.status !== "Cancelada").reduce((s, l) => s + (l.valor || 0), 0);
  const totalLocacoes = locacoes.filter(l => l.status !== "Cancelada").length;

  // ─── Alertas do dia ────────────────────────────────────────────────────────
  const paraEntregarHoje = locacoes.filter(l => l.status === "Para Colocar" && (l.dataEntrega||l.data_entrega) === hoje).length;
  const paraRetirarHoje = locacoes.filter(l => (l.status === "Para Retirar") && (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada) === hoje).length;
  const paraTrocarHoje = locacoes.filter(l => l.status === "Para Trocar" && (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada) === hoje).length;
  const pagVencidos = locacoes.filter(l => !l.pago && l.status !== "Cancelada" && (l.dataEntrega||l.data_entrega) && (l.dataEntrega||l.data_entrega) < hoje).length;
  const paradasMuito = locacoes.filter(l => { if (l.status !== "Colocada") return false; const de = l.dataEntrega||l.data_entrega; return de && (new Date(hoje)-new Date(de))/(1000*60*60*24) > 14; }).length;

  const alertas = [];
  if (atrasadas > 0) alertas.push({ label:`${atrasadas} locações atrasadas`, color:COR.atrasada, action:() => goToLocacoes("Para Colocar") });
  if (paraEntregarHoje > 0) alertas.push({ label:`${paraEntregarHoje} entrega${paraEntregarHoje>1?"s":""} para hoje`, color:COR["Para Colocar"], action:() => goToLocacoes("Para Colocar") });
  if (paraRetirarHoje > 0) alertas.push({ label:`${paraRetirarHoje} retirada${paraRetirarHoje>1?"s":""} para hoje`, color:COR["Para Retirar"], action:() => goToLocacoes("Para Retirar") });
  if (paraTrocarHoje > 0) alertas.push({ label:`${paraTrocarHoje} troca${paraTrocarHoje>1?"s":""} para hoje`, color:COR["Para Trocar"], action:() => goToLocacoes("Para Trocar") });
  if (pagVencidos > 0) alertas.push({ label:`${pagVencidos} pagamentos vencidos`, color:COR["Para Cobrar"], action:() => goToLocacoes("Para Cobrar") });
  if (paradasMuito > 0) alertas.push({ label:`${paradasMuito} caçambas paradas há +14 dias`, color:"#d97706", action:() => goToLocacoes("Colocada") });

  // ─── Operações do período (para gráficos horizontais) ──────────────────────
  // Cada operação filtra por SUA PRÓPRIA data (não só data_entrega)

  // Entregas: locações que foram entregues no período (usa data_colocacao_real, ou data_entrega se já passou de "Para Colocar")
  const entRealizadas = locacoes.filter(l => {
    if (l.status === "Para Colocar" || l.status === "Cancelada") return false;
    const dataReal = l.dataColocacaoReal || l.data_colocacao_real;
    const dataEnt = l.dataEntrega || l.data_entrega;
    return inRange(dataReal || dataEnt);
  }).length;
  const entComAtraso = locacoes.filter(l => {
    if (l.status === "Para Colocar" || l.status === "Cancelada") return false;
    const prev = l.dataEntrega || l.data_entrega;
    const real = l.dataColocacaoReal || l.data_colocacao_real;
    const dataRef = real || prev;
    if (!inRange(dataRef)) return false;
    return prev && real && real > prev;
  }).length;

  // Retiradas: locações que foram retiradas no período (usa data_retirada_real)
  const retRealizadas = locacoes.filter(l => {
    if (!["Encerrada","Para Cobrar"].includes(l.status)) return false;
    const dr = l.dataRetiradaReal || l.data_retirada_real;
    return inRange(dr);
  }).length;
  const retComAtraso = locacoes.filter(l => {
    if (!["Encerrada","Para Cobrar"].includes(l.status)) return false;
    const dr = l.dataRetiradaReal || l.data_retirada_real;
    if (!inRange(dr)) return false;
    const dp = l.dataRetiraPrevista || l.dataRetirada || l.data_retirada;
    return dp && dr && dr > dp;
  }).length;

  // Trocas: locações que tiveram troca realizada no período
  const trcRealizadas = locacoes.filter(l => {
    const dtp = l.dataTrocaPrevista || l.data_troca_prevista;
    if (!dtp || ["Para Trocar","Cancelada","Para Colocar"].includes(l.status)) return false;
    return inRange(dtp);
  }).length;
  const trcComAtraso = locacoes.filter(l => {
    const dtp = l.dataTrocaPrevista || l.data_troca_prevista;
    if (!dtp || ["Para Trocar","Cancelada","Para Colocar"].includes(l.status)) return false;
    if (!inRange(dtp)) return false;
    const real = l.dataRetiradaReal || l.data_retirada_real;
    return real && real > dtp;
  }).length;

  // Encerradas e canceladas no período
  const encerradas = locacoes.filter(l => {
    if (l.status !== "Encerrada") return false;
    const dr = l.dataRetiradaReal || l.data_retirada_real;
    const de = l.dataEntrega || l.data_entrega;
    return inRange(dr || de);
  }).length;
  const canceladas = locacoes.filter(l => { const de = l.dataEntrega||l.data_entrega; return l.status === "Cancelada" && inRange(de); }).length;

  // ─── Faturamento últimos 6 meses ───────────────────────────────────────────
  const months = []; const fats = []; const fatsPago = []; const monthDates = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(anoAtual, mesAtual - i, 1);
    months.push(monthNames[d.getMonth()]);
    monthDates.push({ month: d.getMonth(), year: d.getFullYear() });
    const mn = d.getMonth(), an = d.getFullYear();
    const ml = locacoes.filter(l => { const de = l.dataEntrega||l.data_entrega; if (!de) return false; const dt = new Date(de); return dt.getMonth()===mn && dt.getFullYear()===an && l.status !== "Cancelada"; });
    fats.push(ml.reduce((s,l) => s+(l.valor||0), 0));
    // Recebido por mês: usa dataPagamento (quando o dinheiro entrou)
    const pagos = locacoes.filter(l => { const dp = l.dataPagamento||l.data_pagamento; if (!dp || !l.pago) return false; const dt = new Date(dp); return dt.getMonth()===mn && dt.getFullYear()===an && l.status !== "Cancelada"; });
    fatsPago.push(pagos.reduce((s,l) => s+(l.valor||0), 0));
  }
  const maxFat = Math.max(...fatsPago, 1);
  const fatAnt = fatsPago.length >= 2 ? fatsPago[fatsPago.length - 2] : 0;
  const fatAtual = fatsPago[fatsPago.length - 1];
  const varFat = fatAnt > 0 ? ((fatAtual - fatAnt) / fatAnt * 100) : 0;

  const periodoLabel = periodo === "hoje" ? "Hoje" : periodo === "semana" ? "Semana Atual" : periodo === "mes" ? (monthFull[mesNav.m] + "/" + mesNav.y) : "Personalizado";

  // ─── Top Clientes do período ──────────────────────────────────────────────
  const clienteMap = {};
  locsPeriodo.forEach(l => {
    const nome = l.cliente || "Sem nome";
    if (!clienteMap[nome]) clienteMap[nome] = { nome, qtd:0, valor:0 };
    clienteMap[nome].qtd++;
    clienteMap[nome].valor += (l.valor || 0);
  });
  const topClientes = Object.values(clienteMap).sort((a,b) => b.qtd - a.qtd).slice(0,5);

  // ─── Estado do gráfico selecionado ────────────────────────────────────────
  const [chartSelected, setChartSelected] = useState(null);

  // ─── Estilos ───────────────────────────────────────────────────────────────
  const card = { background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, boxShadow:"0 1px 3px rgba(0,0,0,.04)" };

  // Saudação por horário
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";

  return (
    <div style={{ margin:"0 auto" }}>

      {/* ═══ BOAS-VINDAS ═══ */}
      <div style={{ background:"#fff", borderRadius:10, padding:"20px 24px", marginBottom:18, border:"1px solid #e5e7eb", boxShadow:"0 1px 3px rgba(0,0,0,.04)" }}>
        <div style={{ fontSize:18, fontWeight:700, color:"#1e2d4f" }}>{saudacao}, {userName || "Operador"}</div>
        <div style={{ fontSize:13, color:"#888", marginTop:4 }}>Bem-vindo ao Controla Cacamba. Acompanhe aqui o resumo das suas operacoes do dia.</div>
      </div>

      {/* ═══ FILTRO DE PERÍODO ═══ */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div className="relatorio-period-btns" style={{ display:"flex", alignItems:"center", gap:4, background:"#f5f5f5", borderRadius:8, padding:3 }}>
          {[{k:"hoje",l:"Hoje"},{k:"semana",l:"Semana"}].map(p => (
            <button key={p.k} onClick={() => { setPeriodo(p.k); setChartSelected(null); }} style={{
              padding:"7px 16px", borderRadius:6, fontSize:12, fontWeight:600, border:"none",
              background: periodo === p.k ? "#fff" : "transparent",
              color: periodo === p.k ? "#1a1a1a" : "#888",
              cursor:"pointer", transition:"all .15s",
              boxShadow: periodo === p.k ? "0 1px 3px rgba(0,0,0,.1)" : "none"
            }}>{p.l}</button>
          ))}
          {/* navegador de mês: ‹ junho/2026 › */}
          <div style={{ display:"flex", alignItems:"center", borderRadius:6,
            background: periodo === "mes" ? "#fff" : "transparent",
            boxShadow: periodo === "mes" ? "0 1px 3px rgba(0,0,0,.1)" : "none", transition:"all .15s" }}>
            <button onClick={() => { navegarMes(-1); setChartSelected(null); }} title="Mês anterior" style={{
              border:"none", background:"none", cursor:"pointer", padding:"7px 9px", borderRadius:6,
              fontSize:15, fontWeight:800, lineHeight:1, color: periodo === "mes" ? "#1aae9e" : "#888", transition:"all .15s"
            }}>‹</button>
            <button onClick={() => { setPeriodo("mes"); setMesNav({ y: hojeDate.getFullYear(), m: hojeDate.getMonth() }); setChartSelected(null); }}
              title={isMesCorrente ? "Mês atual" : "Voltar ao mês atual"} style={{
              border:"none", background:"none", cursor:"pointer", padding:"7px 2px", minWidth:96, textAlign:"center",
              fontSize:12, fontWeight:700, color: periodo === "mes" ? "#1a1a1a" : "#888", transition:"all .15s"
            }}>{monthFull[mesNav.m]}/{mesNav.y}</button>
            <button onClick={() => { navegarMes(1); setChartSelected(null); }} title="Próximo mês" style={{
              border:"none", background:"none", cursor:"pointer", padding:"7px 9px", borderRadius:6,
              fontSize:15, fontWeight:800, lineHeight:1, color: periodo === "mes" ? "#1aae9e" : "#888", transition:"all .15s"
            }}>›</button>
          </div>
          <button onClick={() => { setPeriodo("custom"); setChartSelected(null); }} style={{
            padding:"7px 16px", borderRadius:6, fontSize:12, fontWeight:600, border:"none",
            background: periodo === "custom" ? "#fff" : "transparent",
            color: periodo === "custom" ? "#1a1a1a" : "#888",
            cursor:"pointer", transition:"all .15s",
            boxShadow: periodo === "custom" ? "0 1px 3px rgba(0,0,0,.1)" : "none"
          }}>Personalizado</button>
        </div>
        {periodo === "custom" && (
          <div style={{ display:"flex", gap:4 }}>
            <input type="date" value={periodoCustom.de} onChange={e => setPeriodoCustom(v => ({...v, de:e.target.value}))} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #e0e0e0", fontSize:12 }} />
            <input type="date" value={periodoCustom.ate} onChange={e => setPeriodoCustom(v => ({...v, ate:e.target.value}))} style={{ padding:"6px 10px", borderRadius:6, border:"1px solid #e0e0e0", fontSize:12 }} />
          </div>
        )}
      </div>

      {/* ═══ ALERTAS ═══ */}
      {alertas.length > 0 && (
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          {alertas.map((a,i) => (
            <div key={i} onClick={a.action} style={{
              display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:8,
              background:"#fff", border:`1px solid ${a.color}30`, cursor:"pointer", transition:"all .15s"
            }}
              onMouseEnter={e => { e.currentTarget.style.background=`${a.color}10`; e.currentTarget.style.transform="translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.transform=""; }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background:a.color, flexShrink:0 }} />
              <span style={{ fontSize:12, fontWeight:600, color:a.color }}>{a.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ═══ STATUS DAS LOCAÇÕES (cards principais) ═══ */}
      <div className="dash-cards-grid" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:18 }}>
        {statusCards.map(s => {
          const count = countByStatus(s.key);
          const cor = s.key === "Para Cobrar" ? "#e74c3c" : COR[s.key];
          const isCobrar = s.key === "Para Cobrar";
          return (
            <div key={s.key} onClick={() => goToLocacoes(s.key)} style={{
              ...card, padding:"20px 18px", cursor:"pointer", transition:"all .15s",
              borderLeft:`4px solid ${cor}`,
              background: isCobrar && count > 0 ? "#fef2f2" : "#fff",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,.04)"; }}>
              <div style={{ fontSize:11, fontWeight:700, color:isCobrar?"#e74c3c":"#888", textTransform:"uppercase", letterSpacing:.5, marginBottom:10 }}>{s.label}</div>
              <div style={{ fontSize:32, fontWeight:800, color:cor, lineHeight:1 }}><AnimatedNumber value={count} /></div>
            </div>
          );
        })}
      </div>

      {/* ═══ INDICADORES RÁPIDOS ═══ */}
      <div className="dash-cards-row3" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:18 }}>
        <div onClick={() => goToLocacoes("Para Colocar")} style={{ ...card, padding:"18px 20px", cursor:"pointer", transition:"all .15s", background:atrasadas>0?"#fef2f2":"#fff", borderLeft:"4px solid #e74c3c" }}
          onMouseEnter={e => e.currentTarget.style.transform="translateY(-1px)"} onMouseLeave={e => e.currentTarget.style.transform=""}>
          <div style={{ fontSize:11, fontWeight:700, color:"#e74c3c", textTransform:"uppercase", letterSpacing:.4, marginBottom:8 }}>Atrasadas</div>
          <div style={{ fontSize:28, fontWeight:800, color:"#e74c3c" }}><AnimatedNumber value={atrasadas} /></div>
        </div>
        <div style={{ ...card, padding:"18px 20px", borderLeft:"4px solid #2ecc71" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.4, marginBottom:8 }}>Caçambas na Rua</div>
          <div style={{ fontSize:28, fontWeight:800, color:COR["Colocada"] }}><AnimatedNumber value={cacambasAtivas} /></div>
        </div>
        <div style={{ ...card, padding:"18px 20px", borderLeft:"4px solid #1a9e8f" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.4, marginBottom:8 }}>Total Locações</div>
          <div style={{ fontSize:28, fontWeight:800, color:"#1a9e8f" }}><AnimatedNumber value={totalLocacoes} /></div>
        </div>
      </div>

      {/* ═══ FINANCEIRO — RECEBIDO x A RECEBER ═══ */}
      <div className="dash-finance-cards" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:18 }}>
        {/* Recebido */}
        <div style={{ ...card, padding:"28px 32px", background:"linear-gradient(135deg, #1a9e8f 0%, #148f82 50%, #0d7a6e 100%)", border:"none", color:"#fff", borderRadius:14, position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,.06)" }} />
          <div style={{ position:"relative" }}>
            <div style={{ fontSize:13, fontWeight:600, opacity:.85, letterSpacing:.3, marginBottom:6 }}>Você já faturou em {periodoLabel}</div>
            <div style={{ fontSize:38, fontWeight:800, lineHeight:1.1, letterSpacing:-.5 }}>{fmt(recebidoPeriodo)}</div>
            <div style={{ fontSize:12, opacity:.6, marginTop:8 }}>{recebidoPeriodoQtd} locaç{recebidoPeriodoQtd !== 1 ? "ões pagas" : "ão paga"}</div>
          </div>
        </div>
        {/* A Receber */}
        <div onClick={() => goToLocacoes("Para Cobrar")} style={{ ...card, padding:"28px 32px", background:"linear-gradient(135deg, #c0392b 0%, #a93226 50%, #922b21 100%)", border:"none", color:"#fff", borderRadius:14, position:"relative", overflow:"hidden", cursor:"pointer", transition:"all .15s" }}
          onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(192,57,43,.25)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="none"; }}>
          <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120, borderRadius:"50%", background:"rgba(255,255,255,.06)" }} />
          <div style={{ position:"relative" }}>
            <div style={{ fontSize:13, fontWeight:600, opacity:.85, letterSpacing:.3, marginBottom:6 }}>Você tem a receber de seus clientes</div>
            <div style={{ fontSize:38, fontWeight:800, lineHeight:1.1, letterSpacing:-.5 }}>{fmt(aReceberTotal)}</div>
            <div style={{ fontSize:12, opacity:.6, marginTop:8 }}>{aReceberTotalQtd} locaç{aReceberTotalQtd !== 1 ? "ões pendentes" : "ão pendente"}</div>
          </div>
        </div>
      </div>

      {/* ═══ OPERAÇÕES DO PERÍODO (barras horizontais) ═══ */}
      {(() => {
        const HBar = ({ label, value, max, color }) => {
          const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
          return (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ width:90, fontSize:12, color:"#666", textAlign:"right", flexShrink:0, fontWeight:500 }}>{label}</div>
              <div style={{ flex:1, height:28, background:"#f5f5f5", borderRadius:6, overflow:"hidden", position:"relative" }}>
                <div style={{ height:"100%", width:`${pct}%`, background:color, borderRadius:6, transition:"width .6s ease", minWidth: value > 0 ? 2 : 0 }} />
              </div>
              <div style={{ width:36, fontSize:13, fontWeight:700, color, textAlign:"right", flexShrink:0 }}>{value}</div>
            </div>
          );
        };
        const allVals = [entRealizadas, entComAtraso, retRealizadas, retComAtraso, trcRealizadas, trcComAtraso, encerradas, canceladas];
        const globalMax = Math.max(...allVals, 1);
        return (
          <div className="dash-operations" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:18 }}>
            {/* Entregas */}
            <div style={{ ...card, padding:20 }}>
              <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Entregas</div>
              <HBar label="Realizadas" value={entRealizadas} max={globalMax} color="#2ecc71" />
              <HBar label="Com Atraso" value={entComAtraso} max={globalMax} color="#e74c3c" />
            </div>
            {/* Retiradas */}
            <div style={{ ...card, padding:20 }}>
              <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Retiradas</div>
              <HBar label="Realizadas" value={retRealizadas} max={globalMax} color="#2ecc71" />
              <HBar label="Com Atraso" value={retComAtraso} max={globalMax} color="#e74c3c" />
            </div>
            {/* Trocas */}
            <div style={{ ...card, padding:20 }}>
              <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Trocas</div>
              <HBar label="Realizadas" value={trcRealizadas} max={globalMax} color="#2ecc71" />
              <HBar label="Com Atraso" value={trcComAtraso} max={globalMax} color="#e74c3c" />
            </div>
            {/* Encerradas x Canceladas */}
            <div style={{ ...card, padding:20 }}>
              <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Encerradas x Canceladas</div>
              <HBar label="Encerradas" value={encerradas} max={globalMax} color="#2ecc71" />
              <HBar label="Canceladas" value={canceladas} max={globalMax} color="#e74c3c" />
            </div>
          </div>
        );
      })()}

      {/* ═══ GRÁFICO + FINANCEIRO ═══ */}
      <div className="dash-chart-grid" style={{ display:"grid", gridTemplateColumns:"3fr 2fr", gap:14, marginBottom:18 }}>

        {/* Gráfico */}
        <div style={{ ...card, padding:20 }} className="dash-faturamento-chart">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
            <span style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Faturamento</span>
            {varFat !== 0 && <span style={{ fontSize:11, fontWeight:700, color:varFat>0?"#2ecc71":"#e74c3c", background:varFat>0?"#f0fdf4":"#fef2f2", padding:"3px 10px", borderRadius:10 }}>
              {varFat > 0 ? "" : ""} {Math.abs(varFat).toFixed(0)}%
            </span>}
          </div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:10, height:170 }} className="dashboard-faturamento-bars">
            {months.map((m,i) => {
              const isSel = chartSelected === i;
              const isCur = i === months.length - 1;
              const isActive = isSel || (chartSelected === null && isCur);
              const barH = Math.max((fatsPago[i]/maxFat)*150, 3);
              const faturadoH = fatsPago[i] > 0 ? Math.max((fats[i]/maxFat)*150, 3) : 0;
              return (
                <div key={m} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4, cursor:"pointer", transition:"opacity .15s", opacity: chartSelected !== null && !isSel ? .4 : 1 }}
                  onClick={() => {
                    setChartSelected(isSel ? null : i);
                    if (!isSel) {
                      const md=monthDates[i]; const de=`${md.year}-${String(md.month+1).padStart(2,"0")}-01`; const ld=new Date(md.year,md.month+1,0).getDate();
                      setPeriodoCustom({de,ate:`${md.year}-${String(md.month+1).padStart(2,"0")}-${String(ld).padStart(2,"0")}`}); setPeriodo("custom");
                    } else { setPeriodo("mes"); }
                  }}>
                  <span style={{ fontSize:9, fontWeight:600, color:isActive?"#1a1a1a":"#bbb" }}>{fmt(fatsPago[i]).replace("R$","").trim()}</span>
                  <div style={{ width:"100%", borderRadius:4, overflow:"hidden", position:"relative", height:barH }}>
                    <div style={{ position:"absolute", bottom:0, width:"100%", height:"100%", background:isActive?"#2ecc71":"#ccc", borderRadius:4 }} />
                  </div>
                  <span style={{ fontSize:10, fontWeight:isActive?700:400, color:isActive?"#1a1a1a":"#bbb" }}>{m}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:16, marginTop:12, justifyContent:"center" }}>
            <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:"#999" }}><span style={{ width:8, height:8, borderRadius:2, background:"#2ecc71" }} />Recebido</span>
          </div>
          {chartSelected !== null && (
            <div style={{ textAlign:"center", marginTop:8 }}>
              <span style={{ fontSize:11, color:"#999", cursor:"pointer", textDecoration:"underline" }} onClick={() => { setChartSelected(null); setPeriodo("mes"); }}>Limpar seleção</span>
            </div>
          )}
        </div>

        {/* Resumo Financeiro */}
        <div style={{ ...card, padding:20 }}>
          <span style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Resumo Financeiro</span>
          <div style={{ marginTop:16 }}>
            {[
              { label:"Faturado (período)", value:faturamentoPeriodo, color:"#1a1a1a" },
              { label:"Recebido (período)", value:recebidoPeriodo, color:"#2ecc71" },
              { label:"A Receber (período)", value:aReceberPeriodo, color:"#e74c3c" },
              { label:"Recebido (geral)", value:recebidoTotal, color:"#2ecc71" },
              { label:"A Receber (geral)", value:aReceberTotal, color:"#e74c3c" },
            ].map(f => (
              <div key={f.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #f3f3f3" }}>
                <span style={{ fontSize:12, color:"#666" }}>{f.label}</span>
                <span style={{ fontSize:15, fontWeight:800, color:f.color }}>{fmt(f.value)}</span>
              </div>
            ))}
          </div>
          {/* Barra de taxa */}
          <div style={{ marginTop:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
              <span style={{ fontSize:11, color:"#999" }}>Taxa de Recebimento</span>
              <span style={{ fontSize:12, fontWeight:800, color:"#2ecc71" }}>{faturamentoPeriodo > 0 ? Math.round((recebidoPeriodo/faturamentoPeriodo)*100) : 0}%</span>
            </div>
            <div style={{ height:6, background:"#f0f0f0", borderRadius:3, overflow:"hidden" }}>
              <div style={{ height:"100%", width:`${faturamentoPeriodo>0?(recebidoPeriodo/faturamentoPeriodo)*100:0}%`, background:"#2ecc71", borderRadius:3, transition:"width .6s" }} />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ TOP CLIENTES + ÚLTIMAS LOCAÇÕES ═══ */}
      <div className="dash-chart-grid" style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:14, marginBottom:18 }}>

        {/* Top Clientes */}
        <div style={{ ...card, padding:20 }}>
          <span style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Top Clientes — {periodoLabel}</span>
          {topClientes.length === 0 ? (
            <div style={{ padding:"30px 0", textAlign:"center", color:"#ccc", fontSize:13 }}>Sem dados no período</div>
          ) : (
            <div style={{ marginTop:14 }}>
              {topClientes.map((c,i) => (
                <div key={c.nome} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:i<topClientes.length-1?"1px solid #f3f3f3":"none" }}>
                  <div style={{ width:24, height:24, borderRadius:"50%", background:i===0?"#1a9e8f":i===1?"#2ecc71":i===2?"#f39c12":"#e5e7eb", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0 }}>{i+1}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:"#333", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.nome}</div>
                    <div style={{ fontSize:11, color:"#999" }}>{c.qtd} locaç{c.qtd>1?"ões":"ão"}</div>
                  </div>
                  <div style={{ fontSize:13, fontWeight:800, color:"#1a1a1a", flexShrink:0 }}>{fmt(c.valor)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Últimas Locações */}
        <div style={{ ...card, padding:20 }}>
          <span style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Últimas Locações</span>
          <table style={{ width:"100%", borderCollapse:"collapse", marginTop:14 }} className="dash-ultimas-loc-table">
            <thead>
              <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
                {["Nº","Cliente","Endereço","Valor","Status","Pago"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 10px", fontSize:10, fontWeight:700, color:"#bbb", textTransform:"uppercase", letterSpacing:.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...locacoes].filter(l=>l.status!=="Cancelada").sort((a,b) => {
                const da = a.dataSolicitacao||a.data_solicitacao||a.dataEntrega||a.data_entrega||"";
                const db = b.dataSolicitacao||b.data_solicitacao||b.dataEntrega||b.data_entrega||"";
                return db.localeCompare(da);
              }).slice(0,8).map((l,i) => {
                const isAtr = (() => {
                  if (["Cancelada","Encerrada","Para Cobrar"].includes(l.status)) return false;
                  const dr = l.status==="Para Colocar" ? (l.dataEntrega||l.data_entrega) : (l.dataRetiraPrevista||l.dataRetirada);
                  return dr && dr < hoje;
                })();
                return (
                  <tr key={i} style={{ borderBottom:"1px solid #f8f8f8", background:isAtr?"#fef2f2":"" }}
                    onMouseEnter={e => e.currentTarget.style.background=isAtr?"#fee2e2":"#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background=isAtr?"#fef2f2":""}>
                    <td style={{ padding:"10px", fontSize:12 }}>
                      <span style={{ color:"#1a9e8f", fontWeight:700, cursor:"pointer" }} onClick={() => goToLocacoes(l.status)}>{l.id}</span>
                      {isAtr && <span style={{ marginLeft:6, fontSize:9, fontWeight:700, color:"#e74c3c", background:"#fecaca", padding:"1px 5px", borderRadius:6 }}>ATRASO</span>}
                    </td>
                    <td style={{ padding:"10px", fontSize:12, fontWeight:600, color:"#333" }}>{l.cliente}</td>
                    <td style={{ padding:"10px", fontSize:11, color:"#888", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {typeof l.endereco === "string" && l.endereco ? l.endereco : [l.logradouro||l.end_logradouro, l.bairro||l.end_bairro, l.cidade||l.end_cidade].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td style={{ padding:"10px", fontWeight:700, fontSize:12, color:"#1a1a1a" }}>{fmt(l.valor)}</td>
                    <td style={{ padding:"10px" }}><StatusBadge label={l.status} /></td>
                    <td style={{ padding:"10px" }}>
                      <span style={{ fontSize:10, fontWeight:700, color:l.pago?"#2ecc71":"#e74c3c", background:l.pago?"#f0fdf4":"#fef2f2", padding:"2px 8px", borderRadius:10 }}>{l.pago?"PAGO":"PENDENTE"}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Novo Cliente Modal ───────────────────────────────────────────────────────
function NovoClienteModal({ onClose, onSave, initialData, editMode }) {
  const [form, setForm]         = useState(initialData ? { nome:initialData.nome||"", tipo:initialData.tipo||"PF", cpfCnpj:initialData.cpfCnpj||initialData.cpf_cnpj||"", tel:initialData.tel||"", tel2:initialData.tel2||"", email:initialData.email||"", obs:initialData.obs||"" } : { nome:"", tipo:"PF", cpfCnpj:"", tel:"", tel2:"", email:"", obs:"" });
  const [contatos, setContatos] = useState(initialData?.contatos || []);
  const [enderecos, setEnderecos] = useState(initialData?.enderecos || []);
  const [fat, setFat] = useState(() => fatFromCliente(initialData)); // endereço de faturamento (opcional)
  const [newContato, setNewContato] = useState({ nome:"", tel:"", email:"", cargo:"" });
  const [newEndereco, setNewEndereco] = useState({ cep:"", uf:"", cidade:"", logradouro:"", numero:"", complemento:"", bairro:"", nomeEndereco:"", pagamento:"" });
  const [mapModal, setMapModal] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [endErrors, setEndErrors] = useState({});
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjStatus, setCnpjStatus] = useState(""); // "" | "ok" | "nf" | "erro"
  const sf = (k,v) => setForm(p => ({ ...p, [k]:v }));
  const se = (k,v) => { setNewEndereco(p => ({ ...p, [k]:v })); setEndErrors(e => ({ ...e, [k]: false })); };
  const sc = (k,v) => setNewContato(p => ({ ...p, [k]:v }));

  const fetchCep = async (cep) => {
    const clean = cep.replace(/\D/g,"");
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      let data = null;
      try { const res = await fetch("https://viacep.com.br/ws/"+clean+"/json/"); if(res.ok){ data = await res.json(); if(data.erro) data = null; } } catch(e){}
      if(!data) { try { const r2 = await fetch("https://brasilapi.com.br/api/cep/v2/"+clean); if(r2.ok){ const b = await r2.json(); data = { logradouro: b.street||"", bairro: b.neighborhood||"", localidade: b.city||"", uf: b.state||"" }; } } catch(e){} }
      if (data) { se("logradouro", data.logradouro||""); se("bairro", data.bairro||""); se("cidade", data.localidade||""); se("uf", data.uf||""); }
    } catch(e) {}
    setCepLoading(false);
  };

  // Consulta CNPJ via BrasilAPI (gratuita, sem token). Preenche apenas campos vazios.
  const fetchCnpj = async (cnpjRaw) => {
    const clean = (cnpjRaw || "").replace(/\D/g, "");
    if (clean.length !== 14) return;
    setCnpjLoading(true);
    setCnpjStatus("");
    try {
      const res = await fetch("https://brasilapi.com.br/api/cnpj/v1/" + clean);
      if (!res.ok) { setCnpjStatus(res.status === 404 ? "nf" : "erro"); setCnpjLoading(false); return; }
      const d = await res.json();
      // Formata telefone: BrasilAPI retorna "1134567890" → "(11) 3456-7890"
      const telFromDDD = (ddd) => {
        if (!ddd) return "";
        const s = String(ddd).replace(/\D/g, "");
        if (s.length < 10) return "";
        return formatPhone("(" + s.slice(0,2) + ") " + s.slice(2));
      };
      const telNovo = telFromDDD(d.ddd_telefone_1) || telFromDDD(d.ddd_telefone_2);
      // Preenche form — só campos vazios (não sobrescreve o que o usuário já digitou)
      setForm(p => ({
        ...p,
        nome:  p.nome  || d.razao_social || d.nome_fantasia || "",
        tel:   p.tel   || telNovo || "",
        tel2:  p.tel2  || (telNovo && telFromDDD(d.ddd_telefone_2) && telFromDDD(d.ddd_telefone_2) !== telNovo ? telFromDDD(d.ddd_telefone_2) : p.tel2),
        email: p.email || d.email || "",
      }));
      // Preenche endereço em edição — só se estiver todo vazio E ainda não tiver endereço cadastrado
      setNewEndereco(p => {
        const algumPreenchido = p.logradouro || p.cep || p.cidade;
        if (algumPreenchido || enderecos.length > 0) return p;
        const tipoLog = (d.descricao_tipo_de_logradouro || "").trim();
        const logradouroMontado = [tipoLog, d.logradouro].filter(Boolean).join(" ").trim();
        return {
          ...p,
          cep: d.cep ? formatCep(String(d.cep).replace(/\D/g,"")) : p.cep,
          uf: d.uf || p.uf,
          cidade: d.municipio || p.cidade,
          logradouro: logradouroMontado || p.logradouro,
          numero: d.numero ? String(d.numero) : p.numero,
          complemento: d.complemento || p.complemento,
          bairro: d.bairro || p.bairro,
        };
      });
      setCnpjStatus("ok");
    } catch(e) {
      setCnpjStatus("erro");
    }
    setCnpjLoading(false);
  };

  const addContato = () => {
    if (!newContato.nome) return;
    setContatos(p => [...p, { ...newContato, id: Date.now() }]);
    setNewContato({ nome:"", tel:"", email:"", cargo:"" });
  };

  const addEndereco = () => {
    const errs = {};
    if (!newEndereco.logradouro) errs.logradouro = true;
    if (!newEndereco.numero)     errs.numero     = true;
    if (!newEndereco.cidade)     errs.cidade     = true;
    if (!newEndereco.uf)         errs.uf         = true;
    if (Object.keys(errs).length > 0) { setEndErrors(errs); return; }
    setEnderecos(p => [...p, { ...newEndereco, id: Date.now() }]);
    setNewEndereco({ cep:"", uf:"", cidade:"", logradouro:"", numero:"", complemento:"", bairro:"", nomeEndereco:"", pagamento:"" });
  };

  const save = () => {
    if (!form.nome) { alert("Nome do cliente é obrigatório."); return; }
    if (form.tipo === "PJ" && !form.cpfCnpj) { alert("CNPJ obrigatório para Pessoa Jurídica."); return; }
    // Rede de segurança: se há um endereço pendente no formulário (o usuário clicou em ✏️ para
    // editar, alterou os campos mas esqueceu de clicar em "+" para re-adicionar à lista), incluí-lo
    // automaticamente antes de salvar — evita perda do endereço no save.
    let enderecosFinais = enderecos;
    if (newEndereco.logradouro && newEndereco.logradouro.trim() && newEndereco.numero && newEndereco.cidade && newEndereco.uf) {
      enderecosFinais = [...enderecos, { ...newEndereco, id: Date.now() }];
    }
    if (enderecosFinais.length === 0) { alert("Adicione pelo menos um endereço completo antes de salvar."); return; }
    if (enderecosFinais.some(e => !e.logradouro || !e.logradouro.trim())) { alert("Todos os endereços precisam ter um logradouro (rua) válido."); return; }
    const hoje = new Date().toLocaleDateString("pt-BR");
    onSave({ ...form, ...fat, id: initialData?.id, status: initialData?.status || "Ativo", contatos, enderecos: enderecosFinais });
    onClose();
  };

  const mapAddr = [newEndereco.logradouro, newEndereco.numero, newEndereco.bairro, newEndereco.cidade, newEndereco.uf].filter(Boolean).join(", ");

  return (
    <>
    <LargeModal title={editMode ? "Editar Cliente" : "Novo Cliente"} subtitle="* Campos em azul são obrigatórios" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar Cliente" : "Salvar Cliente"} maxWidth={1100}>
      <div style={{ paddingTop:12 }}>
        {/* Basic info */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 240px", gap:14, marginBottom:14 }}>
          <Field label={<span>Nome do Cliente <span style={{color:"#1aae9e"}}>*</span></span>}><input style={inp} value={form.nome} onChange={e => sf("nome",e.target.value)} placeholder="Nome ou Razão Social" /></Field>
          <Field label={form.tipo==="PJ"?<span>CNPJ <span style={{color:"#1aae9e"}}>*</span> {form.tipo==="PJ" && <span style={{ fontWeight:400, color:"#888", fontSize:10 }}>· preenche automaticamente</span>}</span>:"CPF (Opcional)"}>
            <div style={{ position:"relative" }}>
              <input
                style={{ ...inp, paddingRight: 36, background: cnpjLoading ? "#f5f5f5" : inp.background }}
                value={form.cpfCnpj}
                onChange={e => {
                  const raw = e.target.value.replace(/\D/g, "").slice(0, 14);
                  sf("cpfCnpj", formatCpfCnpj(raw));
                  if (cnpjStatus) setCnpjStatus(""); // limpa status ao editar
                  // dispara busca quando completar 14 dígitos e for PJ
                  if (form.tipo === "PJ" && raw.length === 14) {
                    fetchCnpj(raw);
                  }
                }}
                onBlur={e => {
                  const raw = e.target.value.replace(/\D/g, "");
                  if (form.tipo === "PJ" && raw.length === 14 && !cnpjStatus) {
                    fetchCnpj(raw);
                  }
                }}
                placeholder={form.tipo === "PJ" ? "00.000.000/0001-00" : "000.000.000-00 (opcional)"}
              />
              {form.tipo === "PJ" && (
                <div style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", display:"flex", alignItems:"center", pointerEvents:"none" }}>
                  {cnpjLoading && <span style={{ display:"inline-block", width:14, height:14, border:"2px solid #e0e0e0", borderTopColor:"#1a9e8f", borderRadius:"50%", animation:"spin 0.6s linear infinite" }} />}
                  {!cnpjLoading && cnpjStatus === "ok"   && <span title="CNPJ encontrado — dados preenchidos" style={{ color:"#16a34a", fontWeight:800, fontSize:15 }}>✓</span>}
                  {!cnpjLoading && cnpjStatus === "nf"   && <span title="CNPJ não encontrado na base" style={{ color:"#e67e22", fontWeight:800, fontSize:13 }}>⚠</span>}
                  {!cnpjLoading && cnpjStatus === "erro" && <span title="Falha ao consultar — tente novamente" style={{ color:"#e74c3c", fontWeight:800, fontSize:13 }}>✕</span>}
                </div>
              )}
            </div>
            {cnpjStatus === "nf" && <div style={{ color:"#e67e22", fontSize:11, marginTop:4 }}>CNPJ não encontrado na Receita. Você pode preencher manualmente.</div>}
            {cnpjStatus === "erro" && <div style={{ color:"#e74c3c", fontSize:11, marginTop:4 }}>Falha na consulta. <a onClick={() => fetchCnpj(form.cpfCnpj)} style={{ color:"#1a9e8f", cursor:"pointer", textDecoration:"underline" }}>Tentar de novo</a></div>}
          </Field>
          <Field label="Tipo Pessoa">
            <select style={{ ...inp, appearance:"none" }} value={form.tipo} onChange={e => sf("tipo",e.target.value)}>
              <option value="PF">Pessoa Física</option>
              <option value="PJ">Pessoa Jurídica</option>
            </select>
          </Field>
          <Field label="Telefone (Principal)"><input style={inp} value={form.tel} onChange={e => sf("tel",formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
          <Field label="Telefone (Adicional)"><input style={inp} value={form.tel2} onChange={e => sf("tel2",formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
          <Field label="E-mail"><input style={inp} value={form.email} onChange={e => sf("email",e.target.value)} placeholder="email@exemplo.com" /></Field>
          <div style={{ gridColumn:"1/-1" }}>
            <Field label="Observação">
              <textarea style={{ ...inp, minHeight:70, resize:"vertical" }} value={form.obs} onChange={e => sf("obs",e.target.value)} placeholder="Observações" />
            </Field>
          </div>
        </div>

        {/* Contatos */}
        <div style={{ border:"1px solid #e8e8e8", borderRadius:4, marginBottom:14, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", background:"#fafafa", borderBottom:"1px solid #e8e8e8", fontWeight:700, fontSize:13 }}>
            Contatos do Cliente <span style={{ fontWeight:400, color:"#aaa" }}>(Opcional)</span>
          </div>
          <div style={{ padding:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto", gap:10, alignItems:"end", marginBottom:12 }}>
              <Field label="Nome"><input style={inp} value={newContato.nome} onChange={e => sc("nome",e.target.value)} /></Field>
              <Field label="Telefone"><input style={inp} value={newContato.tel} onChange={e => sc("tel",formatPhone(e.target.value))} /></Field>
              <Field label="E-mail"><input style={inp} value={newContato.email} onChange={e => sc("email",e.target.value)} /></Field>
              <Field label="Cargo"><input style={inp} value={newContato.cargo} onChange={e => sc("cargo",e.target.value)} /></Field>
              <button onClick={addContato} style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, width:34, height:34, cursor:"pointer", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT }}>+</button>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead style={{ background:"#f5f5f5" }}>
                <tr>{["Nome","Telefone","E-mail","Cargo"].map(h => <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontWeight:700, color:"#888", fontSize:10, textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {contatos.length === 0 ? (
                  <tr><td colSpan={4} style={{ padding:"12px 14px", color:"#bbb", fontSize:12, textAlign:"center" }}>Nenhum contato cadastrado até o momento</td></tr>
                ) : contatos.map(c => (
                  <tr key={c.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                    <td style={{ padding:"8px 12px" }}>{c.nome}</td>
                    <td style={{ padding:"8px 12px" }}>{c.tel}</td>
                    <td style={{ padding:"8px 12px" }}>{c.email}</td>
                    <td style={{ padding:"8px 12px" }}>{c.cargo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Endereços */}
        <div style={{ border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", background:"#fafafa", borderBottom:"1px solid #e8e8e8", fontWeight:700, fontSize:13 }}>
            Endereços do Cliente <span style={{ fontWeight:400, color:"#e74c3c", fontSize:12 }}>* Obrigatório — ao menos 1 endereço</span>
          </div>
          <div style={{ padding:16 }}>
            <div style={{ display:"grid", gridTemplateColumns:"150px 90px 1fr 1fr 120px", gap:10, marginBottom:10 }}>
              <Field label="CEP"><input style={{ ...inp, background: cepLoading?"#f5f5f5":inp.background }} value={newEndereco.cep} onChange={e => { const v=formatCep(e.target.value); se("cep",v); fetchCep(v); }} placeholder="00000-000" /></Field>
              <Field label={<span>UF <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.uf ? "#e74c3c" : inp.borderColor, background: endErrors.uf ? "#fff5f5" : inp.background}} value={newEndereco.uf} onChange={e => se("uf",e.target.value)} /></Field>
              <Field label={<span>Cidade <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.cidade ? "#e74c3c" : inp.borderColor, background: endErrors.cidade ? "#fff5f5" : inp.background}} value={newEndereco.cidade} onChange={e => se("cidade",e.target.value)} placeholder="OSASCO" /></Field>
              <Field label={<span>Logradouro <span style={{color:"#e74c3c"}}>*</span></span>}><AddressAutocomplete style={{...inp, borderColor: endErrors.logradouro ? "#e74c3c" : inp.borderColor, background: endErrors.logradouro ? "#fff5f5" : inp.background}} value={newEndereco.logradouro} onChange={v => se("logradouro",v)} cidadeHint={newEndereco.cidade} ufHint={newEndereco.uf} placeholder="Rua, Avenida ou nome do local..." onSelect={s => { se("logradouro", s.logradouro); if(s.numero) se("numero", s.numero); if(s.bairro) se("bairro", s.bairro); if(s.cidade) se("cidade", s.cidade); if(s.uf) se("uf", s.uf); if(s.cep) se("cep", s.cep); }} /></Field>
              <Field label={<span>Número <span style={{color:"#e74c3c"}}>*</span></span>}><input style={{...inp, borderColor: endErrors.numero ? "#e74c3c" : inp.borderColor, background: endErrors.numero ? "#fff5f5" : inp.background}} value={newEndereco.numero} onChange={e => se("numero",e.target.value)} /></Field>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto auto", gap:10, alignItems:"end" }}>
              <Field label="Complemento"><input style={inp} value={newEndereco.complemento} onChange={e => se("complemento",e.target.value)} /></Field>
              <Field label="Bairro"><input style={inp} value={newEndereco.bairro} onChange={e => se("bairro",e.target.value)} /></Field>
              <Field label="Nome do Endereço"><input style={inp} value={newEndereco.nomeEndereco} onChange={e => se("nomeEndereco",e.target.value)} /></Field>
              <Field label="Forma de Pagamento">
                <select style={{ ...inp, appearance:"none" }} value={newEndereco.pagamento} onChange={e => se("pagamento",e.target.value)}>
                  <option value="">-- Selecione --</option>
                  <option>À Vista</option><option>Boleto</option><option>PIX</option><option>Cartão</option>
                </select>
              </Field>
              <button type="button" onClick={() => setMapModal(true)} style={{ padding:"7px 10px", background:"none", border:"1px solid #1a9e8f", borderRadius:3, color:"#1a9e8f", fontSize:11, cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", fontFamily:FONT }}>
                📍 Confirmar no Mapa
              </button>
              <button onClick={addEndereco} style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, width:34, height:34, cursor:"pointer", fontSize:20, display:"flex", alignItems:"center", justifyContent:"center" }}>+</button>
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, marginTop:12 }}>
              <thead style={{ background:"#f5f5f5" }}>
                <tr>{["CEP","Nome / Endereço",""].map(h => <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontWeight:700, color:"#888", fontSize:10, textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {enderecos.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding:"12px 14px", color:"#bbb", fontSize:12, textAlign:"center" }}>Nenhum endereço cadastrado até o momento</td></tr>
                ) : enderecos.map((e,idx) => (
                  <tr key={e.id||idx} style={{ borderBottom:"1px solid #f5f5f5" }}>
                    <td style={{ padding:"8px 12px", color:"#555" }}>{e.cep}</td>
                    <td style={{ padding:"8px 12px" }}>{[e.logradouro, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(" - ")}</td>
                    <td style={{ padding:"8px 12px", textAlign:"right", whiteSpace:"nowrap" }}>
                      <button onClick={() => { setNewEndereco({ cep:e.cep||"", uf:e.uf||"", cidade:e.cidade||"", logradouro:e.logradouro||"", numero:e.numero||"", complemento:e.complemento||"", bairro:e.bairro||"", nomeEndereco:e.nomeEndereco||e.nome_endereco||"", pagamento:e.pagamento||"" }); setEnderecos(p => p.filter((_,i) => i !== idx)); }} title="Editar" style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"4px 7px", cursor:"pointer", fontSize:12, color:"#1aae9e", marginRight:4 }}>✏️</button>
                      <button onClick={() => { if(window.confirm("Excluir este endereço?")) setEnderecos(p => p.filter((_,i) => i !== idx)); }} title="Excluir" style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"4px 7px", cursor:"pointer", fontSize:12, color:"#e74c3c" }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Endereço de Faturamento (opcional — não é endereço de obra) */}
        <EnderecoFaturamentoBox fat={fat} setFat={setFat} />
      </div>
    </LargeModal>
    {mapModal && <MapConfirmModal address={mapAddr} cidadeHint={newEndereco.cidade} ufHint={newEndereco.uf} onClose={() => setMapModal(false)} onConfirm={(coords, displayAddr, addrDetails) => { if (addrDetails) { if (addrDetails.cep) se("cep", addrDetails.cep.replace(/\D/g,"").replace(/^(\d{5})(\d{3})$/,"$1-$2")); if (addrDetails.logradouro) { const parts = addrDetails.logradouro.split(","); se("logradouro", parts[0].trim()); if (parts[1]) se("numero", parts[1].trim()); } if (addrDetails.bairro) se("bairro", addrDetails.bairro); if (addrDetails.cidade) se("cidade", addrDetails.cidade); if (addrDetails.uf) se("uf", addrDetails.uf); } }} />}
    </>
  );
}

// ─── Assinatura Tab (Boletos do Tenant) ──────────────────────────────────────
function AssinaturaTab({ apiFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/config/meus-boletos").then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding:40, textAlign:"center", color:"#aaa" }}>Carregando...</div>;
  if (!data) return <div style={{ padding:40, textAlign:"center", color:"#e74c3c" }}>Erro ao carregar dados da assinatura.</div>;

  const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const fmtRef = (ref) => {
    if (!ref) return "—";
    const parts = ref.split("/");
    if (parts.length === 2) { const m = parseInt(parts[0]); return (meses[m-1]||parts[0]) + "/" + parts[1]; }
    return ref;
  };
  const fmtDt = (d) => { if (!d) return "—"; try { const dt = new Date(d); return dt.toLocaleDateString("pt-BR"); } catch(e) { return d; } };
  const fmtVal = (v) => { if (!v && v !== 0) return "—"; return "R$ " + Number(v).toFixed(2).replace(".",","); };

  const desdeDate = data.assinante_desde ? new Date(data.assinante_desde) : null;
  const desdeTxt = desdeDate ? (meses[desdeDate.getMonth()] + "/" + desdeDate.getFullYear()) : "—";
  const mesesAtivo = desdeDate ? Math.max(1, Math.floor((Date.now() - desdeDate.getTime()) / (30.44*24*60*60*1000))) : 0;

  const proxVenc = data.proximo_vencimento ? fmtDt(data.proximo_vencimento) : "—";
  const diasProx = data.proximo_vencimento ? (() => { const d = Math.ceil((new Date(data.proximo_vencimento) - new Date()) / (24*60*60*1000)); return d >= 0 ? "em " + d + " dias" : Math.abs(d) + " dias atrás"; })() : "";

  const statusBadge = (b) => {
    if (b.status === "pago") return { bg:"#ecfdf5", color:"#059669", label:"Pago" };
    if (b.dias_atraso > 0) return { bg:"#fef2f2", color:"#dc2626", label:"Vencido" };
    if (b.status === "pendente" || b.status === "emitido") return { bg:"#fef9ec", color:"#d97706", label:"Pendente" };
    if (b.status === "cancelado") return { bg:"#f3f4f6", color:"#6b7280", label:"Cancelado" };
    return { bg:"#f3f4f6", color:"#6b7280", label: b.status };
  };

  const cardStyle = { background:"#fff", borderRadius:8, border:"1px solid #ebebeb", padding:24 };

  return (
    <div style={cardStyle}>
      {/* Card do plano */}
      <div style={{ background:"linear-gradient(135deg, #0d7a6e 0%, #1aae9e 50%, #23c4b0 100%)", borderRadius:12, padding:"26px 30px", color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-40, right:-40, width:160, height:160, background:"rgba(255,255,255,.06)", borderRadius:"50%" }} />
        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:1.5, opacity:.75, marginBottom:6 }}>Seu plano atual</div>
          <div style={{ fontSize:26, fontWeight:800, marginBottom:4 }}>{data.plano_label}</div>
          <div style={{ fontSize:13, opacity:.85 }}>{data.status_tenant === "ativo" ? "Assinatura ativa" : data.status_tenant === "trial" ? "Período de teste" : "Status: " + data.status_tenant}</div>
        </div>
        <div style={{ position:"relative", zIndex:1, textAlign:"right" }}>
          <div style={{ fontSize:34, fontWeight:800 }}>{fmtVal(data.valor_plano)}<span style={{ fontSize:14, fontWeight:500, opacity:.7 }}>/mês</span></div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(255,255,255,.18)", padding:"5px 14px", borderRadius:20, fontSize:12, fontWeight:700, marginTop:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background: data.status_tenant === "ativo" ? "#4ade80" : "#fbbf24" }} /> {data.status_tenant === "ativo" ? "Ativo" : data.status_tenant}
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:24 }}>
        <div style={{ background:"#f8f9fb", border:"1px solid #e8ebf0", borderRadius:8, padding:"14px 16px" }}>
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.8, color:"#8892a4", marginBottom:6 }}>Próximo vencimento</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a1a2e" }}>{proxVenc}</div>
          {diasProx && <div style={{ fontSize:11, color:"#8892a4", marginTop:2 }}>{diasProx}</div>}
        </div>
        <div style={{ background:"#f8f9fb", border:"1px solid #e8ebf0", borderRadius:8, padding:"14px 16px" }}>
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.8, color:"#8892a4", marginBottom:6 }}>Forma de pagamento</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a1a2e" }}>Boleto</div>
          <div style={{ fontSize:11, color:"#8892a4", marginTop:2 }}>via Asaas</div>
        </div>
        <div style={{ background:"#f8f9fb", border:"1px solid #e8ebf0", borderRadius:8, padding:"14px 16px" }}>
          <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.8, color:"#8892a4", marginBottom:6 }}>Assinante desde</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1a1a2e" }}>{desdeTxt}</div>
          {mesesAtivo > 0 && <div style={{ fontSize:11, color:"#8892a4", marginTop:2 }}>{mesesAtivo} {mesesAtivo === 1 ? "mês" : "meses"}</div>}
        </div>
      </div>

      {/* Boletos */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#1a1a2e" }}>Histórico de Pagamentos</div>
        <div style={{ fontSize:12, color:"#8892a4" }}>{(data.boletos||[]).length} boletos</div>
      </div>

      {(data.boletos||[]).length === 0 ? (
        <div style={{ padding:40, textAlign:"center", color:"#aaa", fontSize:13 }}>Nenhum boleto encontrado.</div>
      ) : (
        <div style={{ border:"1px solid #e8ebf0", borderRadius:8, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead style={{ background:"#f8f9fb" }}>
              <tr>
                {["Referência","Vencimento","Valor","Status","","Ações"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"11px 16px", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:.6, color:"#8892a4", borderBottom:"1px solid #e8ebf0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.boletos||[]).map((b,i) => {
                const st = statusBadge(b);
                const isVencido = b.dias_atraso > 0 && b.status !== "pago";
                return (
                  <tr key={b.id||i} style={{ borderBottom:"1px solid #f2f3f7", background: isVencido ? "#fef8f8" : "" }}>
                    <td style={{ padding:"13px 16px", fontWeight:600 }}>{fmtRef(b.referencia)}</td>
                    <td style={{ padding:"13px 16px", color: isVencido ? "#dc2626" : "#333", fontWeight: isVencido ? 600 : 400 }}>{fmtDt(b.vencimento)}</td>
                    <td style={{ padding:"13px 16px", fontWeight:700, fontVariantNumeric:"tabular-nums" }}>{fmtVal(b.valor)}</td>
                    <td style={{ padding:"13px 16px" }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:700, background:st.bg, color:st.color }}>
                        <span style={{ width:6, height:6, borderRadius:"50%", background:st.color }} />{st.label}
                      </span>
                    </td>
                    <td style={{ padding:"13px 16px", fontSize:11, color:"#8892a4" }}>
                      {b.status === "pago" && b.data_pagamento ? fmtDt(b.data_pagamento) : ""}
                      {isVencido && <span style={{ fontSize:11, color:"#dc2626", fontWeight:600 }}>{b.dias_atraso} dias</span>}
                    </td>
                    <td style={{ padding:"13px 16px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        {(b.status === "pendente" || b.status === "emitido" || isVencido) && b.asaas_invoice_url && (
                          <a href={b.asaas_invoice_url} target="_blank" rel="noopener noreferrer"
                            style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"6px 14px", background: isVencido ? "#fef2f2" : "#f0faf8", color: isVencido ? "#dc2626" : "#1a9e8f", border:"1px solid " + (isVencido ? "#fecaca" : "#c3ece5"), borderRadius:6, fontSize:11, fontWeight:700, textDecoration:"none", fontFamily:FONT }}>
                            🔗 {isVencido ? "Pagar agora" : "Abrir boleto"}
                          </a>
                        )}
                        {b.status === "pago" && b.asaas_invoice_url && (
                          <a href={b.asaas_invoice_url} target="_blank" rel="noopener noreferrer"
                            style={{ display:"inline-flex", alignItems:"center", padding:"6px 10px", background:"none", color:"#8892a4", border:"1px solid #e4e7ec", borderRadius:6, fontSize:11, textDecoration:"none", fontFamily:FONT }}>
                            📄
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Cliente View Modal ───────────────────────────────────────────────────────
function ClienteViewModal({ cliente: c, onClose, onEdit, onDelete, apiFetch, locacoes = [], onEnderecosChanged }) {
  // Conta locações do cliente cruzando por id ou nome (compatibilidade com diferentes formatos)
  const totalLocacoes = locacoes.filter(l =>
    l.clienteId === c.id || l.cliente_id === c.id || l.cliente === c.nome
  ).length;
  const locacoesAtivas = locacoes.filter(l =>
    (l.clienteId === c.id || l.cliente_id === c.id || l.cliente === c.nome) &&
    !["Encerrada", "Cancelada"].includes(l.status)
  ).length;
  const [tab, setTab] = useState("info");
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [planoInfo, setPlanoInfo] = useState(null);
  const [enderecos, setEnderecos] = useState(c.enderecos || []);
  const [editEnd, setEditEnd] = useState(null);
  const [savingEnd, setSavingEnd] = useState(false);
  const fileRef = useRef();
  const API = process.env.REACT_APP_API || "http://localhost:3001";

  const salvarEndereco = async () => {
    if (!editEnd) return;
    setSavingEnd(true);
    try {
      const upd = await apiFetch("/api/clientes/endereco/" + editEnd.id, { method: "PUT", body: { nomeEndereco: editEnd.nome_endereco||"", cep: editEnd.cep||"", logradouro: editEnd.logradouro||"", numero: editEnd.numero||"", complemento: editEnd.complemento||"", bairro: editEnd.bairro||"", cidade: editEnd.cidade||"", uf: editEnd.uf||"SP", pagamento: editEnd.pagamento||"" } });
      const novaLista = enderecos.map(e => e.id === upd.id ? upd : e);
      setEnderecos(novaLista);
      onEnderecosChanged?.(novaLista); // propaga para o estado global de clientes
      setEditEnd(null);
    } catch(err) { alert("Erro ao salvar: " + err.message); }
    setSavingEnd(false);
  };

  const excluirEndereco = async (endId) => {
    if (!window.confirm("Excluir este endereço?")) return;
    try {
      await apiFetch("/api/clientes/endereco/" + endId, { method: "DELETE" });
      const novaLista = enderecos.filter(e => e.id !== endId);
      setEnderecos(novaLista);
      onEnderecosChanged?.(novaLista); // propaga para o estado global de clientes
    } catch(err) { alert("Erro ao excluir: " + err.message); }
  };

  useEffect(() => { if(apiFetch) apiFetch("/api/config/storage").then(setPlanoInfo).catch(() => {}); }, []);

  const handleAnexar = () => {
    if (planoInfo && !planoInfo.permite_anexos) {
      alert("Seu plano atual (Básico) não permite anexar documentos.\n\nFaça upgrade para o plano Controla para desbloquear essa funcionalidade.");
      return;
    }
    fileRef.current?.click();
  };

  const TABS = [
    { key:"info",      label:"Dados" },
    { key:"contatos",  label:`Contatos (${(c.contatos||[]).length})` },
    { key:"enderecos", label:`Endereços (${enderecos.length})` },
    { key:"docs",      label:`Documentos (${docs.length})` },
  ];

  const loadDocs = async () => {
    if (!apiFetch) return;
    setDocsLoading(true);
    try { const data = await apiFetch("/api/documentos?cliente_id=" + c.id); setDocs(data || []); } catch(e) { console.error(e); }
    setDocsLoading(false);
  };
  useEffect(() => { loadDocs(); }, []);

  const addDoc = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const token = sessionStorage.getItem("controla_token");
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("documento", file);
        formData.append("categoria", "geral");
        formData.append("cliente_id", c.id);
        const res = await fetch(API + "/api/documentos", { method:"POST", headers:{ Authorization:"Bearer "+token }, body:formData });
        const data = await res.json();
        if (!res.ok) { alert(data.error || "Erro no upload"); continue; }
      } catch(err) { alert("Erro ao enviar: "+err.message); }
    }
    e.target.value = "";
    await loadDocs();
    setUploading(false);
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Excluir este documento?")) return;
    try { await apiFetch("/api/documentos/"+id, { method:"DELETE" }); setDocs(docs.filter(d => d.id !== id)); } catch(e) { alert("Erro: "+e.message); }
  };
  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:760, maxHeight:"90vh", display:"flex", flexDirection:"column", borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.22)", fontFamily:FONT, overflow:"hidden" }}>
        <div style={{ padding:"14px 22px", borderBottom:"1px solid #e4e4e4", display:"flex", justifyContent:"space-between", alignItems:"center", background:"#f9faff" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:42, height:42, borderRadius:"50%", background:"#1aae9e", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:18 }}>{c.nome?.[0]?.toUpperCase()||"?"}</div>
            <div>
              <div style={{ fontWeight:800, fontSize:16, color:"#1a1a1a" }}>{c.nome}</div>
              <div style={{ fontSize:12, color:"#888" }}>{c.tipo==="PJ"?"Pessoa Jurídica":"Pessoa Física"} {c.cpfCnpj ? "· "+c.cpfCnpj : ""}</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={onEdit} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"7px 14px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:FONT }}>Editar</button>
            {onDelete && <button onClick={() => { if(window.confirm("Tem certeza que deseja EXCLUIR o cliente \""+c.nome+"\"? Essa ação não pode ser desfeita.")) onDelete(c.id); }} style={{ background:"#e74c3c", color:"#fff", border:"none", borderRadius:3, padding:"7px 14px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:FONT }}>Excluir</button>}
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:20 }}>✕</button>
          </div>
        </div>
        <div style={{ display:"flex", borderBottom:"2px solid #ebebeb", padding:"0 22px" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ padding:"10px 14px", border:"none", background:"none", cursor:"pointer", fontWeight:tab===t.key?700:400, color:tab===t.key?"#1aae9e":"#888", borderBottom:tab===t.key?"2px solid #1aae9e":"2px solid transparent", marginBottom:-2, fontFamily:FONT, fontSize:12 }}>{t.label}</button>
          ))}
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
          {tab === "info" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14 }}>
              {[["Nome",c.nome],["Tipo",(c.tipo==="PJ"?"Pessoa Jurídica":"Pessoa Física")],["CPF/CNPJ",c.cpfCnpj||"—"],["Telefone Principal",c.tel||"—"],["Telefone Adicional",c.tel2||"—"],["E-mail",c.email||"—"],["Status",c.status||"Ativo"],["Locações", locacoesAtivas > 0 ? `${totalLocacoes} (${locacoesAtivas} ativa${locacoesAtivas===1?"":"s"})` : totalLocacoes],["Cadastrado em",c.criadoEm||"—"]].map(([l,v]) => (
                <div key={l} style={{ padding:"10px 12px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", marginBottom:4 }}>{l}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:"#333" }}>{v}</div>
                </div>
              ))}
              {c.obs && <div style={{ gridColumn:"1/-1", padding:"10px 12px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", marginBottom:4 }}>Observações</div>
                <div style={{ fontSize:13, color:"#555" }}>{c.obs}</div>
              </div>}
              {fmtEnderecoFaturamento(c) && <div style={{ gridColumn:"1/-1", padding:"10px 12px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa" }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", marginBottom:4 }}>Endereço de faturamento</div>
                <div style={{ fontSize:13, fontWeight:600, color:"#333" }}>{fmtEnderecoFaturamento(c)}</div>
              </div>}
            </div>
          )}
          {tab === "contatos" && (
            <div>
              {(c.contatos||[]).length === 0 ? <EmptyState icon="📞" title="Sem contatos cadastrados" subtitle="" /> :
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead><tr>{["Nome","Telefone","E-mail","Cargo"].map(h=><th key={h} style={{ textAlign:"left", padding:"8px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>)}</tr></thead>
                  <tbody>{c.contatos.map((ct,i)=><tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}><td style={{ padding:"9px 12px", fontWeight:600 }}>{ct.nome}</td><td style={{ padding:"9px 12px", color:"#555" }}>{ct.tel||"—"}</td><td style={{ padding:"9px 12px", color:"#555" }}>{ct.email||"—"}</td><td style={{ padding:"9px 12px", color:"#888" }}>{ct.cargo||"—"}</td></tr>)}</tbody>
                </table>}
            </div>
          )}
          {tab === "enderecos" && (
            <div>
              {enderecos.length === 0 ? <EmptyState icon="📍" title="Sem endereços cadastrados" subtitle="" /> :
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {enderecos.map((e,i) => (
                    editEnd && editEnd.id === e.id ? (
                      <div key={e.id} style={{ padding:"14px 16px", border:"2px solid #1aae9e", borderRadius:6, background:"#f0faf8" }}>
                        <div style={{ fontWeight:700, fontSize:13, marginBottom:10, color:"#1a9e8f" }}>Editando endereço</div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                          {[["Nome do endereço","nome_endereco"],["CEP","cep"],["Logradouro","logradouro"],["Número","numero"],["Complemento","complemento"],["Bairro","bairro"],["Cidade","cidade"],["UF","uf"]].map(([lbl,key]) => (
                            <div key={key}>
                              <div style={{ fontSize:10, fontWeight:700, color:"#888", marginBottom:3, textTransform:"uppercase" }}>{lbl}</div>
                              <input value={editEnd[key]||""} onChange={ev => setEditEnd(p => ({...p, [key]: ev.target.value}))} style={{ width:"100%", padding:"7px 10px", border:"1px solid #ddd", borderRadius:3, fontSize:12, fontFamily:FONT, boxSizing:"border-box" }} />
                            </div>
                          ))}
                          <div style={{ gridColumn:"1/-1" }}>
                            <div style={{ fontSize:10, fontWeight:700, color:"#888", marginBottom:3, textTransform:"uppercase" }}>Pagamento</div>
                            <input value={editEnd.pagamento||""} onChange={ev => setEditEnd(p => ({...p, pagamento: ev.target.value}))} style={{ width:"100%", padding:"7px 10px", border:"1px solid #ddd", borderRadius:3, fontSize:12, fontFamily:FONT, boxSizing:"border-box" }} />
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:8, marginTop:12, justifyContent:"flex-end" }}>
                          <button onClick={() => setEditEnd(null)} style={{ background:"#f4f4f4", color:"#555", border:"1px solid #ddd", borderRadius:3, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Cancelar</button>
                          <button onClick={salvarEndereco} disabled={savingEnd} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"6px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, opacity: savingEnd?.6:1 }}>{savingEnd?"Salvando...":"Salvar"}</button>
                        </div>
                      </div>
                    ) : (
                      <div key={e.id||i} style={{ padding:"12px 16px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>{e.nome_endereco||e.nomeEndereco||"Endereço "+(i+1)}</div>
                          <div style={{ fontSize:12, color:"#555" }}>{[e.logradouro, e.numero, e.complemento, e.bairro, e.cidade, e.uf, e.cep].filter(Boolean).join(", ")}</div>
                          {e.pagamento && <div style={{ fontSize:11, color:"#1aae9e", marginTop:4 }}>💳 Pagamento: {e.pagamento}</div>}
                        </div>
                        <div style={{ display:"flex", gap:6, marginLeft:10, flexShrink:0 }}>
                          <button onClick={() => setEditEnd({...e})} title="Editar" style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"5px 8px", cursor:"pointer", fontSize:13, color:"#1aae9e" }}>✏️</button>
                          <button onClick={() => excluirEndereco(e.id)} title="Excluir" style={{ background:"none", border:"1px solid #ddd", borderRadius:3, padding:"5px 8px", cursor:"pointer", fontSize:13, color:"#e74c3c" }}>🗑️</button>
                        </div>
                      </div>
                    )
                  ))}
                </div>}
            </div>
          )}
          {tab === "docs" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div style={{ fontSize:13, color:"#555" }}>Documentos e comprovantes do cliente.</div>
                <button onClick={handleAnexar} disabled={uploading} style={{ background: uploading?"#aaa":(planoInfo && !planoInfo.permite_anexos ? "#ccc" : "#1aae9e"), color:"#fff", border:"none", borderRadius:3, padding:"7px 14px", fontWeight:700, fontSize:12, cursor: uploading?"wait":"pointer", fontFamily:FONT }}>{uploading ? "Enviando..." : "+ Anexar"}</button>
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt" style={{ display:"none" }} onChange={addDoc} />
              </div>
              {planoInfo && !planoInfo.permite_anexos && (
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8, background:"linear-gradient(135deg, #f8f9fa 0%, #eef1f5 100%)", border:"1px solid #dee2e6", marginBottom:12 }}>
                  <div style={{ width:30, height:30, borderRadius:"50%", background:"linear-gradient(135deg, #6c757d, #495057)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ fontSize:13, filter:"brightness(10)" }}>🔒</span>
                  </div>
                  <div style={{ fontSize:12, color:"#495057", lineHeight:1.4 }}>Recurso disponível no plano <strong style={{ color:"#1aae9e" }}>Controla</strong>. Entre em contato para fazer upgrade.</div>
                </div>
              )}
              {docsLoading ? <div style={{ padding:30, textAlign:"center", color:"#aaa" }}>Carregando...</div> :
              docs.length === 0 ? <EmptyState icon="📄" title="Nenhum documento" subtitle={planoInfo && !planoInfo.permite_anexos ? "Disponível no plano Controla" : "Clique em Anexar para adicionar"} /> :
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {docs.map(d => <div key={d.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa" }}>
                    <span style={{ fontSize:22 }}>{d.tipo?.includes("pdf")?"":d.tipo?.includes("image")?"":""}</span>
                    <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:13 }}>{d.nome_original||d.nome}</div><div style={{ fontSize:11, color:"#aaa" }}>{d.tamanho ? ((d.tamanho/1024).toFixed(1)+" KB") : ""} · {d.created_at ? new Date(d.created_at).toLocaleDateString("pt-BR") : ""}</div></div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => downloadAuth(d.url, d.nome_original||d.nome)} style={{ background:"none", border:"none", cursor:"pointer", color:"#1aae9e", fontSize:12, fontWeight:600 }}>Baixar</button>
                      <button onClick={() => removeDoc(d.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:700 }}>✕</button>
                    </div>
                  </div>)}
                </div>}
            </div>
          )}
        </div>
        <div style={{ padding:"12px 22px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"#f4f4f4", color:"#555", border:"1px solid #e0e0e0", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Clientes ─────────────────────────────────────────────────────────────────
// Input de filtro por coluna (com ícone de lupa)
function ColFilterInput({ value, onChange, placeholder }) {
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px 6px 24px", fontSize: 11, fontFamily: FONT,
          border: "1px solid #d4dadf", borderRadius: 3, background: "#fff",
          color: "#333", outline: "none",
        }}
        onFocus={e => e.target.style.borderColor = "#1aae9e"}
        onBlur={e => e.target.style.borderColor = "#d4dadf"}
      />
    </div>
  );
}

function Clientes({ clientes, setClientes, locacoes = [], setLocacoes, addToast }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editCliente, setEditCliente] = useState(null);
  const [viewCliente, setViewCliente]   = useState(null);
  const [page, setPage] = useState(1);
  const [showColFilters, setShowColFilters] = useState(false);
  const [colFilters, setColFilters] = useState({}); // { nome, tipo, telefone, endereco, criadoEm }
  const PER_PAGE = 10;

  const setColFilter = (col, val) => {
    setColFilters(p => ({ ...p, [col]: val }));
    setPage(1);
  };
  const matchCol = (val, filter) => {
    if (!filter || !String(filter).trim()) return true;
    return String(val||"").toLowerCase().includes(String(filter).toLowerCase().trim());
  };
  const limparFiltros = () => { setColFilters({}); setPage(1); };
  const temFiltroAtivo = Object.values(colFilters).some(v => v && String(v).trim());

  const filtered = clientes.filter(c => {
    // Busca global (existente)
    if (search.trim()) {
      const s = search.toLowerCase();
      const globalMatch =
        String(c.nome||"").toLowerCase().includes(s) ||
        String(c.tel||"").includes(search) ||
        String(c.cpfCnpj||c.cpf_cnpj||"").includes(search) ||
        String(c.email||"").toLowerCase().includes(s);
      if (!globalMatch) {
        const locsCliente = locacoes.filter(l => l.cliente === c.nome || l.clienteId === c.id || l.cliente_id === c.id);
        const locMatch = locsCliente.some(l =>
          String(l.logradouro||l.end_logradouro||"").toLowerCase().includes(s) ||
          String(l.bairro||l.end_bairro||"").toLowerCase().includes(s) ||
          String(l.cidade||l.end_cidade||"").toLowerCase().includes(s) ||
          String(l.endereco||"").toLowerCase().includes(s)
        );
        if (!locMatch) return false;
      }
    }
    // Filtros por coluna
    if (!matchCol(c.nome, colFilters.nome) && !matchCol(c.cpfCnpj||c.cpf_cnpj, colFilters.nome)) return false;
    if (colFilters.tipo) {
      const tipoLabel = c.tipo === "PJ" ? "jurídica pj juridica" : "física pf fisica";
      if (!tipoLabel.includes(String(colFilters.tipo).toLowerCase().trim())) return false;
    }
    if (!matchCol(c.tel, colFilters.telefone)) return false;
    const addr = c.enderecos?.[0]
      ? [c.enderecos[0].logradouro, c.enderecos[0].numero, c.enderecos[0].bairro, c.enderecos[0].cidade, c.enderecos[0].uf].filter(Boolean).join(", ")
      : "";
    if (!matchCol(addr, colFilters.endereco)) return false;
    if (!matchCol(c.criadoEm, colFilters.criadoEm)) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safeP = Math.min(page, totalPages);
  const paged = filtered.slice((safeP - 1) * PER_PAGE, safeP * PER_PAGE);
  const pgBtn = (n, active) => ({ background: active ? "#1a9e8f" : "#f5f5f5", color: active ? "#fff" : "#555", border:"none", borderRadius:3, padding:"5px 10px", fontWeight:600, fontSize:12, cursor:"pointer", minWidth:32 });

  const save = async (clienteData) => {
    try {
      const novo = await apiFetch("/api/clientes", { method: "POST", body: clienteData });
      setClientes(p => [...p, { ...novo, cpfCnpj: novo.cpf_cnpj||novo.cpfCnpj||'', criadoEm: fmtDate(novo.created_at) || '' }]);
      addToast("Cliente cadastrado!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setModal(false);
  };

  const saveEdit = async (clienteData) => {
    try {
      const upd = await apiFetch("/api/clientes/" + clienteData.id, { method: "PUT", body: clienteData });
      setClientes(p => p.map(c => c.id === upd.id ? { ...upd, cpfCnpj: upd.cpf_cnpj||upd.cpfCnpj||'', criadoEm: fmtDate(upd.created_at) || c.criadoEm || '' } : c));
      // Propagar telefone e nome nas locações ativas desse cliente (espelha o backend)
      if (setLocacoes) {
        const statusAtivos = ["Para Colocar","Colocada","Para Retirar","Para Trocar","Para Cobrar"];
        setLocacoes(p => p.map(l => {
          if ((l.cliente_id === upd.id || l.clienteId === upd.id) && statusAtivos.includes(l.status)) {
            const newTel = upd.tel != null ? upd.tel : l.contatoTel;
            return { ...l, contatoTel: newTel, contato_tel: newTel, cliente: upd.nome||l.cliente, cliente_nome: upd.nome||l.cliente_nome, cpfCnpj: upd.cpf_cnpj||upd.cpfCnpj||l.cpfCnpj };
          }
          return l;
        }));
      }
      addToast("Cliente atualizado!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setEditCliente(null);
  };

  return (
    <div>
      <div className="page-action-bar" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#aaa" }}><IcoSearch /></span>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar cliente..." style={{ ...inp, width:280, paddingLeft:34 }} />
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button
            onClick={() => { setShowColFilters(s => !s); if (showColFilters) limparFiltros(); }}
            title={showColFilters ? "Esconder filtros por coluna" : "Filtrar por coluna"}
            style={{
              background: showColFilters ? "#1aae9e" : "#fff",
              color: showColFilters ? "#fff" : "#1aae9e",
              border: `1px solid ${showColFilters ? "#1aae9e" : "#1aae9e88"}`,
              borderRadius: 3, padding: "9px 12px", fontSize: 12, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontFamily: FONT,
              position: "relative"
            }}
          >
            {/* Ícone de funil */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Filtros
            {temFiltroAtivo && (
              <span style={{
                position: "absolute", top: -5, right: -5,
                width: 10, height: 10, borderRadius: "50%",
                background: "#e74c3c", border: "2px solid #fff"
              }} />
            )}
          </button>
          <button onClick={() => exportToExcel(filtered, [
            {label:"Nome",key:"nome"},{label:"Tipo",key:r=>r.tipo==="PJ"?"Jurídica":"Física"},
            {label:"CPF/CNPJ",key:r=>r.cpfCnpj||""},{label:"Telefone",key:r=>r.tel||""},
            {label:"Telefone 2",key:r=>r.tel2||""},{label:"E-mail",key:r=>r.email||""},
            {label:"Logradouro",key:r=>r.enderecos?.[0]?.logradouro||""},{label:"Número",key:r=>r.enderecos?.[0]?.numero||""},
            {label:"Complemento",key:r=>r.enderecos?.[0]?.complemento||""},{label:"Bairro",key:r=>r.enderecos?.[0]?.bairro||""},
            {label:"Cidade",key:r=>r.enderecos?.[0]?.cidade||""},{label:"UF",key:r=>r.enderecos?.[0]?.uf||""},
            {label:"CEP",key:r=>r.enderecos?.[0]?.cep||""},{label:"Observação",key:r=>r.obs||""},
            {label:"Status",key:r=>r.status||"Ativo"},{label:"Cadastrado em",key:r=>r.criadoEm||""},
          ], "clientes")} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            Exportar Excel
          </button>
          <button onClick={() => setModal(true)} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            <IcoPlus /> Novo Cliente
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden" }}>
        <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>
              {["#","Nome","Tipo","Telefone","Endereço","Criado em","Ações"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"9px 13px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #ebebeb" }}>{h}</th>
              ))}
            </tr>
            {showColFilters && (
              <tr style={{ background:"#f7fafc" }}>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}></th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}>
                  <ColFilterInput value={colFilters.nome||""} onChange={v => setColFilter("nome", v)} placeholder="Filtrar nome ou CPF/CNPJ..." />
                </th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}>
                  <ColFilterInput value={colFilters.tipo||""} onChange={v => setColFilter("tipo", v)} placeholder="PF ou PJ..." />
                </th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}>
                  <ColFilterInput value={colFilters.telefone||""} onChange={v => setColFilter("telefone", v)} placeholder="Telefone..." />
                </th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}>
                  <ColFilterInput value={colFilters.endereco||""} onChange={v => setColFilter("endereco", v)} placeholder="Rua, bairro, cidade..." />
                </th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb" }}>
                  <ColFilterInput value={colFilters.criadoEm||""} onChange={v => setColFilter("criadoEm", v)} placeholder="dd/mm/aaaa..." />
                </th>
                <th style={{ padding:"6px 8px", borderBottom:"1px solid #ebebeb", textAlign:"right" }}>
                  {temFiltroAtivo && (
                    <button onClick={limparFiltros} title="Limpar filtros" style={{ background:"none", border:"none", color:"#e74c3c", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:FONT, padding:"4px 8px" }}>
                      ✕ Limpar
                    </button>
                  )}
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={7}><EmptyState icon="👥" title="Nenhum cliente cadastrado" subtitle="Clique em 'Novo Cliente' para começar" /></td></tr>
            ) : paged.map((c,i) => {
              const addr = c.enderecos?.[0] ? [c.enderecos[0].logradouro, c.enderecos[0].numero, c.enderecos[0].bairro, c.enderecos[0].cidade, c.enderecos[0].uf].filter(Boolean).join(", ") : "—";
              return (
              <tr key={c.id} style={{ borderBottom:"1px solid #f4f4f4" }}
                onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background=""}>
                <td className="mob-hide" style={{ padding:"10px 13px", color:"#bbb", fontSize:11 }}>{String((safeP - 1) * PER_PAGE + i + 1).padStart(2,"0")}</td>
                <td className="mob-title" style={{ padding:"10px 13px" }}>
                  <div style={{ fontWeight:700, color:"#1a1a1a", fontSize:13 }}>{c.nome}</div>
                  {c.cpfCnpj && <div style={{ fontSize:11, color:"#aaa" }}>Doc: {c.cpfCnpj}</div>}
                </td>
                <td data-label="Tipo" style={{ padding:"10px 13px" }}>
                  <span style={{ fontSize:11, fontWeight:700, color:c.tipo==="PJ"?"#6c47ff":"#1aae9e", background:c.tipo==="PJ"?"#f0ecff":"#e6faf8", padding:"2px 8px", borderRadius:3 }}>{c.tipo==="PJ"?"Jurídica":"Física"}</span>
                </td>
                <td data-label="Telefone" style={{ padding:"10px 13px", color:"#555", fontSize:12 }}>{c.tel||"—"}</td>
                <td data-label="Endereço" style={{ padding:"10px 13px", color:"#888", fontSize:11, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{addr}</td>
                <td data-label="Criado em" style={{ padding:"10px 13px", color:"#888", fontSize:12 }}>{c.criadoEm||"—"}</td>
                <td className="mob-actions" style={{ padding:"10px 13px" }}>
                  <div style={{ display:"flex", gap:4 }}>
                    <button onClick={() => setViewCliente(c)} style={{ background:"#f0f4ff", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#1aae9e", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEye /> Ver</button>
                    <button onClick={() => setEditCliente(c)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#555", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEdit /> Editar</button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {/* Pagination */}
        <div style={{ padding:"10px 13px", borderTop:"1px solid #f0f0f0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, color:"#888" }}>{Math.min((safeP - 1) * PER_PAGE + 1, filtered.length)}–{Math.min(safeP * PER_PAGE, filtered.length)} de {filtered.length} clientes</span>
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safeP <= 1} style={{ ...pgBtn(0, false), opacity: safeP <= 1 ? .4 : 1 }}></button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let n;
              if (totalPages <= 7) n = i + 1;
              else if (safeP <= 4) n = i + 1;
              else if (safeP >= totalPages - 3) n = totalPages - 6 + i;
              else n = safeP - 3 + i;
              return <button key={n} onClick={() => setPage(n)} style={pgBtn(n, n === safeP)}>{n}</button>;
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safeP >= totalPages} style={{ ...pgBtn(0, false), opacity: safeP >= totalPages ? .4 : 1 }}></button>
          </div>
        </div>
      </div>
      {modal && <NovoClienteModal onClose={() => setModal(false)} onSave={save} />}
      {editCliente && <NovoClienteModal onClose={() => setEditCliente(null)} onSave={saveEdit} initialData={editCliente} editMode />}
      {viewCliente && <ClienteViewModal cliente={viewCliente} onClose={() => setViewCliente(null)} onEdit={() => { setEditCliente(viewCliente); setViewCliente(null); }} onDelete={async (id) => { try { await apiFetch("/api/clientes/"+id, { method:"DELETE" }); setClientes(p => p.filter(c => c.id !== id)); setViewCliente(null); addToast("Cliente excluído!", "success"); } catch(e) { addToast("Erro ao excluir: "+e.message, "error"); } }} apiFetch={apiFetch} locacoes={locacoes} onEnderecosChanged={(eds) => setClientes(prev => prev.map(cli => cli.id === viewCliente.id ? { ...cli, enderecos: eds } : cli))} />}
    </div>
  );
}

// ─── Confirmar Colocada Modal ──────────────────────────────────────────────────
function ConfirmarColocadaModal({ locacoes: selecionadas, motoristas, cacambas, frota, onClose, onConfirmar }) {
  const todasPagas = selecionadas.every(l => !!l.pago);
  const hoje = hojeLocal();
  const [form, setForm] = useState({ dataSolicitacao: selecionadas[0]?.dataSolicitacao||selecionadas[0]?.data_solicitacao||"", dataColocacao: hoje, dataRetirada: "", motorista: "", veiculo: "", comissao: "", pago: todasPagas, dataPagamento: todasPagas ? (selecionadas[0]?.dataPagamento||selecionadas[0]?.data_pagamento||hoje) : "" });
  const [rows, setRows] = useState(selecionadas.map(l => ({ locId: l.id, itinerario: "", mtr: l.mtr||"", cacamba: l.cacamba||"" })));
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setRows(p => p.map((r,j) => j===i ? { ...r, [k]: v } : r));
  // Auto-preenchimento da comissão (colocação) a partir do cadastro do motorista.
  // Só preenche se o campo estiver vazio ou ainda contiver a sugestão anterior (não sobrescreve valor digitado à mão).
  const [comissaoEditada, setComissaoEditada] = useState(false);
  const ultimaSugestaoComissaoRef = useRef("");
  const onMotoristaChange = (nome) => {
    const m = (motoristas||[]).find(x => (x.nome||String(x)) === nome);
    const raw = m ? (m.comissao_colocacao != null ? m.comissao_colocacao : m.comissaoColocacao) : null;
    const sug = (raw != null && raw !== "") ? String(parseFloat(raw)) : "";
    setForm(p => {
      const atual = String(p.comissao||"");
      if (!comissaoEditada || atual === "" || atual === ultimaSugestaoComissaoRef.current) {
        ultimaSugestaoComissaoRef.current = sug;
        return { ...p, motorista: nome, comissao: sug };
      }
      return { ...p, motorista: nome };
    });
  };
  const confirm = () => {
    if (!form.dataRetirada) { alert("A Data Prevista de Retirada é obrigatória."); return; }
    if (form.dataColocacao && form.dataRetirada && form.dataRetirada < form.dataColocacao) { alert("A data de retirada não pode ser anterior à data de colocação."); return; }
    if (!form.motorista) { alert("O Motorista é obrigatório."); return; }
    onConfirmar(form, rows); onClose();
  };

  return (
    <LargeModal title="Confirmar como Colocada" subtitle="Informe os dados da operação" onClose={onClose} onSave={confirm} saveLabel="Confirmar">
      <div style={{ paddingTop: 14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Pedido em"><input type="date" style={inp} value={form.dataSolicitacao} onChange={e => setF("dataSolicitacao", e.target.value)} /></Field>
        <Field label="Data Real da Entrega *"><input type="date" style={inp} value={form.dataColocacao} onChange={e => setF("dataColocacao", e.target.value)} /></Field>
        <Field label="Data Prevista de Retirada *"><input type="date" style={{ ...inp, borderColor: !form.dataRetirada?"#1aae9e":"#e0e0e0" }} value={form.dataRetirada} min={form.dataColocacao||undefined} onChange={e => setF("dataRetirada", e.target.value)} /></Field>
        <Field label="Motorista *">
          <select style={{ ...inp, appearance:"none", borderColor: !form.motorista?"#1aae9e":"#e0e0e0" }} value={form.motorista} onChange={e => onMotoristaChange(e.target.value)}>
            <option value="">-- Selecione --</option>
            {(motoristas||[]).map(m => <option key={m.id||String(m)} value={m.nome||String(m)}>{m.nome||String(m)}</option>)}
          </select>
        </Field>
        <Field label="Veículo">
          <select style={{ ...inp, appearance:"none" }} value={form.veiculo} onChange={e => setF("veiculo", e.target.value)}>
            <option value="">-- Selecione --</option>
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").length === 0 && <option value="" disabled>Nenhum veículo cadastrado</option>}
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").map(v => <option key={v.id||String(v)} value={v.placa||""}>{v.placa ? v.placa+" — "+v.modelo : String(v)}</option>)}
          </select>
        </Field>
        <Field label="Comissão do Motorista (R$)"><input type="number" style={inp} value={form.comissao} onChange={e => { setComissaoEditada(true); setF("comissao", e.target.value); }} placeholder="0,00" /></Field>
        <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:18 }}>
          <input type="checkbox" id="pago" checked={form.pago} onChange={e => { const v = e.target.checked; setForm(p => ({ ...p, pago: v, dataPagamento: (v && !p.dataPagamento) ? hoje : p.dataPagamento })); }} style={{ accentColor:"#2ecc71", width:15, height:15, cursor: todasPagas ? "default" : "pointer" }} disabled={todasPagas} />
          <label htmlFor="pago" style={{ fontSize:13, color: todasPagas ? "#2ecc71" : "#444", cursor: todasPagas ? "default" : "pointer", fontWeight: todasPagas ? 600 : 400 }}>
            {todasPagas ? "Locação já marcada como paga" : "A locação já foi paga?"}
          </label>
        </div>
        {form.pago && (
          <Field label="Data do Pagamento"><input type="date" style={inp} value={form.dataPagamento||""} onChange={e => setF("dataPagamento", e.target.value)} /></Field>
        )}
      </div>
      <SectionHeader icon="📋" label="Locações Selecionadas" />
      <div style={{ border:"1px solid #ebebeb", borderRadius:3, overflow:"hidden", marginTop:10 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>{["Locação","Item Itinerário","MTR/CTR","Caçamba"].map(h => (
              <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {selecionadas.map((l, i) => (
              <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontWeight:700, fontSize:12, color:"#1a1a1a" }}>{l.id}</div>
                  <div style={{ fontSize:11, color:"#888" }}>{l.cliente}</div>
                  <div style={{ fontSize:10, color:"#aaa" }}>{l.logradouro || (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "")}</div>
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <input style={{ ...inp, fontSize:11 }} value={rows[i]?.itinerario||""} onChange={e => setRow(i,"itinerario",e.target.value)} placeholder="-- Selecione --" />
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <input style={{ ...inp, fontSize:11 }} value={rows[i]?.mtr||""} onChange={e => setRow(i,"mtr",e.target.value)} placeholder="MTR-000000" />
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <select style={{ ...inp, appearance:"none", fontSize:11 }} value={rows[i]?.cacamba||""} onChange={e => setRow(i,"cacamba",e.target.value)}>
                    <option value="">-- Selecione --</option>
                    {(cacambas||[]).filter(c => (c.codigo && c.codigo === (rows[i]?.cacamba||"")) || !c.status || c.status === "Disponível").map(c => <option key={c.id||String(c)} value={c.codigo||""}>{c.codigo||String(c)}{c.status && c.status !== "Disponível" && c.codigo === (rows[i]?.cacamba||"") ? " (atual)" : ""}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LargeModal>
  );
}

// ─── Pedido de Retirada Modal ──────────────────────────────────────────────────
function PedidoRetiradaModal({ locacoes: selecionadas, onClose, onConfirmar }) {
  const [form, setForm] = useState({ dataPedido: hojeLocal(), dataPrevista: "" });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const confirm = () => {
    if (!form.dataPedido) { alert("A Data do Pedido de Retirada é obrigatória."); return; }
    if (!form.dataPrevista) { alert("A Data Prevista de Retirada é obrigatória."); return; }
    // Validar que data prevista >= data colocada
    for (const l of selecionadas) {
      const dataCol = l.dataColocacaoReal || l.dataEntrega || l.data_entrega;
      if (dataCol && form.dataPrevista < dataCol) {
        alert("A data prevista de retirada ("+form.dataPrevista+") não pode ser anterior à data da colocação ("+dataCol+") da locação "+l.id+".");
        return;
      }
    }
    onConfirmar(form); onClose();
  };
  return (
    <LargeModal title="Pedido de Retirada" subtitle={"Informe as datas da retirada • " + selecionadas.length + " locação(ões)"} onClose={onClose} onSave={confirm} saveLabel="Confirmar">
      <div style={{ paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Data Pedido de Retirada *"><input type="date" style={{ ...inp, borderColor:!form.dataPedido?"#1aae9e":"#e0e0e0" }} value={form.dataPedido} onChange={e => setF("dataPedido", e.target.value)} /></Field>
        <Field label="Data Prevista Retirada *"><input type="date" style={{ ...inp, borderColor:!form.dataPrevista?"#1aae9e":"#e0e0e0" }} value={form.dataPrevista} onChange={e => setF("dataPrevista", e.target.value)} /></Field>
      </div>
      <SectionHeader icon="📋" label="Locações Selecionadas" />
      <div style={{ border:"1px solid #ebebeb", borderRadius:3, overflow:"hidden", marginTop:10 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>{["Nº Locação","Cliente / Contato / Endereço"].map(h => (
              <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {selecionadas.map(l => (
              <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                <td style={{ padding:"10px 12px", fontWeight:700, fontSize:12, color:"#1a1a1a" }}>{l.id}</td>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontWeight:700 }}>{l.cliente}</div>
                  <div style={{ fontSize:11, color:"#888" }}>Endereço: {l.logradouro || (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "—")}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LargeModal>
  );
}

// ─── Confirmação de Retirada Modal ───────────────────────────────────────────
function ConfirmarRetiradaModal({ locacoes: selecionadas, motoristas, cacambas, frota, onClose, onConfirmar, apiFetch: apiFetchProp, onLocacaoAtualizada }) {
  const todasPagas = selecionadas.every(l => !!l.pago);
  const hoje = hojeLocal();
  const [form, setForm] = useState({ dataRetirada: hoje, motorista: "", veiculo: "", comissao: "", pago: todasPagas, dataPagamento: todasPagas ? (selecionadas[0]?.dataPagamento||selecionadas[0]?.data_pagamento||hoje) : "", destinoRetirada: "", aterroId: "" });
  const [aterros, setAterros] = useState([]);
  useEffect(() => { if (apiFetchProp) apiFetchProp("/api/aterros").then(d => setAterros(d||[])).catch(()=>{}); }, []);
  // Diária extra acumulada por atraso (aditivo): { locId: { novoValor } } após aplicar
  const [diariaAplicada, setDiariaAplicada] = useState({});
  const [aplicandoDiaria, setAplicandoDiaria] = useState(null);
  const diariaPendente = (l) => {
    if (diariaAplicada[l.id] !== undefined) return null;
    const dx = calcDiariaExtraAtraso(l);
    if (!dx) return null;
    const jaAplicado = parseFloat(l.valorExtras != null ? l.valorExtras : l.valor_extras) || 0;
    const pendente = dx.total - jaAplicado;
    if (pendente <= 0.005) return null;
    return { ...dx, pendente };
  };
  const aplicarDiaria = async (l, info) => {
    if (!apiFetchProp || aplicandoDiaria) return;
    setAplicandoDiaria(l.id);
    try {
      const upd = await apiFetchProp("/api/locacoes/" + l.id + "/aplicar-diaria", { method: "POST", body: { valor: Math.round(info.pendente * 100) / 100, dias: info.dias } });
      setDiariaAplicada(p => ({ ...p, [l.id]: parseFloat(upd?.valor) || 0 }));
      if (onLocacaoAtualizada) onLocacaoAtualizada(upd);
    } catch (e) { alert("Erro ao aplicar diária extra: " + e.message); }
    finally { setAplicandoDiaria(null); }
  };
  const [rows, setRows] = useState(selecionadas.map(l => ({ locId: l.id, itinerario: "", mtr: l.mtr||"", localDescarte: "", localEstacionario: "" })));
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setRows(p => p.map((r,j) => j===i ? { ...r, [k]: v } : r));
  // Auto-preenchimento da comissão (retirada) a partir do cadastro do motorista.
  // Só preenche se o campo estiver vazio ou ainda contiver a sugestão anterior (não sobrescreve valor digitado à mão).
  const [comissaoEditada, setComissaoEditada] = useState(false);
  const ultimaSugestaoComissaoRef = useRef("");
  const onMotoristaChange = (nome) => {
    const m = (motoristas||[]).find(x => (x.nome||String(x)) === nome);
    const raw = m ? (m.comissao_retirada != null ? m.comissao_retirada : m.comissaoRetirada) : null;
    const sug = (raw != null && raw !== "") ? String(parseFloat(raw)) : "";
    setForm(p => {
      const atual = String(p.comissao||"");
      if (!comissaoEditada || atual === "" || atual === ultimaSugestaoComissaoRef.current) {
        ultimaSugestaoComissaoRef.current = sug;
        return { ...p, motorista: nome, comissao: sug };
      }
      return { ...p, motorista: nome };
    });
  };
  const confirm = () => {
    if (!form.dataRetirada) { alert("A Data Real de Retirada é obrigatória."); return; }
    if (!form.motorista) { alert("O Motorista é obrigatório."); return; }
    onConfirmar(form, rows); onClose();
  };
  return (
    <LargeModal title="Confirmação de Retirada" subtitle={selecionadas.length + " locação(ões) selecionada(s)"} onClose={onClose} onSave={confirm} saveLabel="Confirmar" maxWidth={1050}>
      <div style={{ paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Data Real de Retirada *"><input type="date" style={{ ...inp, borderColor:!form.dataRetirada?"#1aae9e":"#e0e0e0" }} value={form.dataRetirada} onChange={e => setF("dataRetirada", e.target.value)} /></Field>
        <Field label="Motorista *">
          <select style={{ ...inp, appearance:"none", borderColor:!form.motorista?"#1aae9e":"#e0e0e0" }} value={form.motorista} onChange={e => onMotoristaChange(e.target.value)}>
            <option value="">-- Selecione --</option>
            {(motoristas||[]).map(m => <option key={m.id||String(m)} value={m.nome||String(m)}>{m.nome||String(m)}</option>)}
          </select>
        </Field>
        <Field label="Veículo de Retirada">
          <select style={{ ...inp, appearance:"none" }} value={form.veiculo} onChange={e => setF("veiculo", e.target.value)}>
            <option value="">-- Selecione --</option>
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").length === 0 && <option value="" disabled>Nenhum veículo cadastrado</option>}
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").map(v => <option key={v.id||String(v)} value={v.placa||""}>{v.placa ? v.placa+" — "+v.modelo : String(v)}</option>)}
          </select>
        </Field>
        <Field label="Comissão do Motorista (R$)"><input type="number" style={inp} value={form.comissao} onChange={e => { setComissaoEditada(true); setF("comissao", e.target.value); }} placeholder="0,00" /></Field>
        <Field label="Destino">
          <select style={{ ...inp, appearance:"none" }} value={form.destinoRetirada} onChange={e => setF("destinoRetirada", e.target.value)}>
            <option value="">-- Selecione --</option>
            <option value="patio">Pátio</option>
            <option value="aterro">Aterro (direto)</option>
          </select>
        </Field>
        {form.destinoRetirada === "aterro" && (
          <Field label="Aterro">
            <select style={{ ...inp, appearance:"none" }} value={form.aterroId} onChange={e => setF("aterroId", e.target.value)}>
              <option value="">-- Selecione --</option>
              {(aterros||[]).filter(a => a.ativo).map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </Field>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:18 }}>
          <input type="checkbox" id="pagoRet" checked={form.pago} onChange={e => { const v = e.target.checked; setForm(p => ({ ...p, pago: v, dataPagamento: (v && !p.dataPagamento) ? hoje : p.dataPagamento })); }} style={{ accentColor:"#2ecc71", width:15, height:15, cursor: todasPagas ? "default" : "pointer" }} disabled={todasPagas} />
          <label htmlFor="pagoRet" style={{ fontSize:13, color: todasPagas ? "#2ecc71" : "#444", cursor: todasPagas ? "default" : "pointer", fontWeight: todasPagas ? 600 : 400 }}>
            {todasPagas ? "Locação já marcada como paga" : "A locação já foi paga?"}
          </label>
        </div>
        {form.pago && (
          <Field label="Data do Pagamento"><input type="date" style={inp} value={form.dataPagamento||""} onChange={e => setF("dataPagamento", e.target.value)} /></Field>
        )}
      </div>
      <SectionHeader icon="📋" label="Locações Selecionadas" />
      <div style={{ border:"1px solid #ebebeb", borderRadius:3, overflow:"auto", marginTop:10 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>{["Locação","Item Itinerário","MTR/CTR","Local Descarte","Local Estacionário"].map(h => (
              <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb", whiteSpace:"nowrap" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {selecionadas.map((l, i) => (
              <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                <td style={{ padding:"10px 12px", minWidth:200 }}>
                  <div style={{ fontWeight:700, fontSize:12, color:"#1a1a1a" }}>{l.id} {l.cliente}</div>
                  <div style={{ fontSize:10, color:"#888" }}>Endereço: {l.logradouro||""}{l.numero?", "+l.numero:""}{l.complemento?" - "+l.complemento:""} - {l.bairro||""}{l.cidade?", "+l.cidade:""} - {l.uf||""}</div>
                </td>
                <td style={{ padding:"10px 12px" }}><input style={{ ...inp, fontSize:11, minWidth:140 }} value={rows[i]?.itinerario||""} onChange={e => setRow(i,"itinerario",e.target.value)} placeholder="-- Selecione --" /></td>
                <td style={{ padding:"10px 12px" }}><input style={{ ...inp, fontSize:11, width:100 }} value={rows[i]?.mtr||""} onChange={e => setRow(i,"mtr",e.target.value)} placeholder="MTR-000000" /></td>
                <td style={{ padding:"10px 12px" }}><input style={{ ...inp, fontSize:11, minWidth:130 }} value={rows[i]?.localDescarte||""} onChange={e => setRow(i,"localDescarte",e.target.value)} placeholder="-- Selecione --" /></td>
                <td style={{ padding:"10px 12px" }}><input style={{ ...inp, fontSize:11, minWidth:130 }} value={rows[i]?.localEstacionario||""} onChange={e => setRow(i,"localEstacionario",e.target.value)} placeholder="-- Selecione --" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selecionadas.map(l => {
        const info = diariaPendente(l);
        if (info) return (
          <div key={"dx-"+l.id} style={{ marginTop:10, padding:"10px 14px", background:"#fdecea", border:"1px solid #f5c6c0", borderRadius:4, fontSize:12, color:"#c0392b", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
            <span>
              <strong>#{l.id}</strong> — Diária extra acumulada: <strong>{fmt(info.pendente)}</strong> ({info.dias} dia{info.dias > 1 ? "s" : ""} de atraso × {fmt(info.diaria)}/dia)
            </span>
            <button type="button" disabled={aplicandoDiaria === l.id} onClick={() => aplicarDiaria(l, info)} style={{ padding:"6px 14px", background: aplicandoDiaria === l.id ? "#ccc" : "#e74c3c", color:"#fff", border:"none", borderRadius:4, fontSize:11, fontWeight:700, cursor: aplicandoDiaria === l.id ? "not-allowed" : "pointer", fontFamily:FONT }}>
              {aplicandoDiaria === l.id ? "Aplicando..." : "Aplicar ao valor"}
            </button>
          </div>
        );
        if (diariaAplicada[l.id] !== undefined) return (
          <div key={"dx-"+l.id} style={{ marginTop:10, padding:"10px 14px", background:"#f0fff4", border:"1px solid #b7ebc6", borderRadius:4, fontSize:12, color:"#1a7a38" }}>
            <strong>#{l.id}</strong> — Diária extra aplicada. Novo valor total: <strong>{fmt(diariaAplicada[l.id])}</strong>
          </div>
        );
        return null;
      })}
    </LargeModal>
  );
}

// ─── Pedido de Troca Modal ────────────────────────────────────────────────────
function PedidoTrocaModal({ locacoes: selecionadas, cacambas, onClose, onConfirmar }) {
  const [form, setForm] = useState({ dataSolicitacao: hojeLocal(), dataPrevistaTroca: "", dataPrevistaRetirada: "", gerarMtr: false });
  const [rows, setRows] = useState(selecionadas.map(l => ({ locId: l.id, tipoCacamba: l.tipoCacamba||"", cacamba: l.cacamba||"", mtr: "", preco: l.valor||"" })));
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setRows(p => p.map((r,j) => j===i ? { ...r, [k]: v } : r));
  const confirm = () => {
    if (!form.dataSolicitacao) { alert("A Data de Solicitação da Troca é obrigatória."); return; }
    if (!form.dataPrevistaTroca) { alert("A Data Prevista da Troca é obrigatória."); return; }
    if (!form.dataPrevistaRetirada) { alert("A Data Prevista de Retirada é obrigatória."); return; }
    // Validar que data da troca >= data colocada
    for (const l of selecionadas) {
      const dataCol = l.dataColocacaoReal || l.dataEntrega || l.data_entrega;
      if (dataCol && form.dataPrevistaTroca < dataCol) {
        alert("A data prevista da troca ("+form.dataPrevistaTroca+") não pode ser anterior à data da colocação ("+dataCol+") da locação "+l.id+".");
        return;
      }
    }
    onConfirmar(form, rows); onClose();
  };
  return (
    <LargeModal title="Pedido de Troca" subtitle={"Informe os dados da troca de caçamba • " + selecionadas.length + " locação(ões)"} onClose={onClose} onSave={confirm} saveLabel="Confirmar">
      <div style={{ paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Data Solicitação da Troca *"><input type="date" style={{ ...inp, borderColor:!form.dataSolicitacao?"#1aae9e":"#e0e0e0" }} value={form.dataSolicitacao} onChange={e => setF("dataSolicitacao", e.target.value)} /></Field>
        <Field label="Data Prevista da Troca *"><input type="date" style={{ ...inp, borderColor:!form.dataPrevistaTroca?"#1aae9e":"#e0e0e0" }} value={form.dataPrevistaTroca} onChange={e => setF("dataPrevistaTroca", e.target.value)} /></Field>
        <Field label="Data Prevista de Retirada *"><input type="date" style={{ ...inp, borderColor:!form.dataPrevistaRetirada?"#1aae9e":"#e0e0e0" }} value={form.dataPrevistaRetirada} min={form.dataPrevistaTroca||undefined} onChange={e => setF("dataPrevistaRetirada", e.target.value)} /></Field>
      </div>
      <div style={{ marginBottom:14 }}>
        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#555", cursor:"pointer" }}>
          <input type="checkbox" checked={form.gerarMtr} onChange={e => setF("gerarMtr", e.target.checked)} style={{ accentColor:"#1aae9e" }} />
          Gerar Número da MTR Automaticamente
        </label>
      </div>
      <SectionHeader icon="📋" label="Locações Selecionadas" />
      <div style={{ border:"1px solid #ebebeb", borderRadius:3, overflow:"hidden", marginTop:10 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>{["Nº Locação","Cliente / Contato / Endereço","Tipo","Caçamba","MTR/CTR","Preço"].map(h => (
              <th key={h} style={{ textAlign:"left", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {selecionadas.map((l, i) => (
              <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontWeight:700 }}>{l.id}</div>
                  <div style={{ fontSize:10, color:"#aaa" }}>Endereço: {l.logradouro || (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "—")}</div>
                </td>
                <td style={{ padding:"10px 12px" }}>
                  <div style={{ fontWeight:600 }}>{l.cliente}</div>
                </td>
                <td style={{ padding:"10px 12px", fontSize:11 }}>{rows[i]?.tipoCacamba||"—"}</td>
                <td style={{ padding:"10px 12px" }}>
                  <select style={{ ...inp, appearance:"none", fontSize:11 }} value={rows[i]?.cacamba||""} onChange={e => setRow(i,"cacamba",e.target.value)}>
                    <option value="">-- Selecione --</option>
                    {(cacambas||[]).filter(c => !c.status || c.status === "Disponível").map(c => <option key={c.id||String(c)} value={c.codigo||""}>{c.codigo||String(c)}</option>)}
                  </select>
                </td>
                <td style={{ padding:"10px 12px" }}><input style={{ ...inp, fontSize:11, width:80 }} value={rows[i]?.mtr||""} onChange={e => setRow(i,"mtr",e.target.value)} placeholder="MTR" /></td>
                <td style={{ padding:"10px 12px" }}><input type="number" style={{ ...inp, fontSize:11, width:80 }} value={rows[i]?.preco||""} onChange={e => setRow(i,"preco",e.target.value)} placeholder="0,00" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LargeModal>
  );
}

// ─── Confirmar Troca(s) Modal ──────────────────────────────────────────────────
function ConfirmarTrocaModal({ locacoes: selecionadas, motoristas, cacambas, frota, onClose, onConfirmar }) {
  const [form, setForm] = useState({
    dataTroca: hojeLocal(), motorista: "", veiculo: "",
    comissaoRetirada: "", comissaoColocacao: "", dataPrevistaRetirada: "", gerarMtr: false
  });
  const [rows, setRows] = useState(selecionadas.map(l => ({
    locId: l.id, mtrRetirada: "", localDescarte: "", localEstacionario: "",
    novaLocacao: "", mtrColocada: "", cacambaColocada: ""
  })));
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setRow = (i, k, v) => setRows(p => p.map((r,j) => j===i ? { ...r, [k]: v } : r));
  // Auto-preenchimento das comissões a partir do cadastro do motorista.
  // Colocação (nova caçamba) → comissao_colocacao; Retirada → comissao_retirada.
  // Cada campo só é preenchido se estiver vazio ou ainda contiver a sugestão anterior (não sobrescreve valor digitado à mão).
  const [colocEditada, setColocEditada] = useState(false);
  const [retEditada, setRetEditada] = useState(false);
  const ultimaSugColocRef = useRef("");
  const ultimaSugRetRef = useRef("");
  const onMotoristaChange = (nome) => {
    const m = (motoristas||[]).find(x => (x.nome||String(x)) === nome);
    const rawColoc = m ? (m.comissao_colocacao != null ? m.comissao_colocacao : m.comissaoColocacao) : null;
    const rawRet   = m ? (m.comissao_retirada  != null ? m.comissao_retirada  : m.comissaoRetirada)  : null;
    const sugColoc = (rawColoc != null && rawColoc !== "") ? String(parseFloat(rawColoc)) : "";
    const sugRet   = (rawRet   != null && rawRet   !== "") ? String(parseFloat(rawRet))   : "";
    setForm(p => {
      const next = { ...p, motorista: nome };
      const atualC = String(p.comissaoColocacao||"");
      if (!colocEditada || atualC === "" || atualC === ultimaSugColocRef.current) {
        ultimaSugColocRef.current = sugColoc; next.comissaoColocacao = sugColoc;
      }
      const atualR = String(p.comissaoRetirada||"");
      if (!retEditada || atualR === "" || atualR === ultimaSugRetRef.current) {
        ultimaSugRetRef.current = sugRet; next.comissaoRetirada = sugRet;
      }
      return next;
    });
  };

  const confirm = () => {
    if (!form.dataTroca) { alert("A Data Real da Troca é obrigatória."); return; }
    if (!form.motorista) { alert("O Motorista da Troca é obrigatório."); return; }
    if (!form.dataPrevistaRetirada) { alert("A Data Prevista de Retirada é obrigatória."); return; }
    // Validar que data da troca >= data colocada
    for (const l of selecionadas) {
      const dataCol = l.dataColocacaoReal || l.dataEntrega || l.data_entrega;
      if (dataCol && form.dataTroca < dataCol) {
        alert("A data da troca ("+form.dataTroca+") não pode ser anterior à data da colocação ("+dataCol+") da locação "+l.id+".");
        return;
      }
    }
    onConfirmar(form, rows); onClose();
  };

  return (
    <LargeModal title="Confirmar Troca(s)" subtitle={"Informe os dados da troca realizada • " + selecionadas.length + " locação(ões)"} onClose={onClose} onSave={confirm} saveLabel="Confirmar">
      <div style={{ paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
        <Field label="Data Real da Troca *"><input type="date" style={{ ...inp, borderColor:!form.dataTroca?"#1aae9e":"#e0e0e0" }} value={form.dataTroca} onChange={e => setF("dataTroca", e.target.value)} /></Field>
        <Field label="Motorista da Troca *">
          <select style={{ ...inp, appearance:"none", borderColor:!form.motorista?"#1aae9e":"#e0e0e0" }} value={form.motorista} onChange={e => onMotoristaChange(e.target.value)}>
            <option value="">-- Selecione --</option>
            {(motoristas||[]).map(m => <option key={m.id||String(m)} value={m.nome||String(m)}>{m.nome||String(m)}</option>)}
          </select>
        </Field>
        <Field label="Veículo da Troca">
          <select style={{ ...inp, appearance:"none" }} value={form.veiculo} onChange={e => setF("veiculo", e.target.value)}>
            <option value="">-- Selecione --</option>
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").length === 0 && <option value="" disabled>Nenhum veículo cadastrado</option>}
            {(frota||[]).filter(v => !v.status || v.status === "Ativo").map(v => <option key={v.id||String(v)} value={v.placa||""}>{v.placa ? v.placa+" — "+v.modelo : String(v)}</option>)}
          </select>
        </Field>
        <Field label="Comissão da Retirada (R$)"><input type="number" style={inp} value={form.comissaoRetirada} onChange={e => { setRetEditada(true); setF("comissaoRetirada", e.target.value); }} placeholder="0,00" /></Field>
        <Field label="Comissão da Colocação (R$)"><input type="number" style={inp} value={form.comissaoColocacao} onChange={e => { setColocEditada(true); setF("comissaoColocacao", e.target.value); }} placeholder="0,00" /></Field>
        <Field label="Data Prevista de Retirada *"><input type="date" style={{ ...inp, borderColor:!form.dataPrevistaRetirada?"#1aae9e":"#e0e0e0" }} value={form.dataPrevistaRetirada} onChange={e => setF("dataPrevistaRetirada", e.target.value)} /></Field>
      </div>
      <div style={{ marginBottom:14 }}>
        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#555", cursor:"pointer" }}>
          <input type="checkbox" checked={form.gerarMtr} onChange={e => setF("gerarMtr", e.target.checked)} style={{ accentColor:"#1aae9e" }} />
          Gerar Número da MTR/CTR Automaticamente
        </label>
      </div>
      <div style={{ border:"1px solid #ebebeb", borderRadius:3, overflow:"hidden", marginTop:10 }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>
              <th style={{ textAlign:"left", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#888", borderBottom:"1px solid #ebebeb" }}></th>
              <th colSpan={2} style={{ textAlign:"center", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#1aae9e", borderBottom:"1px solid #ebebeb", background:"#f0f4ff" }}>Retirada</th>
              <th colSpan={3} style={{ textAlign:"center", padding:"7px 12px", fontSize:10, fontWeight:700, color:"#2ecc71", borderBottom:"1px solid #ebebeb", background:"#f0fff4" }}>Colocada</th>
            </tr>
            <tr>
              {["Locação","Item Itinerário","MTR/CTR","Locação","MTR/CTR","Caçamba"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"5px 10px", fontSize:9, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {selecionadas.map((l, i) => (
              <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                <td style={{ padding:"8px 10px" }}>
                  <div style={{ fontWeight:700, fontSize:11 }}>{l.id} {l.cliente}</div>
                  <div style={{ fontSize:10, color:"#aaa" }}>{l.logradouro || (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "")}</div>
                </td>
                <td style={{ padding:"8px 6px", fontSize:11, color:"#aaa" }}>N/A</td>
                <td style={{ padding:"8px 6px" }}><input style={{ ...inp, fontSize:10, width:70 }} value={rows[i]?.mtrRetirada||""} onChange={e => setRow(i,"mtrRetirada",e.target.value)} /></td>
                <td style={{ padding:"8px 6px", fontSize:10, color:"#2ecc71", fontWeight:700 }}>{rows[i]?.novaLocacao||"(Nova)"}</td>
                <td style={{ padding:"8px 6px" }}><input style={{ ...inp, fontSize:10, width:70 }} value={rows[i]?.mtrColocada||""} onChange={e => setRow(i,"mtrColocada",e.target.value)} /></td>
                <td style={{ padding:"8px 6px" }}>
                  <select style={{ ...inp, appearance:"none", fontSize:10 }} value={rows[i]?.cacambaColocada||""} onChange={e => setRow(i,"cacambaColocada",e.target.value)}>
                    <option value="">-- Selecione --</option>
                    {(cacambas||[]).filter(c => !c.status || c.status === "Disponível").map(c => <option key={c.id||String(c)} value={c.codigo||""}>{c.codigo||String(c)}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop:10, padding:"8px 12px", background:"#f0f7ff", borderRadius:4, fontSize:11, color:"#1aae9e" }}>
        💡 Campos com borda <strong>azul</strong> são obrigatórios. A locação antiga irá para "Para Cobrar" e uma nova locação será criada.
      </div>
    </LargeModal>
  );
}

// ─── Confirmação de Pagamento Modal ─────────────────────────────────────────
function ConfirmarPagamentoModal({ locacoes: selecionadas, onClose, onConfirmar, apiFetch, onLocacaoAtualizada }) {
  const [form, setForm] = useState({
    dataPagamento: hojeLocal(),
    modalidade: "",
    comprovante: null,
    comprovanteNome: "",
    obs: ""
  });
  const fileRef = useRef();
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // Diária extra acumulada por atraso (aditivo): { locId: novoValor } após aplicar
  const [diariaAplicada, setDiariaAplicada] = useState({});
  const [aplicandoDiaria, setAplicandoDiaria] = useState(null);
  const diariaPendente = (l) => {
    if (diariaAplicada[l.id] !== undefined) return null;
    const dx = calcDiariaExtraAtraso(l, { incluirCobranca: true });
    if (!dx) return null;
    const jaAplicado = parseFloat(l.valorExtras != null ? l.valorExtras : l.valor_extras) || 0;
    const pendente = dx.total - jaAplicado;
    if (pendente <= 0.005) return null;
    return { ...dx, pendente };
  };
  const aplicarDiaria = async (l, info) => {
    if (!apiFetch || aplicandoDiaria) return;
    setAplicandoDiaria(l.id);
    try {
      const upd = await apiFetch("/api/locacoes/" + l.id + "/aplicar-diaria", { method: "POST", body: { valor: Math.round(info.pendente * 100) / 100, dias: info.dias } });
      setDiariaAplicada(p => ({ ...p, [l.id]: parseFloat(upd?.valor) || 0 }));
      if (onLocacaoAtualizada) onLocacaoAtualizada(upd);
    } catch (e) { alert("Erro ao aplicar diária extra: " + e.message); }
    finally { setAplicandoDiaria(null); }
  };

  const MODALIDADES = [
    "Dinheiro", "PIX", "Cartão de Crédito", "Cartão de Débito",
    "Boleto Bancário", "Transferência Bancária", "Cheque"
  ];

  const confirm = () => {
    if (!form.dataPagamento) { alert("A Data do Pagamento é obrigatória."); return; }
    if (!form.modalidade) { alert("A Modalidade de Pagamento é obrigatória."); return; }
    onConfirmar(form); onClose();
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setF("comprovante", file);
      setF("comprovanteNome", file.name);
    }
  };

  return (
    <LargeModal title="Confirmação de Pagamento" subtitle={selecionadas.length + " locação(ões) selecionada(s)"} onClose={onClose} onSave={confirm} saveLabel="Confirmar Pagamento">
      <div style={{ paddingTop:18 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:18 }}>
          <Field label="Data do Pagamento *">
            <input type="date" style={{ ...inp, borderColor:!form.dataPagamento?"#1aae9e":"#e0e0e0", fontWeight:600 }} value={form.dataPagamento} onChange={e => setF("dataPagamento", e.target.value)} />
          </Field>
          <Field label="Modalidade de Pagamento *">
            <select style={{ ...inp, appearance:"none", borderColor:!form.modalidade?"#1aae9e":"#e0e0e0", fontWeight:600 }} value={form.modalidade} onChange={e => setF("modalidade", e.target.value)}>
              <option value="">-- Selecione a modalidade --</option>
              {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Comprovante de Pagamento">
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <button onClick={() => fileRef.current?.click()}
              style={{ padding:"8px 16px", background:"#f0f4ff", color:"#1aae9e", border:"1px solid #c7d7fd", borderRadius:4, cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:FONT, display:"flex", alignItems:"center", gap:6 }}>
              {form.comprovanteNome ? "Trocar arquivo" : "Anexar comprovante"}
            </button>
            {form.comprovanteNome && (
              <span style={{ fontSize:12, color:"#555", fontWeight:500 }}>
                {form.comprovanteNome}
                <button onClick={() => { setF("comprovante", null); setF("comprovanteNome", ""); }}
                  style={{ background:"none", border:"none", color:"#e74c3c", cursor:"pointer", marginLeft:6, fontSize:14, fontWeight:700 }}>×</button>
              </span>
            )}
            <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display:"none" }} onChange={handleFile} />
          </div>
        </Field>

        <div style={{ marginTop:14 }}>
          <Field label="Observações">
            <textarea style={{ ...inp, minHeight:70, resize:"vertical" }} value={form.obs} onChange={e => setF("obs", e.target.value)} placeholder="Observações sobre o pagamento (opcional)" />
          </Field>
        </div>

        <SectionHeader icon="📋" label="Locações Selecionadas" />
        <div style={{ border:"1px solid #ebebeb", borderRadius:"0 0 3px 3px", overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead style={{ background:"#fafafa" }}>
              <tr>{["Nº Locação","Cliente","Valor","Status Anterior"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"8px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {selecionadas.map(l => {
                const valorExibido = diariaAplicada[l.id] !== undefined ? diariaAplicada[l.id] : l.valor;
                return (
                <tr key={l.id} style={{ borderBottom:"1px solid #f5f5f5" }}>
                  <td style={{ padding:"10px 12px", fontWeight:700, fontSize:12 }}>{l.id}</td>
                  <td style={{ padding:"10px 12px", fontWeight:600 }}>{l.cliente}</td>
                  <td style={{ padding:"10px 12px", fontWeight:700, color:"#2ecc71" }}>{valorExibido ? fmt(valorExibido) : "—"}</td>
                  <td style={{ padding:"10px 12px" }}><span style={{ background:"#fff3e0", color:"#e65100", padding:"2px 8px", borderRadius:3, fontSize:10, fontWeight:700 }}>{l.status}</span></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {selecionadas.map(l => {
          const info = diariaPendente(l);
          if (info) return (
            <div key={"dx-"+l.id} style={{ marginTop:10, padding:"10px 14px", background:"#fdecea", border:"1px solid #f5c6c0", borderRadius:4, fontSize:12, color:"#c0392b", display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
              <span>
                <strong>#{l.id}</strong> — Diária extra acumulada: <strong>{fmt(info.pendente)}</strong> ({info.dias} dia{info.dias > 1 ? "s" : ""} de atraso × {fmt(info.diaria)}/dia)
              </span>
              <button type="button" disabled={aplicandoDiaria === l.id} onClick={() => aplicarDiaria(l, info)} style={{ padding:"6px 14px", background: aplicandoDiaria === l.id ? "#ccc" : "#e74c3c", color:"#fff", border:"none", borderRadius:4, fontSize:11, fontWeight:700, cursor: aplicandoDiaria === l.id ? "not-allowed" : "pointer", fontFamily:FONT }}>
                {aplicandoDiaria === l.id ? "Aplicando..." : "Aplicar ao valor"}
              </button>
            </div>
          );
          if (diariaAplicada[l.id] !== undefined) return (
            <div key={"dx-"+l.id} style={{ marginTop:10, padding:"10px 14px", background:"#f0fff4", border:"1px solid #b7ebc6", borderRadius:4, fontSize:12, color:"#1a7a38" }}>
              <strong>#{l.id}</strong> — Diária extra aplicada. Novo valor total: <strong>{fmt(diariaAplicada[l.id])}</strong>
            </div>
          );
          return null;
        })}

        <div style={{ marginTop:12, padding:"10px 14px", background:"#f0fff4", borderRadius:4, fontSize:11, color:"#1a7a38", display:"flex", alignItems:"center", gap:8 }}>
          💡 Ao confirmar, as locações serão marcadas como <strong style={{ marginLeft:4 }}>Encerrada</strong> e o pagamento será registrado.
        </div>
      </div>
    </LargeModal>
  );
}

// ─── Locação Detail Modal ─────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function LocacaoDetailModal({ locacao, onClose, onUpdate, apiFetch }) {
  const [tab, setTab] = useState("info");
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [planoInfo, setPlanoInfo] = useState(null);
  const [vinculos, setVinculos] = useState({ pai: null, filhas: [], loading: false });
  const fileRef = useRef();
  const API = process.env.REACT_APP_API || "http://localhost:3001";

  useEffect(() => { apiFetch("/api/config/storage").then(setPlanoInfo).catch(() => {}); }, []);

  // Carregar vínculos (pai/filha) quando abre a aba
  const loadVinculos = async () => {
    if (!apiFetch) return;
    setVinculos(p => ({ ...p, loading: true }));
    try {
      const todas = await apiFetch("/api/locacoes");
      const id = String(locacao.id);
      // Buscar filhas: locações cujo obs menciona "troca da locação #ID"
      const filhas = (todas || []).filter(l => l.obs && typeof l.obs === "string" && (l.obs.toLowerCase().includes("troca da locação #" + id) || l.obs.toLowerCase().includes("troca da locacao #" + id) || l.obs.toLowerCase().includes("troca da locação " + id) || l.obs.toLowerCase().includes("troca da locacao " + id)));
      // Buscar pai: se minha obs menciona "troca da locação #", extrair o ID pai
      let pai = null;
      const obsLocal = locacao.obs || "";
      const matchPai = obsLocal.match(/troca da loca[çc][aã]o\s*#?\s*(\d+)/i);
      if (matchPai) {
        const paiId = matchPai[1];
        pai = (todas || []).find(l => String(l.id) === paiId) || null;
      }
      setVinculos({ pai, filhas, loading: false });
    } catch(e) { console.error(e); setVinculos(p => ({ ...p, loading: false })); }
  };

  useEffect(() => { if (tab === "vinculos") loadVinculos(); }, [tab]);

  const handleAnexar = () => {
    if (planoInfo && !planoInfo.permite_anexos) {
      alert("Seu plano atual (Básico) não permite anexar documentos.\n\nFaça upgrade para o plano Controla para desbloquear essa funcionalidade.");
      return;
    }
    fileRef.current?.click();
  };

  // Carregar documentos do backend
  const loadDocs = async () => {
    if (!apiFetch) return;
    setDocsLoading(true);
    try {
      const data = await apiFetch("/api/documentos?locacao_id=" + locacao.id);
      setDocs(data || []);
    } catch(e) { console.error(e); }
    setDocsLoading(false);
  };

  useEffect(() => { loadDocs(); }, []);

  // Carregar histórico quando abre a aba
  const loadHistorico = async () => {
    if (!apiFetch) return;
    setHistLoading(true);
    try {
      const data = await apiFetch("/api/historico/" + locacao.id);
      setHistorico(data || []);
    } catch(e) { console.error(e); }
    setHistLoading(false);
  };

  useEffect(() => { if (tab === "historico") loadHistorico(); }, [tab]);

  const addDoc = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const token = sessionStorage.getItem("controla_token");
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("documento", file);
        formData.append("categoria", "geral");
        formData.append("locacao_id", locacao.id);
        const res = await fetch(API + "/api/documentos", {
          method: "POST",
          headers: { Authorization: "Bearer " + token },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok) { alert(data.error || "Erro no upload"); continue; }
      } catch (err) { alert("Erro ao enviar: " + err.message); }
    }
    e.target.value = "";
    await loadDocs();
    setUploading(false);
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await apiFetch("/api/documentos/" + id, { method: "DELETE" });
      setDocs(docs.filter(d => d.id !== id));
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  const TABS = [
    { key:"info",  label:"Dados do Pedido" },
    { key:"operacional", label:"Operacional" },
    { key:"pagto", label:"Pagamento" },
    { key:"vinculos", label:"Vínculos" },
    { key:"docs",  label:`Documentos (${docs.length})` },
    { key:"historico", label:`Histórico` },
  ];

  const vencOk = locacao.pagamento && (locacao.pagamento === "boleto" || locacao.pagamento === "faturado") && locacao.prazoPagamento && locacao.dataEntrega;
  let dataVenc = null, vencida = false;
  if (vencOk) {
    dataVenc = new Date(locacao.dataEntrega);
    dataVenc.setDate(dataVenc.getDate() + parseInt(locacao.prazoPagamento));
    vencida = dataVenc < new Date() && !locacao.pago;
  }

  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:760, maxHeight:"90vh", display:"flex", flexDirection:"column", borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.22)", fontFamily:FONT, overflow:"hidden" }}>
        {/* Header */}
        <div style={{ padding:"14px 22px", borderBottom:"1px solid #e4e4e4", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontWeight:800, fontSize:16, color:"#1a1a1a" }}>Locação #{locacao.id}</div>
            <div style={{ fontSize:12, color:"#888", marginTop:2 }}>{locacao.cliente} — <span style={{ fontWeight:600, color:locacao.pago?"#2ecc71":"#e74c3c" }}>{locacao.pago?"PAGO":"NÃO PAGO"}</span></div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:20 }}>✕</button>
        </div>

        {/* Overdue warning */}
        {vencida && (
          <div style={{ background:"#fde8e8", borderBottom:"2px solid #e74c3c", padding:"10px 22px", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight:800, color:"#c0392b", fontSize:13 }}>PAGAMENTO VENCIDO</div>
              <div style={{ fontSize:12, color:"#c0392b" }}>Venceu em {dataVenc.toLocaleDateString("pt-BR")} · {locacao.pagamento?.toUpperCase()} · Prazo: {locacao.prazoPagamento} dias — O cliente está devendo!</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:"2px solid #ebebeb", padding:"0 22px" }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ padding:"10px 16px", border:"none", background:"none", cursor:"pointer", fontWeight:tab===t.key?700:400, color:tab===t.key?"#1aae9e":"#888", borderBottom:tab===t.key?"2px solid #1aae9e":"2px solid transparent", marginBottom:-2, fontFamily:FONT, fontSize:13 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content — altura MÍNIMA estável entre abas; flex:1 + overflow interno deixa o rodapé (Fechar) sempre visível dentro do maxHeight do modal */}
        <div className="locdetail-body" style={{ minHeight:"min(60vh, 520px)", flex:"1 1 auto", overflowY:"auto", padding:"18px 22px" }}>

          {tab === "info" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              {[
                ["Cliente", locacao.cliente],
                ["Operação", locacao.operacao||"—"],
                ["Status", locacao.status],
                ["Valor Total", locacao.valor ? fmt(locacao.valor) : "—"],
                ["Data Entrega", (locacao.dataEntrega||"—") + ((locacao.horaEntrega||locacao.hora_entrega) ? "  às " + (locacao.horaEntrega||locacao.hora_entrega) : "")],
                ["Data Retirada", locacao.dataRetiraPrevista||locacao.dataRetirada||"—"],
                ["MTR/CTR", locacao.mtr||"—"],
                ["Forma Pagamento", locacao.pagamento?.toUpperCase()||"—"],
                ["Pago", locacao.pago ? "SIM" : "NÃO"],
                ["Data Pagamento", (locacao.dataPagamento||locacao.data_pagamento) ? fmtDate(locacao.dataPagamento||locacao.data_pagamento) : "—"],
              ].map(([label, val]) => (
                <div key={label} style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:13, color:"#333", fontWeight:600 }}>{val}</div>
                </div>
              ))}
              {(locacao.logradouro || locacao.endereco) && (
                <div style={{ gridColumn:"1/-1", borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Endereço</div>
                  <div style={{ fontSize:13, color:"#333" }}>{locacao.logradouro ? [locacao.logradouro, locacao.numero, locacao.complemento||locacao.end_complemento, locacao.bairro, locacao.cidade, locacao.uf].filter(Boolean).join(", ") : (typeof locacao.endereco === "string" ? locacao.endereco : locacao.endereco ? [locacao.endereco.logradouro,locacao.endereco.numero,locacao.endereco.complemento,locacao.endereco.cidade,locacao.endereco.uf].filter(Boolean).join(", ") : "")}</div>
                </div>
              )}
              {locacao.observacoes && (
                <div style={{ gridColumn:"1/-1" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>Observações</div>
                  <div style={{ fontSize:13, color:"#555" }}>{locacao.observacoes}</div>
                </div>
              )}
            </div>
          )}

          {tab === "operacional" && (() => {
            const secStyle = { border:"1px solid #e8e8e8", borderRadius:6, padding:16, marginBottom:16 };
            const secTitle = (icon, text) => <div style={{ fontWeight:800, fontSize:13, color:"#1a1a1a", marginBottom:12, display:"flex", alignItems:"center", gap:6 }}>{icon} {text}</div>;
            const fieldGrid = { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 };
            const fieldBox = (label, val) => (
              <div style={{ padding:"10px 12px", background:"#f9f9f9", borderRadius:4, border:"1px solid #f0f0f0" }}>
                <div style={{ fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.4, marginBottom:3 }}>{label}</div>
                <div style={{ fontSize:13, fontWeight:600, color: val && val !== "—" ? "#333" : "#ccc" }}>{val || "—"}</div>
              </div>
            );
            const dataColReal = locacao.dataColocacaoReal || locacao.data_colocacao_real || "";
            const dataRetReal = locacao.dataRetiradaReal || locacao.data_retirada_real || "";
            const dataRetPrev = locacao.dataRetiraPrevista || locacao.dataRetirada || locacao.data_retirada || "";
            const isColocada = ["Colocada","Para Retirar","Para Trocar","Para Cobrar","Encerrada"].includes(locacao.status);
            const isRetirada = ["Para Cobrar","Encerrada"].includes(locacao.status);
            return (
              <div>
                {/* Confirmação de Entrega */}
                <div style={secStyle}>
                  {secTitle("", "Confirmação de Entrega (Colocação)")}
                  {isColocada ? (
                    <div style={fieldGrid}>
                      {fieldBox("Data Real da Entrega", fmtDate(dataColReal) || fmtDate(locacao.dataEntrega))}
                      {fieldBox("Data Prev. Entrega", fmtDate(locacao.dataEntrega))}
                      {fieldBox("Caçamba", (locacao.cacamba||"—") + (locacao.tipoCacamba ? " ("+locacao.tipoCacamba+")" : ""))}
                      {fieldBox("Motorista", locacao.motorista)}
                      {fieldBox("Veículo", locacao.veiculo)}
                      {fieldBox("Comissão (R$)", locacao.comissao ? "R$ "+Number(locacao.comissao).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "—")}
                    </div>
                  ) : (
                    <div style={{ padding:20, textAlign:"center", color:"#ccc", fontSize:13 }}>Entrega ainda não confirmada</div>
                  )}
                </div>

                {/* Confirmação de Retirada */}
                <div style={secStyle}>
                  {secTitle("", "Confirmação de Retirada")}
                  {isRetirada ? (
                    <div style={fieldGrid}>
                      {fieldBox("Data Real da Retirada", fmtDate(dataRetReal))}
                      {fieldBox("Data Prev. Retirada", fmtDate(dataRetPrev))}
                      {fieldBox("Motorista Retirada", locacao.motoristaRetirada || locacao.motorista_retirada)}
                    </div>
                  ) : locacao.status === "Para Retirar" ? (
                    <div style={fieldGrid}>
                      {fieldBox("Data Prev. Retirada", fmtDate(dataRetPrev))}
                      {fieldBox("Data Real da Retirada", "Aguardando")}
                      {fieldBox("Motorista Retirada", "—")}
                    </div>
                  ) : (
                    <div style={{ padding:20, textAlign:"center", color:"#ccc", fontSize:13 }}>Retirada ainda não realizada</div>
                  )}
                </div>

                {/* Dados extras */}
                <div style={secStyle}>
                  {secTitle("", "Informações Adicionais")}
                  <div style={fieldGrid}>
                    {fieldBox("Contato", locacao.contatoNome || locacao.contato_nome)}
                    {fieldBox("Telefone", locacao.contatoTel || locacao.contato_tel)}
                    {fieldBox("MTR/CTR", locacao.mtr)}
                    {fieldBox("Operação", locacao.operacao)}
                    {fieldBox("Data Solicitação", fmtDate(locacao.dataSolicitacao || locacao.data_solicitacao))}
                    {(locacao.dataPedidoRetirada || locacao.data_pedido_retirada) && fieldBox("Pedido Retirada", fmtDate(locacao.dataPedidoRetirada || locacao.data_pedido_retirada))}
                    {(locacao.dataPedidoTroca || locacao.data_pedido_troca) && fieldBox("Pedido Troca", fmtDate(locacao.dataPedidoTroca || locacao.data_pedido_troca))}
                    {fieldBox("Classe Resíduo", locacao.classeResiduo || locacao.classe_residuo)}
                  </div>
                </div>
              </div>
            );
          })()}

          {tab === "vinculos" && (() => {
            const cardStyle = (color) => ({ border:"1px solid #e8e8e8", borderRadius:8, overflow:"hidden", marginBottom:16, borderLeft:"4px solid " + color });
            const cardHeader = (icon, title, subtitle, color) => (
              <div style={{ background:"linear-gradient(135deg, " + color + "10 0%, #fff 100%)", padding:"14px 18px", borderBottom:"1px solid #f0f0f0" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:20 }}>{icon}</span>
                  <div>
                    <div style={{ fontWeight:800, fontSize:14, color:"#1a1a1a" }}>{title}</div>
                    <div style={{ fontSize:11, color:"#888", marginTop:1 }}>{subtitle}</div>
                  </div>
                </div>
              </div>
            );
            const locCard = (l, label) => {
              const statusColors = { "Para Colocar":"#1a9e8f", "Colocada":"#2ecc71", "Para Retirar":"#9b59b6", "Para Trocar":"#f39c12", "Para Cobrar":"#f59e0b", "Encerrada":"#888", "Cancelada":"#e74c3c" };
              const cor = statusColors[l.status] || "#888";
              const addr = l.end_logradouro ? [l.end_logradouro, l.end_numero, l.end_complemento, l.end_bairro, l.end_cidade, l.end_uf].filter(Boolean).join(", ") : (typeof l.endereco === "object" && l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.complemento,l.endereco.bairro,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "");
              return (
                <div style={{ padding:"14px 18px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontWeight:900, fontSize:15, color:"#1aae9e" }}>#{l.id}</span>
                      <span style={{ padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:cor+"18", color:cor }}>{l.status}</span>
                      <span style={{ fontSize:11, fontWeight:700, color:l.pago?"#2ecc71":"#e74c3c" }}>{l.pago?"PAGO":"NÃO PAGO"}</span>
                    </div>
                    <span style={{ fontSize:10, color:"#aaa", fontWeight:600 }}>{label}</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                    {[
                      ["Cliente", l.cliente_nome || l.cliente || "—"],
                      ["Caçamba", (l.cacamba_codigo || l.cacamba || "—") + (l.tipo_cacamba || l.tipoCacamba ? " (" + (l.tipo_cacamba || l.tipoCacamba) + ")" : "")],
                      ["Valor", l.valor ? "R$ " + parseFloat(l.valor).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "—"],
                      ["Data Entrega", fmtDate(l.data_entrega || l.dataEntrega) || "—"],
                      ["Data Retirada", fmtDate(l.data_retirada || l.dataRetirada || l.data_retirada_real) || "—"],
                      ["Motorista", l.motorista || "—"],
                    ].map(([lb,vl]) => (
                      <div key={lb} style={{ padding:"8px 10px", background:"#f9f9f9", borderRadius:4, border:"1px solid #f0f0f0" }}>
                        <div style={{ fontSize:9, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 }}>{lb}</div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#333" }}>{vl}</div>
                      </div>
                    ))}
                  </div>
                  {addr && <div style={{ marginTop:8, fontSize:11, color:"#888" }}>Endereço: {addr}</div>}
                  {l.obs && <div style={{ marginTop:6, fontSize:11, color:"#aaa", fontStyle:"italic" }}>{l.obs}</div>}
                </div>
              );
            };
            return (
              <div>
                {vinculos.loading ? (
                  <div style={{ textAlign:"center", padding:40, color:"#aaa", fontSize:13 }}>Carregando vínculos...</div>
                ) : (
                  <div>
                    {/* Explicação */}
                    <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:6, padding:"12px 16px", marginBottom:18, fontSize:12, color:"#0369a1", lineHeight:1.6 }}>
                      Quando uma <strong>troca</strong> é realizada, a locação original (pai) é encerrada e uma nova locação (filha) é criada automaticamente com a nova caçamba no mesmo endereço.
                    </div>

                    {/* Locação Pai */}
                    <div style={cardStyle("#3b82f6")}>
                      {cardHeader("⬆️", "Locação de Origem (Pai)", "Esta locação foi gerada a partir de uma troca", "#3b82f6")}
                      {vinculos.pai ? locCard(vinculos.pai, "ORIGEM") : (
                        <div style={{ padding:"20px 18px", textAlign:"center", color:"#ccc", fontSize:13 }}>
                          {locacao.obs && /troca/i.test(locacao.obs) ? "Locação de origem não encontrada" : "Esta locação não tem origem — é uma locação original"}
                        </div>
                      )}
                    </div>

                    {/* Locação atual */}
                    <div style={{ textAlign:"center", margin:"4px 0 4px", color:"#ddd" }}>
                      <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#f8f8f8", borderRadius:20, padding:"6px 18px", border:"1px solid #ebebeb" }}>
                        <span style={{ fontSize:14 }}>📍</span>
                        <span style={{ fontWeight:800, fontSize:12, color:"#1aae9e" }}>Locação Atual #{locacao.id}</span>
                        <span style={{ fontSize:11, color:"#888" }}>({locacao.status})</span>
                      </div>
                    </div>

                    {/* Locações Filhas */}
                    <div style={cardStyle("#22c55e")}>
                      {cardHeader("⬇️", vinculos.filhas.length > 1 ? "Locações Geradas (Filhas)" : "Locação Gerada (Filha)", "Locações criadas a partir da troca desta", "#22c55e")}
                      {vinculos.filhas.length > 0 ? vinculos.filhas.map((f, i) => (
                        <div key={f.id} style={i > 0 ? { borderTop:"1px solid #f0f0f0" } : {}}>
                          {locCard(f, "FILHA" + (vinculos.filhas.length > 1 ? " #" + (i+1) : ""))}
                        </div>
                      )) : (
                        <div style={{ padding:"20px 18px", textAlign:"center", color:"#ccc", fontSize:13 }}>
                          {["Para Trocar","Para Cobrar","Encerrada"].includes(locacao.status) ? "Nenhuma locação filha encontrada — a troca pode não ter sido confirmada ainda" : "Esta locação não possui locações geradas por troca"}
                        </div>
                      )}
                    </div>

                    {/* Resumo visual */}
                    {(vinculos.pai || vinculos.filhas.length > 0) && (
                      <div style={{ background:"#fafafa", border:"1px solid #ebebeb", borderRadius:6, padding:"12px 16px", marginTop:8, fontSize:12, color:"#555", lineHeight:1.6 }}>
                        <strong style={{ color:"#1a1a1a" }}>Resumo da cadeia:</strong>{" "}
                        {vinculos.pai && <span>Originada da locação <strong style={{ color:"#3b82f6" }}>#{vinculos.pai.id}</strong></span>}
                        {vinculos.pai && vinculos.filhas.length > 0 && " → "}
                        <strong style={{ color:"#1aae9e" }}>#{locacao.id}</strong> (atual)
                        {vinculos.filhas.length > 0 && " → "}
                        {vinculos.filhas.map((f, i) => <span key={f.id}>{i > 0 && ", "}<strong style={{ color:"#22c55e" }}>#{f.id}</strong></span>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {tab === "docs" && (
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <div style={{ fontSize:13, color:"#555" }}>Anexe comprovantes de transferência e outros documentos.</div>
                <button onClick={handleAnexar} disabled={uploading} style={{ background: uploading ? "#aaa" : (planoInfo && !planoInfo.permite_anexos ? "#ccc" : "#1aae9e"), color:"#fff", border:"none", borderRadius:3, padding:"8px 16px", fontWeight:700, fontSize:13, cursor: uploading ? "wait" : "pointer", fontFamily:FONT }}>
                  {uploading ? "Enviando..." : "+ Anexar Documento"}
                </button>
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.txt" style={{ display:"none" }} onChange={addDoc} />
              </div>
              {planoInfo && !planoInfo.permite_anexos && (
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8, background:"linear-gradient(135deg, #f8f9fa 0%, #eef1f5 100%)", border:"1px solid #dee2e6", marginBottom:12 }}>
                  <div style={{ width:30, height:30, borderRadius:"50%", background:"linear-gradient(135deg, #6c757d, #495057)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ fontSize:13, filter:"brightness(10)" }}>🔒</span>
                  </div>
                  <div style={{ fontSize:12, color:"#495057", lineHeight:1.4 }}>Recurso disponível no plano <strong style={{ color:"#1aae9e" }}>Controla</strong>. Entre em contato para fazer upgrade.</div>
                </div>
              )}
              {docsLoading ? (
                <div style={{ padding:30, textAlign:"center", color:"#aaa" }}>Carregando documentos...</div>
              ) : docs.length === 0 ? (
                <EmptyState icon="📎" title="Nenhum documento anexado" subtitle="Clique em 'Anexar Documento' para adicionar comprovantes" />
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {docs.map(doc => {
                    const tipoBadge = doc.categoria === "foto" ? { label:"📷 Foto", bg:"#e8f5e9", color:"#2e7d32" } : doc.categoria === "assinatura" ? { label:"✍️ Assinatura", bg:"#ede7f6", color:"#5e35b1" } : doc.categoria === "evidencia" ? { label:"📷 Foto", bg:"#e8f5e9", color:"#2e7d32" } : { label:"📄 Documento", bg:"#e3f2fd", color:"#1565c0" };
                    return (
                    <div key={doc.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", border:"1px solid #ebebeb", borderRadius:4, background:"#fafafa" }}>
                      <span style={{ fontSize:24 }}>{doc.tipo?.includes("pdf")?"📕":doc.tipo?.includes("image")?"🖼️":"📄"}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontWeight:700, fontSize:13, color:"#1a1a1a" }}>{doc.nome_original || doc.nome}</span>
                          <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, background:tipoBadge.bg, color:tipoBadge.color }}>{tipoBadge.label}</span>
                        </div>
                        <div style={{ fontSize:11, color:"#aaa", marginTop:2 }}>{doc.tamanho ? ((doc.tamanho/1024).toFixed(1) + " KB") : ""} · {doc.created_at ? new Date(doc.created_at).toLocaleDateString("pt-BR") : ""}{doc.uploaded_by_nome ? " por " + doc.uploaded_by_nome : doc.categoria === "foto" ? " pelo motorista" : doc.categoria === "assinatura" ? " pelo locatário" : ""}</div>
                        {doc.descricao && doc.descricao.includes("Obs:") && <div style={{ fontSize:11, color:"#e67e22", marginTop:2, fontStyle:"italic" }}>💬 {doc.descricao.split("Obs:")[1].trim()}</div>}
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={() => downloadAuth(doc.url, doc.nome_original||doc.nome)} style={{ background:"none", border:"none", cursor:"pointer", color:"#1aae9e", fontSize:12, fontWeight:600 }}>Baixar</button>
                        <button onClick={() => removeDoc(doc.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:700 }}>✕</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "pagto" && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>
                {[
                  ["Valor Total", locacao.valor?.toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) || "—"],
                  ["Forma de Pagamento", locacao.pagamento?.toUpperCase() || "—"],
                  ["Status do Pagamento", locacao.pago ? "PAGO" : "NÃO PAGO"],
                  ["Data do Pagamento", locacao.dataPagamento || locacao.data_pagamento ? fmtDate(locacao.dataPagamento||locacao.data_pagamento) : "—"],
                  ["Prazo (dias)", locacao.prazoPagamento || "—"],
                  ["Vencimento", dataVenc ? dataVenc.toLocaleDateString("pt-BR") : "—"],
                  ["Situação", vencida ? "🔴 VENCIDO" : locacao.pago ? "🟢 QUITADO" : "🟡 EM ABERTO"],
                ].map(([l,v]) => (
                  <div key={l} style={{ padding:"12px 14px", border:"1px solid", borderColor: l==="Situação"&&vencida?"#e74c3c":"#ebebeb", borderRadius:4, background: l==="Situação"&&vencida?"#fde8e8":l==="Situação"&&locacao.pago?"#e8f5e9":"#fafafa" }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", marginBottom:4 }}>{l}</div>
                    <div style={{ fontSize:14, fontWeight:800, color: l==="Situação"&&vencida?"#c0392b":l==="Situação"&&locacao.pago?"#1b5e20":"#333" }}>{v}</div>
                  </div>
                ))}
              </div>
              {vencida && (
                <div style={{ background:"#fde8e8", border:"1px solid #e74c3c", borderRadius:4, padding:"12px 16px", color:"#c0392b", fontWeight:700, fontSize:13 }}>
                  Atenção: O pagamento desta locação está vencido há {Math.floor((new Date()-dataVenc)/(1000*60*60*24))} dia(s). O cliente está devendo R$ {locacao.valor?.toLocaleString("pt-BR",{minimumFractionDigits:2})}.
                </div>
              )}
            </div>
          )}

          {tab === "historico" && (
            <div>
              {histLoading ? (
                <div style={{ textAlign:"center", padding:30, color:"#aaa", fontSize:13 }}>Carregando histórico...</div>
              ) : historico.length === 0 ? (
                <EmptyState icon="🕐" title="Nenhuma atividade registrada" subtitle="O histórico de alterações aparecerá aqui" />
              ) : (
                <div style={{ position:"relative", paddingLeft:24 }}>
                  <div style={{ position:"absolute", left:8, top:0, bottom:0, width:2, background:"#e8e8e8" }} />
                  {historico.map(h => (
                    <div key={h.id} style={{ position:"relative", marginBottom:18, paddingLeft:16 }}>
                      <div style={{ position:"absolute", left:-20, top:4, width:12, height:12, borderRadius:"50%", background: h.acao?.includes("Criação")?"#1aae9e": h.acao?.includes("Status")?"#2ecc71": h.acao?.includes("Edição")?"#f39c12":"#888", border:"2px solid #fff", boxShadow:"0 0 0 2px #e8e8e8" }} />
                      <div style={{ fontSize:12, fontWeight:700, color:"#1a1a1a" }}>{h.acao}</div>
                      <div style={{ fontSize:12, color:"#555", marginTop:2 }}>{h.detalhes||""}</div>
                      <div style={{ fontSize:11, color:"#aaa", marginTop:4 }}>
                        {h.usuario} · {new Date(h.created_at).toLocaleString("pt-BR")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding:"12px 22px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"#f4f4f4", color:"#555", border:"1px solid #e0e0e0", borderRadius:3, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:FONT }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── PDF Receipt ──────────────────────────────────────────────────────────────
function printRecibo(l) {
  const addr = l.logradouro ? [l.logradouro, l.numero, l.bairro, l.cidade, l.uf].filter(Boolean).join(", ") : (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "—");
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Recibo #${l.id}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 24px; color: #222; font-size: 13px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 3px solid #1aae9e; }
  .logo { font-size: 22px; font-weight: 900; color: #1aae9e; }
  .logo small { display: block; font-size: 11px; color: #888; font-weight: 400; }
  .title { font-size: 18px; font-weight: 900; color: #1a1a1a; margin-bottom: 16px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
  .field { padding: 8px 12px; border: 1px solid #e8e8e8; border-radius: 4px; background: #fafafa; }
  .field label { font-size: 9px; font-weight: 700; color: #aaa; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 3px; }
  .field span { font-size: 13px; font-weight: 700; color: #1a1a1a; }
  .total-row { display: flex; justify-content: space-between; background: #1aae9e; color: #fff; border-radius: 4px; padding: 12px 18px; font-size: 15px; font-weight: 900; margin-top: 16px; }
  .footer { margin-top: 20px; font-size: 11px; color: #aaa; text-align: center; border-top: 1px solid #e8e8e8; padding-top: 10px; }
  .status-badge { display: inline-block; padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: 700; background: ${l.pago?"#e8f5e9":"#fde8e8"}; color: ${l.pago?"#1b5e20":"#c0392b"}; }
  @media print { body { margin: 0; } }
</style></head><body>
<div class="header">
  <div class="logo">Controla CAÇAMBA<small>Controla Caçambas</small></div>
  <div style="text-align:right"><div style="font-size:12px;color:#888">RECIBO DE LOCAÇÃO</div><div style="font-size:20px;font-weight:900;color:#1aae9e">#${l.id}</div></div>
</div>
<div class="title">Recibo de Locação de Caçamba</div>
<div class="grid">
  <div class="field"><label>Cliente</label><span>${l.cliente||"—"}</span></div>
  <div class="field"><label>Caçamba</label><span>${l.cacamba||"—"} ${l.tipoCacamba?"("+l.tipoCacamba+")":""}</span></div>
  <div class="field"><label>Endereço de Entrega</label><span>${addr}</span></div>
  <div class="field"><label>MTR/CTR</label><span>${l.mtr||"—"}</span></div>
  <div class="field"><label>Data de Entrega</label><span>${fmtDate(l.dataEntrega)}</span></div>
  <div class="field"><label>Data de Retirada</label><span>${fmtDate(l.dataRetiraPrevista||l.dataRetirada)}</span></div>
  <div class="field"><label>Motorista</label><span>${l.motorista||"—"}</span></div>
  <div class="field"><label>Forma de Pagamento</label><span>${(l.pagamento||"—").toUpperCase()}</span></div>
  <div class="field"><label>Status do Pagamento</label><span class="status-badge">${l.pago?"PAGO":"NÃO PAGO"}</span></div>
  <div class="field"><label>Status da Locação</label><span>${l.status||"—"}</span></div>
</div>
${l.observacoes?`<div class="field" style="margin-bottom:10px"><label>Observações</label><span>${l.observacoes}</span></div>`:""}
<div class="total-row"><span>VALOR TOTAL</span><span>R$ ${(l.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</span></div>
<div class="footer">Controla CAÇAMBA · Controla Caçambas · Gerado em ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR")}</div>
</body></html>`;
  const win = window.open("","_blank","width=680,height=800");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 500);
}

// ─── Gerador de MTR/CTR PDF (Modelo com Logo - 3 páginas A4) ────────────────────
async function gerarMtrPDF(locacao, empresaCfg, cacambas, tiposCacamba) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210, ph = 297, mg = 18;
  const fw = pw - 2*mg;
  const emp = empresaCfg || {};
  const fmtD = (d) => { if(!d) return ""; const s=String(d); const p=s.includes("T")?s.split("T")[0]:s; if(p.length!==10) return p; const [y,mn,dd]=p.split("-"); return dd+"/"+mn+"/"+y; };
  // Buscar volume/cubicagem: tipo_cacamba → tiposCacamba.cubicagem
  const tipoNome = locacao.tipoCacamba || locacao.tipo_cacamba || "";
  const tipoObj = (tiposCacamba||[]).find(t => t.nome === tipoNome);
  const cubicagem = tipoObj ? (tipoObj.cubicagem || "") : "";
  // Formatar cubicagem: "4 m³" ou "26m3" → "4 m³"
  const volumeCac = cubicagem ? cubicagem.replace(/m3/gi,"").trim() + " m³" : tipoNome;

  // Load logo (com autenticação)
  const API = process.env.REACT_APP_API || "http://localhost:3001";
  const token = sessionStorage.getItem("controla_token");
  let logoImg = null;
  if (emp.logo_url) {
    try {
      let logoUrl = emp.logo_url;
      if (!logoUrl.startsWith("http")) {
        // logo_url pode ser "tenant_1/logo.png" ou "/api/files/tenant_1/logo.png"
        if (!logoUrl.startsWith("/api/files/")) logoUrl = "/api/files/" + logoUrl;
        logoUrl = API + logoUrl;
      }
      console.log("[MTR] Buscando logo:", logoUrl);
      const r = await fetch(logoUrl, { headers: { Authorization: "Bearer " + token } });
      if (r.ok) {
        const b = await r.blob();
        logoImg = await new Promise(resolve => { const rd = new FileReader(); rd.onloadend = () => resolve(rd.result); rd.readAsDataURL(b); });
        console.log("[MTR] Logo carregada OK, tipo:", b.type, "tamanho:", b.size);
      } else {
        console.warn("[MTR] Logo fetch falhou:", r.status, r.statusText);
      }
    } catch(e){ console.warn("[MTR] Logo não carregada:", e.message); }
  }

  function drawPage(viaNum) {
    let cy = mg;
    const lx = mg;
    const rh = 7; // reduzido de 8 para 7 para caber tudo na página
    const sp = 2; // espaço entre seções (era 3)
    const maxY = ph - mg; // limite inferior da página

    // ═══════════ HEADER ═══════════
    const hdrH = 26;
    doc.setDrawColor(0); doc.setLineWidth(0.8);
    doc.rect(lx, cy, fw, hdrH);

    // Logo area (left)
    const logoW = 30;
    doc.setLineWidth(0.4);
    doc.rect(lx, cy, logoW, hdrH);
    if (logoImg) {
      try { doc.addImage(logoImg, "AUTO", lx + 2, cy + 2, logoW - 4, hdrH - 4); } catch(e){ console.warn("[MTR] Erro ao inserir logo:", e.message); }
    } else {
      doc.setFontSize(10); doc.setFont("helvetica","bold"); doc.setTextColor(120);
      doc.text((emp.nome||"CC").substring(0,4).toUpperCase(), lx + logoW/2, cy + hdrH/2 + 3, {align:"center"});
      doc.setTextColor(0);
    }

    // Company name + subtitle (center area)
    const nboxW = 26;
    const centerW = fw - logoW - nboxW;
    const centerX = lx + logoW + centerW / 2;
    doc.setFontSize(13); doc.setFont("helvetica","bold"); doc.setTextColor(0);
    doc.text(emp.nome || "CONTROLA CAÇAMBAS", centerX, cy + 8, {align:"center"});
    doc.setFontSize(7); doc.setFont("helvetica","normal");
    doc.text("Locação de Caçambas", centerX, cy + 13, {align:"center"});
    doc.setFontSize(9); doc.setFont("helvetica","bold");
    doc.text("MANIFESTO DE TRANSPORTE DE RESÍDUOS", centerX, cy + 20, {align:"center"});

    // Nº DA CTR box (right)
    const nboxX = lx + fw - nboxW;
    doc.setLineWidth(0.8);
    doc.rect(nboxX, cy, nboxW, hdrH);
    doc.setFontSize(7); doc.setFont("helvetica","bold");
    doc.text("Nº DA CTR", nboxX + nboxW/2, cy + 6, {align:"center"});
    doc.setFontSize(9); doc.setFont("helvetica","bold"); doc.setTextColor(0,100,80);
    doc.text(String(locacao.id || locacao.mtr || ""), nboxX + nboxW/2, cy + 13, {align:"center"});
    doc.setTextColor(0);
    doc.setFontSize(8); doc.setFont("helvetica","normal");
    doc.text("(" + viaNum + "ª via)", nboxX + nboxW/2, cy + 19, {align:"center"});
    cy += hdrH + sp;

    // ═══ SECTION HEADER ═══
    function secHdr(text) {
      const sh = 6;
      doc.setDrawColor(0); doc.setLineWidth(0.8);
      doc.rect(lx, cy, fw, sh);
      doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(0);
      doc.text(text, lx + 2, cy + 4.5);
      cy += sh;
    }

    // ═══ FIELD ROW ═══
    function row(fields) {
      let cx = lx;
      fields.forEach(([label, frac]) => {
        const w = fw * frac;
        doc.setDrawColor(0); doc.setLineWidth(0.4);
        doc.rect(cx, cy, w, rh);
        doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(0);
        doc.text(label, cx + 2, cy + 5);
        cx += w;
      });
      cy += rh;
    }

    // ═══════════ TRANSPORTADOR ═══════════
    secHdr("DADOS DO TRANSPORTADOR");
    row([["Nome da Empresa: " + (emp.nome||""), 0.6], ["CNPJ: " + (emp.cnpj||""), 0.4]]);
    const empEnd = (emp.endereco||"") + (emp.bairro?" - "+emp.bairro:"") + (emp.cidade?" - "+emp.cidade:"") + (emp.uf?"/"+emp.uf:"") + (emp.cep?" - CEP: "+emp.cep:"");
    row([["Endereço: " + empEnd, 1.0]]);
    row([["Telefone: " + (emp.telefone||""), 0.4], ["E-mail: " + (emp.email||""), 0.6]]);
    cy += sp;

    // ═══════════ GERADOR ═══════════
    secHdr("DADOS DO GERADOR");
    row([["Nome: " + (locacao.cliente||locacao.cliente_nome||""), 0.65], ["Fone: " + (locacao.contatoTel||locacao.contato_tel||""), 0.35]]);
    row([["CPF / CNPJ: " + (locacao.cpfCnpj||locacao.cpf_cnpj||""), 0.5], ["Contato: " + (locacao.contatoNome||locacao.contato_nome||""), 0.5]]);
    row([["Endereço da Obra: " + [locacao.logradouro||locacao.end_logradouro||"", locacao.numero||locacao.end_numero||"", locacao.complemento||locacao.end_complemento||(locacao.endereco?.complemento)||"", locacao.bairro||locacao.end_bairro||"", locacao.cidade||locacao.end_cidade||""].filter(Boolean).join(", "), 1.0]]);
    cy += sp;

    // ═══════════ COLETA ═══════════
    secHdr("DADOS DA COLETA");
    row([["Data da Colocação: " + fmtD(locacao.dataEntrega||locacao.data_entrega), 0.5], ["Data da Retirada: " + fmtD(locacao.dataRetiraPrevista||locacao.data_retirada_prevista||locacao.dataRetirada||locacao.data_retirada), 0.5]]);
    const locEnd = locacao.logradouro || locacao.end_logradouro || (locacao.endereco?.logradouro) || "";
    const locNum = locacao.numero || locacao.end_numero || (locacao.endereco?.numero) || "";
    const locBairro = locacao.bairro || locacao.end_bairro || (locacao.endereco?.bairro) || "";
    const locCidade = locacao.cidade || locacao.end_cidade || (locacao.endereco?.cidade) || "";
    const locUf = locacao.uf || locacao.end_uf || (locacao.endereco?.uf) || "";
    const locCep = locacao.cep || locacao.end_cep || (locacao.endereco?.cep) || "";
    row([["Endereço: " + locEnd, 1.0]]);
    const locCompl = locacao.complemento || locacao.end_complemento || (locacao.endereco?.complemento) || "";
    row([["Número: " + locNum, 0.25], ["Complemento: " + locCompl, 0.4], ["Bairro: " + locBairro, 0.35]]);
    row([["Município: " + locCidade, 0.4], ["UF: " + locUf, 0.2], ["CEP: " + locCep, 0.4]]);
    row([["Caminhão:", 0.33], ["Nº Caçamba: " + (locacao.cacamba||locacao.cacamba_codigo||""), 0.33], ["Volume: " + volumeCac, 0.34]]);
    cy += sp;

    // ═══════════ RESÍDUOS ═══════════
    secHdr("DESCRIÇÃO DOS RESÍDUOS");
    const res = locacao.residuos || {};
    const resLines = [];
    if (res.classeA?.length) resLines.push("Classe A: " + res.classeA.join(", "));
    if (res.classeB?.length) resLines.push("Classe B: " + res.classeB.join(", "));
    if (res.classeC?.length) resLines.push("Classe C: " + res.classeC.join(", "));
    if (res.classeD?.length) resLines.push("Classe D: " + res.classeD.join(", "));
    const resText = resLines.length ? resLines.join(" | ") : "";
    const outrosText = res.outros || "";
    const descH = Math.max(16, (resLines.length > 2 ? 22 : 16));
    doc.setDrawColor(0); doc.setLineWidth(0.4);
    doc.rect(lx, cy, fw, descH);
    if (resText) {
      doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(0);
      const splitRes = doc.splitTextToSize(resText, fw - 6);
      doc.text(splitRes, lx + 2, cy + 4);
    }
    doc.setFontSize(7); doc.text("OUTROS: " + outrosText, lx + 2, cy + descH - 2);
    cy += descH + sp;

    // ═══════════ DESTINO FINAL ═══════════
    secHdr("DESTINO FINAL");
    row([["Nome:", 1.0]]);
    const half = fw * 0.5;
    ["Autorização Ambiental Nº:", "Órgão Expedidor:", "Validade:"].forEach(label => {
      doc.setDrawColor(0); doc.setLineWidth(0.4);
      doc.rect(lx, cy, half, rh); doc.rect(lx+half, cy, half, rh);
      doc.setFontSize(7); doc.text(label, lx + half + 2, cy + 5);
      cy += rh;
    });
    cy += sp;

    // ═══════════ CONDIÇÕES ═══════════
    secHdr("CONDIÇÕES DO ALUGUEL");
    const condTxt = emp.condicoes_mtr || emp.condicoes_aluguel || "LOCATÁRIA recebe o(s) equipamento(s) referido(s) no presente instrumento, declarando tê-los testado(s) e aproveitado(s) previamente e afirmando que o(s) mesmo(s) se acha(m) em perfeito estado de funcionamento, limpeza e segurança. Declara mais, que entende detalhadamente sua correta utilização, pelo que se obriga a devolvê-lo(s) em idênticas condições de funcionamento, limpeza e segurança no final desta locação ou na hipótese de rescisão do presente contrato. Declara ainda que fará uso de todos os equipamentos de segurança, e se compromete a repassar essas informações a quem for utilizar o(s) mesmo(s).";
    doc.setFontSize(6.5);
    const condLines = doc.splitTextToSize(condTxt, fw - 6);
    // Limitar altura das condições para garantir espaço para assinaturas
    const sigMinH = 40; // altura mínima para assinaturas
    const maxCondH = maxY - cy - 6 - sp - sigMinH; // espaço máximo para condições (6=secHdr)
    const condH = Math.min(Math.max(condLines.length * 3 + 3, 14), Math.max(maxCondH, 14));
    doc.setDrawColor(0); doc.setLineWidth(0.4);
    doc.rect(lx, cy, fw, condH);
    doc.text(condLines, lx + 3, cy + 3.5);
    cy += condH + sp;

    // ═══════════ ASSINATURAS/CARIMBOS ═══════════
    // Garantir que a caixa nunca ultrapassa a margem inferior
    const sigBoxH = Math.max(maxY - cy, sigMinH);
    // Se vai ultrapassar a página, limitar ao espaço disponível
    const actualSigH = Math.min(sigBoxH, maxY - cy);
    doc.setDrawColor(0); doc.setLineWidth(0.8);
    doc.rect(lx, cy, fw, actualSigH);

    // Title
    doc.setFontSize(10); doc.setFont("helvetica","bold");
    doc.text("ASSINATURAS/CARIMBOS", lx + fw/2, cy + 7, {align:"center"});

    // 3 signature columns
    const sigW = fw / 3;
    const lineY = cy + actualSigH - 10;
    ["Gerador (1ª via)", "Transportador (2ª via)", "Destino Final (3ª via)"].forEach((label, i) => {
      const sx = lx + i * sigW;
      if (i > 0) { doc.setLineWidth(0.3); doc.line(sx, cy + 12, sx, cy + actualSigH - 3); }
      const lineX1 = sx + sigW * 0.08;
      const lineX2 = sx + sigW * 0.92;
      doc.setLineWidth(0.6);
      doc.line(lineX1, lineY, lineX2, lineY);
      doc.setFontSize(7); doc.setFont("helvetica","normal");
      doc.text(label, sx + sigW/2, lineY + 4, {align:"center"});
    });
  }

  // 3 pages = 3 vias
  for (let i = 1; i <= 3; i++) {
    if (i > 1) doc.addPage();
    drawPage(i);
  }

  doc.save("MTR_CTR_Locacao_" + (locacao.id || "novo") + ".pdf");
}

// ─── Locação Row Menu ──────────────────────────────────────────────────────────
function LocacaoRowMenu({ locacao: l, onView, onEdit, onEditChild, onStatusChange, onTimeline, onCancel, onRecibo, onReciboChild, onMtr, onMtrChild, onProrrogar, onEnviarAssinatura, empresaCfg, empresaNome, locacoes: allLocs }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top:"auto", right:"auto" });
  const menuRef = useRef();
  const btnRef  = useRef();

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const rightVal = (window.innerWidth - r.right) + "px";
      // Estimar altura do menu (~320px) e verificar se cabe abaixo
      const menuHeight = 320;
      const spaceBelow = window.innerHeight - r.bottom;
      if (spaceBelow < menuHeight && r.top > menuHeight) {
        // Abrir pra cima
        setMenuPos({ bottom: (window.innerHeight - r.top + 4) + "px", top: "auto", right: rightVal });
      } else {
        setMenuPos({ top: r.bottom + 4 + "px", bottom: "auto", right: rightVal });
      }
    }
  }, [open]);

  const STATUS_ACTIONS = {
    "Para Colocar":  [{ label:"Confirmar Colocada",   key:"colocada",       emoji:"✅" }],
    "Colocada":      [{ label:"Programar Retirada",   key:"para_retirar",   emoji:"📤" }, { label:"Programar Troca", key:"para_trocar", emoji:"🔃" }],
    "Para Retirar":  [{ label:"Confirmar Retirada",   key:"encerrada",      emoji:"✅" }, { label:"Programar Troca", key:"para_trocar", emoji:"🔃" }, { label:"Voltar para Colocada", key:"voltar_colocada", emoji:"↩️" }],
    "Para Trocar":   [{ label:"Confirmar Troca(s)",   key:"confirmar_troca", emoji:"✅" }, { label:"Voltar para Colocada", key:"voltar_colocada", emoji:"↩️" }],
    "Para Cobrar":   [{ label:"Confirmar Pagamento",  key:"pago",           emoji:"💰" }],
    "Encerrada":     [{ label:"Voltar para Para Cobrar", key:"voltar_cobrar", emoji:"↩️" }],
    "Cancelada":     [],
  };

  const actions = STATUS_ACTIONS[l.status] || [];
  const menuBtn = (label, onClick, color, emoji) => (
    <button onClick={onClick}
      style={{ width:"100%", textAlign:"left", padding:"9px 14px", background:"none", border:"none", cursor:"pointer", fontSize:13, color:color||"#333", fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}
      onMouseEnter={e => e.currentTarget.style.background="#f5f8ff"}
      onMouseLeave={e => e.currentTarget.style.background="none"}>
      {emoji && <span style={{ fontSize:14 }}>{emoji}</span>}{label}
    </button>
  );

  return (
    <div ref={menuRef} style={{ position:"relative", display:"flex", gap:4, alignItems:"center" }}>
      <button ref={btnRef} onClick={() => setOpen(o => !o)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 10px", cursor:"pointer", color:"#555", fontSize:16, fontWeight:800, lineHeight:1 }}>
        ☰
      </button>
      {open && (
        <div style={{ position:"fixed", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, boxShadow:"0 6px 28px rgba(0,0,0,.18)", zIndex:9999, minWidth:220, overflow:"hidden", maxHeight:"80vh", overflowY:"auto", top:menuPos.top, bottom:menuPos.bottom, right:menuPos.right }}>
          {actions.length > 0 && <>
            <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.5 }}>Operações</div>
            {actions.map(a => menuBtn(a.label, () => { onStatusChange(a.key); setOpen(false); }, null, a.emoji))}
          </>}
          {l.status === "Cancelada" && <>
            <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#1a9e8f", textTransform:"uppercase", letterSpacing:.5 }}>Reativar como</div>
            {["Para Colocar","Colocada","Para Cobrar","Para Trocar"].map(st =>
              menuBtn(st, () => { onStatusChange("reativar:" + st); setOpen(false); }, "#1a9e8f", "↩️")
            )}
          </>}
          {l.status === "Para Trocar" && (() => {
            const childLoc = (allLocs||[]).find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + l.id) || cl.obs.toLowerCase().includes("troca da locacao #" + l.id) || cl.obs.toLowerCase().includes("troca da locação " + l.id) || cl.obs.toLowerCase().includes("troca da locacao " + l.id)));
            return <>
              <div style={{ borderTop:"1px solid #f0f0f0" }}>
                <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#e74c3c", textTransform:"uppercase", letterSpacing:.5 }}>Para Trocar</div>
                {menuBtn("Editar", () => { onEdit(); setOpen(false); }, null, "✏️")}
                {menuBtn("Imprimir MTR/CTR", () => { if(onMtr) onMtr(); setOpen(false); }, "#0d9488", "📄")}
                {menuBtn("Imprimir Recibo", () => { if(onRecibo) onRecibo(); setOpen(false); }, "#1aae9e", "🧾")}
              </div>
              <div style={{ borderTop:"1px solid #f0f0f0" }}>
                <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#2ecc71", textTransform:"uppercase", letterSpacing:.5 }}>Para Colocar</div>
                {menuBtn("Editar", () => { if(childLoc && onEditChild) onEditChild(childLoc); else { alert("Locação de troca não encontrada."); } setOpen(false); }, null, "✏️")}
                {menuBtn("Imprimir MTR/CTR", () => { if(childLoc && onMtrChild) onMtrChild(childLoc); else if(onMtr) onMtr(); setOpen(false); }, "#0d9488", "📄")}
                {menuBtn("Imprimir Recibo", () => { if(childLoc && onReciboChild) onReciboChild(childLoc); else if(onRecibo) onRecibo(); setOpen(false); }, "#1aae9e", "🧾")}
              </div>
            </>;
          })()}
          {l.status !== "Para Trocar" && <div style={{ borderTop:"1px solid #f0f0f0" }}>
            <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.5 }}>Documentos</div>
            {menuBtn("Gerar Recibo PDF", () => { if(onRecibo) onRecibo(); setOpen(false); }, "#1aae9e", "🧾")}
            {menuBtn("Gerar MTR/CTR", () => { if(onMtr) onMtr(); setOpen(false); }, "#0d9488", "📄")}
          </div>}
          <div style={{ borderTop:"1px solid #f0f0f0" }}>
            <div style={{ padding:"6px 14px 3px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.5 }}>Opções</div>
            {l.status !== "Para Trocar" && menuBtn("Editar", () => { onEdit(); setOpen(false); }, null, "✏️")}
            {onProrrogar && ["Colocada","Para Retirar","Para Trocar"].includes(l.status) && menuBtn("Prorrogar", () => { onProrrogar(); setOpen(false); }, "#1a9e8f", "📅")}
            {menuBtn("Linha do Tempo", () => { onTimeline(); setOpen(false); }, null, "🕐")}
            {menuBtn("Enviar Status ao Cliente", () => {
              const tel = (l.contatoTel || l.contato_tel || "").replace(/\D/g, "");
              if (!tel) { alert("Cliente sem telefone cadastrado nesta locação.\n\nEdite a locação e preencha o telefone."); setOpen(false); return; }
              const phone = tel.length <= 11 ? "55" + tel : tel;
              const nome = (l.cliente || l.cliente_nome || "").split(" ")[0];
              const endereco = [l.logradouro, l.numero, l.bairro, l.cidade, l.uf].filter(Boolean).join(", ") || (l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.bairro,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "");
              const statusTitles = { "Para Colocar":"*Sua ca\u00e7amba j\u00e1 est\u00e1 a caminho!*", "Colocada":"*Sua ca\u00e7amba foi entregue com sucesso!*", "Para Retirar":"*Retirada da ca\u00e7amba programada!*", "Para Trocar":"*Troca da ca\u00e7amba programada!*", "Encerrada":"*Loca\u00e7\u00e3o encerrada com sucesso!*", "Para Cobrar":"*Aten\u00e7\u00e3o sobre sua loca\u00e7\u00e3o!*" };
              const titulo = statusTitles[l.status] || "*Atualiza\u00e7\u00e3o da sua ca\u00e7amba!*";
              const msg = titulo + "\n\nOla, " + nome + "! Passando para avisar que o status da sua cacamba foi atualizado para: *" + (l.status||"") + "*\nEndereco: " + endereco + "\n\nQualquer duvida ou ajuste, e so chamar aqui!\nObrigado por escolher nossos servicos!\n\n_" + (empresaNome || "Controla Caçambas") + "_";
              window.open("https://wa.me/" + phone + "?text=" + encodeURIComponent(msg), "_blank");
              setOpen(false);
            }, "#25D366", "💬")}
            {!l.pago && menuBtn("Cobrar via WhatsApp", () => {
              const tel = (l.contatoTel || l.contato_tel || "").replace(/\D/g, "");
              if (!tel) { alert("Cliente sem telefone cadastrado nesta locação.\n\nEdite a locação e preencha o telefone."); setOpen(false); return; }
              const phone = tel.length <= 11 ? "55" + tel : tel;
              const nome = (l.cliente || l.cliente_nome || "").split(" ")[0];
              const valor = l.valor ? parseFloat(l.valor).toLocaleString("pt-BR",{style:"currency",currency:"BRL"}) : "";
              const chavePix = empresaCfg?.chave_pix || "";
              let msg = "*COBRANCA - LOCACAO DE CACAMBA*\n\nOla, " + nome + "!\n\nEstamos entrando em contato para informar sobre o pagamento referente a sua locacao de cacamba.\n\n*Detalhes:*\nLocacao: #" + (l.id||"") + (valor ? "\nValor: *" + valor + "*" : "") + "\n\n";
              if (chavePix) { msg += "*Dados para pagamento via PIX:*\nChave: *" + chavePix + "*\n\n"; }
              msg += "Apos o pagamento, envie o comprovante aqui nesta conversa!\n\nQualquer duvida, estamos a disposicao!\n\n_Atenciosamente, " + (empresaNome || "Controla Caçambas") + "_";
              window.open("https://wa.me/" + phone + "?text=" + encodeURIComponent(msg), "_blank");
              setOpen(false);
            }, "#e74c3c", "💸")}
            {menuBtn("Enviar WhatsApp Personalizado", () => {
              const tel = (l.contatoTel || l.contato_tel || "").replace(/\D/g, "");
              if (!tel) { alert("Cliente sem telefone cadastrado nesta locação.\n\nEdite a locação e preencha o telefone."); setOpen(false); return; }
              if (typeof window.__openWhatsAppCustom === "function") window.__openWhatsAppCustom(l);
              setOpen(false);
            }, "#25D366", "📝")}
            {onEnviarAssinatura && l.status !== "Cancelada" && menuBtn("Enviar link de assinatura", () => { onEnviarAssinatura(); setOpen(false); }, "#0d9488", "✍️")}
            {l.status !== "Cancelada" && l.status !== "Para Retirar" && l.status !== "Para Trocar" && l.status !== "Encerrada" && menuBtn("✕ Cancelar", () => { onCancel(); setOpen(false); }, "#e74c3c", "❌")}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Alterar Pagamento Modal ──────────────────────────────────────────────────
function AlterarPagamentoModal({ locacao, onClose, onConfirm }) {
  const [motivo, setMotivo] = useState("");
  return (
    <LargeModal title="Alterar Status de Pagamento" subtitle={"Locação #" + (locacao?.id||"")} onClose={onClose} onSave={() => {
      if (!motivo.trim()) { alert("Informe o motivo da alteração."); return; }
      onConfirm(motivo);
    }} saveLabel="Confirmar Alteração" maxWidth={500}>
      <SectionBody>
        <div style={{ background:"#fff3cd", border:"1px solid #ffc107", borderRadius:4, padding:12, marginBottom:14, fontSize:12, color:"#856404" }}>
          Ao marcar como <b>NÃO PAGO</b>, esta locação será movida para <b>"Para Cobrar"</b>.
        </div>
        <Field label="Motivo da Alteração *">
          <textarea style={{...inp, minHeight:60, resize:"vertical"}} value={motivo} onChange={e=>setMotivo(e.target.value)} placeholder="Ex: Cliente solicitou estorno, pagamento não confirmado..." />
        </Field>
      </SectionBody>
    </LargeModal>
  );
}

// ─── Prorrogar Locação Modal ──────────────────────────────────────────────────
function ProrrogarModal({ locacao: l, apiFetch, onClose, onSaved }) {
  const dataAtual = String(l.dataRetiraPrevista || l.data_retirada || l.dataRetirada || "").slice(0, 10);
  const diaria = parseFloat(l.diariaExtra != null ? l.diariaExtra : l.diaria_extra) || 0;
  const [novaData, setNovaData] = useState("");
  const [valorAdicional, setValorAdicional] = useState("");
  const [valorEditado, setValorEditado] = useState(false);
  const [obs, setObs] = useState("");
  const [salvando, setSalvando] = useState(false);

  const validData = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s||""));
  const addDias = (base, n) => {
    const b = validData(base) ? base : hojeLocal();
    const d = new Date(b + "T12:00:00");
    d.setDate(d.getDate() + n);
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  };
  const diasAdicionais = (nd) => {
    const base = validData(dataAtual) ? dataAtual : hojeLocal();
    if (!validData(nd)) return 0;
    // Split manual + Date.UTC: diff exato em dias, imune a timezone/DST
    const pb = base.split("-").map(Number), pn = nd.split("-").map(Number);
    const diff = Math.round((Date.UTC(pn[0], pn[1] - 1, pn[2]) - Date.UTC(pb[0], pb[1] - 1, pb[2])) / 86400000);
    return diff > 0 ? diff : 0;
  };
  // Última sugestão automática aplicada ao campo — se o campo ainda contém essa
  // sugestão (ou está vazio), qualquer mudança de data (atalhos +7/+15 OU input
  // livre) recalcula dias × diária. Só preserva valor realmente digitado à mão.
  const ultimaSugestaoRef = useRef("");
  const setData = (nd) => {
    setNovaData(nd);
    const atual = String(valorAdicional);
    if (!valorEditado || atual === "" || atual === ultimaSugestaoRef.current) {
      const dias = diasAdicionais(nd);
      const sugestao = dias > 0 && diaria > 0 ? String((dias * diaria).toFixed(2)) : "";
      ultimaSugestaoRef.current = sugestao;
      setValorAdicional(sugestao);
      if (valorEditado) setValorEditado(false);
    }
  };
  const fmtBRData = (iso) => { const p = String(iso||"").slice(0,10).split("-"); return p.length === 3 ? p[2]+"/"+p[1]+"/"+p[0] : "—"; };
  const btnAtalho = { padding:"7px 14px", background:"none", border:"1px solid #1a9e8f", borderRadius:4, color:"#1a9e8f", fontSize:12, cursor:"pointer", fontWeight:700, fontFamily:FONT };

  const salvar = async () => {
    if (salvando) return;
    if (!validData(novaData)) { alert("Informe a nova data de retirada."); return; }
    if (validData(dataAtual) && novaData <= dataAtual) { alert("A nova data deve ser posterior à retirada prevista atual (" + fmtBRData(dataAtual) + ")."); return; }
    setSalvando(true);
    try {
      const upd = await apiFetch("/api/locacoes/" + l.id + "/prorrogar", {
        method: "POST",
        body: { nova_data: novaData, valor_adicional: parseFloat(valorAdicional) || 0, obs: obs || "" }
      });
      if (onSaved) onSaved(upd);
      onClose();
    } catch (e) { alert("Erro ao prorrogar: " + e.message); }
    finally { setSalvando(false); }
  };

  const dias = diasAdicionais(novaData);
  return (
    <LargeModal title={"Prorrogar Locação #" + l.id} subtitle={l.cliente || l.cliente_nome || ""} onClose={onClose} onSave={salvar} saveLabel={salvando ? "Salvando..." : "✓ Prorrogar"} maxWidth={540}>
      <SectionBody>
        <div style={{ padding:"10px 14px", background:"#f0faf9", border:"1px solid #c9ebe7", borderRadius:4, fontSize:13, color:"#0e5e55", marginBottom:14 }}>
          Retirada prevista atual: <strong>{validData(dataAtual) ? fmtBRData(dataAtual) : "não definida"}</strong>
          {diaria > 0 && <span style={{ marginLeft:8, fontSize:11, color:"#4a8a80" }}>· Diária extra: {fmt(diaria)}/dia</span>}
        </div>
        <div style={{ display:"flex", gap:8, marginBottom:12 }}>
          <button type="button" onClick={() => setData(addDias(dataAtual, 7))} style={btnAtalho}>+7 dias</button>
          <button type="button" onClick={() => setData(addDias(dataAtual, 15))} style={btnAtalho}>+15 dias</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <Field label="Nova data de retirada *">
            <input type="date" style={inp} min={validData(dataAtual) ? dataAtual : undefined} value={novaData} onChange={e => setData(e.target.value)} />
          </Field>
          <Field label="Valor adicional (R$)">
            <input type="number" min="0" step="0.01" style={{ ...inp, textAlign:"right" }} value={valorAdicional} onChange={e => { const v = e.target.value; setValorEditado(v !== "" && v !== ultimaSugestaoRef.current); setValorAdicional(v); }} placeholder="0,00" />
          </Field>
        </div>
        {dias > 0 && (
          <div style={{ fontSize:11, color:"#888", marginBottom:12 }}>
            {dias} dia(s) adicionais{diaria > 0 ? " × " + fmt(diaria) + " = " + fmt(dias * diaria) + " (sugerido — pode ajustar)" : ""}
          </div>
        )}
        <Field label="Observação (opcional)">
          <textarea style={{ ...inp, minHeight:56, resize:"vertical" }} value={obs} onChange={e => setObs(e.target.value)} placeholder="Motivo da prorrogação..." />
        </Field>
      </SectionBody>
    </LargeModal>
  );
}

// ─── Locações ─────────────────────────────────────────────────────────────────
// ─── Tutorial Locações Modal (exibe apenas 1 vez) ────────────────────────────
function TutorialLocacoesModal({ onClose }) {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: "Bem-vindo às Locações!",
      icon: "🚛",
      content: (
        <div>
          <p style={{ fontSize:14, color:"#475569", lineHeight:1.7, marginBottom:14 }}>Aqui é onde você gerencia todas as suas locações de caçambas. Antes de criar a primeira locação, certifique-se de ter cadastrado:</p>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { icon:"👤", label:"Clientes", desc:"Cadastre seus clientes em Clientes no menu lateral" },
              { icon:"🚚", label:"Motoristas", desc:"Adicione os motoristas da sua equipe" },
              { icon:"📦", label:"Tipos de Caçamba", desc:"Defina os tipos (3m³, 5m³, etc.) e valores" },
              { icon:"🏗️", label:"Caçambas", desc:"Numere suas caçambas para controle individual" },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, background:"#f8fffe", border:"1px solid #e0f2ef", borderRadius:8, padding:"10px 14px" }}>
                <span style={{ fontSize:22 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:"#1E2330" }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Como criar uma locação",
      icon: "📋",
      content: (
        <div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[
              { n:"1", text:"Clique no botão \"+ Nova Locação\" no canto superior" },
              { n:"2", text:"Selecione o cliente (ou cadastre um novo na hora)" },
              { n:"3", text:"Informe o endereço de entrega da caçamba" },
              { n:"4", text:"Escolha o tipo de caçamba e defina o valor" },
              { n:"5", text:"Selecione a classificação dos resíduos (A, B, C ou D)" },
              { n:"6", text:"Confirme — a locação será criada com status \"Para Colocar\"" },
            ].map((s, i) => (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <div style={{ minWidth:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, flexShrink:0 }}>{s.n}</div>
                <p style={{ fontSize:13, color:"#475569", margin:0, paddingTop:4, lineHeight:1.5 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Ciclo de vida da locação",
      icon: "🔄",
      content: (
        <div>
          <p style={{ fontSize:13, color:"#475569", lineHeight:1.6, marginBottom:14 }}>Cada locação passa por etapas. Acompanhe o status pelas cores:</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              { status:"Para Colocar", color:"#1a9e8f", desc:"Locação criada, aguardando entrega da caçamba no endereço" },
              { status:"Colocada", color:"#2ecc71", desc:"Caçamba foi entregue e está no local do cliente" },
              { status:"Para Retirar", color:"#9b59b6", desc:"Solicitação de retirada feita, aguardando motorista buscar" },
              { status:"Para Trocar", color:"#f39c12", desc:"Cliente solicitou troca por outra caçamba" },
              { status:"Para Cobrar", color:"#e74c3c", desc:"Caçamba retirada, falta receber o pagamento" },
              { status:"Encerrada", color:"#888", desc:"Locação finalizada — caçamba retirada e pagamento ok" },
              { status:"Cancelada", color:"#e74c3c", desc:"Locação foi cancelada" },
            ].map((s, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8, background:"#fafafa" }}>
                <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background:s.color, flexShrink:0 }}></span>
                <div style={{ flex:1 }}>
                  <span style={{ fontWeight:700, fontSize:12, color:s.color, display:"inline-block", minWidth:100 }}>{s.status}</span>
                  <span style={{ fontSize:12, color:"#64748B", marginLeft:6 }}>{s.desc}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background:"#f0fdfb", border:"1px solid #d1fae5", borderRadius:8, padding:"10px 14px", marginTop:14, fontSize:12, color:"#475569", lineHeight:1.6 }}>
            <strong style={{ color:"#1AAE9E" }}>Dica:</strong> Selecione locações na lista e use o botão <strong>⚡ Ações</strong> para avançar o status em lote!
          </div>
        </div>
      ),
    },
  ];

  const cur = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:FONT }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", width:"100%", maxWidth:520, borderRadius:12, boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden", animation:"fadeIn .25s ease" }}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg, #1E2330 0%, #2a3040 100%)", padding:"22px 24px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-30, width:120, height:120, background:"radial-gradient(circle, rgba(26,174,158,.15), transparent 70%)", borderRadius:"50%" }}></div>
          <div style={{ display:"flex", alignItems:"center", gap:12, position:"relative", zIndex:1 }}>
            <span style={{ fontSize:28 }}>{cur.icon}</span>
            <div>
              <div style={{ color:"#fff", fontSize:17, fontWeight:800 }}>{cur.title}</div>
              <div style={{ color:"#94a3b8", fontSize:11, marginTop:2 }}>Passo {step+1} de {steps.length}</div>
            </div>
          </div>
          {/* Progress bar */}
          <div style={{ marginTop:14, height:3, background:"rgba(255,255,255,.1)", borderRadius:2, position:"relative", zIndex:1 }}>
            <div style={{ height:"100%", width:((step+1)/steps.length*100)+"%", background:"linear-gradient(90deg, #1AAE9E, #4ECDC4)", borderRadius:2, transition:"width .3s ease" }}></div>
          </div>
        </div>
        {/* Content */}
        <div style={{ padding:"20px 24px", maxHeight:"50vh", overflowY:"auto" }}>
          {cur.content}
        </div>
        {/* Footer */}
        <div style={{ padding:"14px 24px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", gap:5 }}>
            {steps.map((_, i) => (
              <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:i===step?"#1AAE9E":"#e0e0e0", transition:"background .2s", cursor:"pointer" }} onClick={() => setStep(i)}></div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {step > 0 && (
              <button onClick={() => setStep(s => s-1)} style={{ padding:"9px 18px", background:"#f5f5f5", border:"none", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, color:"#555" }}>Voltar</button>
            )}
            {!isLast ? (
              <button onClick={() => setStep(s => s+1)} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Próximo</button>
            ) : (
              <button onClick={onClose} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Começar a usar!</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tutorial Mapa Operacional Modal ──────────────────────────────────────────
function TutorialMapaModal({ onClose }) {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: "Mapa Operacional",
      icon: "🗺️",
      content: (
        <div>
          <p style={{ fontSize:14, color:"#475569", lineHeight:1.7, marginBottom:14 }}>O mapa mostra em tempo real onde estão todas as suas caçambas. Cada marcador representa uma locação ativa, colorido de acordo com o status.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              { color:"#1a9e8f", status:"Para Colocar", desc:"Caçamba aguardando entrega" },
              { color:"#888888", status:"Colocada", desc:"Caçamba no local do cliente" },
              { color:"#9b59b6", status:"Para Retirar", desc:"Aguardando retirada" },
              { color:"#E67E22", status:"Para Trocar", desc:"Cliente solicitou troca" },
              { color:"#e74c3c", status:"Atrasada", desc:"Passou da data prevista de retirada" },
            ].map((s, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8, background:"#fafafa" }}>
                <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background:s.color, flexShrink:0 }}></span>
                <span style={{ fontWeight:700, fontSize:12, color:s.color, minWidth:100 }}>{s.status}</span>
                <span style={{ fontSize:12, color:"#64748B" }}>{s.desc}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Funcionalidades do Mapa",
      icon: "📍",
      content: (
        <div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { icon:"🔍", label:"Filtros por status", desc:"Clique nos botões acima do mapa para filtrar por status (Para Colocar, Colocada, etc.)" },
              { icon:"🔎", label:"Busca rápida", desc:"Use a barra de pesquisa para encontrar caçambas por nome do cliente, endereço ou número" },
              { icon:"📌", label:"Popup interativo", desc:"Clique em um marcador para ver detalhes da locação, contato do cliente e ações rápidas" },
              { icon:"📱", label:"WhatsApp integrado", desc:"Envie mensagens ao cliente direto pelo popup do mapa, com um clique" },
              { icon:"🔴", label:"Alerta de atraso", desc:"Locações com caçamba parada além da data prevista ficam vermelhas automaticamente" },
              { icon:"📊", label:"Barra lateral", desc:"Veja a lista de locações ao lado do mapa e clique para focar no marcador" },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, background:"#f8fffe", border:"1px solid #e0f2ef", borderRadius:8, padding:"10px 14px" }}>
                <span style={{ fontSize:20 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:"#1E2330" }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Dicas de uso",
      icon: "💡",
      content: (
        <div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ background:"#f0fdfb", border:"1px solid #d1fae5", borderRadius:8, padding:"12px 16px" }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#1AAE9E", marginBottom:4 }}>Acompanhe diariamente</div>
              <p style={{ fontSize:12, color:"#475569", margin:0, lineHeight:1.6 }}>Abra o mapa todos os dias para ter uma visão geral da operação. Caçambas atrasadas significam dinheiro parado!</p>
            </div>
            <div style={{ background:"#f0fdfb", border:"1px solid #d1fae5", borderRadius:8, padding:"12px 16px" }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#1AAE9E", marginBottom:4 }}>Otimize rotas</div>
              <p style={{ fontSize:12, color:"#475569", margin:0, lineHeight:1.6 }}>Use o mapa junto com os Itinerários para planejar as rotas de entrega e retirada dos motoristas de forma eficiente.</p>
            </div>
            <div style={{ background:"#f0fdfb", border:"1px solid #d1fae5", borderRadius:8, padding:"12px 16px" }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#1AAE9E", marginBottom:4 }}>Ações rápidas</div>
              <p style={{ fontSize:12, color:"#475569", margin:0, lineHeight:1.6 }}>Ao clicar no marcador e depois em uma ação, você será redirecionado para a página de Locações onde pode confirmar entregas, retiradas e trocas.</p>
            </div>
          </div>
        </div>
      ),
    },
  ];
  const cur = steps[step];
  const isLast = step === steps.length - 1;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:FONT }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", width:"100%", maxWidth:520, borderRadius:12, boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden" }}>
        <div style={{ background:"linear-gradient(135deg, #1E2330 0%, #2a3040 100%)", padding:"22px 24px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-30, width:120, height:120, background:"radial-gradient(circle, rgba(26,174,158,.15), transparent 70%)", borderRadius:"50%" }}></div>
          <div style={{ display:"flex", alignItems:"center", gap:12, position:"relative", zIndex:1 }}>
            <span style={{ fontSize:28 }}>{cur.icon}</span>
            <div>
              <div style={{ color:"#fff", fontSize:17, fontWeight:800 }}>{cur.title}</div>
              <div style={{ color:"#94a3b8", fontSize:11, marginTop:2 }}>Passo {step+1} de {steps.length}</div>
            </div>
          </div>
          <div style={{ marginTop:14, height:3, background:"rgba(255,255,255,.1)", borderRadius:2, position:"relative", zIndex:1 }}>
            <div style={{ height:"100%", width:((step+1)/steps.length*100)+"%", background:"linear-gradient(90deg, #1AAE9E, #4ECDC4)", borderRadius:2, transition:"width .3s ease" }}></div>
          </div>
        </div>
        <div style={{ padding:"20px 24px", maxHeight:"50vh", overflowY:"auto" }}>{cur.content}</div>
        <div style={{ padding:"14px 24px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", gap:5 }}>{steps.map((_, i) => (<div key={i} style={{ width:8, height:8, borderRadius:"50%", background:i===step?"#1AAE9E":"#e0e0e0", transition:"background .2s", cursor:"pointer" }} onClick={() => setStep(i)}></div>))}</div>
          <div style={{ display:"flex", gap:8 }}>
            {step > 0 && <button onClick={() => setStep(s => s-1)} style={{ padding:"9px 18px", background:"#f5f5f5", border:"none", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, color:"#555" }}>Voltar</button>}
            {!isLast ? (
              <button onClick={() => setStep(s => s+1)} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Próximo</button>
            ) : (
              <button onClick={onClose} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Entendi!</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tutorial Itinerários Modal ───────────────────────────────────────────────
function TutorialItinerariosModal({ onClose }) {
  const [step, setStep] = useState(0);
  const steps = [
    {
      title: "O que são Itinerários?",
      icon: "🛣️",
      content: (
        <div>
          <p style={{ fontSize:14, color:"#475569", lineHeight:1.7, marginBottom:14 }}>Itinerários são as rotas diárias dos seus motoristas. Aqui você monta a lista de paradas (entregas, retiradas e trocas) que cada motorista deve fazer no dia.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { icon:"📅", label:"Planejamento diário", desc:"Crie um itinerário por dia, por motorista" },
              { icon:"🗂️", label:"Paradas organizadas", desc:"Cada parada é uma locação — entrega, retirada ou troca" },
              { icon:"🗺️", label:"Rota no mapa", desc:"Visualize a rota completa no mapa com distância e tempo estimado" },
              { icon:"🔀", label:"Ordenação inteligente", desc:"O sistema sugere a melhor ordem das paradas por proximidade" },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, background:"#f8fffe", border:"1px solid #e0f2ef", borderRadius:8, padding:"10px 14px" }}>
                <span style={{ fontSize:20 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:"#1E2330" }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Como criar um itinerário",
      icon: "📋",
      content: (
        <div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {[
              { n:"1", text:"Clique em \"+ Novo Itinerário\" e selecione o motorista e a data" },
              { n:"2", text:"Na coluna esquerda (Demanda) aparecem as locações pendentes de operação" },
              { n:"3", text:"Arraste as locações da Demanda para o itinerário, ou clique para adicionar" },
              { n:"4", text:"Reordene as paradas arrastando — ou peça a sugestão de rota inteligente" },
              { n:"5", text:"Gere o PDF do itinerário para imprimir e entregar ao motorista" },
            ].map((s, i) => (
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                <div style={{ minWidth:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:12, flexShrink:0 }}>{s.n}</div>
                <p style={{ fontSize:13, color:"#475569", margin:0, paddingTop:4, lineHeight:1.5 }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Confirmando atendimentos",
      icon: "✅",
      content: (
        <div>
          <p style={{ fontSize:14, color:"#475569", lineHeight:1.7, marginBottom:14 }}>Quando o motorista conclui uma parada, você confirma o atendimento e o status da locação é atualizado automaticamente:</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[
              { op:"Entrega", desc:"Locação muda de \"Para Colocar\" para \"Colocada\"", color:"#2ecc71" },
              { op:"Retirada", desc:"Locação muda de \"Para Retirar\" para \"Para Cobrar\" ou \"Encerrada\"", color:"#9b59b6" },
              { op:"Troca", desc:"Registra a troca de caçamba no local", color:"#f39c12" },
            ].map((s, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8, background:"#fafafa", borderLeft:"3px solid "+s.color }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:s.color }}>{s.op}</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background:"#f0fdfb", border:"1px solid #d1fae5", borderRadius:8, padding:"10px 14px", marginTop:14, fontSize:12, color:"#475569", lineHeight:1.6 }}>
            <strong style={{ color:"#1AAE9E" }}>Dica:</strong> Quando todas as paradas forem atendidas, o itinerário é automaticamente marcado como "Concluído".
          </div>
        </div>
      ),
    },
    {
      title: "App do Motorista",
      icon: "📱",
      content: (
        <div>
          <p style={{ fontSize:14, color:"#475569", lineHeight:1.7, marginBottom:14 }}>O <strong>Controla Motorista</strong> é o aplicativo que seus motoristas usam no dia a dia. Com ele, toda a operação em campo fica conectada ao escritório em tempo real.</p>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { icon:"📋", label:"Recebe o itinerário", desc:"O motorista vê todas as paradas do dia automaticamente no celular" },
              { icon:"🧭", label:"Navegação integrada", desc:"Abre o Waze ou Google Maps direto para o endereço da parada" },
              { icon:"✅", label:"Confirma no campo", desc:"O motorista confirma entregas e retiradas direto pelo app" },
              { icon:"📸", label:"Fotos e assinatura", desc:"Registra fotos da caçamba no local e coleta assinatura digital do cliente" },
              { icon:"🔔", label:"Notificações", desc:"Recebe alertas sonoros quando novos itinerários são criados" },
              { icon:"🔄", label:"Sincronização", desc:"Tudo que o motorista faz no app atualiza no sistema em tempo real" },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, background:"#f8fffe", border:"1px solid #e0f2ef", borderRadius:8, padding:"10px 14px" }}>
                <span style={{ fontSize:20 }}>{item.icon}</span>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color:"#1E2330" }}>{item.label}</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ background:"linear-gradient(135deg, #f0fdfb, #e0f7f3)", border:"1px solid #b2dfdb", borderRadius:8, padding:"12px 16px", marginTop:14, fontSize:12, color:"#475569", lineHeight:1.6, textAlign:"center" }}>
            <strong style={{ color:"#1AAE9E", fontSize:14 }}>Peça ao motorista baixar o app!</strong><br/>Disponível na Play Store: <strong>Controla Motorista</strong>
          </div>
        </div>
      ),
    },
  ];
  const cur = steps[step];
  const isLast = step === steps.length - 1;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.55)", zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:FONT }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", width:"100%", maxWidth:520, borderRadius:12, boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden" }}>
        <div style={{ background:"linear-gradient(135deg, #1E2330 0%, #2a3040 100%)", padding:"22px 24px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-30, width:120, height:120, background:"radial-gradient(circle, rgba(26,174,158,.15), transparent 70%)", borderRadius:"50%" }}></div>
          <div style={{ display:"flex", alignItems:"center", gap:12, position:"relative", zIndex:1 }}>
            <span style={{ fontSize:28 }}>{cur.icon}</span>
            <div>
              <div style={{ color:"#fff", fontSize:17, fontWeight:800 }}>{cur.title}</div>
              <div style={{ color:"#94a3b8", fontSize:11, marginTop:2 }}>Passo {step+1} de {steps.length}</div>
            </div>
          </div>
          <div style={{ marginTop:14, height:3, background:"rgba(255,255,255,.1)", borderRadius:2, position:"relative", zIndex:1 }}>
            <div style={{ height:"100%", width:((step+1)/steps.length*100)+"%", background:"linear-gradient(90deg, #1AAE9E, #4ECDC4)", borderRadius:2, transition:"width .3s ease" }}></div>
          </div>
        </div>
        <div style={{ padding:"20px 24px", maxHeight:"50vh", overflowY:"auto" }}>{cur.content}</div>
        <div style={{ padding:"14px 24px", borderTop:"1px solid #f0f0f0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", gap:5 }}>{steps.map((_, i) => (<div key={i} style={{ width:8, height:8, borderRadius:"50%", background:i===step?"#1AAE9E":"#e0e0e0", transition:"background .2s", cursor:"pointer" }} onClick={() => setStep(i)}></div>))}</div>
          <div style={{ display:"flex", gap:8 }}>
            {step > 0 && <button onClick={() => setStep(s => s-1)} style={{ padding:"9px 18px", background:"#f5f5f5", border:"none", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, color:"#555" }}>Voltar</button>}
            {!isLast ? (
              <button onClick={() => setStep(s => s+1)} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Próximo</button>
            ) : (
              <button onClick={onClose} style={{ padding:"9px 22px", background:"linear-gradient(135deg, #1AAE9E, #148f82)", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.3)" }}>Começar a usar!</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_TABS = [
  { key:"Para Colocar", label:"Para Colocar", color:"#1a9e8f" },
  { key:"Colocada",     label:"Colocada",     color:"#2ecc71" },
  { key:"Para Retirar", label:"Para Retirar", color:"#9b59b6" },
  { key:"Para Trocar",  label:"Para Trocar",  color:"#f39c12" },
  { key:"Para Cobrar",  label:"Para Cobrar",  color:"#e74c3c" },
  { key:"Encerrada",    label:"Encerrada",    color:"#888"    },
  { key:"Cancelada",    label:"Cancelada",    color:"#e74c3c" },
];

// Actions available per tab
const TAB_ACTIONS = {
  "Para Colocar": [
    { label:"Confirmar como Colocada(s)", key:"colocada", color:"#1aae9e" },
  ],
  "Colocada": [
    { label:"Programar Retirada",  key:"para_retirar", color:"#9b59b6" },
    { label:"Programar Troca",     key:"para_trocar",  color:"#f39c12" },
  ],
  "Para Retirar": [
    { label:"Confirmar Retirada",  key:"encerrada",    color:"#27ae60" },
    { label:"Programar Troca",     key:"para_trocar",  color:"#f39c12" },
    { label:"Voltar para Colocada", key:"voltar_colocada", color:"#1a9e8f" },
  ],
  "Para Trocar": [
    { label:"Confirmar Troca(s)",  key:"confirmar_troca", color:"#2ecc71" },
    { label:"Voltar para Colocada", key:"voltar_colocada", color:"#1a9e8f" },
  ],
  "Para Cobrar": [
    { label:"Confirmar Pagamento", key:"pago",         color:"#f59e0b" },
  ],
  "Encerrada": [],
  "Cancelada": [],
};

// ─── Edit Locação Modal ─────────────────────────────────────────────────────
function EditLocacaoModal({ locacao, clientes, motoristas, cacambas, tiposCacamba=[], onClose, onSave, apiFetch }) {
  const [form, setForm] = useState({
    cliente: locacao.cliente||"", clienteId: locacao.clienteId||locacao.cliente_id||null,
    contatoTel: locacao.contatoTel||locacao.contato_tel||"",
    contatoNome: locacao.contatoNome||locacao.contato_nome||"",
    contatoEmail: locacao.contatoEmail||locacao.contato_email||"",
    tipoCacamba: locacao.tipoCacamba||locacao.tipo_cacamba||"",
    cacamba: locacao.cacamba||locacao.cacamba_codigo||"", operacao: locacao.operacao||"ENTREGA",
    motorista: locacao.motorista||"", dataSolicitacao: locacao.dataSolicitacao||locacao.data_solicitacao||"",
    dataEntrega: locacao.dataEntrega||locacao.data_entrega||"",
    horaEntrega: locacao.horaEntrega||locacao.hora_entrega||"",
    dataRetirada: locacao.dataRetirada||locacao.data_retirada||"", valor: locacao.valor||"", valorFmt: initCurrencyFromNumber(locacao.valor),
    pagamento: locacao.pagamento||"dinheiro", prazoPagamento: locacao.prazoPagamento||locacao.prazo_pagamento||"",
    obs: locacao.obs||locacao.observacoes||"", status: locacao.status||"Para Colocar",
    pago: !!locacao.pago, mtr: locacao.mtr||"",
    dataPagamento: locacao.dataPagamento||locacao.data_pagamento||(locacao.pago ? hojeLocal() : ""),
    logradouro: locacao.logradouro||(locacao.endereco?.logradouro)||"",
    numero: locacao.numero||(locacao.endereco?.numero)||"",
    complemento: locacao.complemento||locacao.end_complemento||(locacao.endereco?.complemento)||"",
    bairro: locacao.bairro||(locacao.endereco?.bairro)||"",
    cidade: locacao.cidade||(locacao.endereco?.cidade)||"",
    uf: locacao.uf||(locacao.endereco?.uf)||"",
    cep: locacao.cep||(locacao.endereco?.cep)||"",
    classeA: locacao.residuos?.classeA || [],
    classeB: locacao.residuos?.classeB || [],
    classeC: locacao.residuos?.classeC || [],
    classeD: locacao.residuos?.classeD || [],
    outrosResiduos: locacao.residuos?.outros || "",
    diariaExtra: (locacao.diariaExtra != null && locacao.diariaExtra !== "") ? String(locacao.diariaExtra) : (locacao.diaria_extra != null ? String(locacao.diaria_extra) : ""),
  });
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const toggleRes = (cls, item) => set(cls, form[cls].includes(item) ? form[cls].filter(x => x !== item) : [...form[cls], item]);
  const reqStyle = (field) => ({ ...inp, borderColor: !form[field] ? "#1aae9e" : "#e0e0e0" });
  const [mapModal, setMapModal] = useState(false);
  const API = process.env.REACT_APP_API || "http://localhost:3001";

  // Anexos do backend
  const [anexoComp, setAnexoComp] = useState(null); // doc existente do backend
  const [anexoNF, setAnexoNF] = useState(null);
  const [newFileComp, setNewFileComp] = useState(null); // novo arquivo selecionado
  const [newFileNF, setNewFileNF] = useState(null);
  const [planoInfo, setPlanoInfo] = useState(null);

  // Pagamentos parciais
  const [showParcial, setShowParcial] = useState(false);
  const [pagamentosParciais, setPagamentosParciais] = useState([]);
  const [parcialLoaded, setParcialLoaded] = useState(false);
  const [novoParcial, setNovoParcial] = useState({ valor:"", forma_pagamento:"Dinheiro", data_pagamento: hojeLocal() });
  const [salvandoParcial, setSalvandoParcial] = useState(false);

  const carregarParciais = () => {
    if (!apiFetch) return;
    apiFetch("/api/locacoes/" + locacao.id + "/pagamentos").then(rows => {
      const list = Array.isArray(rows) ? rows : [];
      setPagamentosParciais(list);
      setParcialLoaded(true);
      if (list.length > 0) setShowParcial(true);
    }).catch(() => {});
  };

  const addParcial = async () => {
    const v = parseFloat(String(novoParcial.valor).replace(",", "."));
    if (!v || v <= 0 || salvandoParcial) return;
    setSalvandoParcial(true);
    try {
      // Bloqueia valor acima do restante da locação
      const _valorTotal = parseFloat(form.valor) || 0;
      const _jaPago = pagamentosParciais.reduce((s, p) => s + (parseFloat(p.valor)||0), 0);
      if (_valorTotal > 0 && v > (_valorTotal - _jaPago) + 0.005) {
        alert("Valor acima do estabelecido: o restante desta locação é R$ " + (_valorTotal - _jaPago).toLocaleString("pt-BR", { minimumFractionDigits:2 }) + ". Ajuste o valor do pagamento.");
        setSalvandoParcial(false);
        return;
      }
      const reg = await apiFetch("/api/locacoes/" + locacao.id + "/pagamentos", { method:"POST", body:{ valor: v, forma_pagamento: novoParcial.forma_pagamento, data_pagamento: novoParcial.data_pagamento || hojeLocal() } });
      setPagamentosParciais(p => [...p, reg]);
      setNovoParcial({ valor:"", forma_pagamento:"Dinheiro", data_pagamento: hojeLocal() });
      // Quitou 100%? Marca como PAGO automaticamente (usuário ainda confirma no Salvar)
      if (_valorTotal > 0 && (_jaPago + v) >= _valorTotal - 0.005 && !form.pago && locacao.status !== "Encerrada") {
        setForm(f => ({ ...f, pago: true, dataPagamento: f.dataPagamento || (novoParcial.data_pagamento || hojeLocal()) }));
      }
    } catch(e) { alert("Erro ao registrar pagamento: " + e.message); }
    finally { setSalvandoParcial(false); }
  };

  const delParcial = async (pid) => {
    if (!window.confirm("Excluir este pagamento?")) return;
    try {
      await apiFetch("/api/locacoes/" + locacao.id + "/pagamentos/" + pid, { method:"DELETE" });
      setPagamentosParciais(p => p.filter(x => x.id !== pid));
    } catch(e) { alert("Erro ao excluir pagamento: " + e.message); }
  };

  const fmtParcialData = (d) => { const m = String(d||"").slice(0,10).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? m[3] + "/" + m[2] : String(d||""); };
  const fmtParcialMoney = (v) => (parseFloat(v)||0).toLocaleString("pt-BR", { minimumFractionDigits:2, maximumFractionDigits:2 });
  const totalPagoParcial = pagamentosParciais.reduce((s, p) => s + (parseFloat(p.valor)||0), 0);
  const restanteParcial = (parseFloat(form.valor)||0) - totalPagoParcial;
  const novoParcialValido = (() => { const v = parseFloat(String(novoParcial.valor).replace(",", ".")); return !!v && v > 0; })();

  useEffect(() => {
    if (!apiFetch) return;
    apiFetch("/api/config/storage").then(setPlanoInfo).catch(() => {});
    apiFetch("/api/documentos?locacao_id=" + locacao.id).then(docs => {
      const comp = (docs||[]).find(d => d.categoria === "comprovante");
      const nf = (docs||[]).find(d => d.categoria === "nota_fiscal");
      if (comp) setAnexoComp(comp);
      if (nf) setAnexoNF(nf);
    }).catch(() => {});
    carregarParciais();
  }, []);

  const uploadFile = async (file, categoria) => {
    const token = sessionStorage.getItem("controla_token");
    const fd = new FormData();
    fd.append("documento", file);
    fd.append("categoria", categoria);
    fd.append("locacao_id", locacao.id);
    const res = await fetch(API + "/api/documentos", { method:"POST", headers:{ Authorization:"Bearer "+token }, body:fd });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Erro no upload");
    return data;
  };

  const removeAnexo = async (doc, setter) => {
    if (!doc?.id) return;
    try { await apiFetch("/api/documentos/" + doc.id, { method:"DELETE" }); setter(null); } catch(e) { alert("Erro: "+e.message); }
  };

  const isLocked = ["Colocada", "Para Retirar", "Para Trocar", "Para Cobrar", "Encerrada"].includes(locacao.status);
  const lockedStyle = { ...inp, background: "#e8e8e8", color: "#666", cursor: "not-allowed", pointerEvents: "none" };
  // Data Prev. Retirada é editável em "Colocada" e "Para Retirar" (para remarcar)
  const retiradaEditavel = locacao.status === "Para Retirar" || locacao.status === "Colocada";

  const save = async () => {
    if (!form.logradouro?.trim()) { alert("Logradouro é obrigatório."); return; }
    if (!form.dataEntrega) { alert("Data de Entrega é obrigatória."); return; }
    try {
      const body = { ...form, endereco:{ logradouro:form.logradouro, numero:form.numero, complemento:form.complemento, bairro:form.bairro, cidade:form.cidade, uf:form.uf, cep:form.cep }, residuos:{ classeA: form.classeA||[], classeB: form.classeB||[], classeC: form.classeC||[], classeD: form.classeD||[], outros: form.outrosResiduos||'' } };
      delete body.anexoPagamento; delete body.anexoNF;
      const updated = await apiFetch("/api/locacoes/"+locacao.id, { method:"PUT", body });
      // Upload novos anexos se selecionados
      if (newFileComp) { try { await uploadFile(newFileComp, "comprovante"); } catch(e) { console.error(e); } }
      if (newFileNF) { try { await uploadFile(newFileNF, "nota_fiscal"); } catch(e) { console.error(e); } }
      await apiFetch("/api/historico/"+locacao.id, { method:"POST", body:{ acao:"Edição", detalhes:"Locação editada manualmente" } }).catch(()=>{});
      onSave(updated);
      onClose();
    } catch(e) { alert("Erro ao salvar: "+e.message); }
  };

  return (<>
    <LargeModal title={"Editar Locação #"+locacao.id} subtitle="Altere os dados da locação" onClose={onClose} onSave={save} saveLabel="✓ Salvar Alterações">
      <div style={{ paddingTop:14 }}>
        <SectionHeader icon="👤" label="Cliente e Endereço" />
        {isLocked && (
          <div style={{ padding:"10px 14px", background:"#fff3e0", borderRadius:4, fontSize:12, color:"#e65100", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
            <span></span> <span>Dados do cliente e datas bloqueados (status: <strong style={{margin:"0 4px"}}>{locacao.status}</strong>). Endereço, dados financeiros e observações podem ser alterados.</span>
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
          <Field label="Cliente"><input style={isLocked ? lockedStyle : inp} value={form.cliente} onChange={e=>set("cliente",e.target.value)} readOnly={isLocked} disabled={isLocked} /></Field>
          <Field label="Telefone">
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <input style={lockedStyle} value={form.contatoTel} readOnly disabled placeholder="(11) 99999-9999" />
              {form.contatoTel && <button type="button" onClick={() => {
                const tel = (form.contatoTel||"").replace(/\D/g,"");
                if(!tel){return;}
                const phone = tel.length<=11?"55"+tel:tel;
                const nome = (form.cliente||"").split(" ")[0];
                const endereco = [form.logradouro, form.numero, form.bairro, form.cidade, form.uf].filter(Boolean).join(", ");
                const statusTitles = { "Para Colocar":"*Sua ca\u00e7amba j\u00e1 est\u00e1 a caminho!*", "Colocada":"*Sua ca\u00e7amba foi entregue com sucesso!*", "Para Retirar":"*Retirada da ca\u00e7amba programada!*", "Para Trocar":"*Troca da ca\u00e7amba programada!*", "Encerrada":"*Loca\u00e7\u00e3o encerrada com sucesso!*", "Para Cobrar":"*Aten\u00e7\u00e3o sobre sua loca\u00e7\u00e3o!*" };
                const titulo = statusTitles[locacao.status] || "*Atualiza\u00e7\u00e3o da sua ca\u00e7amba!*";
                const msg = encodeURIComponent(titulo+"\n\nOla, "+nome+"! Passando para avisar que o status da sua cacamba foi atualizado para: *"+(locacao.status||"")+"*\nEndereco: "+endereco+"\n\nQualquer duvida ou ajuste, e so chamar aqui!\nObrigado por escolher nossos servicos!");
                window.open("https://wa.me/"+phone+"?text="+msg,"_blank");
              }} title="Enviar WhatsApp" style={{ background:"#25D366", border:"none", borderRadius:4, padding:"6px 8px", cursor:"pointer", display:"flex", alignItems:"center" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.347 0-4.518-.809-6.234-2.163l-.353-.29-3.661 1.228 1.228-3.661-.29-.353A9.96 9.96 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
              </button>}
            </div>
          </Field>
          <Field label="CEP"><input style={inp} value={form.cep} onChange={e=>{const v=formatCep(e.target.value);set("cep",v);}} placeholder="00000-000" /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <Field label="Logradouro *"><AddressAutocomplete style={reqStyle("logradouro")} value={form.logradouro} onChange={v=>set("logradouro",v)} cidadeHint={form.cidade} ufHint={form.uf} placeholder="Rua, Avenida ou nome do local..." onSelect={s => { set("logradouro", s.logradouro); if(s.numero) set("numero", s.numero); if(s.bairro) set("bairro", s.bairro); if(s.cidade) set("cidade", s.cidade); if(s.uf) set("uf", s.uf); if(s.cep) set("cep", s.cep); if(s.lat) set("lat", s.lat); if(s.lng) set("lng", s.lng); }} /></Field>
          <Field label="Número"><input style={inp} value={form.numero} onChange={e=>set("numero",e.target.value)} /></Field>
          <Field label="Complemento"><input style={inp} value={form.complemento} onChange={e=>set("complemento",e.target.value)} placeholder="Apto, Bloco..." /></Field>
          <Field label="Bairro"><input style={inp} value={form.bairro} onChange={e=>set("bairro",e.target.value)} /></Field>
          <Field label="Cidade"><input style={inp} value={form.cidade} onChange={e=>set("cidade",e.target.value)} /></Field>
          <Field label="UF"><input style={inp} value={form.uf} onChange={e=>set("uf",e.target.value)} maxLength={2} /></Field>
        </div>
        <div style={{ marginBottom:12 }}><button type="button" onClick={() => setMapModal(true)} style={{ padding:"7px 11px", background:"none", border:"1px solid #1a9e8f", borderRadius:3, color:"#1a9e8f", fontSize:12, cursor:"pointer", fontWeight:600, whiteSpace:"nowrap", fontFamily:FONT }}>📍 Confirmar no Mapa</button></div>
        <SectionHeader icon="📦" label="Dados da Locação" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
          <Field label="Tipo de Caçamba">
            <select style={{ ...inp, appearance:"none" }} value={form.tipoCacamba} onChange={e=>set("tipoCacamba",e.target.value)}>
              <option value="">-- Selecione --</option>
              {tiposCacamba.map(t => <option key={t.id} value={t.nome}>{t.nome}{t.cubicagem?" ("+t.cubicagem.replace(/m3/gi,"").trim()+" m³)":""}</option>)}
            </select>
          </Field>
          <Field label="Caçamba (Nº)">
            <select style={{ ...inp, appearance:"none" }} value={form.cacamba} onChange={e=>set("cacamba",e.target.value)}>
              <option value="">-- Selecione --</option>
              {(cacambas||[]).filter(c => (!form.tipoCacamba || !c.tipo || c.tipo === form.tipoCacamba) && (c.codigo === form.cacamba || !c.status || c.status === "Disponível")).map(c => <option key={c.id||String(c)} value={c.codigo||""}>{c.codigo||String(c)}{c.status && c.status !== "Disponível" && c.codigo === form.cacamba ? " ("+c.status+")" : ""}</option>)}
            </select>
          </Field>
          <Field label="Motorista">
            <select style={{ ...inp, appearance:"none" }} value={form.motorista} onChange={e=>set("motorista",e.target.value)}>
              <option value="">-- Selecione --</option>
              {(motoristas||[]).map(m => <option key={m.id||String(m)} value={m.nome||String(m)}>{m.nome||String(m)}</option>)}
            </select>
          </Field>
          <Field label="Operação">
            <select style={{ ...lockedStyle, appearance:"none" }} value={form.operacao} disabled={true}>
              <option value="ENTREGA">ENTREGA</option><option value="RETIRADA">RETIRADA</option><option value="TROCA">TROCA</option>
            </select>
          </Field>
          <Field label="Pedido em"><input type="date" style={isLocked ? lockedStyle : inp} value={form.dataSolicitacao} onChange={e=>set("dataSolicitacao",e.target.value)} readOnly={isLocked} disabled={isLocked} /></Field>
          <Field label="Data Prev. Entrega *"><input type="date" style={isLocked ? lockedStyle : reqStyle("dataEntrega")} value={form.dataEntrega} onChange={e=>set("dataEntrega",e.target.value)} readOnly={isLocked} disabled={isLocked} /></Field>
          <Field label={<span>Hora Prev. <span style={{color:"#aaa", fontWeight:400, fontSize:11}}>(opcional)</span></span>}><input type="time" style={isLocked ? lockedStyle : inp} value={form.horaEntrega} onChange={e=>set("horaEntrega",e.target.value)} readOnly={isLocked} disabled={isLocked} /></Field>
          <Field label="Data Prev. Retirada"><input type="date" style={(isLocked && !retiradaEditavel) ? lockedStyle : inp} value={form.dataRetirada} min={form.dataEntrega||undefined} onChange={e=>{ if(isLocked && !retiradaEditavel) return; if(form.dataEntrega && e.target.value && e.target.value < form.dataEntrega) { alert("A data de retirada não pode ser anterior à data de entrega."); return; } set("dataRetirada",e.target.value); }} readOnly={isLocked && !retiradaEditavel} disabled={isLocked && !retiradaEditavel} /></Field>
          <Field label="Valor (R$)">
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#888", fontWeight:600, pointerEvents:"none" }}>R$</span>
              <input type="text" inputMode="numeric" style={{ ...inp, textAlign:"right", paddingLeft:36 }} value={form.valorFmt||""} onChange={e=>{ const formatted = formatCurrencyInput(e.target.value); set("valorFmt", formatted); set("valor", parseCurrencyToFloat(formatted)); }} />
            </div>
          </Field>
          <Field label={<span>Diária extra (R$/dia) <span style={{color:"#aaa", fontWeight:400, fontSize:11}}>(atraso)</span></span>}>
            <input type="number" min="0" step="0.01" style={{ ...inp, textAlign:"right" }} value={form.diariaExtra} onChange={e=>set("diariaExtra",e.target.value)} placeholder="0,00" />
          </Field>
          <Field label="MTR/CTR"><input style={inp} value={form.mtr} onChange={e=>set("mtr",e.target.value)} /></Field>
          <Field label="Pagamento">
            <select style={{ ...inp, appearance:"none" }} value={form.pagamento} onChange={e=>set("pagamento",e.target.value)}>
              <option value="dinheiro">Dinheiro</option><option value="pix">PIX</option><option value="boleto">Boleto</option><option value="faturado">Faturado</option>
            </select>
          </Field>
          {(form.pagamento==="boleto"||form.pagamento==="faturado") && (
            <Field label="Prazo Pagamento (dias)"><input style={inp} value={form.prazoPagamento} onChange={e=>set("prazoPagamento",e.target.value)} /></Field>
          )}
        </div>
        <Field label="Observações"><textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={form.obs} onChange={e=>set("obs",e.target.value)} /></Field>

        <SectionHeader icon="♻" label="Classificação dos Resíduos" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:18, marginBottom:12 }}>
            <CheckGroup title="Classe A" items={CLASSE_A} checked={form.classeA} onChange={item => toggleRes("classeA", item)} />
            <CheckGroup title="Classe B" items={CLASSE_B} checked={form.classeB} onChange={item => toggleRes("classeB", item)} />
            <CheckGroup title="Classe C" items={CLASSE_C} checked={form.classeC} onChange={item => toggleRes("classeC", item)} />
            <CheckGroup title="Classe D" items={CLASSE_D} checked={form.classeD} onChange={item => toggleRes("classeD", item)} />
          </div>
          <div>
            <label style={{ fontSize:11, color:"#888", display:"block", marginBottom:4 }}>Outros resíduos não listados:</label>
            <input style={inp} value={form.outrosResiduos} onChange={e => set("outrosResiduos", e.target.value)} />
          </div>
        </SectionBody>

        <SectionHeader icon="💳" label="Pagamento e Anexos" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <Field label="Status Pagamento">
            <div
              onClick={() => {
                if (locacao.status === "Encerrada") return;
                const novoPago = !form.pago;
                setForm(p => ({ ...p, pago: novoPago, dataPagamento: (novoPago && !p.dataPagamento) ? hojeLocal() : p.dataPagamento }));
              }}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 14px", borderRadius:6, cursor:locacao.status==="Encerrada"?"not-allowed":"pointer", border:"1px solid "+(form.pago?"#2ecc71":"#e74c3c"), background:form.pago?"#e6f9ee":"#fde8e8", transition:"all .2s", opacity:locacao.status==="Encerrada"?0.6:1 }}
              title={locacao.status==="Encerrada"?"Locação encerrada — pagamento bloqueado":""}
            >
              <div style={{ width:40, height:22, borderRadius:11, background:form.pago?"#2ecc71":"#ccc", position:"relative", transition:"background .2s" }}>
                <div style={{ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:form.pago?20:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)" }} />
              </div>
              <span style={{ fontSize:13, fontWeight:700, color:form.pago?"#1a7a38":"#c0392b" }}>{form.pago?"PAGO":"NÃO PAGO"}</span>
              {locacao.status === "Encerrada" && <span style={{ fontSize:10, color:"#888", marginLeft:4 }}>🔒</span>}
            </div>
          </Field>
          {form.pago && (
            <Field label="Data Pagamento"><input type="date" style={inp} value={form.dataPagamento||""} onChange={e=>set("dataPagamento",e.target.value)} /></Field>
          )}
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600, color:"#555", cursor:"pointer", userSelect:"none" }}>
            <input type="checkbox" checked={showParcial} onChange={e => { const v = e.target.checked; setShowParcial(v); if (v && !parcialLoaded) carregarParciais(); }} style={{ width:15, height:15, accentColor:"#1aae9e", cursor:"pointer" }} />
            Pagamento parcial
          </label>
          {showParcial && (
            <div style={{ marginTop:10, padding:"12px 14px", border:"1px solid #e0e0e0", borderRadius:6, background:"#fafafa" }}>
              <div style={{ display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap", marginBottom:12 }}>
                <div style={{ flex:"1 1 110px", minWidth:100 }}>
                  <label style={{ fontSize:11, color:"#888", display:"block", marginBottom:4 }}>Valor (R$)</label>
                  <input type="number" step="0.01" min="0" style={inp} value={novoParcial.valor} onChange={e => setNovoParcial(p => ({ ...p, valor: e.target.value }))} placeholder="0,00" />
                </div>
                <div style={{ flex:"1 1 120px", minWidth:110 }}>
                  <label style={{ fontSize:11, color:"#888", display:"block", marginBottom:4 }}>Forma</label>
                  <select style={{ ...inp, appearance:"none" }} value={novoParcial.forma_pagamento} onChange={e => setNovoParcial(p => ({ ...p, forma_pagamento: e.target.value }))}>
                    <option value="Dinheiro">Dinheiro</option><option value="PIX">PIX</option><option value="Cartão">Cartão</option><option value="Boleto">Boleto</option><option value="Transferência">Transferência</option>
                  </select>
                </div>
                <div style={{ flex:"1 1 130px", minWidth:120 }}>
                  <label style={{ fontSize:11, color:"#888", display:"block", marginBottom:4 }}>Data</label>
                  <input type="date" style={inp} value={novoParcial.data_pagamento} onChange={e => setNovoParcial(p => ({ ...p, data_pagamento: e.target.value }))} />
                </div>
                <button type="button" onClick={addParcial} disabled={!novoParcialValido || salvandoParcial} style={{ padding:"8px 16px", borderRadius:4, border:"none", background: (!novoParcialValido || salvandoParcial) ? "#ccc" : "#1aae9e", color:"#fff", fontSize:12, fontWeight:700, cursor: (!novoParcialValido || salvandoParcial) ? "not-allowed" : "pointer", fontFamily:FONT }}>OK</button>
              </div>
              {pagamentosParciais.length > 0 && (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, marginBottom:10 }}>
                  <thead>
                    <tr style={{ borderBottom:"1px solid #e0e0e0" }}>
                      <th style={{ textAlign:"left", padding:"4px 6px", fontSize:11, color:"#888", fontWeight:600 }}>Data</th>
                      <th style={{ textAlign:"left", padding:"4px 6px", fontSize:11, color:"#888", fontWeight:600 }}>Forma</th>
                      <th style={{ textAlign:"right", padding:"4px 6px", fontSize:11, color:"#888", fontWeight:600 }}>Valor</th>
                      <th style={{ width:30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagamentosParciais.map(p => (
                      <tr key={p.id} style={{ borderBottom:"1px solid #f0f0f0" }}>
                        <td style={{ padding:"5px 6px", color:"#555" }}>{fmtParcialData(p.data_pagamento)}</td>
                        <td style={{ padding:"5px 6px", color:"#555" }}>{p.forma_pagamento||"—"}</td>
                        <td style={{ padding:"5px 6px", textAlign:"right", fontWeight:600, color:"#333" }}>R$ {fmtParcialMoney(p.valor)}</td>
                        <td style={{ padding:"5px 6px", textAlign:"center" }}>
                          <button type="button" onClick={() => delParcial(p.id)} title="Excluir pagamento" style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontWeight:700, fontSize:13, padding:0 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {restanteParcial > 0 ? (
                <div style={{ fontSize:12, fontWeight:700, color:"#e74c3c" }}>Falta R$ {fmtParcialMoney(restanteParcial)} para quitar esta locação</div>
              ) : (
                <div style={{ fontSize:12, fontWeight:700, color:"#2ecc71" }}>✓ Valor total atingido — a locação pode ser marcada como paga{restanteParcial < 0 ? " (pago R$ " + fmtParcialMoney(Math.abs(restanteParcial)) + " acima do valor)" : ""}</div>
              )}
            </div>
          )}
        </div>
        {planoInfo && !planoInfo.permite_anexos ? (
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:8, background:"linear-gradient(135deg, #f8f9fa 0%, #eef1f5 100%)", border:"1px solid #dee2e6", marginBottom:14 }}>
            <div style={{ width:30, height:30, borderRadius:"50%", background:"linear-gradient(135deg, #6c757d, #495057)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:13, filter:"brightness(10)" }}>🔒</span>
            </div>
            <div style={{ fontSize:12, color:"#495057", lineHeight:1.4 }}>Recurso disponível no plano <strong style={{ color:"#1aae9e" }}>Controla</strong>. Entre em contato para fazer upgrade.</div>
          </div>
        ) : (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <Field label="Anexo Comprovante de Pagamento">
            {anexoComp && !newFileComp ? (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", border:"1px solid #d4edda", borderRadius:6, background:"#f0faf3", fontSize:12 }}>
                <span style={{ fontSize:16 }}>✅</span>
                <span style={{ flex:1, fontWeight:600, color:"#1a7a38" }}>{anexoComp.nome_original}</span>
                <button type="button" onClick={() => downloadAuth(anexoComp.url, anexoComp.nome_original)} style={{ background:"none", border:"none", cursor:"pointer", color:"#1aae9e", fontWeight:600, marginRight:6 }}>Ver</button>
                <button type="button" onClick={() => removeAnexo(anexoComp, setAnexoComp)} style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontWeight:700, fontSize:13 }}>✕</button>
              </div>
            ) : (
              <label style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", border:"2px dashed #d0d0d0", borderRadius:6, cursor:"pointer", background:"#fafafa", fontSize:12, color:"#666", transition:"border-color .2s" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#1aae9e"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#d0d0d0"}>
                <span style={{ fontSize:18 }}>📎</span>
                <span>{newFileComp ? newFileComp.name : "Clique para anexar"}</span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display:"none" }} onChange={e => { if(e.target.files[0]) setNewFileComp(e.target.files[0]); }} />
              </label>
            )}
          </Field>
          <Field label="Anexo NF (Nota Fiscal)">
            {anexoNF && !newFileNF ? (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", border:"1px solid #d4edda", borderRadius:6, background:"#f0faf3", fontSize:12 }}>
                <span style={{ fontSize:16 }}>✅</span>
                <span style={{ flex:1, fontWeight:600, color:"#1a7a38" }}>{anexoNF.nome_original}</span>
                <button type="button" onClick={() => downloadAuth(anexoNF.url, anexoNF.nome_original)} style={{ background:"none", border:"none", cursor:"pointer", color:"#1aae9e", fontWeight:600, marginRight:6 }}>Ver</button>
                <button type="button" onClick={() => removeAnexo(anexoNF, setAnexoNF)} style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontWeight:700, fontSize:13 }}>✕</button>
              </div>
            ) : (
              <label style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 14px", border:"2px dashed #d0d0d0", borderRadius:6, cursor:"pointer", background:"#fafafa", fontSize:12, color:"#666", transition:"border-color .2s" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#1aae9e"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#d0d0d0"}>
                <span style={{ fontSize:18 }}>📎</span>
                <span>{newFileNF ? newFileNF.name : "Clique para anexar"}</span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.xml" style={{ display:"none" }} onChange={e => { if(e.target.files[0]) setNewFileNF(e.target.files[0]); }} />
              </label>
            )}
          </Field>
        </div>
        )}

        <div style={{ marginTop:10, padding:"8px 12px", background:"#f0f7ff", borderRadius:4, fontSize:11, color:"#1aae9e" }}>
          💡 Campos com borda <span style={{ color:"#1aae9e", fontWeight:700 }}>azul</span> são obrigatórios.
        </div>
      </div>
    </LargeModal>
    {mapModal && <MapConfirmModal address={[form.logradouro, form.numero, form.bairro, form.cidade, form.uf].filter(Boolean).join(", ")} cidadeHint={form.cidade} ufHint={form.uf} onClose={() => setMapModal(false)} onConfirm={(coords, displayAddr, addrDetails) => { set("lat", coords.lat); set("lng", coords.lng); if (addrDetails) { if (addrDetails.cep) set("cep", addrDetails.cep.replace(/\D/g,"").replace(/^(\d{5})(\d{3})$/,"$1-$2")); if (addrDetails.logradouro) { const parts = addrDetails.logradouro.split(","); set("logradouro", parts[0].trim()); if (parts[1]) set("numero", parts[1].trim()); } if (addrDetails.bairro) set("bairro", addrDetails.bairro); if (addrDetails.cidade) set("cidade", addrDetails.cidade); if (addrDetails.uf) set("uf", addrDetails.uf); } }} />}
    </>
  );
}

// ─── Modal de Cancelamento ────────────────────────────────────────────────────
function CancelLocacaoModal({ locacao, onClose, onConfirm }) {
  const [motivo, setMotivo] = useState("");
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:8, width:440, maxHeight:"90vh", overflow:"auto", boxShadow:"0 12px 40px rgba(0,0,0,.25)" }}>
        <div style={{ padding:"18px 22px", borderBottom:"1px solid #f0f0f0", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>❌</span>
          <div><div style={{ fontWeight:700, fontSize:15, color:"#1a1a1a", fontFamily:FONT }}>Cancelar Locação #{locacao.id}</div><div style={{ fontSize:11, color:"#888" }}>Informe o motivo do cancelamento</div></div>
        </div>
        <div style={{ padding:"18px 22px" }}>
          <div style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:6 }}>Motivo do Cancelamento *</div>
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex: Cliente desistiu, erro no pedido, endereço incorreto..." style={{ width:"100%", minHeight:90, padding:"10px 12px", borderRadius:4, border:"1px solid #ddd", fontSize:13, fontFamily:FONT, resize:"vertical", boxSizing:"border-box" }} autoFocus />
          <div style={{ display:"flex", gap:10, marginTop:16, justifyContent:"flex-end" }}>
            <button onClick={onClose} style={{ padding:"9px 20px", borderRadius:4, border:"1px solid #ddd", background:"#fff", fontSize:13, cursor:"pointer", fontFamily:FONT, color:"#555" }}>Voltar</button>
            <button onClick={() => { if(!motivo.trim()) { alert("Informe o motivo do cancelamento."); return; } onConfirm(motivo.trim()); }} style={{ padding:"9px 20px", borderRadius:4, border:"none", background:"#e74c3c", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>Confirmar Cancelamento</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Colunas extras da lista de Locações (botão "Visualização") ──────────────
// As colunas padrão por aba continuam fixas; o usuário só ADICIONA estas.
// render(l, ctx) testa camelCase e snake_case. Valor vazio → "—".
// ctx = { clientes } (opcional) — usado por colunas que dependem do cadastro do cliente.
const _locColVal = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));
const _locClienteDe = (l, ctx) => {
  const lista = (ctx && ctx.clientes) || [];
  const cid = l.clienteId || l.cliente_id;
  let c = cid ? lista.find(x => String(x.id) === String(cid)) : null;
  if (!c && l.cliente) c = lista.find(x => x.nome === l.cliente) || null;
  return c;
};
const LOC_COLUNAS_EXTRAS = [
  { key:"obs",            label:"Observação",          render:l => _locColVal(l.obs || l.observacoes) },
  { key:"contato",        label:"Contato",             render:l => _locColVal(l.contatoNome || l.contato_nome) },
  { key:"telefone",       label:"Telefone",            render:l => { const t = l.contatoTel || l.contato_tel; return t ? formatPhone(t) : "—"; } },
  { key:"email",          label:"Email",               render:l => _locColVal(l.contatoEmail || l.contato_email) },
  { key:"mtr",            label:"MTR/CTR",             render:l => _locColVal(l.mtr) },
  { key:"motorista",      label:"Motorista",           render:l => _locColVal(l.motorista) },
  { key:"motoristaRet",   label:"Motorista retirada",  render:l => _locColVal(l.motoristaRetirada || l.motorista_retirada) },
  { key:"tipoCacamba",    label:"Tipo de caçamba",     render:l => _locColVal(l.tipoCacamba || l.tipo_cacamba) },
  { key:"pagamento",      label:"Forma de pagamento",  render:l => _locColVal(l.pagamento) },
  { key:"dataPagamento",  label:"Data pagamento",      render:l => { const d = l.dataPagamento || l.data_pagamento; return d ? fmtDate(d) : "—"; } },
  { key:"dataSolic",      label:"Data solicitação",    render:l => { const d = l.dataSolicitacao || l.data_solicitacao; return d ? fmtDate(d) : "—"; } },
  { key:"horaEntrega",    label:"Hora entrega",        render:l => _locColVal(l.horaEntrega || l.hora_entrega) },
  { key:"bairro",         label:"Bairro",              render:l => _locColVal(l.bairro || l.end_bairro || (l.endereco && typeof l.endereco === "object" ? l.endereco.bairro : "")) },
  { key:"cidadeUf",       label:"Cidade/UF",           render:l => { const c = l.cidade || l.end_cidade || (l.endereco && typeof l.endereco === "object" ? l.endereco.cidade : ""); const u = l.uf || l.end_uf || (l.endereco && typeof l.endereco === "object" ? l.endereco.uf : ""); return c ? c + (u ? "/" + u : "") : (u || "—"); } },
  { key:"cep",            label:"CEP",                 render:l => _locColVal(l.cep || l.end_cep || (l.endereco && typeof l.endereco === "object" ? l.endereco.cep : "")) },
  { key:"diariaExtra",    label:"Diária extra",        render:l => { const v = parseFloat(l.diaria_extra != null ? l.diaria_extra : l.diariaExtra); return v ? fmt(v) : "—"; } },
  { key:"valorExtras",    label:"Valor extras",        render:l => { const v = parseFloat(l.valor_extras != null ? l.valor_extras : l.valorExtras); return v ? fmt(v) : "—"; } },
  { key:"comissao",       label:"Comissão",            render:l => { const v = parseFloat(l.comissao); return v ? fmt(v) : "—"; } },
  { key:"criadoEm",       label:"Criado em",           render:l => l.created_at ? fmtDate(String(l.created_at).replace(" ", "T")) : "—" },
  { key:"endFaturamento", label:"Endereço de faturamento", render:(l, ctx) => { const c = _locClienteDe(l, ctx); return _locColVal(c ? fmtEnderecoFaturamento(c) : ""); } },
];
// Colunas padrão (fixas) por aba — usadas só para exibir no modal de Visualização
const LOC_COLUNAS_PADRAO = (st) => (
  st === "Colocada" ? ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Colocada por","Colocada em","Retirar em","Preço Total","Pago?"] :
  st === "Para Colocar" ? ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Colocar em","Retirar em","Preço Total","Pago?"] :
  st === "Para Retirar" ? ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Colocada por","Pedido em","Retirar em","Preço Total","Pago?"] :
  st === "Para Trocar" ? ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Colocada por","Pedido em","Trocar em","Preço Total","Pago?","Pago Nova?"] :
  (st === "Encerrada" || st === "Para Cobrar") ? ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Colocada por","Retirada por","Colocada em","Retirada em","Preço Total","Pago?"] :
  ["Nº Locação","Cliente / Contato / Endereço","Caçamba","Pedido em","Preço Total","Pago?"]
);
const locColunasStorageKey = () => {
  let uid = null;
  try { const s = getSession(); uid = s && s.id; } catch(_) {}
  if (!uid) { try { const raw = sessionStorage.getItem("controla_session"); const s = raw ? JSON.parse(raw) : null; uid = s && s.id; } catch(_) {} }
  return uid ? "loc.colunasExtras." + uid : "loc.colunasExtras";
};

function Locacoes({ locacoes, setLocacoes, clientes, setClientes, motoristas, cacambas, tiposCacamba, frota, addToast, apiFetch, itinerarios, setItinerarios, initFilter, clearInitFilter, empresaNome }) {
  const [stFilter, setStFilter]   = useState(initFilter || "Para Colocar");
  useEffect(() => { if (initFilter) { setStFilter(initFilter); if (clearInitFilter) clearInitFilter(); } }, [initFilter]);
  const [modal, setModal]         = useState(false);
  const [acaoOpen, setAcaoOpen]   = useState(false);
  const [colocadaModal, setColocadaModal] = useState(false);
  const [retiradaModal, setRetiradaModal] = useState(false);
  const [trocaModal, setTrocaModal]       = useState(false);
  const [confirmarTrocaModal, setConfirmarTrocaModal] = useState(false);
  const [confirmarRetiradaModal, setConfirmarRetiradaModal] = useState(false);
  const [pagamentoModal, setPagamentoModal] = useState(false);
  const [editModal, setEditModal]         = useState(null);
  const [selecionadas, setSelecionadas]   = useState([]);
  const [detailLoc, setDetailLoc]         = useState(null);
  const [empresaCfg, setEmpresaCfg]       = useState(null);
  const [cancelModal, setCancelModal]     = useState(null);
  const [successModal, setSuccessModal]   = useState(null); // { id: locacaoId }

  // Integração Itinerário: marca paradas como Atendido quando locações viram Colocada
  const marcarItinerarioColocada = (locacaoIds) => {
    if (!locacaoIds?.length || !itinerarios?.length) return;
    // Atualizar estado local dos itinerários
    setItinerarios(prev => prev.map(it => {
      if (!it.paradas?.length) return it;
      let changed = false;
      const paradas = it.paradas.map(p => {
        if (locacaoIds.includes(p.locId) && p.status !== "Atendido") {
          changed = true;
          return { ...p, status: "Atendido" };
        }
        return p;
      });
      if (!changed) return it;
      const atendidos = paradas.filter(p => p.status === "Atendido").length;
      const pendentes = paradas.length - atendidos;
      const status = pendentes === 0 ? "Concluído" : atendidos === 0 ? "Em Aberto" : "Em Andamento";
      return { ...it, paradas, atendidos, pendentes, total: paradas.length, status };
    }));
    // Persistir no backend
    apiFetch("/api/itinerarios/marcar-colocada", { method: "PATCH", body: { locacaoIds } }).catch(() => {});
  };
  const [alterarPagoModal, setAlterarPagoModal] = useState(false);
  const [prorrogarModal, setProrrogarModal] = useState(null);
  // Patch local (aditivo) após prorrogar/aplicar diária: atualiza só os campos afetados.
  // Grava camelCase E snake_case (objetos no estado carregam ambas as variantes) e
  // sincroniza também o modal de detalhes se estiver aberto (sem F5).
  const patchLocacaoExtras = (upd) => {
    if (!upd || !upd.id) return;
    const novaRetirada = upd.data_retirada || upd.dataRetirada || null;
    const aplicarPatch = (x) => ({
      ...x,
      valor: parseFloat(upd.valor) || 0,
      valor_extras: upd.valor_extras,
      valorExtras: upd.valor_extras,
      diaria_extra: (upd.diaria_extra !== undefined) ? upd.diaria_extra : x.diaria_extra,
      diariaExtra: (upd.diaria_extra !== undefined) ? upd.diaria_extra : (x.diariaExtra != null ? x.diariaExtra : x.diaria_extra),
      data_retirada_original: (upd.data_retirada_original !== undefined) ? upd.data_retirada_original : x.data_retirada_original,
      ...(novaRetirada ? { dataRetirada: novaRetirada, data_retirada: novaRetirada, dataRetiraPrevista: novaRetirada } : {}),
    });
    setLocacoes(p => p.map(x => String(x.id) === String(upd.id) ? aplicarPatch(x) : x));
    setDetailLoc(prev => (prev && String(prev.id) === String(upd.id)) ? aplicarPatch(prev) : prev);
  };
  const [searchLoc, setSearchLoc]         = useState("");
  // Ordenação das locações: padrão operacional (atrasadas/hoje primeiro)
  const [sortMode, setSortMode] = useState(() => { try { return localStorage.getItem("loc.sortMode") || "operacional"; } catch(_) { return "operacional"; } });
  useEffect(() => { try { localStorage.setItem("loc.sortMode", sortMode); } catch(_) {} }, [sortMode]);
  // Colunas extras da lista (botão "Visualização") — persistidas por usuário em localStorage
  const [colunasExtras, setColunasExtras] = useState(() => {
    try {
      const raw = localStorage.getItem(locColunasStorageKey());
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(k => LOC_COLUNAS_EXTRAS.some(c => c.key === k)) : [];
    } catch(_) { return []; }
  });
  useEffect(() => { try { localStorage.setItem(locColunasStorageKey(), JSON.stringify(colunasExtras)); } catch(_) {} }, [colunasExtras]);
  const [visualizacaoModal, setVisualizacaoModal] = useState(false);
  const extrasAtivas = LOC_COLUNAS_EXTRAS.filter(c => colunasExtras.includes(c.key));
  // Modal "Enviar link de assinatura" (menu hambúrguer da linha)
  const [assinaturaModal, setAssinaturaModal] = useState(null); // { loc, nome, telefone, link, enviando }
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [whatsAppCustomModal, setWhatsAppCustomModal] = useState(null);
  const [whatsAppCustomMsg, setWhatsAppCustomMsg] = useState("");
  const [showTutorial, setShowTutorial] = useState(() => !localStorage.getItem("controla_tutorial_locacoes"));

  // Registrar callback global para WhatsApp personalizado (chamado pelo LocacaoRowMenu)
  useEffect(() => {
    window.__openWhatsAppCustom = (loc) => {
      setWhatsAppCustomModal(loc);
      setWhatsAppCustomMsg("");
    };
    return () => { delete window.__openWhatsAppCustom; };
  }, []);

  useEffect(() => { apiFetch("/api/config").then(d => setEmpresaCfg(d)).catch(() => {}); }, []);

  useEffect(() => { setCurrentPage(1); }, [stFilter, searchLoc, itemsPerPage]);

  const handleRecibo = async (loc) => {
    try {
      const API = process.env.REACT_APP_API || "http://localhost:3001";
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(`${API}/api/recibo/${loc.id}`, {
        headers: { Authorization: "Bearer " + token }
      });
      if (!res.ok) {
        let msg = "Erro " + res.status;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch(_){}
        throw new Error(msg);
      }
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        throw new Error("Resposta inesperada do servidor (não é PDF)");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "Recibo_Locacao_" + loc.id + ".pdf"; a.target = "_blank";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch(e) {
      // Sem fallback silencioso — melhor avisar do que gerar um modelo errado.
      // O modelo antigo (jsPDF) gerava layout diferente do oficial e enganava o cliente.
      alert(
        "Não foi possível gerar o recibo.\n\n" +
        "Motivo: " + (e.message || "erro desconhecido") + "\n\n" +
        "Por favor, avise o suporte para que o recibo desta locação seja gerado manualmente."
      );
    }
  };

  const handleMtr = async (loc) => {
    let cfg = empresaCfg;
    if (!cfg) { try { cfg = await apiFetch("/api/config"); setEmpresaCfg(cfg); } catch(e){} }
    gerarMtrPDF(loc, cfg, cacambas, tiposCacamba);
  };

  const filtered = locacoes.filter(l => {
    if (l.status !== stFilter) return false;
    // Esconder locações filhas de troca da aba "Para Colocar" — elas aparecem na aba "Para Trocar"
    if (stFilter === "Para Colocar" && l.obs && typeof l.obs === "string" && (l.obs.toLowerCase().includes("troca da locação") || l.obs.toLowerCase().includes("troca da locacao"))) {
      // Só esconder se a locação pai ainda estiver em "Para Trocar"
      const parentMatch = l.obs.match(/#(\d+)/);
      if (parentMatch) {
        const parentLoc = locacoes.find(p => String(p.id) === parentMatch[1] && p.status === "Para Trocar");
        if (parentLoc) return false;
      }
    }
    if (!searchLoc.trim()) return true;
    const s = norm(searchLoc);
    return norm(l.cliente).includes(s) || norm(l.logradouro||l.end_logradouro).includes(s) || norm(l.bairro||l.end_bairro).includes(s) || norm(l.cidade||l.end_cidade).includes(s) || norm(typeof l.endereco === "string" ? l.endereco : "").includes(s) || norm(l.cacamba).includes(s) || String(l.id).includes(s) || String(l.contatoTel||l.contato_tel||"").includes(s) || norm(l.contatoNome||l.contato_nome).includes(s);
  });
  // Funções de ordenação (selecionáveis no dropdown da página).
  // - operacional (padrão): atrasadas/hoje primeiro. Para "Para Colocar" usa dataEntrega;
  //   nos demais status, usa a data de retirada prevista. Vazias caem ao fim.
  // - recente: mais recentes por data de criação.
  // - antigo: mais antigos por data de criação.
  const SORT_FNS = {
    operacional: (a, b) => {
      const getDataRef = (l) => {
        if (l.status === "Para Colocar") return l.dataEntrega || l.data_entrega || "";
        return l.dataRetiraPrevista || l.dataRetirada || l.data_retirada_prevista || l.dataEntrega || "";
      };
      const da = String(getDataRef(a) || "9999-12-31").slice(0,10);
      const db = String(getDataRef(b) || "9999-12-31").slice(0,10);
      return da.localeCompare(db);
    },
    recente: ordCriacaoDesc,
    antigo:  (a, b) => -ordCriacaoDesc(a, b),
  };
  filtered.sort(SORT_FNS[sortMode] || SORT_FNS.operacional);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedLocs = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const addLocacao = async (loc) => {
    try {
      const nova = await apiFetch("/api/locacoes", { method: "POST", body: loc });
      const e = nova.endereco || {};
      const cli = clientes.find(c => c.id === nova.cliente_id);
      setLocacoes(p => [...p, { ...nova, cpfCnpj: cli?(cli.cpf_cnpj||cli.cpfCnpj||''):'', dataSolicitacao: nova.data_solicitacao||'', dataEntrega: nova.data_entrega||'', dataRetirada: nova.data_retirada||'', cacamba: nova.cacamba_codigo||'', cliente: nova.cliente_nome||nova.cliente||'', logradouro: e.logradouro||'', numero: e.numero||'', bairro: e.bairro||'', cidade: e.cidade||'', uf: e.uf||'', pago: !!nova.pago, valor: parseFloat(nova.valor)||0, status: nova.status||'Para Colocar', previsao: nova.data_entrega||'—' }]);
      setSuccessModal({ id: nova.id });
      setTimeout(() => reloadLocacoes(), 600);
    } catch(err) { if(addToast) addToast("Erro: "+err.message, "error"); }
  };

  const toggleSel = (id) => setSelecionadas(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelecionadas(selecionadas.length === filtered.length ? [] : filtered.map(l => l.id));

  const confirmarColocada = (form, rows) => {
    setLocacoes(p => p.map(l => {
      if (!selecionadas.includes(l.id)) return l;
      const row = rows.find(r => r.locId === l.id) || {};
      return { ...l, status:"Colocada", dataColocacaoReal: form.dataColocacao, dataRetiraPrevista: form.dataRetirada, motorista: form.motorista, veiculo: form.veiculo, comissao: form.comissao, pago: form.pago, dataPagamento: form.pago ? (form.dataPagamento||null) : null, mtr: row.mtr||l.mtr, cacamba: row.cacamba||l.cacamba };
    }));
    // Salvar status no backend
    for (const id of selecionadas) {
      const row = rows.find(r => r.locId === id) || {};
      const patchBody = { status:"Colocada", motorista: form.motorista||"", dataRetiraPrevista: form.dataRetirada||null, dataColocacaoReal: form.dataColocacao||null, comissao: form.comissao||null };
      if (row.cacamba) patchBody.cacamba = row.cacamba;
      if (row.mtr) patchBody.mtr = row.mtr;
      if (form.pago) { patchBody.pago = 1; patchBody.dataPagamento = form.dataPagamento||null; }
      apiFetch("/api/locacoes/"+id+"/status", { method:"PATCH", body: patchBody }).catch(()=>{});
    }
    if (addToast) addToast("Locação movida para Colocada ✅", selecionadas.length+" locação(ões) atualizada(s)", "🟢", "#2ecc71");
    // Log histórico
    for (const id of selecionadas) {
      const rowHist = rows.find(r => r.locId === id) || {};
      apiFetch("/api/historico/" + id, { method:"POST", body:{ acao:"Status → Colocada", detalhes:"Motorista: "+(form.motorista||"—")+", Data Retirada Prev: "+(form.dataRetirada||"—")+(rowHist.cacamba?", Caçamba: "+rowHist.cacamba:"") } }).catch(()=>{});
    }
    // Integração Itinerário: marcar paradas como Atendido
    marcarItinerarioColocada([...selecionadas]);
    setSelecionadas([]);
    setTimeout(() => reloadLocacoes(), 800);
  };

  const confirmarRetirada = (form) => {
    setLocacoes(p => p.map(l => {
      if (!selecionadas.includes(l.id)) return l;
      return { ...l, status:"Para Retirar", dataPedidoRetirada: form.dataPedido, dataRetiraPrevista: form.dataPrevista };
    }));
    if (addToast) addToast("Locação movida para Retirada 📤", selecionadas.length+" locação(ões)", "", "#9b59b6");
    for (const id of selecionadas) {
      apiFetch("/api/historico/" + id, { method:"POST", body:{ acao:"Status → Para Retirar", detalhes:"Data Pedido: "+(form.dataPedido||"—")+", Data Prevista: "+(form.dataPrevista||"—") } }).catch(()=>{});
      apiFetch("/api/locacoes/" + id + "/status", { method:"PATCH", body:{ status:"Para Retirar", dataRetiraPrevista: form.dataPrevista||null, dataPedidoRetirada: form.dataPedido||null } }).catch(()=>{});
    }
    setSelecionadas([]);
    setTimeout(() => reloadLocacoes(), 800);
  };

  const confirmarRetiradaReal = (form, rows) => {
    setLocacoes(p => p.map(l => {
      if (!selecionadas.includes(l.id)) return l;
      const jaPago = form.pago || l.pago;
      const novoStatus = jaPago ? "Encerrada" : "Para Cobrar";
      return { ...l, status: novoStatus, dataRetiradaReal: form.dataRetirada, motoristaRetirada: form.motorista, pago: jaPago, dataPagamento: form.pago ? (form.dataPagamento||l.dataPagamento||l.data_pagamento||null) : l.dataPagamento };
    }));
    for (const id of selecionadas) {
      const loc = locacoes.find(l => l.id === id);
      const jaPago = form.pago || loc?.pago;
      const novoStatus = jaPago ? "Encerrada" : "Para Cobrar";
      const patchBody = { status: novoStatus, motoristaRetirada: form.motorista||"", dataRetiradaReal: form.dataRetirada||null, comissaoRetirada: form.comissao||null, destinoRetirada: form.destinoRetirada||null, aterroId: form.aterroId||null };
      if (form.pago) { patchBody.pago = 1; patchBody.dataPagamento = form.dataPagamento||null; }
      apiFetch("/api/locacoes/" + id + "/status", { method:"PATCH", body: patchBody }).catch(()=>{});
      apiFetch("/api/historico/" + id, { method:"POST", body:{ acao:"Status → " + novoStatus, detalhes:"Retirada confirmada. Motorista: "+(form.motorista||"—")+", Data: "+(form.dataRetirada||"—")+(jaPago?" | Pago: Sim":" | Não pago → Para Cobrar") } }).catch(()=>{});
    }
    if (addToast) addToast("Locação retirada com sucesso ✅", selecionadas.length+" locação(ões)", "", "#27ae60");
    setSelecionadas([]);
    setTimeout(() => reloadLocacoes(), 800);
  };

  const reloadLocacoes = async () => {
    try {
      const freshLocs = await apiFetch("/api/locacoes");
      setLocacoes((freshLocs || []).map(r => ({
        ...r,
        dataEntrega: r.data_entrega || r.dataEntrega || '',
        dataRetirada: r.data_retirada || r.dataRetirada || '',
        dataRetiraPrevista: r.data_retirada || r.dataRetiraPrevista || '',
        dataPagamento: r.data_pagamento || r.dataPagamento || '',
        dataRetiradaReal: r.data_retirada_real || r.dataRetiradaReal || '',
        dataColocacaoReal: r.data_colocacao_real || r.dataColocacaoReal || '',
        dataSolicitacao: r.data_solicitacao || r.dataSolicitacao || '',
        dataTrocaPrevista: r.data_troca_prevista || r.dataTrocaPrevista || '',
        dataPedidoTroca: r.data_pedido_troca || r.dataPedidoTroca || '',
        dataPedidoRetirada: r.data_pedido_retirada || r.dataPedidoRetirada || '',
        motoristaRetirada: r.motorista_retirada || r.motoristaRetirada || '',
        prazoPagamento: r.prazo_pagamento || r.prazoPagamento || '',
        cacamba: r.cacamba_codigo || r.cacamba || '',
        cliente: r.cliente_nome || r.cliente || '',
        tipoCacamba: r.tipo_cacamba || r.tipoCacamba || '',
        contatoTel: r.contato_tel || r.contatoTel || '',
        contatoNome: r.contato_nome || r.contatoNome || '',
        logradouro: r.logradouro || (r.endereco?.logradouro) || '',
        numero: r.numero || (r.endereco?.numero) || '',
        bairro: r.bairro || (r.endereco?.bairro) || '',
        cidade: r.cidade || (r.endereco?.cidade) || '',
        uf: r.uf || (r.endereco?.uf) || '',
        pago: !!r.pago,
        valor: parseFloat(r.valor) || 0,
        comissao: r.comissao != null ? parseFloat(r.comissao) : null,
        comissaoRetirada: r.comissao_retirada != null ? parseFloat(r.comissao_retirada) : null,
        status: r.status || 'Para Colocar',
        endereco: r.endereco || {},
        obs: r.observacoes || r.obs || '',
        residuos: r.residuos || null,
      })));
    } catch(e) {}
  };

  const confirmarTroca = async (form, rows) => {
    const ids = [...selecionadas];
    setSelecionadas([]);

    // ★ IMEDIATO: Atualizar estado local ANTES de qualquer chamada API
    // Isso garante que a UI mova a locação para "Para Trocar" instantaneamente
    setLocacoes(prev => prev.map(l =>
      ids.map(String).includes(String(l.id)) ? { ...l, status: "Para Trocar" } : l
    ));

    for (const id of ids) {
      const loc = locacoes.find(l => String(l.id) === String(id));
      if (!loc) continue;
      const row = rows.find(r => String(r.locId) === String(id)) || {};
      // 1. Salvar no banco: locação original → Para Trocar (status PRIMEIRO, datas depois)
      try {
        await apiFetch("/api/locacoes/" + id + "/status", { method:"PATCH", body:{ status:"Para Trocar" } });
        console.log("[TROCA] PATCH status OK for", id);
      } catch(patchErr) {
        console.error("[TROCA] PATCH status FALHOU para", id, ":", patchErr);
      }
      // Datas de troca separadas (pode falhar se migration não rodou, sem afetar status)
      try {
        await apiFetch("/api/locacoes/" + id + "/status", { method:"PATCH", body:{ dataTrocaPrevista: form.dataPrevistaTroca || null, dataPedidoTroca: form.dataSolicitacao || hojeLocal(), dataRetiraPrevista: form.dataPrevistaRetirada || null } });
      } catch(e) { console.warn("[TROCA] datas troca não salvas (migration pendente?):", e.message); }
      apiFetch("/api/historico/" + id, { method:"POST", body:{ acao:"Status → Para Trocar", detalhes:"Data Prev. Troca: "+(form.dataPrevistaTroca||"—")+", Data Prev. Retirada: "+(form.dataPrevistaRetirada||"—") } }).catch(()=>{});
      // 2. Criar nova locação filha no banco
      try {
        const novaLocData = {
          cliente: loc.cliente || "", clienteId: loc.clienteId || loc.cliente_id || null,
          endereco: loc.endereco || { logradouro: loc.logradouro, numero: loc.numero, bairro: loc.bairro, cidade: loc.cidade, uf: loc.uf, cep: loc.cep },
          cacamba: row.cacamba || loc.cacamba || "", tipoCacamba: loc.tipoCacamba || loc.tipo_cacamba || "",
          operacao: "TROCA", motorista: "",
          dataSolicitacao: form.dataSolicitacao || hojeLocal(),
          dataEntrega: form.dataPrevistaTroca || "", dataRetirada: form.dataPrevistaRetirada || "",
          valor: parseFloat(row.preco) || loc.valor || 0, pagamento: loc.pagamento || "dinheiro",
          prazoPagamento: loc.prazoPagamento || loc.prazo_pagamento || "",
          obs: "Gerada automaticamente pela troca da locação #" + id,
          status: "Para Colocar", pago: false, mtr: row.mtr || "",
          contatoNome: loc.contatoNome || loc.contato_nome || "",
          contatoTel: loc.contatoTel || loc.contato_tel || loc.telefone || "",
          contatoEmail: loc.contatoEmail || loc.contato_email || "",
          lat: loc.lat || null, lng: loc.lng || null,
        };
        const novaLoc = await apiFetch("/api/locacoes", { method: "POST", body: novaLocData });
        if (novaLoc?.id) {
          apiFetch("/api/historico/" + novaLoc.id, { method:"POST", body:{ acao:"Locação pré-criada (Troca)", detalhes:"Nova locação pré-criada para troca da #" + id } }).catch(()=>{});
        }
      } catch(e) { console.error("[TROCA-SOLICITAR] Erro ao pré-criar locação filha para #" + id + ":", e.message, e); }
    }
    // 3. Recarregar TUDO do servidor — fonte única de verdade
    try {
      const freshLocs = await apiFetch("/api/locacoes");
      console.log("[TROCA] Reload: total=", freshLocs?.length, "status das IDs trocadas:", ids.map(id => { const f = (freshLocs||[]).find(r => String(r.id)===String(id)); return id+"=>"+(f?.status||"NOT FOUND"); }));
      setLocacoes((freshLocs || []).map(r => ({
        ...r,
        dataEntrega: r.data_entrega || r.dataEntrega || '',
        dataRetirada: r.data_retirada || r.dataRetirada || '',
        dataRetiraPrevista: r.data_retirada || r.dataRetiraPrevista || '',
        dataPagamento: r.data_pagamento || r.dataPagamento || '',
        dataRetiradaReal: r.data_retirada_real || r.dataRetiradaReal || '',
        dataColocacaoReal: r.data_colocacao_real || r.dataColocacaoReal || '',
        dataSolicitacao: r.data_solicitacao || r.dataSolicitacao || '',
        dataTrocaPrevista: r.data_troca_prevista || r.dataTrocaPrevista || '',
        dataPedidoTroca: r.data_pedido_troca || r.dataPedidoTroca || '',
        dataPedidoRetirada: r.data_pedido_retirada || r.dataPedidoRetirada || '',
        motoristaRetirada: r.motorista_retirada || r.motoristaRetirada || '',
        prazoPagamento: r.prazo_pagamento || r.prazoPagamento || '',
        cacamba: r.cacamba_codigo || r.cacamba || '',
        cliente: r.cliente_nome || r.cliente || '',
        tipoCacamba: r.tipo_cacamba || r.tipoCacamba || '',
        contatoTel: r.contato_tel || r.contatoTel || '',
        contatoNome: r.contato_nome || r.contatoNome || '',
        logradouro: r.logradouro || (r.endereco?.logradouro) || '',
        numero: r.numero || (r.endereco?.numero) || '',
        bairro: r.bairro || (r.endereco?.bairro) || '',
        cidade: r.cidade || (r.endereco?.cidade) || '',
        uf: r.uf || (r.endereco?.uf) || '',
        pago: !!r.pago,
        valor: parseFloat(r.valor) || 0,
        status: r.status || 'Para Colocar',
        endereco: r.endereco || {},
        obs: r.observacoes || r.obs || '',
      })));
    } catch(reloadErr) {
      console.error("[TROCA] Reload falhou:", reloadErr);
    }
    if (addToast) addToast("Locação movida para Troca 🔃", ids.length+" locação(ões)", "", "#f39c12");
  };

  // Confirmar Troca Real: old locação → "Para Cobrar", nova locação criada ou atualizada
  const confirmarTrocaReal = async (form, rows) => {
    // ★ IMPORTANTE: copiar IDs ANTES de qualquer operação async (closure pode mudar)
    const ids = [...selecionadas];
    const locsSnapshot = [...locacoes];
    let erros = [];
    console.log("[TROCA-REAL] Iniciando para IDs:", ids, "rows:", rows);

    for (const id of ids) {
      const locOriginal = locsSnapshot.find(l => String(l.id) === String(id));
      if (!locOriginal) {
        console.warn("[TROCA-REAL] locOriginal NÃO ENCONTRADA para id=" + id);
        continue;
      }
      console.log("[TROCA-REAL] Processando id=" + id, "cliente=" + locOriginal.cliente, "cacamba=" + locOriginal.cacamba);

      // 1. Old locação → Para Cobrar (ou Encerrada se paga)
      try {
        const novoStatus = locOriginal.pago ? "Encerrada" : "Para Cobrar";
        await apiFetch("/api/locacoes/" + id + "/status", { method: "PATCH", body: { status: novoStatus, motoristaRetirada: form.motorista || "", dataRetiradaReal: form.dataTroca || null, comissaoRetirada: form.comissaoRetirada || null } });
        console.log("[TROCA-REAL] PATCH original " + id + " → " + novoStatus + " OK");
        apiFetch("/api/historico/" + id, { method: "POST", body: { acao: "Troca realizada → " + novoStatus, detalhes: "Motorista: " + (form.motorista || "—") + ", Data troca: " + (form.dataTroca || "—") } }).catch(() => {});
      } catch(patchErr) {
        console.error("[TROCA-REAL] PATCH original FALHOU:", patchErr);
        erros.push("Erro ao atualizar locação #" + id + ": " + patchErr.message);
        continue;
      }

      const row = rows.find(r => String(r.locId) === String(id)) || {};
      // Verificar se já existe locação pré-criada (filha da troca)
      const childExistente = locsSnapshot.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o #" + id) || cl.obs.toLowerCase().includes("troca da locacao #" + id) || cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o " + id) || cl.obs.toLowerCase().includes("troca da locacao " + id)));
      console.log("[TROCA-REAL] childExistente para id=" + id + ":", childExistente ? "SIM #" + childExistente.id : "NÃO — vai criar nova");

      if (childExistente) {
        // 2a. Atualizar locação pré-criada → Colocada com motorista
        try {
          const childBody = { status: "Colocada", motorista: form.motorista || "", dataColocacaoReal: form.dataTroca || null, dataRetiraPrevista: form.dataPrevistaRetirada || null, comissao: form.comissaoColocacao || null };
          if (row.cacambaColocada) childBody.cacamba = row.cacambaColocada;
          if (row.mtrColocada) childBody.mtr = row.mtrColocada;
          await apiFetch("/api/locacoes/" + childExistente.id + "/status", { method: "PATCH", body: childBody });
          console.log("[TROCA-REAL] PATCH child " + childExistente.id + " → Colocada OK");
          apiFetch("/api/historico/" + childExistente.id, { method: "POST", body: { acao: "Troca confirmada → Colocada", detalhes: "Motorista: " + (form.motorista || "—") + ", Data troca: " + (form.dataTroca || "—") } }).catch(() => {});
        } catch(childPatchErr) {
          console.error("[TROCA-REAL] PATCH child FALHOU:", childPatchErr);
          erros.push("Erro ao atualizar filha #" + childExistente.id + ": " + childPatchErr.message);
        }
      } else {
        // 2b. Criar nova locação (não existia pré-criada — caso de importação ou troca direta)
        const postBody = {
          clienteId: locOriginal.clienteId || locOriginal.cliente_id || null,
          cliente: locOriginal.cliente || locOriginal.cliente_nome || "",
          endereco: locOriginal.endereco && typeof locOriginal.endereco === "object" && Object.keys(locOriginal.endereco).some(k => locOriginal.endereco[k])
            ? locOriginal.endereco
            : { logradouro: locOriginal.logradouro || locOriginal.end_logradouro || "", numero: locOriginal.numero || locOriginal.end_numero || "", complemento: locOriginal.complemento || locOriginal.end_complemento || "", bairro: locOriginal.bairro || locOriginal.end_bairro || "", cidade: locOriginal.cidade || locOriginal.end_cidade || "", uf: locOriginal.uf || locOriginal.end_uf || "", cep: locOriginal.cep || locOriginal.end_cep || "" },
          cacamba: row.cacambaColocada || locOriginal.cacamba || locOriginal.cacamba_codigo || "",
          tipoCacamba: locOriginal.tipoCacamba || locOriginal.tipo_cacamba || "",
          operacao: "TROCA",
          motorista: form.motorista || "",
          dataEntrega: form.dataTroca || null,
          dataRetirada: form.dataPrevistaRetirada || null,
          valor: locOriginal.valor || 0,
          pagamento: locOriginal.pagamento || "dinheiro",
          prazoPagamento: locOriginal.prazoPagamento || locOriginal.prazo_pagamento || "",
          obs: "Gerada automaticamente pela troca da locação #" + id,
          status: "Colocada",
          mtr: row.mtrColocada || "",
          contatoNome: locOriginal.contatoNome || locOriginal.contato_nome || "",
          contatoTel: locOriginal.contatoTel || locOriginal.contato_tel || locOriginal.telefone || "",
          contatoEmail: locOriginal.contatoEmail || locOriginal.contato_email || "",
          lat: locOriginal.lat || null,
          lng: locOriginal.lng || null,
          comissao: form.comissaoColocacao || null,
        };
        console.log("[TROCA-REAL] POST criar filha — body:", JSON.stringify(postBody));
        try {
          const novaLoc = await apiFetch("/api/locacoes", { method: "POST", body: postBody });
          console.log("[TROCA-REAL] POST filha CRIADA OK — id=" + (novaLoc?.id || "???"), novaLoc);
          if (novaLoc?.id) {
            apiFetch("/api/historico/" + novaLoc.id, { method: "POST", body: { acao: "Criação via Troca — Colocada", detalhes: "Originada da locação " + id + ". Motorista: " + (form.motorista || "—") } }).catch(() => {});
          }
        } catch(postErr) {
          console.error("[TROCA-REAL] POST filha FALHOU:", postErr.message, postErr);
          erros.push("Erro ao criar locação filha de #" + id + ": " + postErr.message);
        }
      }
    }

    // Recarregar locações do servidor (sempre, mesmo com erros parciais)
    try {
      await reloadLocacoes();
    } catch(reloadErr) {
      console.error("[TROCA-REAL] Reload falhou:", reloadErr);
    }

    if (erros.length > 0) {
      if (addToast) addToast("Troca com erros: " + erros.join("; "), "", "", "#e74c3c");
    } else {
      if (addToast) addToast("Troca realizada com sucesso ✅", ids.length + " locação(ões)", "", "#2ecc71");
    }
    setSelecionadas([]);
  };

  const confirmarPagamento = async (form) => {
    // Atualizar UI local imediatamente
    setLocacoes(p => p.map(l => {
      if (!selecionadas.includes(l.id)) return l;
      return { ...l, pago: true, status: "Encerrada", dataPagamento: form.dataPagamento, modalidadePagamento: form.modalidade, obsPagamento: form.obs };
    }));
    const ids = [...selecionadas];
    setSelecionadas([]);
    if (addToast) addToast("Pagamento confirmado 💰", ids.length + " locação(ões) encerrada(s)", "", "#27ae60");
    // Esperar TODAS as chamadas API terminarem
    const promises = [];
    for (const id of ids) {
      promises.push(apiFetch("/api/locacoes/" + id + "/status", { method: "PATCH", body: { status: "Encerrada", pago: true, dataPagamento: form.dataPagamento, modalidadePagamento: form.modalidade, obsPagamento: form.obs } }).catch(() => {}));
      promises.push(apiFetch("/api/historico/" + id, { method: "POST", body: { acao: "Pagamento Confirmado", detalhes: "Modalidade: " + form.modalidade + " | Data: " + form.dataPagamento + (form.obs ? " | Obs: " + form.obs : "") } }).catch(() => {}));
    }
    await Promise.all(promises);
    setTimeout(() => reloadLocacoes(), 300);
  };

  const selecionadasData = locacoes.filter(l => selecionadas.includes(l.id));

  return (
    <div>
      <div className="page-action-bar" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {(TAB_ACTIONS[stFilter]||[]).length > 0 && selecionadas.length > 0 && (
            <div style={{ position:"relative" }}>
              <button onClick={() => setAcaoOpen(o => !o)} style={{ background:"#f0f4ff", color:"#1aae9e", border:"1px solid #c7d7fd", borderRadius:3, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
                ⚡Ações ({selecionadas.length}) <span style={{ fontSize:10 }}>▾</span>
              </button>
              {acaoOpen && (
                <div style={{ position:"absolute", top:"100%", left:0, marginTop:3, background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, boxShadow:"0 4px 16px rgba(0,0,0,.1)", zIndex:100, minWidth:240, overflow:"hidden" }}>
                  {(TAB_ACTIONS[stFilter]||[]).map(act => (
                    <button key={act.key} onClick={() => {
                      if (!selecionadas.length) { setAcaoOpen(false); return; }
                      if (act.key === "colocada" && stFilter === "Para Colocar") { setColocadaModal(true); setAcaoOpen(false); return; }
                      if (act.key === "encerrada") { setConfirmarRetiradaModal(true); setAcaoOpen(false); return; }
                      if (act.key === "para_retirar") { setRetiradaModal(true); setAcaoOpen(false); return; }
                      if (act.key === "para_trocar") { setTrocaModal(true); setAcaoOpen(false); return; }
                      if (act.key === "confirmar_troca") { setConfirmarTrocaModal(true); setAcaoOpen(false); return; }
                      if (act.key === "pago") { setPagamentoModal(true); setAcaoOpen(false); return; }
                      if (act.key === "voltar_colocada") {
                        if (!window.confirm("Voltar "+selecionadas.length+" locação(ões) para Colocada?")) { setAcaoOpen(false); return; }
                        // Cancelar locações filhas de troca (Para Colocar criadas pela troca)
                        const childIds = [];
                        for (const id of selecionadas) {
                          const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + id) || cl.obs.toLowerCase().includes("troca da locacao #" + id) || cl.obs.toLowerCase().includes("troca da locação " + id) || cl.obs.toLowerCase().includes("troca da locacao " + id)));
                          if (childLoc) childIds.push(childLoc.id);
                        }
                        // Atualizar UI imediatamente
                        setLocacoes(p => p.map(l => {
                          if (selecionadas.includes(l.id)) return { ...l, status:"Colocada" };
                          if (childIds.includes(l.id)) return { ...l, status:"Cancelada" };
                          return l;
                        }));
                        const voltarPromises = [];
                        for (const id of selecionadas) { voltarPromises.push(apiFetch("/api/locacoes/"+id+"/status",{method:"PATCH",body:{status:"Colocada"}}).catch(()=>{})); apiFetch("/api/historico/"+id,{method:"POST",body:{acao:"Status → Colocada",detalhes:"Voltou para Colocada manualmente"}}).catch(()=>{}); }
                        for (const cid of childIds) { voltarPromises.push(apiFetch("/api/locacoes/"+cid+"/status",{method:"PATCH",body:{status:"Cancelada"}}).catch(()=>{})); apiFetch("/api/historico/"+cid,{method:"POST",body:{acao:"Status → Cancelada",detalhes:"Locação de troca cancelada — original voltou para Colocada"}}).catch(()=>{}); }
                        // Recarregar locações do servidor para pegar datas originais
                        Promise.all(voltarPromises).then(() => {
                          apiFetch("/api/locacoes").then(freshLocs => {
                            if (freshLocs && Array.isArray(freshLocs)) setLocacoes(freshLocs.map(r => ({
                              ...r, dataEntrega: r.data_entrega||r.dataEntrega||'', dataRetirada: r.data_retirada||r.dataRetirada||'',
                              dataRetiraPrevista: r.data_retirada||r.dataRetiraPrevista||'', dataPagamento: r.data_pagamento||r.dataPagamento||'',
                              dataRetiradaReal: r.data_retirada_real||r.dataRetiradaReal||'', dataColocacaoReal: r.data_colocacao_real||r.dataColocacaoReal||'',
                              dataSolicitacao: r.data_solicitacao||r.dataSolicitacao||'',
                              motoristaRetirada: r.motorista_retirada||r.motoristaRetirada||'', cacamba: r.cacamba_codigo||r.cacamba||'',
                              cliente: r.cliente_nome||r.cliente||'', tipoCacamba: r.tipo_cacamba||r.tipoCacamba||'',
                              contatoTel: r.contato_tel||r.contatoTel||'', contatoNome: r.contato_nome||r.contatoNome||'',
                              logradouro: r.end_logradouro||r.logradouro||'', numero: r.end_numero||r.numero||'',
                              bairro: r.end_bairro||r.bairro||'', cidade: r.end_cidade||r.cidade||'',
                              uf: r.end_uf||r.uf||'', pago: !!r.pago, valor: parseFloat(r.valor)||0,
                              status: r.status||'Para Colocar', endereco: r.endereco||{}, obs: r.observacoes||r.obs||'',
                            })));
                          }).catch(()=>{});
                        });
                        marcarItinerarioColocada([...selecionadas]);
                        if(addToast) addToast("Locação voltou para Colocada ↩️",selecionadas.length+" locação(ões)"+(childIds.length?" — "+childIds.length+" troca(s) cancelada(s)":""),"","#1a9e8f");
                        setSelecionadas([]); setAcaoOpen(false); return;
                      }
                      else if (act.key === "colocada") {
                        setLocacoes(p => p.map(l => selecionadas.includes(l.id) ? { ...l, status:"Colocada" } : l));
                        if(addToast) addToast("Troca confirmada!", selecionadas.length+" locação(ões)", "", "#2ecc71");
                        for (const id of selecionadas) { apiFetch("/api/locacoes/"+id+"/status",{method:"PATCH",body:{status:"Colocada"}}).catch(()=>{}); apiFetch("/api/historico/"+id,{method:"POST",body:{acao:"Status → Colocada",detalhes:"Troca confirmada"}}).catch(()=>{}); }
                        marcarItinerarioColocada([...selecionadas]);
                      } else {
                        const statusMap = { encerrada:"Encerrada" };
                        const newStatus = statusMap[act.key];
                        if (newStatus) {
                          setLocacoes(p => p.map(l => selecionadas.includes(l.id) ? { ...l, status:newStatus } : l));
                          if(addToast) addToast("Status: "+newStatus, selecionadas.length+" locação(ões)", "", act.color);
                          for (const id of selecionadas) { apiFetch("/api/locacoes/"+id+"/status",{method:"PATCH",body:{status:newStatus}}).catch(()=>{}); apiFetch("/api/historico/"+id,{method:"POST",body:{acao:"Status → "+newStatus,detalhes:"Alteração via ações em lote"}}).catch(()=>{}); }
                        }
                      }
                      setSelecionadas([]); setAcaoOpen(false);
                    }}
                    style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:13, color: act.color||"#333", fontFamily:FONT, display:"block", fontWeight:600 }}
                    onMouseEnter={e => e.currentTarget.style.background="#f5f5f5"}
                    onMouseLeave={e => e.currentTarget.style.background="none"}>
                      {act.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={() => setModal(true)} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            <IcoPlus /> Nova Locação
          </button>
          {selecionadas.length > 0 && (
            <span style={{ fontSize:12, color:"#1aae9e", fontWeight:700, padding:"4px 10px", background:"#f0f4ff", borderRadius:3 }}>{selecionadas.length} selecionada(s)</span>
          )}
          {selecionadas.length > 1 && (
            <button onClick={async () => {
              const locs = locacoes.filter(l => selecionadas.includes(l.id));
              // Validar mesmo cliente: tentar por cliente_id, senão por nome
              const cliIds = locs.map(l => l.cliente_id || l.clienteId).filter(Boolean);
              const nomes = locs.map(l => (l.cliente || l.cliente_nome || "").trim().toLowerCase());
              const mesmoCli = cliIds.length === locs.length
                ? cliIds.every(id => String(id) === String(cliIds[0]))
                : nomes.every(n => n === nomes[0]);
              if (!mesmoCli) {
                if (addToast) addToast("Clientes diferentes!", "Selecione apenas locações do mesmo cliente para gerar recibo consolidado", "⚠️", "#e74c3c");
                return;
              }
              try {
                const ids = locs.map(l => l.id).join(",");
                const token = getToken();
                const r = await fetch(API + "/api/recibo/consolidado?ids=" + ids, {
                  headers: { ...(token ? { Authorization: "Bearer " + token } : {}) }
                });
                if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || "Erro ao gerar recibo"); }
                const blob = await r.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "recibo_consolidado_" + ids.replace(/,/g, "_") + ".pdf";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 10000);
              } catch(e) {
                console.error("Erro recibo consolidado:", e);
                if (addToast) addToast("Erro", e.message || "Falha ao gerar recibo consolidado", "⚠️", "#e74c3c");
                // Fallback: jsPDF
                let cfg = empresaCfg;
                if (!cfg) { try { cfg = await apiFetch("/api/config"); setEmpresaCfg(cfg); } catch(ex){} }
                gerarReciboConsolidadoPDF(locs, cfg);
              }
            }} style={{ background:"#e67e22", color:"#fff", border:"none", borderRadius:3, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:5, fontFamily:FONT }}>
              🧾 Recibo Consolidado
            </button>
          )}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={() => setVisualizacaoModal(true)} title="Escolher colunas adicionais da lista" style={{ background:"#fafafa", color:"#444", border:"1px solid #e0e0e0", borderRadius:3, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            👁 Visualização{extrasAtivas.length > 0 && <span style={{ background:"#1aae9e", color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:10, fontWeight:700 }}>+{extrasAtivas.length}</span>}
          </button>
          <button onClick={() => exportToExcel(filtered, [
            {label:"Nº Locação",key:"id"},{label:"Cliente",key:"cliente"},{label:"CPF/CNPJ",key:r=>r.cpfCnpj||r.cpf_cnpj||""},
            {label:"Telefone",key:r=>r.contatoTel||r.tel||""},{label:"Logradouro",key:r=>r.logradouro||(r.endereco?.logradouro)||""},
            {label:"Número",key:r=>r.numero||(r.endereco?.numero)||""},{label:"Bairro",key:r=>r.bairro||(r.endereco?.bairro)||""},
            {label:"Cidade",key:r=>r.cidade||(r.endereco?.cidade)||""},{label:"UF",key:r=>r.uf||(r.endereco?.uf)||""},
            {label:"CEP",key:r=>r.cep||(r.endereco?.cep)||""},{label:"Caçamba",key:r=>r.cacamba||""},
            {label:"Tipo Caçamba",key:r=>r.tipoCacamba||""},{label:"Operação",key:r=>r.operacao||""},
            {label:"Motorista",key:r=>r.motorista||""},{label:"Status",key:"status"},
            {label:"Data Solicitação",key:r=>r.dataSolicitacao||r.data_solicitacao||""},
            {label:"Pedido Retirada",key:r=>r.dataPedidoRetirada||r.data_pedido_retirada||""},
            {label:"Pedido Troca",key:r=>r.dataPedidoTroca||r.data_pedido_troca||""},
            {label:"Data Entrega",key:r=>r.dataEntrega||r.data_entrega||""},
            {label:"Data Retirada",key:r=>r.dataRetiraPrevista||r.dataRetirada||r.data_retirada||""},
            {label:"Valor (R$)",key:r=>r.valor||""},{label:"Pagamento",key:r=>r.pagamento||""},
            {label:"Pago",key:r=>r.pago?"Sim":"Não"},{label:"MTR/CTR",key:r=>r.mtr||""},
            {label:"Observações",key:r=>r.obs||r.observacoes||""},
            ...extrasAtivas.map(c => ({ label:c.label, key:r => { const v = c.render(r, { clientes }); return v === "—" ? "" : v; } })),
          ], "locacoes_"+stFilter.replace(/\s/g,"_"))} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            Exportar Excel
          </button>
          <select value={sortMode} onChange={e => setSortMode(e.target.value)}
            title="Ordenar locações"
            style={{ padding:"8px 12px", borderRadius:4, border:"1px solid #e0e0e0", fontSize:12, fontFamily:FONT, background:"#fafafa", color:"#444", cursor:"pointer", appearance:"auto" }}>
            <option value="operacional">Ordenar: padrão (atrasadas primeiro)</option>
            <option value="recente">Ordenar: mais recentes primeiro</option>
            <option value="antigo">Ordenar: mais antigas primeiro</option>
          </select>
          <div style={{ position:"relative" }}>
            <input value={searchLoc} onChange={e => setSearchLoc(e.target.value)} placeholder="Buscar por cliente, endereço, nº..." style={{ padding:"8px 14px 8px 34px", borderRadius:4, border:"1px solid #e0e0e0", fontSize:13, fontFamily:FONT, width:260, background:"#fafafa" }} />
            <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:14, color:"#aaa", pointerEvents:"none" }}>🔍</span>
            {searchLoc && <button onClick={() => setSearchLoc("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#aaa", padding:0 }}>✕</button>}
          </div>
        </div>
      </div>

      <div className="status-tabs-row" style={{ display:"flex", gap:0, marginBottom:0, borderBottom:"2px solid #ebebeb", overflowX:"auto", position:"relative" }}>
        {STATUS_TABS.map(t => {
          const count = t.key === "TODAS" ? locacoes.length : locacoes.filter(l => {
            if (l.status !== t.key) return false;
            // Esconder filhas de troca do contador de "Para Colocar"
            if (t.key === "Para Colocar" && l.obs && typeof l.obs === "string" && (l.obs.toLowerCase().includes("troca da locação") || l.obs.toLowerCase().includes("troca da locacao"))) {
              const parentMatch = l.obs.match(/#(\d+)/);
              if (parentMatch) {
                const parentLoc = locacoes.find(p => String(p.id) === parentMatch[1] && p.status === "Para Trocar");
                if (parentLoc) return false;
              }
            }
            return true;
          }).length;
          const active = stFilter === t.key;
          return (
            <button key={t.key} onClick={() => { setStFilter(t.key); setSelecionadas([]); }} style={{
              padding:"10px 16px", fontSize:12, fontWeight:active?700:500, border:"none",
              background: active ? t.color+"14" : "none",
              cursor:"pointer", color: active ? t.color : "#888",
              borderBottom: active ? "3px solid "+t.color : "3px solid transparent",
              borderTop: active ? "3px solid "+t.color : "3px solid transparent",
              borderRadius: active ? "6px 6px 0 0" : "0",
              marginBottom:-2, whiteSpace:"nowrap", fontFamily:FONT,
              display:"flex", alignItems:"center", gap:5,
              transition:"all 0.25s ease",
            }}>
              {t.label}
              <span style={{ background:active?t.color:"#eee", color:active?"#fff":"#888", borderRadius:10, padding:"2px 8px", fontSize:10, fontWeight:700, transition:"all 0.25s ease" }}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderTop:"none", borderRadius:"0 0 4px 4px", overflowX:"auto" }}>
        <table className="tbl-loc loc-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13, tableLayout:"auto" }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>
              <th style={{ padding:"9px 13px", borderBottom:"1px solid #ebebeb", width:40 }}></th>
              {[...LOC_COLUNAS_PADRAO(stFilter), ...extrasAtivas.map(c => c.label), ""].map(h => (
                <th key={h} style={{ textAlign: h==="Caçamba"?"center":"left", padding:"9px 13px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #ebebeb" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={12 + extrasAtivas.length}><EmptyState icon="📦" title="Nenhuma locação encontrada" subtitle="Clique em 'Nova Locação' para cadastrar" /></td></tr>
            ) : paginatedLocs.map(l => {
              const sel = selecionadas.includes(l.id);
              return (
                <tr key={l.id} style={{ borderBottom:"2px solid #e0e0e0", background:sel?"#fff8f4":"" }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background="#fafafa"; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background=""; }}>
                  <td style={{ padding:"10px 13px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <input type="checkbox" checked={sel} onChange={() => toggleSel(l.id)} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
                      <div style={{ width:10, height:10, borderRadius:"50%", background:(()=>{
                        if(l.status==="Cancelada"||l.status==="Encerrada") return "#aaa";
                        const dataRef = l.status==="Para Colocar" ? (l.dataEntrega||l.data_entrega||"") : l.status==="Para Trocar" ? (l.dataTrocaPrevista||l.data_troca_prevista||l.dataRetiraPrevista||l.dataRetirada||"") : (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada_prevista||l.dataEntrega||"");
                        if(!dataRef) return "#1a9e8f";
                        const hoje=hojeLocal();
                        if(dataRef < hoje) return "#e74c3c";
                        if(dataRef === hoje) return "#f39c12";
                        return "#2ecc71";
                      })(), flexShrink:0 }} title={(()=>{
                        if(l.status==="Cancelada") return "Cancelada";
                        if(l.status==="Encerrada") return "Encerrada";
                        const dataRef = l.status==="Para Colocar" ? (l.dataEntrega||l.data_entrega||"") : l.status==="Para Trocar" ? (l.dataTrocaPrevista||l.data_troca_prevista||l.dataRetiraPrevista||"") : (l.dataRetiraPrevista||l.dataRetirada||l.dataEntrega||"");
                        if(!dataRef) return "Sem data";
                        const h=hojeLocal();
                        if(dataRef<h) return "Atrasado";
                        if(dataRef===h) return "Vence hoje";
                        return "No prazo";
                      })()} />
                    </div>
                  </td>
                  <td style={{ padding:"10px 13px" }}>
                    <span onClick={() => setDetailLoc(l)} style={{ color:"#1a9e8f", fontWeight:600, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>{l.id}</span>
                    {(() => {
                      // Mostrar vínculo FILHA (locação gerada pela troca desta)
                      const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o #" + l.id) || cl.obs.toLowerCase().includes("troca da locacao #" + l.id) || cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o " + l.id) || cl.obs.toLowerCase().includes("troca da locacao " + l.id)));
                      // Mostrar vínculo PAI (esta locação foi gerada por troca de outra)
                      const obsLocal = l.obs || "";
                      const matchPai = obsLocal.match(/troca da loca[çc][aã]o\s*#?\s*(\d+)/i);
                      const paiId = matchPai ? matchPai[1] : null;
                      return <>
                        {childLoc && <div style={{ fontSize:10, color:"#2ecc71", fontWeight:600, marginTop:2 }}>{"↓"} Filha #{childLoc.id} <span style={{ color:"#aaa", fontWeight:400 }}>({childLoc.status})</span></div>}
                        {paiId && <div style={{ fontSize:10, color:"#3b82f6", fontWeight:600, marginTop:2 }}>{"↑"} Origem #{paiId}</div>}
                      </>;
                    })()}
                  </td>
                  <td style={{ padding:"10px 13px", maxWidth:260 }}>
                    <div style={{ fontWeight:700, color:"#1a1a1a", fontSize:13 }}>{l.cliente}</div>
                    {l.contatoTel && <div style={{ fontSize:11, color:"#888", display:"flex", alignItems:"center", gap:4 }}>
                      <a href={"https://wa.me/"+(()=>{const t=(l.contatoTel||"").replace(/\D/g,"");return (t.length<=11?"55":"")+t;})()+"?text="+encodeURIComponent("Ola "+(l.cliente||"").split(" ")[0]+"!")} target="_blank" rel="noopener noreferrer" title="Abrir WhatsApp" style={{ display:"inline-flex", cursor:"pointer" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.347 0-4.518-.809-6.234-2.163l-.353-.29-3.661 1.228 1.228-3.661-.29-.353A9.96 9.96 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                      </a>
                      {formatPhone(l.contatoTel)}
                    </div>}
                    <div style={{ fontSize:11, color:"#888" }}>{l.logradouro ? l.logradouro+(l.numero?", "+l.numero:"")+" — "+l.cidade+"/"+l.uf : (typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "")}</div>
                  </td>
                  <td style={{ padding:"10px 13px", textAlign:"center" }}>
                    {(() => {
                      const cObj = (cacambas||[]).find(c => c.codigo === l.cacamba);
                      const tipoNome = l.tipoCacamba || cObj?.tipo || "";
                      const volume = cObj?.volume || "";
                      const fmtVol = volume ? volume.replace(/m3/gi,"").trim() + " m\u00b3" : "";
                      const cacInfo = tipoNome ? tipoNome + (fmtVol ? " (" + fmtVol + ")" : "") : "(N/I)";

                      // Para Trocar: mostrar as duas caçambas (retirar + colocar)
                      if (l.status === "Para Trocar") {
                        const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o #" + l.id) || cl.obs.toLowerCase().includes("troca da locacao #" + l.id) || cl.obs.toLowerCase().includes("troca da loca\u00e7\u00e3o " + l.id) || cl.obs.toLowerCase().includes("troca da locacao " + l.id)));
                        const cObjNova = childLoc ? (cacambas||[]).find(c => c.codigo === childLoc.cacamba) : null;
                        const tipoNova = childLoc ? (childLoc.tipoCacamba || cObjNova?.tipo || "") : "";
                        const volNova = cObjNova?.volume || "";
                        const fmtVolNova = volNova ? volNova.replace(/m3/gi,"").trim() + " m\u00b3" : "";
                        const cacInfoNova = tipoNova ? tipoNova + (fmtVolNova ? " (" + fmtVolNova + ")" : "") : "(N/I)";
                        return <>
                          <div style={{ borderBottom:"1px solid #f0f0f0", paddingBottom:4, marginBottom:4 }}>
                            <div style={{ fontSize:9, fontWeight:800, color:"#e74c3c", textTransform:"uppercase", letterSpacing:.3 }}>PARA TROCAR</div>
                            <div style={{ fontWeight:600, fontSize:11, color:"#1a1a1a" }}>{cacInfo}</div>
                            {l.cacamba && <div style={{ fontSize:10, color:"#888" }}>{"Nº"} {l.cacamba}</div>}
                          </div>
                          <div>
                            <div style={{ fontSize:9, fontWeight:800, color:"#2ecc71", textTransform:"uppercase", letterSpacing:.3 }}>PARA COLOCAR</div>
                            <div style={{ fontWeight:600, fontSize:11, color:"#1a1a1a" }}>{cacInfoNova !== "(N/I)" ? cacInfoNova : cacInfo}</div>
                            {childLoc && <div style={{ fontSize:10, color:"#1a9e8f", fontWeight:600 }}>{"Locação #"}{childLoc.id}</div>}
                            {childLoc?.cacamba && <div style={{ fontSize:10, color:"#888" }}>{"Caçamba Nº"} {childLoc.cacamba}</div>}
                          </div>
                        </>;
                      }

                      return <>
                        <div style={{ fontWeight:600, fontSize:12, color:"#1a1a1a" }}>{cacInfo}</div>
                        {l.cacamba && <div style={{ fontSize:11, color:"#888" }}>{"Nº"} {l.cacamba}</div>}
                      </>;
                    })()}
                  </td>
                  {/* Coluna Colocada por (Colocada, Para Retirar, Para Trocar, Encerrada, Para Cobrar) */}
                  {(stFilter === "Colocada" || stFilter === "Para Retirar" || stFilter === "Para Trocar" || stFilter === "Encerrada" || stFilter === "Para Cobrar") && (
                    <td data-label="Colocada por" style={{ padding:"10px 13px", fontSize:12, color:"#555" }}>{l.motorista || "—"}</td>
                  )}
                  {/* Coluna Retirada por (só Encerrada e Para Cobrar) */}
                  {(stFilter === "Encerrada" || stFilter === "Para Cobrar") && (
                    <td data-label="Retirada por" style={{ padding:"10px 13px", fontSize:12, color:"#555" }}>{l.motoristaRetirada || l.motorista_retirada || "—"}</td>
                  )}
                  <td data-label={stFilter==="Colocada" ? "Colocada em" : stFilter==="Para Colocar" ? "Colocar em" : (stFilter==="Encerrada"||stFilter==="Para Cobrar") ? "Colocada em" : "Pedido em"} style={{ padding:"4px 6px", fontSize:12, verticalAlign:"middle" }}>
                    {stFilter === "Para Trocar"
                      ? ((l.dataPedidoTroca||l.data_pedido_troca) ? <DateCell dateStr={l.dataPedidoTroca||l.data_pedido_troca} noBadge /> : (l.dataSolicitacao||l.data_solicitacao) ? <DateCell dateStr={l.dataSolicitacao||l.data_solicitacao} noBadge /> : <span style={{ color:"#aaa", padding:"6px 7px", display:"block" }}>—</span>)
                      : (stFilter==="Encerrada"||stFilter==="Para Cobrar")
                      ? ((l.dataColocacaoReal||l.data_colocacao_real) ? <DateCell dateStr={l.dataColocacaoReal||l.data_colocacao_real} noBadge /> : (l.dataEntrega||l.data_entrega) ? <DateCell dateStr={l.dataEntrega||l.data_entrega} noBadge /> : <span style={{ color:"#aaa", padding:"6px 7px", display:"block" }}>—</span>)
                      : stFilter==="Para Retirar"
                      ? ((l.dataPedidoRetirada||l.data_pedido_retirada) ? <DateCell dateStr={l.dataPedidoRetirada||l.data_pedido_retirada} noBadge /> : (l.dataSolicitacao||l.data_solicitacao) ? <DateCell dateStr={l.dataSolicitacao||l.data_solicitacao} noBadge /> : <span style={{ color:"#aaa", padding:"6px 7px", display:"block" }}>—</span>)
                      : stFilter==="Cancelada"
                      ? ((l.dataSolicitacao||l.data_solicitacao) ? <DateCell dateStr={l.dataSolicitacao||l.data_solicitacao} noBadge /> : <span style={{ color:"#aaa", padding:"6px 7px", display:"block" }}>—</span>)
                      : (l.dataEntrega ? <DateCell dateStr={l.dataEntrega} noBadge={stFilter==="Colocada"} /> : <span style={{ color:"#aaa", padding:"6px 7px", display:"block" }}>—</span>)
                    }
                  </td>
                  {/* Coluna Retirada em (Encerrada/Para Cobrar) ou Retirar em/Trocar em (demais) - esconder em Cancelada */}
                  {stFilter !== "Cancelada" && <td data-label={(stFilter==="Encerrada"||stFilter==="Para Cobrar") ? "Retirada em" : stFilter==="Para Trocar" ? "Trocar em" : "Retirar em"} style={{ padding:"4px 6px", fontSize:12, verticalAlign:"middle" }}>
                    {(stFilter === "Encerrada" || stFilter === "Para Cobrar")
                      ? (l.dataRetiradaReal ? <DateCell dateStr={l.dataRetiradaReal} noBadge /> : <DateCell dateStr={l.dataRetiraPrevista||l.dataRetirada} noBadge />)
                      : stFilter === "Para Trocar"
                      ? <DateCell dateStr={l.dataTrocaPrevista||l.data_troca_prevista||l.dataRetiraPrevista||l.dataRetirada} />
                      : <>
                          <DateCell dateStr={l.dataRetiraPrevista||l.dataRetirada} noBadge={stFilter==="Para Colocar"} />
                          {stFilter === "Colocada" && (l.dataRetiraPrevista||l.dataRetirada) && (l.dataRetiraPrevista||l.dataRetirada) < hojeLocal() && (
                            <div style={{ marginTop:3, fontSize:10, fontWeight:700, color:"#e74c3c", display:"flex", alignItems:"center", gap:3 }}>
                              <span></span> Caçamba vencida
                            </div>
                          )}
                        </>
                    }
                    {(() => {
                      const dx = calcDiariaExtraAtraso(l);
                      if (!dx) return null;
                      // Descontar o que já foi aplicado ao valor (valor_extras) — mostra só o pendente
                      const jaAplicado = parseFloat(l.valorExtras != null ? l.valorExtras : l.valor_extras) || 0;
                      const pendente = dx.total - jaAplicado;
                      if (pendente <= 0.005) return null;
                      return (
                        <div style={{ marginTop:3, display:"inline-block", background:"#fdecea", color:"#e74c3c", border:"1px solid #f5c6c0", borderRadius:3, padding:"1px 6px", fontSize:10, fontWeight:700 }} title={dx.dias + " dia(s) de atraso × " + fmt(dx.diaria) + "/dia" + (jaAplicado > 0 ? " — " + fmt(jaAplicado) + " já aplicado ao valor" : "")}>
                          +{fmt(pendente)} ({dx.dias}d)
                        </div>
                      );
                    })()}
                  </td>}
                  <td data-label="Preço Total" style={{ padding:"10px 13px", fontWeight:800 }}>{l.valor ? fmt(l.valor) : "—"}</td>
                  <td data-label="Pago?" style={{ padding:"10px 13px" }}>
                    <span style={{ fontSize:11, fontWeight:700, color:l.pago?"#2ecc71":"#e74c3c" }}>{l.pago?"SIM":"NÃO"}</span>
                  </td>
                  {stFilter === "Para Trocar" && (() => {
                    const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + l.id) || cl.obs.toLowerCase().includes("troca da locacao #" + l.id)));
                    return <td data-label="Pago Nova?" style={{ padding:"10px 13px" }}>
                      {childLoc ? <span style={{ fontSize:11, fontWeight:700, color:childLoc.pago?"#2ecc71":"#e74c3c" }}>{childLoc.pago?"SIM":"NÃO"}</span> : <span style={{ color:"#aaa", fontSize:11 }}>—</span>}
                    </td>;
                  })()}
                  {/* Colunas extras escolhidas em "Visualização" */}
                  {extrasAtivas.map(c => {
                    const v = c.render(l, { clientes });
                    return (
                      <td key={c.key} data-label={c.label} className="loc-col-extra" style={{ padding:"10px 13px", fontSize:12, color: v === "—" ? "#aaa" : "#555", maxWidth:220, whiteSpace: (c.key === "obs" || c.key === "endFaturamento") ? "normal" : "nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={v !== "—" ? v : undefined}>{v}</td>
                    );
                  })}
                  <td style={{ padding:"10px 13px" }}>
                    <LocacaoRowMenu locacao={l} empresaCfg={empresaCfg} empresaNome={empresaNome} locacoes={locacoes}
                      onEnviarAssinatura={() => setAssinaturaModal({ loc: l, nome: l.contatoNome || l.contato_nome || l.cliente || l.cliente_nome || "", telefone: l.contatoTel || l.contato_tel || "", link: "", enviando: false })}
                      onView={() => setDetailLoc(l)}
                      onProrrogar={() => setProrrogarModal(l)}
                      onEdit={() => setEditModal(l)}
                      onEditChild={(childLoc) => setEditModal(childLoc)}
                      onRecibo={() => handleRecibo(l)}
                      onReciboChild={(childLoc) => handleRecibo(childLoc)}
                      onMtr={() => handleMtr(l)}
                      onMtrChild={(childLoc) => handleMtr(childLoc)}
                      onTimeline={() => { setDetailLoc(l); setTimeout(() => { /* tab will be set on open */ }, 100); }}
                      onCancel={() => setCancelModal(l)}
                      onStatusChange={(key) => {
                        // Individual row actions - select this row and trigger appropriate modal/action
                        setSelecionadas([l.id]);
                        if (key === "colocada") { setSelecionadas([l.id]); setColocadaModal(true); }
                        else if (key === "para_retirar") { setSelecionadas([l.id]); setRetiradaModal(true); }
                        else if (key === "para_trocar") { setSelecionadas([l.id]); setTrocaModal(true); }
                        else if (key === "confirmar_troca") { setSelecionadas([l.id]); setConfirmarTrocaModal(true); }
                        else if (key === "pago") { setSelecionadas([l.id]); setPagamentoModal(true); }
                        else if (key === "encerrada") { setSelecionadas([l.id]); setConfirmarRetiradaModal(true); }
                        else if (key === "alterar_pago") { setSelecionadas([l.id]); setAlterarPagoModal(true); }
                        else if (key === "voltar_colocada") {
                          if (!window.confirm("Tem certeza que deseja voltar a locação "+l.id+" para Colocada?")) return;
                          // Cancelar locação filha de troca se existir
                          const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + l.id) || cl.obs.toLowerCase().includes("troca da locacao #" + l.id) || cl.obs.toLowerCase().includes("troca da locação " + l.id) || cl.obs.toLowerCase().includes("troca da locacao " + l.id)));
                          // Atualizar UI imediatamente
                          setLocacoes(p => p.map(x => {
                            if (x.id === l.id) return { ...x, status:"Colocada" };
                            if (childLoc && x.id === childLoc.id) return { ...x, status:"Cancelada" };
                            return x;
                          }));
                          const voltarP = [apiFetch("/api/locacoes/"+l.id+"/status", { method:"PATCH", body:{ status:"Colocada" } }).catch(()=>{})];
                          apiFetch("/api/historico/"+l.id, { method:"POST", body:{ acao:"Status → Colocada", detalhes:"Voltou para Colocada manualmente"+(childLoc?" (troca cancelada)":"") } }).catch(()=>{});
                          if (childLoc) {
                            voltarP.push(apiFetch("/api/locacoes/"+childLoc.id+"/status", { method:"PATCH", body:{ status:"Cancelada" } }).catch(()=>{}));
                            apiFetch("/api/historico/"+childLoc.id, { method:"POST", body:{ acao:"Status → Cancelada", detalhes:"Locação de troca cancelada — original #"+l.id+" voltou para Colocada" } }).catch(()=>{});
                          }
                          // Recarregar locações do servidor para restaurar datas originais
                          Promise.all(voltarP).then(() => {
                            apiFetch("/api/locacoes").then(freshLocs => {
                              if (freshLocs && Array.isArray(freshLocs)) setLocacoes(freshLocs.map(r => ({
                                ...r, dataEntrega: r.data_entrega||r.dataEntrega||'', dataRetirada: r.data_retirada||r.dataRetirada||'',
                                dataRetiraPrevista: r.data_retirada||r.dataRetiraPrevista||'', dataPagamento: r.data_pagamento||r.dataPagamento||'',
                                dataRetiradaReal: r.data_retirada_real||r.dataRetiradaReal||'', dataColocacaoReal: r.data_colocacao_real||r.dataColocacaoReal||'',
                                dataSolicitacao: r.data_solicitacao||r.dataSolicitacao||'',
                                motoristaRetirada: r.motorista_retirada||r.motoristaRetirada||'', cacamba: r.cacamba_codigo||r.cacamba||'',
                                cliente: r.cliente_nome||r.cliente||'', tipoCacamba: r.tipo_cacamba||r.tipoCacamba||'',
                                contatoTel: r.contato_tel||r.contatoTel||'', contatoNome: r.contato_nome||r.contatoNome||'',
                                logradouro: r.end_logradouro||r.logradouro||'', numero: r.end_numero||r.numero||'',
                                bairro: r.end_bairro||r.bairro||'', cidade: r.end_cidade||r.cidade||'',
                                uf: r.end_uf||r.uf||'', pago: !!r.pago, valor: parseFloat(r.valor)||0,
                                status: r.status||'Para Colocar', endereco: r.endereco||{}, obs: r.observacoes||r.obs||'',
                              })));
                            }).catch(()=>{});
                          });
                          marcarItinerarioColocada([l.id]);
                          if(addToast) addToast("Locação voltou para Colocada",childLoc?"Troca #"+childLoc.id+" cancelada":"","","#1a9e8f");
                        }
                        else if (key === "voltar_cobrar") {
                          if (!window.confirm("Tem certeza que deseja reabrir a locação "+l.id+" como \"Para Cobrar\"?\n\nA locação voltará como NÃO PAGA.")) return;
                          setLocacoes(p => p.map(x => x.id === l.id ? { ...x, status:"Para Cobrar", pago:false, dataPagamento:"" } : x));
                          apiFetch("/api/locacoes/"+l.id+"/status", { method:"PATCH", body:{ status:"Para Cobrar", pago:false, dataPagamento:null } }).catch(()=>{});
                          apiFetch("/api/historico/"+l.id, { method:"POST", body:{ acao:"Status → Para Cobrar", detalhes:"Locação reaberta manualmente (estava Encerrada) — voltou como NÃO PAGA" } }).catch(()=>{});
                          if (addToast) addToast("Locação reaberta", "#"+l.id+" → Para Cobrar (não paga)", "↩️", "#f59e0b");
                        }
                        else if (key.startsWith("reativar:")) {
                          const novo = key.slice("reativar:".length);
                          if (!window.confirm("Reativar a locação " + l.id + " como \"" + novo + "\"?")) return;
                          setLocacoes(p => p.map(x => x.id === l.id ? { ...x, status: novo } : x));
                          apiFetch("/api/locacoes/" + l.id + "/status", { method:"PATCH", body:{ status: novo } }).catch(()=>{});
                          apiFetch("/api/historico/" + l.id, { method:"POST", body:{ acao:"Status → " + novo, detalhes:"Locação reativada (de Cancelada) para " + novo } }).catch(()=>{});
                          if (addToast) addToast("Locação reativada", "#" + l.id + " → " + novo, "↩️", "#1a9e8f");
                        }
                      }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ padding:"9px 13px", borderTop:"1px solid #f0f0f0", fontSize:12, color:"#aaa", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span>{filtered.length} locações</span>
            <span style={{ color:"#ccc" }}>|</span>
            <span style={{ display:"flex", alignItems:"center", gap:4 }}>
              Exibir
              <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} style={{ border:"1px solid #ddd", borderRadius:3, padding:"2px 4px", fontSize:11, color:"#555", fontFamily:"inherit", cursor:"pointer" }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              por página
            </span>
            <span style={{ color:"#ccc" }}>|</span>
            <span>Página {currentPage} de {totalPages || 1}</span>
          </span>
          {totalPages > 1 && <div style={{ display:"flex", gap:4, alignItems:"center" }}>
            <button disabled={currentPage<=1} onClick={()=>setCurrentPage(1)} style={{ padding:"4px 8px", border:"1px solid #ddd", borderRadius:3, background:currentPage<=1?"#f5f5f5":"#fff", cursor:currentPage<=1?"default":"pointer", fontSize:11, color:"#555", fontFamily:"inherit" }}>«</button>
            <button disabled={currentPage<=1} onClick={()=>setCurrentPage(p=>p-1)} style={{ padding:"4px 8px", border:"1px solid #ddd", borderRadius:3, background:currentPage<=1?"#f5f5f5":"#fff", cursor:currentPage<=1?"default":"pointer", fontSize:11, color:"#555", fontFamily:"inherit" }}>‹</button>
            {Array.from({length:totalPages},(_,i)=>i+1).filter(p => p===1||p===totalPages||Math.abs(p-currentPage)<=2).map((p,idx,arr) => {
              const prev = arr[idx-1];
              return <span key={p} style={{display:"inline-flex",gap:4,alignItems:"center"}}>
                {prev && p - prev > 1 && <span style={{padding:"0 4px"}}>...</span>}
                <button onClick={()=>setCurrentPage(p)} style={{ padding:"4px 10px", border:"1px solid "+(p===currentPage?"#1aae9e":"#ddd"), borderRadius:3, background:p===currentPage?"#1aae9e":"#fff", color:p===currentPage?"#fff":"#555", cursor:"pointer", fontSize:11, fontWeight:p===currentPage?700:400, fontFamily:"inherit" }}>{p}</button>
              </span>;
            })}
            <button disabled={currentPage>=totalPages} onClick={()=>setCurrentPage(p=>p+1)} style={{ padding:"4px 8px", border:"1px solid #ddd", borderRadius:3, background:currentPage>=totalPages?"#f5f5f5":"#fff", cursor:currentPage>=totalPages?"default":"pointer", fontSize:11, color:"#555", fontFamily:"inherit" }}>›</button>
            <button disabled={currentPage>=totalPages} onClick={()=>setCurrentPage(totalPages)} style={{ padding:"4px 8px", border:"1px solid #ddd", borderRadius:3, background:currentPage>=totalPages?"#f5f5f5":"#fff", cursor:currentPage>=totalPages?"default":"pointer", fontSize:11, color:"#555", fontFamily:"inherit" }}>»</button>
          </div>}
        </div>
      </div>

      {modal && <NovaLocacaoModal clientes={clientes} locacoes={locacoes} motoristas={motoristas} cacambas={cacambas} tiposCacamba={tiposCacamba} apiFetch={apiFetch} onClose={() => setModal(false)} onSave={addLocacao} onClienteCreated={async (c)=>{
        try { const novo = await apiFetch("/api/clientes", { method:"POST", body:c }); setClientes(p=>[...p,{...novo, cpfCnpj: novo.cpf_cnpj||novo.cpfCnpj||'', criadoEm: fmtDate(novo.created_at)||''}]); return novo.id; } catch(e){ return null; }
      }} onEnderecoAdded={(cid, novoEnd) => setClientes(prev => prev.map(cli => cli.id === cid ? { ...cli, enderecos: [...(cli.enderecos||[]), novoEnd] } : cli))} />}
      {colocadaModal && <ConfirmarColocadaModal locacoes={selecionadasData} motoristas={motoristas} cacambas={cacambas} frota={frota} onClose={() => setColocadaModal(false)} onConfirmar={confirmarColocada} />}
      {retiradaModal && <PedidoRetiradaModal locacoes={selecionadasData} onClose={() => setRetiradaModal(false)} onConfirmar={confirmarRetirada} />}
      {trocaModal && <PedidoTrocaModal locacoes={selecionadasData} cacambas={cacambas} onClose={() => setTrocaModal(false)} onConfirmar={confirmarTroca} />}
      {confirmarTrocaModal && <ConfirmarTrocaModal locacoes={selecionadasData} motoristas={motoristas} cacambas={cacambas} frota={frota} onClose={() => setConfirmarTrocaModal(false)} onConfirmar={confirmarTrocaReal} />}
      {confirmarRetiradaModal && <ConfirmarRetiradaModal locacoes={selecionadasData} motoristas={motoristas} cacambas={cacambas} frota={frota} onClose={() => setConfirmarRetiradaModal(false)} onConfirmar={confirmarRetiradaReal} apiFetch={apiFetch} onLocacaoAtualizada={patchLocacaoExtras} />}
      {pagamentoModal && <ConfirmarPagamentoModal locacoes={selecionadasData} onClose={() => setPagamentoModal(false)} onConfirmar={confirmarPagamento} apiFetch={apiFetch} onLocacaoAtualizada={patchLocacaoExtras} />}
      {prorrogarModal && <ProrrogarModal locacao={prorrogarModal} apiFetch={apiFetch} onClose={() => setProrrogarModal(null)} onSaved={(upd) => {
        patchLocacaoExtras(upd);
        const nd = upd?.data_retirada || upd?.dataRetirada || "";
        const p = String(nd).slice(0,10).split("-");
        if (addToast) addToast("Locação prorrogada", "#" + (upd?.id||"") + (p.length === 3 ? " — nova retirada " + p[2] + "/" + p[1] + "/" + p[0] : ""), "📅", "#1a9e8f");
      }} />}
      {alterarPagoModal && selecionadasData[0] && <AlterarPagamentoModal locacao={selecionadasData[0]} onClose={()=>{setAlterarPagoModal(false);setSelecionadas([]);}} onConfirm={(motivo) => {
        const loc = selecionadasData[0];
        setLocacoes(p => p.map(x => x.id===loc.id ? { ...x, status:"Para Cobrar", pago:false } : x));
        apiFetch("/api/locacoes/"+loc.id+"/status",{method:"PATCH",body:{status:"Para Cobrar"}}).catch(()=>{});
        apiFetch("/api/locacoes/"+loc.id,{method:"PUT",body:{...loc, pago:0}}).catch(()=>{});
        apiFetch("/api/historico/"+loc.id,{method:"POST",body:{acao:"Pagamento alterado para NÃO PAGO",detalhes:"Motivo: "+motivo+". Movida de Encerrada para Para Cobrar."}}).catch(()=>{});
        if(addToast) addToast("Locação movida para 'Para Cobrar'", motivo, "", "#f59e0b");
        setAlterarPagoModal(false); setSelecionadas([]);
        setTimeout(() => reloadLocacoes(), 800);
      }} />}
      {editModal && <EditLocacaoModal locacao={editModal} clientes={clientes} motoristas={motoristas} cacambas={cacambas} tiposCacamba={tiposCacamba} apiFetch={apiFetch} onClose={() => setEditModal(null)} onSave={async (updated) => {
        const wasPorCobrar = (editModal.status||"") === "Para Cobrar";
        const agoraPago = !!updated.pago;
        const novoStatus = (wasPorCobrar && agoraPago) ? "Encerrada" : (updated.status||editModal.status);
        // Atualizar UI local imediatamente
        setLocacoes(p => p.map(l => l.id === updated.id ? { ...l, ...updated, status: novoStatus, pago: agoraPago } : l));
        const promises = [];
        if (wasPorCobrar && agoraPago && novoStatus !== updated.status) {
          promises.push(apiFetch("/api/locacoes/"+updated.id+"/status", { method:"PATCH", body:{ status:"Encerrada" } }).catch(()=>{}));
          promises.push(apiFetch("/api/historico/"+updated.id, { method:"POST", body:{ acao:"Status → Encerrada", detalhes:"Pagamento confirmado via edição. Movido de Para Cobrar para Encerrada." } }).catch(()=>{}));
        }
        // ★ Se editou locação filha de troca, sincronizar dataTrocaPrevista e dataRetirada no pai
        const childObs = updated.obs || updated.observacoes || editModal.obs || "";
        const parentMatch = typeof childObs === "string" && childObs.match(/troca da loca[çc][aã]o #?(\d+)/i);
        if (parentMatch) {
          const parentId = parentMatch[1];
          const novaDataEntrega = updated.data_entrega || updated.dataEntrega || "";
          const novaDataRetirada = updated.data_retirada || updated.dataRetirada || "";
          promises.push(apiFetch("/api/locacoes/" + parentId + "/status", { method:"PATCH", body:{ dataTrocaPrevista: novaDataEntrega || null, dataRetiraPrevista: novaDataRetirada || null } }).catch(e => console.warn("Erro ao sincronizar datas do pai:", e.message)));
        }
        // Esperar TODAS as chamadas terminarem antes de recarregar
        await Promise.all(promises);
        // Recarregar TUDO do banco para garantir dados corretos
        setTimeout(() => reloadLocacoes(), 300);
        if(addToast) addToast("Locação atualizada!", wasPorCobrar && agoraPago ? "Movida para Encerrada (pago)" : "", "", "#2ecc71");
        setEditModal(null);
      }} />}
      {detailLoc && <LocacaoDetailModal locacao={detailLoc} onClose={() => setDetailLoc(null)} apiFetch={apiFetch} onUpdate={updated => { setLocacoes(p => p.map(l => l.id===updated.id ? updated : l)); setDetailLoc(updated); }} />}
      {cancelModal && <CancelLocacaoModal locacao={cancelModal} onClose={() => setCancelModal(null)} onConfirm={(motivo) => {
        // Buscar locação filha de troca se existir
        const childLoc = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + cancelModal.id) || cl.obs.toLowerCase().includes("troca da locacao #" + cancelModal.id)));
        setLocacoes(p => p.map(x => {
          if (x.id === cancelModal.id) return { ...x, status:"Cancelada" };
          if (childLoc && x.id === childLoc.id) return { ...x, status:"Cancelada" };
          return x;
        }));
        apiFetch("/api/locacoes/"+cancelModal.id+"/status",{method:"PATCH",body:{status:"Cancelada"}}).catch(()=>{});
        apiFetch("/api/historico/"+cancelModal.id,{method:"POST",body:{acao:"Cancelamento",detalhes:"Motivo: "+motivo}}).catch(()=>{});
        if (childLoc) {
          apiFetch("/api/locacoes/"+childLoc.id+"/status",{method:"PATCH",body:{status:"Cancelada"}}).catch(()=>{});
          apiFetch("/api/historico/"+childLoc.id,{method:"POST",body:{acao:"Cancelamento",detalhes:"Cancelada junto com locação pai #"+cancelModal.id+". Motivo: "+motivo}}).catch(()=>{});
        }
        if(addToast) addToast("Locação cancelada", cancelModal.cliente+" — "+motivo, "", "#e74c3c");
        setCancelModal(null);
        setTimeout(() => reloadLocacoes(), 800);
      }} />}
      {successModal && <SuccessModal title="Sucesso!" message="Locação cadastrada com sucesso." detail={"Pedido #" + successModal.id} onClose={() => setSuccessModal(null)} />}

      {/* Modal Visualização — seletor de colunas extras da lista */}
      {visualizacaoModal && (
        <LargeModal title="Colunas da lista" subtitle={"Aba: " + stFilter + " — as colunas padrão são fixas; escolha colunas adicionais"} onClose={() => setVisualizacaoModal(false)} maxWidth={620} cancelLabel={null} onSave={() => setVisualizacaoModal(false)} saveLabel="OK"
          extraFooter={<button onClick={() => setColunasExtras([])} style={{ padding:"7px 14px", background:"#fff", border:"1px solid #e0e0e0", borderRadius:3, fontSize:12, cursor:"pointer", fontWeight:600, color:"#888", fontFamily:FONT }}>Restaurar padrão</button>}>
          <div style={{ paddingTop:16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, marginBottom:8 }}>Colunas padrão</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 16px", marginBottom:18 }}>
              {LOC_COLUNAS_PADRAO(stFilter).map(h => (
                <label key={h} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"#999", cursor:"default" }}>
                  <input type="checkbox" checked disabled style={{ accentColor:"#1aae9e" }} />
                  {h} <span style={{ fontSize:10, color:"#bbb", fontWeight:600 }}>(padrão)</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize:11, fontWeight:700, color:"#1aae9e", textTransform:"uppercase", letterSpacing:.3, marginBottom:8 }}>Colunas adicionais</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 16px" }}>
              {LOC_COLUNAS_EXTRAS.map(c => {
                const on = colunasExtras.includes(c.key);
                return (
                  <label key={c.key} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color: on ? "#1a1a1a" : "#444", fontWeight: on ? 700 : 400, cursor:"pointer" }}>
                    <input type="checkbox" checked={on} onChange={() => setColunasExtras(p => p.includes(c.key) ? p.filter(k => k !== c.key) : [...p, c.key])} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
                    {c.label}
                  </label>
                );
              })}
            </div>
            <div style={{ fontSize:11, color:"#aaa", marginTop:14 }}>As colunas adicionais aparecem antes do menu de ações e valem para todas as abas. A escolha fica salva neste navegador para o seu usuário.</div>
          </div>
        </LargeModal>
      )}

      {/* Modal Enviar link de assinatura (menu hambúrguer) */}
      {assinaturaModal && (() => {
        const am = assinaturaModal;
        const telDigits = String(am.telefone || "").replace(/\D/g, "");
        const enviar = async () => {
          if (telDigits.length < 10) { if (addToast) addToast("Telefone inválido", "Informe o telefone com DDD", "⚠️", "#e74c3c"); return; }
          setAssinaturaModal(p => p ? { ...p, enviando: true } : p);
          try {
            const r = await apiFetch("/api/locacoes/" + am.loc.id + "/assinatura/enviar-link", { method:"POST", body:{ telefone: am.telefone, nome_cliente: (am.nome || "").trim() || null } });
            setAssinaturaModal(p => p ? { ...p, enviando: false, link: r.link_assinatura || "" } : p);
            if (r.whatsapp_url) window.open(r.whatsapp_url, "_blank");
            if (addToast) addToast("Link de assinatura gerado", "WhatsApp aberto para " + am.telefone, "✍️", "#25D366");
          } catch(e) {
            setAssinaturaModal(p => p ? { ...p, enviando: false } : p);
            if (addToast) addToast("Erro ao gerar link", e.message || "Falha na requisição", "⚠️", "#e74c3c");
          }
        };
        const copiar = () => {
          if (!am.link) return;
          const done = () => { if (addToast) addToast("Link copiado", "", "📋", "#1a9e8f"); };
          if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(am.link).then(done).catch(() => window.prompt("Copie o link:", am.link));
          else window.prompt("Copie o link:", am.link);
        };
        return (
          <LargeModal title="Enviar link de assinatura" subtitle={"Locação #" + am.loc.id + " — " + (am.loc.cliente || am.loc.cliente_nome || "")} onClose={() => setAssinaturaModal(null)} maxWidth={480}
            extraFooter={<button onClick={enviar} disabled={am.enviando} style={{ padding:"7px 18px", background: am.enviando ? "#ccc" : "#25D366", border:"none", borderRadius:3, fontSize:13, cursor: am.enviando ? "default" : "pointer", fontWeight:700, color:"#fff", fontFamily:FONT }}>{am.enviando ? "Gerando..." : (am.link ? "Reenviar pelo WhatsApp" : "Enviar pelo WhatsApp")}</button>}>
            <div style={{ paddingTop:16, display:"flex", flexDirection:"column", gap:12 }}>
              <Field label="Nome do cliente">
                <input value={am.nome} onChange={e => { const v = e.target.value; setAssinaturaModal(p => p ? { ...p, nome: v } : p); }} style={{ width:"100%", padding:"8px 10px", border:"1px solid #ddd", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" }} />
              </Field>
              <Field label="Telefone (WhatsApp)">
                <input value={am.telefone} onChange={e => { const v = e.target.value; setAssinaturaModal(p => p ? { ...p, telefone: v } : p); }} placeholder="(11) 99999-9999" style={{ width:"100%", padding:"8px 10px", border:"1px solid #ddd", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" }} />
              </Field>
              <div style={{ fontSize:11, color:"#888" }}>Gera um link válido por 24h para o cliente assinar o recibo digitalmente e abre o WhatsApp com a mensagem pronta.</div>
              {am.link && (
                <div style={{ background:"#f0faf8", border:"1px solid #cdeee8", borderRadius:6, padding:10 }}>
                  <div style={{ fontSize:10, fontWeight:700, color:"#1a9e8f", textTransform:"uppercase", marginBottom:4 }}>Link gerado (fallback)</div>
                  <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                    <input readOnly value={am.link} onFocus={e => e.target.select()} style={{ flex:1, padding:"6px 8px", border:"1px solid #ddd", borderRadius:4, fontSize:11, fontFamily:FONT, background:"#fff" }} />
                    <button onClick={copiar} style={{ padding:"6px 12px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:4, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>Copiar</button>
                  </div>
                </div>
              )}
            </div>
          </LargeModal>
        );
      })()}

      {/* Tutorial primeira vez */}
      {showTutorial && <TutorialLocacoesModal onClose={() => { localStorage.setItem("controla_tutorial_locacoes", "1"); setShowTutorial(false); }} />}

      {/* Modal WhatsApp Personalizado */}
      {whatsAppCustomModal && (() => {
        const loc = whatsAppCustomModal;
        const sendWhatsApp = () => {
          if (!whatsAppCustomMsg.trim()) return;
          const tel = (loc.contatoTel || loc.contato_tel || "").replace(/\D/g, "");
          const phone = tel.length <= 11 ? "55" + tel : tel;
          window.open("https://wa.me/" + phone + "?text=" + encodeURIComponent(whatsAppCustomMsg), "_blank");
          setWhatsAppCustomModal(null);
        };
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <div style={{ background:"#fff", borderRadius:12, width:480, maxWidth:"92vw", boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden" }}>
              <div style={{ background:"#25D366", padding:"16px 20px", display:"flex", alignItems:"center", gap:10 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.96 7.96 0 01-4.108-1.14l-.292-.174-3.04.798.81-2.96-.19-.3A7.96 7.96 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/></svg>
                <div>
                  <div style={{ color:"#fff", fontWeight:800, fontSize:15, fontFamily:FONT }}>Enviar WhatsApp Personalizado</div>
                  <div style={{ color:"rgba(255,255,255,.8)", fontSize:11 }}>{loc.cliente || loc.cliente_nome} — #{loc.id}</div>
                </div>
              </div>
              <div style={{ padding:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", marginBottom:6, letterSpacing:.3 }}>Mensagem</div>
                <textarea
                  autoFocus
                  value={whatsAppCustomMsg}
                  onChange={e => setWhatsAppCustomMsg(e.target.value)}
                  placeholder="Digite a mensagem que deseja enviar ao cliente..."
                  style={{ width:"100%", height:120, padding:12, border:"1px solid #ddd", borderRadius:8, fontSize:13, fontFamily:FONT, resize:"vertical", outline:"none", boxSizing:"border-box" }}
                />
                <div style={{ fontSize:10, color:"#aaa", marginTop:4 }}>📞 {loc.contatoTel || loc.contato_tel || "—"}</div>
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:8, padding:"0 20px 16px" }}>
                <button onClick={() => setWhatsAppCustomModal(null)} style={{ padding:"9px 20px", background:"#f5f5f5", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, color:"#666" }}>Cancelar</button>
                <button onClick={sendWhatsApp} disabled={!whatsAppCustomMsg.trim()} style={{ padding:"9px 24px", background:whatsAppCustomMsg.trim()?"#25D366":"#ccc", color:"#fff", border:"none", borderRadius:6, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.96 7.96 0 01-4.108-1.14l-.292-.174-3.04.798.81-2.96-.19-.3A7.96 7.96 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"/></svg>
                  Enviar Mensagem por WhatsApp
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Mapa Operacional ─────────────────────────────────────────────────────────
const MAPA_STATUS = ["Para Colocar","Colocada","Para Retirar","Para Trocar"];
const MAPA_COLORS = { "Para Colocar":"#1a9e8f", "Colocada":"#888888", "Para Retirar":"#9b59b6", "Para Trocar":"#E67E22", "Atrasada":"#e74c3c" };
const MAPA_RIBS   = { "Para Colocar":"#148f82", "Colocada":"#6b6b6b", "Para Retirar":"#884EA0", "Para Trocar":"#D35400", "Atrasada":"#c0392b" };
const MAPA_DARKS  = { "Para Colocar":"#0d7a6e", "Colocada":"#555555", "Para Retirar":"#7D3C98", "Para Trocar":"#A04000", "Atrasada":"#a93226" };

// ─── Estilos de tiles do MapBox para o Mapa Operacional ───
const MAPA_TILE_STYLES = {
  streets:   "mapbox/streets-v12",
  satellite: "mapbox/satellite-streets-v12", // satélite + rótulos (hybrid)
};

function MapaOperacional({ locacoes, cacambas, clientes, empresaNome, goToLocacoes, onViewLocacao, addToast, onReloadLocacoes }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const tileLayerRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState("TODAS");
  const [selLoc, setSelLoc] = useState(null);
  const [searchMap, setSearchMap] = useState("");
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const [regeocoding, setRegeocoding] = useState(false);
  const [mapStyle, setMapStyle] = useState(() => {
    const saved = localStorage.getItem("controla_mapa_style");
    return (saved === "satellite" || saved === "streets") ? saved : "streets";
  });
  const geoCacheRef = useRef({});
  const [showTutorialMapa, setShowTutorialMapa] = useState(() => !localStorage.getItem("controla_tutorial_mapa"));
  // Ref para callbacks de ação nos popups — navega para Locações (usa os modais de lá)
  const popupActionRef = useRef(null);
  popupActionRef.current = (action, id) => {
    const loc = locacoes.find(l => l.id === id);
    if (!loc) return;
    const map = mapInstanceRef.current;
    if (map) map.closePopup();
    if (action === "ver_locacao" && onViewLocacao) {
      onViewLocacao(loc);
    } else {
      if (goToLocacoes) goToLocacoes(loc.status);
    }
  };
  const hoje = hojeLocal();

  // Helper: detectar se é locação filha de troca (obs contém "troca da locação #")
  const isFilhaTroca = (l) => {
    const obs = l.obs || l.observacoes || "";
    return typeof obs === "string" && (obs.toLowerCase().includes("troca da locação #") || obs.toLowerCase().includes("troca da locacao #"));
  };
  // Helper: detectar se é locação pai que tem filha de troca ativa
  const isPaiTrocaComFilha = (l) => {
    if (l.status !== "Para Trocar") return false;
    // Verificar se existe uma filha para esta locação
    return locacoes.some(cl => isFilhaTroca(cl) && (cl.obs||cl.observacoes||"").includes("#" + l.id) && cl.status !== "Cancelada");
  };

  // Só mostrar locações ativas — esconde pai "Para Trocar" que tem filha ativa
  const ativasRaw = locacoes.filter(l => MAPA_STATUS.includes(l.status));
  const ativas = ativasRaw.filter(l => !isPaiTrocaComFilha(l)).map(l => {
    // Se é filha de troca e está "Para Colocar", mostrar como "Para Trocar" no mapa
    if (isFilhaTroca(l) && l.status === "Para Colocar") {
      return { ...l, _mapaStatus: "Para Trocar" };
    }
    return l;
  });

  // Locações atrasadas (data prevista já passou)
  const atrasadas = ativas.filter(l => {
    const dataRef = l.status === "Para Colocar" ? (l.dataEntrega||l.data_entrega) : (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada);
    return dataRef && dataRef < hoje;
  });

  const diasAtraso = (l) => {
    const dataRef = l.status === "Para Colocar" ? (l.dataEntrega||l.data_entrega) : (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada);
    if (!dataRef) return 0;
    return Math.floor((new Date(hoje) - new Date(dataRef)) / (1000*60*60*24));
  };

  // Gerar link WhatsApp com mensagem de cobrança
  // Helper: buscar telefone correto da locação
  const getTelefone = (l) => {
    const cli = (clientes||[]).find(c => c.nome === l.cliente || String(c.id) === String(l.clienteId || l.cliente_id));
    return l.contatoTel || l.contato_tel || cli?.tel || l.telefone || l.tel || "";
  };

  const gerarWhatsApp = (l) => {
    const dias = diasAtraso(l);
    const tel = getTelefone(l);
    const telLimpo = tel.replace(/\D/g, "");
    const telWpp = telLimpo.startsWith("55") ? telLimpo : "55" + telLimpo;
    const addr = l.logradouro ? [l.logradouro, l.numero, l.bairro].filter(Boolean).join(", ") : (l.endereco && typeof l.endereco === "object") ? [l.endereco.logradouro, l.endereco.numero, l.endereco.bairro].filter(Boolean).join(", ") : "";
    const msg = `Prezado(a) ${l.cliente || "cliente"},\n\nInformamos que a caçamba locada no endereço *${addr}* (Locação #${l.id}) está com *${dias} dia${dias>1?"s":""} de atraso* para ${l.status === "Para Colocar" ? "entrega" : l.status === "Para Trocar" ? "troca" : "retirada"}.\n\nSolicitamos a regularização o mais breve possível.\n\nAtenciosamente,\n${empresaNome || "Controla Caçambas"}`;
    return `https://wa.me/${telWpp}?text=${encodeURIComponent(msg)}`;
  };

  const getAddress = (l) => {
    // Montar endereço completo — formato: "Logradouro Numero, Bairro, Cidade, UF"
    const e = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
    const log = l.logradouro || e.logradouro || "";
    const num = l.numero || e.numero || "";
    const bai = l.bairro || e.bairro || "";
    const cid = l.cidade || e.cidade || "";
    const uf = l.uf || e.uf || "";
    // Formato otimizado para geocodificação: "Rua X 123, Bairro, Cidade, UF"
    if (log) {
      const rua = num ? (log + " " + num) : log;
      return [rua, bai, cid, uf].filter(Boolean).join(", ");
    }
    if (typeof l.endereco === "string" && l.endereco) return l.endereco;
    if (bai || cid) return [bai, cid, uf].filter(Boolean).join(", ");
    return "";
  };

  // Determinar se uma locação é atrasada
  const isAtrasada = (l) => {
    const dataRef = l.status === "Para Colocar" ? (l.dataEntrega||l.data_entrega) : (l.dataRetiraPrevista||l.dataRetirada||l.data_retirada);
    return dataRef && dataRef < hoje;
  };

  // Retorna a key de cor para o marcador (atrasadas ficam vermelhas, filha troca usa _mapaStatus)
  const markerColorKey = (l) => isAtrasada(l) ? "Atrasada" : (l._mapaStatus || l.status);

  const baseFiltered = activeFilter === "TODAS" ? ativas : activeFilter === "ATRASADAS" ? atrasadas : ativas.filter(l => (l._mapaStatus || l.status) === activeFilter);
  const filtered = baseFiltered.filter(l => {
    if (!searchMap.trim()) return true;
    const s = norm(searchMap);
    return String(l.id).includes(s) || norm(l.cliente).includes(s) || norm(l.cacamba).includes(s) || norm(getAddress(l)).includes(s);
  });

  const createMarkerIcon = (status) => {
    const main = MAPA_COLORS[status] || "#666";
    const rib  = MAPA_RIBS[status]   || "#555";
    const dark = MAPA_DARKS[status]   || "#444";
    const isAtras = status === "Atrasada";
    return L.divIcon({
      className: '',
      iconSize: [48, 44],
      iconAnchor: [24, 40],
      popupAnchor: [0, -38],
      html: `<div style="filter:drop-shadow(0 2px 5px rgba(0,0,0,0.45));cursor:pointer;${isAtras?"animation:atrasadaPulse 1.2s ease-in-out infinite":""}">
        <svg width="48" height="44" viewBox="0 0 48 44" xmlns="http://www.w3.org/2000/svg">
          <polygon points="19,38 29,38 24,44" fill="${dark}"/>
          <polygon points="4,12 44,12 40,38 8,38" fill="${main}" stroke="${dark}" stroke-width="1"/>
          <rect x="2" y="9" width="44" height="5" rx="1.5" fill="${dark}"/>
          <path d="M14 9 C14 3 20 3 20 9" fill="none" stroke="${rib}" stroke-width="2" stroke-linecap="round"/>
          <path d="M28 9 C28 3 34 3 34 9" fill="none" stroke="${rib}" stroke-width="2" stroke-linecap="round"/>
          <line x1="16" y1="14" x2="14" y2="36" stroke="${rib}" stroke-width="1" opacity="0.5"/>
          <line x1="24" y1="14" x2="24" y2="36" stroke="${rib}" stroke-width="1" opacity="0.5"/>
          <line x1="32" y1="14" x2="34" y2="36" stroke="${rib}" stroke-width="1" opacity="0.5"/>
          <text x="24" y="29" font-family="Arial,sans-serif" font-weight="900" font-size="9" fill="#fff" text-anchor="middle" opacity="0.85">CC</text>
        </svg>
      </div>
      ${isAtras?'<style>@keyframes atrasadaPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.15);opacity:0.7}}</style>':''}`,
    });
  };

  // Carregar cache do localStorage — v3 = cache limpo com geocodificação corrigida
  if (Object.keys(geoCacheRef.current).length === 0) {
    try {
      const ver = localStorage.getItem("geo_cache_ver");
      if (ver !== "v8") { localStorage.removeItem("geo_cache"); localStorage.setItem("geo_cache_ver","v8"); }
      const saved = JSON.parse(localStorage.getItem("geo_cache") || "{}");
      geoCacheRef.current = saved;
    } catch(e) {}
  }
  const saveGeoCache = () => { try { localStorage.setItem("geo_cache", JSON.stringify(geoCacheRef.current)); } catch(e) {} };

  // Limpar logradouro para geocodificação — remove ruído que confunde a API
  const cleanLog = (raw) => {
    if (!raw) return "";
    let s = raw.trim();
    s = s.replace(/\s*\([^)]*\)\s*/g, " ").trim();  // Remove (RESIDENCIAL TAMBORÉ TRÊS) etc
    s = s.replace(/[Nn][º°]:?\s*/g, "").trim();       // Remove Nº:, N°, nº
    s = s.replace(/,\s*,/g, ",").replace(/\s{2,}/g, " ").trim();
    return s;
  };
  const cleanNum = (raw) => {
    if (!raw) return "";
    return raw.replace(/[Nn][º°]:?\s*/g, "").trim();
  };

  // Cache de centro das cidades (evita re-geocodificar a mesma cidade várias vezes)
  const cidadeCoordsCache = useRef({});
  const getCidadeCoords = async (cidade, uf) => {
    if (!cidade) return null;
    const key = (cidade + "|" + (uf || "")).toLowerCase();
    if (cidadeCoordsCache.current[key]) return cidadeCoordsCache.current[key];
    try {
      const q = cidade + (uf ? ", " + uf : "") + ", Brasil";
      const url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&language=pt&types=place";
      const resp = await fetch(url);
      const data = await resp.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        cidadeCoordsCache.current[key] = lng + "," + lat;
        return lng + "," + lat;
      }
    } catch(e) {}
    return null;
  };

  // Geocodificar locação via Mapbox — usa campos limpos + CEP + proximity da cidade
  const geocodeLocacao = async (l) => {
    const e = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
    const log = cleanLog(l.logradouro || e.logradouro || "");
    const num = cleanNum(l.numero || e.numero || "");
    const bai = (l.bairro || e.bairro || "").trim();
    const cid = (l.cidade || e.cidade || "").trim();
    const uf = (l.uf || e.uf || "").trim();
    const cep = (l.cep || e.cep || "").replace(/\D/g, "").trim();

    const cacheKey = [log, num, bai, cid, uf, cep].filter(Boolean).join("|").toLowerCase();
    if (!cacheKey) return null;
    if (geoCacheRef.current[cacheKey]) return geoCacheRef.current[cacheKey];

    const save = (coords, tag) => {
      geoCacheRef.current[cacheKey] = coords;
      saveGeoCache();
      console.log("[GEO]", tag, "#" + l.id, coords[0].toFixed(5) + "," + coords[1].toFixed(5), "|", log, num, cid);
      return coords;
    };
    const ok = (lat, lng) => lat >= -34 && lat <= 6 && lng >= -75 && lng <= -34;

    // PASSO 1: Pegar coordenadas do centro da cidade para usar como proximity
    // Isso garante que "Rua do Desterro, Barueri" vai priorizar Barueri e não São Paulo
    const proximity = await getCidadeCoords(cid, uf);
    const proxParam = proximity ? "&proximity=" + proximity : "";

    // Centro da cidade (lat/lng) pra validar distância — evita cair em cidade homônima de outro estado
    let centroCidade = null;
    if (proximity) {
      const pp = proximity.split(",").map(Number);
      if (pp.length === 2 && !isNaN(pp[0]) && !isNaN(pp[1])) centroCidade = { lng: pp[0], lat: pp[1] };
    }
    const distGraus = (a, b) => Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2);
    const pertoDaCidade = (lat, lng) => {
      if (!centroCidade) return true;
      return distGraus({ lat, lng }, centroCidade) <= 1.0; // ~110km
    };

    // Montar queries do mais específico para o mais genérico
    // Cada query carrega a flag temRua — quando inclui logradouro, resultado nível cidade
    // (place/locality) NÃO é aceitável. Anti-fix: "Alameda São Vicente, Santana de Parnaíba/SP"
    // caía no MUNICÍPIO de São Vicente/SP (litoral, ~85km — passava no filtro de distância).
    const rua = log ? (num ? log + " " + num : log) : "";
    const queries = [];
    // 1) CEP + Rua (CEP é o mais preciso — identifica a rua exata na cidade certa)
    if (cep && cep.length >= 8 && rua) queries.push({ q: rua + ", " + cep + ", Brasil", temRua: true });
    if (cep && cep.length >= 8) queries.push({ q: cep + ", Brasil", temRua: false });
    // 2) Rua + Cidade
    if (rua && cid) queries.push({ q: rua + ", " + cid + (uf ? ", " + uf : "") + ", Brasil", temRua: true });
    // 3) Rua + Bairro + Cidade
    if (rua && bai && cid) queries.push({ q: rua + ", " + bai + ", " + cid + (uf ? ", " + uf : "") + ", Brasil", temRua: true });
    // 4) Só Rua + Cidade (sem numero)
    if (log && cid && num) queries.push({ q: log + ", " + cid + (uf ? ", " + uf : "") + ", Brasil", temRua: true });
    // 5) Bairro + Cidade (fallback)
    if (bai && cid) queries.push({ q: bai + ", " + cid + (uf ? ", " + uf : "") + ", Brasil", temRua: false });

    // Validar se resultado do Mapbox bate com o UF esperado
    const ufMatch = (feat) => {
      if (!uf || !feat.context) return true; // sem UF pra comparar, aceita
      const region = feat.context.find(c => c.id && c.id.startsWith("region"));
      if (!region) return true;
      const sc = region.short_code ? region.short_code.replace("BR-","").toUpperCase() : "";
      return sc === uf.toUpperCase() || region.text?.toUpperCase() === uf.toUpperCase();
    };

    // Tipos aceitáveis quando a query contém logradouro — nível cidade ou acima é descartado
    const TIPOS_ENDERECO = ["address", "street", "postcode", "neighborhood", "poi"];
    const tipoOk = (feat, temRua) => {
      if (!temRua) return true;
      return (feat.place_type || []).some(t => TIPOS_ENDERECO.includes(t));
    };
    // Normaliza pra comparação: minúsculas, sem acentos
    const normTxt = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
    const cidNorm = normTxt(cid);
    // Município do resultado (context "place.") tem que bater com a cidade esperada
    const cidadeMatch = (feat) => {
      if (!cidNorm) return true;
      const placeItem = (feat.context || []).find(c => c.id && String(c.id).startsWith("place."));
      if (placeItem) return normTxt(placeItem.text_pt || placeItem.text) === cidNorm;
      // Feature É a própria cidade/localidade (sem place no context) — compara o nome dela
      if ((feat.place_type || []).some(t => t === "place" || t === "locality")) {
        return normTxt(feat.text_pt || feat.text) === cidNorm;
      }
      return true; // sem info de município — deixa pros filtros de UF + distância
    };

    try {
      for (let qi = 0; qi < queries.length; qi++) {
        const { q, temRua } = queries[qi];
        const types = qi < 2 ? "&types=address" : "";
        const url = "https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=5&language=pt" + types + proxParam;
        const resp = await fetch(url);
        const data = await resp.json();
        if (data.features && data.features.length > 0) {
          // 1) Filtrar por UF (quando informada). Se nenhum bate a UF, NÃO usa pool genérico.
          let pool = uf ? data.features.filter(f => ufMatch(f)) : data.features;
          // 2) Query tem rua → resultado nível cidade não serve (rua com nome de cidade)
          pool = pool.filter(f => tipoOk(f, temRua));
          // 3) Município do resultado tem que bater com a cidade informada
          pool = pool.filter(cidadeMatch);
          // 4) Filtrar por distância do centro da cidade (rejeita cidade homônima de outro estado)
          pool = pool.filter(f => { const c = f.center; return c && pertoDaCidade(c[1], c[0]); });
          if (pool.length === 0) continue; // nada confiável nesta query → tenta a próxima
          // Escolher resultado que contenha a cidade no context
          const matchCity = cid ? pool.find(f => f.relevance >= 0.4 && f.context?.some(c => c.text?.toLowerCase().includes(cid.toLowerCase()))) : null;
          const best = matchCity
            || pool.find(f => f.relevance >= 0.6 && f.place_type?.includes("address"))
            || pool.find(f => f.relevance >= 0.5)
            || pool[0];
          if (best && best.relevance >= 0.3) {
            const [lng, lat] = best.center;
            if (ok(lat, lng)) return save([lat, lng], "✅q" + (qi + 1));
          }
        }
      }
      // Fallback seguro: nenhuma query achou endereço confiável dentro da cidade.
      // Em vez de aceitar resultado de outro estado, usa o CENTRO DA CIDADE correta.
      if (centroCidade && ok(centroCidade.lat, centroCidade.lng)) {
        return save([centroCidade.lat, centroCidade.lng], "⚠️centro-cidade");
      }
      console.warn("[GEO] ❌ #" + l.id, log, num, bai, cid);
    } catch(err) { console.warn("[GEO] erro #" + l.id, err); }
    return null;
  };

  // Inicializar mapa
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true }).setView([-23.55, -46.63], 11);
    const styleId = MAPA_TILE_STYLES[mapStyle] || MAPA_TILE_STYLES.streets;
    tileLayerRef.current = L.tileLayer("https://api.mapbox.com/styles/v1/" + styleId + "/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN, {
      attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a>',
      tileSize: 512, zoomOffset: -1, maxZoom: 22
    }).addTo(map);
    mapInstanceRef.current = map;
    setTimeout(() => { map.invalidateSize(); }, 300);
    return () => { map.remove(); mapInstanceRef.current = null; tileLayerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trocar camada de tiles quando o estilo muda (sem destruir o mapa)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const styleId = MAPA_TILE_STYLES[mapStyle] || MAPA_TILE_STYLES.streets;
    const newLayer = L.tileLayer("https://api.mapbox.com/styles/v1/" + styleId + "/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN, {
      attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a>',
      tileSize: 512, zoomOffset: -1, maxZoom: 22
    });
    newLayer.addTo(map);
    if (tileLayerRef.current) {
      try { map.removeLayer(tileLayerRef.current); } catch(_){}
    }
    tileLayerRef.current = newLayer;
    try { localStorage.setItem("controla_mapa_style", mapStyle); } catch(_){}
  }, [mapStyle]);

  // Helper: criar popup como DOM element (garante que botões funcionam)
  const makePopupEl = (l, addr) => {
    const displayStatus = l._mapaStatus || l.status;
    const statusColor = MAPA_COLORS[markerColorKey(l)] || "#666";
    const cObj = (cacambas||[]).find(c => c.codigo === l.cacamba);
    const tipoCac = l.tipoCacamba || l.tipo_cacamba || (cObj ? (cObj.tipo||"") : "");
    const volumeCac = l.volumeCacamba || l.volume_cacamba || (cObj ? (cObj.volume||"") : "");
    const tipoCacFull = tipoCac ? tipoCac + (volumeCac ? " / "+volumeCac : "") : "";
    const tel = getTelefone(l);
    const contato = l.contatoNome || l.contato_nome || l.contato || "";
    const dias = diasAtraso(l);
    const atraso = dias > 0;
    const dataEnt = l.dataEntrega || l.data_entrega || "";
    const dataRetPrev = l.dataRetiraPrevista || l.dataRetirada || l.data_retirada || "";
    const dataRetReal = l.dataRetiradaReal || l.data_retirada_real || "";
    const dataColocada = l.dataColocacaoReal || l.dataColocacao || l.data_colocacao || dataEnt || "";
    const valor = l.valor ? "R$"+Number(l.valor).toLocaleString("pt-BR",{minimumFractionDigits:2}) : "";
    const pago = !!l.pago;

    const R = (lbl, val) => '<tr><td style="color:#888;padding:3px 10px 3px 0;white-space:nowrap;font-size:11px">'+lbl+'</td><td style="font-weight:600;font-size:11px">'+val+'</td></tr>';
    const d = (v) => v ? fmtDate(v) : "—";
    const teal = (v) => '<span style="color:#1a9e8f;font-weight:600">'+(v||"—")+'</span>';

    // Badge de atraso
    const atrasoBadge = atraso
      ? '<div style="margin:8px 0;padding:6px 12px;border-radius:6px;background:#fef2f2;border:1px solid #fecaca;color:#e74c3c;font-weight:700;font-size:11px;display:flex;align-items:center;gap:6px"><span style="font-size:14px">⚠️</span> Retirada atrasada '+dias+' dia'+(dias>1?"s":"")+'</div>'
      : '';

    // Badge de pagamento
    const pagoBadge = pago
      ? '<span style="background:#e6f4ea;color:#1a7a38;padding:3px 10px;border-radius:10px;font-weight:700;font-size:10px">PAGO</span>'
      : '<span style="background:#fef2f2;color:#e74c3c;padding:3px 10px;border-radius:10px;font-weight:700;font-size:10px">NÃO PAGO</span>';

    // Status badge
    const statusLabel = displayStatus || "";
    const statusBadge = '<span style="background:'+statusColor+'22;color:'+statusColor+';padding:3px 10px;border-radius:10px;font-weight:700;font-size:10px;text-transform:uppercase">'+statusLabel+'</span>';

    // Info rows - sempre mostra tudo
    let infoRows = "";
    infoRows += R("Colocada em", d(dataColocada));
    infoRows += R("Prev. Retirada", d(dataRetPrev));
    if (dataRetReal) infoRows += R("Retirada Real", '<b style="color:#2ecc71">'+d(dataRetReal)+'</b>');
    infoRows += R("Nº da Caçamba", teal(l.cacamba||"N/I"));
    infoRows += R("Tipo de Caçamba", teal(tipoCacFull));
    infoRows += R("Colocada por", teal(l.motorista||"—"));
    infoRows += R("Valor Total", '<span style="background:#f0f0f0;padding:2px 8px;border-radius:4px;font-weight:700">'+(valor||"—")+'</span>');

    // Criar DOM element
    const div = document.createElement("div");
    div.style.cssText = "font-family:system-ui;min-width:340px;max-width:420px;padding:0";

    div.innerHTML =
      // Header: cliente + badges
      '<div style="margin-bottom:8px">'
        +'<div style="font-weight:800;font-size:14px;color:#1a1a1a;text-transform:uppercase;margin-bottom:4px">'+(l.cliente||"")+'</div>'
        +(contato || tel ? '<div style="font-size:11px;color:#555;margin-bottom:4px">'+(contato?(contato+(tel?" / "+tel:"")):(tel||""))+'</div>' : '')
        +'<div style="font-size:11px;color:#555;line-height:1.4;text-transform:uppercase;margin-bottom:6px">'+(addr||"")+'</div>'
        +'<div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">'+statusBadge+' '+pagoBadge+'</div>'
      +'</div>'
      // Atraso
      +atrasoBadge
      // Info table
      +'<div style="border-top:1px dashed #ddd;padding-top:8px;margin-bottom:8px"><table style="width:100%;border-collapse:collapse;line-height:1.9">'+infoRows+'</table></div>'
      // Actions area
      +'<div id="popup-actions" style="border-top:1px solid #eee;padding-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center"></div>';

    // Botões
    const actionsDiv = div.querySelector("#popup-actions");

    // Botão "Ver Locação #XXXXX" — navega para a página
    const btnVer = document.createElement("button");
    btnVer.textContent = "Ver Locação #" + l.id;
    btnVer.style.cssText = "display:inline-flex;align-items:center;gap:4px;padding:7px 16px;border:none;border-radius:4px;font-size:11px;font-weight:700;cursor:pointer;font-family:system-ui;color:#fff;background:#1a9e8f;transition:all .15s";
    btnVer.addEventListener("mouseenter", () => { btnVer.style.opacity="0.8"; });
    btnVer.addEventListener("mouseleave", () => { btnVer.style.opacity="1"; });
    btnVer.addEventListener("click", (ev) => {
      ev.preventDefault(); ev.stopPropagation();
      if (popupActionRef.current) popupActionRef.current("ver_locacao", l.id);
    });
    actionsDiv.appendChild(btnVer);

    // Botão "Alertar Caçamba vencida" se atrasada
    if (atraso && tel) {
      const btnAlerta = document.createElement("button");
      btnAlerta.textContent = "Alertar Caçamba vencida";
      btnAlerta.style.cssText = "display:inline-flex;align-items:center;gap:4px;padding:7px 14px;border:none;border-radius:4px;font-size:11px;font-weight:700;cursor:pointer;font-family:system-ui;color:#fff;background:#f39c12;transition:all .15s";
      btnAlerta.addEventListener("mouseenter", () => { btnAlerta.style.opacity="0.8"; });
      btnAlerta.addEventListener("mouseleave", () => { btnAlerta.style.opacity="1"; });
      btnAlerta.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        const num = tel.replace(/\D/g,"");
        const fullNum = (num.length <= 11 ? "55" : "") + num;
        const nome = (l.cliente||"").split(" ")[0];
        const msg = "Olá "+nome+"! Informamos que a caçamba"+(tipoCacFull?" ("+tipoCacFull+")":"")+" da locação #"+l.id+" está com o prazo vencido há "+dias+" dia"+(dias>1?"s":"")+". Por favor, entre em contato para agendar a retirada ou renovar a locação. Obrigado! — "+(empresaNome||"");
        window.open("https://wa.me/"+fullNum+"?text="+encodeURIComponent(msg), "_blank");
      });
      actionsDiv.appendChild(btnAlerta);
    }

    // Botão "Cobrar Cliente" se NÃO pago
    if (!pago && tel) {
      const btnCobrar = document.createElement("button");
      btnCobrar.textContent = "Cobrar Cliente";
      btnCobrar.style.cssText = "display:inline-flex;align-items:center;gap:4px;padding:7px 14px;border:none;border-radius:4px;font-size:11px;font-weight:700;cursor:pointer;font-family:system-ui;color:#fff;background:#e74c3c;transition:all .15s";
      btnCobrar.addEventListener("mouseenter", () => { btnCobrar.style.opacity="0.8"; });
      btnCobrar.addEventListener("mouseleave", () => { btnCobrar.style.opacity="1"; });
      btnCobrar.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        const num = tel.replace(/\D/g,"");
        const fullNum = (num.length <= 11 ? "55" : "") + num;
        const nome = (l.cliente||"").split(" ")[0];
        const msg = "Olá "+nome+"! Passando para lembrar sobre o pagamento da locação #"+l.id+(tipoCacFull?" ("+tipoCacFull+")":"")+" no valor de "+(valor||"(valor)")+"."
          +" Por favor, entre em contato para regularizar. Obrigado! — "+(empresaNome||"");
        window.open("https://wa.me/"+fullNum+"?text="+encodeURIComponent(msg), "_blank");
      });
      actionsDiv.appendChild(btnCobrar);
    }

    return div;
  };

  // Helper: criar ícone de cluster
  const createClusterIcon = (count) => {
    const size = count >= 10 ? 48 : 42;
    const fs = count >= 100 ? 12 : count >= 10 ? 14 : 15;
    const bg = count >= 10 ? "#e67e22" : "#1a9e8f";
    return L.divIcon({
      className: '',
      iconSize: [size, size],
      iconAnchor: [size/2, size/2],
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:3px solid #fff;box-shadow:0 2px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;cursor:pointer;animation:clusterPulse 2s ease-in-out infinite">
        <span style="color:#fff;font-weight:900;font-size:${fs}px;font-family:system-ui;line-height:1">${count}</span>
      </div>
      <style>@keyframes clusterPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}</style>`,
    });
  };

  // Atualizar marcadores (com agrupamento)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];
    let isCancelled = false;
    const bounds = [];
    const resolvedItems = []; // { l, addr, coords }

    // Validar se coordenada está dentro do Brasil (lat: -34 a 5, lng: -74 a -34)
    const isValidCoord = (lat, lng) => {
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) return false;
      if (lat === 0 && lng === 0) return false;
      return lat >= -34 && lat <= 6 && lng >= -75 && lng <= -34;
    };

    // Distância aproximada entre 2 pontos em km (Haversine simplificado)
    const distKm = (lat1, lng1, lat2, lng2) => {
      const R = 6371;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    };

    const resolveCoords = async () => {
      const needGeocode = [];
      for (const l of filtered) {
        if (isCancelled) return;
        const addr = getAddress(l);
        if (!addr && !l.lat) continue;
        let coords = null;
        const parsedLat = parseFloat(l.lat);
        const parsedLng = parseFloat(l.lng);
        const e = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
        const cidLoc = (l.cidade || e.cidade || "").trim();
        const ufLoc = (l.uf || e.uf || "").trim();

        // 1ª prioridade: lat/lng do banco — MAS validar contra cidade/UF
        if (isValidCoord(parsedLat, parsedLng)) {
          let trusted = true;
          // Se temos cidade+UF, verificar se as coordenadas fazem sentido
          if (cidLoc && ufLoc) {
            const cityCenter = await getCidadeCoords(cidLoc, ufLoc);
            if (cityCenter) {
              const [ccLng, ccLat] = cityCenter.split(",").map(Number);
              const dist = distKm(parsedLat, parsedLng, ccLat, ccLng);
              if (dist > 150) {
                console.warn("[GEO] ⚠️ Coordenadas salvas #" + l.id + " estão a " + Math.round(dist) + "km de " + cidLoc + "/" + ufLoc + " — re-geocodificando");
                trusted = false;
              }
            }
          }
          if (trusted) coords = [parsedLat, parsedLng];
        }
        // 2ª prioridade: cache (por campos estruturados incluindo CEP)
        if (!coords) {
          const cacheKey = [cleanLog(l.logradouro||e.logradouro||""), cleanNum(l.numero||e.numero||""), l.bairro||e.bairro||"", cidLoc, ufLoc, (l.cep||e.cep||"").replace(/\D/g,"")].filter(Boolean).join("|").toLowerCase();
          if (cacheKey && geoCacheRef.current[cacheKey] && isValidCoord(geoCacheRef.current[cacheKey][0], geoCacheRef.current[cacheKey][1])) {
            coords = geoCacheRef.current[cacheKey];
          }
        }
        if (coords) {
          resolvedItems.push({ l, addr: addr||"", coords }); bounds.push(coords);
        } else if (addr) {
          needGeocode.push({ l, addr });
        }
      }
      if (!isCancelled && bounds.length > 0) { renderGrouped(); map.fitBounds(bounds, { padding:[40,40], maxZoom:14 }); }
      const BATCH = 5;
      for (let i = 0; i < needGeocode.length; i += BATCH) {
        if (isCancelled) return;
        const batch = needGeocode.slice(i, i + BATCH);
        const results = await Promise.all(batch.map(({ l }) => geocodeLocacao(l)));
        for (let j = 0; j < batch.length; j++) {
          if (isCancelled) return;
          const coords = results[j];
          if (!coords) continue;
          resolvedItems.push({ l: batch[j].l, addr: batch[j].addr, coords });
          bounds.push(coords);
        }
        if (!isCancelled) { renderGrouped(); if (bounds.length > 0) map.fitBounds(bounds, { padding:[40,40], maxZoom:14 }); }
        if (i + BATCH < needGeocode.length) await new Promise(r => setTimeout(r, 100));
      }
    };

    const renderGrouped = () => {
      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];
      // Agrupar por coordenadas EXATAMENTE iguais (apenas quando são o mesmo ponto)
      const groups = [];
      const used = new Set();
      for (let i = 0; i < resolvedItems.length; i++) {
        if (used.has(i)) continue;
        const g = [resolvedItems[i]];
        used.add(i);
        for (let j = i + 1; j < resolvedItems.length; j++) {
          if (used.has(j)) continue;
          const d = Math.abs(resolvedItems[i].coords[0]-resolvedItems[j].coords[0]) + Math.abs(resolvedItems[i].coords[1]-resolvedItems[j].coords[1]);
          if (d < 0.00005) { g.push(resolvedItems[j]); used.add(j); }
        }
        groups.push(g);
      }

      for (const g of groups) {
        if (g.length === 1) {
          // Marcador individual
          const { l, addr, coords } = g[0];
          const marker = L.marker(coords, { icon: createMarkerIcon(markerColorKey(l)) })
            .bindPopup(() => makePopupEl(l, addr), { maxWidth: 450, minWidth: 340 })
            .addTo(map);
          marker._locId = l.id;
          markersRef.current.push(marker);
        } else {
          // Cluster: mostra ícone com número
          const center = g[0].coords;
          const clusterMarker = L.marker(center, { icon: createClusterIcon(g.length) }).addTo(map);
          clusterMarker._isCluster = true;
          clusterMarker._clusterItems = g;
          markersRef.current.push(clusterMarker);

          // Ao clicar no cluster → spiderfy (espalha em círculo)
          clusterMarker.on('click', () => {
            // Remover cluster
            map.removeLayer(clusterMarker);
            markersRef.current = markersRef.current.filter(m => m !== clusterMarker);
            // Adicionar individuais espalhados em círculo
            const n = g.length;
            const radius = 0.0005 + n * 0.0001;
            const spiderfied = [];
            g.forEach((item, idx) => {
              const angle = (2 * Math.PI * idx) / n - Math.PI / 2;
              const sCoords = [center[0] + radius * Math.sin(angle), center[1] + radius * Math.cos(angle)];
              // Linha do centro ao marcador
              const lineColor = MAPA_COLORS[markerColorKey(item.l)] || "#1a9e8f";
              const line = L.polyline([center, sCoords], { color: lineColor, weight:2.5, opacity:.6, dashArray:"6 4" }).addTo(map);
              const marker = L.marker(sCoords, { icon: createMarkerIcon(markerColorKey(item.l)) })
                .bindPopup(() => makePopupEl(item.l, item.addr), { maxWidth: 450, minWidth: 340 })
                .addTo(map);
              marker._locId = item.l.id;
              marker._spiderLine = line;
              markersRef.current.push(marker);
              markersRef.current.push(line);
              spiderfied.push({ marker, line });
            });
            // Ao fechar zoom ou clicar fora → reagrupar
            const regroup = () => {
              spiderfied.forEach(s => { map.removeLayer(s.marker); map.removeLayer(s.line); });
              markersRef.current = markersRef.current.filter(m => !spiderfied.some(s => s.marker === m || s.line === m));
              const newCluster = L.marker(center, { icon: createClusterIcon(g.length) }).addTo(map);
              newCluster._isCluster = true;
              newCluster._clusterItems = g;
              markersRef.current.push(newCluster);
              newCluster.on('click', clusterMarker._events.click[0].fn);
              map.off('zoomstart', regroup);
            };
            map.once('zoomstart', regroup);
          });
        }
      }
    };

    resolveCoords();
    return () => { isCancelled = true; };
  }, [filtered.map(l=>l.id).join(","), activeFilter, mapRefreshKey]);

  // Centralizar no marcador selecionado
  const [selTick, setSelTick] = useState(0);
  const focusOnLoc = (id) => {
    setSelLoc(id);
    setSelTick(t => t + 1);
  };
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || selLoc === null) return;
    // Procurar marcador individual
    let marker = markersRef.current.find(m => m._locId === selLoc);
    if (marker) {
      map.setView(marker.getLatLng(), 16, { animate: true });
      setTimeout(() => marker.openPopup(), 350);
      return;
    }
    // Se não encontrou, pode estar dentro de um cluster — expandir
    const cluster = markersRef.current.find(m => m._isCluster && m._clusterItems && m._clusterItems.some(item => item.l.id === selLoc));
    if (cluster) {
      map.setView(cluster.getLatLng(), 16, { animate: true });
      setTimeout(() => {
        cluster.fire("click");
        // Após spiderfy, procurar novamente
        setTimeout(() => {
          const m2 = markersRef.current.find(m => m._locId === selLoc);
          if (m2) { m2.openPopup(); }
        }, 400);
      }, 400);
    }
  }, [selLoc, selTick]);

  // Recalcular posições: reprocessa geocodificação das locações ativas no servidor
  const recalcularPosicoes = async () => {
    if (regeocoding) return;
    const okConfirm = window.confirm(
      "Recalcular posições no mapa?\n\n" +
      "Isso reprocessa a geocodificação (endereço → coordenadas) de TODAS as locações ativas " +
      "(Para Colocar, Colocada, Para Retirar, Para Trocar) do zero.\n\n" +
      "Use se algum pin estiver no lugar errado. Pode levar alguns segundos."
    );
    if (!okConfirm) return;
    setRegeocoding(true);
    try {
      const r = await apiFetch("/api/locacoes/regeocodar", { method: "POST", body: { forcar: true } });
      // Limpar cache local de geocodificação — as coordenadas novas vêm do servidor
      geoCacheRef.current = {};
      try { localStorage.removeItem("geo_cache"); } catch(_) {}
      const sucesso = r.sucesso || 0, falhas = r.falhas || 0, processadas = r.processadas || 0;
      if (addToast) addToast(
        falhas > 0 ? "Posições recalculadas (com falhas)" : "Posições recalculadas ✅",
        sucesso + " atualizada(s)" + (falhas > 0 ? " · " + falhas + " falha(s)" : "") + " de " + processadas + " processada(s)",
        "",
        falhas > 0 ? "#f39c12" : "#2ecc71"
      );
      if (onReloadLocacoes) await onReloadLocacoes();
      setMapRefreshKey(k => k + 1); // força re-render dos marcadores
    } catch(err) {
      if (addToast) addToast("Erro ao recalcular posições", err.message, "", "#e74c3c");
    }
    setRegeocoding(false);
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        <button onClick={() => { setActiveFilter("TODAS"); setSelLoc(null); }} style={{ padding:"8px 18px", fontSize:12, fontWeight:700, border:"none", borderRadius:6, cursor:"pointer", background:activeFilter==="TODAS"?"#1a9e8f":"#fff", color:activeFilter==="TODAS"?"#fff":"#666", fontFamily:FONT, boxShadow:activeFilter==="TODAS"?"0 2px 8px #1a9e8f55":"0 1px 3px rgba(0,0,0,.08)", transition:"all .2s" }}>
          TODAS ({ativas.length})
        </button>
        {MAPA_STATUS.map(st => {
          const color = MAPA_COLORS[st];
          const count = ativas.filter(l => (l._mapaStatus || l.status) === st).length;
          const active = activeFilter === st;
          return (
            <button key={st} onClick={() => { setActiveFilter(st); setSelLoc(null); }} style={{ padding:"8px 18px", fontSize:12, fontWeight:700, border:"none", borderRadius:6, cursor:"pointer", background:active?color:"#fff", color:active?"#fff":color, fontFamily:FONT, boxShadow:active?"0 2px 8px "+color+"55":"0 1px 3px rgba(0,0,0,.08)", transition:"all .2s" }}>
              {st.toUpperCase()} ({count})
            </button>
          );
        })}
        {/* Botão Atrasadas */}
        <button onClick={() => { setActiveFilter("ATRASADAS"); setSelLoc(null); }} style={{ padding:"8px 18px", fontSize:12, fontWeight:700, border:"none", borderRadius:6, cursor:"pointer", background:activeFilter==="ATRASADAS"?"#e74c3c":"#fff", color:activeFilter==="ATRASADAS"?"#fff":"#e74c3c", fontFamily:FONT, boxShadow:activeFilter==="ATRASADAS"?"0 2px 8px #e74c3c55":"0 1px 3px rgba(0,0,0,.08)", transition:"all .2s", position:"relative" }}>
          ATRASADAS ({atrasadas.length})
          {atrasadas.length > 0 && activeFilter !== "ATRASADAS" && <span style={{ position:"absolute", top:-4, right:-4, width:8, height:8, borderRadius:"50%", background:"#e74c3c", border:"2px solid #fff" }} />}
        </button>
        {/* Recalcular posições (re-geocodificação forçada no servidor) */}
        <button onClick={recalcularPosicoes} disabled={regeocoding} title="Reprocessa a geocodificação das locações ativas — use se algum pin estiver no lugar errado"
          style={{ marginLeft:"auto", padding:"8px 14px", fontSize:12, fontWeight:700, border:"1px solid #e0e0e0", borderRadius:6, cursor:regeocoding?"wait":"pointer", background:"#fff", color:regeocoding?"#aaa":"#555", fontFamily:FONT, boxShadow:"0 1px 3px rgba(0,0,0,.08)", transition:"all .2s", display:"flex", alignItems:"center", gap:6, opacity:regeocoding?0.7:1 }}>
          <span style={{ fontSize:13, display:"inline-block", animation:regeocoding?"spin 1s linear infinite":"none" }}>🔄</span>
          {regeocoding ? "Recalculando..." : "Recalcular posições"}
          {regeocoding && <style>{"@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}"}</style>}
        </button>
        <div style={{ position:"relative" }}>
          <input value={searchMap} onChange={e => setSearchMap(e.target.value)} placeholder="Buscar locação, caçamba, cliente..." style={{ padding:"8px 14px", borderRadius:6, border:"1px solid #e0e0e0", fontSize:13, fontFamily:FONT, width:280, background:"#fff" }} />
          {searchMap && <button onClick={() => setSearchMap("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:14 }}>✕</button>}
        </div>
      </div>

      {/* Mapa + Lista */}
      <div className="mapa-grid" style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:14 }}>
        <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, overflow:"hidden", position:"relative" }}>
          <div ref={mapRef} style={{ width:"100%", height:500 }} />
          {/* Toggle Mapa / Satélite */}
          <div style={{
            position:"absolute", top:12, right:12, zIndex:500,
            background:"#fff", borderRadius:6,
            boxShadow:"0 2px 6px rgba(0,0,0,.18), 0 0 0 1px rgba(0,0,0,.06)",
            display:"flex", overflow:"hidden", fontFamily:FONT
          }}>
            <button
              onClick={() => setMapStyle("streets")}
              title="Visualização padrão"
              style={{
                padding:"7px 12px",
                background: mapStyle === "streets" ? "#1a1a1a" : "#fff",
                color: mapStyle === "streets" ? "#fff" : "#444",
                border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
                display:"flex", alignItems:"center", gap:6,
                transition:"all .15s"
              }}
            >
              <span style={{ fontSize:13 }}>🗺️</span> Mapa
            </button>
            <button
              onClick={() => setMapStyle("satellite")}
              title="Visualização de satélite"
              style={{
                padding:"7px 12px",
                background: mapStyle === "satellite" ? "#1a1a1a" : "#fff",
                color: mapStyle === "satellite" ? "#fff" : "#444",
                border:"none", borderLeft:"1px solid #eee", cursor:"pointer", fontSize:12, fontWeight:600,
                display:"flex", alignItems:"center", gap:6,
                transition:"all .15s"
              }}
            >
              <span style={{ fontSize:13 }}>🛰️</span> Satélite
            </button>
          </div>
        </div>

        <div className="mapa-sidebar" style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, overflow:"hidden", alignSelf:"start" }}>
          <div style={{ padding:"10px 14px", borderBottom:"1px solid #f0f0f0", fontSize:11, fontWeight:700, color: activeFilter==="ATRASADAS"?"#e74c3c":"#888", textTransform:"uppercase", letterSpacing:.3, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span>{activeFilter === "ATRASADAS" ? "Caçambas Atrasadas" : "Locações"} ({filtered.length})</span>
          </div>
          <div style={{ maxHeight:460, overflowY:"auto" }}>
            {filtered.length === 0 ? (
              <EmptyState icon="📍" title={activeFilter==="ATRASADAS"?"Nenhuma atrasada":"Nenhuma locação"} subtitle={activeFilter==="ATRASADAS"?"Tudo em dia!":""} />
            ) : filtered.map(l => {
              const color = MAPA_COLORS[l._mapaStatus || l.status] || "#666";
              const dias = diasAtraso(l);
              const isAtrasada = dias > 0;
              return (
                <div key={l.id} onClick={() => focusOnLoc(l.id)} style={{ padding:"11px 14px", borderBottom:"1px solid #f5f5f5", cursor:"pointer", background: selLoc===l.id ? "#f0f4ff" : isAtrasada && activeFilter==="ATRASADAS" ? "#fef8f8" : "", borderLeft: selLoc===l.id ? `3px solid ${color}` : "3px solid transparent", transition:"all .15s" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <span style={{ fontWeight:700, fontSize:12, color:"#1a1a1a", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.cliente}</span>
                    <span style={{ background:color, color:"#fff", padding:"1px 8px", borderRadius:12, fontSize:10, fontWeight:700, flexShrink:0, marginLeft:6 }}>{l._mapaStatus || l.status}</span>
                  </div>
                  <div style={{ fontSize:11, color:"#888", marginBottom:2 }}>{getAddress(l)||"Endereço não informado"}</div>
                  <div style={{ fontSize:11, color:"#aaa", marginBottom: isAtrasada ? 6 : 0 }}>#{l.id} · {l.cacamba||"S/ caçamba"}</div>
                  {/* Indicador de atraso + botão WhatsApp */}
                  {isAtrasada && (
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:4 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:"#e74c3c", background:"#fef2f2", padding:"2px 8px", borderRadius:10 }}>
                        {dias} dia{dias>1?"s":""} de atraso
                      </span>
                      <a href={gerarWhatsApp(l)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:10, background:"#25D366", color:"#fff", fontSize:10, fontWeight:700, textDecoration:"none", cursor:"pointer", transition:"all .15s" }}
                        onMouseEnter={e => e.currentTarget.style.background="#1ebe5d"} onMouseLeave={e => e.currentTarget.style.background="#25D366"}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.025.506 3.934 1.395 5.608L0 24l6.594-1.363A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.875 0-3.621-.534-5.105-1.457l-.366-.217-3.797.785.82-3.676-.238-.378A9.71 9.71 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
                        Cobrar
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display:"flex", gap:16, marginTop:14, padding:"12px 16px", background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, flexWrap:"wrap", alignItems:"center" }}>
        {MAPA_STATUS.map(st => (
          <div key={st} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#555", fontWeight:500 }}>
            <svg width="24" height="20" viewBox="0 0 48 38">
              <rect x="2" y="2" width="44" height="5" rx="1.5" fill={MAPA_DARKS[st]}/>
              <polygon points="4,7 44,7 40,34 8,34" fill={MAPA_COLORS[st]} stroke={MAPA_DARKS[st]} strokeWidth="1"/>
              <path d="M14 2 C14 -3 20 -3 20 2" fill="none" stroke={MAPA_RIBS[st]} strokeWidth="2" strokeLinecap="round"/>
              <path d="M28 2 C28 -3 34 -3 34 2" fill="none" stroke={MAPA_RIBS[st]} strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {st}
          </div>
        ))}
        <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#e74c3c", fontWeight:600 }}>
          <svg width="24" height="20" viewBox="0 0 48 38">
            <rect x="2" y="2" width="44" height="5" rx="1.5" fill={MAPA_DARKS["Atrasada"]}/>
            <polygon points="4,7 44,7 40,34 8,34" fill={MAPA_COLORS["Atrasada"]} stroke={MAPA_DARKS["Atrasada"]} strokeWidth="1"/>
            <path d="M14 2 C14 -3 20 -3 20 2" fill="none" stroke={MAPA_RIBS["Atrasada"]} strokeWidth="2" strokeLinecap="round"/>
            <path d="M28 2 C28 -3 34 -3 34 2" fill="none" stroke={MAPA_RIBS["Atrasada"]} strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Atrasada
        </div>
      </div>

      {/* Botão atualizar mapa */}
      <div style={{ display:"flex", justifyContent:"flex-end", marginTop:8 }}>
        <button onClick={() => { try { localStorage.removeItem("geo_cache"); localStorage.removeItem("geo_cache_ver"); } catch(e) {} geoCacheRef.current = {}; setSelLoc(null); setMapRefreshKey(k => k + 1); }} style={{ background:"#f5f5f5", border:"1px solid #ddd", borderRadius:6, padding:"6px 14px", fontSize:11, cursor:"pointer", color:"#666", fontFamily:FONT, fontWeight:600 }}>🔄 Atualizar Localizações</button>
      </div>

      {/* Mensagem motivacional para o gestor */}
      <div style={{ marginTop:16, padding:"18px 22px", background:"linear-gradient(135deg, #f0faf8 0%, #e8f5f1 100%)", border:"1px solid #d0e8e2", borderRadius:8, display:"flex", gap:14, alignItems:"flex-start" }}>
        <div style={{ fontSize:28, lineHeight:1, flexShrink:0, marginTop:2 }}>💡</div>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"#1a9e8f", marginBottom:6 }}>Dica do Controla</div>
          <div style={{ fontSize:12, color:"#555", lineHeight:1.7 }}>
            Caro gestor, acompanhe este mapa diariamente! Cada caçamba no campo representa seu faturamento e a confiança dos seus clientes. Fique atento às <b style={{ color:"#e74c3c" }}>caçambas atrasadas</b> — cobranças em dia garantem um fluxo de caixa saudável e evitam perdas. Uma operação bem controlada é o segredo para crescer com tranquilidade. <b style={{ color:"#1a9e8f" }}>Boas operações!</b>
          </div>
        </div>
      </div>

      {/* Tutorial primeira vez */}
      {showTutorialMapa && <TutorialMapaModal onClose={() => { localStorage.setItem("controla_tutorial_mapa", "1"); setShowTutorialMapa(false); }} />}

      {/* Modal detalhe da locação */}
      {/* Ações redirecionam para Locações que já tem os modais corretos */}
    </div>
  );
}

// ─── Mapa do Itinerario (Rota) ───────────────────────────────────────────────
const TEMPO_OP = { ENTREGA: 15, TROCA: 25, RETIRADA: 10 };

// Prioridade de operações: ENTREGA primeiro (caminhão sai carregado), TROCA depois, RETIRADA por último
const OP_PRIORIDADE = { ENTREGA: 0, TROCA: 1, RETIRADA: 2 };

// Ordena paradas: 1) por tipo de operação (ENTREGA > TROCA > RETIRADA), 2) dentro de cada grupo, por proximidade (nearest-neighbor)
function ordenarParadasInteligente(coordsList, baseLatLng) {
  if (coordsList.length <= 1) return coordsList.map((_, i) => i);

  // Separar por tipo de operação
  const grupos = { ENTREGA: [], TROCA: [], RETIRADA: [] };
  coordsList.forEach((c, idx) => {
    const g = grupos[c.op] || grupos.ENTREGA;
    g.push({ ...c, origIdx: idx });
  });

  // Dentro de cada grupo, ordenar por nearest-neighbor (mais próximo primeiro)
  function nearestNeighbor(points, startLatLng) {
    if (points.length === 0) return [];
    const result = [];
    const remaining = [...points];
    let current = startLatLng || (remaining[0] ? remaining[0].latLng : [0, 0]);

    while (remaining.length > 0) {
      let minDist = Infinity, minIdx = 0;
      for (let i = 0; i < remaining.length; i++) {
        const d = Math.sqrt(Math.pow(remaining[i].latLng[0] - current[0], 2) + Math.pow(remaining[i].latLng[1] - current[1], 2));
        if (d < minDist) { minDist = d; minIdx = i; }
      }
      const chosen = remaining.splice(minIdx, 1)[0];
      result.push(chosen);
      current = chosen.latLng;
    }
    return result;
  }

  // Montar ordem final: ENTREGA → TROCA → RETIRADA, cada grupo por proximidade
  let lastLatLng = baseLatLng || null;
  const ordemFinal = [];
  for (const tipo of ["ENTREGA", "TROCA", "RETIRADA"]) {
    if (grupos[tipo].length > 0) {
      const sorted = nearestNeighbor(grupos[tipo], lastLatLng);
      ordemFinal.push(...sorted);
      lastLatLng = sorted[sorted.length - 1].latLng;
    }
  }

  return ordemFinal.map(p => p.origIdx);
}

function ItinerarioMapa({ paradas, locacoes, itObj, onReorder, apiFetch, baseEndereco }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef([]);
  const [rotaInfo, setRotaInfo] = useState(null);
  const [sugestao, setSugestao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarSugestao, setMostrarSugestao] = useState(false);
  const geoCacheRef = useRef({});
  const coordsCacheRef = useRef([]);

  const getAddress = (loc) => {
    if (!loc) return "";
    if (loc.endereco && typeof loc.endereco === "object") return [loc.endereco.logradouro, loc.endereco.numero, loc.endereco.bairro, loc.endereco.cidade, loc.endereco.uf].filter(Boolean).join(", ");
    if (typeof loc.endereco === "string") return loc.endereco;
    return "";
  };

  const geocode = async (addr) => {
    if (!addr) return null;
    if (geoCacheRef.current[addr]) return geoCacheRef.current[addr];
    try {
      const q = addr + (addr.toLowerCase().includes("brasil") ? "" : ", Brasil");
      const r = await fetch("https://api.mapbox.com/geocoding/v5/mapbox.places/" + encodeURIComponent(q) + ".json?access_token=" + MAPBOX_TOKEN + "&country=br&limit=1&language=pt");
      const data = await r.json();
      if (data.features && data.features[0]) {
        const [lng, lat] = data.features[0].center;
        geoCacheRef.current[addr] = [lat, lng];
        return [lat, lng];
      }
    } catch(e) {}
    return null;
  };

  const calcTempoOperacoes = (pars) => {
    let total = 0;
    pars.forEach(p => {
      const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {};
      const op = getOperacao(loc);
      total += TEMPO_OP[op] || 8;
    });
    return total;
  };

  const formatTempo = (min) => {
    if (min < 60) return min + " min";
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h + "h" + (m > 0 ? " " + m + "min" : "");
  };

  const addBaseMarker = (map, latLng) => {
    const icon = L.divIcon({
      className: "",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      html: '<div style="width:32px;height:32px;border-radius:50%;background:#1e2d4f;color:#fff;display:flex;align-items:center;justify-content:center;font-size:16px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4)">🏠</div>'
    });
    const marker = L.marker(latLng, { icon }).addTo(map);
    marker.bindPopup("<b>BASE / PATIO</b><br/><small>" + (baseEndereco || "Endereco da empresa") + "</small>");
    layersRef.current.push(marker);
  };

  const addMarkers = (map, coordsList, baseLatLng) => {
    layersRef.current.forEach(layer => { try { map.removeLayer(layer); } catch(e) {} });
    layersRef.current = [];
    if (baseLatLng) addBaseMarker(map, baseLatLng);
    coordsList.forEach((c, i) => {
      const opC = c.op === "RETIRADA" ? "#e74c3c" : c.op === "TROCA" ? "#f39c12" : "#2ecc71";
      const icon = L.divIcon({
        className: "",
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        html: '<div style="width:28px;height:28px;border-radius:50%;background:' + opC + ';color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">' + (i + 1) + '</div>'
      });
      const marker = L.marker(c.latLng, { icon }).addTo(map);
      marker.bindPopup("<b>" + (i+1) + ". " + c.op + "</b><br/>" + c.cliente + "<br/><small>" + c.endereco + "</small><br/><small>Tempo no local: ~" + (TEMPO_OP[c.op]||8) + " min</small>");
      layersRef.current.push(marker);
    });
  };

  const baseCacheRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: true }).setView([-23.55, -46.63], 11);
    L.tileLayer("https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN, {
      attribution: "Mapbox", tileSize: 512, zoomOffset: -1, maxZoom: 22
    }).addTo(map);
    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 300);

    const calcRoute = async () => {
      setLoading(true);
      setSugestao(null);
      setMostrarSugestao(false);

      // Geocodificar a base
      let baseLatLng = baseCacheRef.current;
      if (!baseLatLng && baseEndereco) {
        baseLatLng = await geocode(baseEndereco);
        if (baseLatLng) baseCacheRef.current = baseLatLng;
      }

      const coordsList = [];
      for (let i = 0; i < paradas.length; i++) {
        const loc = locacoes.find(l => String(l.id) === String(paradas[i].locId)) || {};
        let latLng = null;
        if (loc.lat && loc.lng) {
          latLng = [parseFloat(loc.lat), parseFloat(loc.lng)];
        } else {
          const addr = getAddress(loc);
          if (addr) latLng = await geocode(addr);
        }
        if (latLng) {
          coordsList.push({ latLng, op: getOperacao(loc), cliente: loc.cliente || "-", endereco: getAddress(loc), locId: paradas[i].locId, parIdx: i });
        }
      }
      coordsCacheRef.current = coordsList;
      addMarkers(map, coordsList, baseLatLng);

      // Montar waypoints: base (se tiver) + paradas
      const allPoints = [];
      if (baseLatLng) allPoints.push(baseLatLng);
      coordsList.forEach(c => allPoints.push(c.latLng));

      if (allPoints.length >= 2) {
        const waypoints = allPoints.map(p => p[1] + "," + p[0]).join(";");
        try {
          // Rota atual (na ordem atual)
          let distKm = "0";
          const res = await fetch("https://api.mapbox.com/directions/v5/mapbox/driving/" + waypoints + "?access_token=" + MAPBOX_TOKEN + "&geometries=geojson&overview=full&language=pt");
          const data = await res.json();
          if (data.routes && data.routes[0]) {
            const route = data.routes[0];
            distKm = (route.distance / 1000).toFixed(1);
            const deslocMin = Math.round(route.duration / 60);
            const opMin = calcTempoOperacoes(paradas);
            setRotaInfo({ distancia: distKm, deslocamento: deslocMin, operacoes: opMin, total: deslocMin + opMin });
            const routeLine = L.geoJSON(route.geometry, { style: { color: "#1aae9e", weight: 4, opacity: 0.7 } }).addTo(map);
            layersRef.current.push(routeLine);
          }

          // Rota otimizada inteligente: ENTREGA primeiro, depois TROCA, depois RETIRADA, cada grupo por proximidade
          if (coordsList.length >= 2) {
            try {
              // 1) Calcular ordem inteligente: prioridade por tipo + nearest-neighbor
              const smartOrder = ordenarParadasInteligente(coordsList, baseLatLng);
              const reorderedCoords = smartOrder.map(idx => coordsList[idx]);

              // 2) Calcular rota real com Mapbox Directions na ordem inteligente
              const smartWaypoints = reorderedCoords.map(c => c.latLng[1] + "," + c.latLng[0]).join(";");
              const smartWp = baseLatLng ? (baseLatLng[1] + "," + baseLatLng[0] + ";" + smartWaypoints) : smartWaypoints;
              const smartRes = await fetch("https://api.mapbox.com/directions/v5/mapbox/driving/" + smartWp + "?access_token=" + MAPBOX_TOKEN + "&geometries=geojson&overview=full&language=pt");
              const smartData = await smartRes.json();

              if (smartData.routes && smartData.routes[0]) {
                const smartRoute = smartData.routes[0];
                const optDistKm = (smartRoute.distance / 1000).toFixed(1);
                const optDeslocMin = Math.round(smartRoute.duration / 60);
                const optOpMin = reorderedCoords.reduce((acc, c) => acc + (TEMPO_OP[c.op] || 8), 0);
                const currentDist = parseFloat(distKm || "0");
                const economia = currentDist - parseFloat(optDistKm);
                const ordemMudou = smartOrder.some((idx, i) => idx !== i);
                setSugestao({
                  distancia: optDistKm,
                  deslocamento: optDeslocMin,
                  operacoes: optOpMin,
                  total: optDeslocMin + optOpMin,
                  economia: economia > 0 ? economia.toFixed(1) : "0",
                  order: smartOrder,
                  geometry: smartRoute.geometry,
                  reorderedLocIds: smartOrder.map(idx => coordsList[idx].locId),
                  jaOtimizada: !ordemMudou || economia <= 0.5,
                  descricao: "Entregas primeiro, depois trocas, depois retiradas — cada grupo pela mais pr\u00f3xima"
                });
              }
            } catch(e) { console.log("Erro otimizacao:", e); }
          }
        } catch(e) { console.log("Erro rota:", e); }
      }

      if (coordsList.length > 0) {
        const bounds = L.latLngBounds(coordsList.map(c => c.latLng));
        map.fitBounds(bounds, { padding: [30, 30] });
      }
      setLoading(false);
    };

    calcRoute();
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [paradas, locacoes]);

  const aplicarSugestao = () => {
    if (!sugestao || !onReorder) return;
    onReorder(sugestao.reorderedLocIds);
    setMostrarSugestao(false);
    setSugestao(null);
  };

  const verSugestaoNoMapa = () => {
    setMostrarSugestao(true);
    const map = mapInstanceRef.current;
    if (!map || !sugestao) return;
    // Limpar layers antigos
    layersRef.current.forEach(layer => { try { map.removeLayer(layer); } catch(e) {} });
    layersRef.current = [];
    // Redesenhar markers na ordem otimizada
    const reordered = sugestao.order.map((idx, newI) => ({ ...coordsCacheRef.current[idx], num: newI + 1 }));
    reordered.forEach((c, i) => {
      const opC = c.op === "RETIRADA" ? "#e74c3c" : c.op === "TROCA" ? "#f39c12" : "#2ecc71";
      const icon = L.divIcon({
        className: "",
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        html: '<div style="width:28px;height:28px;border-radius:50%;background:' + opC + ';color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;border:3px solid #f59e0b;box-shadow:0 2px 8px rgba(245,158,11,.5)">' + (i + 1) + '</div>'
      });
      const marker = L.marker(c.latLng, { icon }).addTo(map);
      marker.bindPopup("<b>" + (i+1) + ". " + c.op + "</b> (sugest\u00e3o)<br/>" + c.cliente);
      layersRef.current.push(marker);
    });
    // Rota otimizada em laranja
    const routeLine = L.geoJSON(sugestao.geometry, { style: { color: "#f59e0b", weight: 5, opacity: 0.8, dashArray: "8 4" } }).addTo(map);
    layersRef.current.push(routeLine);
  };

  const voltarRotaAtual = () => {
    setMostrarSugestao(false);
    const map = mapInstanceRef.current;
    if (!map) return;
    layersRef.current.forEach(layer => { try { map.removeLayer(layer); } catch(e) {} });
    layersRef.current = [];
    addMarkers(map, coordsCacheRef.current);
    // Redesenhar rota atual
    if (coordsCacheRef.current.length >= 2) {
      const waypoints = coordsCacheRef.current.map(c => c.latLng[1] + "," + c.latLng[0]).join(";");
      fetch("https://api.mapbox.com/directions/v5/mapbox/driving/" + waypoints + "?access_token=" + MAPBOX_TOKEN + "&geometries=geojson&overview=full")
        .then(r => r.json()).then(data => {
          if (data.routes && data.routes[0]) {
            const routeLine = L.geoJSON(data.routes[0].geometry, { style: { color: "#1aae9e", weight: 4, opacity: 0.7 } }).addTo(map);
            layersRef.current.push(routeLine);
          }
        }).catch(() => {});
    }
  };

  const tempoOpInfo = paradas.map(p => {
    const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {};
    const op = getOperacao(loc);
    return { op, tempo: TEMPO_OP[op] || 8 };
  });

  // Calculo de capacidade do caminhao
  // Regra: max 4 vazias OU 2 cheias. 1 cheia = 2 vazias em peso
  // ENTREGA: sai com vazia, entrega -> libera 1 slot vazia
  // RETIRADA: pega cheia -> ocupa 2 slots
  // TROCA: leva vazia + pega cheia -> saldo = +1 slot (troca vazia por cheia)
  const MAX_SLOTS = 4; // 4 slots (1 cheia = 2 slots)
  const capacidadeSteps = [];
  let slotsUsados = 0;
  // No inicio o caminhao carrega as cacambas que vai entregar/trocar
  const entregasCount = paradas.filter(p => { const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {}; return getOperacao(loc) === "ENTREGA"; }).length;
  const trocasCount = paradas.filter(p => { const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {}; return getOperacao(loc) === "TROCA"; }).length;
  const cargaInicialVazias = entregasCount + trocasCount; // cacambas vazias que leva
  slotsUsados = cargaInicialVazias; // cada vazia = 1 slot
  let alerta = slotsUsados > MAX_SLOTS;
  capacidadeSteps.push({ label: "Saida do patio", slots: slotsUsados, max: MAX_SLOTS, alerta: slotsUsados > MAX_SLOTS, vazias: cargaInicialVazias, cheias: 0 });

  let vazias = cargaInicialVazias;
  let cheias = 0;
  paradas.forEach((p, i) => {
    const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {};
    const op = getOperacao(loc);
    if (op === "ENTREGA") {
      vazias = Math.max(0, vazias - 1); // entregou 1 vazia
    } else if (op === "RETIRADA") {
      cheias += 1; // pegou 1 cheia
    } else if (op === "TROCA") {
      vazias = Math.max(0, vazias - 1); // deixou 1 vazia
      cheias += 1; // pegou 1 cheia
    }
    slotsUsados = vazias + (cheias * 2);
    const stepAlerta = slotsUsados > MAX_SLOTS;
    if (stepAlerta) alerta = true;
    capacidadeSteps.push({ label: (i+1) + ". " + op + " - " + (loc.cliente||"-").substring(0,20), slots: slotsUsados, max: MAX_SLOTS, alerta: stepAlerta, vazias, cheias });
  });

  return (
    <div style={{ padding: "0 20px 8px" }}>
      {/* Info da rota */}
      {rotaInfo && (
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10 }}>
            {[
              { icon:"🛣️", label:"Distância", val: rotaInfo.distancia + " km", color:"#0d9488", bg:"#f0fdfa", border:"#99f6e4" },
              { icon:"⏱️", label:"Deslocamento", val: formatTempo(rotaInfo.deslocamento), color:"#475569", bg:"#f8fafc", border:"#e2e8f0" },
              { icon:"⚙️", label:"Operações", val: formatTempo(rotaInfo.operacoes), color:"#d97706", bg:"#fffbeb", border:"#fde68a" },
              { icon:"🕐", label:"Total Estimado", val: formatTempo(rotaInfo.total), color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0" },
            ].map(m => (
              <div key={m.label} style={{ background:m.bg, borderRadius:10, padding:"12px 10px", textAlign:"center", border:"1px solid " + m.border }}>
                <div style={{ fontSize:16, marginBottom:2 }}>{m.icon}</div>
                <div style={{ fontSize:16, fontWeight:800, color:m.color, lineHeight:1.2 }}>{m.val}</div>
                <div style={{ fontSize:9, fontWeight:600, color:m.color, opacity:.7, textTransform:"uppercase", letterSpacing:.5, marginTop:1 }}>{m.label}</div>
              </div>
            ))}
          </div>
          {/* Detalhe tempo por tipo */}
          <div style={{ display:"flex", gap:10, padding:"8px 0 0", fontSize:10, color:"#94a3b8", flexWrap:"wrap" }}>
            <span style={{ background:"#f0fdf4", padding:"2px 8px", borderRadius:4, color:"#15803d" }}>Entregas: {tempoOpInfo.filter(t=>t.op==="ENTREGA").length}x ~{TEMPO_OP.ENTREGA}min</span>
            <span style={{ background:"#fef2f2", padding:"2px 8px", borderRadius:4, color:"#b91c1c" }}>Retiradas: {tempoOpInfo.filter(t=>t.op==="RETIRADA").length}x ~{TEMPO_OP.RETIRADA}min</span>
            <span style={{ background:"#fffbeb", padding:"2px 8px", borderRadius:4, color:"#92400e" }}>🔃 Trocas: {tempoOpInfo.filter(t=>t.op==="TROCA").length}x ~{TEMPO_OP.TROCA}min</span>
          </div>
          <div style={{ marginTop:8, padding:"10px 14px", background:"linear-gradient(135deg, #fffbeb 0%, #fef9c3 100%)", borderRadius:8, border:"1px solid #fde68a", display:"flex", alignItems:"flex-start", gap:8 }}>
            💡
            <div style={{ fontSize:11, color:"#78350f", lineHeight:1.5 }}>
              <strong>{"Caro gestor,"}</strong>{" os tempos estimados consideram operações sem imprevistos. Eventos como trânsito, chuva, ruas bloqueadas ou dificuldade de acesso podem alterar o tempo real da rota."}
            </div>
          </div>
        </div>
      )}

      {/* Capacidade do Caminhao */}
      {paradas.length > 0 && (
        <div style={{ marginBottom:10, padding:"14px 16px", background: alerta ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)" : "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)", borderRadius:10, border:"1px solid " + (alerta ? "#fca5a5" : "#86efac") }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", background: alerta ? "#dc2626" : "#16a34a", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:"#fff", flexShrink:0 }}>{alerta ? "!" : "✓"}</div>
            <span style={{ fontWeight:700, fontSize:12, color: alerta ? "#991b1b" : "#14532d" }}>
              {alerta ? "Capacidade excedida — necessário mais de 1 viagem" : "Capacidade OK — tudo em 1 viagem"}
            </span>
          </div>
          <div style={{ display:"flex", gap:4, alignItems:"center", flexWrap:"wrap", padding:"6px 8px", background:"rgba(255,255,255,.5)", borderRadius:8 }}>
            {capacidadeSteps.map((step, si) => (
              <div key={si} style={{ display:"flex", alignItems:"center", gap:3 }}>
                {si > 0 && <span style={{ color:"#cbd5e1", fontSize:10 }}>→</span>}
                <div title={step.label + "\nVazias: " + step.vazias + " | Cheias: " + step.cheias + "\nSlots: " + step.slots + "/" + step.max} style={{
                  background: step.alerta ? "#dc2626" : step.slots === 0 ? "#e2e8f0" : "#0d9488",
                  color: "#fff", borderRadius:5, padding:"3px 7px", fontSize:9, fontWeight:700,
                  opacity: step.slots === 0 ? 0.5 : 1, cursor:"default", whiteSpace:"nowrap",
                  boxShadow: step.alerta ? "0 1px 4px rgba(220,38,38,.3)" : "none"
                }}>
                  {si === 0 ? "Pátio" : si} {step.vazias > 0 ? "V" + step.vazias : ""}{step.cheias > 0 ? " C" + step.cheias : ""}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize:9, color: alerta ? "#b91c1c" : "#15803d", marginTop:6, opacity:.7 }}>
            Capacidade: 4 vazias ou 2 cheias (1 cheia = 2 slots) | V=vazia C=cheia
          </div>
        </div>
      )}

      {/* Sugestão de rota otimizada */}
      {sugestao && !mostrarSugestao && (
        <div style={{ marginBottom:10, padding:"14px 16px", background: sugestao.jaOtimizada ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)" : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", borderRadius:10, border:"1px solid " + (sugestao.jaOtimizada ? "#86efac" : "#fde68a") }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", background: sugestao.jaOtimizada ? "#16a34a" : "#f59e0b", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:"#fff", flexShrink:0 }}>{sugestao.jaOtimizada ? "✓" : "💡"}</div>
              <div>
                <div style={{ fontWeight:800, fontSize:13, color: sugestao.jaOtimizada ? "#14532d" : "#78350f" }}>
                  {sugestao.jaOtimizada ? "Rota já otimizada!" : "Rota inteligente disponível"}
                </div>
                {sugestao.descricao && <div style={{ fontSize:10, color: sugestao.jaOtimizada ? "#15803d" : "#92400e", fontStyle:"italic", marginTop:1 }}>{sugestao.descricao}</div>}
              </div>
            </div>
            {!sugestao.jaOtimizada && <span style={{ background:"linear-gradient(135deg, #16a34a, #15803d)", color:"#fff", padding:"4px 12px", borderRadius:20, fontSize:10, fontWeight:700, boxShadow:"0 2px 6px rgba(22,163,74,.3)" }}>-{sugestao.economia} km</span>}
          </div>
          {!sugestao.jaOtimizada && (
            <>
              <div style={{ display:"flex", gap:10, fontSize:11, color:"#78350f", marginBottom:10, padding:"8px 10px", background:"rgba(255,255,255,.5)", borderRadius:6 }}>
                <span>🛣 {sugestao.distancia} km</span>
                <span>{formatTempo(sugestao.deslocamento)}</span>
                <span>{formatTempo(sugestao.operacoes)}</span>
                <span style={{ fontWeight:700 }}>Total: {formatTempo(sugestao.total)}</span>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={verSugestaoNoMapa} style={{ background:"#f59e0b", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 2px 6px rgba(245,158,11,.3)", transition:"transform .15s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                  👁 Ver no mapa
                </button>
                <button onClick={aplicarSugestao} style={{ background:"linear-gradient(135deg, #16a34a, #15803d)", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 2px 6px rgba(22,163,74,.3)", transition:"transform .15s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                  {"Aplicar rota inteligente"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Mostrando sugestão no mapa */}
      {mostrarSugestao && sugestao && (
        <div style={{ marginBottom:10, padding:"10px 16px", background:"linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", borderRadius:10, border:"2px dashed #f59e0b", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:12, color:"#78350f", fontWeight:600 }}>👁 Visualizando rota sugerida ({sugestao.distancia} km — economiza {sugestao.economia} km)</span>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={aplicarSugestao} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:6, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Aplicar</button>
            <button onClick={voltarRotaAtual} style={{ background:"#fff", color:"#475569", border:"1px solid #e2e8f0", borderRadius:6, padding:"6px 14px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>Voltar rota atual</button>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign:"center", padding:12, fontSize:12, color:"#94a3b8", fontWeight:500 }}>Calculando rota...</div>}
      <div ref={mapRef} style={{ height: 320, borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow:"0 2px 8px rgba(0,0,0,.06)" }} />

      {/* Botao sugerir rota - sempre visivel */}
      {!loading && !sugestao && coordsCacheRef.current.length >= 2 && (
        <div style={{ textAlign:"center", padding:"14px 0 4px" }}>
          <button onClick={() => {
            const coords = coordsCacheRef.current;
            if (coords.length < 2) return;
            // Ordem inteligente: ENTREGA primeiro, TROCA, depois RETIRADA — cada grupo por proximidade
            const smartOrder = ordenarParadasInteligente(coords, baseCacheRef.current);
            const reorderedCoords = smartOrder.map(idx => coords[idx]);
            const smartWaypoints = reorderedCoords.map(c => c.latLng[1] + "," + c.latLng[0]).join(";");
            const wp = baseCacheRef.current ? (baseCacheRef.current[1] + "," + baseCacheRef.current[0] + ";" + smartWaypoints) : smartWaypoints;
            fetch("https://api.mapbox.com/directions/v5/mapbox/driving/" + wp + "?access_token=" + MAPBOX_TOKEN + "&geometries=geojson&overview=full&language=pt")
              .then(r => r.json()).then(data => {
                if (data.routes && data.routes[0]) {
                  const route = data.routes[0];
                  const optDistKm = (route.distance / 1000).toFixed(1);
                  const optDeslocMin = Math.round(route.duration / 60);
                  const optOpMin = reorderedCoords.reduce((acc, c) => acc + (TEMPO_OP[c.op] || 15), 0);
                  const currentDist = rotaInfo ? parseFloat(rotaInfo.distancia) : 0;
                  const economia = currentDist - parseFloat(optDistKm);
                  const ordemMudou = smartOrder.some((idx, i) => idx !== i);
                  setSugestao({
                    distancia: optDistKm, deslocamento: optDeslocMin, operacoes: optOpMin,
                    total: optDeslocMin + optOpMin, economia: economia > 0 ? economia.toFixed(1) : "0",
                    order: smartOrder, geometry: route.geometry,
                    reorderedLocIds: smartOrder.map(idx => coords[idx].locId),
                    jaOtimizada: !ordemMudou || economia <= 0.5,
                    descricao: "Entregas primeiro, depois trocas, depois retiradas \u2014 cada grupo pela mais pr\u00f3xima"
                  });
                } else {
                  alert("N\u00e3o foi poss\u00edvel calcular a rota. Verifique se os endere\u00e7os est\u00e3o corretos.");
                }
              }).catch(() => alert("Erro ao calcular rota otimizada."));
          }} style={{
            background:"linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", color:"#fff", border:"none",
            borderRadius:10, padding:"12px 28px", fontSize:13, fontWeight:700, cursor:"pointer",
            fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:8,
            boxShadow:"0 4px 12px rgba(15,23,42,.2)", transition:"transform .15s, box-shadow .15s"
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.03)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(15,23,42,.3)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(15,23,42,.2)"; }}>
            💡 Sugerir rota inteligente
          </button>
        </div>
      )}
    </div>
  );
}

// ─── MotoristasAoVivoModal ────────────────────────────────────────────────────
// Mapa em tempo real da posição atual dos motoristas (feature Controla Plus).
// O app do motorista envia a posição a cada 60s enquanto está em foreground.
// ─────────────────────────────────────────────────────────────────────────────
function MotoristasAoVivoModal({ onClose, apiFetch, empresaCfg, addToast }) {
  const [motoristasLive, setMotoristasLive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [selTick, setSelTick] = useState(0);
  const [locked, setLocked] = useState(false); // true = plano não permite
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({}); // { [motoristaId]: L.Marker }
  const refreshTimerRef = useRef(null);

  const planoOk = empresaCfg && ["controla_plus", "escala"].includes(String(empresaCfg.plano || "").toLowerCase());

  // Carrega motoristas com posição
  const carregar = async () => {
    try {
      // apiFetch retorna o JSON direto (array) e LANÇA erro em falha — não é um Response.
      // (o código antigo testava r.ok/r.status, que são undefined, e caía sempre no return)
      const rows = await apiFetch("/api/motoristas/ao-vivo");
      setMotoristasLive(Array.isArray(rows) ? rows : []);
      setLoading(false);
    } catch (e) {
      // 403 (plano não permite) chega como erro lançado
      const msg = String(e?.message || "").toLowerCase();
      if (msg.includes("plus") || msg.includes("plano")) setLocked(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!planoOk) { setLocked(true); setLoading(false); return; }
    carregar();
    refreshTimerRef.current = setInterval(carregar, 30 * 1000); // auto-refresh 30s
    return () => { if (refreshTimerRef.current) clearInterval(refreshTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planoOk]);

  // Inicializa o mapa (só quando destravado)
  useEffect(() => {
    if (!planoOk || locked) return;
    if (!window.L || !mapRef.current || mapInstanceRef.current) return;

    const map = window.L.map(mapRef.current, { zoomControl: true })
      .setView([-23.55, -46.63], 11); // São Paulo como fallback
    window.L.tileLayer(
      `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
      { tileSize: 512, zoomOffset: -1, maxZoom: 19, attribution: "© Mapbox © OpenStreetMap" }
    ).addTo(map);
    mapInstanceRef.current = map;

    return () => {
      try { map.remove(); } catch {}
      mapInstanceRef.current = null;
      markersRef.current = {};
    };
  }, [planoOk, locked]);

  // Classifica o "frescor" da posição
  const classificarFrescor = (seg) => {
    if (seg == null) return { cor: "#94a3b8", label: "Sem posição", ordem: 3 };
    if (seg <= 120) return { cor: "#16a34a", label: "Ao vivo", ordem: 0 };
    if (seg <= 600) return { cor: "#f59e0b", label: "Recente", ordem: 1 };
    return { cor: "#94a3b8", label: "Offline", ordem: 2 };
  };

  const fmtTempo = (seg) => {
    if (seg == null) return "—";
    if (seg < 60) return `${seg}s atrás`;
    if (seg < 3600) return `${Math.floor(seg / 60)}min atrás`;
    if (seg < 86400) return `${Math.floor(seg / 3600)}h atrás`;
    return `${Math.floor(seg / 86400)}d atrás`;
  };

  // Atualiza markers quando a lista muda
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const idsAtuais = new Set();
    motoristasLive.forEach(m => {
      if (m.lat_atual == null || m.lng_atual == null) return;
      idsAtuais.add(m.id);
      const frescor = classificarFrescor(m.segundos_desde_update);

      const iconHtml = `
        <div style="position:relative;display:flex;flex-direction:column;align-items:center;">
          <div style="background:${frescor.cor};color:#fff;padding:3px 9px;border-radius:12px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff;margin-bottom:2px;">
            ${m.nome ? m.nome.split(" ")[0] : "?"}
          </div>
          <div style="width:28px;height:28px;border-radius:50%;background:${frescor.cor};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#fff;font-size:15px;">🚚</div>
          <div style="width:2px;height:8px;background:${frescor.cor};"></div>
        </div>`;
      const icon = window.L.divIcon({
        html: iconHtml,
        className: "motorista-live-icon",
        iconSize: [80, 50],
        iconAnchor: [40, 50],
      });

      const popupHtml = `
        <div style="font-family:system-ui,sans-serif;min-width:180px;">
          <div style="font-weight:700;font-size:14px;margin-bottom:6px;">${m.nome || "—"}</div>
          <div style="font-size:12px;color:#64748b;margin-bottom:3px;">
            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${frescor.cor};margin-right:6px;"></span>
            ${frescor.label} — ${fmtTempo(m.segundos_desde_update)}
          </div>
          ${m.tel ? `<div style="font-size:11px;color:#64748b;">📞 ${m.tel}</div>` : ""}
        </div>`;

      if (markersRef.current[m.id]) {
        markersRef.current[m.id].setLatLng([m.lat_atual, m.lng_atual]);
        markersRef.current[m.id].setIcon(icon);
        markersRef.current[m.id].setPopupContent(popupHtml);
      } else {
        const marker = window.L.marker([m.lat_atual, m.lng_atual], { icon })
          .addTo(map)
          .bindPopup(popupHtml);
        marker.on("click", () => { setSelId(m.id); setSelTick(t => t + 1); });
        markersRef.current[m.id] = marker;
      }
    });

    // Remove markers de motoristas que não estão mais na lista
    Object.keys(markersRef.current).forEach(id => {
      if (!idsAtuais.has(Number(id)) && !idsAtuais.has(id)) {
        try { map.removeLayer(markersRef.current[id]); } catch {}
        delete markersRef.current[id];
      }
    });

    // Ajusta bounds na primeira carga (quando há markers e ainda não centralizou)
    const comPos = motoristasLive.filter(m => m.lat_atual != null && m.lng_atual != null);
    if (comPos.length > 0 && !mapInstanceRef.current._alreadyFitBounds) {
      const bounds = window.L.latLngBounds(comPos.map(m => [m.lat_atual, m.lng_atual]));
      try { map.fitBounds(bounds.pad(0.2)); mapInstanceRef.current._alreadyFitBounds = true; } catch {}
    }
  }, [motoristasLive]);

  // Foca no marker ao clicar na lista
  useEffect(() => {
    if (selId == null) return;
    const map = mapInstanceRef.current;
    const marker = markersRef.current[selId];
    if (map && marker) {
      map.setView(marker.getLatLng(), 15, { animate: true });
      marker.openPopup();
    }
  }, [selId, selTick]);

  // Render do empty/locked state
  if (locked || !planoOk) {
    return (
      <LargeModal title="📍 Motoristas ao vivo" subtitle="Veja em tempo real onde seus motoristas estão" onClose={onClose} hideSave maxWidth={720}>
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>🔒</div>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 8, fontFamily: FONT }}>
            Disponível no plano Controla Plus
          </h3>
          <p style={{ fontSize: 14, color: "#64748b", maxWidth: 460, margin: "0 auto 20px", lineHeight: 1.6, fontFamily: FONT }}>
            Acompanhe em tempo real a posição de cada motorista no mapa enquanto eles cumprem o itinerário.
            Disponível apenas para assinantes do <b>Controla Plus</b>.
          </p>
          <button onClick={onClose} style={{ background: "#1aae9e", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>
            Entendi
          </button>
        </div>
      </LargeModal>
    );
  }

  // Ordena motoristas: os "ao vivo" primeiro, depois por nome
  const motoristasOrdenados = [...motoristasLive].sort((a, b) => {
    const fa = classificarFrescor(a.segundos_desde_update).ordem;
    const fb = classificarFrescor(b.segundos_desde_update).ordem;
    if (fa !== fb) return fa - fb;
    return (a.nome || "").localeCompare(b.nome || "");
  });

  const comPos = motoristasOrdenados.filter(m => m.lat_atual != null && m.lng_atual != null);
  const semPos = motoristasOrdenados.filter(m => m.lat_atual == null || m.lng_atual == null);

  return (
    <LargeModal title="📍 Motoristas ao vivo" subtitle={loading ? "Carregando posições..." : `${comPos.length} motorista${comPos.length !== 1 ? "s" : ""} com posição · atualiza a cada 30s`} onClose={onClose} hideSave maxWidth={1100}>
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, height: "72vh" }}>
        {/* LISTA LATERAL */}
        <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, overflowY: "auto", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: .5, marginBottom: 10, fontFamily: FONT }}>
            Motoristas do App
          </div>
          {loading && <div style={{ color: "#94a3b8", fontSize: 12, padding: 12 }}>Carregando...</div>}
          {!loading && motoristasOrdenados.length === 0 && (
            <div style={{ fontSize: 12, color: "#64748b", padding: 12, fontFamily: FONT }}>
              Nenhum motorista com app ativo. Ative o app no cadastro do motorista.
            </div>
          )}
          {motoristasOrdenados.map(m => {
            const frescor = classificarFrescor(m.segundos_desde_update);
            const temPos = m.lat_atual != null && m.lng_atual != null;
            const ativo = selId === m.id;
            return (
              <div key={m.id}
                onClick={() => { if (temPos) { setSelId(m.id); setSelTick(t => t + 1); } }}
                style={{
                  background: ativo ? "#e0f2fe" : "#fff",
                  border: ativo ? "2px solid #0d9488" : "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "10px 12px",
                  marginBottom: 6,
                  cursor: temPos ? "pointer" : "default",
                  opacity: temPos ? 1 : 0.6,
                  transition: "all .15s",
                  fontFamily: FONT,
                }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: frescor.cor, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.nome || "—"}</div>
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, paddingLeft: 18 }}>
                  {frescor.label} {temPos ? "· " + fmtTempo(m.segundos_desde_update) : ""}
                </div>
              </div>
            );
          })}
        </div>

        {/* MAPA */}
        <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid #e2e8f0" }}>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
          {!loading && comPos.length === 0 && (
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", background: "rgba(255,255,255,.95)", padding: "16px 24px", borderRadius: 10, boxShadow: "0 4px 12px rgba(0,0,0,.15)", textAlign: "center", fontFamily: FONT }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📡</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>Aguardando posições</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, maxWidth: 260 }}>
                Peça para o motorista abrir o app. A posição aparece aqui em até 60s.
              </div>
            </div>
          )}
        </div>
      </div>
    </LargeModal>
  );
}

// ─── Itinerarios ──────────────────────────────────────────────────────────────
function Itinerarios({ itinerarios: initIts, setItinerarios: setGlobalIts, locacoes, setLocacoes, motoristas, apiFetch, addToast, empresaNome, empresaEndereco, empresaCfg }) {
  const [its, setIts]   = useState(initIts);
  const [sel, setSel]   = useState(null);
  const [modal, setModal] = useState(false);
  const [confirmarModal, setConfirmarModal] = useState(null); // { itIdx, parIdx, loc, par }
  const [editModal, setEditModal] = useState(null);
  const [showParabens, setShowParabens] = useState(false);
  const [showConfirmFechar, setShowConfirmFechar] = useState(false);
  const [showMapa, setShowMapa] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [alertPopup, setAlertPopup] = useState(null); // { title, msg, type:"error"|"warning"|"info" }
  const [showTutorialIt, setShowTutorialIt] = useState(() => !localStorage.getItem("controla_tutorial_itinerarios"));
  const [demandaBusca, setDemandaBusca] = useState("");
  const [demandaOpFilter, setDemandaOpFilter] = useState("TODAS");
  const [demandaApenasLivres, setDemandaApenasLivres] = useState(true);
  const [showAoVivo, setShowAoVivo] = useState(false); // modal Motoristas ao vivo
  const [dragFromDemanda, setDragFromDemanda] = useState(null); // locId sendo arrastado da esquerda
  const [dragItIdx, setDragItIdx] = useState(null); // indice arrastado na direita (reorder)
  const [dragOverItIdx, setDragOverItIdx] = useState(null); // indice de destino (reorder)
  const [dropZoneActive, setDropZoneActive] = useState(false); // highlight zona de drop
  // Sincronizar com estado global
  useEffect(() => { setIts(initIts); }, [initIts]);
  const [filterData, setFilterData] = useState("");
  const [filterMotorista, setFilterMotorista] = useState("");
  const [paginaIt, setPaginaIt] = useState(1);
  const POR_PAGINA = 5;
  const itsFiltered = its
    .map((it, origIdx) => ({ ...it, _origIdx: origIdx }))
    .filter(it => {
      if (filterData && it.data !== filterData) return false;
      if (filterMotorista && it.motorista !== filterMotorista) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.data !== b.data) return a.data > b.data ? -1 : 1;
      return (b.id || b._origIdx) - (a.id || a._origIdx);
    });
  const totalPaginas = Math.max(1, Math.ceil(itsFiltered.length / POR_PAGINA));
  const paginaAtual = Math.min(paginaIt, totalPaginas);
  const itsPaginados = itsFiltered.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);
  const it = sel !== null ? its[sel] : null;

  const opColor = { ENTREGA:"#2ecc71", RETIRADA:"#e74c3c", TROCA:"#f39c12" };

  const moverParada = (itIdx, fromIdx, toIdx) => {
    const itObj = its[itIdx];
    if (!itObj || toIdx < 0 || toIdx >= itObj.paradas.length) return;
    const novasParadas = [...itObj.paradas];
    const [moved] = novasParadas.splice(fromIdx, 1);
    novasParadas.splice(toIdx, 0, moved);
    const updater = prev => prev.map((itm, i) => i !== itIdx ? itm : { ...itm, paradas: novasParadas });
    setIts(updater);
    if (setGlobalIts) setGlobalIts(updater);
    // Salvar no backend
    if (itObj.id) apiFetch("/api/itinerarios/" + itObj.id, { method: "PUT", body: { motorista: itObj.motorista, data: itObj.data, locIds: novasParadas.map(p => p.locId) } }).catch(() => {});
  };

  // Confirmar atendimento (chamado pelo modal ou desfazer direto)
  const confirmarAtendimento = (itIdx, parIdx, formData) => {
    const itObj = its[itIdx];
    if (!itObj) return;
    const par = itObj.paradas[parIdx];
    const newParStatus = par.status === "Atendido" ? "Pendente" : "Atendido";
    const locId = par.locId;

    const updater = prev => {
      const next = prev.map((it, i) => {
        if (i !== itIdx) return it;
        const paradas = it.paradas.map((p, j) =>
          j === parIdx ? { ...p, status: newParStatus } : p
        );
        const atendidos = paradas.filter(p => p.status === "Atendido").length;
        const pendentes = paradas.length - atendidos;
        // NÃO fecha automaticamente — somente manual via botão "Fechar Itinerário"
        const status = it.status === "Concluído" ? "Concluído" : atendidos === 0 ? "Em Aberto" : "Em Andamento";
        return { ...it, paradas, atendidos, pendentes, total: paradas.length, status };
      });
      return next;
    };
    setIts(updater);
    if (setGlobalIts) setGlobalIts(updater);

    // Buscar a locação para saber a operação REAL (derivada do status)
    const locObj = locacoes.find(l => String(l.id) === String(locId)) || {};
    const operacao = formData?.operacao || getOperacao(locObj);
    const pago = formData?.pago !== undefined ? formData.pago : !!locObj.pago;

    // Dados de pagamento vindos do formulário
    const pagData = pago ? {
      dataPagamento: formData?.dataPagamento || hojeLocal(),
      modalidadePagamento: formData?.modalidadePagamento || "",
      obsPagamento: formData?.obsPagamento || ""
    } : {};

    if (newParStatus === "Atendido") {
      const mot = formData?.motorista || itObj.motorista || "";
      if (operacao === "RETIRADA") {
        // Retirada: locação → Encerrada se paga, senão Para Cobrar
        const statusFinal = pago ? "Encerrada" : "Para Cobrar";
        const patchBody = { status: statusFinal, motoristaRetirada: mot, pago: pago ? 1 : 0 };
        if (formData?.dataColocacao) patchBody.dataRetiradaReal = formData.dataColocacao;
        if (formData?.mtr) patchBody.mtr = formData.mtr;
        patchBody.comissaoRetirada = formData?.comissaoRetirada || null;
        if (pago) { patchBody.dataPagamento = pagData.dataPagamento; patchBody.modalidadePagamento = pagData.modalidadePagamento; patchBody.obsPagamento = pagData.obsPagamento; }
        if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(locId) ? { ...l, status: statusFinal, pago, motoristaRetirada: mot, dataRetiradaReal: formData?.dataColocacao || hojeLocal(), ...pagData } : l));
        apiFetch("/api/locacoes/" + locId + "/status", { method: "PATCH", body: patchBody }).catch(() => {});
        apiFetch("/api/itinerarios/marcar-colocada", { method: "PATCH", body: { locacaoIds: [locId] } }).catch(() => {});
        apiFetch("/api/historico/" + locId, { method: "POST", body: { acao: "Status → " + statusFinal, detalhes: "Retirada confirmada no itinerário. Motorista: " + (mot || "—") + (pago ? " (Pago - " + (pagData.modalidadePagamento||"") + ")" : " (Não pago)") } }).catch(() => {});
      } else if (operacao === "TROCA") {
        // ═══ TROCA: Detectar se locId é a FILHA ou a PAI ═══
        const obsLoc = locObj.obs || locObj.observacoes || "";
        const isFilha = typeof obsLoc === "string" && (obsLoc.toLowerCase().includes("troca da locação #") || obsLoc.toLowerCase().includes("troca da locacao #"));

        if (isFilha) {
          // ═══ FILHA no itinerário: filha → Colocada, pai → Para Cobrar/Encerrada ═══
          // [1] FILHA (locId) → Colocada
          const patchFilha = { status: "Colocada", motorista: mot };
          if (formData?.dataColocacao) patchFilha.dataColocacaoReal = formData.dataColocacao;
          if (formData?.dataRetirada) patchFilha.dataRetiraPrevista = formData.dataRetirada;
          patchFilha.comissao = formData?.comissaoColocacao || null;
          if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(locId) ? { ...l, status: "Colocada", motorista: mot, dataColocacaoReal: formData?.dataColocacao || hojeLocal(), dataRetiraPrevista: formData?.dataRetirada || "" } : l));
          apiFetch("/api/locacoes/" + locId + "/status", { method: "PATCH", body: patchFilha }).catch(() => {});
          apiFetch("/api/itinerarios/marcar-colocada", { method: "PATCH", body: { locacaoIds: [locId] } }).catch(() => {});
          apiFetch("/api/historico/" + locId, { method: "POST", body: { acao: "Troca confirmada → Colocada", detalhes: "Caçamba colocada no endereço via itinerário. Motorista: " + (mot || "—") } }).catch(() => {});

          // [2] PAI → Para Cobrar ou Encerrada (extrair ID do pai do obs)
          const parentMatch = obsLoc.match(/#(\d+)/);
          if (parentMatch) {
            const parentId = parentMatch[1];
            const parentLoc = locacoes.find(l2 => String(l2.id) === parentId);
            const parentPago = pago || !!parentLoc?.pago;
            const statusPai = parentPago ? "Encerrada" : "Para Cobrar";
            const patchPai = { status: statusPai, motoristaRetirada: mot, pago: parentPago ? 1 : 0 };
            if (formData?.dataColocacao) patchPai.dataRetiradaReal = formData.dataColocacao;
            if (formData?.mtr) patchPai.mtr = formData.mtr;
            patchPai.comissaoRetirada = formData?.comissaoRetirada || null;
            if (parentPago) { patchPai.dataPagamento = pagData.dataPagamento || hojeLocal(); patchPai.modalidadePagamento = pagData.modalidadePagamento || ""; patchPai.obsPagamento = pagData.obsPagamento || ""; }
            if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === parentId ? { ...l, status: statusPai, pago: parentPago, motoristaRetirada: mot, dataRetiradaReal: formData?.dataColocacao || hojeLocal() } : l));
            apiFetch("/api/locacoes/" + parentId + "/status", { method: "PATCH", body: patchPai }).catch(() => {});
            apiFetch("/api/historico/" + parentId, { method: "POST", body: { acao: "Status → " + statusPai + " (Troca)", detalhes: "Caçamba retirada via troca no itinerário. Motorista: " + (mot || "—") + (parentPago ? " (Pago)" : " (Não pago)") } }).catch(() => {});
          }
          if (addToast) addToast("Troca confirmada!", "Caçamba colocada e antiga retirada", "", "#1aae9e");
        } else {
          // ═══ PAI no itinerário (fluxo original): pai → Para Cobrar, filha → Colocada ═══
          const statusFinal = pago ? "Encerrada" : "Para Cobrar";
          const patchBody = { status: statusFinal, motoristaRetirada: mot, pago: pago ? 1 : 0 };
          if (formData?.dataColocacao) patchBody.dataRetiradaReal = formData.dataColocacao;
          if (formData?.mtr) patchBody.mtr = formData.mtr;
          patchBody.comissaoRetirada = formData?.comissaoRetirada || null;
          if (pago) { patchBody.dataPagamento = pagData.dataPagamento; patchBody.modalidadePagamento = pagData.modalidadePagamento; patchBody.obsPagamento = pagData.obsPagamento; }
          if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(locId) ? { ...l, status: statusFinal, pago, motoristaRetirada: mot, dataRetiradaReal: formData?.dataColocacao || hojeLocal(), ...pagData } : l));
          apiFetch("/api/locacoes/" + locId + "/status", { method: "PATCH", body: patchBody }).catch(() => {});
          apiFetch("/api/itinerarios/marcar-colocada", { method: "PATCH", body: { locacaoIds: [locId] } }).catch(() => {});
          apiFetch("/api/historico/" + locId, { method: "POST", body: { acao: "Status → " + statusFinal + " (Troca)", detalhes: "Troca confirmada no itinerário. Motorista: " + (mot || "—") + (pago ? " (Pago)" : " (Não pago)") } }).catch(() => {});
          // Filha → Colocada
          const childExistente = locacoes.find(cl => cl.obs && typeof cl.obs === "string" && (cl.obs.toLowerCase().includes("troca da locação #" + locId) || cl.obs.toLowerCase().includes("troca da locacao #" + locId)));
          if (childExistente) {
            apiFetch("/api/locacoes/" + childExistente.id + "/status", { method: "PATCH", body: { status: "Colocada", motorista: mot, dataRetiraPrevista: formData?.dataRetirada || null, comissao: formData?.comissaoColocacao || null } }).catch(() => {});
            if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(childExistente.id) ? { ...l, status: "Colocada", motorista: mot, dataColocacaoReal: formData?.dataColocacao || hojeLocal() } : l));
            apiFetch("/api/historico/" + childExistente.id, { method: "POST", body: { acao: "Troca confirmada → Colocada", detalhes: "Via itinerário. Motorista: " + (mot || "—") } }).catch(() => {});
          }
          if (addToast) addToast("Troca confirmada!", "", "", "#1aae9e");
        }
      } else {
        // Entrega: locação → Colocada
        const patchBody = { status: "Colocada", motorista: mot, pago: pago ? 1 : 0 };
        if (formData?.dataColocacao) patchBody.dataColocacaoReal = formData.dataColocacao;
        if (formData?.dataRetirada) patchBody.dataRetiraPrevista = formData.dataRetirada;
        patchBody.comissao = formData?.comissao || null;
        if (pago) { patchBody.dataPagamento = pagData.dataPagamento; patchBody.modalidadePagamento = pagData.modalidadePagamento; patchBody.obsPagamento = pagData.obsPagamento; }
        if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(locId) ? { ...l, status: "Colocada", pago, motorista: mot, dataColocacaoReal: formData?.dataColocacao || hojeLocal(), dataRetiraPrevista: formData?.dataRetirada || l.dataRetiraPrevista, ...pagData } : l));
        apiFetch("/api/locacoes/" + locId + "/status", { method: "PATCH", body: patchBody }).catch(() => {});
        apiFetch("/api/itinerarios/marcar-colocada", { method: "PATCH", body: { locacaoIds: [locId] } }).catch(() => {});
        apiFetch("/api/historico/" + locId, { method: "POST", body: { acao: "Status → Colocada", detalhes: "Entrega confirmada no itinerário. Motorista: " + (mot || "—") + (pago ? " (Pago - " + (pagData.modalidadePagamento||"") + ")" : "") } }).catch(() => {});
      }
    } else {
      // Desfazer — volta ao status anterior conforme operação
      const statusAnterior = operacao === "RETIRADA" ? "Para Retirar" : operacao === "TROCA" ? "Para Trocar" : "Para Colocar";
      if (setLocacoes) setLocacoes(prev => prev.map(l => String(l.id) === String(locId) ? { ...l, status: statusAnterior } : l));
      apiFetch("/api/locacoes/" + locId + "/status", { method: "PATCH", body: { status: statusAnterior } }).catch(() => {});
      apiFetch("/api/historico/" + locId, { method: "POST", body: { acao: "Status → " + statusAnterior, detalhes: "Desfeito atendimento no itinerário" } }).catch(() => {});
    }

    const paradasAtualizadas = itObj.paradas.map((p, j) => j === parIdx ? { ...p, status: newParStatus } : p);
    const todosConcluidos = paradasAtualizadas.every(p => p.status === "Atendido");
    const toastOp = operacao === "RETIRADA" ? "Retirada" : operacao === "TROCA" ? "Troca" : "Entrega";
    const toastStatus = (operacao === "RETIRADA" || operacao === "TROCA") ? (pago ? "Encerrada" : "Para Cobrar") : "Colocada";
    if (todosConcluidos && newParStatus === "Atendido") {
      if (addToast) addToast("Itinerário Concluído!", "Todas as paradas foram atendidas", "", "#2ecc71");
    } else {
      if (addToast) addToast(newParStatus === "Atendido" ? toastOp + " atendida! Locação → " + toastStatus : "Atendimento desfeito", "", newParStatus === "Atendido" ? "" : "", newParStatus === "Atendido" ? "#2ecc71" : "#f39c12");
    }
    setTimeout(() => { if (setLocacoes) apiFetch("/api/locacoes").then(fl => { if (fl && Array.isArray(fl)) setLocacoes(fl.map(r => ({ ...r, dataEntrega: r.data_entrega||r.dataEntrega||'', dataRetirada: r.data_retirada||r.dataRetirada||'', dataRetiraPrevista: r.data_retirada||r.dataRetiraPrevista||'', dataPagamento: r.data_pagamento||r.dataPagamento||'', dataRetiradaReal: r.data_retirada_real||r.dataRetiradaReal||'', dataColocacaoReal: r.data_colocacao_real||r.dataColocacaoReal||'', dataSolicitacao: r.data_solicitacao||r.dataSolicitacao||'', dataTrocaPrevista: r.data_troca_prevista||r.dataTrocaPrevista||'', dataPedidoTroca: r.data_pedido_troca||r.dataPedidoTroca||'', dataPedidoRetirada: r.data_pedido_retirada||r.dataPedidoRetirada||'', motoristaRetirada: r.motorista_retirada||r.motoristaRetirada||'', prazoPagamento: r.prazo_pagamento||r.prazoPagamento||'', cacamba: r.cacamba_codigo||r.cacamba||'', cliente: r.cliente_nome||r.cliente||'', tipoCacamba: r.tipo_cacamba||r.tipoCacamba||'', contatoTel: r.contato_tel||r.contatoTel||'', contatoNome: r.contato_nome||r.contatoNome||'', logradouro: r.logradouro||(r.endereco?.logradouro)||'', numero: r.numero||(r.endereco?.numero)||'', bairro: r.bairro||(r.endereco?.bairro)||'', cidade: r.cidade||(r.endereco?.cidade)||'', uf: r.uf||(r.endereco?.uf)||'', pago: !!r.pago, valor: parseFloat(r.valor)||0, status: r.status||'Para Colocar', endereco: r.endereco||{}, obs: r.observacoes||r.obs||'' }))); }).catch(()=>{}); }, 800);
  };

  const addIt = async (data) => {
    try {
      // Bloquear duplicado: mesmo motorista + mesmo dia
      const jaExiste = its.find(i => i.motorista === data.motorista && i.data === data.data);
      if (jaExiste) {
        setAlertPopup({ title:"Itinerário duplicado", msg:"Já existe um itinerário para o motorista " + data.motorista + " no dia " + fmtDate(data.data) + ". Cada motorista pode ter apenas um itinerário por dia.", type:"warning" });
        return;
      }
      const novo = await apiFetch("/api/itinerarios", { method: "POST", body: data });
      const paradas = (novo.locIds||data.locIds).map(id => ({ locId: id, status: "Pendente", obs: "" }));

      // Locação filha de troca NÃO é pré-criada — só será criada ao confirmar a troca no itinerário
      // Isso evita: locação fantasma "Para Colocar", IDs pulando, novaLocId se perdendo

      const newIt = { ...novo, motorista: novo.motorista||data.motorista, data: novo.data||data.data, total: paradas.length, atendidos: 0, pendentes: paradas.length, status: "Em Aberto", paradas };
      setIts(p => [...p, newIt]);
      if (setGlobalIts) setGlobalIts(p => [...p, newIt]);
      setModal(false);
      if (addToast) addToast("Itinerário criado!", "success");
    } catch(err) {
      console.error("Erro ao criar itinerário:", err);
      if (addToast) addToast("Erro ao criar itinerário. Verifique os dados e tente novamente.", "error");
    }
  };

  const excluirIt = async (itIdx) => {
    const itObj = its[itIdx];
    if (!itObj) return;
    if (!window.confirm("Tem certeza que deseja excluir este itinerário de " + fmtDate(itObj.data) + " — " + itObj.motorista + "?\n\nEsta ação não pode ser desfeita.")) return;
    try {
      if (itObj.id) await apiFetch("/api/itinerarios/" + itObj.id, { method: "DELETE" });
      const updater = prev => prev.filter((_, i) => i !== itIdx);
      setIts(updater);
      if (setGlobalIts) setGlobalIts(updater);
      setSel(null);
      if (addToast) addToast("Itinerário excluído!", "", "", "#e74c3c");
    } catch (err) {
      if (addToast) addToast("Erro ao excluir itinerário.", "error");
    }
  };

  const editarIt = async (itIdx, data) => {
    const itObj = its[itIdx];
    if (!itObj || !itObj.id) return;
    try {
      // Bloquear duplicado ao editar
      const jaExiste = its.find((i, idx) => idx !== itIdx && i.motorista === data.motorista && i.data === data.data);
      if (jaExiste) {
        setAlertPopup({ title:"Itinerário duplicado", msg:"Já existe um itinerário para o motorista " + data.motorista + " no dia " + fmtDate(data.data) + ". Cada motorista pode ter apenas um itinerário por dia.", type:"warning" });
        return;
      }
      await apiFetch("/api/itinerarios/" + itObj.id, { method: "PUT", body: data });
      const updater = prev => prev.map((it, i) => {
        if (i !== itIdx) return it;
        const paradas = (data.locIds || []).map(id => {
          const existing = it.paradas?.find(p => String(p.locId) === String(id));
          return existing || { locId: id, status: "Pendente", obs: "" };
        });
        const atendidos = paradas.filter(p => p.status === "Atendido").length;
        const pendentes = paradas.length - atendidos;
        const status = it.status === "Concluído" ? "Concluído" : atendidos === 0 ? "Em Aberto" : "Em Andamento";
        return { ...it, motorista: data.motorista, data: data.data, paradas, atendidos, pendentes, total: paradas.length, status };
      });
      setIts(updater);
      if (setGlobalIts) setGlobalIts(updater);
      setEditModal(null);
      if (addToast) addToast("Itinerário atualizado!", "", "", "#1aae9e");
    } catch (err) {
      if (addToast) addToast("Erro ao editar itinerário.", "error");
    }
  };

  // Aplicar filtro de status
  const itsFiltroPorStatus = itsFiltered.filter(it => {
    if (statusFilter === "Todos") return true;
    if (statusFilter === "Em Aberto") return it.status === "Em Aberto";
    if (statusFilter === "Em Andamento") return it.status === "Em Andamento";
    if (statusFilter === "Concluído") return it.status === "Concluído";
    return true;
  });

  // Adicionar demanda ao itinerário
  const adicionarDemanda = (locId) => {
    const newParadas = [...it.paradas, { locId: String(locId), status: "Pendente", obs: "" }];
    const updater = prev => prev.map((itm, i) => i !== sel ? itm : { ...itm, paradas: newParadas, total: newParadas.length, pendentes: newParadas.filter(p => p.status === "Pendente").length, atendidos: newParadas.filter(p => p.status === "Atendido").length });
    setIts(updater);
    if (setGlobalIts) setGlobalIts(updater);
    if (it.id) apiFetch("/api/itinerarios/" + it.id, { method: "PUT", body: { motorista: it.motorista, data: it.data, locIds: newParadas.map(p => p.locId) } }).catch(() => {});
    if (addToast) addToast("Locação adicionada ao itinerário!", "", "", "#1aae9e");
  };

  // Remover parada do itinerário
  const removerParada = (parIdx) => {
    const removida = it.paradas[parIdx];
    const newParadas = it.paradas.filter((_, i) => i !== parIdx);
    const newStatus = it.status === "Concluído" ? "Concluído" : newParadas.filter(p => p.status === "Atendido").length === 0 ? "Em Aberto" : "Em Andamento";
    const updater = prev => prev.map((itm, i) => i !== sel ? itm : { ...itm, paradas: newParadas, total: newParadas.length, pendentes: newParadas.filter(p => p.status === "Pendente").length, atendidos: newParadas.filter(p => p.status === "Atendido").length, status: newStatus });
    setIts(updater);
    if (setGlobalIts) setGlobalIts(updater);
    if (it.id) apiFetch("/api/itinerarios/" + it.id, { method: "PUT", body: { motorista: it.motorista, data: it.data, locIds: newParadas.map(p => p.locId) } }).catch(() => {});
    if (addToast) addToast("Locação #" + (removida?.locId || "") + " removida do itinerário", "", "", "#f39c12");
  };

  return (
    <div className="itinerario-grid" style={{ display:"grid", gridTemplateColumns:it?"320px 1fr":"1fr", gap:16, alignItems:"start" }}>
      {/* === LIST VIEW — Quando nenhum itinerário selecionado === */}
      {!it && (
        <div style={{ gridColumn:"1 / -1" }}>
          {/* Header */}
          <div className="page-action-bar" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <div>
              <h2 style={{ fontSize:24, fontWeight:900, color:"#0f172a", margin:0, letterSpacing:-.4, fontFamily:FONT }}>Itinerários</h2>
              <span style={{ fontSize:13, color:"#94a3b8" }}>{itsFiltroPorStatus.length} itinerário{itsFiltroPorStatus.length !== 1 ? "s" : ""}</span>
            </div>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <button onClick={() => setShowAoVivo(true)} style={{
                background:"#fff",
                color:"#0d9488",
                border:"1.5px solid #0d9488",
                borderRadius:10,
                padding:"11px 18px",
                fontSize:13,
                fontWeight:700,
                cursor:"pointer",
                display:"flex",
                alignItems:"center",
                gap:6,
                fontFamily:FONT,
                transition:"all .15s"
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f0fdfa"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
                📍 Motoristas ao vivo
              </button>
              <button onClick={() => setModal(true)} style={{ background:"linear-gradient(135deg, #1aae9e 0%, #0d9488 100%)", color:"#fff", border:"none", borderRadius:10, padding:"12px 24px", fontSize:14, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:7, fontFamily:FONT, boxShadow:"0 4px 12px rgba(26,174,158,.25)", transition:"all .15s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(26,174,158,.35)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(26,174,158,.25)"; }}>
                <span>+</span> Novo Itinerário
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="filter-row" style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
            <input type="date" value={filterData} onChange={e=>{setFilterData(e.target.value);setPaginaIt(1);}} style={{...inp, fontSize:13, padding:"10px 12px", borderRadius:8, border:"1px solid #e2e8f0", minWidth:140 }} />
            <select value={filterMotorista} onChange={e=>{setFilterMotorista(e.target.value);setPaginaIt(1);}} style={{...inp, fontSize:13, padding:"10px 12px", borderRadius:8, border:"1px solid #e2e8f0", minWidth:160, appearance:"none"}}>
              <option value="">Todos motoristas</option>
              {motoristas.map(m=><option key={m.id||m.nome} value={m.nome}>{m.nome}</option>)}
            </select>

            {/* Status Filter Tabs */}
            <div style={{ display:"flex", gap:8, alignItems:"center", marginLeft:"auto" }}>
              {["Todos", "Em Aberto", "Em Andamento", "Concluído"].map(st => (
                <button
                  key={st}
                  onClick={() => setStatusFilter(st)}
                  style={{
                    background: statusFilter === st ? "#0d9488" : "#f1f5f9",
                    color: statusFilter === st ? "#fff" : "#475569",
                    border: statusFilter === st ? "none" : "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "8px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: FONT,
                    transition: "all .15s"
                  }}
                  onMouseEnter={e => { if (statusFilter !== st) e.currentTarget.style.background = "#e2e8f0"; }}
                  onMouseLeave={e => { if (statusFilter !== st) e.currentTarget.style.background = "#f1f5f9"; }}
                >
                  {st}
                </button>
              ))}
            </div>

            {(filterData || filterMotorista || statusFilter !== "Todos") && (
              <button onClick={() => { setFilterData(""); setFilterMotorista(""); setStatusFilter("Todos"); setPaginaIt(1); }} style={{
                background:"#f1f5f9", border:"1px solid #e2e8f0", borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer",
                color:"#64748b", fontFamily:FONT, fontWeight:600, transition:"all .15s"
              }}
                onMouseEnter={e => e.currentTarget.style.background = "#e2e8f0"}
                onMouseLeave={e => e.currentTarget.style.background = "#f1f5f9"}>
                Limpar
              </button>
            )}
          </div>

          {/* Tabela de Itinerários */}
          {itsFiltroPorStatus.length === 0 ? (
            <EmptyState icon="📋" title="Nenhum itinerário encontrado" subtitle={its.length===0?"Clique em 'Novo Itinerário' para começar":"Nenhum resultado para os filtros aplicados"} />
          ) : (
            <>
              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, overflow:"hidden" }}>
                <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                  <thead>
                    <tr style={{ borderBottom:"2px solid #e2e8f0" }}>
                      {["Data Itinerário","Motorista Responsável","Paradas","Progresso","Situação","Criado em"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"12px 16px", fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:.3, background:"#f8fafc" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {itsFiltroPorStatus.slice((paginaAtual-1)*POR_PAGINA, paginaAtual*POR_PAGINA).map((itCard) => {
                      const i = itCard._origIdx;
                      const isFechado = itCard.status === "Concluído";
                      const isAndamento = itCard.status === "Em Andamento";
                      const pctCard = itCard.total > 0 ? Math.round((itCard.atendidos / itCard.total) * 100) : 0;
                      // Contar operações
                      const ops = itCard.paradas.map(p => { const l = locacoes.find(lc => String(lc.id) === String(p.locId)) || {}; return getOperacao(l); });
                      const nE = ops.filter(o => o === "ENTREGA").length;
                      const nR = ops.filter(o => o === "RETIRADA").length;
                      const nT = ops.filter(o => o === "TROCA").length;
                      const nNao = itCard.paradas.filter(p => p.status === "Não atendido" || p.status === "Não Atendido").length;
                      return (
                        <tr key={i} onClick={() => setSel(i)} style={{ borderBottom:"1px solid #f1f5f9", cursor:"pointer", transition:"background .15s", background:"#fff" }}
                          onMouseEnter={e => e.currentTarget.style.background="#f8fffe"}
                          onMouseLeave={e => e.currentTarget.style.background="#fff"}>
                          <td data-label="Data" style={{ padding:"14px 16px" }}>
                            <span style={{ color:"#0d9488", fontWeight:700, cursor:"pointer" }}>{fmtDate(itCard.data)}</span>
                          </td>
                          <td className="mob-title" style={{ padding:"14px 16px", fontWeight:600, color:"#0f172a" }}>{itCard.motorista}</td>
                          <td data-label="Paradas" style={{ padding:"14px 16px" }}>
                            <div style={{ display:"flex", gap:4, fontSize:11 }}>
                              <span style={{ fontWeight:700, color:"#0f172a" }}>{itCard.total}</span>
                              <span style={{ color:"#94a3b8" }}>({nE > 0 ? nE+"E " : ""}{nR > 0 ? nR+"R " : ""}{nT > 0 ? nT+"T" : ""})</span>
                            </div>
                          </td>
                          <td data-label="Progresso" style={{ padding:"14px 16px" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ width:60, height:6, background:"#f1f5f9", borderRadius:4, overflow:"hidden" }}>
                                <div style={{ height:"100%", width:pctCard+"%", background:isFechado?"#22c55e":"#14b8a6", borderRadius:4 }} />
                              </div>
                              <span style={{ fontSize:11, fontWeight:600, color:isFechado?"#16a34a":"#64748b" }}>{pctCard}%</span>
                            </div>
                          </td>
                          <td data-label="Situação" style={{ padding:"14px 16px" }}>
                            <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", color:isFechado?"#16a34a":isAndamento?"#d97706":"#64748b", background:isFechado?"#dcfce7":isAndamento?"#fef3c7":"#f1f5f9", padding:"4px 10px", borderRadius:6, border:"1px solid "+(isFechado?"#bbf7d0":isAndamento?"#fde68a":"#e2e8f0") }}>
                              {isFechado ? "FECHADO" : isAndamento ? "EM ANDAMENTO" : "EM ABERTO"}
                            </span>
                            {nNao > 0 && (
                              <span style={{ marginLeft:6, fontSize:10, fontWeight:700, color:"#b45309", background:"#fef3c7", border:"1px solid #fde68a", padding:"3px 8px", borderRadius:6, whiteSpace:"nowrap" }}>
                                ⚠ {nNao} não realizada{nNao > 1 ? "s" : ""}
                              </span>
                            )}
                          </td>
                          <td className="mob-hide" style={{ padding:"14px 16px", fontSize:12, color:"#94a3b8" }}>{fmtDate(itCard.data)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Paginação */}
              {Math.ceil(itsFiltroPorStatus.length / POR_PAGINA) > 1 && (
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, padding:"16px 0" }}>
                  <button disabled={paginaAtual<=1} onClick={() => setPaginaIt(paginaAtual-1)} style={{ background:paginaAtual<=1?"#f8fafc":"#fff", border:"1px solid #e2e8f0", borderRadius:6, padding:"6px 12px", fontSize:12, cursor:paginaAtual<=1?"not-allowed":"pointer", color:paginaAtual<=1?"#cbd5e1":"#475569", fontWeight:600, fontFamily:FONT }}>Anterior</button>
                  {Array.from({ length: Math.ceil(itsFiltroPorStatus.length / POR_PAGINA) }, (_, p) => (
                    <button key={p+1} onClick={() => setPaginaIt(p+1)} style={{ background:paginaAtual===p+1?"#0d9488":"#fff", color:paginaAtual===p+1?"#fff":"#475569", border:paginaAtual===p+1?"none":"1px solid #e2e8f0", borderRadius:6, width:32, height:32, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>{p+1}</button>
                  ))}
                  <button disabled={paginaAtual>=Math.ceil(itsFiltroPorStatus.length/POR_PAGINA)} onClick={() => setPaginaIt(paginaAtual+1)} style={{ background:paginaAtual>=Math.ceil(itsFiltroPorStatus.length/POR_PAGINA)?"#f8fafc":"#fff", border:"1px solid #e2e8f0", borderRadius:6, padding:"6px 12px", fontSize:12, cursor:paginaAtual>=Math.ceil(itsFiltroPorStatus.length/POR_PAGINA)?"not-allowed":"pointer", color:paginaAtual>=Math.ceil(itsFiltroPorStatus.length/POR_PAGINA)?"#cbd5e1":"#475569", fontWeight:600, fontFamily:FONT }}>Próxima</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* === NEW DETAIL VIEW — Full-width two-panel layout === */}
      {it && (
        <div style={{ gridColumn: "1 / -1" }}>
          {/* TOP HEADER BAR */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, borderBottom: "1px solid #e2e8f0", marginBottom: 16 }}>
            <button onClick={() => setSel(null)} style={{ background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "#0d9488", cursor: "pointer", fontFamily: FONT, padding: 0 }}>
              ← Voltar
            </button>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{fmtDate(it.data)}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{it.motorista}</div>
            </div>
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              color: it.status === "Concluído" ? "#4ade80" : it.status === "Em Andamento" ? "#fbbf24" : "#94a3b8",
              background: it.status === "Concluído" ? "rgba(74,222,128,.15)" : it.status === "Em Andamento" ? "rgba(251,191,36,.15)" : "rgba(148,163,184,.15)",
              padding: "6px 12px",
              borderRadius: 20,
              border: "1px solid " + (it.status === "Concluído" ? "rgba(74,222,128,.3)" : it.status === "Em Andamento" ? "rgba(251,191,36,.3)" : "rgba(148,163,184,.3)")
            }}>{it.status}</span>
          </div>

          {/* ACTION BUTTONS ROW */}
          <div className="itinerario-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 16 }}>
            {it.status !== "Concluído" && (
              <button onClick={() => setEditModal({ itIdx: sel, motorista: it.motorista, data: it.data, paradas: it.paradas })}
                style={{ background: "#0d9488", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: FONT, transition: "all .15s", display: "inline-flex", alignItems: "center", gap: 5 }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                Editar
              </button>
            )}
            <button onClick={() => {
              const mot = motoristas.find(m => m.nome === it.motorista);
              const tel = mot?.tel || mot?.telefone || "";
              if (!tel) { alert("Motorista sem telefone cadastrado.\n\nCadastre o telefone do motorista para enviar via WhatsApp."); return; }
              const phone = tel.replace(/\D/g,"");
              const phoneFormatted = phone.length <= 11 ? "55" + phone : phone;
              const paradasTexto = it.paradas.map((p, i) => {
                const loc = locacoes.find(l => String(l.id) === String(p.locId)) || {};
                const end = typeof loc.endereco === "string" ? loc.endereco : loc.endereco ? [loc.endereco.logradouro, loc.endereco.numero, loc.endereco.bairro, loc.endereco.cidade].filter(Boolean).join(", ") : "—";
                const op = getOperacao(loc);
                return `*${i+1}. ${op}*\n${loc.cliente || "—"}\n${end}`;
              }).join("\n\n");
              const msg = `*ITINERÁRIO — ${fmtDate(it.data)}*\n\nMotorista: *${it.motorista}*\nTotal de paradas: *${it.total}*\n\n${paradasTexto}\n\n_${empresaNome || "Controla Caçambas"}_`;
              window.open("https://wa.me/" + phoneFormatted + "?text=" + encodeURIComponent(msg), "_blank");
            }} style={{ background: "#25d366", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: FONT, transition: "all .15s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
              WhatsApp
            </button>
            <button onClick={() => gerarItinerarioPDF(it, locacoes, empresaNome)} style={{ background: "#334155", border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: FONT, transition: "all .15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#1e293b"}
              onMouseLeave={e => e.currentTarget.style.background = "#334155"}>
              Imprimir
            </button>
            {it.status !== "Concluído" && (
              <button onClick={() => setShowConfirmFechar(true)} style={{
                background: it.atendidos === it.total && it.total > 0 ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" : "#334155",
                border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: FONT, transition: "all .15s"
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                Fechar Itinerário
              </button>
            )}
            {it.status === "Concluído" && (
              <button onClick={async () => {
                if (!window.confirm("Reabrir este itinerário?")) return;
                const itObj = its[sel];
                let novoStatus = (it.paradas || []).some(p => p.status === "Atendido") ? "Em Andamento" : "Em Aberto";
                try {
                  if (itObj?.id) {
                    const r = await apiFetch("/api/itinerarios/" + itObj.id + "/reabrir", { method: "PATCH" });
                    if (r?.status) novoStatus = r.status;
                  }
                  const updater = prev => prev.map((itm, i) => i !== sel ? itm : { ...itm, status: novoStatus });
                  setIts(updater);
                  if (setGlobalIts) setGlobalIts(updater);
                  if (addToast) addToast("Itinerário reaberto!", "Status: " + novoStatus, "", "#f59e0b");
                } catch (err) {
                  if (addToast) addToast("Erro ao reabrir itinerário.", err?.message || "", "", "#e74c3c");
                }
              }} style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                border: "none", borderRadius: 6, padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: FONT, transition: "all .15s"
              }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                🔓 Reabrir Itinerário
              </button>
            )}
          </div>

          {/* SUMMARY CARDS ROW */}
          {(() => {
            const paradasInfo = it.paradas.map(p => {
              const l = locacoes.find(lc => String(lc.id) === String(p.locId)) || {};
              return { ...p, op: getOperacao(l) };
            });
            const countEntregas = paradasInfo.filter(p => p.op === "ENTREGA").length;
            const countRetiradas = paradasInfo.filter(p => p.op === "RETIRADA").length;
            const countTrocas = paradasInfo.filter(p => p.op === "TROCA").length;
            const pct = it.total > 0 ? Math.round((it.atendidos / it.total) * 100) : 0;
            return (
              <div className="kpi-cards-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{countEntregas}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#15803d", marginTop: 2 }}>Entregas</div>
                </div>
                <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#dc2626" }}>{countRetiradas}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#991b1b", marginTop: 2 }}>Retiradas</div>
                </div>
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#d97706" }}>{countTrocas}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#92400e", marginTop: 2 }}>Trocas</div>
                </div>
                <div style={{ background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 10, padding: "12px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#0d9488" }}>{pct}%</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#0d5d54", marginTop: 2 }}>Progresso</div>
                </div>
              </div>
            );
          })()}

          {/* MODO ROTA INTELIGENTE TOGGLE */}
          <div style={{ marginBottom: 16 }}>
            <button onClick={() => setShowMapa(!showMapa)} style={{
              width: "100%",
              background: showMapa ? "linear-gradient(135deg, #0d9488 0%, #06b6d4 100%)" : "#f1f5f9",
              color: showMapa ? "#fff" : "#475569",
              border: showMapa ? "none" : "1px solid #e2e8f0",
              borderRadius: 8,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: FONT,
              transition: "all .2s"
            }}
              onMouseEnter={e => { if (!showMapa) e.currentTarget.style.background = "#e2e8f0"; }}
              onMouseLeave={e => { if (!showMapa) e.currentTarget.style.background = "#f1f5f9"; }}>
              {showMapa ? "Ocultar Mapa de Rota" : "Modo Rota Inteligente"}
            </button>
          </div>

          {/* MAP COMPONENT */}
          {showMapa && it.paradas && it.paradas.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <ItinerarioMapa paradas={it.paradas} locacoes={locacoes} itObj={it} apiFetch={apiFetch} baseEndereco={empresaEndereco} onReorder={(newLocIds) => {
                const newParadas = newLocIds.map(locId => {
                  const existing = it.paradas.find(p => String(p.locId) === String(locId));
                  return existing || { locId, status: "Pendente", obs: "" };
                });
                const updater = prev => prev.map((itm, i) => i !== sel ? itm : { ...itm, paradas: newParadas });
                setIts(updater);
                if (setGlobalIts) setGlobalIts(updater);
                if (it.id) apiFetch("/api/itinerarios/" + it.id, { method: "PUT", body: { motorista: it.motorista, data: it.data, locIds: newLocIds } }).catch(() => {});
                if (addToast) addToast("Rota otimizada!", "", "", "#16a34a");
              }} />
            </div>
          )}

          {/* TWO-PANEL LAYOUT */}
          <div className="itinerario-panels" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* LEFT PANEL — Pesquisar Demandas */}
            {it.status !== "Concluído" && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Pesquisar Demandas</div>

                {/* Search + Filter */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ position: "relative", marginBottom: 8 }}>
                    <input type="text" placeholder="Pesquisar por cliente, nº locação..." value={demandaBusca} onChange={e => setDemandaBusca(e.target.value)} style={{ ...inp, fontSize: 12, padding: "7px 10px 7px 30px", width: "100%" }} />
                    <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 13 }}>🔍</span>
                  </div>

                  {/* Operation Filter Buttons */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                    {["TODAS", "ENTREGA", "RETIRADA", "TROCA"].map(op => (
                      <button key={op} onClick={() => setDemandaOpFilter(op)} style={{
                        padding: "5px 10px", fontSize: 11, fontWeight: 700, border: "1px solid", borderRadius: 3, cursor: "pointer",
                        borderColor: demandaOpFilter === op ? "#1aae9e" : "#e0e0e0",
                        background: demandaOpFilter === op ? "rgba(26,174,158,.1)" : "#fff",
                        color: demandaOpFilter === op ? "#1aae9e" : "#666",
                        fontFamily: FONT
                      }}>
                        {op}
                      </button>
                    ))}
                  </div>

                  {/* Checkbox: apenas sem itinerário */}
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555", cursor: "pointer", userSelect: "none" }}>
                    <input type="checkbox" checked={demandaApenasLivres} onChange={e => setDemandaApenasLivres(e.target.checked)} style={{ accentColor: "#1aae9e" }} />
                    Exibir apenas locações sem itinerário
                  </label>
                </div>

                {/* Demandas List */}
                {(() => {
                  const locIdsNoItinerario = new Set((it?.paradas || []).map(p => String(p.locId)));
                  const locIdsEmOutrosItinerarios = new Set();
                  its.forEach((itx, idx) => {
                    if (idx === sel) return;
                    if (itx.status === "Concluído") return;
                    (itx.paradas || []).forEach(p => {
                      // Parada já concluída (entrega feita) não bloqueia a próxima operação:
                      // libera a locação p/ agendar a retirada em outro itinerário.
                      if (p.status === "Atendido") return;
                      locIdsEmOutrosItinerarios.add(String(p.locId));
                    });
                  });

                  // Na troca: esconder pai (Para Trocar que tem filha), mostrar só a filha
                  const isFilhaTrocaIt = (l) => {
                    const obs = l.obs || l.observacoes || "";
                    return typeof obs === "string" && (obs.toLowerCase().includes("troca da locação #") || obs.toLowerCase().includes("troca da locacao #"));
                  };
                  const isPaiTrocaComFilhaIt = (l) => {
                    if (l.status !== "Para Trocar") return false;
                    return locacoes.some(cl => isFilhaTrocaIt(cl) && (cl.obs||cl.observacoes||"").includes("#" + l.id) && cl.status !== "Cancelada");
                  };

                  let demandas = locacoes.filter(l => {
                    if (locIdsNoItinerario.has(String(l.id))) return false;
                    if (!["Para Colocar", "Para Retirar", "Para Trocar"].includes(l.status)) return false;
                    // Esconder pai da troca que já tem filha — mostrar só a filha
                    if (isPaiTrocaComFilhaIt(l)) return false;
                    if (demandaApenasLivres && locIdsEmOutrosItinerarios.has(String(l.id))) return false;
                    if (demandaOpFilter !== "TODAS" && getOperacao(l) !== demandaOpFilter) return false;
                    if (demandaBusca.trim()) {
                      const s = norm(demandaBusca);
                      if (!norm(l.cliente).includes(s) && !String(l.id).includes(demandaBusca) && !norm(typeof l.endereco === "string" ? l.endereco : [l.logradouro, l.bairro, l.cidade].filter(Boolean).join(" ")).includes(s)) return false;
                    }
                    return true;
                  });

                  return (
                    <div className="demandas-list" style={{ border:"1px solid #e2e8f0", borderRadius:8, overflowY:"auto", maxHeight:500 }}>
                      {demandas.length === 0 && <div style={{ padding:24, textAlign:"center", color:"#94a3b8", fontSize:12 }}>Nenhuma locação encontrada</div>}
                      {demandas.map((l, i) => {
                        // Filha de troca "Para Colocar" → mostrar como TROCA
                        const op = (isFilhaTrocaIt(l) && l.status === "Para Colocar") ? "TROCA" : getOperacao(l);
                        const opColors = { ENTREGA:"#2ecc71", RETIRADA:"#e74c3c", TROCA:"#f39c12" };
                        const cor = opColors[op] || "#888";
                        const end = typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro, l.endereco.numero, l.endereco.bairro, l.endereco.cidade].filter(Boolean).join(", ") : "";
                        const tel = l.contatoTel || l.contato_tel || l.telefone || l.tel || "";
                        const isDragging = dragFromDemanda === l.id;
                        return (
                          <div key={i} draggable onDragStart={() => setDragFromDemanda(l.id)} onDragEnd={() => { setDragFromDemanda(null); setDropZoneActive(false); }}
                            style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:"1px solid #f1f5f9", background:isDragging?"#e6faf8":"#fff", borderLeft:"3px solid "+cor, cursor:"grab", opacity:isDragging?.5:1, transition:"all .15s" }}
                            onMouseEnter={e => { if(!isDragging) e.currentTarget.style.background="#f8fffe"; }}
                            onMouseLeave={e => { if(!isDragging) e.currentTarget.style.background="#fff"; }}>
                            <span style={{ color:"#ccc", fontSize:16, userSelect:"none", cursor:"grab", flexShrink:0 }} title="Arraste para o itinerário">&#x2630;</span>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                                <span style={{ fontSize:10, fontWeight:700, color:cor, textTransform:"uppercase" }}>{op}</span>
                                <span style={{ fontSize:11, fontWeight:700, color:"#0d9488" }}>#{l.id}</span>
                              </div>
                              <div style={{ fontWeight:600, fontSize:12, color:"#0f172a", marginBottom:1 }}>{l.cliente}</div>
                              {end && <div style={{ fontSize:10, color:"#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{end}</div>}
                              {(tel || l.valor) && <div style={{ display:"flex", gap:6, marginTop:3, fontSize:10, color:"#64748b" }}>
                                {tel && <span>{tel}</span>}
                                {l.valor && <span style={{ fontWeight:600 }}>R$ {Number(l.valor).toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>}
                              </div>}
                            </div>
                            <button onClick={() => adicionarDemanda(l.id)} style={{ background:"#0d9488", border:"none", borderRadius:6, padding:"6px 12px", cursor:"pointer", color:"#fff", fontWeight:700, fontSize:11, fontFamily:FONT, whiteSpace:"nowrap", transition:"all .15s" }}
                              onMouseEnter={e => e.currentTarget.style.background="#0f766e"}
                              onMouseLeave={e => e.currentTarget.style.background="#0d9488"}>
                              + Adicionar
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* RIGHT PANEL — Itens do Itinerário (Drop Zone + Drag Reorder) */}
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#0f172a", marginBottom:12 }}>Itens do Itinerário ({it.paradas.length})</div>

              <div
                onDragOver={e => { e.preventDefault(); if (dragFromDemanda) setDropZoneActive(true); }}
                onDragLeave={() => setDropZoneActive(false)}
                onDrop={e => { e.preventDefault(); if (dragFromDemanda) { adicionarDemanda(dragFromDemanda); setDragFromDemanda(null); } setDropZoneActive(false); setDragItIdx(null); setDragOverItIdx(null); }}
                style={{ border:dropZoneActive?"2px dashed #1aae9e":"1px solid #e2e8f0", borderRadius:8, background:dropZoneActive?"#e6faf8":"#fff", minHeight:80, transition:"all .2s" }}>

                {it.paradas.length === 0 && (
                  <div style={{ padding:32, textAlign:"center", color:dropZoneActive?"#0d9488":"#94a3b8", fontSize:12, fontWeight:500 }}>
                    {dropZoneActive ? "Solte aqui para adicionar" : "Nenhuma parada adicionada. Arraste locações da esquerda ou clique em '+ Adicionar'."}
                  </div>
                )}

                {it.paradas.map((par, pi) => {
                  const loc = locacoes.find(l => String(l.id) === String(par.locId)) || { cliente:"—", endereco:"—" };
                  const atendido = par.status === "Atendido";
                  const naoAtendido = par.status === "Não atendido" || par.status === "Não Atendido";
                  const op = getOperacao(loc);
                  const corOp = op === "ENTREGA" ? "#2ecc71" : op === "RETIRADA" ? "#e74c3c" : "#f39c12";
                  const corBgOp = op === "ENTREGA" ? "#f0fdf4" : op === "RETIRADA" ? "#fef2f2" : "#fffbeb";
                  const end = typeof loc.endereco === "string" ? loc.endereco : loc.endereco ? [loc.endereco.logradouro, loc.endereco.numero, loc.endereco.bairro, loc.endereco.cidade].filter(Boolean).join(", ") : "";
                  const tel = loc.contatoTel || loc.contato_tel || loc.telefone || loc.tel || "";
                  const isDragging = dragItIdx === pi;
                  const isOver = dragOverItIdx === pi && dragItIdx !== null && dragItIdx !== pi;

                  return (
                    <div key={pi}
                      draggable={!atendido && it.status !== "Concluído"}
                      onDragStart={e => { e.stopPropagation(); setDragItIdx(pi); setDragFromDemanda(null); }}
                      onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (dragItIdx !== null) setDragOverItIdx(pi); }}
                      onDrop={e => {
                        e.preventDefault(); e.stopPropagation();
                        if (dragItIdx !== null && dragItIdx !== pi) { moverParada(sel, dragItIdx, pi); }
                        else if (dragFromDemanda) { adicionarDemanda(dragFromDemanda); setDragFromDemanda(null); }
                        setDragItIdx(null); setDragOverItIdx(null); setDropZoneActive(false);
                      }}
                      onDragEnd={() => { setDragItIdx(null); setDragOverItIdx(null); }}
                      style={{
                        background: isDragging ? "#e0f2fe" : atendido ? "#f9fafb" : naoAtendido ? "#fff7ed" : corBgOp,
                        borderBottom:"1px solid "+(atendido?"#e5e7eb":"#e2e8f0"),
                        borderTop: isOver ? "3px solid #1aae9e" : "3px solid transparent",
                        borderLeft: naoAtendido ? "4px solid #f59e0b" : undefined,
                        padding:"12px 14px",
                        opacity: isDragging ? .4 : atendido ? .7 : 1,
                        cursor: atendido ? "default" : "grab",
                        transition:"all .15s",
                      }}>
                      {/* Header: numero + op + id + status + remover */}
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          {!atendido && it.status !== "Concluído" && <span style={{ color:"#cbd5e1", fontSize:16, cursor:"grab", userSelect:"none", flexShrink:0 }} title="Arraste para reordenar">&#x2630;</span>}
                          <span style={{ width:26, height:26, borderRadius:"50%", background:atendido?"#22c55e":naoAtendido?"#f59e0b":corOp, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, flexShrink:0 }}>
                            {atendido ? "✓" : naoAtendido ? "⚠" : pi+1}
                          </span>
                          <span style={{ fontSize:12, fontWeight:700, color:"#0f172a" }}>#{par.locId}</span>
                          <span style={{ fontSize:10, fontWeight:700, color:corOp, textTransform:"uppercase" }}>{op}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:atendido?"#16a34a":naoAtendido?"#b45309":"#94a3b8", background:atendido?"#dcfce7":naoAtendido?"#fef3c7":"#f1f5f9", padding:"3px 8px", borderRadius:10 }}>
                            {atendido ? "ATENDIDO" : naoAtendido ? "NÃO ATENDIDO" : "PENDENTE"}
                          </span>
                          {!atendido && it.status !== "Concluído" && (
                            <button onClick={() => removerParada(pi)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#e74c3c", padding:"2px", fontWeight:700, lineHeight:1 }} title="Remover">&#x2715;</button>
                          )}
                        </div>
                      </div>
                      {/* Body: cliente + endereço + telefone + valor */}
                      <div style={{ paddingLeft:atendido?0:24, marginLeft:atendido?0:8 }}>
                        <div style={{ fontWeight:700, fontSize:13, color:atendido?"#94a3b8":"#0f172a", textDecoration:atendido?"line-through":"none", marginBottom:2 }}>{loc.cliente}</div>
                        {end && <div style={{ fontSize:11, color:"#64748b", marginBottom:4 }}>{end}</div>}
                        {naoAtendido && (
                          <div style={{ fontSize:11, fontWeight:600, color:"#b45309", background:"#fef3c7", border:"1px solid #fde68a", borderRadius:6, padding:"4px 8px", marginBottom:4, display:"inline-block" }}>
                            ⚠ Visita não realizada{par.motivo ? ": " + par.motivo : ""}
                          </div>
                        )}
                        <div style={{ display:"flex", gap:8, alignItems:"center", justifyContent:"space-between", flexWrap:"wrap" }}>
                          <div style={{ display:"flex", gap:6, fontSize:10, color:"#64748b", flexWrap:"wrap" }}>
                            {tel && <span style={{ background:"#f1f5f9", padding:"2px 8px", borderRadius:4 }}>{tel}</span>}
                            {loc.valor && <span style={{ background:loc.pago?"#f0fdf4":"#fffbeb", padding:"2px 8px", borderRadius:4, fontWeight:700, color:loc.pago?"#16a34a":"#d97706" }}>R$ {Number(loc.valor).toLocaleString("pt-BR",{minimumFractionDigits:2})}{loc.pago?" (pago)":""}</span>}
                          </div>
                          {!atendido && it.status !== "Concluído" && (
                            <label style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:4 }} title="Confirmar atendimento">
                              <input type="checkbox" checked={false} readOnly onClick={e => {
                                e.preventDefault();
                                const locModal = locacoes.find(l2 => String(l2.id) === String(par.locId)) || {};
                                setConfirmarModal({ itIdx:sel, parIdx:pi, loc:locModal, par });
                              }} style={{ width:18, height:18, accentColor:corOp, cursor:"pointer" }} />
                            </label>
                          )}
                          {atendido && <span style={{ fontSize:11, color:"#22c55e", fontWeight:700 }}>Concluido</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parabéns Modal */}
      {showParabens && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowParabens(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
            borderRadius: 16,
            padding: "40px 48px",
            textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,.15)",
            maxWidth: 480
          }}>
            <h2 style={{ fontSize: 28, fontWeight: 900, color: "#166534", margin: "0 0 12px", fontFamily: FONT }}>Parabéns!</h2>
            <p style={{ fontSize: 15, color: "#15803d", margin: "0 0 8px", fontWeight: 600 }}>
              {it?.motorista} finalizou a rota!
            </p>
            <p style={{ fontSize: 13, color: "#22863a", margin: "0 0 24px", opacity: 0.8 }}>
              {fmtDate(it?.data)} — {it?.total} paradas atendidas
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setShowParabens(false)} style={{
                background: "#16a34a",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FONT
              }}>
                Fechar
              </button>
              <button onClick={() => { setShowParabens(false); gerarItinerarioPDF(it, locacoes, empresaNome); }} style={{
                background: "#fff",
                color: "#16a34a",
                border: "2px solid #16a34a",
                borderRadius: 8,
                padding: "10px 24px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FONT
              }}>
                PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Close Modal */}
      {showConfirmFechar && it && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowConfirmFechar(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff",
            borderRadius: 12,
            padding: "32px 36px",
            textAlign: "center",
            boxShadow: "0 20px 50px rgba(0,0,0,.2)",
            maxWidth: 400
          }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: "0 0 8px", fontFamily: FONT }}>Fechar Itinerário?</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
              {it.motorista} — {fmtDate(it.data)}
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <div style={{ background: "#f0fdf4", padding: "10px 16px", borderRadius: 8, border: "1px solid #bbf7d0" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#16a34a" }}>{it.atendidos}</div>
                <div style={{ fontSize: 10, color: "#15803d", fontWeight: 600 }}>Atendidas</div>
              </div>
              {it.pendentes > 0 && (
                <div style={{ background: "#fef3c7", padding: "10px 16px", borderRadius: 8, border: "1px solid #fde68a" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#d97706" }}>{it.pendentes}</div>
                  <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600 }}>Pendentes</div>
                </div>
              )}
            </div>
            {it.pendentes > 0 && (
              <p style={{ fontSize: 12, color: "#dc2626", margin: "0 0 16px", background: "#fef2f2", padding: "8px 12px", borderRadius: 6 }}>
                Atenção: {it.pendentes} parada{it.pendentes > 1 ? "s" : ""} não {it.pendentes > 1 ? "foram" : "foi"} atendida{it.pendentes > 1 ? "s" : ""}.
              </p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setShowConfirmFechar(false)} style={{
                background: "#f5f5f5",
                color: "#555",
                border: "1px solid #ddd",
                borderRadius: 6,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: FONT
              }}>
                Cancelar
              </button>
              <button onClick={() => {
                setShowConfirmFechar(false);
                const updater = prev => prev.map((itm, i) => i !== sel ? itm : { ...itm, status: "Concluído" });
                setIts(updater);
                if (setGlobalIts) setGlobalIts(updater);
                const itObj = its[sel];
                if (itObj?.id) apiFetch("/api/itinerarios/" + itObj.id, { method: "PUT", body: { motorista: itObj.motorista, data: itObj.data, locIds: itObj.paradas.map(p => p.locId), status: "Concluído" } }).catch(() => {});
                if (it.pendentes === 0) {
                  setShowParabens(true);
                } else {
                  if (addToast) addToast("Itinerário fechado!", "", "", "#2ecc71");
                }
              }} style={{
                background: it.pendentes > 0 ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "10px 20px",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FONT
              }}>
                {it.pendentes > 0 ? "Fechar Mesmo Assim" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAoVivo && <MotoristasAoVivoModal onClose={() => setShowAoVivo(false)} apiFetch={apiFetch} empresaCfg={empresaCfg} addToast={addToast} />}
      {modal && <NovoItinerarioModal onClose={() => setModal(false)} onSave={addIt} locacoes={locacoes} motoristas={motoristas} itinerarios={its} apiFetch={apiFetch} />}
      {editModal && <NovoItinerarioModal
        onClose={() => setEditModal(null)}
        onSave={(data) => editarIt(editModal.itIdx, data)}
        locacoes={locacoes}
        motoristas={motoristas}
        itinerarios={its}
        apiFetch={apiFetch}
        editData={{ motorista: editModal.motorista, data: editModal.data, locIds: (editModal.paradas||[]).map(p => p.locId) }}
      />}
      {confirmarModal && <ConfirmarAtendimentoModal
        loc={confirmarModal.loc}
        par={confirmarModal.par}
        motorista={it?.motorista || ""}
        motoristas={motoristas}
        dataItinerario={it?.data || hojeLocal()}
        onClose={() => setConfirmarModal(null)}
        onConfirmar={(formData) => {
          confirmarAtendimento(confirmarModal.itIdx, confirmarModal.parIdx, formData);
          setConfirmarModal(null);
        }}
      />}

      {/* Tutorial primeira vez */}
      {showTutorialIt && <TutorialItinerariosModal onClose={() => { localStorage.setItem("controla_tutorial_itinerarios", "1"); setShowTutorialIt(false); }} />}

      {/* Popup de Alerta Estilizado */}
      {alertPopup && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={() => setAlertPopup(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:12, padding:"32px 36px", maxWidth:420, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,.2)", textAlign:"center", animation:"fadeInScale .25s ease" }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background: alertPopup.type==="error"?"#fef2f2":alertPopup.type==="warning"?"#fffbeb":"#f0fdfa", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", border:"2px solid "+(alertPopup.type==="error"?"#fca5a5":alertPopup.type==="warning"?"#fde68a":"#99f6e4") }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={alertPopup.type==="error"?"#dc2626":alertPopup.type==="warning"?"#d97706":"#0d9488"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {alertPopup.type==="warning" ? <><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></> : alertPopup.type==="error" ? <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></> : <><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>}
              </svg>
            </div>
            <h3 style={{ fontSize:18, fontWeight:800, color:"#0f172a", margin:"0 0 8px", fontFamily:FONT }}>{alertPopup.title}</h3>
            <p style={{ fontSize:13, color:"#64748b", margin:"0 0 24px", lineHeight:1.6 }}>{alertPopup.msg}</p>
            <button onClick={() => setAlertPopup(null)} style={{ background:"linear-gradient(135deg, #1aae9e 0%, #0d9488 100%)", color:"#fff", border:"none", borderRadius:8, padding:"10px 36px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT, boxShadow:"0 2px 8px rgba(26,174,158,.3)" }}>
              Entendi
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfirmarAtendimentoModal({ loc, par, motorista, motoristas=[], dataItinerario, onClose, onConfirmar }) {
  const dataIt = dataItinerario || hojeLocal();
  const op = getOperacao(loc);
  const isRetirada = op === "RETIRADA";
  const isTroca = op === "TROCA";
  const MODALIDADES = ["Dinheiro", "PIX", "Cartão de Crédito", "Cartão de Débito", "Boleto Bancário", "Transferência Bancária", "Cheque"];

  // Sugestão de comissão a partir do cadastro do motorista (colocação/retirada)
  const sugComissao = (nome, campo) => {
    const m = (motoristas || []).find(x => (x.nome || String(x)) === nome);
    const raw = m ? (campo === "colocacao"
      ? (m.comissao_colocacao != null ? m.comissao_colocacao : m.comissaoColocacao)
      : (m.comissao_retirada != null ? m.comissao_retirada : m.comissaoRetirada)) : null;
    return (raw != null && raw !== "") ? String(parseFloat(raw)) : "";
  };
  const sugColIni = (!isRetirada) ? sugComissao(motorista || "", "colocacao") : "";
  const sugRetIni = (isRetirada || isTroca) ? sugComissao(motorista || "", "retirada") : "";

  // Data da operação é preenchida automaticamente com a data do itinerário
  const [form, setForm] = useState({
    dataColocacao: dataIt,
    dataRetirada: loc?.dataRetiraPrevista || loc?.dataRetirada || loc?.data_retirada || "",
    motorista: motorista || "",
    mtr: loc?.mtr || "",
    // Comissões (auto-preenchidas pelo motorista; enviadas ao backend conforme a operação)
    comissao: (!isRetirada && !isTroca) ? sugColIni : "",   // ENTREGA → comissao
    comissaoColocacao: isTroca ? sugColIni : "",             // TROCA (colocação da nova)
    comissaoRetirada: (isRetirada || isTroca) ? sugRetIni : "", // RETIRADA / TROCA (retirada da antiga)
    pago: !!loc?.pago,
    dataPagamento: loc?.pago ? (loc?.dataPagamento || loc?.data_pagamento || dataIt) : dataIt,
    modalidadePagamento: loc?.modalidadePagamento || loc?.modalidade_pagamento || "",
    obsPagamento: loc?.obsPagamento || loc?.obs_pagamento || "",
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Auto-preenchimento da comissão ao trocar de motorista — não sobrescreve valor digitado à mão.
  // Só preenche o campo se ele estiver vazio ou ainda contiver a sugestão anterior.
  const [comColEditada, setComColEditada] = useState(false);
  const [comRetEditada, setComRetEditada] = useState(false);
  const ultimaSugColRef = useRef(sugColIni);
  const ultimaSugRetRef = useRef(sugRetIni);
  const onMotoristaChange = (nome) => {
    const sugCol = sugComissao(nome, "colocacao");
    const sugRet = sugComissao(nome, "retirada");
    setForm(p => {
      const next = { ...p, motorista: nome };
      if (!isRetirada) {
        const campoCol = isTroca ? "comissaoColocacao" : "comissao";
        const atualCol = String(p[campoCol] || "");
        if (!comColEditada || atualCol === "" || atualCol === ultimaSugColRef.current) {
          ultimaSugColRef.current = sugCol;
          next[campoCol] = sugCol;
        }
      }
      if (isRetirada || isTroca) {
        const atualRet = String(p.comissaoRetirada || "");
        if (!comRetEditada || atualRet === "" || atualRet === ultimaSugRetRef.current) {
          ultimaSugRetRef.current = sugRet;
          next.comissaoRetirada = sugRet;
        }
      }
      return next;
    });
  };

  const confirm = () => {
    // Para entrega e troca, data prevista de retirada é obrigatória
    if (!isRetirada && !form.dataRetirada) { alert("A Data Prevista de Retirada é obrigatória."); return; }
    if (!isRetirada && form.dataColocacao && form.dataRetirada && form.dataRetirada < form.dataColocacao) { alert("A data de retirada não pode ser anterior à data da operação."); return; }
    if (form.pago && !form.modalidadePagamento) { alert("A Modalidade de Pagamento é obrigatória."); return; }
    onConfirmar({ ...form, operacao: op });
  };

  const endereco = typeof loc?.endereco === "string" ? loc.endereco : loc?.endereco ? [loc.endereco.logradouro, loc.endereco.numero, loc.endereco.bairro, loc.endereco.cidade, loc.endereco.uf].filter(Boolean).join(", ") : "—";
  const opLabel = isRetirada ? "Retirada" : isTroca ? "Troca" : "Entrega";
  const opBg = isRetirada ? "#e74c3c" : isTroca ? "#f39c12" : "#2ecc71";

  return (
    <LargeModal title={"Confirmar " + opLabel} subtitle={"Locação #" + (par?.locId||"")} onClose={onClose} onSave={confirm} saveLabel={"Confirmar " + opLabel} maxWidth={680}>
      {/* Info da locação */}
      <div style={{ background:"#f8fafc", border:"1px solid #e8e8e8", borderRadius:8, padding:16, marginTop:14, marginBottom:18 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>{loc?.cliente || "—"}</div>
            <div style={{ fontSize:12, color:"#888", marginTop:3 }}>📍 {endereco}</div>
            <div style={{ display:"flex", gap:14, marginTop:6, fontSize:12 }}>
              {loc?.cacamba && <span style={{ color:"#666" }}><b>{loc.cacamba}</b></span>}
              {loc?.tipoCacamba && <span style={{ color:"#888" }}>{loc.tipoCacamba}</span>}
              {loc?.valor > 0 && <span style={{ color:"#1aae9e", fontWeight:700 }}>R$ {Number(loc.valor).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>}
            </div>
          </div>
          <span style={{ fontSize:11, fontWeight:800, color:"#fff", background:opBg, padding:"4px 12px", borderRadius:4, letterSpacing:.3 }}>{op}</span>
        </div>
      </div>

      {/* Data da operação — automática do itinerário, exibida como informação */}
      <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:"12px 16px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:18 }}>📅</span>
        <div>
          <div style={{ fontSize:10, color:"#0369a1", fontWeight:700, textTransform:"uppercase", letterSpacing:.5 }}>Data da {opLabel}</div>
          <div style={{ fontSize:15, fontWeight:800, color:"#0c4a6e" }}>{fmtDate(dataIt)}</div>
        </div>
        <div style={{ marginLeft:"auto", fontSize:10, color:"#7dd3fc" }}>Preenchida automaticamente pelo itinerário</div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        {/* Data Prevista de Retirada — só para Entrega e Troca */}
        {!isRetirada && (
          <Field label="Data Prevista de Retirada *">
            <input type="date" style={{ ...inp, borderColor: !form.dataRetirada ? "#f39c12" : "#e0e0e0", fontWeight:600 }} value={form.dataRetirada} min={dataIt} onChange={e => setF("dataRetirada", e.target.value)} />
          </Field>
        )}
        <Field label="Motorista">
          <select style={{ ...inp, appearance:"none" }} value={form.motorista} onChange={e => onMotoristaChange(e.target.value)}>
            <option value="">-- Selecione --</option>
            {motoristas.map(m => <option key={m.id||m.nome} value={m.nome||String(m)}>{m.nome||String(m)}</option>)}
          </select>
        </Field>
        {(isRetirada || isTroca) && (
          <Field label="MTR/CTR">
            <input type="text" style={inp} value={form.mtr} onChange={e => setF("mtr", e.target.value)} placeholder="MTR-000000" />
          </Field>
        )}
        {/* Comissão do motorista — auto-preenchida pelo cadastro, editável */}
        {!isRetirada && !isTroca && (
          <Field label="Comissão do Motorista (R$)">
            <input type="number" style={inp} value={form.comissao} onChange={e => { setComColEditada(true); setF("comissao", e.target.value); }} placeholder="0,00" />
          </Field>
        )}
        {isRetirada && (
          <Field label="Comissão do Motorista (R$)">
            <input type="number" style={inp} value={form.comissaoRetirada} onChange={e => { setComRetEditada(true); setF("comissaoRetirada", e.target.value); }} placeholder="0,00" />
          </Field>
        )}
        {isTroca && (
          <Field label="Comissão Colocação (R$)">
            <input type="number" style={inp} value={form.comissaoColocacao} onChange={e => { setComColEditada(true); setF("comissaoColocacao", e.target.value); }} placeholder="0,00" />
          </Field>
        )}
        {isTroca && (
          <Field label="Comissão Retirada (R$)">
            <input type="number" style={inp} value={form.comissaoRetirada} onChange={e => { setComRetEditada(true); setF("comissaoRetirada", e.target.value); }} placeholder="0,00" />
          </Field>
        )}
      </div>

      {/* Seção de Pagamento */}
      <div style={{ borderTop:"1px solid #e8e8e8", paddingTop:14, marginTop:6 }}>
        <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none", marginBottom: form.pago ? 14 : 0 }}>
          <input type="checkbox" checked={form.pago} onChange={e => setF("pago", e.target.checked)} style={{ accentColor:"#1aae9e", width:18, height:18 }} />
          <span style={{ fontSize:13, fontWeight:700, color: form.pago ? "#16a34a" : "#555" }}>{form.pago ? "Caçamba paga" : "Caçamba não paga"}</span>
        </label>

        {form.pago && (
          <div style={{ background:"#f0fff4", border:"1px solid #bbf7d0", borderRadius:8, padding:14 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:10 }}>
              <Field label="Data do Pagamento *">
                <input type="date" style={{ ...inp, fontWeight:600 }} value={form.dataPagamento} onChange={e => setF("dataPagamento", e.target.value)} />
              </Field>
              <Field label="Modalidade de Pagamento *">
                <select style={{ ...inp, appearance:"none", borderColor:!form.modalidadePagamento?"#f39c12":"#e0e0e0", fontWeight:600 }} value={form.modalidadePagamento} onChange={e => setF("modalidadePagamento", e.target.value)}>
                  <option value="">-- Selecione --</option>
                  {MODALIDADES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Observações do Pagamento">
              <input type="text" style={inp} value={form.obsPagamento} onChange={e => setF("obsPagamento", e.target.value)} placeholder="Observações (opcional)" />
            </Field>
          </div>
        )}

        {/* Feedback visual do resultado da operação */}
        <div style={{ marginTop:12, padding:"10px 14px", borderRadius:8, fontSize:12, fontWeight:600, background: (isRetirada || isTroca) ? (form.pago ? "#f0fdf4" : "#fffbeb") : form.pago ? "#f0fdf4" : "#f8fafc", border:"1px solid " + ((isRetirada || isTroca) ? (form.pago ? "#bbf7d0" : "#fde68a") : form.pago ? "#bbf7d0" : "#e2e8f0"), color: (isRetirada || isTroca) ? (form.pago ? "#166534" : "#92400e") : form.pago ? "#166534" : "#475569" }}>
          {isRetirada && !form.pago && "Ao confirmar, a locação será movida para \"Para Cobrar\"."}
          {isRetirada && form.pago && "Ao confirmar, a locação será marcada como \"Encerrada\"."}
          {isTroca && !form.pago && "Ao confirmar: a caçamba antiga vai para \"Para Cobrar\" e a nova será marcada como \"Colocada\"."}
          {isTroca && form.pago && "Ao confirmar: a caçamba antiga será \"Encerrada\" e a nova marcada como \"Colocada\"."}
          {!isRetirada && !isTroca && !form.pago && "Ao confirmar, a locação será marcada como \"Colocada\"."}
          {!isRetirada && !isTroca && form.pago && "Ao confirmar, a locação será marcada como \"Colocada\" com pagamento registrado."}
        </div>
      </div>
    </LargeModal>
  );
}

function NovoItinerarioModal({ onClose, onSave, locacoes, motoristas=[], itinerarios=[], apiFetch, editData }) {
  const isEdit = !!editData;
  const [motorista, setMotorista] = useState(editData?.motorista || "");
  const [data, setData]           = useState(editData?.data || hojeLocal());
  const [locIds, setLocIds]       = useState(editData?.locIds || []);
  const [opFilt, setOpFilt]       = useState("TODAS");
  const [busca, setBusca]         = useState("");
  const [apenasLivres, setApenasLivres] = useState(true);
  const [menuAberto, setMenuAberto] = useState(null);
  const [viewLoc, setViewLoc]     = useState(null);
  const [dragIdx, setDragIdx]     = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragFromSearch, setDragFromSearch] = useState(null);
  const [dropHighlight, setDropHighlight] = useState(false);
  const [timelineLoc, setTimelineLoc] = useState(null);
  const [histData, setHistData]   = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const opColor = { ENTREGA:"#2ecc71", RETIRADA:"#e74c3c", TROCA:"#f39c12" };

  // Fechar menu ao clicar fora — DEVE ficar antes de qualquer return condicional
  useEffect(() => {
    if (!menuAberto) return;
    const handler = () => setMenuAberto(null);
    setTimeout(() => document.addEventListener("click", handler), 10);
    return () => document.removeEventListener("click", handler);
  }, [menuAberto]);

  // === MODO SIMPLES: Só motorista + data (para criação) ===
  if (!isEdit) {
    const saveCriar = () => {
      if (!motorista) { return; }
      if (!data) { return; }
      onSave({ motorista, data, locIds: [] });
    };
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.45)", zIndex:1200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ background:"#fff", borderRadius:12, maxWidth:480, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,.2)", overflow:"hidden" }}>
          <div style={{ padding:"24px 28px 20px", borderBottom:"1px solid #f1f5f9" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:"#0f172a", fontFamily:FONT }}>Novo Itinerário</h2>
                <p style={{ margin:"4px 0 0", fontSize:12, color:"#94a3b8" }}>Defina o motorista e a data para criar o itinerário</p>
              </div>
              <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", fontSize:20, padding:4 }}>✕</button>
            </div>
          </div>
          <div style={{ padding:"24px 28px" }}>
            <div style={{ marginBottom:20 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#374151", textTransform:"uppercase", letterSpacing:.5, marginBottom:6 }}>Motorista Responsável *</label>
              <select style={{ ...inp, width:"100%", appearance:"none", borderColor:!motorista?"#fca5a5":"#e2e8f0", fontSize:14, padding:"12px 14px", borderRadius:8 }} value={motorista} onChange={e => setMotorista(e.target.value)}>
                <option value="">-- Selecione --</option>
                {motoristas.map(m => <option key={m.id||m.nome} value={m.nome}>{m.nome}</option>)}
              </select>
            </div>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#374151", textTransform:"uppercase", letterSpacing:.5, marginBottom:6 }}>Data do Itinerário *</label>
              <input type="date" style={{ ...inp, width:"100%", fontSize:14, padding:"12px 14px", borderRadius:8 }} value={data} onChange={e => setData(e.target.value)} />
            </div>
            <p style={{ fontSize:12, color:"#94a3b8", margin:"0 0 4px", lineHeight:1.5 }}>Após criar, você poderá adicionar as locações na tela de edição do itinerário.</p>
          </div>
          <div style={{ padding:"16px 28px 24px", display:"flex", justifyContent:"flex-end", gap:10 }}>
            <button onClick={onClose} style={{ background:"#f1f5f9", color:"#64748b", border:"1px solid #e2e8f0", borderRadius:8, padding:"10px 20px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Cancelar</button>
            <button onClick={saveCriar} disabled={!motorista||!data} style={{ background:!motorista||!data?"#94a3b8":"linear-gradient(135deg, #1aae9e 0%, #0d9488 100%)", color:"#fff", border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:700, cursor:!motorista||!data?"not-allowed":"pointer", fontFamily:FONT, boxShadow:motorista&&data?"0 2px 8px rgba(26,174,158,.3)":"none", opacity:!motorista||!data?.6:1 }}>Continuar</button>
          </div>
        </div>
      </div>
    );
  }

  // === MODO EDIÇÃO: Modal completo com drag & drop ===

  // IDs de locações já vinculadas a algum itinerário ativo (não-Concluído)
  // Usar String() para normalizar comparações (locIds podem ser string ou number)
  const locIdsEmItinerarios = new Set();
  itinerarios.forEach(it => {
    if (it.status === "Concluído") return;
    (it.paradas || []).forEach(p => {
      // Só bloquear paradas Pendentes — Atendidas já foram resolvidas e a locação pode aparecer em novo itinerário
      if (p.status !== "Atendido") locIdsEmItinerarios.add(String(p.locId));
    });
  });
  // No modo edição, as locações do itinerário atual não devem ser filtradas
  const locIdsDoEditAtual = new Set(editData?.locIds || []);

  const addLoc = (id) => { if (!locIds.includes(id)) setLocIds(p => [...p, id]); setMenuAberto(null); };
  const removeLoc = (id) => setLocIds(p => p.filter(x => x !== id));
  const toggle = (id) => setLocIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  // Drag & drop — reordenar dentro dos itens
  const handleDragStart = (idx) => { setDragIdx(idx); setDragFromSearch(null); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (idx) => {
    if (dragFromSearch !== null) {
      // Drop vindo da pesquisa — adicionar na posição
      if (!locIds.includes(dragFromSearch)) {
        setLocIds(prev => { const arr = [...prev]; arr.splice(idx, 0, dragFromSearch); return arr; });
      }
      setDragFromSearch(null); setDragOverIdx(null); setDropHighlight(false); return;
    }
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    setLocIds(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setDragIdx(null); setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); setDragFromSearch(null); setDropHighlight(false); };

  // Drag & drop — arrastar da pesquisa para os itens
  const handleSearchDragStart = (locId) => { setDragFromSearch(locId); setDragIdx(null); };
  const handleDropZone = (e) => {
    e.preventDefault();
    if (dragFromSearch !== null && !locIds.includes(dragFromSearch)) {
      setLocIds(prev => [...prev, dragFromSearch]);
    }
    setDragFromSearch(null); setDropHighlight(false);
  };
  const handleDropZoneDragOver = (e) => { e.preventDefault(); setDropHighlight(true); };
  const handleDropZoneDragLeave = () => { setDropHighlight(false); };

  const statusExcluidos = ["Cancelada", "Encerrada", "Colocada", "Para Cobrar"];
  // Filha de troca só é oculta se ainda está em "Para Colocar" (redundante com a mãe "Para Trocar")
  // Se já evoluiu para outro status (ex: Para Retirar), aparece normalmente
  const isFilhaTrocaInicial = (l) => {
    if (l.status !== "Para Colocar") return false;
    const obs = l.obs || "";
    return typeof obs === "string" && (obs.toLowerCase().includes("troca da locação #") || obs.toLowerCase().includes("troca da locacao #"));
  };
  const locacoesAtivas = locacoes.filter(l => !statusExcluidos.includes(l.status) && !isFilhaTrocaInicial(l));
  let filtered = opFilt === "TODAS" ? locacoesAtivas : locacoesAtivas.filter(l => getOperacao(l) === opFilt);
  if (busca) {
    const b = norm(busca);
    filtered = filtered.filter(l => norm(l.cliente).includes(b) || String(l.id).includes(b) || norm(typeof l.endereco === "string" ? l.endereco : "").includes(b));
  }
  if (apenasLivres) {
    filtered = filtered.filter(l => !locIdsEmItinerarios.has(String(l.id)) || locIdsDoEditAtual.has(l.id));
  }

  const save = () => { if (!motorista) { alert("Selecione um motorista."); return; } if (locIds.length === 0) { alert("Selecione pelo menos uma locação."); return; } onSave({ motorista, data, locIds }); };

  // Carregar histórico de uma locação
  const loadHist = async (locId) => {
    setTimelineLoc(locId);
    setHistLoading(true);
    try {
      const data = await apiFetch("/api/historico/" + locId);
      setHistData(data || []);
    } catch(e) { setHistData([]); }
    setHistLoading(false);
  };

  return (
    <LargeModal title={isEdit ? "Editar Itinerário" : "Novo Itinerário"} subtitle={isEdit ? "Altere dados e locações do itinerário" : "Defina o motorista e selecione as locações"} onClose={onClose} onSave={save} saveLabel={isEdit ? "Salvar Alterações" : "Criar Itinerário"} maxWidth={850}>
      <div style={{ paddingTop:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:20 }}>
        <Field label="Motorista Responsável *">
          <select style={{ ...inp, appearance:"none", borderColor:!motorista?"#e74c3c":"#e0e0e0" }} value={motorista} onChange={e => setMotorista(e.target.value)}>
            <option value="">-- Selecione --</option>
            {motoristas.map(m => <option key={m.id||m.nome} value={m.nome}>{m.nome}</option>)}
          </select>
        </Field>
        <Field label="Data do Itinerário *">
          <input type="date" style={inp} value={data} onChange={e => setData(e.target.value)} />
        </Field>
      </div>

      {/* Seção: Locações selecionadas (Itens do Itinerário) — sempre visível como drop zone */}
      <SectionHeader icon="📋" label={`Itens do Itinerário (${locIds.length})`} />
      <div onDrop={handleDropZone} onDragOver={handleDropZoneDragOver} onDragLeave={handleDropZoneDragLeave}
        style={{ border: dropHighlight ? "2px dashed #1aae9e" : "1px solid #d1d5db", borderRadius:4, minHeight:60, maxHeight:200, overflowY:"auto", marginBottom:18, marginTop:8, background: dropHighlight ? "#e6faf8" : "#fff", transition:"all .2s" }}>
        {locIds.length === 0 && (
          <div style={{ padding:16, textAlign:"center", color: dropHighlight ? "#1aae9e" : "#bbb", fontSize:12 }}>
            {dropHighlight ? "Solte aqui para adicionar" : "Arraste locações da pesquisa abaixo ou clique no checkbox"}
          </div>
        )}
        {locIds.map((id, idx) => {
          const l = locacoes.find(x => String(x.id) === String(id)) || {};
          const end = typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.bairro,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "";
          const isDragging = dragIdx === idx;
          const isOver = dragOverIdx === idx && dragIdx !== idx;
          return (
            <div key={id} draggable onDragStart={() => handleDragStart(idx)} onDragOver={(e) => handleDragOver(e, idx)} onDrop={(e) => { e.stopPropagation(); handleDrop(idx); }} onDragEnd={handleDragEnd}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderBottom:"1px solid #f3f4f6", background: isDragging ? "#e0e7ff" : isOver ? "#dbeafe" : "#f8fafc", opacity: isDragging ? 0.5 : 1, borderTop: isOver ? "2px solid #1aae9e" : "2px solid transparent", transition:"background .15s, border-top .15s", cursor:"grab" }}>
              <span style={{ cursor:"grab", color:"#bbb", fontSize:16, lineHeight:1, userSelect:"none", padding:"0 2px" }} title="Arraste para reordenar">⠿</span>
              <span style={{ fontSize:11, fontWeight:800, color:"#aaa", minWidth:20 }}>{idx+1}</span>
              <span style={{ fontSize:10, fontWeight:800, color:"#fff", background:opColor[getOperacao(l)]||"#888", padding:"2px 8px", borderRadius:3, minWidth:56, textAlign:"center" }}>{getOperacao(l)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:12, color:"#1a1a1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{id} — {l.cliente||"—"}</div>
                <div style={{ fontSize:11, color:"#aaa", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{end}</div>
              </div>
              <button onClick={() => removeLoc(id)} style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:3, padding:"3px 8px", cursor:"pointer", color:"#dc2626", fontWeight:700, fontSize:11, fontFamily:FONT }} title="Remover do itinerário">✕</button>
            </div>
          );
        })}
      </div>

      <SectionHeader icon="🔍" label="Pesquisar Demandas" />
      {/* Busca + filtros */}
      <div style={{ display:"flex", gap:6, marginBottom:8, marginTop:10, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ position:"relative", flex:1, minWidth:180 }}>
          <input type="text" placeholder="Pesquisar por cliente, nº locação..." value={busca} onChange={e=>setBusca(e.target.value)} style={{ ...inp, fontSize:12, padding:"7px 10px 7px 30px", width:"100%" }} />
          <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", color:"#aaa", fontSize:13 }}>🔍</span>
        </div>
        {["TODAS","ENTREGA","RETIRADA","TROCA"].map(op => (
          <button key={op} onClick={() => setOpFilt(op)} style={{ padding:"5px 10px", fontSize:11, fontWeight:700, border:"1px solid", borderRadius:3, cursor:"pointer", borderColor:opFilt===op?"#1aae9e":"#e0e0e0", background:opFilt===op?"#f0f4ff":"#fff", color:opFilt===op?"#1aae9e":"#666", fontFamily:FONT }}>
            {op}
          </button>
        ))}
      </div>
      {/* Checkbox: apenas sem itinerário */}
      <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#555", marginBottom:10, cursor:"pointer", userSelect:"none" }}>
        <input type="checkbox" checked={apenasLivres} onChange={e => setApenasLivres(e.target.checked)} style={{ accentColor:"#1aae9e" }} />
        Mostrar apenas locações sem itinerário
      </label>

      <div className="modal-demandas" style={{ border:"1px solid #ebebeb", borderRadius:3, maxHeight:300, overflowY:"auto" }}>
        {filtered.length === 0 && <div style={{ padding:20, textAlign:"center", color:"#aaa", fontSize:12 }}>Nenhuma locação encontrada</div>}
        {filtered.map((l, i) => {
          const checked = locIds.includes(l.id);
          const emOutro = locIdsEmItinerarios.has(String(l.id)) && !locIdsDoEditAtual.has(l.id);
          const end = typeof l.endereco === "string" ? l.endereco : l.endereco ? [l.endereco.logradouro,l.endereco.numero,l.endereco.cidade,l.endereco.uf].filter(Boolean).join(", ") : "";
          return (
            <div key={i} draggable onDragStart={() => handleSearchDragStart(l.id)} onDragEnd={handleDragEnd}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", borderBottom:"1px solid #f5f5f5", background: checked ? "#f0f4ff" : emOutro ? "#fafafa" : "", borderLeft: checked ? "3px solid #1aae9e" : "3px solid transparent", opacity: emOutro && !checked ? 0.5 : 1, cursor: checked ? "default" : "grab" }}>
              <span style={{ cursor:"grab", color:"#ccc", fontSize:14, lineHeight:1, userSelect:"none", padding:"0 2px" }} title="Arraste para itens do itinerário">⠿</span>
              <input type="checkbox" checked={checked} onChange={() => toggle(l.id)} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
              <span style={{ fontSize:10, fontWeight:800, color:opColor[getOperacao(l)]||"#888", minWidth:56 }}>{getOperacao(l)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:12, color:"#1a1a1a" }}>{l.id} — {l.cliente}</div>
                <div style={{ fontSize:11, color:"#aaa", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{end}</div>
              </div>
              <span style={{ fontSize:11, color:"#888", whiteSpace:"nowrap" }}>{fmtDate(l.previsao||l.dataEntrega||l.data_entrega)}</span>
              <StatusBadge label={l.status} />
              {/* Menu de ações (≡) */}
              <div style={{ position:"relative" }}>
                <button onClick={(e) => { e.stopPropagation(); setMenuAberto(menuAberto === l.id ? null : l.id); }} style={{ background:"none", border:"1px solid #e0e0e0", borderRadius:3, padding:"3px 7px", cursor:"pointer", fontSize:14, color:"#888", lineHeight:1 }} title="Ações">☰</button>
                {menuAberto === l.id && (
                  <div onClick={e => e.stopPropagation()} style={{ position:"absolute", right:0, top:"100%", marginTop:2, background:"#fff", border:"1px solid #e0e0e0", borderRadius:4, boxShadow:"0 4px 12px rgba(0,0,0,.12)", zIndex:999, minWidth:160, overflow:"hidden" }}>
                    {!checked && <button onClick={() => addLoc(l.id)} style={{ width:"100%", textAlign:"left", padding:"8px 14px", border:"none", background:"#fff", cursor:"pointer", fontSize:12, fontFamily:FONT, color:"#1a1a1a", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid #f3f4f6" }} onMouseEnter={e=>e.target.style.background="#f8f9fa"} onMouseLeave={e=>e.target.style.background="#fff"}>
                      <span style={{ color:"#1aae9e" }}>+</span> Adicionar
                    </button>}
                    {checked && <button onClick={() => removeLoc(l.id)} style={{ width:"100%", textAlign:"left", padding:"8px 14px", border:"none", background:"#fff", cursor:"pointer", fontSize:12, fontFamily:FONT, color:"#dc2626", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid #f3f4f6" }} onMouseEnter={e=>e.target.style.background="#fef2f2"} onMouseLeave={e=>e.target.style.background="#fff"}>
                      <span>✕</span> Remover
                    </button>}
                    <button onClick={() => { setViewLoc(l); setMenuAberto(null); }} style={{ width:"100%", textAlign:"left", padding:"8px 14px", border:"none", background:"#fff", cursor:"pointer", fontSize:12, fontFamily:FONT, color:"#1a1a1a", display:"flex", alignItems:"center", gap:8, borderBottom:"1px solid #f3f4f6" }} onMouseEnter={e=>e.target.style.background="#f8f9fa"} onMouseLeave={e=>e.target.style.background="#fff"}>
                      <span style={{ color:"#1aae9e" }}>📋</span> Abrir Locação
                    </button>
                    <button onClick={() => { loadHist(l.id); setMenuAberto(null); }} style={{ width:"100%", textAlign:"left", padding:"8px 14px", border:"none", background:"#fff", cursor:"pointer", fontSize:12, fontFamily:FONT, color:"#1a1a1a", display:"flex", alignItems:"center", gap:8 }} onMouseEnter={e=>e.target.style.background="#f8f9fa"} onMouseLeave={e=>e.target.style.background="#fff"}>
                      🕐 Linha do Tempo
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mini modal: Ver Locação */}
      {viewLoc && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,.3)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setViewLoc(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:8, padding:20, maxWidth:500, width:"90%", boxShadow:"0 8px 30px rgba(0,0,0,.15)", maxHeight:"70vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <h3 style={{ margin:0, fontSize:15, fontFamily:FONT }}>Locação #{viewLoc.id}</h3>
              <button onClick={() => setViewLoc(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:18 }}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, fontSize:12 }}>
              <div><span style={{ color:"#888" }}>Cliente:</span> <b>{viewLoc.cliente}</b></div>
              <div><span style={{ color:"#888" }}>Operação:</span> <b style={{ color:opColor[viewLoc.operacao]||"#333" }}>{viewLoc.operacao}</b></div>
              <div><span style={{ color:"#888" }}>Status:</span> <StatusBadge label={viewLoc.status} /></div>
              <div><span style={{ color:"#888" }}>Caçamba:</span> <b>{viewLoc.cacamba || "—"}</b></div>
              <div style={{ gridColumn:"1/-1" }}><span style={{ color:"#888" }}>Endereço:</span> <b>{typeof viewLoc.endereco === "string" ? viewLoc.endereco : viewLoc.endereco ? [viewLoc.endereco.logradouro,viewLoc.endereco.numero,viewLoc.endereco.bairro,viewLoc.endereco.cidade,viewLoc.endereco.uf].filter(Boolean).join(", ") : "—"}</b></div>
              <div><span style={{ color:"#888" }}>Motorista:</span> <b>{viewLoc.motorista || "—"}</b></div>
              <div><span style={{ color:"#888" }}>Previsão:</span> <b>{fmtDate(viewLoc.previsao||viewLoc.dataEntrega||viewLoc.data_entrega)}</b></div>
              {viewLoc.mtr && <div><span style={{ color:"#888" }}>MTR:</span> <b>{viewLoc.mtr}</b></div>}
              {viewLoc.valor && <div><span style={{ color:"#888" }}>Valor:</span> <b>R$ {Number(viewLoc.valor).toFixed(2)}</b></div>}
              {viewLoc.observacoes && <div style={{ gridColumn:"1/-1" }}><span style={{ color:"#888" }}>Obs:</span> <b>{viewLoc.observacoes}</b></div>}
            </div>
          </div>
        </div>
      )}

      {/* Mini modal: Linha do Tempo */}
      {timelineLoc && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,.3)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setTimelineLoc(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:8, padding:20, maxWidth:500, width:"90%", boxShadow:"0 8px 30px rgba(0,0,0,.15)", maxHeight:"70vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <h3 style={{ margin:0, fontSize:15, fontFamily:FONT }}>🕐 Linha do Tempo — Locação #{timelineLoc}</h3>
              <button onClick={() => setTimelineLoc(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:18 }}>✕</button>
            </div>
            {histLoading ? <div style={{ textAlign:"center", padding:20, color:"#aaa" }}>Carregando...</div> :
              histData.length === 0 ? <div style={{ textAlign:"center", padding:20, color:"#aaa", fontSize:12 }}>Nenhum registro encontrado</div> :
              <div style={{ borderLeft:"2px solid #e5e7eb", paddingLeft:16, marginLeft:8 }}>
                {histData.map((h, i) => (
                  <div key={i} style={{ marginBottom:14, position:"relative" }}>
                    <div style={{ position:"absolute", left:-23, top:3, width:10, height:10, borderRadius:"50%", background: i===0 ? "#1aae9e" : "#d1d5db" }} />
                    <div style={{ fontSize:11, color:"#888" }}>{h.created_at ? new Date(h.created_at).toLocaleString("pt-BR") : ""}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:"#1a1a1a", marginTop:2 }}>{h.acao}</div>
                    {h.detalhes && <div style={{ fontSize:11, color:"#666", marginTop:2 }}>{h.detalhes}</div>}
                    {h.usuario && <div style={{ fontSize:10, color:"#aaa", marginTop:2 }}>Por: {h.usuario}</div>}
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      )}
    </LargeModal>
  );
}

// ─── Relatórios ───────────────────────────────────────────────────────────────
function Relatorios({ locacoes, clientes, apiFetch }) {
  const [periodo, setPeriodo] = useState("mes");
  const [customDe, setCustomDe] = useState("");
  const [customAte, setCustomAte] = useState("");
  const [chartSelected, setChartSelected] = useState(null);
  const [detailLoc, setDetailLoc] = useState(null);

  const hoje = hojeLocal();
  const hojeDate = new Date();

  // Reliable date getter
  const getDataEntrega = (l) => {
    const d = l.dataEntrega || l.data_entrega;
    return d ? String(d).split("T")[0] : null;
  };
  const getDataPagamento = (l) => {
    const d = l.dataPagamento || l.data_pagamento;
    return d ? String(d).split("T")[0] : null;
  };

  // Period range calculation
  const getRange = () => {
    const now = hojeDate;
    if (periodo === "todos") return { de: "2000-01-01", ate: "2099-12-31" };
    if (periodo === "mes") {
      const lastDay = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
      return { de: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`, ate: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}` };
    }
    if (periodo === "semana") {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate()); const day = d.getDay(); d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
      const dStr = d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
      const fim = new Date(d); fim.setDate(fim.getDate() + 6);
      const fimStr = fim.getFullYear()+"-"+String(fim.getMonth()+1).padStart(2,"0")+"-"+String(fim.getDate()).padStart(2,"0");
      return { de: dStr, ate: fimStr };
    }
    if (periodo === "hoje") return { de: hoje, ate: hoje };
    if (periodo === "custom" && customDe && customAte) return { de: customDe, ate: customAte };
    return { de: hoje, ate: hoje };
  };
  const range = getRange();
  const inRange = (ds) => ds && ds >= range.de && ds <= range.ate;

  const fmtDateBR = (ds) => {
    if (!ds) return "—";
    const s = String(ds).split("T")[0];
    if (s.length < 10) return "—";
    const [y,m,d] = s.split("-");
    return d+"/"+m+"/"+y;
  };

  const getPeriodLabel = () => {
    if (periodo === "todos") return "Todo o período";
    if (periodo === "mes") return fmtDateBR(range.de) + " a " + fmtDateBR(range.ate);
    if (periodo === "semana") return fmtDateBR(range.de) + " a " + fmtDateBR(range.ate);
    if (periodo === "hoje") return fmtDateBR(hoje);
    if (periodo === "custom") return fmtDateBR(customDe) + " a " + fmtDateBR(customAte);
    return "—";
  };

  // ─── Filter locações (excl. canceladas para financeiro) ─────────
  const allInPeriod = locacoes.filter(l => {
    const de = getDataEntrega(l);
    if (periodo === "todos") return true;
    return inRange(de);
  });
  const filteredLocs = allInPeriod.filter(l => l.status !== "Cancelada");
  const canceladasCount = allInPeriod.filter(l => l.status === "Cancelada").length;

  // ─── KPIs Financeiros ─────────────────────────────────────────
  // Faturamento: igual ao Dashboard — locações PAGAS com data_pagamento no período
  const locsFaturadas = locacoes.filter(l => l.pago && l.status !== "Cancelada" && (periodo === "todos" || inRange(getDataPagamento(l))));
  const totalFaturado = locsFaturadas.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
  // A Receber GERAL: todas as locações não pagas (não só do período)
  const locsAReceberGeral = locacoes.filter(l => !l.pago && l.status !== "Cancelada");
  const totalAReceberGeral = locsAReceberGeral.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
  // A Receber do período (por entrega)
  const locsAReceber = filteredLocs.filter(l => !l.pago);
  const totalAReceber = locsAReceber.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
  const ticketMedio = filteredLocs.length > 0 ? filteredLocs.reduce((s,l) => s + (parseFloat(l.valor)||0), 0) / filteredLocs.length : 0;
  const taxaPagamento = filteredLocs.length > 0 ? Math.round((locsFaturadas.length / filteredLocs.length) * 100) : 0;

  // ─── Contagens por status ─────────────────────────────────────
  const STATUS_INFO = [
    { key:"Para Colocar", color:"#1a9e8f" },
    { key:"Colocada",     color:"#2ecc71" },
    { key:"Para Retirar", color:"#9b59b6" },
    { key:"Para Trocar",  color:"#f39c12" },
    { key:"Para Cobrar",  color:"#f59e0b" },
    { key:"Encerrada",    color:"#888" },
  ];
  const byStatus = STATUS_INFO.map(s => ({ ...s, count: filteredLocs.filter(l => l.status === s.key).length }));
  const totalByStatus = filteredLocs.length;

  // ─── Por tipo de caçamba ──────────────────────────────────────
  const byTipo = {};
  filteredLocs.forEach(l => {
    const tipo = l.tipoCacamba || l.tipo_cacamba || "Sem tipo";
    if (!byTipo[tipo]) byTipo[tipo] = { count:0, total:0 };
    byTipo[tipo].count++;
    byTipo[tipo].total += (l.valor||0);
  });
  const tiposSorted = Object.entries(byTipo).sort((a,b) => b[1].total - a[1].total);

  // ─── Top clientes ─────────────────────────────────────────────
  const clienteMap = {};
  filteredLocs.forEach(l => {
    const cid = l.cliente_id || l.clienteId || l.cliente;
    const nome = l.cliente || "Desconhecido";
    if (!clienteMap[cid]) clienteMap[cid] = { nome, fat:0, qtd:0, pago:0 };
    clienteMap[cid].qtd++;
    clienteMap[cid].fat += (l.valor||0);
    if (l.pago) clienteMap[cid].pago += (l.valor||0);
  });
  const topClientes = Object.values(clienteMap).sort((a,b) => b.fat - a.fat).slice(0,8);

  // ─── Faturamento últimos 6 meses ──────────────────────────────
  const monthNames = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(hojeDate.getFullYear(), hojeDate.getMonth()-i, 1);
    const mn = d.getMonth(), an = d.getFullYear();
    const prefix = `${an}-${String(mn+1).padStart(2,"0")}`;
    const locs = locacoes.filter(l => {
      if (l.status === "Cancelada") return false;
      const de = getDataEntrega(l);
      return de && de.startsWith(prefix);
    });
    const fat = locs.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
    // Recebido: usa data_pagamento (igual Dashboard)
    const recebido = locacoes.filter(l => {
      if (l.status === "Cancelada" || !l.pago) return false;
      const dp = getDataPagamento(l);
      return dp && dp.startsWith(prefix);
    }).reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
    months.push({ label: monthNames[mn], fat, recebido, count: locs.length, prefix, mn, an });
  }
  const maxFatMonth = Math.max(...months.map(m => m.fat), 1);

  // ─── Pagamentos em aberto ─────────────────────────────────────
  const emAberto = filteredLocs.filter(l => !l.pago && l.valor > 0).sort((a,b) => (b.valor||0) - (a.valor||0));

  // ─── Forma de pagamento ───────────────────────────────────────
  const byPagamento = {};
  filteredLocs.forEach(l => {
    const pg = l.pagamento || "Não informado";
    if (!byPagamento[pg]) byPagamento[pg] = { count:0, total:0 };
    byPagamento[pg].count++;
    byPagamento[pg].total += (l.valor||0);
  });
  const pagSorted = Object.entries(byPagamento).sort((a,b) => b[1].total - a[1].total);

  const card = { background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, padding:18 };

  return (
    <div>
      {/* ═══ Filtro de Período ═══ */}
      <div style={{ ...card, marginBottom:16, padding:"12px 16px" }}>
        <div className="relatorio-period-btns" style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {[["todos","Todo Período"],["mes","Mês Atual"],["semana","Esta Semana"],["hoje","Hoje"],["custom","Personalizado"]].map(([k,l]) => (
            <button key={k} onClick={() => setPeriodo(k)} style={{ padding:"7px 16px", fontSize:12, fontWeight:700, border:"1px solid", borderRadius:4, cursor:"pointer", fontFamily:FONT, borderColor:periodo===k?"#1aae9e":"#e0e0e0", background:periodo===k?"#1aae9e":"#fff", color:periodo===k?"#fff":"#666", transition:"all .15s" }}>{l}</button>
          ))}
          {periodo === "custom" && <>
            <input type="date" value={customDe} onChange={e => setCustomDe(e.target.value)} style={{...inp, fontSize:11, padding:"5px 8px", width:140}} />
            <span style={{ fontSize:11, color:"#aaa" }}>até</span>
            <input type="date" value={customAte} onChange={e => setCustomAte(e.target.value)} style={{...inp, fontSize:11, padding:"5px 8px", width:140}} />
          </>}
          <div style={{ marginLeft:"auto", fontSize:12, color:"#888" }}>
            {getPeriodLabel()} — <b>{filteredLocs.length}</b> locações {canceladasCount > 0 && <span style={{ color:"#e74c3c" }}>({canceladasCount} canceladas)</span>}
          </div>
        </div>
      </div>

      {/* ═══ Cards Financeiros ═══ */}
      <div className="relatorio-summary-cards" style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:18 }}>
        {[
          { label:"Faturamento", value:fmt(totalFaturado), sub:`${locsFaturadas.length} locações pagas`, color:"#1aae9e", icon:"💰" },
          { label:"A Receber",   value:fmt(totalAReceberGeral), sub:`${locsAReceberGeral.length} pendentes (total)`, color:"#e67e22", icon:"⏳" },
          { label:"Total Período", value:fmt(totalFaturado + totalAReceber), sub:`${filteredLocs.length} locações`, color:"#3b82f6", icon:"📋" },
          { label:"Ticket Médio",value:fmt(ticketMedio),   sub:"por locação", color:"#9b59b6", icon:"📊" },
        ].map((c,i) => (
          <div key={i} style={{ background:"#fff", borderRadius:6, border:"1px solid #e8e8e8", padding:"16px 18px", borderLeft:`4px solid ${c.color}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ fontSize:10, color:"#888", fontWeight:700, textTransform:"uppercase", letterSpacing:.4, marginBottom:6 }}>{c.label}</div>
                <div style={{ fontSize:24, fontWeight:900, color:"#1a1a1a" }}>{c.value}</div>
                <div style={{ fontSize:11, color:"#aaa", marginTop:4 }}>{c.sub}</div>
              </div>
              {c.icon && <span style={{ fontSize:22, opacity:.25 }}>{c.icon}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* ═══ Gráfico Mensal + Status ═══ */}
      <div className="dash-chart-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:18 }}>
        {/* Gráfico faturamento mensal — estilo igual ao Dashboard */}
        <div style={{ ...card, padding:20 }} className="relatorio-chart-container">
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
            <span style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Faturamento</span>
          </div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:10, height:170 }} className="relatorio-chart-bars">
            {months.map((m,i) => {
              const isSel = chartSelected === i;
              const isCur = i === months.length - 1;
              const isActive = isSel || (chartSelected === null && isCur);
              const maxRec = Math.max(...months.map(x => x.recebido), 1);
              const barH = Math.max((m.recebido / maxRec) * 150, m.recebido > 0 ? 3 : 0);
              return (
                <div key={m.label} onClick={() => {
                  if (isSel) { setChartSelected(null); setPeriodo("mes"); }
                  else {
                    setChartSelected(i);
                    const lastDay = new Date(m.an, m.mn+1, 0).getDate();
                    setCustomDe(`${m.an}-${String(m.mn+1).padStart(2,"0")}-01`);
                    setCustomAte(`${m.an}-${String(m.mn+1).padStart(2,"0")}-${String(lastDay).padStart(2,"0")}`);
                    setPeriodo("custom");
                  }
                }} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4, cursor:"pointer", transition:"opacity .15s", opacity: chartSelected !== null && !isSel ? .4 : 1 }}>
                  <span style={{ fontSize:9, fontWeight:600, color:isActive?"#1a1a1a":"#bbb" }}>{m.recebido > 0 ? fmt(m.recebido).replace("R$","").trim() : ""}</span>
                  <div style={{ width:"100%", borderRadius:4, overflow:"hidden", position:"relative", height:barH }}>
                    <div style={{ position:"absolute", bottom:0, width:"100%", height:"100%", background:isActive?"#2ecc71":"#ccc", borderRadius:4 }} />
                  </div>
                  <span style={{ fontSize:10, fontWeight:isActive?700:400, color:isActive?"#1a1a1a":"#bbb" }}>{m.label}</span>
                  <span style={{ fontSize:9, color:"#bbb" }}>{m.count}x</span>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:16, marginTop:12, justifyContent:"center" }}>
            <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:"#999" }}><span style={{ width:8, height:8, borderRadius:2, background:"#2ecc71" }} />Recebido</span>
          </div>
          {chartSelected !== null ? (
            <div style={{ textAlign:"center", marginTop:8 }}>
              <span style={{ fontSize:11, color:"#999", cursor:"pointer", textDecoration:"underline" }} onClick={() => { setChartSelected(null); setPeriodo("mes"); }}>Limpar seleção</span>
            </div>
          ) : (
            <div style={{ textAlign:"center", marginTop:8, fontSize:10, color:"#bbb" }}>Clique em um mês para filtrar</div>
          )}
        </div>

        {/* Status */}
        <div style={card}>
          <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Distribuição por Status</div>
          {totalByStatus === 0 ? <EmptyState icon="📊" title="Sem locações" subtitle="Nenhuma locação no período" /> : (
            <div>
              {byStatus.filter(s => s.count > 0).map(s => {
                const pct = Math.round((s.count / totalByStatus) * 100);
                return (
                  <div key={s.key} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:s.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, color:"#555", flex:1, fontWeight:500 }}>{s.key}</span>
                    <div style={{ width:120, height:8, background:"#f0f0f0", borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${pct}%`, background:s.color, borderRadius:4, transition:"width .4s" }} />
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, color:"#333", width:28, textAlign:"right" }}>{s.count}</span>
                    <span style={{ fontSize:10, color:"#aaa", width:32 }}>{pct}%</span>
                  </div>
                );
              })}
              {canceladasCount > 0 && (
                <div style={{ display:"flex", alignItems:"center", gap:10, marginTop:4, paddingTop:8, borderTop:"1px dashed #eee" }}>
                  <div style={{ width:10, height:10, borderRadius:"50%", background:"#e74c3c", flexShrink:0 }} />
                  <span style={{ fontSize:12, color:"#e74c3c", flex:1, fontWeight:500 }}>Canceladas</span>
                  <span style={{ fontSize:12, fontWeight:700, color:"#e74c3c" }}>{canceladasCount}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ═══ Top Clientes + Por Tipo ═══ */}
      <div className="dash-chart-grid" style={{ display:"grid", gridTemplateColumns:"3fr 2fr", gap:14, marginBottom:18 }}>
        {/* Top clientes */}
        <div style={card}>
          <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:16 }}>Top Clientes por Faturamento</div>
          {topClientes.length === 0 ? <EmptyState icon="👥" title="Sem dados" subtitle="Nenhum cliente no período" /> : (
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }} className="relatorio-top-clientes-tbl">
              <thead>
                <tr>
                  {["#","Cliente","Qtd","Faturado","Recebido","% Total"].map(h => (
                    <th key={h} style={{ textAlign:"left", padding:"8px 8px", fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", borderBottom:"2px solid #f0f0f0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topClientes.map((c,i) => (
                  <tr key={i} style={{ borderBottom:"1px solid #f8f8f8" }}>
                    <td style={{ padding:"8px 8px" }}>
                      <span style={{ background:i<3?"#e6faf8":"#f5f5f5", color:i<3?"#1aae9e":"#aaa", fontWeight:800, fontSize:10, padding:"2px 7px", borderRadius:4 }}>#{i+1}</span>
                    </td>
                    <td style={{ padding:"8px 8px", fontWeight:600, color:"#1a1a1a" }}>{c.nome}</td>
                    <td style={{ padding:"8px 8px", color:"#666" }}>{c.qtd}</td>
                    <td style={{ padding:"8px 8px", fontWeight:700 }}>{fmt(c.fat)}</td>
                    <td style={{ padding:"8px 8px", color:"#2ecc71", fontWeight:600 }}>{fmt(c.pago)}</td>
                    <td style={{ padding:"8px 8px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:50, height:6, background:"#f0f0f0", borderRadius:3, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${topClientes[0]?.fat > 0 ? Math.round((c.fat/topClientes[0].fat)*100) : 0}%`, background:"#1aae9e", borderRadius:3 }} />
                        </div>
                        <span style={{ fontSize:10, color:"#666" }}>{(totalFaturado + totalAReceber) > 0 ? Math.round((c.fat/(totalFaturado + totalAReceber))*100) : 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Por tipo de caçamba + forma de pagamento */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={card}>
            <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:12 }}>Por Tipo de Caçamba</div>
            {tiposSorted.length === 0 ? <EmptyState icon="📊" title="Sem dados" subtitle="" /> : (
              tiposSorted.map(([tipo, data]) => (
                <div key={tipo} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, fontSize:12 }}>
                  <span style={{ fontWeight:600, color:"#333" }}>{tipo}</span>
                  <span style={{ color:"#666" }}>{data.count}x — <b>{fmt(data.total)}</b></span>
                </div>
              ))
            )}
          </div>
          <div style={card}>
            <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a", marginBottom:12 }}>Formas de Pagamento</div>
            {pagSorted.length === 0 ? <EmptyState icon="💳" title="Sem dados" subtitle="" /> : (
              pagSorted.map(([pg, data]) => (
                <div key={pg} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, fontSize:12 }}>
                  <span style={{ fontWeight:600, color:"#333", textTransform:"capitalize" }}>{pg}</span>
                  <span style={{ color:"#666" }}>{data.count}x — <b>{fmt(data.total)}</b></span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ═══ Pagamentos em Aberto ═══ */}
      {emAberto.length > 0 && (
        <div style={{ ...card, marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:14, color:"#1a1a1a" }}>Pagamentos em Aberto ({emAberto.length})</div>
            <div style={{ fontSize:13, fontWeight:700, color:"#e67e22" }}>Total: {fmt(totalAReceber)}</div>
          </div>
          <div style={{ maxHeight:400, overflowY:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }} className="relatorio-pagos-table">
            <thead>
              <tr>
                {["Nº","Cliente","Data Entrega","Valor","Status","Forma Pag."].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 8px", fontSize:10, fontWeight:700, color:"#999", textTransform:"uppercase", borderBottom:"2px solid #f0f0f0", position:"sticky", top:0, background:"#fff", zIndex:1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {emAberto.map(l => {
                const de = getDataEntrega(l);
                const isVencida = de && de < hoje;
                return (
                  <tr key={l.id} style={{ borderBottom:"1px solid #f8f8f8", background:isVencida ? "#fef2f2" : "" }}>
                    <td style={{ padding:"8px 8px", fontWeight:600, color:"#1aae9e", cursor:"pointer", textDecoration:"underline" }} onClick={() => setDetailLoc(l)}>{l.id}</td>
                    <td style={{ padding:"8px 8px", fontWeight:600 }}>{l.cliente || "—"}</td>
                    <td style={{ padding:"8px 8px", color:"#666" }}>{fmtDateBR(de)}</td>
                    <td style={{ padding:"8px 8px", fontWeight:700 }}>{fmt(l.valor)}</td>
                    <td style={{ padding:"8px 8px" }}>
                      <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:10, background:isVencida ? "#fee2e2" : "#fef3c7", color:isVencida ? "#dc2626" : "#92400e" }}>
                        {isVencida ? "Vencido" : "Em aberto"}
                      </span>
                    </td>
                    <td style={{ padding:"8px 8px", color:"#666", textTransform:"capitalize" }}>{l.pagamento || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Modal de detalhe da locação */}
      {detailLoc && <LocacaoDetailModal locacao={detailLoc} onClose={() => setDetailLoc(null)} apiFetch={apiFetch} onUpdate={updated => { setDetailLoc(updated); }} />}
    </div>
  );
}

// ─── Relatório de Locações (PDF Download) ────────────────────────────────────
function RelatorioLocacoes({ locacoes, clientes, empresaNome }) {
  const [clienteFilter, setClienteFilter] = useState("");
  const [clienteFilterId, setClienteFilterId] = useState(null);
  const [clienteSearch, setClienteSearch] = useState("");
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const clienteDropRef = useRef();
  const [dataIni, setDataIni] = useState(() => { const d = new Date(); return d.getFullYear()+"-"+(d.getMonth()+1).toString().padStart(2,"0")+"-01"; });
  const [dataFim, setDataFim] = useState(() => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); return last.getFullYear()+"-"+(last.getMonth()+1).toString().padStart(2,"0")+"-"+last.getDate().toString().padStart(2,"0"); });
  const [statusFilter, setStatusFilter] = useState({"Para Colocar":true,"Colocada":true,"Para Retirar":true,"Para Trocar":true,"Para Cobrar":true,"Encerrada":true});
  const [showCanceladas, setShowCanceladas] = useState(false);
  const [showResumo, setShowResumo] = useState(true);
  const [gerando, setGerando] = useState(false);

  const toggleStatus = (s) => setStatusFilter(p => ({ ...p, [s]: !p[s] }));

  useEffect(() => {
    const handler = (e) => { if (clienteDropRef.current && !clienteDropRef.current.contains(e.target)) setShowClienteDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clientesSorted = clientes.filter(c => c.status === "Ativo").sort((a,b) => (a.nome||"").localeCompare(b.nome||""));
  const clientesFiltered = clienteSearch ? clientesSorted.filter(c => {
    const s = clienteSearch.toLowerCase();
    if (String(c.nome||"").toLowerCase().includes(s) || String(c.tel||"").includes(clienteSearch) || String(c.cpfCnpj||c.cpf_cnpj||"").includes(clienteSearch)) return true;
    const locsC = locacoes.filter(l => l.cliente === c.nome || l.clienteId === c.id || l.cliente_id === c.id);
    return locsC.some(l => String(l.logradouro||l.end_logradouro||"").toLowerCase().includes(s) || String(l.bairro||l.end_bairro||"").toLowerCase().includes(s) || String(l.cidade||l.end_cidade||"").toLowerCase().includes(s) || String(l.endereco||"").toLowerCase().includes(s));
  }) : clientesSorted;

  // ─── GERAR PDF ───
  const gerarRelatorio = () => {
    setGerando(true);
    try {
      // Filtrar dados
      let filtered = locacoes.filter(l => {
        if (l.status === "Cancelada") return showCanceladas;
        if (!statusFilter[l.status]) return false;
        // Coletar TODAS as datas relevantes da locação
        const todasDatas = [
          l.dataSolicitacao, l.data_solicitacao,
          l.dataEntrega, l.data_entrega,
          l.dataColocacaoReal, l.data_colocacao_real,
          l.dataRetiradaReal, l.data_retirada_real,
          l.dataRetiraPrevista, l.data_retirada,
          l.dataPagamento, l.data_pagamento,
          l.dataTrocaPrevista, l.data_troca_prevista,
        ].filter(d => d && String(d).length >= 10).map(d => String(d).slice(0,10));
        // Se não tem nenhuma data, incluir para não esconder dados
        if (todasDatas.length === 0) return true;
        // Locação entra no relatório se QUALQUER data cai no período
        const algumaDentro = todasDatas.some(d => {
          if (dataIni && d < dataIni) return false;
          if (dataFim && d > dataFim) return false;
          return true;
        });
        // Locações ativas sempre entram (estão em andamento)
        const statusAtivo = ["Colocada","Para Retirar","Para Trocar","Para Cobrar"].includes(l.status);
        if (statusAtivo) return true;
        return algumaDentro;
      });
      if (clienteFilterId || clienteFilter) {
        const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, ' ');
        const nomeF = norm(clienteFilter);
        filtered = filtered.filter(l => {
          // 1) Prioridade absoluta: match por cliente_id (à prova de bala)
          if (clienteFilterId) {
            const idL = l.cliente_id ?? l.clienteId ?? null;
            if (idL != null) return Number(idL) === Number(clienteFilterId);
            // se a locação não tem cliente_id (legado), cai pro match de nome EXATO abaixo
          }
          // 2) Fallback: match EXATO de nome (sem .includes — evita "Camila" casar com "Camila Silva")
          return norm(l.cliente || l.cliente_nome) === nomeF;
        });
      }
      // Helper resiliente — extrai sempre YYYY-MM-DD de qualquer formato (ISO, Date object, com timezone)
      const extractDate = (v) => {
        if (!v) return "";
        const s = String(v);
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}-${m[3]}`;
        const d = new Date(s);
        if (!isNaN(d.getTime())) {
          return d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0") + "-" + String(d.getDate()).padStart(2,"0");
        }
        return "";
      };
      filtered.sort((a, b) => {
        const cA = (a.cliente || "").toLowerCase();
        const cB = (b.cliente || "").toLowerCase();
        if (cA !== cB) return cA < cB ? -1 : 1;
        // Ordem 1: DT PEDIDO (dataSolicitacao) — coluna mostrada como referência principal
        const sA = extractDate(a.dataSolicitacao || a.data_solicitacao);
        const sB = extractDate(b.dataSolicitacao || b.data_solicitacao);
        if (sA && sB && sA !== sB) return sA < sB ? -1 : 1;
        if (sA && !sB) return -1;
        if (!sA && sB) return 1;
        // Ordem 2: DT ENTREGA — desempate quando pedido é mesma data ou ausente
        const eA = extractDate(a.dataEntrega || a.data_entrega);
        const eB = extractDate(b.dataEntrega || b.data_entrega);
        if (eA && eB && eA !== eB) return eA < eB ? -1 : 1;
        if (eA && !eB) return -1;
        if (!eA && eB) return 1;
        // Ordem 3: ID (consistente)
        return String(a.id||"").localeCompare(String(b.id||""));
      });
      if (filtered.length === 0) { alert("Nenhuma locacao encontrada para os filtros selecionados."); setGerando(false); return; }

      // Agrupar por cliente
      const groups = {};
      filtered.forEach(l => {
        const key = l.cliente || "Sem cliente";
        if (!groups[key]) { const cli = clientes.find(c => c.nome === key); groups[key] = { nome: key, cpf: cli ? (cli.cpf_cnpj||cli.cpfCnpj||"") : "", locs: [] }; }
        groups[key].locs.push(l);
      });

      // Totais
      const totalPrevisto = filtered.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
      const totalRecebido = filtered.filter(l=>l.pago).reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
      const totalPendente = totalPrevisto - totalRecebido;
      const totalComissoes = filtered.reduce((s,l) => s + (parseFloat(l.comissao)||0), 0);
      const totalCanceladas = filtered.filter(l=>l.status==="Cancelada").reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
      // Situação atual: todas as contagens respeitam o filtro aplicado (cliente, período, status)
      const cPC = filtered.filter(l=>l.status==="Para Colocar").length;
      const cCo = filtered.filter(l=>l.status==="Colocada").length;
      const cPR = filtered.filter(l=>l.status==="Para Retirar").length;
      const cPT = filtered.filter(l=>l.status==="Para Trocar").length;
      const cPCo = filtered.filter(l=>l.status==="Para Cobrar").length;
      const cEnc = filtered.filter(l=>l.status==="Encerrada").length;
      const cCanc = filtered.filter(l=>l.status==="Cancelada").length;
      const excCanc = filtered.filter(l => l.status !== "Cancelada");

      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pw = 210, mg = 8, uw = pw - 2*mg;
      const fmtV = (v) => "R$ " + Number(v||0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
      const fmtD = (v) => { if (!v) return "-"; const s = String(v).includes("T") ? String(v).split("T")[0] : String(v); const p = s.split("-"); return p.length === 3 ? p[2]+"/"+p[1]+"/"+p[0] : s; };
      let y = 10;
      let pageNum = 1;

      // ─── Header ───
      const drawHeader = () => {
        doc.setFontSize(11); doc.setFont("helvetica","bold"); doc.setTextColor(30,30,30);
        doc.text((empresaNome||"CONTROLA CACAMBAS").toUpperCase(), pw/2, y, { align:"center" });
        y += 5; doc.setFontSize(9); doc.setTextColor(80,80,80);
        doc.text("RELATORIO DE LOCACOES", pw/2, y, { align:"center" });
        y += 4; doc.setDrawColor(26,158,143); doc.setLineWidth(0.5); doc.line(mg, y, mg+uw, y); y += 4;
        doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(60,60,60);
        doc.text("CLIENTE: " + (clienteFilter||"Todos"), mg, y);
        doc.text("PERIODO: " + fmtD(dataIni) + " a " + fmtD(dataFim), pw/2, y, { align:"center" });
        doc.text("CANCELADAS: " + (showCanceladas?"Sim":"Nao"), pw-mg, y, { align:"right" });
        y += 3; doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 4;
      };

      const checkNewPage = (need) => {
        if (y + need > 282) { doc.setFontSize(6); doc.setTextColor(150,150,150); doc.text("Pagina " + pageNum, pw/2, 292, { align:"center" }); doc.addPage(); pageNum++; y = 10; drawHeader(); }
      };

      drawHeader();

      // ─── Tabela por cliente ───
      const cols = [
        { h:"Nr. Locacao", w:24 }, { h:"Situacao", w:16 }, { h:"Pago", w:10 },
        { h:"Dt Pedido", w:17 }, { h:"Dt Entrega", w:17 }, { h:"Dt Retirada", w:17 },
        { h:"Cacamba", w:24 }, { h:"Endereco", w:38 }, { h:"Valor", w:18 }, { h:"Comissao", w:15 }
      ];
      const totalW = cols.reduce((s,c) => s + c.w, 0);
      const scale = uw / totalW;
      cols.forEach(c => c.w = c.w * scale);

      Object.values(groups).forEach(g => {
        checkNewPage(20);
        // Cliente header
        doc.setFillColor(240,250,248); doc.rect(mg, y-2, uw, 6, "F");
        doc.setDrawColor(26,158,143); doc.setLineWidth(0.4); doc.line(mg, y+4, mg+uw, y+4);
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text(g.nome + (g.cpf ? " ("+g.cpf+")" : ""), mg+2, y+2); y += 7;

        // Table header
        doc.setFillColor(245,247,250); doc.rect(mg, y-2, uw, 5, "F");
        doc.setFontSize(5.5); doc.setFont("helvetica","bold"); doc.setTextColor(120,120,120);
        let cx = mg;
        cols.forEach(c => { doc.text(c.h.toUpperCase(), cx+1, y+1); cx += c.w; });
        y += 5;

        // Rows
        doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(5.5);
        g.locs.forEach((l, idx) => {
          checkNewPage(5);
          if (idx%2===1) { doc.setFillColor(250,251,252); doc.rect(mg, y-2.5, uw, 4.5, "F"); }
          const tipoCac = l.tipoCacamba||l.tipo_cacamba||"";
          const volCac = l.volumeCacamba||l.volume_cacamba||"";
          const cacStr = tipoCac ? "("+tipoCac+(volCac?"/"+volCac:"")+")" : String(l.cacamba||"-");
          const endObj = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
          const endereco = String([l.logradouro||endObj.logradouro, (l.numero||endObj.numero)?("n "+(l.numero||endObj.numero)):"", l.bairro||endObj.bairro, l.cidade||endObj.cidade, l.uf||endObj.uf].filter(Boolean).join(", ") || (typeof l.endereco === "string" ? l.endereco : "") || "-");
          const row = [
            (l.pago?"[PAGO] ":"") + String(l.id||""),
            l.status||"-",
            l.pago?"SIM":"NAO",
            fmtD(l.dataSolicitacao||l.data_solicitacao),
            fmtD(l.dataEntrega||l.data_entrega),
            fmtD(l.dataRetiradaReal||l.data_retirada_real||l.dataRetiraPrevista||l.dataRetirada||l.data_retirada),
            String(cacStr).substring(0,28),
            String(endereco).substring(0,42),
            fmtV(l.valor),
            fmtV(l.comissao),
          ];
          cx = mg;
          row.forEach((v, ci) => {
            const txt = doc.splitTextToSize(String(v), cols[ci].w - 1);
            doc.text(txt[0]||"-", cx+1, y); cx += cols[ci].w;
          });
          y += 4;
        });

        // Subtotal
        checkNewPage(14);
        doc.setDrawColor(26,158,143); doc.setLineWidth(0.3); doc.line(mg, y, mg+uw, y); y += 3;
        doc.setFillColor(240,250,248); doc.rect(mg, y-2, uw, 5, "F");
        const gExc = g.locs.filter(l=>l.status!=="Cancelada");
        const subtotal = g.locs.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
        const subRec = g.locs.filter(l=>l.pago).reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
        const subCom = g.locs.reduce((s,l) => s + (parseFloat(l.comissao)||0), 0);
        doc.setFontSize(6); doc.setFont("helvetica","bold"); doc.setTextColor(30,30,30);
        doc.text("("+gExc.length+") Locacoes (exceto canceladas)", mg+2, y+1);
        doc.text("SUBTOTAL: " + fmtV(subtotal), mg+uw-2, y+1, { align:"right" });
        y += 6;
        doc.setFontSize(5.5); doc.setFont("helvetica","normal"); doc.setTextColor(80,80,80);
        const gCo = gExc.filter(l=>l.status==="Colocada").length;
        const gPR2 = gExc.filter(l=>l.status==="Para Retirar").length;
        const gPT2 = gExc.filter(l=>l.status==="Para Trocar").length;
        const gPC2 = gExc.filter(l=>l.status==="Para Colocar").length;
        const gPCo2 = gExc.filter(l=>l.status==="Para Cobrar").length;
        const gEnc2 = gExc.filter(l=>l.status==="Encerrada").length;
        doc.text("P/ Colocar: "+gPC2+"  |  No Cliente: "+(gCo+gPR2+gPT2)+" ("+gCo+" Colocadas | "+gPR2+" P/ Retirar | "+gPT2+" P/ Trocar)  |  P/ Cobrar: "+gPCo2+"  |  Encerradas: "+gEnc2, mg+2, y);
        y += 3;
        doc.text("Previsto: "+fmtV(subtotal)+"  |  Recebido: "+fmtV(subRec)+"  |  A Receber: "+fmtV(subtotal-subRec)+"  |  Comissoes: "+fmtV(subCom), mg+2, y);
        y += 6;
      });

      // ─── RESUMO GERAL (pagina 2) ───
      if (showResumo) {
        doc.addPage(); pageNum++; y = 10;
        drawHeader();

        doc.setFontSize(10); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("RESUMO GERAL", pw/2, y, { align:"center" }); y += 6;
        doc.setDrawColor(26,158,143); doc.setLineWidth(0.4); doc.line(mg, y, mg+uw, y); y += 6;

        // Financeiro
        const finItems = [
          { label:"FATURAMENTO TOTAL PREVISTO:", val: fmtV(totalPrevisto), color:[26,26,26] },
          { label:"TOTAL RECEBIDO:", val: fmtV(totalRecebido), color:[59,130,246] },
          { label:"TOTAL PENDENTE DE PAGAMENTO:", val: fmtV(totalPendente), color:[231,76,60] },
          { label:"TOTAL COMISSOES:", val: fmtV(totalComissoes), color:[100,100,100] },
          { label:"TOTAL EM LOCACOES CANCELADAS:", val: fmtV(totalCanceladas), color:[100,100,100] },
        ];
        finItems.forEach(item => {
          doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(80,80,80);
          doc.text(item.label, pw/2-2, y, { align:"right" });
          doc.setTextColor(...item.color); doc.setFontSize(8);
          doc.text(item.val, pw/2+2, y);
          y += 5;
        });
        y += 4;

        // Situacao atual
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 5;
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("SITUACAO ATUAL DAS LOCACOES", pw/2, y, { align:"center" }); y += 4;
        doc.setFontSize(6.5); doc.setTextColor(80,80,80); doc.setFont("helvetica","normal");
        doc.text(excCanc.length + " Locacoes (exceto canceladas)", pw/2, y, { align:"center" }); y += 5;

        const statusItems = [
          { label:"PARA COLOCAR:", val: cPC },
          { label:"NO CLIENTE:", val: cCo+cPR+cPT, sub: "("+cCo+" Colocadas | "+cPR+" P/ Retirar | "+cPT+" P/ Trocar)" },
          { label:"PARA COBRAR:", val: cPCo },
          { label:"ENCERRADAS:", val: cEnc },
          { label:"CANCELADAS:", val: cCanc },
        ];
        statusItems.forEach(item => {
          doc.setFontSize(7); doc.setFont("helvetica","bold"); doc.setTextColor(50,50,50);
          doc.text(item.label, pw/2-2, y, { align:"right" });
          doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26); doc.setFontSize(8);
          doc.text(String(item.val), pw/2+2, y);
          if (item.sub) { doc.setFontSize(5.5); doc.setFont("helvetica","normal"); doc.setTextColor(130,130,130); doc.text(item.sub, pw/2+10, y); }
          y += 4.5;
        });
        y += 4;

        // Grafico pizza de status (desenhado com jsPDF)
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 5;
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("DISTRIBUICAO POR STATUS", pw/2, y, { align:"center" }); y += 6;

        const pieData = [
          { label:"P/ Colocar", count:cPC, r:26, g:158, b:143 },
          { label:"Colocadas", count:cCo, r:46, g:204, b:113 },
          { label:"P/ Retirar", count:cPR, r:155, g:89, b:182 },
          { label:"P/ Trocar", count:cPT, r:243, g:156, b:18 },
          { label:"P/ Cobrar", count:cPCo, r:231, g:76, b:60 },
          { label:"Encerradas", count:cEnc, r:107, g:114, b:128 },
          { label:"Canceladas", count:cCanc, r:220, g:38, b:38 },
        ];
        const pieTotal = pieData.reduce((s,d) => s + d.count, 0) || 1;
        const pieCx = pw/2, pieCy = y + 30, pieR = 25;
        let startAngle = -Math.PI/2;
        const pieSlices = pieData.filter(d=>d.count>0);
        pieSlices.forEach(d => {
          const sweep = (d.count / pieTotal) * Math.PI * 2;
          const endAngle = startAngle + sweep;
          doc.setFillColor(d.r, d.g, d.b);
          const steps = Math.max(Math.ceil(sweep / 0.05), 2);
          const points = [[pieCx, pieCy]];
          for (let s = 0; s <= steps; s++) {
            const a = startAngle + (sweep * s / steps);
            points.push([pieCx + pieR * Math.cos(a), pieCy + pieR * Math.sin(a)]);
          }
          for (let s = 1; s < points.length - 1; s++) {
            doc.triangle(points[0][0], points[0][1], points[s][0], points[s][1], points[s+1][0], points[s+1][1], "F");
          }
          startAngle = endAngle;
        });
        // White center circle (donut)
        doc.setFillColor(255,255,255); doc.circle(pieCx, pieCy, pieR*0.5, "F");
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text(String(pieTotal), pieCx, pieCy+1, { align:"center" });
        // Legenda do grafico de status
        y = pieCy + pieR + 8;
        const legCols = 3;
        const legColW = uw / legCols;
        pieSlices.forEach((d, i) => {
          const col = i % legCols;
          const row2 = Math.floor(i / legCols);
          const lx = mg + col * legColW + 4;
          const ly = y + row2 * 5;
          doc.setFillColor(d.r, d.g, d.b); doc.circle(lx, ly, 1.2, "F");
          doc.setFontSize(5.5); doc.setFont("helvetica","normal"); doc.setTextColor(60,60,60);
          doc.text(d.count + " " + d.label, lx + 3, ly + 0.5);
        });
        y += Math.ceil(pieSlices.length / legCols) * 5 + 6;

        // Pizza financeiro
        checkNewPage(70);
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 5;
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("RECEBIDO vs PENDENTE", pw/2, y, { align:"center" }); y += 6;

        const finPie = [
          { label:"Recebido", count: totalRecebido, r:59, g:130, b:246 },
          { label:"Pendente", count: Math.max(totalPendente, 0), r:248, g:113, b:113 },
        ];
        const finPieTotal = totalPrevisto || 1;
        const fin2Cx = pw/2, fin2Cy = y + 25, fin2R = 22;
        let fStartAngle = -Math.PI/2;
        finPie.filter(d=>d.count>0).forEach(d => {
          const sweep = (d.count / finPieTotal) * Math.PI * 2;
          doc.setFillColor(d.r, d.g, d.b);
          const steps = Math.max(Math.ceil(sweep / 0.05), 2);
          const points = [[fin2Cx, fin2Cy]];
          for (let s = 0; s <= steps; s++) {
            const a = fStartAngle + (sweep * s / steps);
            points.push([fin2Cx + fin2R * Math.cos(a), fin2Cy + fin2R * Math.sin(a)]);
          }
          for (let s = 1; s < points.length - 1; s++) {
            doc.triangle(points[0][0], points[0][1], points[s][0], points[s][1], points[s+1][0], points[s+1][1], "F");
          }
          fStartAngle += sweep;
        });
        doc.setFillColor(255,255,255); doc.circle(fin2Cx, fin2Cy, fin2R*0.45, "F");
        // Valor total no centro
        doc.setFontSize(6); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text(fmtV(totalPrevisto), fin2Cx, fin2Cy+1, { align:"center" });

        // Legenda abaixo do grafico
        y = fin2Cy + fin2R + 8;
        const legItems = [
          { label:"Recebido: " + fmtV(totalRecebido), r:59, g:130, b:246 },
          { label:"Pendente: " + fmtV(totalPendente), r:248, g:113, b:113 },
        ];
        legItems.forEach((it, i) => {
          doc.setFillColor(it.r, it.g, it.b); doc.circle(pw/2 - 35 + i*65, y, 1.5, "F");
          doc.setFontSize(6); doc.setFont("helvetica","normal"); doc.setTextColor(60,60,60);
          doc.text(it.label, pw/2 - 31 + i*65, y+0.5);
        });
      }

      // Footer last page
      doc.setFontSize(6); doc.setTextColor(150,150,150);
      doc.text("Pagina " + pageNum, pw/2, 292, { align:"center" });
      doc.text("Gerado por Controla Cacambas", pw-mg, 292, { align:"right" });

      // Download
      doc.save("relatorio_locacoes_" + (dataIni||"").replace(/-/g,"") + "_" + (dataFim||"").replace(/-/g,"") + ".pdf");
    } catch(e) {
      console.error("Erro ao gerar PDF:", e);
      alert("Erro ao gerar relatorio: " + e.message);
    }
    setGerando(false);
  };

  const chk = { width:16, height:16, accentColor:"#1a9e8f", cursor:"pointer" };

  return (
    <div>
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, padding:20, marginBottom:16 }}>
        <div style={{ fontWeight:800, fontSize:15, color:"#1a1a1a", marginBottom:16 }}>Relatorio de Locacoes</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:16 }}>
          <div ref={clienteDropRef} style={{ position:"relative" }}>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Cliente</label>
            <input type="text" placeholder={clienteFilter || "TODOS - digite para buscar..."} value={clienteSearch}
              onChange={e => { setClienteSearch(e.target.value); setShowClienteDropdown(true); }}
              onFocus={() => setShowClienteDropdown(true)}
              style={{ ...inp, width:"100%", background: clienteFilter ? "#f0faf8" : "#fff" }}
            />
            {clienteFilter && <button onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); }} style={{ position:"absolute", right:6, top:25, background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#aaa", padding:4 }}>x</button>}
            {showClienteDropdown && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff", border:"1px solid #e0e0e0", borderRadius:4, boxShadow:"0 4px 16px rgba(0,0,0,.12)", zIndex:100, maxHeight:220, overflowY:"auto" }}>
                <div onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); setShowClienteDropdown(false); }}
                  style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#1a9e8f", borderBottom:"1px solid #f0f0f0" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f5f5f5"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>TODOS</div>
                {clientesFiltered.map(c => (
                  <div key={c.id} onClick={() => { setClienteFilter(c.nome); setClienteFilterId(c.id); setClienteSearch(""); setShowClienteDropdown(false); }}
                    style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, color:"#333", borderBottom:"1px solid #f8f8f8", background: clienteFilter === c.nome ? "#f0faf8" : "#fff" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f5f8ff"} onMouseLeave={e=>e.currentTarget.style.background=clienteFilter===c.nome?"#f0faf8":"#fff"}>
                    {c.nome}{(c.cpf_cnpj||c.cpfCnpj) && <span style={{ marginLeft:6, fontSize:10, color:"#aaa" }}>({c.cpf_cnpj||c.cpfCnpj})</span>}
                  </div>
                ))}
                {clientesFiltered.length === 0 && <div style={{ padding:"12px", fontSize:11, color:"#aaa", textAlign:"center" }}>Nenhum cliente encontrado</div>}
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Data Inicial</label>
            <input type="date" value={dataIni} onChange={e=>setDataIni(e.target.value)} style={{ ...inp, width:"100%" }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Data Final</label>
            <input type="date" value={dataFim} onChange={e=>setDataFim(e.target.value)} style={{ ...inp, width:"100%" }} />
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:6 }}>Situacao da Locacao</label>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            {["Para Colocar","Colocada","Para Retirar","Para Trocar","Para Cobrar","Encerrada"].map(s => (
              <label key={s} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, cursor:"pointer", color: statusFilter[s] ? "#333" : "#aaa" }}>
                <input type="checkbox" checked={statusFilter[s]||false} onChange={()=>toggleStatus(s)} style={chk} />
                <span style={{ fontWeight:600 }}>{s.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ display:"flex", gap:20, marginBottom:16 }}>
          <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, cursor:"pointer" }}>
            <input type="checkbox" checked={showCanceladas} onChange={()=>setShowCanceladas(p=>!p)} style={chk} />
            <span>Exibir Locacoes Canceladas?</span>
          </label>
          <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, cursor:"pointer" }}>
            <input type="checkbox" checked={showResumo} onChange={()=>setShowResumo(p=>!p)} style={chk} />
            <span>Exibir Resumo?</span>
          </label>
        </div>

        <div style={{ textAlign:"center", display:"flex", justifyContent:"center", gap:10 }}>
          <button onClick={gerarRelatorio} disabled={gerando} style={{ padding:"10px 32px", fontSize:13, fontWeight:700, border:"none", borderRadius:4, cursor:gerando?"wait":"pointer", fontFamily:FONT, color:"#fff", background: gerando ? "#aaa" : "#1a9e8f", transition:"all .15s" }}
            onMouseEnter={e=>{if(!gerando) e.currentTarget.style.opacity="0.85";}} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
            {gerando ? "Gerando..." : "Gerar Relatorio PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Relatorio Detalhado de Locações (Excel) ─────────────────────────────────
function RelatorioDetalhado({ locacoes, clientes, empresaNome }) {
  const inp = { padding:"8px 10px", border:"1px solid #d8dde3", borderRadius:4, fontSize:12, fontFamily:FONT, outline:"none" };
  const [clienteFilter, setClienteFilter] = useState("");
  const [clienteFilterId, setClienteFilterId] = useState(null);
  const [clienteSearch, setClienteSearch] = useState("");
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const clienteDropRef = useRef(null);
  const [dataIni, setDataIni] = useState(() => { const d = new Date(); return d.getFullYear()+"-"+(d.getMonth()+1).toString().padStart(2,"0")+"-01"; });
  const [dataFim, setDataFim] = useState(() => { const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); return last.getFullYear()+"-"+(last.getMonth()+1).toString().padStart(2,"0")+"-"+last.getDate().toString().padStart(2,"0"); });
  const [statusFilter, setStatusFilter] = useState({"Para Colocar":true,"Colocada":true,"Para Retirar":true,"Para Trocar":true,"Para Cobrar":true,"Encerrada":true});
  const [showCanceladas, setShowCanceladas] = useState(false);
  const [gerando, setGerando] = useState(false);

  // Colunas selecionáveis agrupadas
  const columnGroups = [
    { title:"Dados de Cliente", cols:[
      { key:"clienteNome", label:"Nome do Cliente", default:true },
      { key:"tipoCliente", label:"Tipo Cliente", default:true },
      { key:"cpfCnpj", label:"CPF/CNPJ Cliente", default:true },
    ]},
    { title:"Dados do Pedido", cols:[
      { key:"tipoCacamba", label:"Tipo de Caçamba", default:true },
      { key:"cacambaCodigo", label:"Caçamba", default:true },
      { key:"operacao", label:"Operação", default:false },
      { key:"statusLoc", label:"Situação", default:true },
    ]},
    { title:"Dados de Endereço", cols:[
      { key:"endLogradouro", label:"Logradouro", default:true },
      { key:"endNumero", label:"Número", default:true },
      { key:"endBairro", label:"Bairro", default:true },
      { key:"endCidade", label:"Cidade", default:true },
      { key:"endUf", label:"Estado", default:true },
      { key:"endCep", label:"CEP", default:false },
    ]},
    { title:"Dados de Contato", cols:[
      { key:"contatoNome", label:"Nome do Contato", default:true },
      { key:"contatoTel", label:"Telefone", default:true },
      { key:"contatoEmail", label:"Email Contato", default:false },
    ]},
    { title:"Dados da Entrega", cols:[
      { key:"dataSolicitacao", label:"Data Solicitação", default:true },
      { key:"dataEntrega", label:"Data Prev. Entrega", default:true },
      { key:"motorista", label:"Motorista Entrega", default:true },
    ]},
    { title:"Dados de Retirada", cols:[
      { key:"dataRetirada", label:"Data Prev. Retirada", default:true },
      { key:"dataRetiradaReal", label:"Data Real Retirada", default:true },
      { key:"motoristaRetirada", label:"Motorista Retirada", default:true },
    ]},
    { title:"Dados Financeiros", cols:[
      { key:"valor", label:"Valor Locação", default:true },
      { key:"pago", label:"Pago?", default:true },
      { key:"dataPagamento", label:"Data Pagamento", default:true },
      { key:"pagamento", label:"Forma Pagamento", default:false },
      { key:"obs", label:"Observações", default:false },
    ]},
  ];

  const allCols = columnGroups.flatMap(g => g.cols);
  const [selected, setSelected] = useState(() => {
    const m = {}; allCols.forEach(c => m[c.key] = c.default); return m;
  });

  const toggleCol = k => setSelected(p => ({ ...p, [k]: !p[k] }));
  const markAll = () => { const m = {}; allCols.forEach(c => m[c.key] = true); setSelected(m); };
  const unmarkAll = () => { const m = {}; allCols.forEach(c => m[c.key] = false); setSelected(m); };
  const toggleStatus = s => setStatusFilter(p => ({ ...p, [s]: !p[s] }));

  useEffect(() => {
    const handler = (e) => { if (clienteDropRef.current && !clienteDropRef.current.contains(e.target)) setShowClienteDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clientesSorted = clientes.filter(c => c.status === "Ativo").sort((a,b) => (a.nome||"").localeCompare(b.nome||""));
  const clientesFiltered = clienteSearch ? clientesSorted.filter(c => {
    const s = clienteSearch.toLowerCase();
    if (String(c.nome||"").toLowerCase().includes(s) || String(c.tel||"").includes(clienteSearch) || String(c.cpfCnpj||c.cpf_cnpj||"").includes(clienteSearch)) return true;
    const locsC = locacoes.filter(l => l.cliente === c.nome || l.clienteId === c.id || l.cliente_id === c.id);
    return locsC.some(l => String(l.logradouro||l.end_logradouro||"").toLowerCase().includes(s) || String(l.bairro||l.end_bairro||"").toLowerCase().includes(s) || String(l.cidade||l.end_cidade||"").toLowerCase().includes(s) || String(l.endereco||"").toLowerCase().includes(s));
  }) : clientesSorted;

  const fmtD = v => { if (!v) return ""; const s = String(v).includes("T") ? String(v).split("T")[0] : String(v); const p = s.split("-"); return p.length === 3 ? p[2]+"/"+p[1]+"/"+p[0] : s; };

  const gerarExcel = async () => {
    setGerando(true);
    try {
      // Filtrar dados
      let filtered = locacoes.filter(l => {
        if (l.status === "Cancelada") return showCanceladas;
        if (!statusFilter[l.status]) return false;
        const todasDatas = [
          l.dataSolicitacao, l.data_solicitacao,
          l.dataEntrega, l.data_entrega,
          l.dataColocacaoReal, l.data_colocacao_real,
          l.dataRetiradaReal, l.data_retirada_real,
          l.dataRetiraPrevista, l.data_retirada,
          l.dataPagamento, l.data_pagamento,
          l.dataTrocaPrevista, l.data_troca_prevista,
        ].filter(d => d && String(d).length >= 10).map(d => String(d).slice(0,10));
        if (todasDatas.length === 0) return true;
        const algumaDentro = todasDatas.some(d => {
          if (dataIni && d < dataIni) return false;
          if (dataFim && d > dataFim) return false;
          return true;
        });
        const statusAtivo = ["Colocada","Para Retirar","Para Trocar","Para Cobrar"].includes(l.status);
        if (statusAtivo) return true;
        return algumaDentro;
      });
      if (clienteFilterId || clienteFilter) {
        const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, ' ');
        const nomeF = norm(clienteFilter);
        filtered = filtered.filter(l => {
          if (clienteFilterId) {
            const idL = l.cliente_id ?? l.clienteId ?? null;
            if (idL != null) return Number(idL) === Number(clienteFilterId);
          }
          return norm(l.cliente || l.cliente_nome) === nomeF;
        });
      }
      filtered.sort((a,b) => {
        const cA = (a.cliente||a.cliente_nome||"").toLowerCase(), cB = (b.cliente||b.cliente_nome||"").toLowerCase();
        if (cA !== cB) return cA < cB ? -1 : 1;
        return (a.data_solicitacao||"") < (b.data_solicitacao||"") ? -1 : 1;
      });
      if (filtered.length === 0) { alert("Nenhuma locação encontrada para os filtros selecionados."); setGerando(false); return; }

      const activeCols = allCols.filter(c => selected[c.key]);
      if (activeCols.length === 0) { alert("Selecione pelo menos uma coluna."); setGerando(false); return; }

      // Buscar logo como ArrayBuffer
      let logoBuffer = null;
      try {
        const resp = await fetch("/logo-full.png");
        logoBuffer = await resp.arrayBuffer();
      } catch(e) { console.warn("Logo não encontrado"); }

      const getValue = (l, key) => {
        const cli = clientes.find(c => c.nome === (l.cliente||l.cliente_nome));
        const endObj = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
        switch(key) {
          case "clienteNome": return l.cliente || l.cliente_nome || "";
          case "tipoCliente": return cli ? (cli.tipo||"PF") : "";
          case "cpfCnpj": return cli ? (cli.cpf_cnpj||cli.cpfCnpj||"") : "";
          case "tipoCacamba": return l.tipoCacamba || l.tipo_cacamba || "";
          case "cacambaCodigo": return String(l.cacamba_codigo || l.cacamba || "");
          case "operacao": return l.operacao || "";
          case "statusLoc": return l.status || "";
          case "endLogradouro": return l.end_logradouro || endObj.logradouro || "";
          case "endNumero": return l.end_numero || endObj.numero || "";
          case "endBairro": return l.end_bairro || endObj.bairro || "";
          case "endCidade": return l.end_cidade || endObj.cidade || "";
          case "endUf": return l.end_uf || endObj.uf || "";
          case "endCep": return l.end_cep || endObj.cep || "";
          case "contatoNome": return l.contatoNome || l.contato_nome || "";
          case "contatoTel": return l.contatoTel || l.contato_tel || "";
          case "contatoEmail": return l.contatoEmail || l.contato_email || "";
          case "dataSolicitacao": return fmtD(l.dataSolicitacao || l.data_solicitacao);
          case "dataEntrega": return fmtD(l.dataEntrega || l.data_entrega);
          case "dataRetirada": return fmtD(l.dataRetiraPrevista || l.dataRetirada || l.data_retirada);
          case "dataRetiradaReal": return fmtD(l.dataRetiradaReal || l.data_retirada_real);
          case "motorista": return l.motorista || "";
          case "motoristaRetirada": return l.motorista_retirada || l.motoristaRetirada || "";
          case "valor": return parseFloat(l.valor) || 0;
          case "pago": return l.pago ? "SIM" : "NÃO";
          case "dataPagamento": return fmtD(l.dataPagamento || l.data_pagamento);
          case "pagamento": return l.pagamento || "";
          case "obs": return l.obs || "";
          default: return "";
        }
      };

      // ─── ExcelJS Workbook ───
      const ExcelJS = window.ExcelJS;
      if (!ExcelJS) { alert("Biblioteca ExcelJS não carregada. Recarregue a página."); setGerando(false); return; }

      const wb = new ExcelJS.Workbook();
      wb.creator = "Controla Caçambas";
      wb.created = new Date();
      const ws = wb.addWorksheet("Relatório Detalhado", {
        pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.5, header: 0.3, footer: 0.2 } },
        properties: { defaultRowHeight: 16 },
      });

      const numCols = activeCols.length + 1;
      const teal = "FF1A9E8F";
      const darkTeal = "FF148F82";
      const lightTeal = "FFF0FAF8";
      const white = "FFFFFFFF";
      const dark = "FF1A1A1A";
      const gray = "FF888888";
      const thinBorder = { style:"thin", color:{argb:"FFD0D0D0"} };
      const tealBorder = { style:"thin", color:{argb:teal} };

      // ─── LOGO ───
      let logoImgId = null;
      if (logoBuffer) {
        logoImgId = wb.addImage({ buffer: logoBuffer, extension: "png" });
        ws.addImage(logoImgId, { tl: { col: 0.1, row: 0.15 }, ext: { width: 50, height: 50 } });
      }

      // ─── ROW 1: Nome empresa ───
      ws.getRow(1).height = 40;
      ws.mergeCells(1, 1, 1, numCols);
      const r1 = ws.getCell(1, 1);
      r1.value = (empresaNome||"CONTROLA CAÇAMBAS").toUpperCase();
      r1.font = { name:"Arial", bold:true, size:16, color:{argb:white} };
      r1.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
      r1.alignment = { horizontal:"center", vertical:"middle" };

      // ─── ROW 2: Subtítulo ───
      ws.getRow(2).height = 24;
      ws.mergeCells(2, 1, 2, numCols);
      const r2 = ws.getCell(2, 1);
      r2.value = "RELATÓRIO DETALHADO DE LOCAÇÕES";
      r2.font = { name:"Arial", bold:true, size:12, color:{argb:white} };
      r2.fill = { type:"pattern", pattern:"solid", fgColor:{argb:darkTeal} };
      r2.alignment = { horizontal:"center", vertical:"middle" };

      // ─── ROW 3: Filtros ───
      ws.getRow(3).height = 20;
      ws.mergeCells(3, 1, 3, numCols);
      const r3 = ws.getCell(3, 1);
      r3.value = "Período: " + fmtD(dataIni) + " a " + fmtD(dataFim) + "   |   Cliente: " + (clienteFilter||"TODOS") + "   |   Canceladas: " + (showCanceladas?"Sim":"Não");
      r3.font = { name:"Arial", size:9, color:{argb:dark} };
      r3.fill = { type:"pattern", pattern:"solid", fgColor:{argb:lightTeal} };
      r3.alignment = { horizontal:"center", vertical:"middle" };

      // ─── ROW 4: Espaço ───
      ws.getRow(4).height = 6;

      // ─── ROW 5: Cabeçalho das colunas ───
      const headerLabels = ["Nº Locação", ...activeCols.map(c => c.label)];
      ws.getRow(5).height = 26;
      headerLabels.forEach((label, i) => {
        const cell = ws.getCell(5, i+1);
        cell.value = label;
        cell.font = { name:"Arial", bold:true, size:9, color:{argb:white} };
        cell.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
        cell.alignment = { horizontal:"center", vertical:"middle", wrapText:true };
        cell.border = { top:tealBorder, bottom:tealBorder, left:{style:"thin",color:{argb:"FF0D7A6E"}}, right:{style:"thin",color:{argb:"FF0D7A6E"}} };
      });

      // ─── Larguras de coluna ───
      const colW = [18]; // Nº Locação
      activeCols.forEach(c => {
        if (c.key === "clienteNome") colW.push(32);
        else if (c.key === "endLogradouro") colW.push(35);
        else if (c.key === "obs") colW.push(40);
        else if (c.key === "cpfCnpj") colW.push(22);
        else if (c.key === "contatoNome") colW.push(26);
        else if (c.key === "contatoTel" || c.key === "contatoEmail") colW.push(22);
        else if (c.key === "valor") colW.push(16);
        else if (c.key === "endUf") colW.push(8);
        else if (c.key === "endNumero" || c.key === "endCep") colW.push(12);
        else colW.push(17);
      });
      colW.forEach((w, i) => { ws.getColumn(i+1).width = w; });

      // ─── DADOS ───
      const statusColors = { "Para Colocar":"FF1A9E8F", "Colocada":"FF2ECC71", "Para Retirar":"FF9B59B6", "Para Trocar":"FFF39C12", "Para Cobrar":"FFF59E0B", "Encerrada":"FF888888", "Cancelada":"FFE74C3C" };

      filtered.forEach((l, idx) => {
        const rowNum = 6 + idx;
        const row = ws.getRow(rowNum);
        row.height = 18;
        const isAlt = idx % 2 === 1;
        const bgColor = isAlt ? "FFF5F9F8" : white;

        // Nº Locação
        const c0 = ws.getCell(rowNum, 1);
        c0.value = String(l.id||"");
        c0.font = { name:"Arial", size:9, bold:true, color:{argb:dark} };
        c0.fill = { type:"pattern", pattern:"solid", fgColor:{argb:bgColor} };
        c0.alignment = { horizontal:"center", vertical:"middle" };
        c0.border = { bottom:thinBorder };

        activeCols.forEach((col, ci) => {
          const cell = ws.getCell(rowNum, ci+2);
          const val = getValue(l, col.key);
          cell.value = val;
          cell.fill = { type:"pattern", pattern:"solid", fgColor:{argb:bgColor} };
          cell.alignment = { horizontal: col.key==="valor"?"right":"left", vertical:"middle", wrapText: col.key==="obs" };
          cell.border = { bottom:thinBorder };

          // Cores especiais
          if (col.key === "pago") {
            cell.font = { name:"Arial", size:9, bold:true, color:{argb: val==="SIM"?"FF27AE60":"FFE74C3C"} };
          } else if (col.key === "statusLoc") {
            cell.font = { name:"Arial", size:9, bold:true, color:{argb: statusColors[val]||dark} };
          } else if (col.key === "valor") {
            cell.numFmt = "R$ #,##0.00";
            cell.font = { name:"Arial", size:9, color:{argb:dark} };
          } else {
            cell.font = { name:"Arial", size:9, color:{argb:dark} };
          }
        });
      });

      // ─── Linha vazia ───
      const gapRow = 6 + filtered.length;

      // ─── RESUMO FINANCEIRO ───
      const totalValor = filtered.reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
      const totalRecebido = filtered.filter(l=>l.pago).reduce((s,l) => s + (parseFloat(l.valor)||0), 0);
      const totalPendente = totalValor - totalRecebido;

      const sumRow1 = gapRow + 1;
      ws.getRow(sumRow1).height = 28;
      ws.mergeCells(sumRow1, 1, sumRow1, numCols);
      const sc1 = ws.getCell(sumRow1, 1);
      sc1.value = "TOTAL: " + filtered.length + " locações";
      sc1.font = { name:"Arial", bold:true, size:12, color:{argb:white} };
      sc1.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
      sc1.alignment = { horizontal:"center", vertical:"middle" };

      const sumRow2 = gapRow + 2;
      ws.getRow(sumRow2).height = 22;
      ws.mergeCells(sumRow2, 1, sumRow2, numCols);
      const sc2 = ws.getCell(sumRow2, 1);
      sc2.value = "Valor Total: R$ " + totalValor.toLocaleString("pt-BR",{minimumFractionDigits:2}) + "   |   Recebido: R$ " + totalRecebido.toLocaleString("pt-BR",{minimumFractionDigits:2}) + "   |   Pendente: R$ " + totalPendente.toLocaleString("pt-BR",{minimumFractionDigits:2});
      sc2.font = { name:"Arial", bold:true, size:10, color:{argb:dark} };
      sc2.fill = { type:"pattern", pattern:"solid", fgColor:{argb:lightTeal} };
      sc2.alignment = { horizontal:"center", vertical:"middle" };

      // Status breakdown
      const sumRow3 = gapRow + 3;
      ws.getRow(sumRow3).height = 18;
      ws.mergeCells(sumRow3, 1, sumRow3, numCols);
      // Situação atual: ativas = TODAS (situação real), encerradas/canceladas = do período
      const cCo = locacoes.filter(l=>l.status==="Colocada").length;
      const cPR = locacoes.filter(l=>l.status==="Para Retirar").length;
      const cPT = locacoes.filter(l=>l.status==="Para Trocar").length;
      const cPC = locacoes.filter(l=>l.status==="Para Colocar").length;
      const cPCo = locacoes.filter(l=>l.status==="Para Cobrar").length;
      const cEnc = filtered.filter(l=>l.status==="Encerrada").length;
      const cCanc = filtered.filter(l=>l.status==="Cancelada").length;
      const sc3 = ws.getCell(sumRow3, 1);
      sc3.value = "P/ Colocar: "+cPC+"   |   No Cliente: "+(cCo+cPR+cPT)+" ("+cCo+" Coloc. | "+cPR+" P/ Ret. | "+cPT+" P/ Troc.)   |   P/ Cobrar: "+cPCo+"   |   Encerradas: "+cEnc+(cCanc?"   |   Canceladas: "+cCanc:"");
      sc3.font = { name:"Arial", size:9, color:{argb:gray} };
      sc3.fill = { type:"pattern", pattern:"solid", fgColor:{argb:white} };
      sc3.alignment = { horizontal:"center", vertical:"middle" };

      // Footer
      const footRow = gapRow + 5;
      ws.getRow(footRow).height = 14;
      ws.mergeCells(footRow, 1, footRow, numCols);
      const fc = ws.getCell(footRow, 1);
      fc.value = "Gerado por Controla Caçambas — " + new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
      fc.font = { name:"Arial", size:7, italic:true, color:{argb:"FFAAAAAA"} };
      fc.alignment = { horizontal:"center", vertical:"middle" };

      // ─── Print area ───
      ws.pageSetup.printArea = "A1:" + String.fromCharCode(64 + Math.min(numCols, 26)) + (footRow);

      // ─── Header/Footer de impressão ───
      ws.headerFooter = { oddHeader: "&C&B" + (empresaNome||"Controla Caçambas"), oddFooter: "&LPágina &P de &N&RGerado em &D" };

      // ─── Download ───
      const buffer = await wb.xlsx.writeBuffer();
      const blob2 = new Blob([buffer], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob2);
      const a = document.createElement("a");
      a.href = url;
      a.download = "relatorio_detalhado_" + (dataIni||"").replace(/-/g,"") + "_" + (dataFim||"").replace(/-/g,"") + ".xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      console.error("Erro ao gerar Excel:", e);
      alert("Erro ao gerar relatório: " + e.message);
    }
    setGerando(false);
  };

  const chk = { width:16, height:16, accentColor:"#1a9e8f", cursor:"pointer" };

  return (
    <div>
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, padding:20, marginBottom:16 }}>
        <div style={{ fontWeight:800, fontSize:15, color:"#1a1a1a", marginBottom:16 }}>Relatório Detalhado de Locações</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:16 }}>
          <div ref={clienteDropRef} style={{ position:"relative" }}>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Clientes</label>
            <input type="text" placeholder={clienteFilter || "TODOS - digite para buscar..."} value={clienteSearch}
              onChange={e => { setClienteSearch(e.target.value); setShowClienteDropdown(true); }}
              onFocus={() => setShowClienteDropdown(true)}
              style={{ ...inp, width:"100%", background: clienteFilter ? "#f0faf8" : "#fff" }}
            />
            {clienteFilter && <button onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); }} style={{ position:"absolute", right:6, top:25, background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#aaa", padding:4 }}>x</button>}
            {showClienteDropdown && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff", border:"1px solid #e0e0e0", borderRadius:4, boxShadow:"0 4px 16px rgba(0,0,0,.12)", zIndex:100, maxHeight:220, overflowY:"auto" }}>
                <div onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); setShowClienteDropdown(false); }}
                  style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#1a9e8f", borderBottom:"1px solid #f0f0f0" }}>TODOS</div>
                {clientesFiltered.map(c => (
                  <div key={c.id} onClick={() => { setClienteFilter(c.nome); setClienteFilterId(c.id); setClienteSearch(c.nome); setShowClienteDropdown(false); }}
                    style={{ padding:"7px 12px", cursor:"pointer", fontSize:12, borderBottom:"1px solid #f8f8f8" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0faf8"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    <span style={{ fontWeight:600 }}>{c.nome}</span>
                    {(c.cpf_cnpj||c.cpfCnpj) && <span style={{ marginLeft:8, fontSize:10, color:"#999" }}>{formatCpfCnpj(c.cpf_cnpj||c.cpfCnpj)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Data Inicial</label>
            <input type="date" value={dataIni} onChange={e=>setDataIni(e.target.value)} style={{ ...inp, width:"100%" }} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Data Final</label>
            <input type="date" value={dataFim} onChange={e=>setDataFim(e.target.value)} style={{ ...inp, width:"100%" }} />
          </div>
        </div>

        {/* Status checkboxes */}
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:6 }}>Situação da Locação</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:14 }}>
            {["Para Colocar","Colocada","Para Retirar","Para Trocar","Para Cobrar","Encerrada"].map(s => (
              <label key={s} style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, cursor:"pointer", fontWeight:600, color:"#444" }}>
                <input type="checkbox" checked={!!statusFilter[s]} onChange={()=>toggleStatus(s)} style={chk} />{s.toUpperCase()}
              </label>
            ))}
            <label style={{ display:"flex", alignItems:"center", gap:4, fontSize:12, cursor:"pointer", fontWeight:600, color:"#e74c3c" }}>
              <input type="checkbox" checked={showCanceladas} onChange={()=>setShowCanceladas(!showCanceladas)} style={chk} />CANCELADA
            </label>
          </div>
        </div>
      </div>

      {/* Column selector */}
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, padding:20, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#555" }}>Selecione abaixo as informações do relatório</div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={markAll} style={{ background:"none", border:"1px solid #1a9e8f", borderRadius:4, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#1a9e8f", cursor:"pointer", fontFamily:FONT }}>✓ Marcar Todos</button>
            <button onClick={unmarkAll} style={{ background:"none", border:"1px solid #ccc", borderRadius:4, padding:"5px 12px", fontSize:11, fontWeight:700, color:"#888", cursor:"pointer", fontFamily:FONT }}>☐ Desmarcar Todos</button>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:20 }}>
          {columnGroups.map(g => (
            <div key={g.title}>
              <div style={{ fontSize:12, fontWeight:800, color:"#1a9e8f", marginBottom:8, borderBottom:"2px solid #1a9e8f", paddingBottom:4 }}>{g.title}</div>
              {g.cols.map(c => (
                <label key={c.key} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, cursor:"pointer", color:"#444", marginBottom:5, fontWeight: selected[c.key] ? 600 : 400 }}>
                  <input type="checkbox" checked={!!selected[c.key]} onChange={()=>toggleCol(c.key)} style={chk} />{c.label}
                </label>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Generate button */}
      <div style={{ textAlign:"center", marginTop:10 }}>
        <button onClick={gerarExcel} disabled={gerando} style={{ padding:"12px 36px", fontSize:14, fontWeight:700, border:"none", borderRadius:4, cursor:gerando?"wait":"pointer", fontFamily:FONT, color:"#fff", background: gerando ? "#aaa" : "#d4880f", transition:"all .15s", boxShadow:"0 2px 8px rgba(0,0,0,.15)" }}
          onMouseEnter={e=>{if(!gerando) e.currentTarget.style.opacity="0.85";}} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
          {gerando ? "Gerando..." : "⬇ Gerar Relatório Excel"}
        </button>
      </div>
    </div>
  );
}

// ─── Relatório Mensal (Resumo Diário) ────────────────────────────────────────
function RelatorioMensal({ locacoes, clientes, empresaNome }) {
  const inp = { padding:"8px 10px", border:"1px solid #d8dde3", borderRadius:4, fontSize:12, fontFamily:FONT, outline:"none" };
  const hojeRM = new Date();
  const [mes, setMes] = useState(hojeRM.getMonth() + 1);
  const [ano, setAno] = useState(hojeRM.getFullYear());
  const [clienteFilter, setClienteFilter] = useState("");
  const [clienteFilterId, setClienteFilterId] = useState(null);
  const [clienteSearch, setClienteSearch] = useState("");
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const clienteDropRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState({"Para Colocar":true,"Colocada":true,"Para Retirar":true,"Para Trocar":true,"Para Cobrar":true,"Encerrada":true});
  const [showCanceladas, setShowCanceladas] = useState(false);
  const [gerando, setGerando] = useState(false);

  const toggleStatus = (s) => setStatusFilter(p => ({ ...p, [s]: !p[s] }));

  useEffect(() => {
    const handler = (e) => { if (clienteDropRef.current && !clienteDropRef.current.contains(e.target)) setShowClienteDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const clientesSorted = clientes.filter(c => c.status === "Ativo").sort((a,b) => (a.nome||"").localeCompare(b.nome||""));
  const clientesFiltered = clienteSearch ? clientesSorted.filter(c => {
    const s = clienteSearch.toLowerCase();
    return String(c.nome||"").toLowerCase().includes(s) || String(c.tel||"").includes(clienteSearch) || String(c.cpfCnpj||c.cpf_cnpj||"").includes(clienteSearch);
  }) : clientesSorted;

  const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const DIAS_SEM = ["Domingo","Segunda-feira","Terça-feira","Quarta-feira","Quinta-feira","Sexta-feira","Sábado"];

  // ─── Coleta movimentações agrupadas por dia ───
  const coletarMovimentacoes = () => {
    const mesStr = String(mes).padStart(2,"0");
    const anoStr = String(ano);
    const dias = {};
    const diasNoMes = new Date(ano, mes, 0).getDate();
    for (let d = 1; d <= diasNoMes; d++) dias[d] = { entregas:[], retiradas:[], trocas:[], pagamentos:[] };

    const passaFiltros = (l) => {
      if (clienteFilterId) {
        const lid = l.cliente_id || l.clienteId;
        if (String(lid) !== String(clienteFilterId)) return false;
      }
      if (l.status === "Cancelada") return showCanceladas;
      if (!statusFilter[l.status]) return false;
      return true;
    };

    const extrairDia = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr).slice(0,10);
      if (s.length < 10) return null;
      const [y, m, dia] = s.split("-");
      if (y !== anoStr || m !== mesStr) return null;
      const n = parseInt(dia, 10);
      if (isNaN(n) || n < 1 || n > 31) return null;
      return n;
    };

    locacoes.forEach(l => {
      if (!passaFiltros(l)) return;
      const op = String(l.operacao || "ENTREGA").toUpperCase();

      // Entrega ou Troca (pela data de colocação real)
      const diaColoc = extrairDia(l.dataColocacaoReal || l.data_colocacao_real);
      if (diaColoc) {
        if (op === "TROCA") dias[diaColoc].trocas.push(l);
        else dias[diaColoc].entregas.push(l);
      }

      // Retirada (ignora se a locação foi retirada por troca — dataTrocaPrevista preenchida)
      const diaRet = extrairDia(l.dataRetiradaReal || l.data_retirada_real);
      if (diaRet) {
        const foiTroca = l.dataTrocaPrevista || l.data_troca_prevista;
        if (!foiTroca) dias[diaRet].retiradas.push(l);
      }

      // Pagamento (apenas se realmente pago)
      if (l.pago) {
        const diaPag = extrairDia(l.dataPagamento || l.data_pagamento);
        if (diaPag) dias[diaPag].pagamentos.push(l);
      }
    });

    return { dias, diasNoMes };
  };

  const fmtV = (v) => "R$ " + Number(v||0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
  const getEndereco = (l) => {
    const endObj = (l.endereco && typeof l.endereco === "object") ? l.endereco : {};
    const parts = [
      l.logradouro || l.end_logradouro || endObj.logradouro,
      (l.numero || l.end_numero || endObj.numero) ? ("nº " + (l.numero || l.end_numero || endObj.numero)) : "",
      l.bairro || l.end_bairro || endObj.bairro,
      l.cidade || l.end_cidade || endObj.cidade,
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
    return (typeof l.endereco === "string" ? l.endereco : "") || "-";
  };
  const getCacamba = (l) => {
    const cod = l.cacamba || l.cacamba_codigo || "";
    const tipo = l.tipoCacamba || l.tipo_cacamba || "";
    if (cod && tipo) return cod + " (" + tipo + ")";
    return cod || tipo || "-";
  };
  const getClienteNome = (l) => l.cliente || l.cliente_nome || "—";

  // ─── PDF ───
  const gerarPDF = () => {
    setGerando(true);
    try {
      const { dias, diasNoMes } = coletarMovimentacoes();

      // Totais mensais
      let totEntregas=0, totRetiradas=0, totTrocas=0, totFaturado=0;
      const clientesAtendidos = new Set();
      const faturamentoPorCliente = {};
      for (let d=1; d<=diasNoMes; d++) {
        const dd = dias[d];
        totEntregas += dd.entregas.length;
        totRetiradas += dd.retiradas.length;
        totTrocas += dd.trocas.length;
        dd.pagamentos.forEach(l => {
          const v = parseFloat(l.valor)||0;
          totFaturado += v;
          const nome = getClienteNome(l);
          faturamentoPorCliente[nome] = (faturamentoPorCliente[nome]||0) + v;
        });
        [...dd.entregas, ...dd.retiradas, ...dd.trocas, ...dd.pagamentos].forEach(l => {
          const nome = getClienteNome(l); if (nome && nome !== "—") clientesAtendidos.add(nome);
        });
      }

      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pw = 210, mg = 10, uw = pw - 2*mg;
      let y = 10;
      let pageNum = 1;

      const drawHeader = () => {
        doc.setFontSize(11); doc.setFont("helvetica","bold"); doc.setTextColor(30,30,30);
        doc.text((empresaNome||"CONTROLA CACAMBAS").toUpperCase(), pw/2, y, { align:"center" });
        y += 5; doc.setFontSize(9); doc.setTextColor(80,80,80);
        doc.text("RELATORIO MENSAL - " + MESES[mes-1].toUpperCase() + "/" + ano, pw/2, y, { align:"center" });
        y += 4; doc.setDrawColor(26,158,143); doc.setLineWidth(0.5); doc.line(mg, y, mg+uw, y); y += 4;
        doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(60,60,60);
        doc.text("CLIENTE: " + (clienteFilter||"Todos"), mg, y);
        doc.text("CANCELADAS: " + (showCanceladas?"Sim":"Nao"), pw-mg, y, { align:"right" });
        y += 3; doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 4;
      };

      const checkNewPage = (need) => {
        if (y + need > 282) { doc.setFontSize(6); doc.setTextColor(150,150,150); doc.text("Pagina " + pageNum, pw/2, 292, { align:"center" }); doc.addPage(); pageNum++; y = 10; drawHeader(); }
      };

      drawHeader();

      // Iterar dias
      for (let d = 1; d <= diasNoMes; d++) {
        const dd = dias[d];
        const totalDia = dd.entregas.length + dd.retiradas.length + dd.trocas.length + dd.pagamentos.length;
        if (totalDia === 0) continue;

        const dateObj = new Date(ano, mes-1, d);
        const diaSem = DIAS_SEM[dateObj.getDay()];
        const dataStr = String(d).padStart(2,"0") + "/" + String(mes).padStart(2,"0") + "/" + ano;

        checkNewPage(14);
        // Cabeçalho do dia
        doc.setFillColor(240,250,248); doc.rect(mg, y-3, uw, 6, "F");
        doc.setDrawColor(26,158,143); doc.setLineWidth(0.4); doc.line(mg, y+3, mg+uw, y+3);
        doc.setFontSize(9); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("DIA " + dataStr + "  (" + diaSem + ")", mg+2, y+1);
        const resumoDia = dd.entregas.length + " entregas | " + dd.retiradas.length + " retiradas | " + dd.trocas.length + " trocas | " + dd.pagamentos.length + " pagamentos";
        doc.setFontSize(6.5); doc.setFont("helvetica","normal"); doc.setTextColor(100,100,100);
        doc.text(resumoDia, mg+uw-2, y+1, { align:"right" });
        y += 7;

        // Seção ENTREGAS
        if (dd.entregas.length > 0) {
          checkNewPage(6);
          doc.setFontSize(7.5); doc.setFont("helvetica","bold"); doc.setTextColor(26,158,143);
          doc.text("ENTREGAS (" + dd.entregas.length + "):", mg+2, y); y += 3.5;
          doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(7);
          dd.entregas.forEach(l => {
            checkNewPage(4);
            const linha = getClienteNome(l) + " - " + getEndereco(l) + " - " + getCacamba(l) + " - " + fmtV(l.valor);
            const split = doc.splitTextToSize("• " + linha, uw - 6);
            split.forEach(t => { checkNewPage(4); doc.text(t, mg+4, y); y += 3.5; });
          });
          y += 1;
        }

        // Seção RETIRADAS
        if (dd.retiradas.length > 0) {
          checkNewPage(6);
          doc.setFontSize(7.5); doc.setFont("helvetica","bold"); doc.setTextColor(155,89,182);
          doc.text("RETIRADAS (" + dd.retiradas.length + "):", mg+2, y); y += 3.5;
          doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(7);
          dd.retiradas.forEach(l => {
            checkNewPage(4);
            const linha = getClienteNome(l) + " - " + getEndereco(l) + " - " + getCacamba(l);
            const split = doc.splitTextToSize("• " + linha, uw - 6);
            split.forEach(t => { checkNewPage(4); doc.text(t, mg+4, y); y += 3.5; });
          });
          y += 1;
        }

        // Seção TROCAS
        if (dd.trocas.length > 0) {
          checkNewPage(6);
          doc.setFontSize(7.5); doc.setFont("helvetica","bold"); doc.setTextColor(243,156,18);
          doc.text("TROCAS (" + dd.trocas.length + "):", mg+2, y); y += 3.5;
          doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(7);
          dd.trocas.forEach(l => {
            checkNewPage(4);
            const linha = getClienteNome(l) + " - " + getEndereco(l) + " - " + getCacamba(l);
            const split = doc.splitTextToSize("• " + linha, uw - 6);
            split.forEach(t => { checkNewPage(4); doc.text(t, mg+4, y); y += 3.5; });
          });
          y += 1;
        }

        // Seção PAGAMENTOS
        if (dd.pagamentos.length > 0) {
          checkNewPage(6);
          doc.setFontSize(7.5); doc.setFont("helvetica","bold"); doc.setTextColor(59,130,246);
          doc.text("PAGAMENTOS (" + dd.pagamentos.length + "):", mg+2, y); y += 3.5;
          doc.setFont("helvetica","normal"); doc.setTextColor(50,50,50); doc.setFontSize(7);
          let subPag = 0;
          dd.pagamentos.forEach(l => {
            checkNewPage(4);
            const forma = l.pagamento || "-";
            const linha = getClienteNome(l) + " - " + fmtV(l.valor) + " (" + forma + ")";
            const split = doc.splitTextToSize("• " + linha, uw - 6);
            split.forEach(t => { checkNewPage(4); doc.text(t, mg+4, y); y += 3.5; });
            subPag += parseFloat(l.valor)||0;
          });
          checkNewPage(4);
          doc.setFont("helvetica","bold"); doc.setTextColor(59,130,246); doc.setFontSize(7);
          doc.text("Total recebido no dia: " + fmtV(subPag), mg+uw-2, y, { align:"right" });
          y += 4;
        }
        y += 2;
      }

      // Resumo Mensal
      checkNewPage(60);
      doc.setDrawColor(26,158,143); doc.setLineWidth(0.5); doc.line(mg, y, mg+uw, y); y += 5;
      doc.setFontSize(11); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
      doc.text("RESUMO DO MES", pw/2, y, { align:"center" }); y += 6;

      const linhasResumo = [
        { l:"TOTAL DE ENTREGAS:", v: String(totEntregas) },
        { l:"TOTAL DE RETIRADAS:", v: String(totRetiradas) },
        { l:"TOTAL DE TROCAS:", v: String(totTrocas) },
        { l:"CLIENTES ATENDIDOS:", v: String(clientesAtendidos.size) },
        { l:"FATURAMENTO RECEBIDO:", v: fmtV(totFaturado) },
      ];
      linhasResumo.forEach(it => {
        checkNewPage(5);
        doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(80,80,80);
        doc.text(it.l, pw/2-2, y, { align:"right" });
        doc.setTextColor(26,26,26); doc.setFontSize(9);
        doc.text(it.v, pw/2+2, y);
        y += 5;
      });

      // Top 5 clientes
      const topClientes = Object.entries(faturamentoPorCliente).sort((a,b) => b[1]-a[1]).slice(0,5);
      if (topClientes.length > 0) {
        checkNewPage(20 + topClientes.length*5);
        y += 4;
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(mg, y, mg+uw, y); y += 5;
        doc.setFontSize(9); doc.setFont("helvetica","bold"); doc.setTextColor(26,26,26);
        doc.text("TOP 5 CLIENTES POR FATURAMENTO", pw/2, y, { align:"center" }); y += 5;
        topClientes.forEach((tc, i) => {
          doc.setFontSize(8); doc.setFont("helvetica","bold"); doc.setTextColor(50,50,50);
          doc.text((i+1) + ". " + tc[0], mg+2, y);
          doc.setTextColor(26,158,143);
          doc.text(fmtV(tc[1]), mg+uw-2, y, { align:"right" });
          y += 5;
        });
      }

      doc.setFontSize(6); doc.setTextColor(150,150,150);
      doc.text("Pagina " + pageNum, pw/2, 292, { align:"center" });
      doc.text("Gerado por Controla Cacambas", pw-mg, 292, { align:"right" });

      doc.save("relatorio_mensal_" + String(mes).padStart(2,"0") + "_" + ano + ".pdf");
    } catch(e) {
      console.error("Erro ao gerar PDF:", e);
      alert("Erro ao gerar relatorio: " + e.message);
    }
    setGerando(false);
  };

  // ─── Excel ───
  const gerarExcel = async () => {
    setGerando(true);
    try {
      const ExcelJS = window.ExcelJS;
      if (!ExcelJS) { alert("Biblioteca ExcelJS não carregada. Recarregue a página."); setGerando(false); return; }
      const { dias, diasNoMes } = coletarMovimentacoes();

      const wb = new ExcelJS.Workbook();
      wb.creator = "Controla Caçambas";
      wb.created = new Date();
      const ws = wb.addWorksheet("Relatório Mensal", {
        pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.6, bottom: 0.5, header: 0.3, footer: 0.2 } },
        properties: { defaultRowHeight: 16 },
      });

      const teal = "FF1A9E8F", darkTeal = "FF148F82", lightTeal = "FFF0FAF8", white = "FFFFFFFF", dark = "FF1A1A1A";
      const thinBorder = { style:"thin", color:{argb:"FFD0D0D0"} };
      const numCols = 8;

      // Header
      ws.getRow(1).height = 34;
      ws.mergeCells(1, 1, 1, numCols);
      const r1 = ws.getCell(1, 1);
      r1.value = (empresaNome||"CONTROLA CAÇAMBAS").toUpperCase();
      r1.font = { name:"Arial", bold:true, size:15, color:{argb:white} };
      r1.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
      r1.alignment = { horizontal:"center", vertical:"middle" };

      ws.getRow(2).height = 22;
      ws.mergeCells(2, 1, 2, numCols);
      const r2 = ws.getCell(2, 1);
      r2.value = "RELATÓRIO MENSAL — " + MESES[mes-1].toUpperCase() + "/" + ano;
      r2.font = { name:"Arial", bold:true, size:11, color:{argb:white} };
      r2.fill = { type:"pattern", pattern:"solid", fgColor:{argb:darkTeal} };
      r2.alignment = { horizontal:"center", vertical:"middle" };

      ws.getRow(3).height = 18;
      ws.mergeCells(3, 1, 3, numCols);
      const r3 = ws.getCell(3, 1);
      r3.value = "Cliente: " + (clienteFilter||"TODOS") + "   |   Canceladas: " + (showCanceladas?"Sim":"Não");
      r3.font = { name:"Arial", size:9, color:{argb:dark} };
      r3.fill = { type:"pattern", pattern:"solid", fgColor:{argb:lightTeal} };
      r3.alignment = { horizontal:"center", vertical:"middle" };

      ws.getRow(4).height = 6;

      // Cabeçalhos de coluna
      const headers = ["Data","Dia da Semana","Tipo","Cliente","Endereço","Caçamba","Valor","Pagamento"];
      ws.getRow(5).height = 24;
      headers.forEach((h, i) => {
        const cell = ws.getCell(5, i+1);
        cell.value = h;
        cell.font = { name:"Arial", bold:true, size:9, color:{argb:white} };
        cell.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
        cell.alignment = { horizontal:"center", vertical:"middle", wrapText:true };
      });
      const colWs = [12, 18, 14, 30, 40, 20, 14, 14];
      colWs.forEach((w, i) => { ws.getColumn(i+1).width = w; });

      // Preencher linhas
      const tipoColors = { "ENTREGA":"FF1A9E8F", "RETIRADA":"FF9B59B6", "TROCA":"FFF39C12", "PAGAMENTO":"FF3B82F6" };
      let rowNum = 6;
      for (let d=1; d<=diasNoMes; d++) {
        const dd = dias[d];
        const totalDia = dd.entregas.length + dd.retiradas.length + dd.trocas.length + dd.pagamentos.length;
        if (totalDia === 0) continue;
        const dateObj = new Date(ano, mes-1, d);
        const dataStr = String(d).padStart(2,"0") + "/" + String(mes).padStart(2,"0") + "/" + ano;
        const diaSem = DIAS_SEM[dateObj.getDay()];

        const addLinha = (tipo, l, valor, pagamento) => {
          const row = ws.getRow(rowNum);
          row.height = 16;
          row.getCell(1).value = dataStr;
          row.getCell(2).value = diaSem;
          row.getCell(3).value = tipo;
          row.getCell(3).font = { name:"Arial", size:9, bold:true, color:{argb:tipoColors[tipo]||dark} };
          row.getCell(4).value = getClienteNome(l);
          row.getCell(5).value = getEndereco(l);
          row.getCell(6).value = getCacamba(l);
          row.getCell(7).value = valor != null ? parseFloat(valor)||0 : "";
          row.getCell(7).numFmt = "R$ #,##0.00";
          row.getCell(8).value = pagamento || "";
          for (let c=1; c<=numCols; c++) {
            const cell = row.getCell(c);
            if (!cell.font) cell.font = { name:"Arial", size:9, color:{argb:dark} };
            cell.alignment = { vertical:"middle", wrapText: c===5 };
            cell.border = { bottom: thinBorder };
          }
          rowNum++;
        };

        dd.entregas.forEach(l => addLinha("ENTREGA", l, l.valor, ""));
        dd.retiradas.forEach(l => addLinha("RETIRADA", l, null, ""));
        dd.trocas.forEach(l => addLinha("TROCA", l, null, ""));
        dd.pagamentos.forEach(l => addLinha("PAGAMENTO", l, l.valor, l.pagamento||""));
      }

      if (rowNum === 6) {
        const cell = ws.getCell(6, 1);
        ws.mergeCells(6, 1, 6, numCols);
        cell.value = "Nenhuma movimentação no período selecionado.";
        cell.font = { name:"Arial", size:10, italic:true, color:{argb:"FF888888"} };
        cell.alignment = { horizontal:"center", vertical:"middle" };
      }

      ws.views = [{ state: "frozen", ySplit: 5 }];

      const buffer = await wb.xlsx.writeBuffer();
      const blob2 = new Blob([buffer], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob2);
      const a = document.createElement("a");
      a.href = url;
      a.download = "relatorio_mensal_" + String(mes).padStart(2,"0") + "_" + ano + ".xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) {
      console.error("Erro ao gerar Excel:", e);
      alert("Erro ao gerar relatório: " + e.message);
    }
    setGerando(false);
  };

  const chk = { width:16, height:16, accentColor:"#1a9e8f", cursor:"pointer" };
  const anoAtual = hojeRM.getFullYear();
  const anosOpts = [];
  for (let a = anoAtual - 3; a <= anoAtual + 1; a++) anosOpts.push(a);

  return (
    <div>
      <div style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:6, padding:20, marginBottom:16 }}>
        <div style={{ fontWeight:800, fontSize:15, color:"#1a1a1a", marginBottom:6 }}>Relatório Mensal</div>
        <div style={{ fontSize:12, color:"#888", marginBottom:16 }}>Resumo diário das operações do mês (entregas, retiradas, trocas e pagamentos)</div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:16 }}>
          <div ref={clienteDropRef} style={{ position:"relative" }}>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Cliente</label>
            <input type="text" placeholder={clienteFilter || "TODOS - digite para buscar..."} value={clienteSearch}
              onChange={e => { setClienteSearch(e.target.value); setShowClienteDropdown(true); }}
              onFocus={() => setShowClienteDropdown(true)}
              style={{ ...inp, width:"100%", background: clienteFilter ? "#f0faf8" : "#fff" }}
            />
            {clienteFilter && <button onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); }} style={{ position:"absolute", right:6, top:25, background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#aaa", padding:4 }}>x</button>}
            {showClienteDropdown && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff", border:"1px solid #e0e0e0", borderRadius:4, boxShadow:"0 4px 16px rgba(0,0,0,.12)", zIndex:100, maxHeight:220, overflowY:"auto" }}>
                <div onClick={() => { setClienteFilter(""); setClienteFilterId(null); setClienteSearch(""); setShowClienteDropdown(false); }}
                  style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#1a9e8f", borderBottom:"1px solid #f0f0f0" }}>TODOS</div>
                {clientesFiltered.map(c => (
                  <div key={c.id} onClick={() => { setClienteFilter(c.nome); setClienteFilterId(c.id); setClienteSearch(""); setShowClienteDropdown(false); }}
                    style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, color:"#333", borderBottom:"1px solid #f8f8f8" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f5f8ff"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                    {c.nome}{(c.cpf_cnpj||c.cpfCnpj) && <span style={{ marginLeft:6, fontSize:10, color:"#aaa" }}>({c.cpf_cnpj||c.cpfCnpj})</span>}
                  </div>
                ))}
                {clientesFiltered.length === 0 && <div style={{ padding:"12px", fontSize:11, color:"#aaa", textAlign:"center" }}>Nenhum cliente encontrado</div>}
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Mês</label>
            <select value={mes} onChange={e=>setMes(parseInt(e.target.value,10))} style={{ ...inp, width:"100%" }}>
              {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:4 }}>Ano</label>
            <select value={ano} onChange={e=>setAno(parseInt(e.target.value,10))} style={{ ...inp, width:"100%" }}>
              {anosOpts.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:11, fontWeight:700, color:"#555", display:"block", marginBottom:6 }}>Status a incluir</label>
          <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
            {Object.keys(statusFilter).map(s => (
              <label key={s} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#333", cursor:"pointer" }}>
                <input type="checkbox" style={chk} checked={statusFilter[s]} onChange={() => toggleStatus(s)} />
                {s}
              </label>
            ))}
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#e74c3c", cursor:"pointer" }}>
              <input type="checkbox" style={{...chk, accentColor:"#e74c3c"}} checked={showCanceladas} onChange={() => setShowCanceladas(v=>!v)} />
              Incluir Canceladas
            </label>
          </div>
        </div>
      </div>

      <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
        <button onClick={gerarPDF} disabled={gerando} style={{ padding:"12px 28px", fontSize:14, fontWeight:700, border:"none", borderRadius:4, cursor:gerando?"wait":"pointer", fontFamily:FONT, color:"#fff", background: gerando ? "#aaa" : "#1a9e8f", boxShadow:"0 2px 8px rgba(0,0,0,.15)" }}
          onMouseEnter={e=>{if(!gerando) e.currentTarget.style.opacity="0.85";}} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
          {gerando ? "Gerando..." : "⬇ Gerar Relatório PDF"}
        </button>
        <button onClick={gerarExcel} disabled={gerando} style={{ padding:"12px 28px", fontSize:14, fontWeight:700, border:"none", borderRadius:4, cursor:gerando?"wait":"pointer", fontFamily:FONT, color:"#fff", background: gerando ? "#aaa" : "#d4880f", boxShadow:"0 2px 8px rgba(0,0,0,.15)" }}
          onMouseEnter={e=>{if(!gerando) e.currentTarget.style.opacity="0.85";}} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
          {gerando ? "Gerando..." : "⬇ Gerar Relatório Excel"}
        </button>
      </div>
    </div>
  );
}

// ─── Motoristas Page ──────────────────────────────────────────────────────────
function MotoristasPage({ motoristas, setMotoristas, addToast, apiFetch, empresaCfg }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editMot, setEditMot] = useState(null);
  const [appInfo, setAppInfo] = useState(null);

  useEffect(() => { apiFetch("/api/motoristas/app-info").then(d => setAppInfo(d)).catch(() => {}); }, []);

  const filtered = motoristas.filter(m =>
    norm(m.nome).includes(norm(search)) ||
    (m.cnh||"").includes(search) ||
    (m.tel||"").includes(search)
  );

  const save = async (data) => {
    try {
      const novo = await apiFetch("/api/motoristas", { method: "POST", body: data });
      setMotoristas(p => [...p, novo]);
      addToast("Motorista cadastrado!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setModal(false);
  };

  const saveEdit = async (data) => {
    try {
      const upd = await apiFetch("/api/motoristas/" + data.id, { method: "PUT", body: data });
      setMotoristas(p => p.map(m => m.id === upd.id ? upd : m));
      addToast("Motorista atualizado!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setEditMot(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este motorista?")) return;
    try {
      await apiFetch("/api/motoristas/" + id, { method: "DELETE" });
      setMotoristas(p => p.filter(m => m.id !== id));
      addToast("Motorista excluído!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const isVencido = (d) => { if(!d) return false; return new Date(d) < new Date(); };
  const isProximo = (d) => { if(!d) return false; const diff = (new Date(d) - new Date()) / (1000*60*60*24); return diff >= 0 && diff <= 30; };

  const permitApp = empresaCfg?.permite_app_motorista;
  const motAtivosApp = motoristas.filter(m => m.ativo_app).length;

  return (
    <div>
      {/* Banner App Motorista */}
      {permitApp ? (
        <div style={{ background:"linear-gradient(135deg, #1a9e8f 0%, #148f82 100%)", borderRadius:8, padding:"14px 20px", marginBottom:14, display:"flex", alignItems:"center", justifyContent:"space-between", color:"#fff" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:22 }}>📱</span>
            <div>
              <div style={{ fontWeight:700, fontSize:14 }}>Controla Motorista App</div>
              <div style={{ fontSize:11, opacity:.85 }}>
                {motAtivosApp} de {appInfo?.max || "—"} motoristas ativos
                {appInfo?.valorPorMotorista ? ` · R$ ${appInfo.valorPorMotorista.toFixed(2)}/motorista/mês` : ""}
              </div>
            </div>
          </div>
          <div style={{ background:"rgba(255,255,255,.2)", borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:700 }}>
            {motAtivosApp}/{appInfo?.max || 0} vagas
          </div>
        </div>
      ) : (
        <div style={{ background:"#fff8e1", border:"1px solid #ffe082", borderRadius:8, padding:"14px 20px", marginBottom:14, display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:22 }}>📱</span>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:"#856404" }}>App Motorista — Disponível a partir do plano Controla</div>
            <div style={{ fontSize:11, color:"#856404", opacity:.8 }}>Seus motoristas poderão receber itinerários, confirmar paradas e enviar fotos pelo celular. R$ 10,00/motorista/mês.</div>
          </div>
        </div>
      )}

      <div className="page-action-bar" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#aaa" }}><IcoSearch /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar motorista..." style={{ ...inp, width:280, paddingLeft:34 }} />
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => exportToExcel(filtered, [
            {label:"Nome",key:"nome"},{label:"CNH",key:r=>r.cnh||""},{label:"Categoria CNH",key:r=>r.categoriaCnh||r.categoria_cnh||""},
            {label:"Validade CNH",key:r=>r.validadeCnh||r.validade_cnh||""},{label:"Telefone",key:r=>r.tel||""},
            {label:"E-mail",key:r=>r.email||""},{label:"Endereço",key:r=>r.endereco||""},
            {label:"Status",key:r=>r.status||"Ativo"},{label:"Observações",key:r=>r.obs||""},
          ], "motoristas")} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            Exportar Excel
          </button>
          <button onClick={() => setModal(true)} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            <IcoPlus /> Novo Motorista
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ background:"#fff", borderRadius:6, border:"1px solid #ebebeb" }}>
        <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
              {["#","Nome","CNH","Cat.","Venc. CNH","Telefone","Status", ...(permitApp ? ["App"] : []),"Ações"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"10px 13px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={permitApp ? 9 : 8}><EmptyState icon="🚗" title="Nenhum motorista cadastrado" subtitle="Clique em 'Novo Motorista' para cadastrar" /></td></tr>
            ) : filtered.map((m, i) => (
              <tr key={m.id} style={{ borderBottom:"1px solid #f4f4f4" }}
                onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background=""}>
                <td className="mob-hide" style={{ padding:"10px 13px", color:"#aaa", fontSize:11 }}>{String(i+1).padStart(2,"0")}</td>
                <td className="mob-title" style={{ padding:"10px 13px", fontWeight:700, color:"#1a1a1a" }}>{m.nome}</td>
                <td data-label="CNH" style={{ padding:"10px 13px", color:"#555" }}>{m.cnh||"—"}</td>
                <td data-label="Cat." style={{ padding:"10px 13px", color:"#555" }}>{m.categoria_cnh||"—"}</td>
                <td data-label="Venc. CNH" style={{ padding:"10px 13px" }}>
                  {m.venc_cnh ? (
                    <span style={{ color: isVencido(m.venc_cnh)?"#e74c3c":isProximo(m.venc_cnh)?"#f39c12":"#555", fontWeight: isVencido(m.venc_cnh)||isProximo(m.venc_cnh)?700:400 }}>
                      {isVencido(m.venc_cnh)?"":isProximo(m.venc_cnh)?"":""}{new Date(m.venc_cnh).toLocaleDateString("pt-BR")}
                    </span>
                  ) : "—"}
                </td>
                <td data-label="Telefone" style={{ padding:"10px 13px", color:"#555" }}>{m.tel||"—"}</td>
                <td data-label="Status" style={{ padding:"10px 13px" }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, background:m.status==="Ativo"?"#e8faf0":"#fee", color:m.status==="Ativo"?"#2ecc71":"#e74c3c" }}>{m.status||"Ativo"}</span>
                </td>
                {permitApp && (
                  <td data-label="App" style={{ padding:"10px 13px" }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, background:m.ativo_app?"#e8faf5":"#f5f5f5", color:m.ativo_app?"#1aae9e":"#aaa", border:"1px solid "+(m.ativo_app?"#1aae9e":"#ddd") }}>
                      {m.ativo_app ? "📱 Ativo" : "—"}
                    </span>
                  </td>
                )}
                <td className="mob-actions" style={{ padding:"10px 13px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={() => setEditMot(m)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#555", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEdit /> Editar</button>
                    <button onClick={() => remove(m.id)} style={{ background:"#fee", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:600 }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding:"9px 13px", borderTop:"1px solid #f0f0f0", fontSize:12, color:"#aaa" }}>{filtered.length} de {motoristas.length} motoristas{permitApp ? ` · ${motAtivosApp} com app` : ""}</div>
      </div>
      {(modal || editMot) && <MotoristaModal onClose={() => { setModal(false); setEditMot(null); }} onSave={editMot ? saveEdit : save} initialData={editMot} editMode={!!editMot} empresaCfg={empresaCfg} apiFetch={apiFetch} appInfo={appInfo} />}
    </div>
  );
}

function MotoristaModal({ onClose, onSave, initialData, editMode, empresaCfg, apiFetch, appInfo }) {
  const fileRef = useRef();
  const _comissaoInit = (snake, camel) => { const v = initialData ? (initialData[snake] != null ? initialData[snake] : initialData[camel]) : null; return (v == null || v === "") ? "" : String(parseFloat(v)); };
  const [form, setForm] = useState(initialData ? {
    id: initialData.id, nome: initialData.nome||"", cnh: initialData.cnh||"", categoriaCnh: initialData.categoria_cnh||initialData.categoriaCnh||"",
    vencCnh: initialData.venc_cnh ? initialData.venc_cnh.split("T")[0] : (initialData.vencCnh||""), tel: initialData.tel||"", email: initialData.email||"",
    obs: initialData.obs||"", status: initialData.status||"Ativo", cursos: initialData.cursos||"",
    comissaoColocacao: _comissaoInit("comissao_colocacao","comissaoColocacao"), comissaoRetirada: _comissaoInit("comissao_retirada","comissaoRetirada")
  } : { nome:"", cnh:"", categoriaCnh:"", vencCnh:"", tel:"", email:"", obs:"", status:"Ativo", cursos:"", comissaoColocacao:"", comissaoRetirada:"" });
  const [anexos, setAnexos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const sf = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const permitAnexos = empresaCfg && (empresaCfg.plano === "controla" || empresaCfg.plano === "controla_plus" || empresaCfg.plano === "operacao" || empresaCfg.plano === "escala");

  const CURSOS_OPCOES = ["MOPP","Direção Defensiva","Cargas Perigosas","Primeiros Socorros","NR-11","NR-35","Transporte Coletivo"];

  useEffect(() => {
    if (editMode && initialData?.id && apiFetch) {
      apiFetch("/api/documentos?categoria=motorista_" + initialData.id).then(docs => setAnexos(docs||[])).catch(() => {});
    }
  }, []);

  const capitalizeName = (name) => {
    return name.replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleUpload = async (files) => {
    if (!permitAnexos || !apiFetch) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("documento", file);
        fd.append("categoria", "motorista_" + (initialData?.id || "novo"));
        fd.append("descricao", "Documento do motorista " + (form.nome || ""));
        const res = await fetch((process.env.REACT_APP_API || "http://localhost:3001") + "/api/documentos", {
          method: "POST", headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") }, body: fd
        });
        if (res.ok) { const doc = await res.json(); setAnexos(p => [...p, doc]); }
        else { const err = await res.json(); alert(err.error || "Erro ao enviar arquivo"); }
      } catch(e) { alert("Erro ao enviar: " + e.message); }
    }
    setUploading(false);
  };

  const removeAnexo = async (doc) => {
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await apiFetch("/api/documentos/" + doc.id, { method: "DELETE" });
      setAnexos(p => p.filter(d => d.id !== doc.id));
    } catch(e) { alert("Erro: " + e.message); }
  };

  const toggleCurso = (curso) => {
    const current = form.cursos ? form.cursos.split(",").map(s=>s.trim()).filter(Boolean) : [];
    if (current.includes(curso)) sf("cursos", current.filter(c => c !== curso).join(", "));
    else sf("cursos", [...current, curso].join(", "));
  };

  const hasCurso = (curso) => {
    const current = form.cursos ? form.cursos.split(",").map(s=>s.trim()) : [];
    return current.includes(curso);
  };

  const save = () => {
    if (!form.nome) { alert("Nome do motorista é obrigatório."); return; }
    onSave({ ...form, nome: capitalizeName(form.nome) });
  };

  return (
    <LargeModal title={editMode ? "Editar Motorista" : "Novo Motorista"} subtitle="Cadastro de motorista" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar" : "Salvar Motorista"} maxWidth={750}>
      <SectionHeader icon="👤" label="Dados Pessoais" />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, padding:"12px 0" }}>
        <Field label="Nome *"><input style={inp} value={form.nome} onChange={e => sf("nome", e.target.value)} onBlur={e => sf("nome", capitalizeName(e.target.value))} placeholder="Nome completo" /></Field>
        <Field label="Telefone"><input style={inp} value={form.tel} onChange={e => sf("tel", formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
        <Field label="E-mail"><input style={inp} value={form.email} onChange={e => sf("email", e.target.value)} placeholder="email@exemplo.com" /></Field>
        <Field label="Status">
          <select style={{ ...inp, appearance:"none" }} value={form.status} onChange={e => sf("status", e.target.value)}>
            <option value="Ativo">Ativo</option><option value="Inativo">Inativo</option><option value="Férias">Férias</option><option value="Afastado">Afastado</option>
          </select>
        </Field>
      </div>

      <SectionHeader icon="🪪" label="CNH e Habilitação" />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, padding:"12px 0" }}>
        <Field label="Nº CNH"><input style={inp} value={form.cnh} onChange={e => sf("cnh", e.target.value)} placeholder="00000000000" /></Field>
        <Field label="Categoria">
          <select style={{ ...inp, appearance:"none" }} value={form.categoriaCnh} onChange={e => sf("categoriaCnh", e.target.value)}>
            <option value="">-- Selecione --</option>
            {["A","B","C","D","E","AB","AC","AD","AE"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Vencimento CNH"><input type="date" style={inp} value={form.vencCnh} onChange={e => sf("vencCnh", e.target.value)} /></Field>
      </div>

      <SectionHeader icon="💰" label="Comissões (padrão)" />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, padding:"12px 0" }}>
        <Field label="Comissão por colocação (R$)">
          <input type="number" step="0.01" min="0" style={inp} value={form.comissaoColocacao} onChange={e => sf("comissaoColocacao", e.target.value)} placeholder="0,00" />
          <div style={{ fontSize:11, color:"#999", marginTop:4 }}>Auto-preenche na entrega e na colocação da troca. Deixe vazio se não paga comissão.</div>
        </Field>
        <Field label="Comissão por retirada (R$)">
          <input type="number" step="0.01" min="0" style={inp} value={form.comissaoRetirada} onChange={e => sf("comissaoRetirada", e.target.value)} placeholder="0,00" />
          <div style={{ fontSize:11, color:"#999", marginTop:4 }}>Auto-preenche na retirada. Deixe vazio se não paga comissão.</div>
        </Field>
      </div>

      <SectionHeader icon="🎓" label="Cursos e Certificações" />
      <div style={{ padding:"12px 0" }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:10 }}>
          {CURSOS_OPCOES.map(c => (
            <label key={c} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:hasCurso(c)?"#1aae9e":"#666", cursor:"pointer", background:hasCurso(c)?"#e8faf5":"#f5f5f5", padding:"5px 10px", borderRadius:20, border:"1px solid "+(hasCurso(c)?"#1aae9e":"#e0e0e0"), fontWeight:hasCurso(c)?600:400 }}>
              <input type="checkbox" checked={hasCurso(c)} onChange={() => toggleCurso(c)} style={{ accentColor:"#1aae9e" }} />
              {c}
            </label>
          ))}
        </div>
        <Field label="Outros cursos"><input style={inp} value={form.cursos ? form.cursos.split(",").filter(c => !CURSOS_OPCOES.includes(c.trim())).join(", ") : ""} onChange={e => {
          const predefinidos = form.cursos ? form.cursos.split(",").map(s=>s.trim()).filter(c => CURSOS_OPCOES.includes(c)) : [];
          const outros = e.target.value;
          sf("cursos", [...predefinidos, ...outros.split(",").map(s=>s.trim()).filter(Boolean)].join(", "));
        }} placeholder="Outros cursos separados por vírgula" /></Field>
      </div>

      <SectionHeader icon="📄" label="Documentos (CNH, Certificados)" />
      <div style={{ padding:"12px 0" }}>
        {permitAnexos ? (<>
          <div style={{ border:"2px dashed #ddd", borderRadius:4, padding:16, textAlign:"center", cursor:"pointer", background:uploading?"#f0f9ff":"#fafafa" }}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); handleUpload(Array.from(e.dataTransfer.files)); }}>
            <input ref={fileRef} type="file" multiple style={{ display:"none" }} onChange={e => handleUpload(Array.from(e.target.files))} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
            <div style={{ fontSize:22, marginBottom:4 }}>{uploading ? "" : ""}</div>
            <div style={{ fontWeight:600, fontSize:12, color:"#555" }}>{uploading ? "Enviando..." : "Arraste ou clique para anexar CNH, certificados, etc."}</div>
          </div>
          {anexos.length > 0 && <div style={{ marginTop:8 }}>
            {anexos.map(d => (
              <div key={d.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:"#f0f7ff", border:"1px solid #c5dff5", borderRadius:3, padding:"6px 10px", marginBottom:4, fontSize:12 }}>
                <button onClick={() => downloadAuth(d.url||"/api/files/"+d.caminho, d.nome_original||d.nome||"Documento")} style={{ background:"none", border:"none", cursor:"pointer", color:"#1a5fa8", textDecoration:"none", display:"flex", alignItems:"center", gap:5, fontSize:12 }}>{d.nome_original||d.nome||"Documento"}</button>
                <button onClick={() => removeAnexo(d)} style={{ background:"none", border:"none", cursor:"pointer", color:"#e74c3c", fontSize:14, fontWeight:700 }}>×</button>
              </div>
            ))}
          </div>}
        </>) : (
          <div style={{ background:"#fff8e1", border:"1px solid #ffe082", borderRadius:4, padding:12, fontSize:12, color:"#856404" }}>
            Anexos disponíveis a partir do <b>plano Controla</b>.
          </div>
        )}
      </div>

      {/* ── App Motorista (somente leitura — ativação pelo admin) ── */}
      {editMode && empresaCfg?.permite_app_motorista ? (<>
        <SectionHeader icon="📱" label="App Motorista" />
        <div style={{ padding:"12px 0" }}>
          <div style={{ background:initialData?.ativo_app?"#e8faf5":"#f9f9f9", border:"1px solid "+(initialData?.ativo_app?"#1aae9e":"#e0e0e0"), borderRadius:6, padding:14 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:initialData?.ativo_app?"#1aae9e":"#666" }}>
                  {initialData?.ativo_app ? "📱 App Ativo" : "App não ativado"}
                </div>
                {initialData?.ativo_app && initialData?.email_app && (
                  <div style={{ fontSize:11, color:"#888", marginTop:3 }}>Login: {initialData.email_app}</div>
                )}
                {initialData?.ultimo_login_app && (
                  <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Último acesso: {new Date(initialData.ultimo_login_app).toLocaleString("pt-BR")}</div>
                )}
              </div>
              <span style={{ fontSize:11, fontWeight:700, padding:"3px 12px", borderRadius:20, background:initialData?.ativo_app?"#e8faf5":"#f5f5f5", color:initialData?.ativo_app?"#1aae9e":"#aaa", border:"1px solid "+(initialData?.ativo_app?"#1aae9e":"#ddd") }}>
                {initialData?.ativo_app ? "Ativo" : "Inativo"}
              </span>
            </div>
            {!initialData?.ativo_app && (
              <div style={{ fontSize:11, color:"#888", marginTop:8 }}>A ativação do app é feita pelo suporte Controla. Entre em contato para liberar o acesso deste motorista.</div>
            )}
          </div>
        </div>
      </>) : editMode && !empresaCfg?.permite_app_motorista ? (
        <div style={{ marginTop:12 }}>
          <SectionHeader icon="📱" label="App Motorista" />
          <div style={{ background:"#fff8e1", border:"1px solid #ffe082", borderRadius:4, padding:12, fontSize:12, color:"#856404", marginTop:8 }}>
            App Motorista disponível a partir do <b>plano Controla</b>. Entre em contato com o suporte para saber mais.
          </div>
        </div>
      ) : null}

      <div style={{ marginTop:8 }}>
        <Field label="Observações"><textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={form.obs} onChange={e => sf("obs", e.target.value)} placeholder="Observações sobre o motorista..." /></Field>
      </div>
    </LargeModal>
  );
}

// ─── Tipos de Caçamba Page ─────────────────────────────────────────────────────
function TiposCacambaPage({ tiposCacamba, setTiposCacamba, addToast, apiFetch }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const filtered = tiposCacamba.filter(t =>
    (t.nome||"").toLowerCase().includes(search.toLowerCase()) ||
    (t.descricao||"").toLowerCase().includes(search.toLowerCase())
  );

  const save = async (data) => {
    try {
      const novo = await apiFetch("/api/tipos-cacamba", { method:"POST", body:data });
      setTiposCacamba(p => [...p, novo]);
      addToast("Tipo de caçamba cadastrado!", "success");
    } catch(err) { addToast("Erro: "+err.message, "error"); }
    setModal(false);
  };

  const saveEdit = async (data) => {
    try {
      const upd = await apiFetch("/api/tipos-cacamba/"+data.id, { method:"PUT", body:data });
      setTiposCacamba(p => p.map(t => t.id===upd.id ? upd : t));
      addToast("Tipo atualizado!", "success");
    } catch(err) { addToast("Erro: "+err.message, "error"); }
    setEditItem(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este tipo de caçamba?")) return;
    try {
      await apiFetch("/api/tipos-cacamba/"+id, { method:"DELETE" });
      setTiposCacamba(p => p.filter(t => t.id!==id));
      addToast("Tipo excluído!", "success");
    } catch(err) { addToast("Erro: "+err.message, "error"); }
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#aaa" }}><IcoSearch /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar por..." style={{ ...inp, width:300, paddingLeft:34 }} />
        </div>
        <button onClick={() => setModal(true)} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
          <IcoPlus /> Novo Tipo de Caçamba
        </button>
      </div>
      <div style={{ background:"#fff", borderRadius:6, border:"1px solid #ebebeb" }}>
        <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
              {["Nome","Descrição","Cubicagem","Criado em","Ações"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"10px 13px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5}><EmptyState icon="📐" title="Nenhum tipo cadastrado" subtitle="Clique em 'Novo Tipo de Caçamba' para cadastrar" /></td></tr>
            ) : filtered.map(t => (
              <tr key={t.id} style={{ borderBottom:"1px solid #f4f4f4" }}
                onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background=""}>
                <td className="mob-title" style={{ padding:"10px 13px", fontWeight:700, color:"#1aae9e" }}>{t.nome}</td>
                <td data-label="Descrição" style={{ padding:"10px 13px", color:"#777", fontSize:12 }}>{t.descricao||"—"}</td>
                <td data-label="Cubicagem" style={{ padding:"10px 13px", fontWeight:700 }}>{t.cubicagem ? t.cubicagem.replace(/m3/gi,"").trim() + " m³" : "—"}</td>
                <td data-label="Criado em" style={{ padding:"10px 13px", color:"#aaa", fontSize:11 }}>{t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="mob-actions" style={{ padding:"10px 13px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={() => setEditItem(t)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#555", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEdit /> Editar</button>
                    <button onClick={() => remove(t.id)} style={{ background:"#fee", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:600 }}>Excluir</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding:"9px 13px", borderTop:"1px solid #f0f0f0", fontSize:12, color:"#aaa" }}>{filtered.length} de {tiposCacamba.length} tipos</div>
      </div>
      {(modal || editItem) && <TipoCacambaModal onClose={() => { setModal(false); setEditItem(null); }} onSave={editItem ? saveEdit : save} initialData={editItem} editMode={!!editItem} />}
    </div>
  );
}

function TipoCacambaModal({ onClose, onSave, initialData, editMode }) {
  const [form, setForm] = useState(initialData ? {
    id: initialData.id, nome: initialData.nome||"", descricao: initialData.descricao||"", cubicagem: initialData.cubicagem||""
  } : { nome:"", descricao:"", cubicagem:"" });
  const sf = (k,v) => setForm(p => ({ ...p, [k]:v }));

  const save = () => {
    if (!form.nome) { alert("Nome do tipo é obrigatório."); return; }
    onSave(form);
  };

  return (
    <LargeModal title={editMode ? "Editar Tipo de Caçamba" : "Novo Tipo de Caçamba"} subtitle="Cadastro de tipo de caçamba" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar" : "Salvar"} maxWidth={550}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, paddingTop:12 }}>
        <Field label="Nome *"><input style={inp} value={form.nome} onChange={e => sf("nome", e.target.value)} placeholder="Ex: Caçamba de Entulho" /></Field>
        <Field label="Cubicagem"><input style={inp} value={form.cubicagem} onChange={e => sf("cubicagem", e.target.value)} placeholder="Ex: 5,0 m3" /></Field>
      </div>
      <div style={{ marginTop:14 }}>
        <Field label="Descrição"><textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={form.descricao} onChange={e => sf("descricao", e.target.value)} placeholder="Descrição do tipo..." /></Field>
      </div>
    </LargeModal>
  );
}

// ─── Caçambas Page ────────────────────────────────────────────────────────────
function AuthImg({ src, alt="", style={}, zoomable=true }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [zoom, setZoom] = useState(false);
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    fetch(src, { headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob && !cancelled) setBlobUrl(URL.createObjectURL(blob)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [src]);
  if (!blobUrl) return <div style={{ ...style, background:"#f5f5f5", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:"#ccc" }}>📷</div>;
  return (<>
    <img src={blobUrl} alt={alt} style={{ ...style, cursor: zoomable ? "zoom-in" : "default" }} onClick={e => { if (zoomable) { e.stopPropagation(); setZoom(true); } }} />
    {zoom && <div onClick={() => setZoom(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.8)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out", padding:24 }}>
      <img src={blobUrl} alt={alt} style={{ maxWidth:"90vw", maxHeight:"85vh", borderRadius:10, objectFit:"contain", boxShadow:"0 20px 60px rgba(0,0,0,.5)" }} />
      <div style={{ position:"absolute", top:20, right:24, color:"#fff", fontSize:14, fontWeight:600, background:"rgba(0,0,0,.4)", borderRadius:8, padding:"6px 14px", cursor:"pointer" }}>✕ Fechar</div>
    </div>}
  </>);
}

function CacambasPage({ cacambas, setCacambas, tiposCacamba, addToast, apiFetch, API, goToLocacoes }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editCac, setEditCac] = useState(null);
  const [filterStatus, setFilterStatus] = useState("Todos");
  const [reconciliando, setReconciliando] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Ordenação: padrão por código crescente (004, 005, ..., 035). Persistente no navegador.
  const [sortMode, setSortMode] = useState(() => { try { return localStorage.getItem("cacambas.sortMode") || "codigo_asc"; } catch(_) { return "codigo_asc"; } });
  useEffect(() => { try { localStorage.setItem("cacambas.sortMode", sortMode); } catch(_) {} }, [sortMode]);

  // Re-buscar caçambas sempre que a aba é aberta (garante locacao_ativa atualizada)
  useEffect(() => {
    let cancelado = false;
    (async () => {
      setRefreshing(true);
      try {
        const nova = await apiFetch("/api/cacambas");
        if (!cancelado && Array.isArray(nova)) setCacambas(nova);
      } catch(_) { /* mantém estado anterior em caso de falha */ }
      if (!cancelado) setRefreshing(false);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = cacambas.filter(c => {
    if (filterStatus !== "Todos" && c.status !== filterStatus) return false;
    const s = search.toLowerCase();
    if (!s) return true;
    return (c.codigo||"").toLowerCase().includes(s)
        || (c.numero||"").toLowerCase().includes(s)
        || (c.tipo||"").toLowerCase().includes(s)
        || (c.fabricante||"").toLowerCase().includes(s)
        || (c.descricao||"").toLowerCase().includes(s)
        || (c.observacoes||"").toLowerCase().includes(s)
        || (c.locacao_ativa?.cliente||"").toLowerCase().includes(s)
        || (c.locacao_ativa?.endereco||"").toLowerCase().includes(s);
  });
  // Ordenação configurável (padrão: por código crescente, com comparação numérica para tratar "004"/"010")
  const STATUS_RANK = { "Disponível": 0, "Em manutenção": 1, "Alocada": 2, "Inativa": 3 };
  const cmpCodigo = (a, b) => String(a.codigo||"").localeCompare(String(b.codigo||""), undefined, { numeric: true });
  const CAC_SORT_FNS = {
    codigo_asc:  (a, b) => cmpCodigo(a, b),
    codigo_desc: (a, b) => cmpCodigo(b, a),
    disponivel:  (a, b) => { const r = (STATUS_RANK[a.status]??99) - (STATUS_RANK[b.status]??99); return r !== 0 ? r : cmpCodigo(a, b); },
    recente:     (a, b) => { const ai = a?.id, bi = b?.id; if (typeof ai === "number" && typeof bi === "number") return bi - ai; return String(bi||"").localeCompare(String(ai||""), undefined, { numeric: true }); },
  };
  filtered.sort(CAC_SORT_FNS[sortMode] || CAC_SORT_FNS.codigo_asc);

  const reconciliar = async () => {
    if (!window.confirm("Esta ação vai verificar todas as caçambas marcadas como 'Alocada' e liberar as que não têm locação ativa.\n\nContinuar?")) return;
    setReconciliando(true);
    try {
      const r = await apiFetch("/api/cacambas/reconciliar", { method:"POST" });
      // Recarregar lista
      const nova = await apiFetch("/api/cacambas");
      setCacambas(nova);
      if (r.liberadas > 0) {
        addToast("✓ " + r.liberadas + " caçamba(s) liberada(s) — estava(m) marcada(s) como Alocada mas sem locação ativa.", "success");
      } else {
        addToast("Nenhuma caçamba precisou ser corrigida. Tudo em ordem.", "success");
      }
    } catch(err) { addToast("Erro ao reconciliar: " + err.message, "error"); }
    setReconciliando(false);
  };

  const statusCount = { "Disponível": 0, "Alocada": 0, "Em manutenção": 0, "Inativa": 0 };
  cacambas.forEach(c => { if(statusCount[c.status] !== undefined) statusCount[c.status]++; else statusCount["Disponível"]++; });

  const save = async (data) => {
    try {
      const nova = await apiFetch("/api/cacambas", { method: "POST", body: data });
      setCacambas(p => [...p, nova]);
      addToast("Caçamba cadastrada!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setModal(false);
  };

  const saveEdit = async (data) => {
    try {
      const upd = await apiFetch("/api/cacambas/" + data.id, { method: "PUT", body: data });
      setCacambas(p => p.map(c => c.id === upd.id ? upd : c));
      addToast("Caçamba atualizada!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
    setEditCac(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta caçamba?")) return;
    try {
      await apiFetch("/api/cacambas/" + id, { method: "DELETE" });
      setCacambas(p => p.filter(c => c.id !== id));
      addToast("Caçamba excluída!", "success");
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };

  const statusColors = { "Disponível":{ bg:"#e8faf0", fg:"#2ecc71" }, "Alocada":{ bg:"#e8f0fe", fg:"#1a9e8f" }, "Em manutenção":{ bg:"#fff5e6", fg:"#f39c12" }, "Inativa":{ bg:"#f5f5f5", fg:"#999" } };
  const getStatusStyle = (st) => statusColors[st] || statusColors["Disponível"];

  return (
    <div>
      {/* KPI Cards */}
      <div className="kpi-cards-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:18 }}>
        {[
          { label:"Disponíveis", val: statusCount["Disponível"], color:"#2ecc71", key:"Disponível" },
          { label:"Alocadas", val: statusCount["Alocada"], color:"#1a9e8f", key:"Alocada" },
          { label:"Em Manutenção", val: statusCount["Em manutenção"], color:"#f39c12", key:"Em manutenção" },
          { label:"Inativas", val: statusCount["Inativa"], color:"#999", key:"Inativa" },
        ].map(k => (
          <div key={k.label} onClick={() => setFilterStatus(filterStatus === k.key ? "Todos" : k.key)} style={{ background: filterStatus===k.key ? k.color+"11" : "#fff", borderRadius:6, border: filterStatus===k.key ? `2px solid ${k.color}` : "1px solid #ebebeb", padding:"16px 20px", borderTop:`3px solid ${k.color}`, cursor:"pointer", transition:"all .2s" }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.5 }}>{k.label}</div>
            <div style={{ fontSize:28, fontWeight:800, color:"#1a1a1a", marginTop:4 }}>{k.val}</div>
          </div>
        ))}
      </div>

      <div className="page-action-bar" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"#aaa" }}><IcoSearch /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar caçamba..." style={{ ...inp, width:280, paddingLeft:34 }} />
          </div>
          <select value={sortMode} onChange={e => setSortMode(e.target.value)} title="Ordenar caçambas"
            style={{ padding:"8px 12px", borderRadius:4, border:"1px solid #e0e0e0", fontSize:12, fontFamily:FONT, background:"#fafafa", color:"#444", cursor:"pointer", appearance:"auto" }}>
            <option value="codigo_asc">Ordenar: por código (crescente)</option>
            <option value="codigo_desc">Ordenar: por código (decrescente)</option>
            <option value="disponivel">Ordenar: disponíveis primeiro</option>
            <option value="recente">Ordenar: mais recentes primeiro</option>
          </select>
          {filterStatus !== "Todos" && <button onClick={() => setFilterStatus("Todos")} style={{ background:"#f0f0f0", border:"none", borderRadius:20, padding:"5px 14px", fontSize:11, fontWeight:600, cursor:"pointer", color:"#666" }}>✕ Limpar filtro: {filterStatus}</button>}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button
            onClick={async () => {
              setRefreshing(true);
              try { const nova = await apiFetch("/api/cacambas"); if (Array.isArray(nova)) setCacambas(nova); }
              catch(e) { addToast("Falha ao atualizar: " + e.message, "error"); }
              setRefreshing(false);
            }}
            disabled={refreshing}
            title="Atualizar dados"
            style={{ background:"#fff", color:"#555", border:"1px solid #d4d4d4", borderRadius:3, padding:"9px 12px", fontSize:12, fontWeight:700, cursor: refreshing?"wait":"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT, opacity: refreshing?0.6:1 }}
          >
            <span style={{ display:"inline-block", transition:"transform .6s", transform: refreshing ? "rotate(360deg)" : "rotate(0deg)" }}>🔄</span>
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
          <button onClick={reconciliar} disabled={reconciliando} title="Libera caçambas marcadas como Alocada que não têm locação ativa" style={{ background:"#fff", color:"#555", border:"1px solid #d4d4d4", borderRadius:3, padding:"9px 14px", fontSize:12, fontWeight:700, cursor: reconciliando?"wait":"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT, opacity: reconciliando?0.6:1 }}>
            {reconciliando ? "Reconciliando..." : "🛠️ Reconciliar status"}
          </button>
          <button onClick={() => exportToExcel(filtered, [
            {label:"Código",key:r=>r.codigo||""},{label:"Número",key:r=>r.numero||""},
            {label:"Tipo",key:r=>r.tipo||""},{label:"Volume",key:r=>r.volume||""},
            {label:"Fabricante",key:r=>r.fabricante||""},{label:"Status",key:r=>r.status||""},
            {label:"Cliente Atual",key:r=>r.locacao_ativa?.cliente||""},
            {label:"Endereço Atual",key:r=>r.locacao_ativa?.endereco||""},
            {label:"Faixas Refletivas",key:r=>r.faixas_refletivas?"Sim":"Não"},
            {label:"Manutenção",key:r=>r.em_manutencao?"Sim":"Não"},
            {label:"Motivo Manutenção",key:r=>r.motivo_manutencao||""},
            {label:"Observações",key:r=>r.observacoes||r.obs||""},
          ], "cacambas")} style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:3, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            Exportar Excel
          </button>
          <button onClick={() => setModal(true)} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"9px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            <IcoPlus /> Nova Caçamba
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ background:"#fff", borderRadius:6, border:"1px solid #ebebeb" }}>
        <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:"2px solid #f0f0f0" }}>
              {["Foto","Tipo da Caçamba","Nº Caçamba","Fabricante","Status","Cliente Atual / Endereço","Faixas","Manutenção","Observação","Ações"].map(h => (
                <th key={h} style={{ textAlign:"left", padding:"10px 13px", fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10}><EmptyState icon="" title="Nenhuma caçamba encontrada" subtitle={filterStatus!=="Todos"?"Nenhuma caçamba com status '"+filterStatus+"'":"Clique em 'Nova Caçamba' para cadastrar"} /></td></tr>
            ) : filtered.map((c) => {
              const sc = getStatusStyle(c.status);
              return (
              <tr key={c.id} style={{ borderBottom:"1px solid #f4f4f4" }}
                onMouseEnter={e => e.currentTarget.style.background="#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background=""}>
                <td className="mob-thumb" style={{ padding:"8px 13px", width:50 }}>
                  {c.foto_url ? <AuthImg src={API+c.foto_url} alt="" style={{ width:40, height:40, borderRadius:6, objectFit:"cover", border:"1px solid #eee" }} /> : <div style={{ width:40, height:40, borderRadius:6, background:"#f5f5f5", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:"#ccc" }}>📷</div>}
                </td>
                <td className="mob-title" style={{ padding:"10px 13px", fontWeight:700, color:"#1aae9e" }}>{c.tipo||"—"} {c.volume ? <span style={{ fontSize:11, color:"#888", fontWeight:400 }}>({c.volume.replace(/m3/gi,"").trim()} m³)</span> : ""}</td>
                <td data-label="Nº Caçamba" style={{ padding:"10px 13px", fontWeight:700 }}>{c.numero||c.codigo||"—"}</td>
                <td data-label="Fabricante" style={{ padding:"10px 13px", fontSize:12, color:"#555" }}>{c.fabricante||"—"}</td>
                <td data-label="Status" style={{ padding:"10px 13px" }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20, background:sc.bg, color:sc.fg }}>{c.status||"Disponível"}</span>
                  {c.status === "Alocada" && !c.locacao_ativa && (
                    <div style={{ marginTop:4, fontSize:10, color:"#e74c3c", fontWeight:700, display:"flex", alignItems:"center", gap:3 }} title="Marcada como Alocada mas sem locação ativa — clique em Reconciliar status">
                      ⚠ Inconsistente
                    </div>
                  )}
                </td>
                <td data-label="Cliente Atual" style={{ padding:"10px 13px", fontSize:12, maxWidth:240 }}>
                  {c.locacao_ativa ? (
                    <div
                      onClick={() => goToLocacoes && goToLocacoes(c.locacao_ativa.status)}
                      style={{ cursor: goToLocacoes ? "pointer" : "default" }}
                      title="Abrir locação"
                    >
                      <div style={{ fontWeight:700, color:"#1a1a1a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {c.locacao_ativa.cliente || "—"}
                      </div>
                      <div style={{ fontSize:11, color:"#777", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        📍 {c.locacao_ativa.endereco || "Endereço não informado"}
                      </div>
                      <div style={{ fontSize:10, color:"#1a9e8f", marginTop:2, fontWeight:600 }}>
                        #{c.locacao_ativa.id} · {c.locacao_ativa.status}
                      </div>
                    </div>
                  ) : (
                    <span style={{ color:"#bbb" }}>—</span>
                  )}
                </td>
                <td data-label="Faixas" style={{ padding:"10px 13px", fontSize:12 }}>
                  {c.faixas_refletivas ? <span style={{ color:"#2ecc71", fontWeight:700 }}>✓ Sim</span> : <span style={{ color:"#e74c3c", fontWeight:700 }}>✗ Não</span>}
                </td>
                <td data-label="Manutenção" style={{ padding:"10px 13px", fontSize:12 }}>
                  {c.em_manutencao ? <span style={{ color:"#f39c12", fontWeight:600 }}>{c.motivo_manutencao||"Sim"}</span> : <span style={{ color:"#aaa" }}>—</span>}
                </td>
                <td data-label="Observação" style={{ padding:"10px 13px", color:"#777", fontSize:12, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.observacoes||c.descricao||"—"}</td>
                <td className="mob-actions" style={{ padding:"10px 13px" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <button onClick={() => setEditCac(c)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#555", display:"flex", gap:4, alignItems:"center", fontSize:12, fontWeight:600 }}><IcoEdit /> Editar</button>
                    <button onClick={() => remove(c.id)} style={{ background:"#fee", border:"none", borderRadius:3, padding:"5px 9px", cursor:"pointer", color:"#e74c3c", fontSize:12, fontWeight:600 }}>Excluir</button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        <div style={{ padding:"9px 13px", borderTop:"1px solid #f0f0f0", fontSize:12, color:"#aaa" }}>{filtered.length} de {cacambas.length} caçambas</div>
      </div>
      {(modal || editCac) && <CacambaModal tiposCacamba={tiposCacamba} onClose={() => { setModal(false); setEditCac(null); }} onSave={editCac ? saveEdit : save} initialData={editCac} editMode={!!editCac} apiFetch={apiFetch} API={API} setCacambas={setCacambas} />}
    </div>
  );
}

function CacambaModal({ onClose, onSave, initialData, editMode, tiposCacamba=[], apiFetch, API, setCacambas }) {
  const [form, setForm] = useState(initialData ? {
    id: initialData.id,
    tipo_cacamba_id: initialData.tipo_cacamba_id||"",
    numero: initialData.numero||initialData.codigo||"",
    fabricante: initialData.fabricante||"",
    status: initialData.status||"Disponível",
    observacoes: initialData.observacoes||initialData.descricao||"",
    em_manutencao: !!initialData.em_manutencao,
    motivo_manutencao: initialData.motivo_manutencao||"",
    faixas_refletivas: initialData.faixas_refletivas !== undefined ? !!initialData.faixas_refletivas : true,
  } : { tipo_cacamba_id:"", numero:"", fabricante:"", status:"Disponível", observacoes:"", em_manutencao:false, motivo_manutencao:"", faixas_refletivas:true });
  const [uploading, setUploading] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const sf = (k,v) => setForm(p => ({ ...p, [k]:v }));

  // Carregar foto com autenticação (blob URL)
  useEffect(() => {
    if (!initialData?.foto_url) return;
    let cancelled = false;
    fetch(API + initialData.foto_url, { headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") } })
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob && !cancelled) setFotoPreview(URL.createObjectURL(blob)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const save = () => {
    if (!form.tipo_cacamba_id) { alert("Tipo da Caçamba é obrigatório."); return; }
    if (!form.numero) { alert("Número da Caçamba é obrigatório."); return; }
    const tipoObj = tiposCacamba.find(t => String(t.id) === String(form.tipo_cacamba_id));
    onSave({ ...form, codigo: form.numero, tipo: tipoObj?.nome||"", volume: tipoObj?.cubicagem||"", descricao: form.observacoes });
  };

  const handleFotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !form.id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("foto", file);
      const r = await fetch(API + "/api/cacambas/" + form.id + "/foto", { method: "POST", headers: { Authorization: "Bearer " + sessionStorage.getItem("controla_token") }, body: fd });
      const data = await r.json();
      if (r.ok) {
        setFotoPreview(URL.createObjectURL(file));
        if (setCacambas) setCacambas(p => p.map(c => c.id === form.id ? { ...c, foto: data.foto, foto_url: data.foto_url } : c));
      } else { alert(data.error || "Erro no upload"); }
    } catch(err) { alert("Erro: " + err.message); }
    setUploading(false);
  };

  const removeFoto = async () => {
    if (!form.id) return;
    try {
      await apiFetch("/api/cacambas/" + form.id + "/foto", { method: "DELETE" });
      setFotoPreview(null);
      if (setCacambas) setCacambas(p => p.map(c => c.id === form.id ? { ...c, foto: null, foto_url: null } : c));
    } catch(err) { alert("Erro: " + err.message); }
  };

  const [fotoZoom, setFotoZoom] = useState(false);
  const motivosManutencao = ["Solda", "Pintura", "Avaria", "Sem faixa refletiva", "Reforma", "Outro"];

  return (
    <LargeModal title={editMode ? "Editar Caçamba" : "Nova Caçamba"} subtitle="Cadastro de caçamba" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar" : "Salvar Caçamba"} maxWidth={680}>
      {/* Foto */}
      {editMode && (
        <div style={{ display:"flex", alignItems:"center", gap:16, paddingTop:12, paddingBottom:8, borderBottom:"1px solid #f0f0f0", marginBottom:14 }}>
          <div onClick={() => fotoPreview && setFotoZoom(true)} style={{ width:80, height:80, borderRadius:10, overflow:"hidden", background:"#f5f5f5", border:"1px solid #eee", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", cursor: fotoPreview ? "zoom-in" : "default", position:"relative" }}>
            {fotoPreview ? <img src={fotoPreview} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : <span style={{ fontSize:28, color:"#ccc" }}>📷</span>}
            {fotoPreview && <div style={{ position:"absolute", bottom:2, right:2, background:"rgba(0,0,0,.5)", borderRadius:4, padding:"1px 4px", fontSize:10, color:"#fff" }}>🔍</div>}
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:6 }}>Foto da Caçamba</div>
            <div style={{ display:"flex", gap:8 }}>
              <label style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:4, padding:"5px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                {uploading ? "Enviando..." : (fotoPreview ? "Trocar foto" : "Enviar foto")}
                <input type="file" accept="image/*" onChange={handleFotoUpload} style={{ display:"none" }} disabled={uploading} />
              </label>
              {fotoPreview && <button onClick={removeFoto} style={{ background:"#fee", color:"#e74c3c", border:"none", borderRadius:4, padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>Remover</button>}
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, paddingTop: editMode ? 0 : 12 }}>
        <Field label="Tipo da Caçamba *">
          <select style={{ ...inp, appearance:"none", borderColor:!form.tipo_cacamba_id?"#1aae9e":"#e0e0e0" }} value={form.tipo_cacamba_id} onChange={e => sf("tipo_cacamba_id", e.target.value)}>
            <option value="">-- Selecione --</option>
            {tiposCacamba.map(t => <option key={t.id} value={t.id}>{t.nome} {t.cubicagem ? "(" + t.cubicagem.replace(/m3/gi,"").trim() + " m³)" : ""}</option>)}
          </select>
        </Field>
        <Field label="Número da Caçamba *"><input style={{ ...inp, borderColor:!form.numero?"#1aae9e":"#e0e0e0" }} value={form.numero} onChange={e => sf("numero", e.target.value)} placeholder="Ex: 001" /></Field>
        <Field label="Nome do Fabricante"><input style={inp} value={form.fabricante} onChange={e => sf("fabricante", e.target.value)} placeholder="Ex: MetalForte" /></Field>
      </div>

      {/* Faixas Refletivas + Status */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
        <Field label="Faixas Refletivas">
          <div style={{ display:"flex", gap:16, paddingTop:6 }}>
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
              <input type="radio" name="faixas" checked={form.faixas_refletivas===true} onChange={() => sf("faixas_refletivas", true)} style={{ accentColor:"#1aae9e" }} /> Sim
            </label>
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
              <input type="radio" name="faixas" checked={form.faixas_refletivas===false} onChange={() => sf("faixas_refletivas", false)} style={{ accentColor:"#e74c3c" }} /> Não
            </label>
          </div>
        </Field>
        <Field label="Status">
          <div style={{ fontSize:12, color:"#888", fontStyle:"italic", paddingTop:8 }}>
            {form.status === "Alocada" ? "Status controlado automaticamente (em uso)" : form.em_manutencao ? "Em manutenção" : "" + (form.status||"Disponível")}
          </div>
        </Field>
      </div>

      {/* Manutenção */}
      <div style={{ marginTop:14, padding:14, borderRadius:8, background: form.em_manutencao ? "#fffbeb" : "#fafafa", border: form.em_manutencao ? "1px solid #f59e0b33" : "1px solid #eee" }}>
        <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, fontWeight:600, cursor:"pointer", color: form.em_manutencao ? "#d97706" : "#555" }}>
          <input type="checkbox" checked={form.em_manutencao} onChange={e => { sf("em_manutencao", e.target.checked); if (!e.target.checked) sf("motivo_manutencao", ""); }} style={{ accentColor:"#f59e0b", width:16, height:16 }} />
          Em manutenção
        </label>
        {form.em_manutencao && (
          <div style={{ marginTop:10 }}>
            <Field label="Motivo da Manutenção">
              <select style={{ ...inp, appearance:"none" }} value={motivosManutencao.includes(form.motivo_manutencao) ? form.motivo_manutencao : (form.motivo_manutencao ? "Outro" : "")} onChange={e => sf("motivo_manutencao", e.target.value === "Outro" ? "" : e.target.value)}>
                <option value="">-- Selecione --</option>
                {motivosManutencao.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            {(form.motivo_manutencao === "" || !motivosManutencao.includes(form.motivo_manutencao)) && form.em_manutencao && (
              <div style={{ marginTop:8 }}>
                <Field label="Descreva o motivo">
                  <input style={inp} value={form.motivo_manutencao} onChange={e => sf("motivo_manutencao", e.target.value)} placeholder="Descreva o motivo..." />
                </Field>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop:14 }}>
        <Field label="Observação"><textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={form.observacoes} onChange={e => sf("observacoes", e.target.value)} placeholder="Observações..." /></Field>
      </div>
      {fotoZoom && fotoPreview && (
        <div onClick={() => setFotoZoom(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.8)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", cursor:"zoom-out", padding:24 }}>
          <img src={fotoPreview} alt="" style={{ maxWidth:"90vw", maxHeight:"85vh", borderRadius:10, objectFit:"contain", boxShadow:"0 20px 60px rgba(0,0,0,.5)" }} />
          <div style={{ position:"absolute", top:20, right:24, color:"#fff", fontSize:14, fontWeight:600, background:"rgba(0,0,0,.4)", borderRadius:8, padding:"6px 14px", cursor:"pointer" }}>✕ Fechar</div>
        </div>
      )}
    </LargeModal>
  );
}

// ─── Importação de Dados ────────────────────────────────────────────────────────
function ImportacaoTab({ apiFetch, addToast, API }) {
  const [step, setStep] = useState("upload"); // upload | preview | importing | done
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [modoImport, setModoImport] = useState("locacoes"); // "locacoes" | "clientes"
  const [sobrescrever, setSobrescrever] = useState(false); // só para modo clientes
  const fileRef = useRef(null);

  // URLs e nomes de modelo dinâmicos pelo modo
  const cfg = modoImport === "clientes"
    ? {
        modelo:    "/api/importar/clientes/modelo",
        preview:   "/api/importar/clientes/preview",
        executar:  "/api/importar/clientes/executar",
        modeloName: "modelo_clientes.csv",
        titulo:    "Importação de Clientes",
        subtitulo: "Importe apenas o cadastro de clientes — sem locações.",
      }
    : {
        modelo:    "/api/importar/modelo",
        preview:   "/api/importar/preview",
        executar:  "/api/importar/executar",
        modeloName: "modelo_importacao_controla.csv",
        titulo:    "Importação de Locações + Clientes",
        subtitulo: "Importe o histórico completo: clientes, locações, motoristas e tipos de caçamba.",
      };

  const convertXlsToCsv = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const XLSX = window.XLSX;
          if (!XLSX) { reject(new Error("Biblioteca XLSX não carregada")); return; }
          const data = new Uint8Array(evt.target.result);
          const wb = XLSX.read(data, { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          // Desmerge: preencher todas as células de ranges merged com o valor da primeira célula
          if (ws["!merges"]) {
            for (const merge of ws["!merges"]) {
              const val = ws[XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c })];
              if (!val) continue;
              for (let r = merge.s.r; r <= merge.e.r; r++) {
                for (let c = merge.s.c; c <= merge.e.c; c++) {
                  if (r === merge.s.r && c === merge.s.c) continue;
                  ws[XLSX.utils.encode_cell({ r, c })] = { ...val };
                }
              }
            }
          }
          const csv = XLSX.utils.sheet_to_csv(ws, { FS: ";", RS: "\n" });
          const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
          const csvFile = new File([blob], file.name.replace(/\.xlsx?$/i, ".csv"), { type: "text/csv" });
          resolve(csvFile);
        } catch (err) { reject(err); }
      };
      reader.onerror = () => reject(new Error("Erro ao ler arquivo"));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["csv", "xls", "xlsx"].includes(ext)) {
      addToast("Envie um arquivo .CSV, .XLS ou .XLSX", "error");
      return;
    }
    if (ext === "xls" || ext === "xlsx") {
      try {
        addToast("Convertendo arquivo Excel para CSV...", "info");
        const csvFile = await convertXlsToCsv(f);
        setSelectedFile(csvFile);
        addToast("Arquivo convertido com sucesso!", "success");
      } catch (err) {
        addToast("Erro ao converter arquivo: " + err.message, "error");
      }
    } else {
      setSelectedFile(f);
    }
  };

  const handleDownloadModelo = async () => {
    try {
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + cfg.modelo, { headers: { Authorization: "Bearer " + token } });
      if (!res.ok) throw new Error("Erro ao baixar modelo");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = cfg.modeloName; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { addToast("Erro: " + e.message, "error"); }
  };

  const handleUploadPreview = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("arquivo", selectedFile);
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + cfg.preview, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro no upload");
      setPreview(data);
      setStep("preview");
    } catch (e) { addToast("Erro: " + e.message, "error"); }
    setUploading(false);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setStep("importing");
    try {
      const formData = new FormData();
      formData.append("arquivo", selectedFile);
      if (modoImport === "clientes") formData.append("sobrescrever", String(sobrescrever));
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + cfg.executar, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro na importação");
      setResult(data);
      setStep("done");
      addToast("Importação concluída com sucesso!", "success");
    } catch (e) {
      addToast("Erro: " + e.message, "error");
      setStep("preview");
    }
    setImporting(false);
  };

  const resetImport = () => { setStep("upload"); setPreview(null); setResult(null); setSelectedFile(null); if (fileRef.current) fileRef.current.value = ""; };

  const cardS = { background:"#fff", borderRadius:8, border:"1px solid #ebebeb", padding:24 };
  const stepBadge = (num, active) => (
    <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:26, height:26, borderRadius:13, background:active?"#1aae9e":"#e0e0e0", color:active?"#fff":"#999", fontSize:12, fontWeight:700, flexShrink:0 }}>{num}</span>
  );

  // ─── Step 1: Upload ───
  if (step === "upload") return (
    <div style={cardS}>
      <div style={{ fontSize:15, fontWeight:700, color:"#1e2d4f", marginBottom:4, display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:18 }}>📥</span> {cfg.titulo}</div>
      <div style={{ fontSize:12, color:"#888", marginBottom:18 }}>{cfg.subtitulo}</div>

      {/* Toggle de modo */}
      <div style={{ display:"flex", gap:10, marginBottom:24 }}>
        <button
          onClick={() => { setModoImport("locacoes"); resetImport(); }}
          style={{
            flex:1, padding:"14px 16px", borderRadius:10,
            border: modoImport === "locacoes" ? "2px solid #1aae9e" : "1px solid #e5e5e5",
            background: modoImport === "locacoes" ? "#f0faf8" : "#fff",
            cursor:"pointer", textAlign:"left", fontFamily:"inherit",
            transition:"all .15s"
          }}
        >
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <span style={{ fontSize:18 }}>📋</span>
            <span style={{ fontSize:13, fontWeight:700, color: modoImport === "locacoes" ? "#1aae9e" : "#1e2d4f" }}>Locações + Clientes</span>
          </div>
          <div style={{ fontSize:11, color:"#888" }}>Importa o histórico completo (de migração de outro sistema, ex: Net Caçambas)</div>
        </button>
        <button
          onClick={() => { setModoImport("clientes"); resetImport(); }}
          style={{
            flex:1, padding:"14px 16px", borderRadius:10,
            border: modoImport === "clientes" ? "2px solid #1aae9e" : "1px solid #e5e5e5",
            background: modoImport === "clientes" ? "#f0faf8" : "#fff",
            cursor:"pointer", textAlign:"left", fontFamily:"inherit",
            transition:"all .15s"
          }}
        >
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <span style={{ fontSize:18 }}>👥</span>
            <span style={{ fontSize:13, fontWeight:700, color: modoImport === "clientes" ? "#1aae9e" : "#1e2d4f" }}>Apenas Clientes</span>
          </div>
          <div style={{ fontSize:11, color:"#888" }}>Importa só o cadastro de clientes (nome, CPF/CNPJ, telefone, endereço)</div>
        </button>
      </div>

      {/* Passo a passo */}
      <div style={{ marginBottom:24, padding:20, background:"linear-gradient(135deg, #f8fffe, #f0faf8)", borderRadius:12, border:"1px solid #d5ece8" }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#1e2d4f", marginBottom:14 }}>Como funciona:</div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
            {stepBadge("1", true)}
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"#1e2d4f" }}>Baixe o modelo CSV</div>
              <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Baixe nosso arquivo modelo para ver exatamente quais colunas e formatos usar.</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
            {stepBadge("2", true)}
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"#1e2d4f" }}>Preencha com seus dados</div>
              <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Abra o modelo no Excel ou Google Planilhas e preencha com os dados dos seus clientes e locações. Mantenha o cabeçalho da primeira linha.</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
            {stepBadge("3", true)}
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:"#1e2d4f" }}>Faça o upload</div>
              <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Envie o arquivo preenchido aqui. O sistema vai analisar e mostrar um preview antes de importar.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Download modelo */}
      <div style={{ marginBottom:24, padding:18, background:"#f0f7ff", borderRadius:10, border:"1px solid #c8ddf5", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:"#1565c0" }}>Arquivo Modelo</div>
          <div style={{ fontSize:11, color:"#666", marginTop:3 }}>CSV com 3 exemplos preenchidos e todas as colunas aceitas pelo sistema.</div>
        </div>
        <button onClick={handleDownloadModelo}
          style={{ background:"#1565c0", color:"#fff", border:"none", borderRadius:6, padding:"9px 20px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:6 }}>
          Baixar Modelo CSV
        </button>
      </div>

      {/* Dicas */}
      <div style={{ marginBottom:24, padding:16, background:"#fffbf0", borderRadius:10, border:"1px solid #f0e6c8" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#b8860b", marginBottom:8 }}>Dicas importantes:</div>
        <div style={{ fontSize:11, color:"#777", lineHeight:1.8 }}>
          <b>Separador:</b> O arquivo deve usar ponto-e-vírgula (<b>;</b>) como separador de colunas.<br/>
          <b>Datas:</b> Use o formato <b>DD/MM/AAAA</b> (ex: 01/03/2026).<br/>
          <b>Valores:</b> Use vírgula como decimal (ex: 450,00).<br/>
          <b>Status aceitos:</b> Para Colocar, Colocada, Para Retirar, Para Trocar, Para Cobrar, Encerrada, Cancelada.<br/>
          <b>Tipo cliente:</b> PF (pessoa física) ou PJ (pessoa jurídica).<br/>
          <b>Clientes duplicados:</b> Se já existir um cliente com o mesmo CPF/CNPJ ou nome, ele não será duplicado.<br/>
          <b>Vindo de outro sistema?</b> Exporte os dados como CSV, ajuste as colunas conforme o modelo e importe normalmente.
        </div>
      </div>

      {/* Colunas aceitas */}
      <div style={{ marginBottom:24, padding:16, background:"#f8f9fa", borderRadius:10, border:"1px solid #ebebeb" }}>
        <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:8 }}>Colunas do modelo (na ordem):</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
          {["Nome Cliente *","Tipo (PF/PJ)","CPF/CNPJ","Telefone","Tipo Caçamba","Endereço","Bairro","Cidade","UF","Status","Data Pedido","Data Entrega","Data Real Entrega","Prev. Retirada","Data Retirada Real","Motorista Entrega","Motorista Retirada","Valor","Pago (SIM/NÃO)","Data Pagamento","Observações"].map((c, i) => (
            <span key={i} style={{ padding:"3px 10px", borderRadius:4, fontSize:10, fontWeight:600, background:c.includes("*")?"#e8f5e9":"#f5f5f5", color:c.includes("*")?"#2e7d32":"#666", border:"1px solid "+(c.includes("*")?"#c8e6c9":"#e0e0e0") }}>{c}</span>
          ))}
        </div>
        <div style={{ fontSize:10, color:"#aaa", marginTop:6 }}>* Campo obrigatório. Os demais são opcionais.</div>
      </div>

      {/* Upload area */}
      <div style={{ border:"2px dashed "+(selectedFile?"#1aae9e":"#d0d0d0"), borderRadius:12, padding:selectedFile?"20px 24px":"40px 24px", textAlign:"center", background:selectedFile?"#f0faf9":"#fafafa", transition:"all .2s", cursor:"pointer" }}
        onClick={() => fileRef.current?.click()}>
        {selectedFile ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12 }}>
            <span style={{ fontSize:28 }}>📄</span>
            <div style={{ textAlign:"left" }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#1e2d4f" }}>{selectedFile.name}</div>
              <div style={{ fontSize:11, color:"#888" }}>{(selectedFile.size / 1024).toFixed(0)} KB</div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); resetImport(); }} style={{ marginLeft:12, background:"none", border:"1px solid #e0e0e0", borderRadius:4, padding:"4px 10px", fontSize:11, cursor:"pointer", color:"#e74c3c" }}>Remover</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize:40, marginBottom:8 }}>📁</div>
            <div style={{ fontSize:14, fontWeight:600, color:"#666" }}>Clique para selecionar o arquivo</div>
            <div style={{ fontSize:11, color:"#aaa", marginTop:4 }}>Aceita .CSV, .XLS ou .XLSX (Net Caçambas, etc.)</div>
          </>
        )}
        <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx" style={{ display:"none" }} onChange={handleFileSelect} />
      </div>

      {selectedFile && (
        <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
          <button onClick={handleUploadPreview} disabled={uploading}
            style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, padding:"10px 28px", fontSize:14, fontWeight:700, cursor:uploading?"wait":"pointer", opacity:uploading?0.6:1, fontFamily:FONT }}>
            {uploading ? "Analisando..." : "Analisar Arquivo"}
          </button>
        </div>
      )}
    </div>
  );

  // ─── Step 2: Preview ───
  // Preview específico para modo CLIENTES
  if (step === "preview" && preview && modoImport === "clientes") return (
    <div style={cardS}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1e2d4f", display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:18 }}>👀</span> Preview da Importação de Clientes</div>
          <div style={{ fontSize:12, color:"#888", marginTop:2 }}>Confira os dados antes de importar.</div>
        </div>
        <button onClick={resetImport} style={{ background:"none", border:"1px solid #d0d0d0", borderRadius:6, padding:"8px 16px", fontSize:12, cursor:"pointer", color:"#666", fontFamily:FONT }}>Voltar</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:12, marginBottom:24 }}>
        <div style={{ background:"#f8f9fa", borderRadius:10, padding:"16px 14px", border:"1px solid #ebebeb", textAlign:"center" }}>
          <div style={{ fontSize:24, fontWeight:800, color:"#3498db" }}>{preview.total}</div>
          <div style={{ fontSize:11, color:"#888", fontWeight:600 }}>Clientes no arquivo</div>
        </div>
        <div style={{ background:"#f8f9fa", borderRadius:10, padding:"16px 14px", border:"1px solid #ebebeb", textAlign:"center" }}>
          <div style={{ fontSize:24, fontWeight:800, color: preview.duplicatas_internas?.length ? "#e67e22" : "#16a34a" }}>{preview.duplicatas_internas?.length || 0}</div>
          <div style={{ fontSize:11, color:"#888", fontWeight:600 }}>Duplicatas no arquivo</div>
        </div>
      </div>

      {preview.duplicatas_internas?.length > 0 && (
        <div style={{ marginBottom:20, padding:14, background:"#fff8e1", borderRadius:8, border:"1px solid #ffe082" }}>
          <div style={{ fontSize:12, color:"#f57f17", fontWeight:700, marginBottom:6 }}>⚠ Atenção: {preview.duplicatas_internas.length} duplicata(s) interna(s)</div>
          <div style={{ fontSize:11, color:"#795548" }}>O mesmo nome aparece mais de uma vez no arquivo. Apenas o primeiro será importado.</div>
        </div>
      )}

      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#555", marginBottom:10 }}>Preview dos primeiros clientes (até 200):</div>
        <div style={{ maxHeight:500, overflow:"auto", border:"1px solid #e0e0e0", borderRadius:8 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead>
              <tr style={{ background:"#f5f5f5", position:"sticky", top:0 }}>
                {["Nome", "Tipo", "CPF/CNPJ", "Telefone", "Email", "Cidade/UF"].map((h, i) => (
                  <th key={i} style={{ padding:"8px 10px", textAlign:"left", fontWeight:700, color:"#555", borderBottom:"2px solid #e0e0e0", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.clientes.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid #f0f0f0" }}>
                  <td style={{ padding:"6px 10px", fontWeight:600, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.nome}</td>
                  <td style={{ padding:"6px 10px" }}><span style={{ padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600, background:c.tipo==="PJ"?"#e8eaf6":"#e0f2f1", color:c.tipo==="PJ"?"#3949ab":"#00695c" }}>{c.tipo}</span></td>
                  <td style={{ padding:"6px 10px", fontSize:10, color:"#666", whiteSpace:"nowrap" }}>{c.cpf_cnpj||"—"}</td>
                  <td style={{ padding:"6px 10px", fontSize:10, color:"#666", whiteSpace:"nowrap" }}>{c.tel||"—"}</td>
                  <td style={{ padding:"6px 10px", fontSize:10, color:"#666", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.email||"—"}</td>
                  <td style={{ padding:"6px 10px", fontSize:10, color:"#666", whiteSpace:"nowrap" }}>{c.cidade ? `${c.cidade}/${c.uf||"SP"}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview.total > 200 && <div style={{ fontSize:11, color:"#aaa", marginTop:6, textAlign:"center" }}>Mostrando 200 de {preview.total} clientes</div>}
      </div>

      {/* Toggle sobrescrever */}
      <div style={{ marginBottom:20, padding:14, background:"#f8f9fa", borderRadius:8, border:"1px solid #ebebeb" }}>
        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
          <input type="checkbox" checked={sobrescrever} onChange={e => setSobrescrever(e.target.checked)} style={{ width:16, height:16, accentColor:"#1aae9e", cursor:"pointer" }} />
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:"#1e2d4f" }}>Sobrescrever clientes existentes</div>
            <div style={{ fontSize:11, color:"#888", marginTop:2 }}>Se marcado, atualiza os dados de clientes que já existem (mesmo CPF/CNPJ ou nome). Se desmarcado, ignora os duplicados.</div>
          </div>
        </label>
      </div>

      <div style={{ padding:14, background:"#fff8e1", borderRadius:8, border:"1px solid #ffe082", marginBottom:20 }}>
        <div style={{ fontSize:12, color:"#f57f17", fontWeight:600 }}>Atenção:</div>
        <div style={{ fontSize:11, color:"#795548", marginTop:4, lineHeight:1.6 }}>
          A duplicidade é checada por <strong>CPF/CNPJ</strong> primeiro, depois por <strong>nome</strong> (case insensitive). Recomendamos fazer um backup antes de importar.
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", gap:12 }}>
        <button onClick={resetImport} style={{ background:"none", border:"1px solid #d0d0d0", borderRadius:6, padding:"10px 20px", fontSize:13, cursor:"pointer", color:"#666", fontFamily:FONT }}>Cancelar</button>
        <button onClick={handleImport}
          style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, padding:"10px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
          Importar {preview.total} Clientes
        </button>
      </div>
    </div>
  );

  if (step === "preview" && preview) return (
    <div style={cardS}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#1e2d4f", display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:18 }}>👀</span> Preview da Importação</div>
          <div style={{ fontSize:12, color:"#888", marginTop:2 }}>Confira os dados antes de importar.</div>
        </div>
        <button onClick={resetImport} style={{ background:"none", border:"1px solid #d0d0d0", borderRadius:6, padding:"8px 16px", fontSize:12, cursor:"pointer", color:"#666", fontFamily:FONT }}>Voltar</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:12, marginBottom:24 }}>
        {[
          { label:"Locações", num:preview.total_locacoes, icon:"📦", color:"#1aae9e" },
          { label:"Clientes", num:preview.total_clientes, icon:"👥", color:"#3498db" },
          { label:"Tipos Caçamba", num:preview.total_tipos, icon:"📐", color:"#9b59b6" },
          { label:"Motoristas", num:preview.total_motoristas, icon:"🚗", color:"#e67e22" },
        ].map((c, i) => (
          <div key={i} style={{ background:"#f8f9fa", borderRadius:10, padding:"16px 14px", border:"1px solid #ebebeb", textAlign:"center" }}>
            <div style={{ fontSize:22, marginBottom:4 }}>{c.icon}</div>
            <div style={{ fontSize:24, fontWeight:800, color:c.color }}>{c.num}</div>
            <div style={{ fontSize:11, color:"#888", fontWeight:600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {preview.situacoes?.length > 0 && (
        <div style={{ marginBottom:20, padding:14, background:"#f8f9fa", borderRadius:8, border:"1px solid #ebebeb" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:8 }}>Situações encontradas:</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {preview.situacoes.map((s, i) => {
              const su = s.toUpperCase();
              return <span key={i} style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600, background:su.includes("ENCERRAD")?"#e8f5e9":su.includes("COLOC")?"#e3f2fd":su.includes("CANCEL")?"#ffebee":"#fff3e0", color:su.includes("ENCERRAD")?"#2e7d32":su.includes("COLOC")?"#1565c0":su.includes("CANCEL")?"#c62828":"#e65100" }}>{s}</span>;
            })}
          </div>
        </div>
      )}

      {preview.tipos_cacamba?.length > 0 && (
        <div style={{ marginBottom:20, padding:14, background:"#f8f9fa", borderRadius:8, border:"1px solid #ebebeb" }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:8 }}>Tipos de caçamba:</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {preview.tipos_cacamba.map((t, i) => <span key={i} style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600, background:"#f3e5f5", color:"#7b1fa2" }}>{t}</span>)}
          </div>
        </div>
      )}

      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:"#555", marginBottom:10 }}>Preview das primeiras locações:</div>
        <div style={{ maxHeight:500, overflow:"auto", border:"1px solid #e0e0e0", borderRadius:8 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead>
              <tr style={{ background:"#f5f5f5", position:"sticky", top:0 }}>
                {["Status", "Cliente", "Tipo Caçamba", "Endereço", "Entrega", "Valor"].map((h, i) => (
                  <th key={i} style={{ padding:"8px 10px", textAlign:"left", fontWeight:700, color:"#555", borderBottom:"2px solid #e0e0e0", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.locacoes.map((loc, i) => {
                const su = (loc.situacao||"").toUpperCase();
                return (
                  <tr key={i} style={{ borderBottom:"1px solid #f0f0f0" }}>
                    <td style={{ padding:"6px 10px" }}><span style={{ padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:600, background:su.includes("ENCERRAD")?"#e8f5e9":su.includes("COLOC")?"#e3f2fd":su.includes("CANCEL")?"#ffebee":"#fff3e0", color:su.includes("ENCERRAD")?"#2e7d32":su.includes("COLOC")?"#1565c0":su.includes("CANCEL")?"#c62828":"#e65100" }}>{loc.situacao}</span></td>
                    <td style={{ padding:"6px 10px", maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{loc.cliente_nome}</td>
                    <td style={{ padding:"6px 10px", fontSize:10, color:"#666" }}>{loc.tipo_cacamba}</td>
                    <td style={{ padding:"6px 10px", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontSize:10, color:"#888" }}>{loc.endereco}</td>
                    <td style={{ padding:"6px 10px", whiteSpace:"nowrap", fontSize:10 }}>{loc.data_entrega || loc.data_entrega_real || loc.data_entrega_prev || ""}</td>
                    <td style={{ padding:"6px 10px", fontWeight:600, whiteSpace:"nowrap" }}>R$ {loc.valor || loc.valor_locacao || "0"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {preview.total_locacoes > 100 && <div style={{ fontSize:11, color:"#aaa", marginTop:6, textAlign:"center" }}>Mostrando 100 de {preview.total_locacoes} locações</div>}
      </div>

      <div style={{ padding:14, background:"#fff8e1", borderRadius:8, border:"1px solid #ffe082", marginBottom:20 }}>
        <div style={{ fontSize:12, color:"#f57f17", fontWeight:600 }}>Atenção:</div>
        <div style={{ fontSize:11, color:"#795548", marginTop:4, lineHeight:1.6 }}>
          A importação vai criar os clientes que ainda não existem no sistema e adicionar todas as locações listadas. Clientes com mesmo CPF/CNPJ ou nome não serão duplicados. Recomendamos fazer um backup antes de importar.
        </div>
      </div>

      <div style={{ display:"flex", justifyContent:"flex-end", gap:12 }}>
        <button onClick={resetImport} style={{ background:"none", border:"1px solid #d0d0d0", borderRadius:6, padding:"10px 20px", fontSize:13, cursor:"pointer", color:"#666", fontFamily:FONT }}>Cancelar</button>
        <button onClick={handleImport}
          style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, padding:"10px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
          Importar {preview.total_locacoes} Locações
        </button>
      </div>
    </div>
  );

  // ─── Step 3: Importing ───
  if (step === "importing") return (
    <div style={cardS}>
      <div style={{ textAlign:"center", padding:"60px 20px" }}>
        <div style={{ fontSize:48, marginBottom:16, animation:"spin 1s linear infinite" }}>⏳</div>
        <div style={{ fontSize:16, fontWeight:700, color:"#1e2d4f", marginBottom:8 }}>Importando dados...</div>
        <div style={{ fontSize:13, color:"#888" }}>Isso pode levar alguns segundos. Não feche esta página.</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  // ─── Step 4: Done ───
  if (step === "done" && result) return (
    <div style={cardS}>
      <div style={{ textAlign:"center", padding:"40px 20px" }}>
        <div style={{ fontSize:56, marginBottom:12 }}>✅</div>
        <div style={{ fontSize:18, fontWeight:800, color:"#1e2d4f", marginBottom:6 }}>Importação Concluída!</div>
        <div style={{ fontSize:13, color:"#888", marginBottom:24 }}>Seus dados foram importados com sucesso.</div>

        {modoImport === "clientes" ? (
          <div style={{ display:"inline-grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, textAlign:"center", marginBottom:24 }}>
            <div style={{ background:"#e8f5e9", borderRadius:10, padding:"16px 24px" }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#2e7d32" }}>{result.criados ?? 0}</div>
              <div style={{ fontSize:12, color:"#555", fontWeight:600 }}>Criados</div>
            </div>
            <div style={{ background:"#e3f2fd", borderRadius:10, padding:"16px 24px" }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#1565c0" }}>{result.atualizados ?? 0}</div>
              <div style={{ fontSize:12, color:"#555", fontWeight:600 }}>Atualizados</div>
            </div>
            <div style={{ background:"#f5f5f5", borderRadius:10, padding:"16px 24px" }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#888" }}>{result.ignorados ?? 0}</div>
              <div style={{ fontSize:12, color:"#555", fontWeight:600 }}>Ignorados (já existiam)</div>
            </div>
          </div>
        ) : (
          <div style={{ display:"inline-grid", gridTemplateColumns:"1fr 1fr", gap:16, textAlign:"center", marginBottom:24 }}>
            <div style={{ background:"#e8f5e9", borderRadius:10, padding:"16px 24px" }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#2e7d32" }}>{result.clientes_importados}</div>
              <div style={{ fontSize:12, color:"#555", fontWeight:600 }}>Clientes novos</div>
            </div>
            <div style={{ background:"#e3f2fd", borderRadius:10, padding:"16px 24px" }}>
              <div style={{ fontSize:28, fontWeight:800, color:"#1565c0" }}>{result.locacoes_importadas}</div>
              <div style={{ fontSize:12, color:"#555", fontWeight:600 }}>Locações importadas</div>
            </div>
          </div>
        )}

        {result.erros && result.erros.length > 0 && (
          <div style={{ textAlign:"left", marginBottom:20, padding:14, background:"#fff3e0", borderRadius:8, border:"1px solid #ffe082", maxHeight:150, overflow:"auto" }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#e65100", marginBottom:6 }}>Avisos ({result.erros.length}):</div>
            {result.erros.map((e, i) => (
              <div key={i} style={{ fontSize:11, color:"#795548", marginBottom:2 }}>
                {typeof e === "string" ? e : `Linha ${e.linha}${e.nome ? ` (${e.nome})` : ""}: ${e.erro}`}
              </div>
            ))}
          </div>
        )}

        <button onClick={resetImport} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, padding:"10px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
          Nova Importação
        </button>
      </div>
    </div>
  );

  return null;
}


// ─── Configurações da Empresa ──────────────────────────────────────────────────
function ConfiguracoesPage({ apiFetch, addToast, userRole }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [assinaturaPreview, setAssinaturaPreview] = useState(null);
  const [nomeSigInput, setNomeSigInput] = useState('');
  const [uploadingAssinatura, setUploadingAssinatura] = useState(false);
  const [showDrawModal, setShowDrawModal] = useState(false);
  const sigCanvasRef = useRef(null);
  const sigDrawingRef = useRef(false);
  const sigLastPosRef = useRef({ x: 0, y: 0 });
  const [mapModal, setMapModal] = useState(false);

  const API = process.env.REACT_APP_API || "http://localhost:3001";

  // Carregar imagem autenticada como blob URL
  const loadAuthImage = async (imageUrl, setter) => {
    if (!imageUrl) return;
    try {
      const token = sessionStorage.getItem("controla_token");
      const url = imageUrl.startsWith("/api/") ? API + imageUrl : API + "/api/files/" + imageUrl;
      const res = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (res.ok) { const blob = await res.blob(); setter(URL.createObjectURL(blob)); }
    } catch(e) { console.error("Erro ao carregar imagem:", e); }
  };

  useEffect(() => {
    apiFetch("/api/config").then(d => {
      setCfg(d);
      setLoading(false);
      if (d.logo_url) loadAuthImage(d.logo_url, setLogoPreview);
      if (d.qrpix_url) loadAuthImage(d.qrpix_url, setQrPreview);
      if (d.assinatura_url) loadAuthImage(d.assinatura_url, setAssinaturaPreview);
      setNomeSigInput(d.nome_signatario || '');
    }).catch(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  const salvar = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch("/api/config", { method: "PUT", body: cfg });
      setCfg(updated);
      addToast("Configurações salvas!", "success");
    } catch (e) { addToast("Erro: " + e.message, "error"); }
    setSaving(false);
  };

  const uploadFile = async (file, endpoint, previewSetter) => {
    const formData = new FormData();
    formData.append(endpoint === "logo" ? "logo" : "qrpix", file);
    try {
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + "/api/config/" + endpoint, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const url = endpoint === "logo" ? data.logo_url : data.qrpix_url;
      set(endpoint === "logo" ? "logo_url" : "qrpix_url", url);
      previewSetter(URL.createObjectURL(file));
      addToast(endpoint === "logo" ? "Logo enviado!" : "QR Code enviado!", "success");
    } catch (e) { addToast("Erro no upload: " + e.message, "error"); }
  };

  const uploadAssinatura = async (file) => {
    setUploadingAssinatura(true);
    try {
      const formData = new FormData();
      formData.append("assinatura", file);
      formData.append("nome_signatario", nomeSigInput);
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + "/api/config/assinatura", {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      set("assinatura_url", data.assinatura_url);
      set("nome_signatario", data.nome_signatario || nomeSigInput);
      setAssinaturaPreview(URL.createObjectURL(file));
      addToast("Assinatura enviada com sucesso!", "success");
    } catch (e) { addToast("Erro no upload: " + e.message, "error"); }
    setUploadingAssinatura(false);
  };

  const salvarNomeSig = async () => {
    try {
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + "/api/config/assinatura", {
        method: "PUT",
        headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
        body: JSON.stringify({ nome_signatario: nomeSigInput }),
      });
      if (!res.ok) throw new Error("Erro ao salvar");
      set("nome_signatario", nomeSigInput);
      addToast("Nome do signatário salvo!", "success");
    } catch (e) { addToast("Erro: " + e.message, "error"); }
  };

  const removerAssinatura = async () => {
    if (!window.confirm("Remover a assinatura dos recibos?")) return;
    try {
      const token = sessionStorage.getItem("controla_token");
      const res = await fetch(API + "/api/config/assinatura", {
        method: "DELETE",
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) throw new Error("Erro ao remover");
      set("assinatura_url", null);
      set("nome_signatario", null);
      setAssinaturaPreview(null);
      setNomeSigInput('');
      addToast("Assinatura removida!", "success");
    } catch (e) { addToast("Erro: " + e.message, "error"); }
  };

  const initCanvas = (canvas) => {
    if (!canvas) return;
    sigCanvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const getCanvasPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const onCanvasDown = (e) => {
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    sigDrawingRef.current = true;
    sigLastPosRef.current = getCanvasPos(e, canvas);
  };

  const onCanvasMove = (e) => {
    e.preventDefault();
    if (!sigDrawingRef.current) return;
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const pos = getCanvasPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(sigLastPosRef.current.x, sigLastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    sigLastPosRef.current = pos;
  };

  const onCanvasUp = () => { sigDrawingRef.current = false; };

  const clearCanvas = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  const salvarDesenho = async () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], "assinatura_desenho.png", { type: "image/png" });
      await uploadAssinatura(file);
      setShowDrawModal(false);
    }, "image/png");
  };

  const [cfgTab, setCfgTab] = useState("empresa");
  const [storageInfo, setStorageInfo] = useState(null);

  useEffect(() => {
    apiFetch("/api/config/storage").then(setStorageInfo).catch(() => {});
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#aaa" }}>Carregando...</div>;
  if (!cfg) return <div style={{ padding: 40, textAlign: "center", color: "#e74c3c" }}>Erro ao carregar configurações</div>;

  const imgBox = { width: 150, height: 150, border: "2px dashed #d0d0d0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: "#fafafa", transition: "border-color .2s" };

  const fmtBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const gb = bytes / (1024*1024*1024);
    if (gb >= 1) return gb.toFixed(gb >= 10 ? 0 : 1) + " GB";
    const mb = bytes / (1024*1024);
    if (mb >= 1) return mb.toFixed(0) + " MB";
    return (bytes / 1024).toFixed(0) + " KB";
  };

  const tabs = [
    { key:"empresa", label:"Empresa", icon:"🏢" },
    ...(userRole === "admin" ? [{ key:"visual", label:"Identidade Visual", icon:"🎨" }] : []),
    ...(userRole === "admin" ? [{ key:"financeiro", label:"Financeiro", icon:"💰" }] : []),
    { key:"documentos", label:"Recibos e Documentos", icon:"📄" },
    { key:"importacao", label:"Importação", icon:"📥" },
    ...(userRole === "admin" ? [{ key:"plano", label:"Plano e Armazenamento", icon:"📦" }] : []),
    ...(userRole === "admin" ? [{ key:"assinatura", label:"Assinatura", icon:"📋" }] : []),
    ...(userRole === "admin" ? [{ key:"usuarios", label:"Usuários", icon:"👥" }] : []),
  ];

  const tabStyle = (active) => ({
    padding:"10px 18px", fontSize:13, fontWeight: active ? 700 : 500, cursor:"pointer", border:"none",
    background: active ? "#fff" : "transparent", color: active ? "#1aae9e" : "#666",
    borderBottom: active ? "2px solid #1aae9e" : "2px solid transparent",
    fontFamily: FONT, transition:"all .2s", display:"flex", alignItems:"center", gap:6,
  });

  const cardStyle = { background:"#fff", borderRadius:8, border:"1px solid #ebebeb", padding:24 };
  const sectionTitle = (icon, text) => <div style={{ fontSize:15, fontWeight:700, color:"#1e2d4f", marginBottom:18, display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:18 }}>{icon}</span> {text}</div>;

  const saveBtn = (
    <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end" }}>
      <button onClick={salvar} disabled={saving}
        style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, padding:"10px 28px", fontSize:14, fontWeight:700, cursor: saving?"wait":"pointer", opacity: saving?0.6:1, fontFamily:FONT }}>
        {saving ? "Salvando..." : "Salvar Configurações"}
      </button>
    </div>
  );

  return (<>
    <div>
      {/* Abas */}
      <div className="config-tabs" style={{ display:"flex", gap:0, borderBottom:"1px solid #e4e4e4", marginBottom:20, background:"#fafafa", borderRadius:"8px 8px 0 0", padding:"0 4px", overflowX:"auto" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setCfgTab(t.key)} style={tabStyle(cfgTab === t.key)}>
            <span style={{ fontSize:15 }}>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* ─── ABA: Empresa ─── */}
      {cfgTab === "empresa" && (() => {
        const ro = userRole !== "admin";
        const roInp = ro ? { ...inp, background:"#f0f0f0", color:"#888", cursor:"not-allowed" } : inp;
        return (
        <div style={cardStyle}>
          {sectionTitle("", "Dados da Empresa")}
          {ro && <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"10px 14px", marginBottom:16, fontSize:12, color:"#9a3412", fontWeight:600 }}>Somente administradores podem editar os dados da empresa.</div>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <Field label="Nome da Empresa *"><input style={roInp} value={cfg.nome||""} onChange={e => { if (ro) return; set("nome", e.target.value); }} readOnly={ro} placeholder="Ex: Controla Caçambas" /></Field>
            <Field label="CNPJ/CPF"><input style={roInp} value={cfg.cnpj||""} onChange={e=>{ if (ro) return; const raw=e.target.value.replace(/\D/g,"").slice(0,14); set("cnpj",formatCpfCnpj(raw));}} readOnly={ro} placeholder="00.000.000/0000-00" /></Field>
            <Field label="Endereço"><input style={roInp} value={cfg.endereco||""} onChange={e => { if (ro) return; set("endereco", e.target.value); }} readOnly={ro} placeholder="Rua, Número" /></Field>
            <Field label="Bairro"><input style={roInp} value={cfg.bairro||""} onChange={e => { if (ro) return; set("bairro", e.target.value); }} readOnly={ro} /></Field>
            <Field label="Cidade"><input style={roInp} value={cfg.cidade||""} onChange={e => { if (ro) return; set("cidade", e.target.value); }} readOnly={ro} /></Field>
            <Field label="UF"><input style={roInp} value={cfg.uf||""} onChange={e => { if (ro) return; set("uf", e.target.value); }} readOnly={ro} maxLength={2} /></Field>
            <Field label="CEP"><input style={roInp} value={cfg.cep||""} onChange={e => { if (ro) return; set("cep", formatCep(e.target.value)); }} readOnly={ro} placeholder="00000-000" /></Field>
            <Field label="Telefone"><input style={roInp} value={cfg.telefone||""} onChange={e => { if (ro) return; set("telefone", formatPhone(e.target.value)); }} readOnly={ro} placeholder="(11) 99999-9999" /></Field>
            <Field label={<span>Diária extra padrão (R$/dia) <span style={{color:"#aaa", fontWeight:400, fontSize:11}}>(atraso na retirada)</span></span>}><input type="number" min="0" step="0.01" style={roInp} value={cfg.diaria_extra_padrao != null ? cfg.diaria_extra_padrao : ""} onChange={e => { if (ro) return; set("diaria_extra_padrao", e.target.value); }} readOnly={ro} placeholder="0,00" /></Field>
            <div style={{ gridColumn:"1 / -1" }}>
              <Field label="E-mail"><input style={roInp} value={cfg.email||""} onChange={e => { if (ro) return; set("email", e.target.value); }} readOnly={ro} placeholder="contato@empresa.com.br" /></Field>
            </div>
            {!ro && <div style={{ gridColumn:"1 / -1" }}>
              <button type="button" onClick={() => setMapModal(true)} style={{ padding:"8px 14px", background:"none", border:"1px solid #1a9e8f", borderRadius:5, color:"#1a9e8f", fontSize:12, cursor:"pointer", fontWeight:600, fontFamily:FONT }}>Confirmar Endereco no Mapa</button>
            </div>}
          </div>
          {!ro && saveBtn}
        </div>
        );
      })()}

      {/* ─── ABA: Identidade Visual ─── */}
      {cfgTab === "visual" && (
        <div style={cardStyle}>
          {sectionTitle("🎨", "Identidade Visual")}
          <div style={{ display:"flex", gap:32, flexWrap:"wrap" }}>
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#333", marginBottom:12 }}>Logo da Empresa</div>
              <label style={imgBox}
                onMouseEnter={e => e.currentTarget.style.borderColor="#1aae9e"}
                onMouseLeave={e => e.currentTarget.style.borderColor="#d0d0d0"}>
                {(logoPreview || cfg.logo_url) ? (
                  <img src={logoPreview} alt="Logo" style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
                ) : (
                  <div style={{ textAlign:"center", color:"#aaa" }}>
                    <div style={{ fontSize:32 }}>📷</div>
                    <div style={{ fontSize:11, marginTop:6 }}>Clique para enviar</div>
                  </div>
                )}
                <input type="file" accept=".png,.jpg,.jpeg,.svg" style={{ display:"none" }} onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0], "logo", setLogoPreview); }} />
              </label>
              <div style={{ fontSize:10, color:"#aaa", marginTop:6 }}>PNG, JPG ou SVG (máx 2MB)</div>
            </div>
          </div>
          {saveBtn}
        </div>
      )}

      {/* ─── ABA: Financeiro ─── */}
      {cfgTab === "financeiro" && (
        <div style={cardStyle}>
          {sectionTitle("", "Financeiro e Cobrança")}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:24 }}>
            <div style={{ gridColumn:"1 / -1" }}>
              <Field label="Chave PIX (aparece na cobrança via WhatsApp)"><input style={inp} value={cfg.chave_pix||""} onChange={e => set("chave_pix", e.target.value)} placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória" /></Field>
            </div>
          </div>
          <div style={{ fontSize:14, fontWeight:600, color:"#333", marginBottom:12 }}>QR Code PIX</div>
          <div style={{ display:"flex", alignItems:"flex-start", gap:20 }}>
            <label style={imgBox}
              onMouseEnter={e => e.currentTarget.style.borderColor="#1aae9e"}
              onMouseLeave={e => e.currentTarget.style.borderColor="#d0d0d0"}>
              {(qrPreview || cfg.qrpix_url) ? (
                <img src={qrPreview} alt="QR PIX" style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
              ) : (
                <div style={{ textAlign:"center", color:"#aaa" }}>
                  <div style={{ fontSize:32 }}>📱</div>
                  <div style={{ fontSize:11, marginTop:6 }}>Clique para enviar</div>
                </div>
              )}
              <input type="file" accept=".png,.jpg,.jpeg" style={{ display:"none" }} onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0], "qrpix", setQrPreview); }} />
            </label>
            <div style={{ fontSize:11, color:"#888", maxWidth:250, lineHeight:"1.5" }}>
              Envie a imagem do QR Code PIX da sua empresa. Essa imagem aparece na cobrança enviada por WhatsApp para facilitar o pagamento do cliente.
              <div style={{ fontSize:10, color:"#aaa", marginTop:6 }}>PNG ou JPG (máx 2MB)</div>
            </div>
          </div>

          {saveBtn}
        </div>
      )}

      {/* ─── ABA: Recibos e Documentos ─── */}
      {cfgTab === "documentos" && (
        <div style={cardStyle}>
          {sectionTitle("", "Recibos e Documentos")}

          <div style={{ marginBottom:24 }}>
            <Field label="Condições do Aluguel (aparece no recibo)">
              <textarea style={{ ...inp, minHeight:100, resize:"vertical" }} value={cfg.condicoes_aluguel||""} onChange={e => set("condicoes_aluguel", e.target.value)} placeholder="Texto que aparecerá na seção 'Condições do Aluguel' do recibo..." />
            </Field>
          </div>

          <div style={{ marginBottom:16 }}>
            <Field label="Condições do MTR/CTR (aparece no Manifesto de Transporte de Resíduos)">
              <textarea style={{ ...inp, minHeight:100, resize:"vertical" }} value={cfg.condicoes_mtr||""} onChange={e => set("condicoes_mtr", e.target.value)} placeholder="Texto que aparecerá na seção 'Condições' do MTR/CTR..." />
            </Field>
          </div>

          <div style={{ marginTop:8 }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:8 }}>Exemplos prontos (clique para usar):</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                { nome:"Padrão - Locação de Caçamba", texto:"LOCATÁRIA recebe o(s) equipamento(s) referido(s) no presente instrumento, declarando tê-los testado(s) e aproveitado(s) previamente e afirmando que o(s) mesmo(s) se acha(m) em perfeito estado de funcionamento, limpeza e segurança. Declara mais, que entende detalhadamente sua correta utilização, pelo que se obriga a devolvê-lo(s) em idênticas condições de funcionamento, limpeza e segurança no final desta locação ou na hipótese de rescisão do presente contrato. Declara ainda que fará uso de todos os equipamentos de segurança, e se compromete a repassar essas informações a quem for utilizar o(s) mesmo(s)." },
                { nome:"Completo - Com responsabilidade ambiental", texto:"O GERADOR declara que os resíduos descritos neste manifesto foram devidamente classificados, acondicionados, rotulados e disponibilizados para transporte em conformidade com as normas ambientais vigentes. O TRANSPORTADOR se responsabiliza pela coleta, transporte e entrega dos resíduos ao destino final indicado, seguindo as rotas e procedimentos estabelecidos pela legislação ambiental. O DESTINATÁRIO se compromete a receber, tratar e/ou dispor os resíduos de forma ambientalmente adequada, conforme sua licença ambiental. Todas as partes reconhecem suas responsabilidades conforme a Política Nacional de Resíduos Sólidos (Lei 12.305/2010)." },
                { nome:"Simples - Termos básicos", texto:"O locatário recebe a caçamba em perfeito estado e se compromete a devolvê-la nas mesmas condições. É proibido depositar materiais perigosos, inflamáveis, explosivos, hospitalares ou radioativos. O peso máximo não deve ultrapassar a capacidade da caçamba. A empresa não se responsabiliza por danos causados pelo mau uso do equipamento." }
              ].map((ex, idx) => (
                <button key={idx} onClick={() => set("condicoes_mtr", ex.texto)}
                  style={{ textAlign:"left", background:"#f8f9fa", border:"1px solid #e0e0e0", borderRadius:8, padding:"10px 14px", cursor:"pointer", fontFamily:FONT, fontSize:11, color:"#444", transition:"all .15s" }}
                  onMouseOver={e => { e.currentTarget.style.background="#f0faf9"; e.currentTarget.style.borderColor="#1aae9e"; }}
                  onMouseOut={e => { e.currentTarget.style.background="#f8f9fa"; e.currentTarget.style.borderColor="#e0e0e0"; }}>
                  <span style={{ fontWeight:600, color:"#1aae9e" }}>{ex.nome}</span>
                  <div style={{ marginTop:3, fontSize:10, color:"#888", lineHeight:"1.4" }}>{ex.texto.substring(0, 140)}...</div>
                </button>
              ))}
            </div>
          </div>

          {/* ─── Assinatura dos Recibos ─── */}
          <div style={{ marginTop:32, borderTop:"1px solid #e8e8e8", paddingTop:24 }}>
            <div style={{ fontSize:15, fontWeight:700, color:"#333", marginBottom:4 }}>Assinatura dos Recibos</div>
            <div style={{ fontSize:12, color:"#888", marginBottom:16, lineHeight:"1.5" }}>
              Envie uma imagem ou desenhe a assinatura da empresa. Ela aparecerá automaticamente em todos os recibos.
            </div>

            <div style={{ display:"flex", gap:24, flexWrap:"wrap", alignItems:"flex-start" }}>
              {/* Preview / Upload / Desenhar */}
              <div style={{ textAlign:"center" }}>
                {assinaturaPreview ? (
                  <div style={{ ...imgBox, width:220, height:100, cursor:"default", borderStyle:"solid", borderColor:"#1aae9e" }}>
                    <img src={assinaturaPreview} alt="Assinatura" style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
                  </div>
                ) : (
                  <div style={{ ...imgBox, width:220, height:100, cursor:"default", flexDirection:"column", gap:4 }}>
                    <div style={{ fontSize:11, color:"#aaa" }}>Nenhuma assinatura configurada</div>
                  </div>
                )}
                <div style={{ display:"flex", gap:8, marginTop:10, justifyContent:"center" }}>
                  <label style={{ padding:"7px 14px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT, display:"inline-block" }}>
                    Enviar imagem
                    <input type="file" accept=".png,.jpg,.jpeg" style={{ display:"none" }} onChange={e => { if (e.target.files[0]) uploadAssinatura(e.target.files[0]); }} />
                  </label>
                  <button onClick={() => setShowDrawModal(true)} style={{ padding:"7px 14px", background:"#fff", color:"#1aae9e", border:"1.5px solid #1aae9e", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
                    Desenhar
                  </button>
                </div>
                <div style={{ fontSize:10, color:"#aaa", marginTop:6 }}>PNG ou JPG</div>
              </div>

              {/* Nome do signatário + ações */}
              <div style={{ flex:1, minWidth:200 }}>
                <Field label="Nome do signatário (aparece abaixo da assinatura)">
                  <div style={{ display:"flex", gap:8 }}>
                    <input style={{ ...inp, flex:1 }} value={nomeSigInput} onChange={e => setNomeSigInput(e.target.value)} placeholder="Ex: João da Silva — Diretor" />
                    <button onClick={salvarNomeSig} style={{ padding:"8px 16px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap", fontFamily:FONT }}>Salvar nome</button>
                  </div>
                </Field>
                {assinaturaPreview && (
                  <button onClick={removerAssinatura} style={{ marginTop:12, padding:"8px 16px", background:"#fff", color:"#e74c3c", border:"1px solid #e74c3c", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT, transition:"all .15s" }}
                    onMouseOver={e => { e.currentTarget.style.background="#e74c3c"; e.currentTarget.style.color="#fff"; }}
                    onMouseOut={e => { e.currentTarget.style.background="#fff"; e.currentTarget.style.color="#e74c3c"; }}>
                    Remover assinatura
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Modal Desenhar Assinatura */}
          {showDrawModal && (
            <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={() => setShowDrawModal(false)}>
              <div style={{ background:"#fff", borderRadius:16, padding:24, width:520, maxWidth:"95vw", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize:16, fontWeight:700, color:"#333", marginBottom:4 }}>Desenhar Assinatura</div>
                <div style={{ fontSize:12, color:"#888", marginBottom:16 }}>Use o mouse ou o dedo para desenhar a assinatura abaixo.</div>
                <canvas
                  ref={initCanvas}
                  width={480} height={180}
                  style={{ border:"1.5px solid #d0d0d0", borderRadius:10, cursor:"crosshair", width:"100%", height:180, touchAction:"none", background:"#fff" }}
                  onMouseDown={onCanvasDown} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp} onMouseLeave={onCanvasUp}
                  onTouchStart={onCanvasDown} onTouchMove={onCanvasMove} onTouchEnd={onCanvasUp}
                />
                <div style={{ display:"flex", justifyContent:"space-between", marginTop:16 }}>
                  <button onClick={clearCanvas} style={{ padding:"8px 20px", background:"#f5f5f5", color:"#666", border:"1px solid #ddd", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Limpar</button>
                  <div style={{ display:"flex", gap:8 }}>
                    <button onClick={() => setShowDrawModal(false)} style={{ padding:"8px 20px", background:"#f5f5f5", color:"#666", border:"1px solid #ddd", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Cancelar</button>
                    <button onClick={salvarDesenho} style={{ padding:"8px 20px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Salvar assinatura</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {saveBtn}
        </div>
      )}

      {/* ─── ABA: Importação ─── */}
      {cfgTab === "importacao" && <ImportacaoTab apiFetch={apiFetch} addToast={addToast} API={API} />}

      {/* ─── ABA: Plano e Armazenamento ─── */}
      {cfgTab === "plano" && (
        <div style={cardStyle}>
          {sectionTitle("", "Seu Plano e Armazenamento")}
          {storageInfo ? (
            <div>
              {/* Card do plano atual */}
              <div style={{ background:"linear-gradient(135deg, #1aae9e, #158f82)", borderRadius:12, padding:"24px 28px", color:"#fff", marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:12, opacity:0.8, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>Plano Atual</div>
                    <div style={{ fontSize:28, fontWeight:800 }}>{storageInfo.plano_label || "Básico"}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:12, opacity:0.8, marginBottom:4 }}>Valor mensal</div>
                    <div style={{ fontSize:22, fontWeight:700 }}>
                      {["base","basico"].includes(storageInfo.plano) ? "R$ 120" : ["operacao","controla"].includes(storageInfo.plano) ? "R$ 200" : ["escala","controla_plus"].includes(storageInfo.plano) ? "R$ 300" : "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Armazenamento */}
              <div style={{ background:"#f8f9fa", borderRadius:10, padding:20, border:"1px solid #ebebeb", marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:"#1e2d4f" }}>Armazenamento de Documentos</span>
                  {storageInfo.storage_limit > 0 ? (
                    <span style={{ fontSize:13, color:"#666" }}>{fmtBytes(storageInfo.storage_used)} de {fmtBytes(storageInfo.storage_limit)} usado</span>
                  ) : (
                    <span style={{ fontSize:13, color:"#e74c3c", fontWeight:600 }}>Indisponível no plano Básico</span>
                  )}
                </div>
                {storageInfo.storage_limit > 0 ? (
                  <>
                    <div style={{ height:12, background:"#e5e7eb", borderRadius:6, overflow:"hidden" }}>
                      <div style={{ height:"100%", width: Math.min((storageInfo.storage_used / storageInfo.storage_limit) * 100, 100) + "%", background: (storageInfo.storage_used / storageInfo.storage_limit) > 0.9 ? "#e74c3c" : (storageInfo.storage_used / storageInfo.storage_limit) > 0.7 ? "#f39c12" : "#1aae9e", borderRadius:6, transition:"width .5s" }} />
                    </div>
                    <div style={{ fontSize:12, color:"#999", marginTop:6 }}>{((storageInfo.storage_used / storageInfo.storage_limit) * 100).toFixed(1)}% utilizado</div>
                  </>
                ) : (
                  <div style={{ padding:"16px 0", textAlign:"center", color:"#aaa", fontSize:13 }}>
                    Para anexar documentos, faça upgrade para o plano Controla.
                  </div>
                )}
              </div>

              {/* Features do plano */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[
                  { label:"Anexar Documentos", ok: storageInfo.permite_anexos, desc: storageInfo.permite_anexos ? "PDFs, contratos, notas" : "Não incluso — Upgrade para Controla" },
                  { label:"Rota Inteligente", ok: true, desc: "Otimização de rotas" },
                  { label:"Caçambas na Rua", ok: true, desc: ["base","basico"].includes(storageInfo.plano) ? "Até 70" : ["operacao","controla"].includes(storageInfo.plano) ? "Até 200" : "Ilimitadas" },
                  { label:"Armazenamento", ok: storageInfo.storage_limit > 0, desc: storageInfo.storage_limit > 0 ? "2 GB" : "Não incluso — Upgrade para Controla" },
                ].map((f, i) => (
                  <div key={i} style={{ background:"#fff", border:"1px solid #ebebeb", borderRadius:8, padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", background: f.ok ? "rgba(46,204,113,.1)" : "rgba(231,76,60,.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>{f.ok ? "✓" : "✕"}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:"#1e2d4f" }}>{f.label}</div>
                      <div style={{ fontSize:11, color:"#999" }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop:20, padding:"14px 18px", background:"rgba(26,174,158,.06)", borderRadius:8, border:"1px solid rgba(26,174,158,.15)" }}>
                <p style={{ fontSize:12, color:"#666", lineHeight:1.5 }}>Para alterar seu plano ou solicitar upgrade, entre em contato com nossa equipe pelo suporte.</p>
              </div>
            </div>
          ) : (
            <div style={{ padding:40, textAlign:"center", color:"#aaa" }}>Carregando informações do plano...</div>
          )}
        </div>
      )}

      {/* ─── ABA: Assinatura (Boletos) ─── */}
      {cfgTab === "assinatura" && <AssinaturaTab apiFetch={apiFetch} />}

      {/* ─── ABA: Usuários ─── */}
      {cfgTab === "usuarios" && (
        <div style={cardStyle}>
          {sectionTitle("👥", "Usuários e Permissões")}
          <div style={{ padding:"40px 20px", textAlign:"center", color:"#aaa" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>👥</div>
            <div style={{ fontSize:14, fontWeight:600, color:"#888", marginBottom:6 }}>Em breve</div>
            <div style={{ fontSize:12, color:"#aaa", maxWidth:400, margin:"0 auto", lineHeight:"1.5" }}>
              Gestão de usuários, cargos, permissões de acesso e redefinição de senhas. Essa funcionalidade estará disponível em uma próxima atualização.
            </div>
          </div>
        </div>
      )}
    </div>
    {mapModal && <MapConfirmModal address={[cfg.endereco, cfg.bairro, cfg.cidade, cfg.uf].filter(Boolean).join(", ")} cidadeHint={cfg.cidade} ufHint={cfg.uf} onClose={() => setMapModal(false)} onConfirm={(coords, displayAddr, addrDetails) => { if (addrDetails) { if (addrDetails.cep) set("cep", addrDetails.cep.replace(/\D/g,"").replace(/^(\d{5})(\d{3})$/,"$1-$2")); if (addrDetails.logradouro) { const parts = addrDetails.logradouro.split(","); set("endereco", parts[0].trim()); if (parts[1]) set("numero", parts[1].trim()); } if (addrDetails.bairro) set("bairro", addrDetails.bairro); if (addrDetails.cidade) set("cidade", addrDetails.cidade); if (addrDetails.uf) set("uf", addrDetails.uf); } }} />}
    </>
  );
}

// ─── Orçamentos ──────────────────────────────────────────────────────────────
const ORC_STATUS = [
  { key: "todos",    label: "Todos",    color: "#6b7280" },
  { key: "rascunho", label: "Rascunho", color: "#6b7280" },
  { key: "enviado",  label: "Enviado",  color: "#3b82f6" },
  { key: "aprovado", label: "Aprovado", color: "#10b981" },
  { key: "recusado", label: "Recusado", color: "#ef4444" },
  { key: "vencido",  label: "Vencido",  color: "#f59e0b" },
];
const orcStatusBadge = (s) => {
  const st = ORC_STATUS.find(x => x.key === s) || ORC_STATUS[1];
  return { background: st.color + "18", color: st.color, fontWeight: 700, fontSize: 10, padding: "3px 10px", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".4px", display: "inline-block" };
};

function OrcamentoFormModal({ onClose, onSave, clientes, locacoes = [], tiposCacamba = [], editData, empresaCfg, apiFetch }) {
  const emptyItem = { descricao: "", tipo_cacamba: "", quantidade: 1, qtd_cacambas: 1, valor_unitario: "", valor_total: 0 };
  const [form, setForm] = useState(editData ? {
    ...editData,
    desconto: editData.desconto || 0,
    acrescimo: editData.acrescimo || 0,
    itens: (editData.itens && editData.itens.length) ? editData.itens.map(it => ({ ...it, valor_unitario: it.valor_unitario || "" })) : [{ ...emptyItem }],
  } : {
    numero: "", cliente_id: null, cliente_nome: "", cliente_cpf_cnpj: "", cliente_telefone: "", cliente_email: "", cliente_endereco: "",
    data_emissao: new Date().toISOString().split("T")[0], data_validade: "", status: "rascunho",
    observacoes: "", condicoes: empresaCfg?.condicoes_aluguel || "", subtotal: 0, desconto: 0, acrescimo: 0, total: 0,
    itens: [{ ...emptyItem }],
  });
  const [cliModal, setCliModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Gerar número ao abrir se novo
  useEffect(() => {
    if (!editData && !form.numero) {
      apiFetch("/api/orcamentos/gerar-numero/next").then(d => {
        if (d.numero) setForm(p => ({ ...p, numero: d.numero }));
      }).catch(() => {});
    }
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setItem = (idx, k, v) => {
    setForm(p => {
      const itens = [...p.itens];
      itens[idx] = { ...itens[idx], [k]: v };
      // Recalcular total do item
      const qty = Number(itens[idx].quantidade) || 1;
      const qCac = Number(itens[idx].qtd_cacambas) || 1;
      const vu = Number(itens[idx].valor_unitario) || 0;
      itens[idx].valor_total = qty * qCac * vu;
      return { ...p, itens };
    });
  };
  const addItem = () => setForm(p => ({ ...p, itens: [...p.itens, { ...emptyItem }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, itens: p.itens.filter((_, i) => i !== idx) }));

  // Cálculos
  const subtotal = form.itens.reduce((s, it) => s + (Number(it.valor_total) || 0), 0);
  const total = subtotal - (Number(form.desconto) || 0) + (Number(form.acrescimo) || 0);

  const selectCliente = (c) => {
    let endereco = "";
    const ends = c.enderecos || [];
    if (ends.length > 0) {
      const e = ends[0];
      endereco = [e.logradouro, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(", ");
    } else {
      const locCli = locacoes.find(l => l.cliente === c.nome || l.clienteId === c.id || l.cliente_id === c.id);
      if (locCli) endereco = [locCli.logradouro || locCli.end_logradouro, locCli.bairro || locCli.end_bairro, locCli.cidade || locCli.end_cidade].filter(Boolean).join(", ");
    }
    setForm(p => ({
      ...p,
      cliente_id: c.id,
      cliente_nome: c.nome || "",
      cliente_cpf_cnpj: c.cpf_cnpj || c.cpfCnpj || "",
      cliente_telefone: c.tel || "",
      cliente_email: c.email || "",
      cliente_endereco: endereco,
    }));
    setCliModal(false);
  };

  const salvar = async (asStatus) => {
    if (!form.cliente_nome) return alert("Informe o cliente");
    if (!form.itens.length || !form.itens[0].descricao) return alert("Adicione pelo menos um item");
    setLoading(true);
    try {
      const payload = { ...form, subtotal, total, status: asStatus || form.status, itens: form.itens.map((it, i) => ({ ...it, ordem: i })) };
      const isEdit = !!editData;
      const url = isEdit ? `/api/orcamentos/${editData.id}` : "/api/orcamentos";
      const method = isEdit ? "PUT" : "POST";
      const saved = await apiFetch(url, { method, body: payload });
      onSave(saved, isEdit);
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  const accent = "#1aae9e";
  const inp = { width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #d4d8dd", fontSize: 13, fontFamily: FONT, outline: "none", boxSizing: "border-box" };
  const cardBlock = { background: "#fff", borderRadius: 10, border: "1px solid #e8ecf0", padding: "20px 24px", marginBottom: 16 };
  const blockTitle = (icon, text) => (<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}><span style={{ fontSize: 16 }}>{icon}</span><span style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", fontFamily: FONT }}>{text}</span></div>);

  return (
    <LargeModal title={editData ? "Editar Orçamento" : "Novo Orçamento"} subtitle={form.numero || "Gerando número..."} onClose={onClose} maxWidth={920} onSave={() => salvar()} saveLabel={loading ? "Salvando..." : "Salvar Orçamento"} extraFooter={!editData && <button onClick={() => salvar("rascunho")} style={{ padding: "9px 18px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Salvar como Rascunho</button>}>

      {/* Dados do Cliente */}
      <div style={cardBlock}>
        {blockTitle("", "Dados do Cliente")}
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <button onClick={() => setCliModal(true)} style={{ padding: "8px 16px", background: accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Selecionar Cliente Cadastrado</button>
          {form.cliente_nome && <span style={{ fontSize: 12, color: "#10b981", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>✓ {form.cliente_nome}</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Nome / Razão Social *"><input style={inp} value={form.cliente_nome} onChange={e => set("cliente_nome", e.target.value)} placeholder="Nome do cliente" /></Field>
          <Field label="CPF / CNPJ"><input style={inp} value={form.cliente_cpf_cnpj} onChange={e => set("cliente_cpf_cnpj", formatCpfCnpj(e.target.value.replace(/\D/g,"").slice(0,14)))} placeholder="000.000.000-00" /></Field>
          <Field label="Telefone"><input style={inp} value={form.cliente_telefone} onChange={e => set("cliente_telefone", formatPhone(e.target.value))} placeholder="(11) 99999-9999" /></Field>
          <Field label="E-mail"><input style={inp} value={form.cliente_email} onChange={e => set("cliente_email", e.target.value)} placeholder="email@exemplo.com" /></Field>
          <div style={{ gridColumn: "1 / -1" }}><Field label="Endereço"><input style={inp} value={form.cliente_endereco} onChange={e => set("cliente_endereco", e.target.value)} placeholder="Rua, número, bairro, cidade" /></Field></div>
        </div>
      </div>

      {/* Dados do Orçamento */}
      <div style={cardBlock}>
        {blockTitle("", "Dados do Orçamento")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <Field label="Número"><input style={{ ...inp, background: "#f9fafb" }} value={form.numero} readOnly /></Field>
          <Field label="Data Emissão"><input style={inp} type="date" value={form.data_emissao} onChange={e => set("data_emissao", e.target.value)} /></Field>
          <Field label="Validade"><input style={inp} type="date" value={form.data_validade} onChange={e => set("data_validade", e.target.value)} /></Field>
          <Field label="Status">
            <select style={{ ...inp, appearance: "none" }} value={form.status} onChange={e => set("status", e.target.value)}>
              {ORC_STATUS.filter(s => s.key !== "todos").map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Condições Comerciais"><textarea style={{ ...inp, minHeight: 60, resize: "vertical" }} value={form.condicoes} onChange={e => set("condicoes", e.target.value)} placeholder="Condições de pagamento, prazos, etc." /></Field>
        </div>
      </div>

      {/* Itens do Orçamento */}
      <div style={cardBlock}>
        {blockTitle("", "Itens do Orçamento")}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: FONT }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
              {["Descrição","Tipo Caçamba","Qtd","Caçambas","Valor Unit. (R$)","Total (R$)",""].map(h => <th key={h} style={{ textAlign: "left", padding: "8px 8px", fontSize: 10, color: "#6b7280", fontWeight: 700, textTransform: "uppercase" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {form.itens.map((it, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "6px 4px", minWidth: 180 }}><input style={{ ...inp, padding: "8px 10px" }} value={it.descricao} onChange={e => setItem(idx, "descricao", e.target.value)} placeholder="Ex: Locação de caçamba" /></td>
                <td style={{ padding: "6px 4px", minWidth: 110 }}>
                  <select style={{ ...inp, padding: "8px 10px", appearance: "none" }} value={it.tipo_cacamba} onChange={e => setItem(idx, "tipo_cacamba", e.target.value)}>
                    <option value="">Selecionar</option>
                    {tiposCacamba.map(t => <option key={t.id} value={t.nome}>{t.nome}{t.cubicagem ? " (" + t.cubicagem.replace(/m3/gi,"").trim() + " m³)" : ""}</option>)}
                  </select>
                </td>
                <td style={{ padding: "6px 4px", width: 70 }}><input style={{ ...inp, padding: "8px 10px", textAlign: "center" }} type="number" min="1" value={it.quantidade} onChange={e => setItem(idx, "quantidade", e.target.value)} /></td>
                <td style={{ padding: "6px 4px", width: 80 }}><input style={{ ...inp, padding: "8px 10px", textAlign: "center" }} type="number" min="1" value={it.qtd_cacambas} onChange={e => setItem(idx, "qtd_cacambas", e.target.value)} /></td>
                <td style={{ padding: "6px 4px", width: 110 }}><input style={{ ...inp, padding: "8px 10px", textAlign: "right" }} type="number" step="0.01" min="0" value={it.valor_unitario} onChange={e => setItem(idx, "valor_unitario", e.target.value)} placeholder="0,00" /></td>
                <td style={{ padding: "6px 8px", width: 100, fontWeight: 700, color: accent, textAlign: "right" }}>{fmt(Number(it.valor_total) || 0)}</td>
                <td style={{ padding: "6px 4px", width: 36 }}>
                  {form.itens.length > 1 && <button onClick={() => removeItem(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16 }}>✕</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addItem} style={{ marginTop: 10, padding: "8px 16px", background: "#f3f4f6", color: "#374151", border: "1px dashed #d1d5db", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT, width: "100%" }}>+ Adicionar Item</button>
      </div>

      {/* Resumo Financeiro */}
      <div style={{ ...cardBlock, background: "linear-gradient(135deg, #f8fffe 0%, #fff 100%)", borderLeft: `4px solid ${accent}` }}>
        {blockTitle("", "Resumo Financeiro")}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Field label="Desconto (R$)"><input style={{ ...inp, textAlign: "right" }} type="number" step="0.01" min="0" value={form.desconto} onChange={e => set("desconto", e.target.value)} placeholder="0,00" /></Field>
          <Field label="Acréscimo (R$)"><input style={{ ...inp, textAlign: "right" }} type="number" step="0.01" min="0" value={form.acrescimo} onChange={e => set("acrescimo", e.target.value)} placeholder="0,00" /></Field>
          <div />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 28, padding: "14px 0", borderTop: "2px solid #e5e7eb" }}>
          <div style={{ textAlign: "right" }}><div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase" }}>Subtotal</div><div style={{ fontSize: 16, fontWeight: 700, color: "#374151" }}>{fmt(subtotal)}</div></div>
          {Number(form.desconto) > 0 && <div style={{ textAlign: "right" }}><div style={{ fontSize: 10, color: "#ef4444", fontWeight: 600, textTransform: "uppercase" }}>Desconto</div><div style={{ fontSize: 16, fontWeight: 700, color: "#ef4444" }}>- {fmt(Number(form.desconto))}</div></div>}
          {Number(form.acrescimo) > 0 && <div style={{ textAlign: "right" }}><div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600, textTransform: "uppercase" }}>Acréscimo</div><div style={{ fontSize: 16, fontWeight: 700, color: "#f59e0b" }}>+ {fmt(Number(form.acrescimo))}</div></div>}
          <div style={{ textAlign: "right", padding: "8px 16px", background: "#eef9f7", borderRadius: 8 }}><div style={{ fontSize: 10, color: accent, fontWeight: 700, textTransform: "uppercase" }}>Total</div><div style={{ fontSize: 22, fontWeight: 900, color: accent }}>{fmt(total)}</div></div>
        </div>
      </div>

      {/* Observações */}
      <div style={cardBlock}>
        {blockTitle("", "Observações")}
        <textarea style={{ ...inp, minHeight: 70, resize: "vertical" }} value={form.observacoes} onChange={e => set("observacoes", e.target.value)} placeholder="Observações adicionais sobre o orçamento..." />
      </div>

      {cliModal && <ClienteSelectorModal clientes={clientes} locacoes={locacoes} onSelect={selectCliente} onClose={() => setCliModal(false)} />}
    </LargeModal>
  );
}

function gerarOrcamentoPDF(orc, empresaCfg, opts = {}) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  const pw = 210, ph = 297, mg = 15;
  const uw = pw - 2 * mg;
  // Cores customizáveis (opts.accent / opts.headerBg). Padrão = identidade Controla.
  const accent = opts.accent || [26, 174, 158];
  const headerBg = opts.headerBg || [13, 27, 42];
  const logoDataUrl = opts.logoDataUrl || null;
  let y = mg;

  // Header — faixa com dados da empresa
  doc.setFillColor(headerBg[0], headerBg[1], headerBg[2]);
  doc.rect(0, 0, pw, 38, "F");
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(0, 38, pw, 2, "F");

  // Logo (se incluída) à esquerda + nome ao lado
  const nomeEmpresa = empresaCfg?.nome || "Empresa";
  let textX = mg;
  if (logoDataUrl) {
    try {
      const fmtImg = logoDataUrl.includes("image/png") ? "PNG" : "JPEG";
      doc.addImage(logoDataUrl, fmtImg, mg, 7, 24, 24, undefined, "FAST");
      textX = mg + 28;
    } catch (e) { /* se falhar, segue sem logo */ }
  }
  doc.setFontSize(16); doc.setTextColor(255, 255, 255); doc.setFont(undefined, "bold");
  doc.text(nomeEmpresa.toUpperCase(), textX, 16);
  doc.setFontSize(8); doc.setFont(undefined, "normal"); doc.setTextColor(180, 200, 210);
  const infoLine = [empresaCfg?.cnpj, empresaCfg?.telefone, empresaCfg?.email].filter(Boolean).join("  |  ");
  if (infoLine) doc.text(infoLine, textX, 23);
  const endLine = [empresaCfg?.endereco, empresaCfg?.bairro, empresaCfg?.cidade, empresaCfg?.uf].filter(Boolean).join(", ");
  if (endLine) doc.text(endLine, textX, 29);

  // Badge "ORÇAMENTO"
  doc.setFontSize(12); doc.setTextColor(255, 255, 255); doc.setFont(undefined, "bold");
  doc.text("ORÇAMENTO", pw - mg, 16, { align: "right" });
  doc.setFontSize(9); doc.setFont(undefined, "normal");
  doc.text(orc.numero || "", pw - mg, 23, { align: "right" });
  const statusLabel = (ORC_STATUS.find(s => s.key === orc.status) || {}).label || orc.status;
  doc.text(statusLabel.toUpperCase(), pw - mg, 30, { align: "right" });

  y = 48;

  // Dados cliente e orçamento lado a lado
  doc.setFontSize(9); doc.setFont(undefined, "bold"); doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text("DADOS DO CLIENTE", mg, y);
  doc.text("DADOS DO ORÇAMENTO", pw / 2 + 5, y);
  y += 6;

  doc.setFont(undefined, "normal"); doc.setTextColor(50, 50, 50); doc.setFontSize(8.5);
  const cliLines = [
    orc.cliente_nome,
    orc.cliente_cpf_cnpj ? "CPF/CNPJ: " + orc.cliente_cpf_cnpj : null,
    orc.cliente_telefone ? "Tel: " + orc.cliente_telefone : null,
    orc.cliente_email || null,
    orc.cliente_endereco || null,
  ].filter(Boolean);
  cliLines.forEach(l => { doc.text(l, mg, y); y += 4.5; });

  let yRight = 48 + 6;
  const orcLines = [
    "Número: " + (orc.numero || ""),
    "Emissão: " + (orc.data_emissao ? orc.data_emissao.split("-").reverse().join("/") : ""),
    orc.data_validade ? "Validade: " + orc.data_validade.split("-").reverse().join("/") : null,
  ].filter(Boolean);
  orcLines.forEach(l => { doc.text(l, pw / 2 + 5, yRight); yRight += 4.5; });

  y = Math.max(y, yRight) + 6;

  // Linha separadora
  doc.setDrawColor(accent[0], accent[1], accent[2]); doc.setLineWidth(0.4);
  doc.line(mg, y, mg + uw, y); y += 6;

  // Tabela de itens
  doc.setFontSize(9); doc.setFont(undefined, "bold"); doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text("ITENS DO ORÇAMENTO", mg, y); y += 6;

  // Header tabela
  const cols = [
    { x: mg, w: 60, label: "Descrição" },
    { x: mg + 60, w: 30, label: "Tipo" },
    { x: mg + 90, w: 15, label: "Qtd" },
    { x: mg + 105, w: 20, label: "Caçambas" },
    { x: mg + 125, w: 25, label: "Unit. (R$)" },
    { x: mg + 150, w: 30, label: "Total (R$)" },
  ];

  doc.setFillColor(240, 242, 245); doc.rect(mg, y - 3.5, uw, 6, "F");
  doc.setFontSize(7); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
  cols.forEach(c => doc.text(c.label, c.x + 1, y));
  y += 5;

  doc.setFont(undefined, "normal"); doc.setTextColor(50, 50, 50); doc.setFontSize(8);
  const itens = orc.itens || [];
  itens.forEach((it, idx) => {
    if (y > ph - 40) { doc.addPage(); y = mg + 10; }
    if (idx % 2 === 0) { doc.setFillColor(250, 251, 252); doc.rect(mg, y - 3.5, uw, 6, "F"); }
    doc.text(String(it.descricao || "").substring(0, 40), cols[0].x + 1, y);
    doc.text(String(it.tipo_cacamba || "-"), cols[1].x + 1, y);
    doc.text(String(it.quantidade || 1), cols[2].x + 1, y);
    doc.text(String(it.qtd_cacambas || 1), cols[3].x + 1, y);
    doc.text(Number(it.valor_unitario || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 }), cols[4].x + 1, y);
    doc.setFont(undefined, "bold");
    doc.text(Number(it.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 }), cols[5].x + 1, y);
    doc.setFont(undefined, "normal");
    y += 6;
  });

  y += 4;
  doc.setDrawColor(220, 220, 220); doc.line(mg, y, mg + uw, y); y += 6;

  // Resumo financeiro
  const sub = Number(orc.subtotal) || 0;
  const desc = Number(orc.desconto) || 0;
  const acre = Number(orc.acrescimo) || 0;
  const tot = Number(orc.total) || 0;
  const rx = pw - mg;

  doc.setFontSize(8.5); doc.setTextColor(100, 100, 100);
  doc.text("Subtotal:", rx - 40, y); doc.text("R$ " + sub.toLocaleString("pt-BR", { minimumFractionDigits: 2 }), rx, y, { align: "right" }); y += 5;
  if (desc > 0) { doc.setTextColor(220, 50, 50); doc.text("Desconto:", rx - 40, y); doc.text("- R$ " + desc.toLocaleString("pt-BR", { minimumFractionDigits: 2 }), rx, y, { align: "right" }); y += 5; }
  if (acre > 0) { doc.setTextColor(200, 150, 0); doc.text("Acréscimo:", rx - 40, y); doc.text("+ R$ " + acre.toLocaleString("pt-BR", { minimumFractionDigits: 2 }), rx, y, { align: "right" }); y += 5; }
  doc.setFillColor(accent[0], accent[1], accent[2]); doc.rect(rx - 55, y - 4, 55, 8, "F");
  doc.setFontSize(10); doc.setFont(undefined, "bold"); doc.setTextColor(255, 255, 255);
  doc.text("TOTAL: R$ " + tot.toLocaleString("pt-BR", { minimumFractionDigits: 2 }), rx - 2, y + 1, { align: "right" });
  y += 14;

  // Condições
  if (orc.condicoes) {
    if (y > ph - 50) { doc.addPage(); y = mg + 10; }
    doc.setFontSize(9); doc.setFont(undefined, "bold"); doc.setTextColor(accent[0], accent[1], accent[2]);
    doc.text("CONDIÇÕES COMERCIAIS", mg, y); y += 5;
    doc.setFontSize(7.5); doc.setFont(undefined, "normal"); doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(orc.condicoes, uw);
    doc.text(lines, mg, y); y += lines.length * 3.5 + 4;
  }

  // Observações
  if (orc.observacoes) {
    if (y > ph - 40) { doc.addPage(); y = mg + 10; }
    doc.setFontSize(9); doc.setFont(undefined, "bold"); doc.setTextColor(accent[0], accent[1], accent[2]);
    doc.text("OBSERVAÇÕES", mg, y); y += 5;
    doc.setFontSize(7.5); doc.setFont(undefined, "normal"); doc.setTextColor(80, 80, 80);
    const lines = doc.splitTextToSize(orc.observacoes, uw);
    doc.text(lines, mg, y); y += lines.length * 3.5 + 4;
  }

  // Rodapé
  const fy = ph - 12;
  doc.setDrawColor(accent[0], accent[1], accent[2]); doc.setLineWidth(0.3);
  doc.line(mg, fy - 2, mg + uw, fy - 2);
  doc.setFontSize(6.5); doc.setTextColor(150, 150, 150); doc.setFont(undefined, "normal");
  doc.text(nomeEmpresa + (empresaCfg?.cnpj ? " — CNPJ: " + empresaCfg.cnpj : ""), pw / 2, fy + 1, { align: "center" });
  doc.text([empresaCfg?.telefone, empresaCfg?.email].filter(Boolean).join(" | "), pw / 2, fy + 4.5, { align: "center" });
  doc.text("Documento gerado pelo sistema Controla Caçambas — www.controlacacambas.com.br", pw / 2, fy + 8, { align: "center" });

  doc.save(`orcamento_${orc.numero || "novo"}.pdf`);
}

function OrcamentosPage({ clientes, locacoes = [], tiposCacamba = [], addToast, apiFetch, empresaCfg }) {
  const [orcamentos, setOrcamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [periodoIni, setPeriodoIni] = useState("");
  const [periodoFim, setPeriodoFim] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pdfModal, setPdfModal] = useState(null); // { orc } — personalização antes de gerar
  const [gerandoPdf, setGerandoPdf] = useState(false);
  // Cor preferida persiste entre sessões
  const [corOrcamento, setCorOrcamento] = useState(() => {
    try { return localStorage.getItem("controla_orcamento_cor") || "#1aae9e"; } catch(_) { return "#1aae9e"; }
  });
  const [incluirLogo, setIncluirLogo] = useState(() => {
    try { return localStorage.getItem("controla_orcamento_logo") !== "0"; } catch(_) { return true; }
  });

  // Converte "#1aae9e" → [26,174,158]
  const hexToRgb = (hex) => {
    const h = String(hex || "").replace("#", "");
    if (h.length !== 6) return [26, 174, 158];
    return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
  };
  // Escurece uma cor (pra usar como header bg) — multiplica por fator
  const escurecer = (rgb, fator = 0.30) => rgb.map(c => Math.max(0, Math.round(c * fator)));
  // Carrega a logo da empresa como dataURL (autenticado)
  const carregarLogoDataUrl = async () => {
    if (!empresaCfg?.logo_url) return null;
    try {
      const API = process.env.REACT_APP_API || "http://localhost:3001";
      const token = sessionStorage.getItem("controla_token");
      const url = empresaCfg.logo_url.startsWith("/api/") ? API + empresaCfg.logo_url : API + "/api/files/" + empresaCfg.logo_url;
      const r = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (!r.ok) return null;
      const blob = await r.blob();
      return await new Promise((resolve) => { const rd = new FileReader(); rd.onload = () => resolve(rd.result); rd.onerror = () => resolve(null); rd.readAsDataURL(blob); });
    } catch (e) { return null; }
  };

  const loadOrcamentos = async () => {
    try {
      const data = await apiFetch("/api/orcamentos");
      setOrcamentos(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { loadOrcamentos(); }, []);

  const filtered = orcamentos.filter(o => {
    if (statusFilter !== "todos" && o.status !== statusFilter) return false;
    if (search.trim()) {
      const s = norm(search);
      if (!norm(o.numero).includes(s) && !norm(o.cliente_nome).includes(s)) return false;
    }
    if (periodoIni && o.data_emissao < periodoIni) return false;
    if (periodoFim && o.data_emissao > periodoFim) return false;
    return true;
  });

  const onSave = (saved, isEdit) => {
    if (isEdit) setOrcamentos(p => p.map(o => o.id === saved.id ? saved : o));
    else setOrcamentos(p => [saved, ...p]);
    setFormModal(false); setEditData(null);
    addToast(isEdit ? "Orçamento atualizado!" : "Orçamento criado!", "success");
  };

  const duplicar = async (id) => {
    try {
      const d = await apiFetch(`/api/orcamentos/${id}/duplicar`, { method: "POST" });
      setOrcamentos(p => [d, ...p]); addToast("Orçamento duplicado!", "success");
    } catch (e) { addToast("Erro ao duplicar", "error"); }
    setMenuOpen(null);
  };

  const mudarStatus = async (id, status) => {
    try {
      await apiFetch(`/api/orcamentos/${id}/status`, { method: "PATCH", body: { status } });
      setOrcamentos(p => p.map(o => o.id === id ? { ...o, status } : o));
      addToast("Status atualizado!", "success");
    } catch (e) { addToast("Erro", "error"); }
  };

  const abrirEditar = async (id) => {
    try {
      const d = await apiFetch(`/api/orcamentos/${id}`);
      setEditData(d); setFormModal(true);
    } catch (e) { addToast("Erro ao carregar", "error"); }
    setMenuOpen(null);
  };

  const excluirOrcamento = async (id) => {
    try {
      await apiFetch(`/api/orcamentos/${id}`, { method: "DELETE" });
      setOrcamentos(p => p.filter(o => o.id !== id));
      addToast("Orçamento excluído!", "success");
    } catch (e) { addToast("Erro ao excluir", "error"); }
    setConfirmDel(null);
  };

  const gerarPDF = async (id) => {
    try {
      const d = await apiFetch(`/api/orcamentos/${id}`);
      setPdfModal({ orc: d });   // abre o popup de personalização
    } catch (e) { addToast("Erro ao carregar orçamento", "error"); }
    setMenuOpen(null);
  };

  const confirmarGerarPDF = async () => {
    if (!pdfModal?.orc) return;
    setGerandoPdf(true);
    try {
      // Salva preferências
      try { localStorage.setItem("controla_orcamento_cor", corOrcamento); localStorage.setItem("controla_orcamento_logo", incluirLogo ? "1" : "0"); } catch(_) {}
      const accent = hexToRgb(corOrcamento);
      const headerBg = escurecer(accent, 0.28);
      let logoDataUrl = null;
      if (incluirLogo) logoDataUrl = await carregarLogoDataUrl();
      gerarOrcamentoPDF(pdfModal.orc, empresaCfg, { accent, headerBg, logoDataUrl });
      setPdfModal(null);
    } catch (e) {
      addToast("Erro ao gerar PDF: " + (e?.message || ""), "error");
    }
    setGerandoPdf(false);
  };

  const accent = "#1aae9e";
  const totalAprovados = orcamentos.filter(o => o.status === "aprovado").reduce((s, o) => s + Number(o.total || 0), 0);
  const totalPendentes = orcamentos.filter(o => ["rascunho","enviado"].includes(o.status)).reduce((s, o) => s + Number(o.total || 0), 0);

  return (
    <div>
      {/* Cards resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        {[
          { label: "Total de Orçamentos", value: orcamentos.length, color: "#6366f1", icon: "📋" },
          { label: "Aprovados", value: orcamentos.filter(o => o.status === "aprovado").length, color: "#10b981", icon: "✅" },
          { label: "Valor Aprovado", value: fmt(totalAprovados), color: accent, icon: "💰" },
          { label: "Valor Pendente", value: fmt(totalPendentes), color: "#f59e0b", icon: "⏳" },
        ].map((c, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8ecf0", padding: "16px 20px", boxShadow: "0 1px 6px rgba(0,0,0,.03)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".3px", fontFamily: FONT }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c.color, fontFamily: FONT, marginTop: 4 }}>{c.value}</div>
              </div>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: c.color + "14", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{c.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros e busca */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8ecf0", padding: "14px 20px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button onClick={() => { setEditData(null); setFormModal(true); }} style={{ padding: "9px 18px", background: accent, color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: FONT, boxShadow: "0 2px 8px rgba(26,174,158,.25)" }}>+ Novo Orçamento</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <input style={{ width: "100%", padding: "9px 14px", borderRadius: 6, border: "1px solid #d4d8dd", fontSize: 12, fontFamily: FONT, outline: "none", boxSizing: "border-box" }} placeholder="Buscar por número ou cliente..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {ORC_STATUS.map(s => (
            <button key={s.key} onClick={() => setStatusFilter(s.key)} style={{ padding: "6px 12px", borderRadius: 5, border: `1.5px solid ${statusFilter === s.key ? s.color : "#e5e7eb"}`, background: statusFilter === s.key ? s.color + "14" : "#fff", color: statusFilter === s.key ? s.color : "#888", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>{s.label}</button>
          ))}
        </div>
        <input style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #d4d8dd", fontSize: 11, fontFamily: FONT, width: 120 }} type="date" value={periodoIni} onChange={e => setPeriodoIni(e.target.value)} />
        <span style={{ fontSize: 11, color: "#aaa" }}>até</span>
        <input style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #d4d8dd", fontSize: 11, fontFamily: FONT, width: 120 }} type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} />
      </div>

      {/* Tabela */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e8ecf0", overflow: "visible" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT }}>
          <thead>
            <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb", borderRadius: "10px 10px 0 0" }}>
              {["Número","Cliente","Emissão","Validade","Itens","Valor Total","Status","Ações"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "11px 14px", fontSize: 10, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".4px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>Carregando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Nenhum orçamento encontrado</div>
                <div style={{ fontSize: 11, color: "#bbb", marginTop: 4 }}>Crie seu primeiro orçamento clicando no botão acima</div>
              </td></tr>
            ) : filtered.map(o => (
              <tr key={o.id} style={{ borderBottom: "1px solid #f3f4f6", cursor: "pointer" }} onDoubleClick={() => abrirEditar(o.id)}>
                <td style={{ padding: "12px 14px", fontWeight: 700, color: accent }}>{o.numero}</td>
                <td style={{ padding: "12px 14px", fontWeight: 500 }}>{o.cliente_nome || "—"}</td>
                <td style={{ padding: "12px 14px", color: "#6b7280" }}>{o.data_emissao ? o.data_emissao.split("-").reverse().join("/") : "—"}</td>
                <td style={{ padding: "12px 14px", color: "#6b7280" }}>{o.data_validade ? o.data_validade.split("-").reverse().join("/") : "—"}</td>
                <td style={{ padding: "12px 14px", color: "#6b7280" }}>{o.qtd_itens || 0}</td>
                <td style={{ padding: "12px 14px", fontWeight: 700 }}>{fmt(Number(o.total || 0))}</td>
                <td style={{ padding: "12px 14px" }}><span style={orcStatusBadge(o.status)}>{(ORC_STATUS.find(s => s.key === o.status)||{}).label || o.status}</span></td>
                <td style={{ padding: "12px 14px", position: "relative" }}>
                  <button onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === o.id ? null : o.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 18, padding: "2px 6px" }}>⋮</button>
                  {menuOpen === o.id && (
                    <div style={{ position: "absolute", right: 14, bottom: "100%", marginBottom: 4, background: "#fff", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,.15)", border: "1px solid #e8ecf0", zIndex: 999, minWidth: 170, overflow: "hidden" }} onClick={() => setMenuOpen(null)}>
                      {[
                        { label: "Editar", icon: "✏️", action: () => abrirEditar(o.id) },
                        { label: "Gerar PDF", icon: "📄", action: () => gerarPDF(o.id) },
                        { label: "Duplicar", icon: "📋", action: () => duplicar(o.id) },
                      ].map((a, i) => (
                        <button key={i} onClick={a.action} style={{ width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: 8, fontFamily: FONT, borderBottom: "1px solid #f3f4f6" }}>{a.icon} {a.label}</button>
                      ))}
                      <div style={{ padding: "6px 16px", fontSize: 10, color: "#aaa", fontWeight: 600, textTransform: "uppercase" }}>Alterar status</div>
                      {ORC_STATUS.filter(s => s.key !== "todos" && s.key !== o.status).map(s => (
                        <button key={s.key} onClick={() => { mudarStatus(o.id, s.key); setMenuOpen(null); }} style={{ width: "100%", textAlign: "left", padding: "8px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, color: s.color, display: "flex", alignItems: "center", gap: 6, fontFamily: FONT }}>
                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color }} /> {s.label}
                        </button>
                      ))}
                      <div style={{ borderTop: "1px solid #fee2e2", marginTop: 4 }}>
                        <button onClick={() => { setConfirmDel(o); setMenuOpen(null); }} style={{ width: "100%", textAlign: "left", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#ef4444", display: "flex", alignItems: "center", gap: 8, fontFamily: FONT }}>Excluir</button>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {formModal && <OrcamentoFormModal onClose={() => { setFormModal(false); setEditData(null); }} onSave={onSave} clientes={clientes} locacoes={locacoes} tiposCacamba={tiposCacamba} editData={editData} empresaCfg={empresaCfg} apiFetch={apiFetch} />}

      {/* Modal de personalização do PDF do orçamento */}
      {pdfModal && (() => {
        const presets = [
          { nome: "Teal (padrão)", cor: "#1aae9e" },
          { nome: "Azul", cor: "#2563eb" },
          { nome: "Verde", cor: "#16a34a" },
          { nome: "Roxo", cor: "#7c3aed" },
          { nome: "Laranja", cor: "#ea580c" },
          { nome: "Vermelho", cor: "#dc2626" },
          { nome: "Petróleo", cor: "#0f766e" },
          { nome: "Grafite", cor: "#334155" },
        ];
        const previewHeader = (() => { const a = hexToRgb(corOrcamento); const h = escurecer(a, 0.28); return `rgb(${h[0]},${h[1]},${h[2]})`; })();
        return (
          <div onClick={() => !gerandoPdf && setPdfModal(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center", padding:20, fontFamily:FONT }}>
            <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:16, maxWidth:480, width:"100%", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,.3)" }}>
              {/* Header do modal */}
              <div style={{ padding:"20px 24px", borderBottom:"1px solid #eee" }}>
                <div style={{ fontSize:16, fontWeight:800, color:"#1e2d4f" }}>🎨 Personalizar Orçamento</div>
                <div style={{ fontSize:12, color:"#888", marginTop:3 }}>Escolha a cor e se inclui a logo antes de gerar o PDF.</div>
              </div>

              <div style={{ padding:"20px 24px" }}>
                {/* Preview da faixa do header */}
                <div style={{ borderRadius:10, overflow:"hidden", border:"1px solid #e8e8e8", marginBottom:18 }}>
                  <div style={{ background: previewHeader, padding:"14px 16px", display:"flex", alignItems:"center", gap:10 }}>
                    {incluirLogo && empresaCfg?.logo_url
                      ? <div style={{ width:32, height:32, borderRadius:4, background:"rgba(255,255,255,.9)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, color:"#888", fontWeight:700 }}>LOGO</div>
                      : null}
                    <div>
                      <div style={{ color:"#fff", fontWeight:800, fontSize:14 }}>{(empresaCfg?.nome || "Empresa").toUpperCase()}</div>
                      <div style={{ color:"rgba(255,255,255,.7)", fontSize:9 }}>{empresaCfg?.telefone || "—"}</div>
                    </div>
                    <div style={{ marginLeft:"auto", color:"#fff", fontWeight:800, fontSize:12 }}>ORÇAMENTO</div>
                  </div>
                  <div style={{ height:4, background: corOrcamento }} />
                  <div style={{ padding:"10px 16px", fontSize:11, color:"#999", background:"#fafafa" }}>Pré-visualização do cabeçalho</div>
                </div>

                {/* Presets de cor */}
                <div style={{ fontSize:12, fontWeight:700, color:"#555", marginBottom:8 }}>Cor do orçamento</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8, marginBottom:14 }}>
                  {presets.map(p => (
                    <button key={p.cor} onClick={() => setCorOrcamento(p.cor)} title={p.nome}
                      style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:5, padding:"8px 4px", borderRadius:8, cursor:"pointer", background: corOrcamento===p.cor ? "#f0faf8" : "#fff", border: corOrcamento===p.cor ? "2px solid "+p.cor : "1px solid #e5e5e5", fontFamily:FONT }}>
                      <span style={{ width:26, height:26, borderRadius:"50%", background:p.cor, border:"2px solid #fff", boxShadow:"0 0 0 1px #ddd" }} />
                      <span style={{ fontSize:9, color:"#666", fontWeight:600 }}>{p.nome}</span>
                    </button>
                  ))}
                </div>

                {/* Color picker custom */}
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
                  <span style={{ fontSize:12, color:"#666" }}>Cor personalizada:</span>
                  <input type="color" value={corOrcamento} onChange={e => setCorOrcamento(e.target.value)} style={{ width:42, height:30, border:"1px solid #ddd", borderRadius:6, cursor:"pointer", padding:2 }} />
                  <span style={{ fontSize:12, fontFamily:"monospace", color:"#888" }}>{corOrcamento}</span>
                </div>

                {/* Toggle logo */}
                <label style={{ display:"flex", alignItems:"center", gap:10, cursor: empresaCfg?.logo_url ? "pointer" : "not-allowed", padding:"10px 12px", background:"#f8f9fa", borderRadius:8, opacity: empresaCfg?.logo_url ? 1 : 0.55 }}>
                  <input type="checkbox" checked={incluirLogo && !!empresaCfg?.logo_url} disabled={!empresaCfg?.logo_url} onChange={e => setIncluirLogo(e.target.checked)} style={{ width:16, height:16, accentColor:"#1aae9e", cursor:"pointer" }} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:"#1e2d4f" }}>Incluir logo da empresa</div>
                    <div style={{ fontSize:11, color:"#888", marginTop:1 }}>{empresaCfg?.logo_url ? "A logo aparece no cabeçalho do orçamento" : "Nenhuma logo cadastrada (Configurações → Logo)"}</div>
                  </div>
                </label>
              </div>

              {/* Footer */}
              <div style={{ padding:"16px 24px", borderTop:"1px solid #eee", display:"flex", justifyContent:"flex-end", gap:10 }}>
                <button onClick={() => setPdfModal(null)} disabled={gerandoPdf} style={{ padding:"10px 18px", border:"1px solid #ddd", background:"#fff", borderRadius:8, fontSize:13, cursor:"pointer", color:"#666", fontFamily:FONT }}>Cancelar</button>
                <button onClick={confirmarGerarPDF} disabled={gerandoPdf} style={{ padding:"10px 24px", border:"none", background: corOrcamento, color:"#fff", borderRadius:8, fontSize:13, fontWeight:700, cursor: gerandoPdf?"wait":"pointer", fontFamily:FONT, opacity: gerandoPdf?0.7:1 }}>
                  {gerandoPdf ? "Gerando..." : "📄 Gerar PDF"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal confirmar exclusão */}
      {confirmDel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,.2)" }}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fef2f2", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: 12 }}>🗑️</div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e293b", fontFamily: FONT }}>Excluir orçamento?</h3>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "#6b7280", fontFamily: FONT, lineHeight: 1.4 }}>
                Tem certeza que deseja excluir o orçamento <strong style={{ color: "#374151" }}>{confirmDel.numero}</strong> do cliente <strong style={{ color: "#374151" }}>{confirmDel.cliente_nome}</strong>? Esta ação não pode ser desfeita.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
              <button onClick={() => setConfirmDel(null)} style={{ padding: "10px 24px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Cancelar</button>
              <button onClick={() => excluirOrcamento(confirmDel.id)} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Sim, excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calculadora de Caçambas ──────────────────────────────────────────────────
function CalculadoraCacambas({ tiposCacamba = [] }) {
  const [largura, setLargura] = useState("");
  const [altura, setAltura] = useState("");
  const [profundidade, setProfundidade] = useState("");
  const [cacambaSel, setCacambaSel] = useState("");
  const [resultado, setResultado] = useState(null);
  const [animating, setAnimating] = useState(false);

  // Montar lista de caçambas disponíveis a partir dos tipos cadastrados
  const listaCacambas = (() => {
    const tiposComVolume = tiposCacamba.filter(t => t.cubicagem).map(t => {
      const n = parseFloat(String(t.cubicagem).replace(/[^\d.,]/g,"").replace(",","."));
      return { nome: t.nome, cap: n || 0, desc: t.descricao || "" };
    }).filter(t => t.cap > 0).sort((a,b) => a.cap - b.cap);
    if (tiposComVolume.length >= 1) return tiposComVolume;
    return [
      { nome: "3 m³", cap: 3, desc: "Pequenas reformas, limpeza de quintal" },
      { nome: "5 m³", cap: 5, desc: "Reformas residenciais, demolições leves" },
      { nome: "7 m³", cap: 7, desc: "Obras médias, grandes reformas" },
      { nome: "10 m³", cap: 10, desc: "Grandes obras, demolições" },
    ];
  })();

  const cacambaAtual = listaCacambas.find(c => c.nome === cacambaSel) || null;

  const calcular = () => {
    const l = parseFloat(largura), a = parseFloat(altura), p = parseFloat(profundidade);
    if (!l || !a || !p || l <= 0 || a <= 0 || p <= 0 || !cacambaAtual) return;
    const vol = l * a * p;
    setAnimating(true);
    setTimeout(() => {
      setResultado(vol);
      setAnimating(false);
    }, 400);
  };

  const limpar = () => { setLargura(""); setAltura(""); setProfundidade(""); setResultado(null); };

  const qtdCacambas = resultado && cacambaAtual ? Math.ceil(resultado / cacambaAtual.cap) : 0;
  const ocupacaoUltima = resultado && cacambaAtual ? ((resultado % cacambaAtual.cap) || cacambaAtual.cap) / cacambaAtual.cap * 100 : 0;

  const accent = "#1aae9e";
  const card = { background: "#fff", borderRadius: 12, border: "1px solid #e8ecf0", boxShadow: "0 2px 12px rgba(0,0,0,.04)" };
  const inp = { width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid #d4d8dd", fontSize: 15, fontFamily: FONT, outline: "none", boxSizing: "border-box", transition: "border-color .2s, box-shadow .2s" };
  const label = { display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6 };

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ ...card, padding: "28px 32px", marginBottom: 20, background: "linear-gradient(135deg, #0d1b2a 0%, #1b2d45 50%, #1a3a4a 100%)", border: "none", color: "#fff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 180, height: 180, background: "radial-gradient(circle, rgba(26,174,158,.15) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "absolute", bottom: -20, left: "30%", width: 120, height: 120, background: "radial-gradient(circle, rgba(26,174,158,.08) 0%, transparent 70%)", borderRadius: "50%" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(26,174,158,.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4ecdc4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="18" x2="16" y2="18"/></svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: FONT }}>Calculadora de Caçambas</h2>
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "rgba(255,255,255,.6)", fontFamily: FONT }}>Ferramenta de apoio ao atendimento comercial</p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, alignItems: "start" }}>
        {/* Bloco Comercial */}
        <div style={{ ...card, padding: "24px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>💡</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", fontFamily: FONT }}>Como funciona?</span>
          </div>
          <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7, margin: "0 0 16px", fontFamily: FONT }}>
            Quando seu cliente não sabe qual caçamba precisa, use esta calculadora para ajudá-lo. Basta medir (ou estimar) as dimensões do material que será descartado.
          </p>
          <div style={{ background: "#f8fafc", borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: ".5px" }}>Dica para o atendimento</span>
            <p style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, margin: "8px 0 0", fontFamily: FONT }}>
              Pergunte ao cliente: <em>"Se você juntasse todo o material num monte, qual seria mais ou menos a largura, altura e profundidade?"</em> — isso é suficiente para uma boa estimativa.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { icon: "📐", title: "Largura", desc: "Lado a lado do monte de entulho" },
              { icon: "📏", title: "Altura", desc: "Do chão ao ponto mais alto" },
              { icon: "🔲", title: "Profundidade", desc: "De frente para trás" },
            ].map((m, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: i % 2 === 0 ? "#fafbfc" : "#fff", borderRadius: 6 }}>
                <span style={{fontSize: 18}}>{m.icon}</span>
                <div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1e2d4f", fontFamily: FONT }}>{m.title}</span>
                  <span style={{ fontSize: 11, color: "#888", marginLeft: 6, fontFamily: FONT }}>{m.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calculadora */}
        <div style={{ ...card, padding: "24px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#eef9f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/></svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", fontFamily: FONT }}>Calcular Volume</span>
          </div>

          {/* Visualização 3D do cubo */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
            <svg width="160" height="130" viewBox="0 0 160 130">
              <defs>
                <linearGradient id="face1" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor={accent} stopOpacity="0.25"/><stop offset="100%" stopColor={accent} stopOpacity="0.1"/></linearGradient>
                <linearGradient id="face2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={accent} stopOpacity="0.15"/><stop offset="100%" stopColor={accent} stopOpacity="0.05"/></linearGradient>
                <linearGradient id="face3" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor={accent} stopOpacity="0.35"/><stop offset="100%" stopColor={accent} stopOpacity="0.2"/></linearGradient>
              </defs>
              {/* Cubo isométrico */}
              <polygon points="80,15 140,45 140,95 80,125 20,95 20,45" fill="url(#face1)" stroke={accent} strokeWidth="1.5" opacity="0.9"/>
              <polygon points="80,15 140,45 140,95 80,65" fill="url(#face2)" stroke={accent} strokeWidth="1.5"/>
              <polygon points="80,15 20,45 20,95 80,65" fill="url(#face3)" stroke={accent} strokeWidth="1.5"/>
              <line x1="80" y1="65" x2="140" y2="95" stroke={accent} strokeWidth="1" strokeDasharray="4,3" opacity="0.4"/>
              <line x1="80" y1="65" x2="20" y2="95" stroke={accent} strokeWidth="1" strokeDasharray="4,3" opacity="0.4"/>
              <line x1="80" y1="65" x2="80" y2="125" stroke={accent} strokeWidth="1" strokeDasharray="4,3" opacity="0.4"/>
              {/* Labels */}
              <text x="50" y="30" fill="#6b7280" fontSize="9" fontWeight="600" fontFamily="Segoe UI">Largura</text>
              <text x="130" y="72" fill="#6b7280" fontSize="9" fontWeight="600" fontFamily="Segoe UI" textAnchor="end">Altura</text>
              <text x="20" y="72" fill="#6b7280" fontSize="9" fontWeight="600" fontFamily="Segoe UI">Prof.</text>
              {/* Setas */}
              <line x1="46" y1="32" x2="80" y2="18" stroke="#9ca3af" strokeWidth="0.8" markerEnd="url(#arw)"/>
              <line x1="138" y1="50" x2="138" y2="90" stroke="#9ca3af" strokeWidth="0.8"/>
              <line x1="22" y1="50" x2="22" y2="90" stroke="#9ca3af" strokeWidth="0.8"/>
            </svg>
          </div>

          {/* Seletor de Caçamba */}
          <div style={{ marginBottom: 16 }}>
            <label style={label}>Caçamba</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {listaCacambas.map((c, i) => (
                <button key={i} onClick={() => setCacambaSel(c.nome)} style={{ padding: "10px 18px", borderRadius: 8, border: `2px solid ${cacambaSel === c.nome ? accent : "#d4d8dd"}`, background: cacambaSel === c.nome ? "#eef9f7" : "#fff", color: cacambaSel === c.nome ? accent : "#374151", fontSize: 14, fontWeight: cacambaSel === c.nome ? 800 : 500, cursor: "pointer", fontFamily: FONT, transition: "all .15s" }}>
                  {c.nome}{c.cap ? ` (${c.cap} m³)` : ""}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={label}>Largura (m)</label>
              <input
                style={inp}
                type="number" step="0.1" min="0" placeholder="Ex: 2.5"
                value={largura} onChange={e => setLargura(e.target.value)}
                onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = "0 0 0 3px rgba(26,174,158,.12)"; }}
                onBlur={e => { e.target.style.borderColor = "#d4d8dd"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={label}>Altura (m)</label>
              <input
                style={inp}
                type="number" step="0.1" min="0" placeholder="Ex: 1.5"
                value={altura} onChange={e => setAltura(e.target.value)}
                onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = "0 0 0 3px rgba(26,174,158,.12)"; }}
                onBlur={e => { e.target.style.borderColor = "#d4d8dd"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div>
              <label style={label}>Profundidade (m)</label>
              <input
                style={inp}
                type="number" step="0.1" min="0" placeholder="Ex: 2.0"
                value={profundidade} onChange={e => setProfundidade(e.target.value)}
                onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = "0 0 0 3px rgba(26,174,158,.12)"; }}
                onBlur={e => { e.target.style.borderColor = "#d4d8dd"; e.target.style.boxShadow = "none"; }}
                onKeyDown={e => { if (e.key === "Enter") calcular(); }}
              />
            </div>
          </div>

          {/* Fórmula visual */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 18, padding: "10px 0", background: "#f8fafc", borderRadius: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: largura ? accent : "#ccc", fontFamily: FONT }}>{largura || "L"}</span>
            <span style={{ fontSize: 14, color: "#bbb" }}>×</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: altura ? accent : "#ccc", fontFamily: FONT }}>{altura || "A"}</span>
            <span style={{ fontSize: 14, color: "#bbb" }}>×</span>
            <span style={{ fontSize: 14, fontWeight: 600, color: profundidade ? accent : "#ccc", fontFamily: FONT }}>{profundidade || "P"}</span>
            <span style={{ fontSize: 14, color: "#bbb" }}>=</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: resultado ? accent : "#ccc", fontFamily: FONT }}>{resultado ? resultado.toFixed(2) + " m³" : "? m³"}</span>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={calcular} disabled={!largura || !altura || !profundidade || !cacambaSel} style={{ flex: 1, padding: "13px 20px", background: (!largura || !altura || !profundidade || !cacambaSel) ? "#d1d5db" : accent, color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: (!largura || !altura || !profundidade || !cacambaSel) ? "default" : "pointer", fontFamily: FONT, transition: "all .2s", boxShadow: (!largura || !altura || !profundidade || !cacambaSel) ? "none" : "0 4px 14px rgba(26,174,158,.3)" }}>
              Calcular Volume
            </button>
            {resultado && (
              <button onClick={limpar} style={{ padding: "13px 20px", background: "#f3f4f6", color: "#6b7280", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>
                Limpar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Resultado */}
      {resultado && cacambaAtual && (
        <div style={{ marginTop: 20, animation: animating ? "none" : "fadeIn .4s ease" }}>
          {/* Card principal do resultado */}
          <div style={{ ...card, padding: "28px 32px", marginBottom: 16, borderLeft: `4px solid ${accent}`, background: "linear-gradient(135deg, #f8fffe 0%, #fff 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "#eef9f7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🎯</div>
              <div>
                <span style={{ fontSize: 16, fontWeight: 800, color: "#1e2d4f", fontFamily: FONT }}>Resultado da Estimativa</span>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "#888", fontFamily: FONT }}>Volume calculado: <strong style={{ color: accent }}>{resultado.toFixed(2)} m³</strong> · Caçamba selecionada: <strong style={{ color: accent }}>{cacambaAtual.nome} ({cacambaAtual.cap} m³)</strong></p>
              </div>
            </div>

            {/* Resultado grande */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
              {/* Quantidade */}
              <div style={{ flex: "1 1 250px", background: qtdCacambas === 1 ? "#eef9f7" : "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)", borderRadius: 12, padding: "22px 24px", border: `2px solid ${qtdCacambas === 1 ? accent : "#93c5fd"}`, textAlign: "center" }}>
                <div style={{ fontSize: 48, fontWeight: 900, color: qtdCacambas === 1 ? accent : "#0284c7", fontFamily: FONT, lineHeight: 1 }}>{qtdCacambas}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: qtdCacambas === 1 ? "#0d7a6e" : "#0c4a6e", marginTop: 6, fontFamily: FONT }}>
                  caçamba{qtdCacambas > 1 ? "s" : ""} de {cacambaAtual.nome}
                </div>
                <p style={{ fontSize: 12, color: "#64748b", margin: "8px 0 0", fontFamily: FONT }}>
                  {resultado.toFixed(2)} m³ ÷ {cacambaAtual.cap} m³ = {(resultado / cacambaAtual.cap).toFixed(1)} → <strong>{qtdCacambas} caçamba{qtdCacambas > 1 ? "s" : ""}</strong>
                </p>
              </div>

              {/* Detalhes */}
              <div style={{ flex: "1 1 250px", display: "flex", flexDirection: "column", gap: 10 }}>
                {qtdCacambas === 1 ? (
                  <div style={{ background: "#eef9f7", borderRadius: 8, padding: "14px 18px", border: "1px solid #a7f3d0", flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 20 }}>✅</span>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#065f46", fontFamily: FONT }}>Cabe em 1 caçamba!</span>
                      <p style={{ margin: "3px 0 0", fontSize: 11, color: "#047857", fontFamily: FONT }}>Ocupação estimada de {Math.round((resultado / cacambaAtual.cap) * 100)}% da capacidade</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ background: "#f0f9ff", borderRadius: 8, padding: "12px 16px", border: "1px solid #bae6fd" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 15 }}>ℹ️</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#0c4a6e", fontFamily: FONT }}>Seu cliente vai precisar de aproximadamente {qtdCacambas} caçambas de {cacambaAtual.nome}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fafbfc", borderRadius: 8, padding: "12px 16px", border: "1px solid #e8ecf0" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", fontFamily: FONT }}>
                        {qtdCacambas - 1} caçamba{qtdCacambas - 1 > 1 ? "s" : ""} cheia{qtdCacambas - 1 > 1 ? "s" : ""} + última com ~{Math.round(ocupacaoUltima)}% de ocupação
                      </span>
                    </div>
                  </>
                )}

                {/* Barra de ocupação visual */}
                <div style={{ background: "#fff", borderRadius: 8, padding: "12px 16px", border: "1px solid #e8ecf0" }}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {Array.from({ length: qtdCacambas }, (_, idx) => {
                      const isLast = idx === qtdCacambas - 1;
                      const fillPct = isLast ? ocupacaoUltima : 100;
                      return (
                        <div key={idx} style={{ flex: 1, height: 22, background: "#e5e7eb", borderRadius: 4, overflow: "hidden", position: "relative", minWidth: 30 }}>
                          <div style={{ width: fillPct + "%", height: "100%", background: `linear-gradient(90deg, ${accent}, #4ecdc4)`, borderRadius: 4, transition: "width .5s ease" }} />
                          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: fillPct > 50 ? "#fff" : "#888" }}>
                            {Math.round(fillPct)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <span style={{ fontSize: 10, color: "#9ca3af", marginTop: 4, display: "block", fontFamily: FONT }}>Ocupação estimada por caçamba</span>
                </div>
              </div>
            </div>

            {/* Comparativo com outras caçambas */}
            {listaCacambas.length > 1 && (
              <div style={{ borderTop: "1px solid #e8ecf0", paddingTop: 18 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".5px", fontFamily: FONT }}>Comparativo com outras caçambas</span>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginTop: 12 }}>
                  {listaCacambas.filter(c => c.nome !== cacambaAtual.nome).map((c, i) => {
                    const q = Math.ceil(resultado / c.cap);
                    return (
                      <div key={i} style={{ background: "#fafbfc", borderRadius: 8, padding: "12px 14px", border: "1px solid #e8ecf0", cursor: "pointer", transition: "all .15s" }} onClick={() => { setCacambaSel(c.nome); calcular(); }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#374151", fontFamily: FONT }}>{c.nome}</div>
                        <div style={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>{c.cap} m³</div>
                        <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: q < qtdCacambas ? "#059669" : q > qtdCacambas ? "#d97706" : "#6b7280", fontFamily: FONT }}>
                          {q} caçamba{q > 1 ? "s" : ""}
                          {q < qtdCacambas && <span style={{ fontSize: 10, marginLeft: 4, color: "#059669" }}>(-{qtdCacambas - q})</span>}
                          {q > qtdCacambas && <span style={{ fontSize: 10, marginLeft: 4, color: "#d97706" }}>(+{q - qtdCacambas})</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Aviso legal */}
          <div style={{ padding: "12px 18px", background: "#fffbeb", borderRadius: 8, border: "1px solid #fde68a", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 15, marginTop: 1 }}>⚠️</span>
            <p style={{ fontSize: 11, color: "#92400e", lineHeight: 1.6, margin: 0, fontFamily: FONT }}>
              <strong>Importante:</strong> Esta é uma estimativa baseada nas dimensões informadas. O volume real pode variar dependendo do formato, compactação e tipo do material. Na dúvida, recomende a caçamba imediatamente acima.
            </p>
          </div>
        </div>
      )}

      {/* Guia rápido (quando sem resultado) */}
      {!resultado && (
        <div style={{ marginTop: 20, ...card, padding: "24px 28px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>♻️</div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "#1e2d4f", fontFamily: FONT }}>Suas Caçambas Disponíveis</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
            {listaCacambas.map((c, i) => {
              const colors = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899"];
              const cor = colors[i % colors.length];
              return (
                <div key={i} style={{ background: "#fafbfc", borderRadius: 8, padding: "14px 16px", borderLeft: `3px solid ${cor}`, cursor: "pointer", transition: "all .15s" }} onClick={() => setCacambaSel(c.nome)}>
                  <span style={{ fontSize: 18, fontWeight: 900, color: cor, fontFamily: FONT }}>{c.nome}</span>
                  <div style={{ fontSize: 12, color: "#888", fontFamily: FONT }}>{c.cap} m³</div>
                  {c.desc && <p style={{ fontSize: 11, color: "#6b7280", margin: "6px 0 0", lineHeight: 1.5, fontFamily: FONT }}>{c.desc}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Controla.AI ─────────────────────────────────────────────────────────────
function ControlaAI({ apiFetch, addToast, empresaCfg }) {
  const [pergunta, setPergunta] = useState('');
  const [resposta, setResposta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uso, setUso] = useState({ usadoMes: 0, limiteMensal: 50, planoPermitido: true });
  const [historico, setHistorico] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const [histExpandido, setHistExpandido] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    apiFetch('/api/ia/uso').then(d => { if (d && !d.error) setUso(d); }).catch(() => {});
  }, []);

  const PERGUNTAS_PRE = [
    { cat: "Dia a Dia", icon: "📅", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd", perguntas: [
      { texto: "O que tenho pra fazer hoje?", desc: "Entregas, retiradas e trocas do dia" },
      { texto: "Tem alguma locação atrasada?", desc: "Locações com data prevista ultrapassada" },
      { texto: "Qual caçamba está parada há mais tempo?", desc: "Giro de caçamba e permanência em campo" },
    ]},
    { cat: "Financeiro", icon: "💰", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", perguntas: [
      { texto: "Qual cliente demora mais para pagar?", desc: "Análise de tempo médio de pagamento" },
      { texto: "Quais clientes estão com pagamento pendente?", desc: "Lista completa de inadimplentes" },
      { texto: "Compare o faturamento deste mês com o anterior", desc: "Evolução da receita" },
      { texto: "Sugira ajustes nos valores das locações baseado nos dados", desc: "Otimização de preços" },
    ]},
    { cat: "Operacional", icon: "⚙️", color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4", perguntas: [
      { texto: "Qual motorista é mais produtivo?", desc: "Ranking de entregas e atendimentos" },
      { texto: "Qual região tem mais demanda?", desc: "Mapeamento de oportunidades" },
      { texto: "Qual o giro médio das caçambas este mês?", desc: "Tempo médio em campo" },
    ]},
    { cat: "Comercial", icon: "🏪", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", perguntas: [
      { texto: "Quais clientes estão inativos há mais de 30 dias?", desc: "Reativação de carteira" },
      { texto: "Qual tipo de caçamba é mais alugado?", desc: "Mix de produtos" },
      { texto: "Faça um resumo geral do mês com os principais indicadores", desc: "Visão executiva completa" },
    ]},
  ];

  const enviar = async (txt) => {
    const p = txt || pergunta;
    if (!p.trim()) return;
    if (uso.usadoMes >= uso.limiteMensal) { setErro('Limite mensal de ' + uso.limiteMensal + ' perguntas atingido.'); return; }
    setLoading(true); setErro(''); setResposta(null);
    try {
      const res = await apiFetch('/api/ia/perguntar', { method: 'POST', body: { pergunta: p.trim() } });
      if (res.error) { setErro(res.error); setLoading(false); return; }
      setResposta(res);
      setUso(prev => ({ ...prev, usadoMes: res.usadoMes || (prev.usadoMes + 1) }));
      setPergunta('');
    } catch (e) {
      setErro('Erro ao enviar pergunta. Tente novamente.');
    }
    setLoading(false);
  };

  const carregarHistorico = async () => {
    setShowHist(true);
    try {
      const res = await apiFetch('/api/ia/historico?limit=30');
      if (res && res.perguntas) setHistorico(res.perguntas);
    } catch (e) {}
  };

  const pctUso = uso.limiteMensal > 0 ? Math.round((uso.usadoMes / uso.limiteMensal) * 100) : 0;

  if (!uso.planoPermitido) {
    return (
      <EmptyState icon="🤖" title="Controla.AI" subtitle="Esta funcionalidade está disponível apenas no plano Controla Plus. Fale com nosso suporte para fazer upgrade." />
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", borderRadius: 16, padding: "28px 32px 24px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(20,184,166,.1)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              🤖
              <span style={{ fontSize: 24, fontWeight: 800, color: "#fff", letterSpacing: -.5, fontFamily: FONT }}>Controla.AI</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#14b8a6", background: "rgba(20,184,166,.15)", padding: "3px 10px", borderRadius: 20, border: "1px solid rgba(20,184,166,.3)", textTransform: "uppercase", letterSpacing: .5 }}>Plus</span>
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,.6)", margin: 0, maxWidth: 500 }}>
              Faça perguntas sobre seu negócio e receba análises inteligentes baseadas nos seus dados reais.
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", fontWeight: 600, marginBottom: 4 }}>Perguntas este mês</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: pctUso >= 90 ? "#f87171" : "#14b8a6" }}>{uso.usadoMes}<span style={{ fontSize: 13, color: "rgba(255,255,255,.4)", fontWeight: 500 }}>/{uso.limiteMensal}</span></div>
            <div style={{ width: 100, height: 4, background: "rgba(255,255,255,.1)", borderRadius: 4, overflow: "hidden", marginTop: 4 }}>
              <div style={{ height: "100%", width: pctUso + "%", background: pctUso >= 90 ? "#ef4444" : "#14b8a6", borderRadius: 4, transition: "width .3s" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Campo de pergunta livre */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "16px 20px", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 8, textTransform: "uppercase", letterSpacing: .5 }}>Faça sua pergunta</div>
        <div style={{ display: "flex", gap: 10 }}>
          <input
            type="text" value={pergunta}
            onChange={e => setPergunta(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !loading) enviar(); }}
            placeholder="Ex: Qual cliente mais devedor? Como melhorar meu faturamento?"
            disabled={loading}
            style={{ flex: 1, padding: "12px 16px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 14, fontFamily: FONT, outline: "none", background: loading ? "#f8fafc" : "#fff" }}
          />
          <button onClick={() => enviar()} disabled={loading || !pergunta.trim()} style={{
            background: loading ? "#94a3b8" : "linear-gradient(135deg, #0d9488, #14b8a6)",
            color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 13,
            fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: FONT,
            display: "flex", alignItems: "center", gap: 6, minWidth: 110, justifyContent: "center",
            boxShadow: loading ? "none" : "0 2px 8px rgba(13,148,136,.3)"
          }}>
            {loading ? "Pensando..." : "Enviar"}
          </button>
        </div>
        {erro && <div style={{ marginTop: 8, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, color: "#dc2626", fontWeight: 500 }}>{erro}</div>}
      </div>

      {/* Resposta */}
      {loading && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "32px 20px", marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8, animation: "pulse 1.5s infinite" }}>🤖</div>
          <div style={{ fontSize: 14, color: "#64748b", fontWeight: 500 }}>Analisando seus dados...</div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Isso pode levar alguns segundos</div>
        </div>
      )}
      {resposta && !loading && (
        <div style={{ background: "linear-gradient(135deg, #f0fdfa 0%, #f0f9ff 100%)", borderRadius: 12, border: "1px solid #99f6e4", padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              🤖
              <span style={{ fontSize: 13, fontWeight: 700, color: "#0d9488" }}>Controla.AI</span>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 10, color: "#94a3b8" }}>{resposta.tokensEntrada + resposta.tokensSaida} tokens</span>
              <button onClick={() => { navigator.clipboard.writeText(resposta.resposta); if (addToast) addToast("Resposta copiada!", "", "", "#0d9488"); }} style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", fontSize: 10, cursor: "pointer", color: "#64748b", fontWeight: 600, fontFamily: FONT
              }}>Copiar</button>
            </div>
          </div>
          <div style={{ fontSize: 14, color: "#1e293b", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{resposta.resposta}</div>
        </div>
      )}

      {/* Perguntas pré-moldadas */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
          💡Perguntas sugeridas
        </div>
        {PERGUNTAS_PRE.map(cat => (
          <div key={cat.cat} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: cat.color, textTransform: "uppercase", letterSpacing: .5, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
              <span>{cat.icon}</span> {cat.cat}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
              {cat.perguntas.map(p => (
                <button key={p.texto} onClick={() => { setPergunta(p.texto); enviar(p.texto); }} disabled={loading}
                  style={{
                    background: cat.bg, border: "1px solid " + cat.border, borderRadius: 10, padding: "12px 14px",
                    textAlign: "left", cursor: loading ? "not-allowed" : "pointer", transition: "all .15s",
                    opacity: loading ? 0.6 : 1
                  }}
                  onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = "0 3px 12px rgba(0,0,0,.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>{p.texto}</div>
                  <div style={{ fontSize: 10, color: "#64748b" }}>{p.desc}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Botão Histórico */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <button onClick={carregarHistorico} style={{
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 24px",
          fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#475569", fontFamily: FONT,
          display: "inline-flex", alignItems: "center", gap: 6
        }}>📜 Ver histórico de perguntas</button>
      </div>

      {/* Modal Histórico */}
      {showHist && (
        <LargeModal title="Histórico — Controla.AI" subtitle={historico.length + " perguntas registradas"} onClose={() => setShowHist(false)} maxWidth={800} saveLabel="" onSave={null}>
          {historico.length === 0 ? (
            <EmptyState icon="📜" title="Nenhuma pergunta ainda" subtitle="Faça sua primeira pergunta ao Controla.AI" />
          ) : (
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {historico.map((h, i) => (
                <div key={h.id || i} style={{ borderBottom: "1px solid #f1f5f9", padding: "12px 0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setHistExpandido(histExpandido === i ? null : i)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{h.pergunta}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{h.usuario_nome} — {fmtDate(h.created_at?.split('T')[0] || h.created_at)} — {(h.tokens_entrada||0) + (h.tokens_saida||0)} tokens</div>
                    </div>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>{histExpandido === i ? "" : ""}</span>
                  </div>
                  {histExpandido === i && (
                    <div style={{ marginTop: 8, padding: "10px 14px", background: "#f0fdfa", borderRadius: 8, fontSize: 13, color: "#1e293b", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                      {h.resposta}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </LargeModal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── MÓDULO FINANCEIRO (Isolado) ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Helpers Financeiro ──────────────────────────────────────────────────────
const FIN_STATUS_COLORS = { pendente:"#f59e0b", pago:"#2ecc71", vencido:"#e74c3c", cancelado:"#9ca3af" };
const FIN_STATUS_LABELS = { pendente:"Pendente", pago:"Pago", vencido:"Vencido", cancelado:"Cancelado" };
const FinBadge = ({ status }) => (<span style={{ display:"inline-block", padding:"2px 10px", borderRadius:10, fontSize:11, fontWeight:700, color:"#fff", background:FIN_STATUS_COLORS[status]||"#888" }}>{FIN_STATUS_LABELS[status]||status}</span>);
const finFmt = (v) => "R$ " + Number(v||0).toFixed(2).replace(".",",").replace(/\B(?=(\d{3})+(?!\d))/g,".");
const finFmtDate = (d) => { if (!d) return "—"; const s = String(d).split("T")[0]; const [y,m,dd] = s.split("-"); return dd+"/"+m+"/"+y; };

// ─── Dashboard Financeiro ────────────────────────────────────────────────────
function FinDashboard({ apiFetch, addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch("/api/financeiro/seed-defaults").then(() =>
      apiFetch("/api/financeiro/dashboard")
    ).then(d => { setData(d); setLoading(false); }).catch(e => { addToast("Erro: "+e.message,"error"); setLoading(false); });
  }, []);

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#aaa" }}>Carregando dados financeiros...</div>;
  if (!data) return <EmptyState icon="💰" title="Sem dados" subtitle="Não foi possível carregar o dashboard financeiro" />;

  const cards = [
    { label:"Receitas (mês)", value:finFmt(data.receitasMes), color:"#2ecc71", icon:"📈" },
    { label:"Despesas (mês)", value:finFmt(data.despesasMes), color:"#e74c3c", icon:"📉" },
    { label:"Saldo", value:finFmt(data.saldo), color:data.saldo>=0?"#2ecc71":"#e74c3c", icon:"💰" },
    { label:"Pendentes", value:finFmt(data.totalPendente), color:"#f59e0b", icon:"⏳" },
    { label:"Vencidos", value:finFmt(data.totalVencido), color:"#e74c3c", icon:"🚨" },
  ];

  return (
    <div>
      {/* Cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:14, marginBottom:24 }}>
        {cards.map((c,i) => (
          <div key={i} style={{ background:"#fff", borderRadius:10, padding:"18px 16px", boxShadow:"0 1px 4px rgba(0,0,0,.06)", borderLeft:"4px solid "+c.color }}>
            <div style={{ fontSize:11, color:"#888", fontWeight:600, marginBottom:6 }}>{c.icon} {c.label}</div>
            <div style={{ fontSize:22, fontWeight:800, color:c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Evolução mensal */}
      {data.evolucaoMensal && data.evolucaoMensal.length > 0 && (
        <div style={{ background:"#fff", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)", marginBottom:20 }}>
          <h3 style={{ margin:"0 0 16px", fontSize:15, fontWeight:700 }}>📊 Evolução Mensal (últimos 6 meses)</h3>
          <div style={{ display:"flex", alignItems:"flex-end", gap:8, height:160 }}>
            {data.evolucaoMensal.map((m,i) => {
              const maxVal = Math.max(...data.evolucaoMensal.map(x => Math.max(x.receitas||0, x.despesas||0)), 1);
              const hR = ((m.receitas||0)/maxVal)*130;
              const hD = ((m.despesas||0)/maxVal)*130;
              return (
                <div key={i} style={{ flex:1, textAlign:"center" }}>
                  <div style={{ display:"flex", justifyContent:"center", gap:3, alignItems:"flex-end", height:140 }}>
                    <div style={{ width:16, height:Math.max(hR,4), background:"#2ecc71", borderRadius:"3px 3px 0 0", transition:"height .3s" }} title={"Receitas: "+finFmt(m.receitas)} />
                    <div style={{ width:16, height:Math.max(hD,4), background:"#e74c3c", borderRadius:"3px 3px 0 0", transition:"height .3s" }} title={"Despesas: "+finFmt(m.despesas)} />
                  </div>
                  <div style={{ fontSize:10, color:"#888", marginTop:4 }}>{m.mes}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display:"flex", gap:16, justifyContent:"center", marginTop:12 }}>
            <span style={{ fontSize:11, color:"#2ecc71", fontWeight:600 }}>● Receitas</span>
            <span style={{ fontSize:11, color:"#e74c3c", fontWeight:600 }}>● Despesas</span>
          </div>
        </div>
      )}

      {/* Top categorias despesas */}
      {data.topCategorias && data.topCategorias.length > 0 && (
        <div style={{ background:"#fff", borderRadius:10, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
          <h3 style={{ margin:"0 0 14px", fontSize:15, fontWeight:700 }}>🏷️ Top Categorias de Despesa (mês)</h3>
          {data.topCategorias.map((c,i) => {
            const maxV = data.topCategorias[0]?.total || 1;
            return (
              <div key={i} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:3 }}>
                  <span style={{ fontWeight:600 }}>{c.nome}</span>
                  <span style={{ color:"#e74c3c", fontWeight:700 }}>{finFmt(c.total)}</span>
                </div>
                <div style={{ height:8, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
                  <div style={{ width:((c.total/maxV)*100)+"%", height:"100%", background:"#e74c3c", borderRadius:4, transition:"width .3s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Plano de Contas ─────────────────────────────────────────────────────────
function FinPlanoContas({ apiFetch, addToast }) {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(null); // null | {mode:"cat"|"subcat", edit?:obj, catId?:number}
  const [form, setForm] = useState({});
  const [showExport, setShowExport] = useState(false);
  const [exportDe, setExportDe] = useState("");
  const [exportAte, setExportAte] = useState("");
  const [gerando, setGerando] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      await apiFetch("/api/financeiro/seed-defaults");
      const data = await apiFetch("/api/financeiro/categorias");
      setCategorias(data || []);
    } catch(e) { addToast("Erro: "+e.message,"error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveCat = async () => {
    try {
      if (form.id) {
        await apiFetch("/api/financeiro/categorias/"+form.id, { method:"PUT", body:form });
      } else {
        await apiFetch("/api/financeiro/categorias", { method:"POST", body:form });
      }
      addToast("Categoria salva!","success"); setShowModal(null); load();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const saveSubcat = async () => {
    try {
      if (form.id) {
        await apiFetch("/api/financeiro/subcategorias/"+form.id, { method:"PUT", body:form });
      } else {
        await apiFetch("/api/financeiro/subcategorias", { method:"POST", body:form });
      }
      addToast("Subcategoria salva!","success"); setShowModal(null); load();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm("Excluir esta categoria?")) return;
    try { await apiFetch("/api/financeiro/categorias/"+id, { method:"DELETE" }); addToast("Excluída","success"); load(); }
    catch(e) { addToast("Erro: "+e.message,"error"); }
  };
  const deleteSubcat = async (id) => {
    if (!window.confirm("Excluir esta subcategoria?")) return;
    try { await apiFetch("/api/financeiro/subcategorias/"+id, { method:"DELETE" }); addToast("Excluída","success"); load(); }
    catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  // ═══ Exportar Plano de Contas em Excel ═══
  const exportarPlanoExcel = async () => {
    setGerando(true);
    try {
      const ExcelJS = window.ExcelJS;
      if (!ExcelJS) { addToast("ExcelJS não carregado. Recarregue a página.", "error"); setGerando(false); return; }

      const wb = new ExcelJS.Workbook();
      wb.creator = "Controla Caçambas";
      wb.created = new Date();

      // Cores
      const teal = "FF1A9E8F";
      const tealLight = "FFE8F8F5";
      const green = "FF2ECC71";
      const greenLight = "FFF0FDF4";
      const red = "FFE74C3C";
      const redLight = "FFFEF2F2";
      const gray = "FF6B7280";
      const grayLight = "FFF9FAFB";
      const white = "FFFFFFFF";
      const dark = "FF1A1A2E";

      const borderThin = { top:{style:"thin",color:{argb:"FFE5E7EB"}}, bottom:{style:"thin",color:{argb:"FFE5E7EB"}}, left:{style:"thin",color:{argb:"FFE5E7EB"}}, right:{style:"thin",color:{argb:"FFE5E7EB"}} };

      // Buscar lançamentos se houver período (excluindo cancelados)
      let lancamentos = [];
      if (exportDe || exportAte) {
        try {
          // Buscar pagos
          const paramsPago = new URLSearchParams({ limit: 99999, status: "pago" });
          if (exportDe) paramsPago.set("de", exportDe);
          if (exportAte) paramsPago.set("ate", exportAte);
          const dataPago = await apiFetch("/api/financeiro/lancamentos?" + paramsPago.toString());
          const pagos = (dataPago.lancamentos || dataPago || []);

          // Buscar pendentes
          const paramsPend = new URLSearchParams({ limit: 99999, status: "pendente" });
          if (exportDe) paramsPend.set("de", exportDe);
          if (exportAte) paramsPend.set("ate", exportAte);
          const dataPend = await apiFetch("/api/financeiro/lancamentos?" + paramsPend.toString());
          const pendentes = (dataPend.lancamentos || dataPend || []);

          // Combinar pagos + pendentes (exclui cancelados)
          lancamentos = [...(Array.isArray(pagos) ? pagos : []), ...(Array.isArray(pendentes) ? pendentes : [])];
        } catch(e) { lancamentos = []; }
      }

      // ─── Aba: Plano de Contas ───
      const temLanc = lancamentos.length > 0;
      const ws = wb.addWorksheet("Plano de Contas", { properties: { tabColor: { argb: teal } } });
      const cols = temLanc ? [
        { width: 5 },   // A: #
        { width: 30 },  // B: Categoria
        { width: 35 },  // C: Subcategoria
        { width: 14 },  // D: Tipo
        { width: 16 },  // E: Pago (R$)
        { width: 16 },  // F: Pendente (R$)
        { width: 16 },  // G: Total (R$)
      ] : [
        { width: 5 },   // A: #
        { width: 30 },  // B: Categoria
        { width: 35 },  // C: Subcategoria
        { width: 14 },  // D: Tipo
        { width: 16 },  // E: —
      ];
      ws.columns = cols;
      const lastCol = temLanc ? "G" : "E";
      const lastColIdx = temLanc ? 7 : 5;

      // Header
      ws.mergeCells("A1:" + lastCol + "1");
      const h1 = ws.getCell("A1");
      h1.value = "PLANO DE CONTAS";
      h1.font = { name:"Arial", bold:true, size:16, color:{argb:white} };
      h1.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} };
      h1.alignment = { horizontal:"center", vertical:"middle" };
      ws.getRow(1).height = 36;

      // Subtítulo com período
      ws.mergeCells("A2:" + lastCol + "2");
      const h2 = ws.getCell("A2");
      const periodoTxt = (exportDe || exportAte) ? ("Período: " + (exportDe ? new Date(exportDe+"T12:00:00").toLocaleDateString("pt-BR") : "início") + " até " + (exportAte ? new Date(exportAte+"T12:00:00").toLocaleDateString("pt-BR") : "hoje")) : "Todas as categorias";
      h2.value = periodoTxt + "  |  Gerado em " + new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
      h2.font = { name:"Arial", size:9, italic:true, color:{argb:gray} };
      h2.fill = { type:"pattern", pattern:"solid", fgColor:{argb:tealLight} };
      h2.alignment = { horizontal:"center", vertical:"middle" };
      ws.getRow(2).height = 22;

      // Linha vazia
      ws.addRow([]);
      let row = 4;

      // Calcular totais por categoria (separando pago vs pendente, excluindo cancelados)
      const totaisCat = {};
      const totaisSub = {};
      const totaisCatPago = {};
      const totaisSubPago = {};
      const totaisCatPend = {};
      const totaisSubPend = {};
      for (const l of lancamentos) {
        if (l.status === "cancelado") continue; // segurança extra
        const cid = l.categoria_id; const sid = l.subcategoria_id; const v = parseFloat(l.valor) || 0;
        if (cid) totaisCat[cid] = (totaisCat[cid] || 0) + v;
        if (sid) totaisSub[sid] = (totaisSub[sid] || 0) + v;
        const isPago = l.status === "pago";
        if (cid) { if (isPago) totaisCatPago[cid] = (totaisCatPago[cid]||0)+v; else totaisCatPend[cid] = (totaisCatPend[cid]||0)+v; }
        if (sid) { if (isPago) totaisSubPago[sid] = (totaisSubPago[sid]||0)+v; else totaisSubPend[sid] = (totaisSubPend[sid]||0)+v; }
      }

      const numCols = temLanc ? ["E","F","G"] : ["E"];
      const isNumCol = (ci) => temLanc ? ci >= 5 : ci === 5;

      const renderTipo = (tipo, cats) => {
        const isRec = tipo === "receita";
        const cor = isRec ? green : red;
        const corLight = isRec ? greenLight : redLight;
        const label = isRec ? "RECEITAS" : "DESPESAS";
        let totalTipoPago = 0, totalTipoPend = 0, totalTipo = 0;

        // Cabeçalho do tipo
        const tData = temLanc ? ["", label, "", tipo.toUpperCase(), "", "", ""] : ["", label, "", tipo.toUpperCase(), ""];
        const tRow = ws.addRow(tData);
        tRow.height = 28;
        tRow.eachCell((c, ci) => {
          c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:cor} };
          c.font = { name:"Arial", bold:true, size:12, color:{argb:white} };
          c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
          c.border = borderThin;
        });
        ws.mergeCells(row, 2, row, 3);
        row++;

        // Cabeçalho da tabela
        const hdrData = temLanc ? ["#", "Categoria", "Subcategoria", "Tipo", "Pago (R$)", "Pendente (R$)", "Total (R$)"] : ["#", "Categoria", "Subcategoria", "Tipo", "—"];
        const hdr = ws.addRow(hdrData);
        hdr.height = 22;
        hdr.eachCell((c, ci) => {
          c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:corLight} };
          c.font = { name:"Arial", bold:true, size:10, color:{argb:dark} };
          c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
          c.border = borderThin;
        });
        row++;

        let idx = 1;
        for (const cat of cats) {
          const catPago = totaisCatPago[cat.id] || 0;
          const catPend = totaisCatPend[cat.id] || 0;
          const catTotal = totaisCat[cat.id] || 0;
          totalTipoPago += catPago; totalTipoPend += catPend; totalTipo += catTotal;
          const subs = cat.subcategorias || [];

          // Linha da categoria
          const cData = temLanc
            ? [idx, cat.nome, "", isRec ? "Receita" : "Despesa", catPago, catPend, catTotal]
            : [idx, cat.nome, "", isRec ? "Receita" : "Despesa", ""];
          const cRow = ws.addRow(cData);
          cRow.height = 22;
          cRow.eachCell((c, ci) => {
            c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:grayLight} };
            c.font = { name:"Arial", bold:true, size:10, color:{argb:dark} };
            c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
            c.border = borderThin;
            if (isNumCol(ci)) c.numFmt = '#,##0.00;[Red]-#,##0.00;"-"';
          });
          row++;

          // Subcategorias
          for (const sub of subs) {
            const subPago = totaisSubPago[sub.id] || 0;
            const subPend = totaisSubPend[sub.id] || 0;
            const subTotal = totaisSub[sub.id] || 0;
            const sData = temLanc
              ? ["", "", "  \u21B3 " + sub.nome + (sub.descricao ? " — " + sub.descricao : ""), "", subPago, subPend, subTotal]
              : ["", "", "  \u21B3 " + sub.nome + (sub.descricao ? " — " + sub.descricao : ""), "", ""];
            const sRow = ws.addRow(sData);
            sRow.height = 18;
            sRow.eachCell((c, ci) => {
              c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:white} };
              c.font = { name:"Arial", size:9, color:{argb:gray} };
              c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
              c.border = borderThin;
              if (isNumCol(ci)) c.numFmt = '#,##0.00;[Red]-#,##0.00;"-"';
            });
            row++;
          }

          if (subs.length === 0) {
            const eData = temLanc ? ["", "", "  (sem subcategorias)", "", "", "", ""] : ["", "", "  (sem subcategorias)", "", ""];
            const eRow = ws.addRow(eData);
            eRow.height = 16;
            eRow.eachCell((c) => { c.font = { name:"Arial", size:8, italic:true, color:{argb:"FFCCCCCC"} }; c.border = borderThin; });
            row++;
          }

          idx++;
        }

        // Subtotal
        const stData = temLanc
          ? ["", "TOTAL " + label, "", "", totalTipoPago, totalTipoPend, totalTipo]
          : ["", "TOTAL " + label, "", "", ""];
        const stRow = ws.addRow(stData);
        stRow.height = 24;
        stRow.eachCell((c, ci) => {
          c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:cor} };
          c.font = { name:"Arial", bold:true, size:11, color:{argb:white} };
          c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
          c.border = borderThin;
          if (isNumCol(ci)) c.numFmt = '#,##0.00;[Red]-#,##0.00;"-"';
        });
        ws.mergeCells(row, 2, row, temLanc ? 3 : 4);
        row++;

        // Espaço
        ws.addRow([]); row++;

        return { pago: totalTipoPago, pendente: totalTipoPend, total: totalTipo };
      };

      const recCats = categorias.filter(c => c.tipo === "receita");
      const desCats = categorias.filter(c => c.tipo === "despesa");
      const totRec = renderTipo("receita", recCats);
      const totDesp = renderTipo("despesa", desCats);

      // ─── Resumo Final ───
      const resumoData = temLanc ? ["", "RESUMO GERAL", "", "", "", "", ""] : ["", "RESUMO GERAL", "", "", ""];
      const sRow = ws.addRow(resumoData);
      sRow.height = 28;
      sRow.eachCell((c) => { c.fill = { type:"pattern", pattern:"solid", fgColor:{argb:teal} }; c.font = { name:"Arial", bold:true, size:12, color:{argb:white} }; c.alignment = { vertical:"middle" }; c.border = borderThin; });
      ws.mergeCells(row, 2, row, temLanc ? lastColIdx : 4); row++;

      if (temLanc) {
        // Receitas com pago/pendente/total
        const r1 = ws.addRow(["", "Total Receitas", "", "", totRec.pago, totRec.pendente, totRec.total]);
        r1.eachCell((c, ci) => { c.font = { name:"Arial", bold:true, size:10, color:{argb:"FF16A34A"} }; c.border = borderThin; c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" }; if (isNumCol(ci)) c.numFmt = '#,##0.00'; });
        ws.mergeCells(row, 2, row, 3); row++;

        const r2 = ws.addRow(["", "Total Despesas", "", "", totDesp.pago, totDesp.pendente, totDesp.total]);
        r2.eachCell((c, ci) => { c.font = { name:"Arial", bold:true, size:10, color:{argb:"FFDC2626"} }; c.border = borderThin; c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" }; if (isNumCol(ci)) c.numFmt = '#,##0.00'; });
        ws.mergeCells(row, 2, row, 3); row++;

        const saldoPago = totRec.pago - totDesp.pago;
        const saldoTotal = totRec.total - totDesp.total;
        const r3 = ws.addRow(["", "SALDO", "", "", saldoPago, totRec.pendente - totDesp.pendente, saldoTotal]);
        r3.height = 24;
        r3.eachCell((c, ci) => {
          c.fill = { type:"pattern", pattern:"solid", fgColor:{argb: saldoTotal >= 0 ? greenLight : redLight} };
          c.font = { name:"Arial", bold:true, size:11, color:{argb: saldoTotal >= 0 ? "FF16A34A" : "FFDC2626"} };
          c.border = borderThin; c.alignment = { vertical:"middle", horizontal: isNumCol(ci) ? "right" : "left" };
          if (isNumCol(ci)) c.numFmt = '#,##0.00;[Red]-#,##0.00';
        });
        ws.mergeCells(row, 2, row, 3); row++;
      } else {
        const r1 = ws.addRow(["", "Selecione um período para ver valores realizados", "", "", ""]);
        r1.eachCell((c) => { c.font = { name:"Arial", size:10, italic:true, color:{argb:gray} }; c.border = borderThin; });
        ws.mergeCells(row, 2, row, 4); row++;
      }

      // Footer
      ws.addRow([]);
      const footData = temLanc ? ["", "Gerado por Controla Caçambas \u2014 " + new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}), "", "", "", "", ""] : ["", "Gerado por Controla Caçambas \u2014 " + new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}), "", "", ""];
      const fRow = ws.addRow(footData);
      fRow.eachCell((c) => { c.font = { name:"Arial", size:7, italic:true, color:{argb:"FFAAAAAA"} }; });
      ws.mergeCells(row+1, 2, row+1, lastColIdx);

      // Impressão
      ws.pageSetup = { orientation:"portrait", fitToPage:true, fitToWidth:1, fitToHeight:0, paperSize:9 };

      // Download
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type:"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const dtSuffix = exportDe ? "_"+exportDe.replace(/-/g,"") : "";
      a.download = "plano_de_contas" + dtSuffix + ".xlsx"; a.click();
      URL.revokeObjectURL(url);
      addToast("Plano de contas exportado!", "success");
      setShowExport(false);
    } catch(e) {
      console.error("Erro ao exportar:", e);
      addToast("Erro ao exportar: " + e.message, "error");
    }
    setGerando(false);
  };

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"#aaa" }}>Carregando plano de contas...</div>;

  const receitas = categorias.filter(c => c.tipo === "receita");
  const despesas = categorias.filter(c => c.tipo === "despesa");

  const renderCatGroup = (cats, tipo) => (
    <div style={{ marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:tipo==="receita"?"#2ecc71":"#e74c3c" }}>
          {tipo==="receita"?"📈 Receitas":"📉 Despesas"}
        </h3>
        <button onClick={() => { setForm({ nome:"", tipo, cor:tipo==="receita"?"#2ecc71":"#e74c3c", icone:"📁" }); setShowModal({ mode:"cat" }); }} style={{ background:tipo==="receita"?"#2ecc71":"#e74c3c", color:"#fff", border:"none", borderRadius:4, padding:"5px 12px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>+ Categoria</button>
      </div>
      {cats.length === 0 && <div style={{ padding:20, textAlign:"center", color:"#ccc", fontSize:12 }}>Nenhuma categoria</div>}
      {cats.map(cat => (
        <div key={cat.id} style={{ background:"#fff", borderRadius:8, marginBottom:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
          <div className="fincat-row" style={{ display:"flex", alignItems:"center", padding:"12px 16px", gap:10, borderLeft:"4px solid "+(cat.cor||"#888") }}>
            <span style={{ fontSize:16 }}>{cat.icone||"📁"}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{cat.nome}</div>
              <div style={{ fontSize:11, color:"#888" }}>{(cat.subcategorias||[]).length} subcategorias</div>
            </div>
            <button onClick={() => { setForm({ ...cat }); setShowModal({ mode:"cat" }); }} style={{ background:"#f0f4ff", border:"1px solid #e0e0e0", borderRadius:3, padding:"3px 8px", fontSize:11, cursor:"pointer", color:"#555", fontFamily:FONT }}>Editar</button>
            <button onClick={() => deleteCat(cat.id)} style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:3, padding:"3px 8px", fontSize:11, cursor:"pointer", color:"#dc2626", fontFamily:FONT }}>Excluir</button>
            <button onClick={() => { setForm({ categoria_id:cat.id, nome:"", descricao:"" }); setShowModal({ mode:"subcat", catId:cat.id }); }} style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:3, padding:"3px 8px", fontSize:11, cursor:"pointer", color:"#16a34a", fontFamily:FONT }}>+ Sub</button>
          </div>
          {(cat.subcategorias||[]).length > 0 && (
            <div style={{ borderTop:"1px solid #f3f4f6" }}>
              {cat.subcategorias.map(sub => (
                <div key={sub.id} className="finsub-row" style={{ display:"flex", alignItems:"center", padding:"8px 16px 8px 48px", gap:8, borderBottom:"1px solid #fafafa" }}>
                  <span style={{ fontSize:10, color:"#bbb" }}>↳</span>
                  <div style={{ flex:1, fontSize:12 }}>{sub.nome} {sub.descricao && <span style={{ color:"#bbb" }}>— {sub.descricao}</span>}</div>
                  <button onClick={() => { setForm({ ...sub }); setShowModal({ mode:"subcat", catId:cat.id }); }} style={{ background:"none", border:"1px solid #e0e0e0", borderRadius:3, padding:"2px 6px", fontSize:10, cursor:"pointer", color:"#888", fontFamily:FONT }}>Editar</button>
                  <button onClick={() => deleteSubcat(sub.id)} style={{ background:"none", border:"1px solid #fecaca", borderRadius:3, padding:"2px 6px", fontSize:10, cursor:"pointer", color:"#dc2626", fontFamily:FONT }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" };

  return (
    <div>
      {/* Cabeçalho com botão Exportar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div style={{ fontSize:15, fontWeight:700, color:"#1a1a2e", fontFamily:FONT }}>Plano de Contas</div>
        <button onClick={()=>{ setShowExport(true); setExportDe(""); setExportAte(""); }} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", background:"#1a9e8f", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, boxShadow:"0 1px 4px rgba(26,158,143,.25)" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><polyline points="9 15 12 18 15 15"/></svg>
          Exportar Excel
        </button>
      </div>

      {renderCatGroup(receitas, "receita")}
      {renderCatGroup(despesas, "despesa")}

      {/* Modal Exportar Excel */}
      {showExport && (
        <LargeModal title="Exportar Plano de Contas" onClose={()=>setShowExport(false)} onSave={exportarPlanoExcel} saveLabel={gerando ? "Gerando..." : "Exportar"} maxWidth={420}>
          <div style={{ padding:"18px 0", display:"grid", gap:16 }}>
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"12px 14px", fontSize:12, color:"#166534", fontFamily:FONT, lineHeight:1.5 }}>
              Exporte o plano de contas completo em Excel (.xlsx) formatado para enviar ao contador. Se informar um período, os valores realizados no período serão incluídos.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <Field label="Período de">
                <input type="date" value={exportDe} onChange={e=>setExportDe(e.target.value)} style={{ width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" }} />
              </Field>
              <Field label="Período até">
                <input type="date" value={exportAte} onChange={e=>setExportAte(e.target.value)} style={{ width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" }} />
              </Field>
            </div>
            <div style={{ fontSize:11, color:"#9ca3af", fontFamily:FONT }}>
              Deixe em branco para exportar apenas a estrutura do plano (sem valores).
            </div>
          </div>
        </LargeModal>
      )}

      {showModal && (
        <LargeModal title={showModal.mode==="cat" ? (form.id?"Editar Categoria":"Nova Categoria") : (form.id?"Editar Subcategoria":"Nova Subcategoria")} onClose={()=>setShowModal(null)} onSave={showModal.mode==="cat"?saveCat:saveSubcat} saveLabel="Salvar" maxWidth={460}>
          <div style={{ padding:"14px 0", display:"grid", gap:12 }}>
            <Field label="Nome *">
              <input style={inp} value={form.nome||""} onChange={e=>setForm(p=>({...p, nome:e.target.value}))} placeholder="Nome da categoria" />
            </Field>
            {showModal.mode==="cat" && <>
              <Field label="Tipo">
                <select style={{...inp, appearance:"none"}} value={form.tipo||"despesa"} onChange={e=>setForm(p=>({...p, tipo:e.target.value}))}>
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                </select>
              </Field>
              <Field label="Cor">
                <input type="color" value={form.cor||"#6b7280"} onChange={e=>setForm(p=>({...p, cor:e.target.value}))} style={{ width:50, height:32, border:"none", cursor:"pointer" }} />
              </Field>
            </>}
            {showModal.mode==="subcat" && (
              <Field label="Descrição">
                <input style={inp} value={form.descricao||""} onChange={e=>setForm(p=>({...p, descricao:e.target.value}))} placeholder="Descrição opcional" />
              </Field>
            )}
          </div>
        </LargeModal>
      )}
    </div>
  );
}

// ─── Lançamentos Financeiros ─────────────────────────────────────────────────
function FinLancamentos({ apiFetch, addToast, clientes, tipo: filtroTipo }) {
  const [lancamentos, setLancamentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState({ tipo:filtroTipo||"", status:"", categoria_id:"", de:"", ate:"", busca:"" });
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showPagar, setShowPagar] = useState(null);
  const [showAvisoPagar, setShowAvisoPagar] = useState(null); // aviso (só Contas a Receber)
  const [formPagar, setFormPagar] = useState({ data_pagamento:"", forma_pagamento:"" });
  const limit = 30;
  const isReceber = filtroTipo === "receita"; // Contas a Receber
  // Data exibida na 1ª coluna: receber → data de colocação da caçamba (fallback: criação do lançamento)
  const dataColuna = (l) => isReceber ? (l.data_colocacao || l.created_at) : l.data_vencimento;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtros.tipo) params.set("tipo", filtros.tipo);
      if (filtros.status) params.set("status", filtros.status);
      if (filtros.categoria_id) params.set("categoria_id", filtros.categoria_id);
      if (filtros.de) params.set("de", filtros.de);
      if (filtros.ate) params.set("ate", filtros.ate);
      if (filtros.busca) params.set("busca", filtros.busca);
      params.set("page", pagina);
      params.set("limit", limit);
      const data = await apiFetch("/api/financeiro/lancamentos?"+params.toString());
      setLancamentos(data.lancamentos || []);
      setTotal(data.total || 0);
    } catch(e) { addToast("Erro: "+e.message,"error"); }
    setLoading(false);
  };

  const loadCats = async () => {
    try {
      const data = await apiFetch("/api/financeiro/categorias");
      setCategorias(data || []);
    } catch(e) {}
  };

  useEffect(() => { loadCats(); }, []);
  useEffect(() => { load(); }, [pagina, filtros]);

  const [form, setForm] = useState({});

  const openNew = () => {
    setEditItem(null);
    setForm({ tipo:filtroTipo||"despesa", categoria_id:"", subcategoria_id:"", descricao:"", valor:"", data_vencimento:"", data_competencia:"", forma_pagamento:"", documento:"", observacoes:"", cliente_id:"", cliente_nome:"", centro_custo:"", recorrente:false, recorrencia_tipo:"mensal", recorrencia_fim:"" });
    setShowModal(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({ ...item, data_vencimento:(item.data_vencimento||"").split("T")[0], data_competencia:(item.data_competencia||"").split("T")[0], data_pagamento:(item.data_pagamento||"").split("T")[0], recorrencia_fim:(item.recorrencia_fim||"").split("T")[0] });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const body = { ...form, valor:parseFloat(form.valor)||0 };
      if (editItem) {
        await apiFetch("/api/financeiro/lancamentos/"+editItem.id, { method:"PUT", body });
      } else {
        await apiFetch("/api/financeiro/lancamentos", { method:"POST", body });
      }
      addToast("Lançamento salvo!","success"); setShowModal(false); load();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const pagar = async () => {
    try {
      await apiFetch("/api/financeiro/lancamentos/"+showPagar.id+"/pagar", { method:"PUT", body:formPagar });
      addToast("Marcado como pago!","success"); setShowPagar(null); load();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  // Ao clicar em "Pagar": em Contas a Receber, mostra aviso antes (recomenda confirmar pela aba Locações).
  const iniciarPagar = (l) => {
    setFormPagar({ data_pagamento:new Date().toISOString().split("T")[0], forma_pagamento:l.forma_pagamento||"" });
    if (isReceber) setShowAvisoPagar(l);
    else setShowPagar(l);
  };

  const cancelar = async (id) => {
    if (!window.confirm("Cancelar este lançamento?")) return;
    try { await apiFetch("/api/financeiro/lancamentos/"+id+"/cancelar", { method:"PUT" }); addToast("Cancelado","success"); load(); }
    catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir permanentemente?")) return;
    try { await apiFetch("/api/financeiro/lancamentos/"+id, { method:"DELETE" }); addToast("Excluído","success"); load(); }
    catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const subcats = form.categoria_id ? (categorias.find(c => c.id === parseInt(form.categoria_id))?.subcategorias || []) : [];
  const totalPages = Math.ceil(total / limit);
  const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:13, fontFamily:FONT, boxSizing:"border-box" };

  // ─── Buscar TODOS os lançamentos do filtro atual (sem paginação) pra export ──
  const fetchAllFiltered = async () => {
    const params = new URLSearchParams();
    if (filtros.tipo) params.set("tipo", filtros.tipo);
    if (filtros.status) params.set("status", filtros.status);
    if (filtros.categoria_id) params.set("categoria_id", filtros.categoria_id);
    if (filtros.de) params.set("de", filtros.de);
    if (filtros.ate) params.set("ate", filtros.ate);
    if (filtros.busca) params.set("busca", filtros.busca);
    params.set("page", 1);
    params.set("limit", 10000);
    const data = await apiFetch("/api/financeiro/lancamentos?" + params.toString());
    return data.lancamentos || [];
  };

  const formatarDataBR = (d) => {
    if (!d) return "—";
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return s;
  };

  const tipoRelatorio = filtroTipo === "receita" ? "Contas a Receber" : filtroTipo === "despesa" ? "Contas a Pagar" : "Lançamentos Financeiros";

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT EXCEL — XLSX formatado via ExcelJS (cores Controla, totais, freeze, autofilter)
  // ═══════════════════════════════════════════════════════════════════════════
  const handleExportExcel = async () => {
    try {
      if (!window.ExcelJS) {
        addToast("Biblioteca Excel não carregada. Recarregue a página.", "error");
        return;
      }
      addToast("Gerando Excel...", "info");
      const todos = await fetchAllFiltered();
      if (!todos.length) { addToast("Nenhum lançamento para exportar", "error"); return; }

      const ExcelJS = window.ExcelJS;
      const wb = new ExcelJS.Workbook();
      wb.creator = "Controla Caçambas";
      wb.created = new Date();

      // Paleta padrão do sistema (ARGB sem #)
      const C_TEAL  = "FF1AAE9E";
      const C_NAVY  = "FF0F172A";
      const C_WHITE = "FFFFFFFF";
      const C_ZEBRA = "FFF8FAFC";
      const C_BORDER = "FFE2E8F0";
      const C_SLATE = "FF64748B";

      const ws = wb.addWorksheet(tipoRelatorio.substring(0, 31), {
        pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 } },
        views: [{ state: "frozen", ySplit: 4 }],
      });

      // ─── Larguras + chaves
      ws.columns = [
        { key: "data_vencimento",   width: 13 },
        { key: "tipo",              width: 11 },
        { key: "descricao",         width: 42 },
        { key: "categoria_nome",    width: 22 },
        { key: "subcategoria_nome", width: 22 },
        { key: "cliente_nome",      width: 26 },
        { key: "centro_custo",      width: 16 },
        { key: "valor",             width: 15 },
        { key: "status",            width: 12 },
        { key: "data_pagamento",    width: 13 },
        { key: "forma_pagamento",   width: 14 },
        { key: "documento",         width: 14 },
        { key: "observacoes",       width: 30 },
      ];
      const totalCols = ws.columns.length; // 13
      const lastColLetter = String.fromCharCode(64 + totalCols); // M

      // ─── Linha 1: Título principal (fundo navy)
      ws.mergeCells(`A1:${lastColLetter}1`);
      const cTitle = ws.getCell("A1");
      cTitle.value = tipoRelatorio.toUpperCase();
      cTitle.font = { name: "Calibri", bold: true, size: 18, color: { argb: C_WHITE } };
      cTitle.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C_NAVY } };
      cTitle.alignment = { vertical: "middle", horizontal: "center" };
      ws.getRow(1).height = 30;

      // ─── Linha 2: Subtítulo (período + emissão)
      ws.mergeCells(`A2:${lastColLetter}2`);
      const cSub = ws.getCell("A2");
      const periodoStr = (filtros.de || filtros.ate)
        ? `Período: ${formatarDataBR(filtros.de) || "início"}  a  ${formatarDataBR(filtros.ate) || "hoje"}`
        : "Período: todos os lançamentos";
      cSub.value = `Controla Caçambas  ·  ${periodoStr}  ·  Emitido em ${new Date().toLocaleString("pt-BR")}`;
      cSub.font = { name: "Calibri", size: 10, italic: true, color: { argb: "FFCBD5E1" } };
      cSub.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C_NAVY } };
      cSub.alignment = { vertical: "middle", horizontal: "center" };
      ws.getRow(2).height = 18;

      // ─── Linha 3: faixa teal de acento
      ws.mergeCells(`A3:${lastColLetter}3`);
      ws.getCell("A3").fill = { type: "pattern", pattern: "solid", fgColor: { argb: C_TEAL } };
      ws.getRow(3).height = 4;

      // ─── Linha 4: Cabeçalho da tabela (teal)
      const headers = [
        (isReceber ? "Data Coloc." : "Data Venc."), "Tipo", "Descrição", "Categoria", "Subcategoria",
        "Cliente", "Centro Custo", "Valor", "Status", "Pago em",
        "Forma Pgto", "Documento", "Observações"
      ];
      const headerRow = ws.getRow(4);
      headers.forEach((h, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = h;
        cell.font = { name: "Calibri", bold: true, size: 11, color: { argb: C_WHITE } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C_TEAL } };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
        cell.border = {
          top: { style: "thin", color: { argb: C_BORDER } },
          bottom: { style: "medium", color: { argb: C_NAVY } },
          left: { style: "thin", color: { argb: C_WHITE } },
          right: { style: "thin", color: { argb: C_WHITE } },
        };
      });
      headerRow.height = 22;

      // ─── Linhas de dados (5+)
      const statusLabels = { pago: "Pago", pendente: "Pendente", vencido: "Vencido", cancelado: "Cancelado" };
      const statusBgColors = { pago: "FFD1FAE5", pendente: "FFFEF3C7", vencido: "FFFEE2E2", cancelado: "FFE5E7EB" };
      const statusFgColors = { pago: "FF065F46", pendente: "FF92400E", vencido: "FF991B1B", cancelado: "FF6B7280" };

      todos.forEach((l, idx) => {
        const dataPrimeiraStr = isReceber ? (l.data_colocacao || l.created_at) : l.data_vencimento;
        const dataVencDate = dataPrimeiraStr ? new Date(String(dataPrimeiraStr).slice(0, 10) + "T00:00:00") : null;
        const dataPagDate  = l.data_pagamento  ? new Date(String(l.data_pagamento).slice(0, 10)  + "T00:00:00") : null;
        const valorNum = parseFloat(l.valor) || 0;
        const statusKey = l.status || "pendente";
        const isZebra = idx % 2 === 0;

        const row = ws.addRow({
          data_vencimento:   dataVencDate,
          tipo:              l.tipo === "receita" ? "Receita" : "Despesa",
          descricao:         l.descricao || "",
          categoria_nome:    l.categoria_nome || "",
          subcategoria_nome: l.subcategoria_nome || "",
          cliente_nome:      l.cliente_nome || "",
          centro_custo:      l.centro_custo || "",
          valor:             valorNum,
          status:            statusLabels[statusKey] || statusKey,
          data_pagamento:    dataPagDate,
          forma_pagamento:   l.forma_pagamento || "",
          documento:         l.documento || "",
          observacoes:       l.observacoes || "",
        });
        row.height = 19;

        // Aplicar formatação por célula
        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
          cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
          if (isZebra) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C_ZEBRA } };
          cell.border = { bottom: { style: "thin", color: { argb: C_BORDER } } };
          cell.font = { name: "Calibri", size: 10, color: { argb: "FF1E293B" } };
        });

        // Data vencimento
        const cDv = row.getCell(1);
        cDv.numFmt = "dd/mm/yyyy";
        cDv.alignment = { vertical: "middle", horizontal: "center" };

        // Tipo (negrito + cor)
        const cTipo = row.getCell(2);
        cTipo.alignment = { vertical: "middle", horizontal: "center" };
        cTipo.font = { name: "Calibri", bold: true, size: 10, color: { argb: l.tipo === "receita" ? "FF16A34A" : "FFDC2626" } };

        // Valor (R$ + cor)
        const cVal = row.getCell(8);
        cVal.numFmt = '"R$" #,##0.00';
        cVal.alignment = { vertical: "middle", horizontal: "right" };
        cVal.font = { name: "Calibri", bold: true, size: 10, color: { argb: l.tipo === "receita" ? "FF16A34A" : "FFDC2626" } };

        // Status (pill colorido)
        const cStatus = row.getCell(9);
        const sBg = statusBgColors[statusKey] || "FFE5E7EB";
        const sFg = statusFgColors[statusKey] || "FF6B7280";
        cStatus.fill = { type: "pattern", pattern: "solid", fgColor: { argb: sBg } };
        cStatus.font = { name: "Calibri", bold: true, size: 9, color: { argb: sFg } };
        cStatus.alignment = { vertical: "middle", horizontal: "center" };
        cStatus.border = { bottom: { style: "thin", color: { argb: C_BORDER } } };

        // Data pagamento
        const cDp = row.getCell(10);
        cDp.numFmt = "dd/mm/yyyy";
        cDp.alignment = { vertical: "middle", horizontal: "center" };
      });

      // ─── Linha de totais
      const lastDataRow = ws.lastRow.number;
      const totalRowNum = lastDataRow + 1;
      ws.mergeCells(`A${totalRowNum}:G${totalRowNum}`);
      const cTotalLabel = ws.getCell(`A${totalRowNum}`);
      cTotalLabel.value = `TOTAL  ·  ${todos.length} lançamento${todos.length === 1 ? "" : "s"}`;
      cTotalLabel.font = { name: "Calibri", bold: true, size: 12, color: { argb: C_NAVY } };
      cTotalLabel.alignment = { vertical: "middle", horizontal: "right" };

      const cTotalVal = ws.getCell(`H${totalRowNum}`);
      cTotalVal.value = { formula: `SUM(H5:H${lastDataRow})` };
      cTotalVal.numFmt = '"R$" #,##0.00';
      cTotalVal.font = { name: "Calibri", bold: true, size: 13, color: { argb: C_TEAL } };
      cTotalVal.alignment = { vertical: "middle", horizontal: "right" };

      // Aplicar fundo e bordas em toda a linha de totais
      for (let c = 1; c <= totalCols; c++) {
        const cell = ws.getRow(totalRowNum).getCell(c);
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
        cell.border = {
          top: { style: "medium", color: { argb: C_TEAL } },
          bottom: { style: "medium", color: { argb: C_NAVY } },
        };
      }
      ws.getRow(totalRowNum).height = 28;

      // ─── Linhas secundárias de breakdown (Pagos / Pendentes / Vencidos)
      const breakdown = [
        { label: "Pagos",     status: "pago",     cor: "FF16A34A" },
        { label: "Pendentes", status: "pendente", cor: "FFD97706" },
        { label: "Vencidos",  status: "vencido",  cor: "FFDC2626" },
      ];
      breakdown.forEach((b, i) => {
        const r = totalRowNum + 1 + i;
        const filtrados = todos.filter(l => l.status === b.status);
        const v = filtrados.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
        ws.mergeCells(`A${r}:G${r}`);
        const lc = ws.getCell(`A${r}`);
        lc.value = `${b.label}  ·  ${filtrados.length} lançamento${filtrados.length === 1 ? "" : "s"}`;
        lc.font = { name: "Calibri", bold: true, size: 10, color: { argb: b.cor } };
        lc.alignment = { vertical: "middle", horizontal: "right" };
        const vc = ws.getCell(`H${r}`);
        vc.value = v;
        vc.numFmt = '"R$" #,##0.00';
        vc.font = { name: "Calibri", bold: true, size: 10, color: { argb: b.cor } };
        vc.alignment = { vertical: "middle", horizontal: "right" };
        for (let c = 1; c <= totalCols; c++) {
          ws.getRow(r).getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFAFAFA" } };
        }
        ws.getRow(r).height = 20;
      });

      // ─── AutoFilter no cabeçalho
      ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: totalCols } };

      // ─── Gerar arquivo
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = tipoRelatorio.toLowerCase().replace(/\s+/g, "_").replace(/[^\w_-]/g, "") + "_" + new Date().toISOString().slice(0,10) + ".xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addToast(`${todos.length} lançamento(s) exportado(s) em Excel`, "success");
    } catch (e) {
      console.error("[Excel export]", e);
      addToast("Erro ao gerar Excel: " + e.message, "error");
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // EXPORT PDF — Layout padronizado A4 paisagem (cores Controla)
  // Sem dependência de funções em TDZ — todas as helpers declaradas ANTES do uso.
  // ═══════════════════════════════════════════════════════════════════════════
  const handleExportPDF = async () => {
    try {
      addToast("Gerando PDF...", "info");
      const todos = await fetchAllFiltered();
      if (!todos.length) { addToast("Nenhum lançamento para exportar", "error"); return; }

      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
      const W = 297, H = 210, M = 10;
      const cW = W - M * 2;

      // ─── Helpers
      const fmtBRL = (v) => "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
      const statusBg = { pago: [209, 250, 229], pendente: [254, 243, 199], vencido: [254, 226, 226], cancelado: [229, 231, 235] };
      const statusFg = { pago: [6, 95, 70], pendente: [146, 64, 14], vencido: [153, 27, 27], cancelado: [107, 114, 128] };
      const statusLbl = { pago: "Pago", pendente: "Pendente", vencido: "Vencido", cancelado: "Cancelado" };

      // ─── Colunas (definidas ANTES de qualquer função que use)
      const cols = [
        { h: (isReceber ? "DATA COLOC." : "DATA VENC."), w: 18, key: "data_vencimento", align: "left" },
        { h: "TIPO",       w: 18, key: "tipo",            align: "center" },
        { h: "DESCRICAO",  w: 65, key: "descricao",       align: "left" },
        { h: "CATEGORIA",  w: 40, key: "categoria_nome",  align: "left" },
        { h: "CLIENTE",    w: 48, key: "cliente_nome",    align: "left" },
        { h: "VALOR",      w: 28, key: "valor",           align: "right" },
        { h: "STATUS",     w: 22, key: "status",          align: "center" },
        { h: "PAGTO",      w: 25, key: "data_pagamento",  align: "left" },
      ];
      const sumW = cols.reduce((s, c) => s + c.w, 0);
      const scale = cW / sumW;
      cols.forEach(c => { c.w = Math.round(c.w * scale * 10) / 10; });

      // Estado de cursor Y (mantido em ref pra ser mutável dentro de helpers)
      const cursor = { y: 0 };

      // Função pra desenhar header da tabela (usada na pág 1 e após page break)
      function drawTableHeader() {
        doc.setFillColor(15, 23, 42);
        doc.rect(M, cursor.y, cW, 7, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        let cx = M;
        for (const c of cols) {
          const tx = c.align === "right" ? cx + c.w - 2 : c.align === "center" ? cx + c.w / 2 : cx + 2;
          doc.text(c.h, tx, cursor.y + 4.7, { align: c.align });
          cx += c.w;
        }
        cursor.y += 7;
      }

      function checkPage(need) {
        if (cursor.y + need > H - 14) {
          doc.addPage();
          cursor.y = 18;
          drawTableHeader();
        }
      }

      // ─── HEADER da página 1
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, W, 32, "F");
      doc.setFillColor(26, 174, 158);
      doc.rect(0, 32, W, 1.5, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(26, 174, 158);
      doc.text("CONTROLA CACAMBAS", M, 10);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(tipoRelatorio, M, 20);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(180, 200, 210);
      const periodo = (filtros.de || filtros.ate)
        ? `Periodo: ${formatarDataBR(filtros.de) || "inicio"}  a  ${formatarDataBR(filtros.ate) || "hoje"}`
        : "Periodo: todos os lancamentos";
      doc.text(periodo, M, 27);
      const emissao = "Emitido em " + new Date().toLocaleDateString("pt-BR") + " as " + new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
      doc.text(emissao, W - M, 27, { align: "right" });

      cursor.y = 40;

      // ─── 4 CARDS DE TOTAIS
      const tTodos = todos.length;
      const tPago = todos.filter(l => l.status === "pago").length;
      const tPend = todos.filter(l => l.status === "pendente").length;
      const tVenc = todos.filter(l => l.status === "vencido").length;
      const vTodos = todos.reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
      const vPago = todos.filter(l => l.status === "pago").reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
      const vPend = todos.filter(l => l.status === "pendente").reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);
      const vVenc = todos.filter(l => l.status === "vencido").reduce((s, l) => s + (parseFloat(l.valor) || 0), 0);

      const cards = [
        { label: "TOTAL",     qtd: tTodos, valor: vTodos, fg: [15, 23, 42], bg: [241, 245, 249], accent: [26, 174, 158] },
        { label: "PAGOS",     qtd: tPago,  valor: vPago,  fg: [22, 163, 74], bg: [240, 253, 244], accent: [34, 197, 94] },
        { label: "PENDENTES", qtd: tPend,  valor: vPend,  fg: [217, 119, 6], bg: [255, 251, 235], accent: [245, 158, 11] },
        { label: "VENCIDOS",  qtd: tVenc,  valor: vVenc,  fg: [220, 38, 38], bg: [254, 242, 242], accent: [239, 68, 68] },
      ];
      const cw = (cW - 9) / 4;
      cards.forEach((c, i) => {
        const x = M + i * (cw + 3);
        doc.setFillColor(c.bg[0], c.bg[1], c.bg[2]);
        doc.roundedRect(x, cursor.y, cw, 22, 2, 2, "F");
        doc.setFillColor(c.accent[0], c.accent[1], c.accent[2]);
        doc.roundedRect(x + 2, cursor.y + 1.5, 15, 1.3, 0.6, 0.6, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        doc.setTextColor(c.fg[0], c.fg[1], c.fg[2]);
        doc.text(c.label, x + 3, cursor.y + 7);
        doc.setFontSize(6);
        doc.setTextColor(100, 116, 139);
        doc.text(c.qtd + " lancamento" + (c.qtd === 1 ? "" : "s"), x + 3, cursor.y + 11);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(c.fg[0], c.fg[1], c.fg[2]);
        doc.text(fmtBRL(c.valor), x + 3, cursor.y + 18.5);
      });
      cursor.y += 28;

      // ─── Tabela: header inicial
      drawTableHeader();

      // ─── Linhas
      todos.forEach((l, idx) => {
        checkPage(6);
        if (idx % 2 === 1) {
          doc.setFillColor(248, 250, 252);
          doc.rect(M, cursor.y, cW, 5.5, "F");
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.8);
        doc.setTextColor(45, 55, 72);
        let cx = M;
        for (const c of cols) {
          // STATUS é renderizado especial (pill)
          if (c.key === "status") {
            const st = l.status || "pendente";
            const bg = statusBg[st] || [243, 244, 246];
            const fg = statusFg[st] || [120, 120, 120];
            const pillW = 18, pillH = 3.8;
            doc.setFillColor(bg[0], bg[1], bg[2]);
            doc.roundedRect(cx + (c.w - pillW) / 2, cursor.y + 0.8, pillW, pillH, 1.5, 1.5, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(6);
            doc.setTextColor(fg[0], fg[1], fg[2]);
            doc.text((statusLbl[st] || st).toUpperCase(), cx + c.w / 2, cursor.y + 3.5, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(6.8);
            doc.setTextColor(45, 55, 72);
            cx += c.w;
            continue;
          }

          // Conteúdo das demais colunas
          let txt = "";
          if (c.key === "data_vencimento") {
            // Contas a Receber: mostra a data de colocação (fallback: criação do lançamento)
            txt = formatarDataBR(isReceber ? (l.data_colocacao || l.created_at) : l.data_vencimento);
          } else if (c.key === "data_pagamento") {
            txt = formatarDataBR(l[c.key]);
          } else if (c.key === "valor") {
            txt = fmtBRL(l.valor);
          } else if (c.key === "tipo") {
            txt = l.tipo === "receita" ? "Receita" : "Despesa";
          } else {
            txt = String(l[c.key] != null && l[c.key] !== "" ? l[c.key] : "—");
          }

          // Cortar se exceder
          const maxChars = Math.max(8, Math.floor(c.w / 1.4));
          if (txt.length > maxChars) txt = txt.substring(0, maxChars - 1) + "…";

          // Cores especiais
          let restaurar = false;
          if (c.key === "valor") {
            if (l.tipo === "receita") doc.setTextColor(22, 163, 74);
            else doc.setTextColor(220, 38, 38);
            doc.setFont("helvetica", "bold");
            restaurar = true;
          } else if (c.key === "tipo") {
            if (l.tipo === "receita") doc.setTextColor(22, 163, 74);
            else doc.setTextColor(220, 38, 38);
            doc.setFont("helvetica", "bold");
            restaurar = true;
          }

          const tx = c.align === "right" ? cx + c.w - 2 : c.align === "center" ? cx + c.w / 2 : cx + 2;
          doc.text(txt, tx, cursor.y + 3.8, { align: c.align });

          if (restaurar) {
            doc.setFont("helvetica", "normal");
            doc.setTextColor(45, 55, 72);
          }
          cx += c.w;
        }
        cursor.y += 5.5;
      });

      // ─── Linha de totais
      checkPage(10);
      cursor.y += 2;
      doc.setFillColor(241, 245, 249);
      doc.rect(M, cursor.y, cW, 8, "F");
      doc.setFillColor(26, 174, 158);
      doc.rect(M, cursor.y, 1.5, 8, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(15, 23, 42);
      doc.text(`TOTAL: ${tTodos} lancamento${tTodos === 1 ? "" : "s"}`, M + 4, cursor.y + 5);
      doc.setFontSize(11);
      doc.setTextColor(26, 174, 158);
      doc.text(fmtBRL(vTodos), W - M - 3, cursor.y + 5, { align: "right" });

      // ─── Footer em todas as páginas (uma única passagem após todo o conteúdo)
      const totalPg = doc.getNumberOfPages();
      for (let p = 1; p <= totalPg; p++) {
        doc.setPage(p);
        doc.setFontSize(6.5);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(150, 150, 150);
        doc.text("Controla Cacambas — Gestao Inteligente de Cacambas", M, H - 6);
        doc.text(`Pagina ${p} de ${totalPg}`, W - M, H - 6, { align: "right" });
      }

      const fname = tipoRelatorio.toLowerCase().replace(/\s+/g, "_").replace(/[^\w_-]/g, "") + "_" + new Date().toISOString().slice(0,10) + ".pdf";
      doc.save(fname);
      addToast(`${todos.length} lançamento(s) exportado(s) em PDF`, "success");
    } catch (e) {
      console.error("[PDF export]", e);
      addToast("Erro ao gerar PDF: " + e.message, "error");
    }
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
        <input type="text" placeholder="Buscar..." value={filtros.busca} onChange={e=>setFiltros(p=>({...p, busca:e.target.value}))} style={{ ...inp, flex:1, minWidth:160, maxWidth:250 }} />
        {!filtroTipo && (
          <select style={{...inp, width:120, appearance:"none"}} value={filtros.tipo} onChange={e=>setFiltros(p=>({...p, tipo:e.target.value}))}>
            <option value="">Todos tipos</option>
            <option value="receita">Receitas</option>
            <option value="despesa">Despesas</option>
          </select>
        )}
        <select style={{...inp, width:120, appearance:"none"}} value={filtros.status} onChange={e=>setFiltros(p=>({...p, status:e.target.value}))}>
          <option value="">Todos status</option>
          <option value="pendente">Pendente</option>
          <option value="pago">Pago</option>
          <option value="vencido">Vencido</option>
          <option value="cancelado">Cancelado</option>
        </select>
        <select style={{...inp, width:160, appearance:"none"}} value={filtros.categoria_id} onChange={e=>setFiltros(p=>({...p, categoria_id:e.target.value}))}>
          <option value="">Todas categorias</option>
          {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <input type="date" style={{...inp, width:130}} value={filtros.de} onChange={e=>setFiltros(p=>({...p, de:e.target.value}))} title="De" />
        <input type="date" style={{...inp, width:130}} value={filtros.ate} onChange={e=>setFiltros(p=>({...p, ate:e.target.value}))} title="Até" />
        <button onClick={handleExportExcel} title="Exportar lançamentos filtrados para Excel" style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:4, padding:"8px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Excel
        </button>
        <button onClick={handleExportPDF} title="Exportar lançamentos filtrados para PDF" style={{ background:"#dc2626", color:"#fff", border:"none", borderRadius:4, padding:"8px 12px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
          PDF
        </button>
        <button onClick={openNew} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:4, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, whiteSpace:"nowrap" }}>+ Novo Lançamento</button>
      </div>

      {/* Tabela */}
      <div style={{ background:"#fff", borderRadius:8, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
        <table className="mob-cards" style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background:"#f8f9fa", borderBottom:"2px solid #e5e7eb" }}>
              <th style={{ padding:"10px 12px", textAlign:"left", fontWeight:700 }}>{isReceber ? "Data Coloc." : "Data Venc."}</th>
              <th style={{ padding:"10px 12px", textAlign:"left", fontWeight:700 }}>Tipo</th>
              <th style={{ padding:"10px 12px", textAlign:"left", fontWeight:700 }}>Descrição</th>
              <th style={{ padding:"10px 12px", textAlign:"left", fontWeight:700 }}>Categoria</th>
              <th style={{ padding:"10px 12px", textAlign:"right", fontWeight:700 }}>Valor</th>
              <th style={{ padding:"10px 12px", textAlign:"center", fontWeight:700 }}>Status</th>
              <th style={{ padding:"10px 12px", textAlign:"center", fontWeight:700 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding:30, textAlign:"center", color:"#aaa" }}>Carregando...</td></tr>}
            {!loading && lancamentos.length === 0 && <tr><td colSpan={7} style={{ padding:30, textAlign:"center", color:"#aaa" }}>Nenhum lançamento encontrado</td></tr>}
            {!loading && lancamentos.map(l => {
              const status = l.status === "pendente" && l.data_vencimento && new Date(l.data_vencimento) < new Date(new Date().toISOString().split("T")[0]) ? "vencido" : l.status;
              return (
                <tr key={l.id} style={{ borderBottom:"1px solid #f3f4f6" }}>
                  <td data-label={isReceber ? "Data Coloc." : "Data Venc."} style={{ padding:"8px 12px" }}>{finFmtDate(dataColuna(l))}</td>
                  <td data-label="Tipo" style={{ padding:"8px 12px" }}><span style={{ color:l.tipo==="receita"?"#2ecc71":"#e74c3c", fontWeight:700, fontSize:10, textTransform:"uppercase" }}>{l.tipo}</span></td>
                  <td className="mob-title" style={{ padding:"8px 12px", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.descricao}{l.cliente_nome ? <span style={{ color:"#aaa" }}> — {l.cliente_nome}</span>:""}</td>
                  <td data-label="Categoria" style={{ padding:"8px 12px", color:"#888" }}>{l.categoria_nome||"—"}{l.subcategoria_nome?" / "+l.subcategoria_nome:""}</td>
                  <td data-label="Valor" style={{ padding:"8px 12px", textAlign:"right", fontWeight:700, color:l.tipo==="receita"?"#2ecc71":"#e74c3c" }}>{finFmt(l.valor)}</td>
                  <td data-label="Status" style={{ padding:"8px 12px", textAlign:"center" }}><FinBadge status={status} /></td>
                  <td className="mob-actions" style={{ padding:"8px 12px", textAlign:"center" }}>
                    <div style={{ display:"flex", gap:4, justifyContent:"center" }}>
                      {(status==="pendente"||status==="vencido") && <button onClick={()=>iniciarPagar(l)} style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:3, padding:"3px 7px", fontSize:10, cursor:"pointer", color:"#16a34a", fontWeight:600, fontFamily:FONT }}>Pagar</button>}
                      <button onClick={()=>openEdit(l)} style={{ background:"#f0f4ff", border:"1px solid #ddd", borderRadius:3, padding:"3px 7px", fontSize:10, cursor:"pointer", color:"#555", fontFamily:FONT }}>Editar</button>
                      {status!=="cancelado" && status!=="pago" && <button onClick={()=>cancelar(l.id)} style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:3, padding:"3px 7px", fontSize:10, cursor:"pointer", color:"#dc2626", fontFamily:FONT }}>Cancelar</button>}
                      <button onClick={()=>excluir(l.id)} style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:3, padding:"3px 5px", fontSize:10, cursor:"pointer", color:"#dc2626", fontFamily:FONT }}>🗑</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:14 }}>
          {Array.from({ length:totalPages }, (_, i) => (
            <button key={i} onClick={()=>setPagina(i+1)} style={{ padding:"5px 10px", border:"1px solid "+(pagina===i+1?"#1aae9e":"#e0e0e0"), borderRadius:3, background:pagina===i+1?"#1aae9e":"#fff", color:pagina===i+1?"#fff":"#555", fontSize:11, cursor:"pointer", fontWeight:pagina===i+1?700:400, fontFamily:FONT }}>{i+1}</button>
          ))}
        </div>
      )}

      {/* Modal Novo/Editar */}
      {showModal && (
        <LargeModal title={editItem?"Editar Lançamento":"Novo Lançamento"} onClose={()=>setShowModal(false)} onSave={save} saveLabel="Salvar" maxWidth={600}>
          <div style={{ padding:"14px 0", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label="Tipo *">
              <select style={{...inp, appearance:"none"}} value={form.tipo||"despesa"} onChange={e=>setForm(p=>({...p, tipo:e.target.value, categoria_id:"", subcategoria_id:""}))}>
                <option value="receita">Receita</option>
                <option value="despesa">Despesa</option>
              </select>
            </Field>
            <Field label="Valor *">
              <input type="number" step="0.01" style={inp} value={form.valor||""} onChange={e=>setForm(p=>({...p, valor:e.target.value}))} placeholder="0,00" />
            </Field>
            <Field label="Categoria">
              <select style={{...inp, appearance:"none"}} value={form.categoria_id||""} onChange={e=>setForm(p=>({...p, categoria_id:e.target.value, subcategoria_id:""}))}>
                <option value="">— Selecione —</option>
                {categorias.filter(c=>c.tipo===form.tipo).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </Field>
            <Field label="Subcategoria">
              <select style={{...inp, appearance:"none"}} value={form.subcategoria_id||""} onChange={e=>setForm(p=>({...p, subcategoria_id:e.target.value}))}>
                <option value="">— Selecione —</option>
                {subcats.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </Field>
            <Field label="Descrição *" style={{ gridColumn:"1/-1" }}>
              <input style={inp} value={form.descricao||""} onChange={e=>setForm(p=>({...p, descricao:e.target.value}))} placeholder="Descrição do lançamento" />
            </Field>
            <Field label="Data Vencimento *">
              <input type="date" style={inp} value={form.data_vencimento||""} onChange={e=>setForm(p=>({...p, data_vencimento:e.target.value}))} />
            </Field>
            <Field label="Data Competência">
              <input type="date" style={inp} value={form.data_competencia||""} onChange={e=>setForm(p=>({...p, data_competencia:e.target.value}))} />
            </Field>
            <Field label="Forma de Pagamento">
              <select style={{...inp, appearance:"none"}} value={form.forma_pagamento||""} onChange={e=>setForm(p=>({...p, forma_pagamento:e.target.value}))}>
                <option value="">—</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="cartao">Cartão</option>
                <option value="transferencia">Transferência</option>
                <option value="cheque">Cheque</option>
              </select>
            </Field>
            <Field label="Nº Documento">
              <input style={inp} value={form.documento||""} onChange={e=>setForm(p=>({...p, documento:e.target.value}))} placeholder="NF, recibo, etc." />
            </Field>
            <Field label="Cliente">
              <select style={{...inp, appearance:"none"}} value={form.cliente_id||""} onChange={e => { const c = clientes.find(x=>x.id===parseInt(e.target.value)); setForm(p=>({...p, cliente_id:e.target.value, cliente_nome:c?.nome||""})); }}>
                <option value="">— Nenhum —</option>
                {(clientes||[]).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select>
            </Field>
            <Field label="Centro de Custo">
              <input style={inp} value={form.centro_custo||""} onChange={e=>setForm(p=>({...p, centro_custo:e.target.value}))} placeholder="Ex: Operacional, Administrativo" />
            </Field>
            <Field label="Observações" style={{ gridColumn:"1/-1" }}>
              <textarea style={{...inp, minHeight:60}} value={form.observacoes||""} onChange={e=>setForm(p=>({...p, observacoes:e.target.value}))} />
            </Field>
            {!editItem && (
              <div style={{ gridColumn:"1/-1", background:"#f0f4ff", padding:12, borderRadius:6, border:"1px solid #dbeafe" }}>
                <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:12 }}>
                  <input type="checkbox" checked={!!form.recorrente} onChange={e=>setForm(p=>({...p, recorrente:e.target.checked}))} style={{ accentColor:"#1aae9e" }} />
                  <span style={{ fontWeight:600 }}>Lançamento Recorrente</span>
                </label>
                {form.recorrente && (
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:10 }}>
                    <Field label="Frequência">
                      <select style={{...inp, appearance:"none"}} value={form.recorrencia_tipo||"mensal"} onChange={e=>setForm(p=>({...p, recorrencia_tipo:e.target.value}))}>
                        <option value="semanal">Semanal</option>
                        <option value="quinzenal">Quinzenal</option>
                        <option value="mensal">Mensal</option>
                        <option value="anual">Anual</option>
                      </select>
                    </Field>
                    <Field label="Até quando?">
                      <input type="date" style={inp} value={form.recorrencia_fim||""} onChange={e=>setForm(p=>({...p, recorrencia_fim:e.target.value}))} />
                    </Field>
                  </div>
                )}
              </div>
            )}
          </div>
        </LargeModal>
      )}

      {/* Modal Pagar */}
      {showPagar && (
        <LargeModal title={"Confirmar Pagamento — "+showPagar.descricao} onClose={()=>setShowPagar(null)} onSave={pagar} saveLabel="Confirmar Pagamento" maxWidth={420}>
          <div style={{ padding:"14px 0", display:"grid", gap:12 }}>
            <div style={{ textAlign:"center", fontSize:24, fontWeight:800, color:"#2ecc71" }}>{finFmt(showPagar.valor)}</div>
            <Field label="Data do Pagamento *">
              <input type="date" style={inp} value={formPagar.data_pagamento} onChange={e=>setFormPagar(p=>({...p, data_pagamento:e.target.value}))} />
            </Field>
            <Field label="Forma de Pagamento">
              <select style={{...inp, appearance:"none"}} value={formPagar.forma_pagamento} onChange={e=>setFormPagar(p=>({...p, forma_pagamento:e.target.value}))}>
                <option value="">—</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="cartao">Cartão</option>
                <option value="transferencia">Transferência</option>
              </select>
            </Field>
          </div>
        </LargeModal>
      )}

      {/* Aviso ao pagar em Contas a Receber — recomenda confirmar pela aba Locações */}
      {showAvisoPagar && (
        <LargeModal title="Atenção — Confirmação de Recebimento" onClose={()=>setShowAvisoPagar(null)} maxWidth={460}
          saveLabel="Continuar e marcar como pago"
          onSave={()=>{ const l=showAvisoPagar; setShowAvisoPagar(null); setShowPagar(l); }}>
          <div style={{ padding:"16px 4px", display:"grid", gap:12 }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"14px 16px" }}>
              <div style={{ fontSize:24, lineHeight:1 }}>⚠️</div>
              <div style={{ fontSize:13, color:"#92400e", lineHeight:1.5 }}>
                É necessário e recomendado confirmar o pagamento pela aba <b>Locações</b>.
                Lá o recebimento é registrado junto com a baixa da locação, mantendo o financeiro e a operação sincronizados.
              </div>
            </div>
            <div style={{ fontSize:12.5, color:"#666", lineHeight:1.5 }}>
              Se preferir, você pode continuar e marcar como pago apenas aqui no financeiro — mas a locação correspondente não será atualizada.
            </div>
          </div>
        </LargeModal>
      )}

    </div>
  );
}

// ─── Contas a Pagar (filtro tipo=despesa + status pendente/vencido) ──────────
function FinContasPagar({ apiFetch, addToast, clientes }) {
  return <FinLancamentos apiFetch={apiFetch} addToast={addToast} clientes={clientes} tipo="despesa" />;
}

// ─── Contas a Receber (filtro tipo=receita + status pendente/vencido) ────────
function FinContasReceber({ apiFetch, addToast, clientes }) {
  return <FinLancamentos apiFetch={apiFetch} addToast={addToast} clientes={clientes} tipo="receita" />;
}

// ─── Comissões de Motoristas (controle de pagamento pelo gestor) ─────────────
// Lista comissões de Colocação/Retirada de todos os motoristas (mesma regra do
// app do motorista) e permite marcar/desfazer pago — reflete no app.
function FinComissoes({ apiFetch, addToast, motoristas, verLocacao, empresaNome, empresaCfg }) {
  const hoje = hojeLocal();
  const primeiroDiaMes = hoje.slice(0, 7) + "-01";
  const [periodo, setPeriodo] = useState("mes"); // mes | mesPassado | custom
  const [de, setDe] = useState(primeiroDiaMes);
  const [ate, setAte] = useState(hoje);
  const [motorista, setMotorista] = useState("");
  const [dados, setDados] = useState({ itens: [], totais: { aReceber: 0, recebido: 0, qtd: 0 }, porMotorista: [] });
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sel, setSel] = useState(() => new Set());

  const inp = { padding: "8px 10px", border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 13, fontFamily: FONT, boxSizing: "border-box" };
  const keyOf = (i) => i.locacao_id + "|" + i.tipo;

  const aplicarPeriodo = (p) => {
    setPeriodo(p);
    if (p === "mes") { setDe(primeiroDiaMes); setAte(hoje); }
    else if (p === "mesPassado") {
      const [y, m] = hoje.split("-").map(Number);
      const py = m === 1 ? y - 1 : y, pm = m === 1 ? 12 : m - 1;
      const mm = String(pm).padStart(2, "0");
      const ultimo = new Date(py, pm, 0).getDate();
      setDe(py + "-" + mm + "-01"); setAte(py + "-" + mm + "-" + String(ultimo).padStart(2, "0"));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ de, ate });
      if (motorista) params.set("motorista", motorista);
      const data = await apiFetch("/api/financeiro/comissoes?" + params.toString());
      setDados({ itens: data.itens || [], totais: data.totais || { aReceber: 0, recebido: 0, qtd: 0 }, porMotorista: data.porMotorista || [] });
      setSel(new Set());
    } catch (e) { addToast("Erro: " + e.message, "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [de, ate, motorista]); // eslint-disable-line

  const marcar = async (itensAlvo, pago) => {
    if (!itensAlvo.length) { addToast("Nenhuma comissão selecionada", "error"); return; }
    if (salvando) return;
    setSalvando(true);
    // Feedback otimista: reflete imediatamente na UI; load() reconcilia com o backend.
    const alvoKeys = new Set(itensAlvo.map(keyOf));
    const hojeStr = hojeLocal();
    setDados(prev => ({
      ...prev,
      itens: prev.itens.map(i => alvoKeys.has(keyOf(i)) ? { ...i, pago, data_pagamento: pago ? hojeStr : null } : i),
    }));
    setSel(prev => { const n = new Set(prev); alvoKeys.forEach(k => n.delete(k)); return n; });
    try {
      const r = await apiFetch("/api/financeiro/comissoes/marcar", {
        method: "POST",
        body: { itens: itensAlvo.map(i => ({ locacao_id: i.locacao_id, tipo: i.tipo })), pago },
      });
      const ok = (r.atualizados || 0) + (r.criados || 0);
      if (ok > 0) addToast(ok + (pago ? " comissão(ões) marcada(s) como paga(s)" : " comissão(ões) desfeita(s) — voltaram para A Pagar"), "success");
      if (r.falhas && r.falhas.length) addToast(r.falhas.length + " item(ns) não processado(s)", "error");
      await load(); // reconcilia (totais, data de pagamento real)
    } catch (e) {
      addToast("Erro: " + e.message, "error");
      await load(); // desfaz o otimista restaurando o estado real
    }
    setSalvando(false);
  };

  // Agrupamento por motorista (mantém ordem do resumo do backend: maior volume primeiro)
  // Cada grupo separa A PAGAR (naoPagos) de PAGO (pagos).
  const grupos = useMemo(() => {
    const map = {};
    for (const i of dados.itens) {
      const nome = i.motorista || "Sem motorista";
      if (!map[nome]) map[nome] = { motorista: nome, itens: [], naoPagos: [], pagos: [], aPagar: 0, pago: 0 };
      map[nome].itens.push(i);
      if (i.pago) { map[nome].pagos.push(i); map[nome].pago += i.valor; }
      else { map[nome].naoPagos.push(i); map[nome].aPagar += i.valor; }
    }
    const ordem = dados.porMotorista.map(m => m.motorista);
    return Object.values(map).sort((a, b) => {
      const ia = ordem.indexOf(a.motorista), ib = ordem.indexOf(b.motorista);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  }, [dados]);

  // Todas as comissões NÃO pagas (base da seleção — pagas nunca entram)
  const naoPagosAll = useMemo(() => dados.itens.filter(i => !i.pago), [dados]);

  // Opções do select de motorista: cadastro + nomes presentes nas comissões
  const opcoesMotorista = useMemo(() => {
    const nomes = new Set((motoristas || []).map(m => m.nome).filter(Boolean));
    dados.porMotorista.forEach(m => { if (m.motorista) nomes.add(m.motorista); });
    if (motorista) nomes.add(motorista);
    return [...nomes].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [motoristas, dados, motorista]);

  // Seleção considera SOMENTE comissões a pagar (pagas nunca entram)
  const selItens = dados.itens.filter(i => !i.pago && sel.has(keyOf(i)));
  const toggleSel = (i) => { if (i.pago) return; setSel(prev => { const n = new Set(prev); const k = keyOf(i); if (n.has(k)) n.delete(k); else n.add(k); return n; }); };
  const toggleGrupo = (g) => setSel(prev => {
    const n = new Set(prev);
    const alvo = g.naoPagos; // só as a pagar
    if (!alvo.length) return n;
    const todos = alvo.every(i => n.has(keyOf(i)));
    alvo.forEach(i => { if (todos) n.delete(keyOf(i)); else n.add(keyOf(i)); });
    return n;
  });
  const allSelected = naoPagosAll.length > 0 && naoPagosAll.every(i => sel.has(keyOf(i)));
  const toggleTodos = () => setSel(allSelected ? new Set() : new Set(naoPagosAll.map(keyOf)));

  // ─── Recibo de Comissões PAGAS (PDF via jsPDF, mesmo mecanismo dos outros recibos) ───
  const gerarReciboComissoes = (g) => {
    const pagos = (g.pagos || []).slice().sort((a, b) => String(a.data || "").localeCompare(String(b.data || "")));
    if (!pagos.length) { addToast("Nenhuma comissão paga de " + g.motorista + " no período", "info"); return; }
    const empresa = (empresaNome || empresaCfg?.nome || "Controla Caçambas");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pw = 210, ph = 297, mg = 14, uw = pw - 2 * mg;
    let y = 0;

    // Cabeçalho
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, pw, 40, "F");
    doc.setFillColor(26, 174, 158); doc.rect(0, 40, pw, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(26, 174, 158);
    doc.text(empresa.toUpperCase(), mg, 12);
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(255, 255, 255);
    doc.text("Recibo de Comissões", mg, 24);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(148, 163, 184);
    doc.text("Período: " + finFmtDate(de) + "  a  " + finFmtDate(ate), mg, 32);
    doc.setFontSize(7); doc.setTextColor(100, 116, 139);
    doc.text("Emitido em " + new Date().toLocaleDateString("pt-BR") + " às " + new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }), pw - mg, 32, { align: "right" });

    y = 52;
    // Motorista
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(15, 118, 110);
    doc.text("Motorista: " + g.motorista, mg, y);
    y += 8;

    // Cabeçalho da tabela
    const cols = [
      { label: "Data", x: mg, w: 24, align: "left" },
      { label: "Tipo", x: mg + 24, w: 28, align: "left" },
      { label: "Locação", x: mg + 52, w: 26, align: "left" },
      { label: "Cliente", x: mg + 78, w: 66, align: "left" },
      { label: "Valor", x: mg + uw, w: 0, align: "right" },
    ];
    const drawHead = () => {
      doc.setFillColor(240, 253, 250); doc.rect(mg, y, uw, 8, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(15, 118, 110);
      cols.forEach(c => doc.text(c.label, c.x, y + 5.5, { align: c.align }));
      y += 8;
    };
    drawHead();

    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(40, 40, 40);
    let total = 0, zebra = 0;
    for (const it of pagos) {
      if (y > ph - 40) { doc.addPage(); y = 18; drawHead(); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(40, 40, 40); }
      if (zebra % 2 === 0) { doc.setFillColor(250, 250, 252); doc.rect(mg, y, uw, 7, "F"); }
      const rowY = y + 5;
      const cli = String(it.cliente || "").length > 40 ? String(it.cliente).slice(0, 39) + "…" : String(it.cliente || "");
      doc.text(finFmtDate(it.data), cols[0].x, rowY);
      doc.text(it.tipo === "Colocação" ? "Colocação" : "Retirada", cols[1].x, rowY);
      doc.text("#" + it.locacao_id, cols[2].x, rowY);
      doc.text(cli, cols[3].x, rowY);
      doc.text(finFmt(it.valor), cols[4].x, rowY, { align: "right" });
      total += Number(it.valor) || 0;
      y += 7; zebra++;
    }

    // Total pago em destaque
    y += 2;
    doc.setDrawColor(15, 118, 110); doc.setLineWidth(0.4); doc.line(mg, y, mg + uw, y);
    y += 6;
    doc.setFillColor(240, 253, 250); doc.roundedRect(mg + uw - 78, y - 4, 78, 12, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(15, 118, 110);
    doc.text("TOTAL PAGO", mg + uw - 74, y + 4);
    doc.setFontSize(13); doc.setTextColor(22, 163, 74);
    doc.text(finFmt(total), mg + uw - 4, y + 4, { align: "right" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120, 120, 120);
    doc.text(pagos.length + " comissão(ões) paga(s) no período", mg, y + 4);
    y += 24;

    // Assinatura
    if (y > ph - 40) { doc.addPage(); y = 40; }
    doc.setDrawColor(120, 120, 120); doc.setLineWidth(0.3);
    doc.line(mg, y, mg + 90, y);
    doc.setFontSize(8.5); doc.setTextColor(80, 80, 80);
    doc.text("Assinatura do motorista", mg, y + 5);
    doc.text(g.motorista, mg, y + 10);
    doc.text("Data: ____ / ____ / ______", mg + uw - 4, y + 5, { align: "right" });

    // Rodapé
    doc.setFontSize(6.5); doc.setTextColor(160, 160, 160);
    doc.text(empresa + "  ·  Recibo de comissões  ·  gerado em " + new Date().toLocaleDateString("pt-BR"), pw / 2, ph - 8, { align: "center" });

    const safeNome = String(g.motorista).replace(/[^\w\-]+/g, "_");
    doc.save("recibo_comissoes_" + safeNome + "_" + de + "_" + ate + ".pdf");
  };

  const btnPeriodo = (p, label) => (
    <button onClick={() => aplicarPeriodo(p)} style={{ padding: "7px 12px", border: "1px solid " + (periodo === p ? "#1aae9e" : "#e0e0e0"), borderRadius: 4, background: periodo === p ? "#1aae9e" : "#fff", color: periodo === p ? "#fff" : "#555", fontSize: 12, fontWeight: periodo === p ? 700 : 500, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>{label}</button>
  );

  return (
    <div>
      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {btnPeriodo("mes", "Este mês")}
        {btnPeriodo("mesPassado", "Mês passado")}
        {btnPeriodo("custom", "Personalizado")}
        {periodo === "custom" && (<>
          <input type="date" style={{ ...inp, width: 130 }} value={de} onChange={e => setDe(e.target.value)} title="De" />
          <input type="date" style={{ ...inp, width: 130 }} value={ate} onChange={e => setAte(e.target.value)} title="Até" />
        </>)}
        <select style={{ ...inp, width: 180, appearance: "none" }} value={motorista} onChange={e => setMotorista(e.target.value)}>
          <option value="">Todos os motoristas</option>
          {opcoesMotorista.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button disabled={salvando || selItens.length === 0} onClick={() => marcar(selItens, true)} style={{ background: selItens.length ? "#16a34a" : "#d1d5db", color: "#fff", border: "none", borderRadius: 4, padding: "8px 14px", fontSize: 12, fontWeight: 700, cursor: selItens.length ? "pointer" : "default", fontFamily: FONT, whiteSpace: "nowrap" }}>
          ✓ Marcar selecionadas como pagas{selItens.length ? " (" + selItens.length + ")" : ""}
        </button>
      </div>

      {/* Cards de totais */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 14 }}>
        <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: .4 }}>A Pagar</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#f59e0b", marginTop: 4 }}>{finFmt(dados.totais.aReceber)}</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{dados.totais.qtdPendentes || 0} comissão(ões)</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", borderLeft: "4px solid #2ecc71" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#065f46", textTransform: "uppercase", letterSpacing: .4 }}>Pago</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#2ecc71", marginTop: 4 }}>{finFmt(dados.totais.recebido)}</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{dados.totais.qtdPagas || 0} comissão(ões)</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 8, padding: "14px 16px", boxShadow: "0 1px 4px rgba(0,0,0,.06)", borderLeft: "4px solid #1aae9e" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0f766e", textTransform: "uppercase", letterSpacing: .4 }}>Total do Período</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#1aae9e", marginTop: 4 }}>{finFmt((dados.totais.aReceber || 0) + (dados.totais.recebido || 0))}</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{dados.totais.qtd || 0} comissão(ões) · {grupos.length} motorista(s)</div>
        </div>
      </div>

      {/* Tabela agrupada por motorista */}
      <div style={{ background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
        <table className="mob-cards" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #e5e7eb" }}>
              <th style={{ padding: "10px 12px", width: 34, textAlign: "center" }}>
                <input type="checkbox" checked={allSelected} disabled={naoPagosAll.length === 0} onChange={toggleTodos} style={{ accentColor: "#1aae9e", cursor: naoPagosAll.length ? "pointer" : "default" }} title="Selecionar todas a pagar" />
              </th>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700 }}>Data</th>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700 }}>Tipo</th>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700 }}>Locação</th>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 700 }}>Cliente</th>
              <th style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>Valor</th>
              <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#aaa" }}>Carregando...</td></tr>}
            {!loading && dados.itens.length === 0 && <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#aaa" }}>Nenhuma comissão no período</td></tr>}
            {!loading && grupos.map(g => {
              const grpSelecionaveis = g.naoPagos.length;
              const grpTodosSel = grpSelecionaveis > 0 && g.naoPagos.every(i => sel.has(keyOf(i)));
              return (
              <Fragment key={g.motorista}>
                {/* Cabeçalho do motorista */}
                <tr style={{ background: "#f0fdfa", borderBottom: "1px solid #ccfbf1" }}>
                  <td style={{ padding: "8px 12px", textAlign: "center" }}>
                    <input type="checkbox" checked={grpTodosSel} disabled={grpSelecionaveis === 0} onChange={() => toggleGrupo(g)} style={{ accentColor: "#1aae9e", cursor: grpSelecionaveis ? "pointer" : "default" }} title="Selecionar comissões a pagar deste motorista" />
                  </td>
                  <td colSpan={6} style={{ padding: "8px 12px" }}>
                    <span style={{ fontWeight: 800, color: "#0f766e", fontSize: 13 }}>🚛 {g.motorista}</span>
                    <span style={{ marginLeft: 10, fontSize: 11, color: "#92400e", fontWeight: 700 }}>A pagar: {finFmt(g.aPagar)}</span>
                    <span style={{ marginLeft: 10, fontSize: 11, color: "#065f46", fontWeight: 700 }}>Pago: {finFmt(g.pago)}</span>
                    <span style={{ marginLeft: 10, fontSize: 11, color: "#999" }}>{g.itens.length} comissão(ões)</span>
                    {g.pagos.length > 0 && (
                      <button onClick={() => gerarReciboComissoes(g)} title="Gerar recibo das comissões pagas deste motorista"
                        style={{ marginLeft: 12, background: "#0f766e", color: "#fff", border: "none", borderRadius: 4, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
                        🧾 Recibo
                      </button>
                    )}
                  </td>
                </tr>

                {/* Sub-bloco: A PAGAR */}
                {g.naoPagos.length > 0 && (
                  <tr className="mob-hide" style={{ background: "#fffbeb" }}>
                    <td colSpan={7} style={{ padding: "4px 12px", fontSize: 10.5, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: .4 }}>A pagar ({g.naoPagos.length})</td>
                  </tr>
                )}
                {g.naoPagos.map(i => (
                  <tr key={keyOf(i)} style={{ borderBottom: "1px solid #f3f4f6", background: sel.has(keyOf(i)) ? "#f0fdf4" : "#fff" }}>
                    <td className="mob-thumb" style={{ padding: "8px 12px", textAlign: "center" }}>
                      <input type="checkbox" checked={sel.has(keyOf(i))} onChange={() => toggleSel(i)} style={{ accentColor: "#1aae9e", cursor: "pointer" }} />
                    </td>
                    <td data-label="Data" style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{finFmtDate(i.data)}</td>
                    <td data-label="Tipo" style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{i.tipo === "Colocação" ? "📥 Colocação" : "📤 Retirada"}</td>
                    <td data-label="Locação" style={{ padding: "8px 12px" }}>
                      <span onClick={() => verLocacao && verLocacao(i.locacao_id)} style={{ color: "#1aae9e", fontWeight: 700, cursor: verLocacao ? "pointer" : "default", textDecoration: verLocacao ? "underline" : "none" }} title="Ver locação">#{i.locacao_id}</span>
                    </td>
                    <td className="mob-title" data-label="Cliente" style={{ padding: "8px 12px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.cliente}</td>
                    <td data-label="Valor" style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#e74c3c" }}>{finFmt(i.valor)}</td>
                    <td data-label="Status" style={{ padding: "8px 12px", textAlign: "center" }}>
                      <button disabled={salvando} onClick={() => marcar([i], true)} title="Marcar esta comissão como paga"
                        style={{ padding: "4px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: salvando ? "default" : "pointer", color: "#fff", background: "#16a34a", border: "none", whiteSpace: "nowrap", fontFamily: FONT }}>
                        ✓ Pagar
                      </button>
                    </td>
                  </tr>
                ))}

                {/* Sub-bloco: PAGO */}
                {g.pagos.length > 0 && (
                  <tr className="mob-hide" style={{ background: "#f0fdf4" }}>
                    <td colSpan={7} style={{ padding: "4px 12px", fontSize: 10.5, fontWeight: 700, color: "#065f46", textTransform: "uppercase", letterSpacing: .4 }}>Pagas ({g.pagos.length})</td>
                  </tr>
                )}
                {g.pagos.map(i => (
                  <tr key={keyOf(i)} style={{ borderBottom: "1px solid #f3f4f6", background: "#fbfffd" }}>
                    <td className="mob-thumb" style={{ padding: "8px 12px", textAlign: "center", color: "#a7f3d0" }}>✓</td>
                    <td data-label="Data" style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{finFmtDate(i.data)}</td>
                    <td data-label="Tipo" style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{i.tipo === "Colocação" ? "📥 Colocação" : "📤 Retirada"}</td>
                    <td data-label="Locação" style={{ padding: "8px 12px" }}>
                      <span onClick={() => verLocacao && verLocacao(i.locacao_id)} style={{ color: "#1aae9e", fontWeight: 700, cursor: verLocacao ? "pointer" : "default", textDecoration: verLocacao ? "underline" : "none" }} title="Ver locação">#{i.locacao_id}</span>
                    </td>
                    <td className="mob-title" data-label="Cliente" style={{ padding: "8px 12px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{i.cliente}</td>
                    <td data-label="Valor" style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "#065f46" }}>{finFmt(i.valor)}</td>
                    <td data-label="Status" style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, color: "#065f46", background: "#d1fae5", border: "1px solid #a7f3d0" }}>
                        ✓ PAGO{i.data_pagamento ? " · " + finFmtDate(i.data_pagamento) : ""}
                      </span>
                      <button disabled={salvando} onClick={() => marcar([i], false)} title="Desfazer o pagamento (volta para A Pagar)"
                        style={{ marginLeft: 8, background: "none", border: "none", color: "#d97706", fontSize: 11, fontWeight: 600, cursor: salvando ? "default" : "pointer", textDecoration: "underline", fontFamily: FONT }}>
                        Desfazer
                      </button>
                    </td>
                  </tr>
                ))}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 11.5, color: "#999", lineHeight: 1.5 }}>
        As comissões nascem como <b>A Pagar</b> quando a operação é confirmada nas Locações. Ao marcar como paga aqui,
        o lançamento em Contas a Pagar é baixado e o motorista vê a comissão como <b>Paga</b> no app dele.
      </div>
    </div>
  );
}

// ─── Gráfico Pizza SVG ───────────────────────────────────────────────────────
const PIE_COLORS = ["#2ecc71","#3498db","#e74c3c","#f39c12","#9b59b6","#1abc9c","#e67e22","#c0392b","#2980b9","#8e44ad","#16a085","#d35400"];
const PIE_COLORS_DESP = ["#e74c3c","#c0392b","#d35400","#e67e22","#f39c12","#9b59b6","#8e44ad","#2980b9","#3498db","#1abc9c","#16a085","#2ecc71"];
function PieChart({ data, size=200 }) {
  const total = data.reduce((s,d) => s + (d.value||0), 0);
  if (total === 0) return <div style={{ width:size, height:size, display:"flex", alignItems:"center", justifyContent:"center", color:"#ccc", fontSize:12 }}>Sem dados</div>;
  let cum = 0;
  const slices = data.map((d,i) => {
    const pct = d.value / total;
    const start = cum;
    cum += pct;
    const end = cum;
    const largeArc = pct > 0.5 ? 1 : 0;
    const r = size/2 - 4;
    const cx = size/2, cy = size/2;
    const x1 = cx + r * Math.cos(2*Math.PI*start - Math.PI/2);
    const y1 = cy + r * Math.sin(2*Math.PI*start - Math.PI/2);
    const x2 = cx + r * Math.cos(2*Math.PI*end - Math.PI/2);
    const y2 = cy + r * Math.sin(2*Math.PI*end - Math.PI/2);
    const color = d.color || PIE_COLORS[i % PIE_COLORS.length];
    if (pct < 0.005) return null;
    if (pct >= 0.9999) return <circle key={i} cx={cx} cy={cy} r={r} fill={color} />;
    return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc},1 ${x2},${y2} Z`} fill={color} stroke="#fff" strokeWidth="2" />;
  });
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{slices}</svg>;
}

// ─── Relatórios Financeiros (Resultado do Período) ──────────────────────────
function FinRelatorios({ apiFetch, addToast }) {
  const [dre, setDre] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredSlice, setHoveredSlice] = useState(null);
  const hoje = new Date();
  const mesAtual = hoje.toISOString().split("T")[0].substring(0,7);
  const [de, setDe] = useState(mesAtual+"-01");
  const [ate, setAte] = useState(new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().split("T")[0]);
  const [atalho, setAtalho] = useState("mes");

  // Filtros
  const [filtroTipo, setFiltroTipo] = useState("");         // receita | despesa | ""
  const [filtroCategoria, setFiltroCategoria] = useState(""); // categoria_id
  const [filtroStatus, setFiltroStatus] = useState("pago");  // pago | pendente | todos
  const [filtroForma, setFiltroForma] = useState("");         // forma_pagamento
  const [categorias, setCategorias] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    apiFetch("/api/financeiro/categorias").then(d => setCategorias(d||[])).catch(()=>{});
  }, []);

  const setAtalhoAndDates = (key) => {
    setAtalho(key);
    const h = new Date();
    if (key === "mes") { setDe(h.toISOString().split("T")[0].substring(0,7)+"-01"); setAte(new Date(h.getFullYear(), h.getMonth()+1, 0).toISOString().split("T")[0]); }
    else if (key === "trim") { const m = h.getMonth(); const qi = m < 3 ? 0 : m < 6 ? 3 : m < 9 ? 6 : 9; setDe(h.getFullYear()+"-"+String(qi+1).padStart(2,"0")+"-01"); setAte(new Date(h.getFullYear(), qi+3, 0).toISOString().split("T")[0]); }
    else if (key === "ano") { setDe(h.getFullYear()+"-01-01"); setAte(h.getFullYear()+"-12-31"); }
    else if (key === "custom") { /* não altera datas */ }
  };

  const buildQuery = () => {
    let q = "de="+de+"&ate="+ate;
    if (filtroTipo) q += "&tipo="+filtroTipo;
    if (filtroCategoria) q += "&categoria_id="+filtroCategoria;
    if (filtroStatus) q += "&status_lanc="+filtroStatus;
    if (filtroForma) q += "&forma_pagamento="+encodeURIComponent(filtroForma);
    return q;
  };

  const loadDRE = async () => {
    setLoading(true);
    try {
      const q = buildQuery();
      const [dreData, lancData] = await Promise.all([
        apiFetch("/api/financeiro/relatorios/dre?"+q),
        apiFetch("/api/financeiro/lancamentos?"+q+"&limit=500")
      ]);
      setDre(dreData);
      setLancamentos(lancData.lancamentos || []);
    } catch(e) { addToast("Erro: "+e.message,"error"); }
    setLoading(false);
  };

  useEffect(() => { loadDRE(); }, [de, ate, filtroTipo, filtroCategoria, filtroStatus, filtroForma]);

  const activeFilterCount = [filtroTipo, filtroCategoria, filtroForma].filter(Boolean).length + (filtroStatus !== "pago" ? 1 : 0);
  const clearFilters = () => { setFiltroTipo(""); setFiltroCategoria(""); setFiltroStatus("pago"); setFiltroForma(""); };
  const catsFiltradas = filtroTipo ? categorias.filter(c => c.tipo === filtroTipo) : categorias;

  const handleExportPDF = () => {
    if (!dre) return addToast("Nenhum dado para exportar","error");
    const doc = new jsPDF({ unit:"mm", format:"a4" });
    const W = 210, H = 297, M = 14;
    const cW = W - M*2;
    let y = 0;
    const checkPage = (need) => { if (y + need > H - 20) { doc.addPage(); y = 18; } };
    const hex2rgb = (h) => { const x=h.replace("#",""); return [parseInt(x.substring(0,2),16),parseInt(x.substring(2,4),16),parseInt(x.substring(4,6),16)]; };

    // ═══ HEADER ELEGANTE ═══
    doc.setFillColor(15,23,42);
    doc.rect(0, 0, W, 42, "F");
    // gradient accent
    doc.setFillColor(26,174,158); doc.rect(0, 42, W, 2, "F");
    // empresa
    doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(26,174,158);
    doc.text("Controla Cacambas".toUpperCase(), M, 12);
    // titulo
    doc.setFont("helvetica","bold"); doc.setFontSize(18); doc.setTextColor(255,255,255);
    doc.text("Resultado Financeiro", M, 24);
    // periodo
    doc.setFont("helvetica","normal"); doc.setFontSize(10); doc.setTextColor(148,163,184);
    doc.text(finFmtDate(de) + "  a  " + finFmtDate(ate), M, 32);
    // emissão
    doc.setFontSize(7); doc.setTextColor(100,116,139);
    doc.text("Emitido em " + new Date().toLocaleDateString("pt-BR") + " as " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}), M, 38);

    y = 52;

    // ═══ CARDS RESUMO ═══
    const cardW = (cW - 8) / 3;
    const cards = [
      { label:"RECEITAS", val:dre.totalReceitas, fg:[22,163,74], bg:[240,253,244], bdr:[187,247,208], accent:[34,197,94] },
      { label:"DESPESAS", val:dre.totalDespesas, fg:[220,38,38], bg:[254,242,242], bdr:[252,165,165], accent:[239,68,68] },
      { label:dre.resultado>=0?"LUCRO":"PREJUIZO", val:Math.abs(dre.resultado),
        fg:dre.resultado>=0?[26,174,158]:[220,38,38],
        bg:dre.resultado>=0?[240,253,250]:[254,242,242],
        bdr:dre.resultado>=0?[153,246,228]:[252,165,165],
        accent:dre.resultado>=0?[26,174,158]:[239,68,68] }
    ];
    cards.forEach((c,i) => {
      const x = M + i*(cardW+4);
      // shadow
      doc.setFillColor(230,230,230); doc.roundedRect(x+0.5, y+0.5, cardW, 24, 3, 3, "F");
      // card bg
      doc.setFillColor(...c.bg); doc.roundedRect(x, y, cardW, 24, 3, 3, "F");
      doc.setDrawColor(...c.bdr); doc.setLineWidth(0.3); doc.roundedRect(x, y, cardW, 24, 3, 3, "S");
      // accent bar top
      doc.setFillColor(...c.accent); doc.roundedRect(x+3, y+1.5, 18, 1.5, 0.75, 0.75, "F");
      // label
      doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(...c.fg);
      doc.text(c.label, x+5, y+9);
      // value
      doc.setFontSize(14); doc.setTextColor(...c.fg);
      doc.text(finFmt(c.val), x+5, y+19);
    });
    y += 32;

    // ═══ BARRA PROPORCIONAL ═══
    const soma = (dre.totalReceitas||0) + (dre.totalDespesas||0);
    if (soma > 0) {
      const recPct = Math.round((dre.totalReceitas/soma)*100);
      const desPct = 100 - recPct;
      // labels above bar
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(60,60,60);
      doc.text("Receitas vs Despesas", M, y+3); y += 6;
      const barH = 8;
      // full background
      doc.setFillColor(243,244,246); doc.roundedRect(M, y, cW, barH, 3, 3, "F");
      const rW = Math.max(5, (dre.totalReceitas/soma)*cW);
      // receita (green gradient feel)
      if (dre.totalReceitas > 0) { doc.setFillColor(34,197,94); doc.roundedRect(M, y, rW, barH, 3, 3, "F"); }
      // despesa (red)
      if (dre.totalDespesas > 0) { doc.setFillColor(239,68,68); doc.roundedRect(M+rW, y, cW-rW, barH, 3, 3, "F"); }
      // values inside bar
      doc.setFont("helvetica","bold"); doc.setFontSize(6.5); doc.setTextColor(255,255,255);
      if (recPct >= 15) doc.text(finFmt(dre.totalReceitas), M+rW/2, y+barH/2+1.5, {align:"center"});
      if (desPct >= 15) doc.text(finFmt(dre.totalDespesas), M+rW+(cW-rW)/2, y+barH/2+1.5, {align:"center"});
      y += barH + 2;
      doc.setFont("helvetica","normal"); doc.setFontSize(6.5); doc.setTextColor(120,120,120);
      doc.text("Receitas " + recPct + "%", M, y+3);
      doc.text("Despesas " + desPct + "%", W-M, y+3, {align:"right"});
      y += 9;
    }

    // ═══ COMPOSICAO — Receitas ═══
    const drawBarSection = (title, items, totalVal, colors, titleRgb, iconChar) => {
      if (!items.length) return;
      checkPage(14 + items.length * 10);
      // section header with colored left border
      doc.setFillColor(titleRgb[0], titleRgb[1], titleRgb[2]); doc.rect(M, y, 1.5, 7, "F");
      doc.setFont("helvetica","bold"); doc.setFontSize(9.5); doc.setTextColor(...titleRgb);
      doc.text(title, M+5, y+5);
      doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(140,140,140);
      doc.text(finFmt(totalVal), W-M, y+5, {align:"right"});
      y += 11;
      const maxBar = cW * 0.38;
      items.forEach((item, i) => {
        checkPage(10);
        const pct = totalVal > 0 ? item.value / totalVal : 0;
        const barW = Math.max(3, pct * maxBar);
        const rgb = hex2rgb(colors[i % colors.length]);
        // zebra bg
        if (i%2===0) { doc.setFillColor(250,250,252); doc.rect(M, y-1, cW, 8, "F"); }
        // color dot
        doc.setFillColor(...rgb); doc.circle(M+3, y+2.5, 1.5, "F");
        // label
        doc.setFont("helvetica","normal"); doc.setFontSize(7.5); doc.setTextColor(55,55,55);
        doc.text(item.label, M+8, y+3.5);
        // bar
        const barX = M + 56;
        doc.setFillColor(240,240,240); doc.roundedRect(barX, y+0.5, maxBar, 4, 2, 2, "F");
        doc.setFillColor(...rgb); doc.roundedRect(barX, y+0.5, barW, 4, 2, 2, "F");
        // value
        doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(40,40,40);
        doc.text(finFmt(item.value), barX + maxBar + 4, y+3.5);
        // pct
        doc.setFont("helvetica","normal"); doc.setFontSize(6.5); doc.setTextColor(160,160,160);
        doc.text(Math.round(pct*100) + "%", W-M, y+3.5, {align:"right"});
        y += 8;
      });
      y += 5;
    };

    drawBarSection("Receitas por Categoria", recPie, dre.totalReceitas, PIE_COLORS, [22,163,74]);
    drawBarSection("Despesas por Categoria", despPie, dre.totalDespesas, PIE_COLORS_DESP, [220,38,38]);

    // ═══ DETALHAMENTO ═══
    checkPage(18);
    // section header
    doc.setFillColor(15,23,42); doc.roundedRect(M, y, cW, 10, 2, 2, "F");
    doc.setFillColor(26,174,158); doc.roundedRect(M, y, 3, 10, 2, 2, "F");
    doc.setFont("helvetica","bold"); doc.setFontSize(10); doc.setTextColor(255,255,255);
    doc.text("Detalhamento por Categoria", M+8, y+6.8);
    doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(148,163,184);
    doc.text(finFmtDate(de) + " ate " + finFmtDate(ate), W-M-4, y+6.8, {align:"right"});
    y += 15;

    // Receitas detalhamento
    if ((dre.receitas||[]).length > 0) {
      checkPage(10);
      doc.setFillColor(240,253,244); doc.roundedRect(M, y, cW, 7, 1.5, 1.5, "F");
      doc.setDrawColor(187,247,208); doc.setLineWidth(0.3); doc.roundedRect(M, y, cW, 7, 1.5, 1.5, "S");
      doc.setFont("helvetica","bold"); doc.setFontSize(8.5); doc.setTextColor(22,163,74);
      doc.text("RECEITAS", M+4, y+4.8);
      doc.text(finFmt(dre.totalReceitas), W-M-4, y+4.8, {align:"right"});
      y += 10;
      (dre.receitas||[]).forEach(cat => {
        checkPage(9);
        // category row
        doc.setFillColor(248,255,250); doc.rect(M+2, y, cW-4, 6, "F");
        doc.setDrawColor(220,252,231); doc.setLineWidth(0.2); doc.line(M+2, y+6, M+cW-2, y+6);
        doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(30,30,30);
        doc.text(cat.nome, M+6, y+4);
        doc.setTextColor(22,163,74);
        doc.text(finFmt(cat.total), W-M-4, y+4, {align:"right"});
        y += 7.5;
        (cat.subcategorias||[]).forEach(sub => {
          checkPage(6);
          doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(100,100,100);
          doc.text(sub.nome, M+14, y+3);
          doc.setTextColor(70,70,70);
          doc.text(finFmt(sub.total), W-M-4, y+3, {align:"right"});
          doc.setDrawColor(235,235,235); doc.setLineWidth(0.1); doc.line(M+14, y+5, W-M-4, y+5);
          y += 6;
        });
        y += 1;
      });
      y += 4;
    }

    // Despesas detalhamento
    if ((dre.despesas||[]).length > 0) {
      checkPage(10);
      doc.setFillColor(254,242,242); doc.roundedRect(M, y, cW, 7, 1.5, 1.5, "F");
      doc.setDrawColor(252,165,165); doc.setLineWidth(0.3); doc.roundedRect(M, y, cW, 7, 1.5, 1.5, "S");
      doc.setFont("helvetica","bold"); doc.setFontSize(8.5); doc.setTextColor(220,38,38);
      doc.text("DESPESAS", M+4, y+4.8);
      doc.text(finFmt(dre.totalDespesas), W-M-4, y+4.8, {align:"right"});
      y += 10;
      (dre.despesas||[]).forEach(cat => {
        checkPage(9);
        doc.setFillColor(255,248,248); doc.rect(M+2, y, cW-4, 6, "F");
        doc.setDrawColor(254,226,226); doc.setLineWidth(0.2); doc.line(M+2, y+6, M+cW-2, y+6);
        doc.setFont("helvetica","bold"); doc.setFontSize(7.5); doc.setTextColor(30,30,30);
        doc.text(cat.nome, M+6, y+4);
        doc.setTextColor(220,38,38);
        doc.text(finFmt(cat.total), W-M-4, y+4, {align:"right"});
        y += 7.5;
        (cat.subcategorias||[]).forEach(sub => {
          checkPage(6);
          doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(100,100,100);
          doc.text(sub.nome, M+14, y+3);
          doc.setTextColor(70,70,70);
          doc.text(finFmt(sub.total), W-M-4, y+3, {align:"right"});
          doc.setDrawColor(235,235,235); doc.setLineWidth(0.1); doc.line(M+14, y+5, W-M-4, y+5);
          y += 6;
        });
        y += 1;
      });
      y += 4;
    }

    // ═══ RESULTADO FINAL ═══
    checkPage(16);
    const isLucro = dre.resultado >= 0;
    // dark bg
    doc.setFillColor(15,23,42); doc.roundedRect(M, y, cW, 14, 2.5, 2.5, "F");
    // accent left bar
    doc.setFillColor(isLucro?26:239, isLucro?174:68, isLucro?158:68);
    doc.roundedRect(M, y, 3, 14, 2, 2, "F");
    // label
    doc.setFont("helvetica","bold"); doc.setFontSize(11); doc.setTextColor(255,255,255);
    doc.text("RESULTADO LIQUIDO", M+8, y+9);
    // value
    doc.setFontSize(14); doc.setTextColor(isLucro?26:239, isLucro?174:68, isLucro?158:68);
    doc.text(finFmt(dre.resultado), W-M-6, y+9.5, {align:"right"});

    // ═══ RODAPE ═══
    const np = doc.internal.getNumberOfPages();
    for (let p=1; p<=np; p++) {
      doc.setPage(p);
      // line
      doc.setDrawColor(200,200,200); doc.setLineWidth(0.2); doc.line(M, H-16, W-M, H-16);
      // accent dot
      doc.setFillColor(26,174,158); doc.circle(M+1, H-11, 1, "F");
      doc.setFont("helvetica","normal"); doc.setFontSize(6.5); doc.setTextColor(140,140,140);
      doc.text("Controla Cacambas" + "  |  Resultado Financeiro", M+4, H-10);
      doc.text("Pagina " + p + " de " + np, W-M, H-10, {align:"right"});
    }

    doc.save("relatorio-financeiro-" + de + "-a-" + ate + ".pdf");
    addToast("PDF exportado!","success");
  };

  const inp = { padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:6, fontSize:13, fontFamily:FONT, background:"#fff" };

  // Dados para os gráficos pizza
  const despPie = (dre?.despesas||[]).map((c,i) => ({ label:c.nome, value:c.total, color:PIE_COLORS_DESP[i % PIE_COLORS_DESP.length] }));
  const recPie = (dre?.receitas||[]).map((c,i) => ({ label:c.nome, value:c.total, color:PIE_COLORS[i % PIE_COLORS.length] }));

  return (
    <div>
      {/* Barra de período */}
      <div className="filter-row" style={{ background:"#fff", borderRadius:10, padding:"16px 20px", marginBottom:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)", display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        {[{k:"mes",l:"Este Mês"},{k:"trim",l:"Trimestre"},{k:"ano",l:"Este Ano"},{k:"custom",l:"Personalizado"}].map(a => (
          <button key={a.k} onClick={()=>setAtalhoAndDates(a.k)} style={{ padding:"7px 16px", fontSize:12, fontWeight:atalho===a.k?700:500, border:"1px solid "+(atalho===a.k?"#1aae9e":"#e0e0e0"), borderRadius:20, background:atalho===a.k?"#1aae9e":"#fff", color:atalho===a.k?"#fff":"#555", cursor:"pointer", fontFamily:FONT, transition:"all .15s" }}>{a.l}</button>
        ))}
        <div style={{ display:"flex", gap:6, alignItems:"center", marginLeft:8 }}>
          <input type="date" style={inp} value={de} onChange={e=>{setDe(e.target.value);setAtalho("custom");}} />
          <span style={{ color:"#bbb", fontSize:12 }}>até</span>
          <input type="date" style={inp} value={ate} onChange={e=>{setAte(e.target.value);setAtalho("custom");}} />
        </div>
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <button onClick={()=>setShowFilters(p=>!p)} style={{ background:showFilters||activeFilterCount?"#1aae9e":"#fff", color:showFilters||activeFilterCount?"#fff":"#555", border:"1px solid "+(showFilters||activeFilterCount?"#1aae9e":"#e0e0e0"), borderRadius:20, padding:"8px 16px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:6, transition:"all .15s" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filtros{activeFilterCount > 0 && <span style={{ background:"#fff", color:"#1aae9e", borderRadius:10, padding:"1px 6px", fontSize:10, fontWeight:800 }}>{activeFilterCount}</span>}
          </button>
          <button onClick={handleExportPDF} style={{ background:"linear-gradient(135deg,#dc2626,#ef4444)", color:"#fff", border:"none", borderRadius:20, padding:"8px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:6, boxShadow:"0 2px 8px rgba(220,38,38,.3)", transition:"all .15s" }} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";}}>📄 Exportar PDF</button>
        </div>
      </div>

      {/* Painel de filtros */}
      {showFilters && (
        <div className="filter-row" style={{ background:"#fff", borderRadius:10, padding:"16px 20px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,.06)", border:"1px solid #e8f5e9", display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={{ fontSize:10, fontWeight:600, color:"#888", textTransform:"uppercase", letterSpacing:.5 }}>Tipo</label>
            <select value={filtroTipo} onChange={e=>{ setFiltroTipo(e.target.value); setFiltroCategoria(""); }} style={{ ...inp, minWidth:120, cursor:"pointer" }}>
              <option value="">Todos</option>
              <option value="receita">Receitas</option>
              <option value="despesa">Despesas</option>
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={{ fontSize:10, fontWeight:600, color:"#888", textTransform:"uppercase", letterSpacing:.5 }}>Categoria</label>
            <select value={filtroCategoria} onChange={e=>setFiltroCategoria(e.target.value)} style={{ ...inp, minWidth:160, cursor:"pointer" }}>
              <option value="">Todas</option>
              {catsFiltradas.map(c => <option key={c.id} value={c.id}>{c.icone||""} {c.nome}</option>)}
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={{ fontSize:10, fontWeight:600, color:"#888", textTransform:"uppercase", letterSpacing:.5 }}>Status</label>
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)} style={{ ...inp, minWidth:120, cursor:"pointer" }}>
              <option value="pago">Pagos</option>
              <option value="pendente">Pendentes</option>
              <option value="todos">Todos</option>
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <label style={{ fontSize:10, fontWeight:600, color:"#888", textTransform:"uppercase", letterSpacing:.5 }}>Forma Pgto.</label>
            <select value={filtroForma} onChange={e=>setFiltroForma(e.target.value)} style={{ ...inp, minWidth:130, cursor:"pointer" }}>
              <option value="">Todas</option>
              {["PIX","Dinheiro","Boleto","Cartão Crédito","Cartão Débito","Transferência","Cheque"].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} style={{ marginTop:16, background:"none", border:"1px solid #fca5a5", borderRadius:20, padding:"6px 14px", fontSize:11, fontWeight:600, color:"#dc2626", cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:4 }}>Limpar filtros</button>
          )}
        </div>
      )}

      {loading && <div style={{ textAlign:"center", padding:60, color:"#aaa" }}>Gerando relatório...</div>}

      {dre && !loading && (
        <div>
          {/* Cards resumo */}
          <div className="kpi-cards-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:16, marginBottom:24 }}>
            <div style={{ background:"linear-gradient(135deg, #f0fdf4, #dcfce7)", borderRadius:12, padding:"20px 20px", border:"1px solid #bbf7d0" }}>
              <div style={{ fontSize:11, color:"#16a34a", fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:.5 }}>Total Receitas</div>
              <div style={{ fontSize:28, fontWeight:900, color:"#15803d" }}>{finFmt(dre.totalReceitas)}</div>
            </div>
            <div style={{ background:"linear-gradient(135deg, #fef2f2, #fecaca)", borderRadius:12, padding:"20px 20px", border:"1px solid #fca5a5" }}>
              <div style={{ fontSize:11, color:"#dc2626", fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:.5 }}>Total Despesas</div>
              <div style={{ fontSize:28, fontWeight:900, color:"#b91c1c" }}>{finFmt(dre.totalDespesas)}</div>
            </div>
            <div style={{ background: dre.resultado >= 0 ? "linear-gradient(135deg, #f0f9ff, #dbeafe)" : "linear-gradient(135deg, #fef2f2, #fee2e2)", borderRadius:12, padding:"20px 20px", border:"1px solid "+(dre.resultado >= 0 ? "#93c5fd" : "#fca5a5") }}>
              <div style={{ fontSize:11, color:dre.resultado>=0?"#2563eb":"#dc2626", fontWeight:600, marginBottom:6, textTransform:"uppercase", letterSpacing:.5 }}>Resultado</div>
              <div style={{ fontSize:28, fontWeight:900, color:dre.resultado>=0?"#1d4ed8":"#b91c1c" }}>{finFmt(dre.resultado)}</div>
              <div style={{ fontSize:11, color:"#888", marginTop:4 }}>{dre.resultado >= 0 ? "Lucro no período" : "Prejuízo no período"}</div>
            </div>
          </div>

          {/* Gráficos pizza lado a lado */}
          <div className="dash-chart-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:24 }}>
            {/* Pizza Receitas */}
            <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
              <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700, color:"#15803d" }}>📈 Receitas por Categoria</h3>
              <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                <PieChart data={recPie} size={160} />
                <div style={{ flex:1 }}>
                  {recPie.map((d,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, fontSize:12 }}>
                      <div style={{ width:12, height:12, borderRadius:3, background:d.color, flexShrink:0 }} />
                      <span style={{ flex:1, color:"#555" }}>{d.label}</span>
                      <span style={{ fontWeight:700, color:"#15803d" }}>{finFmt(d.value)}</span>
                      <span style={{ color:"#aaa", fontSize:10, minWidth:35, textAlign:"right" }}>{dre.totalReceitas > 0 ? Math.round(d.value/dre.totalReceitas*100) : 0}%</span>
                    </div>
                  ))}
                  {recPie.length === 0 && <div style={{ color:"#ccc", fontSize:12, padding:10 }}>Nenhuma receita no período</div>}
                </div>
              </div>
            </div>

            {/* Pizza Despesas */}
            <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
              <h3 style={{ margin:"0 0 16px", fontSize:14, fontWeight:700, color:"#b91c1c" }}>📉 Despesas por Categoria</h3>
              <div style={{ display:"flex", alignItems:"center", gap:20 }}>
                <PieChart data={despPie} size={160} />
                <div style={{ flex:1 }}>
                  {despPie.map((d,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, fontSize:12 }}>
                      <div style={{ width:12, height:12, borderRadius:3, background:d.color, flexShrink:0 }} />
                      <span style={{ flex:1, color:"#555" }}>{d.label}</span>
                      <span style={{ fontWeight:700, color:"#b91c1c" }}>{finFmt(d.value)}</span>
                      <span style={{ color:"#aaa", fontSize:10, minWidth:35, textAlign:"right" }}>{dre.totalDespesas > 0 ? Math.round(d.value/dre.totalDespesas*100) : 0}%</span>
                    </div>
                  ))}
                  {despPie.length === 0 && <div style={{ color:"#ccc", fontSize:12, padding:10 }}>Nenhuma despesa no período</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Barra receita vs despesa visual */}
          {(dre.totalReceitas > 0 || dre.totalDespesas > 0) && (
            <div style={{ background:"#fff", borderRadius:12, padding:20, boxShadow:"0 1px 4px rgba(0,0,0,.06)", marginBottom:24 }}>
              <h3 style={{ margin:"0 0 14px", fontSize:14, fontWeight:700 }}>Receitas vs Despesas</h3>
              <div style={{ display:"flex", height:36, borderRadius:8, overflow:"hidden", background:"#f3f4f6" }}>
                {dre.totalReceitas > 0 && <div style={{ width: (dre.totalReceitas / (dre.totalReceitas + dre.totalDespesas) * 100) + "%", background:"linear-gradient(90deg, #2ecc71, #27ae60)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:11, fontWeight:700, minWidth:60, transition:"width .5s" }}>{finFmt(dre.totalReceitas)}</div>}
                {dre.totalDespesas > 0 && <div style={{ width: (dre.totalDespesas / (dre.totalReceitas + dre.totalDespesas) * 100) + "%", background:"linear-gradient(90deg, #e74c3c, #c0392b)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:11, fontWeight:700, minWidth:60, transition:"width .5s" }}>{finFmt(dre.totalDespesas)}</div>}
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, fontSize:11, color:"#888" }}>
                <span>Receitas ({dre.totalReceitas > 0 ? Math.round(dre.totalReceitas/(dre.totalReceitas+dre.totalDespesas)*100) : 0}%)</span>
                <span>Despesas ({dre.totalDespesas > 0 ? Math.round(dre.totalDespesas/(dre.totalReceitas+dre.totalDespesas)*100) : 0}%)</span>
              </div>
            </div>
          )}

          {/* Detalhamento por categoria */}
          <div style={{ background:"#fff", borderRadius:12, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
            <div style={{ background:"linear-gradient(135deg, #1e2330, #2a3142)", padding:"18px 24px", color:"#fff" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>📋 Detalhamento por Categoria</h3>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", marginTop:2 }}>{finFmtDate(de)} até {finFmtDate(ate)}</div>
            </div>

            <div style={{ padding:20 }}>
              {/* Receitas */}
              {(dre.receitas||[]).length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"2px solid #2ecc71", marginBottom:4 }}>
                    <span style={{ fontWeight:800, fontSize:13, color:"#2ecc71" }}>RECEITAS</span>
                    <span style={{ fontWeight:800, fontSize:13, color:"#2ecc71" }}>{finFmt(dre.totalReceitas)}</span>
                  </div>
                  {(dre.receitas||[]).map((cat,i) => (
                    <div key={i}>
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 12px", background:"#f0fdf4", fontWeight:600, fontSize:12, borderRadius:4, marginTop:2 }}>
                        <span>{cat.nome}</span>
                        <span style={{ color:"#2ecc71" }}>{finFmt(cat.total)}</span>
                      </div>
                      {(cat.subcategorias||[]).map((sub,j) => (
                        <div key={j} style={{ display:"flex", justifyContent:"space-between", padding:"6px 12px 6px 32px", fontSize:12, color:"#666", borderBottom:"1px solid #fafafa" }}>
                          <span>{sub.nome}</span>
                          <span>{finFmt(sub.total)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Despesas */}
              {(dre.despesas||[]).length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"2px solid #e74c3c", marginBottom:4 }}>
                    <span style={{ fontWeight:800, fontSize:13, color:"#e74c3c" }}>DESPESAS</span>
                    <span style={{ fontWeight:800, fontSize:13, color:"#e74c3c" }}>{finFmt(dre.totalDespesas)}</span>
                  </div>
                  {(dre.despesas||[]).map((cat,i) => (
                    <div key={i}>
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 12px", background:"#fef2f2", fontWeight:600, fontSize:12, borderRadius:4, marginTop:2 }}>
                        <span>{cat.nome}</span>
                        <span style={{ color:"#e74c3c" }}>{finFmt(cat.total)}</span>
                      </div>
                      {(cat.subcategorias||[]).map((sub,j) => (
                        <div key={j} style={{ display:"flex", justifyContent:"space-between", padding:"6px 12px 6px 32px", fontSize:12, color:"#666", borderBottom:"1px solid #fafafa" }}>
                          <span>{sub.nome}</span>
                          <span>{finFmt(sub.total)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Resultado final */}
              <div style={{ display:"flex", justifyContent:"space-between", padding:"16px 0", borderTop:"3px solid #1e2330", fontSize:17, fontWeight:900 }}>
                <span>RESULTADO LÍQUIDO</span>
                <span style={{ color:dre.resultado>=0?"#2ecc71":"#e74c3c" }}>{finFmt(dre.resultado)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !dre && <EmptyState icon="📊" title="Selecione um período" subtitle="Escolha as datas acima para gerar o relatório financeiro" />}
    </div>
  );
}

// ─── Relatório por Motorista ──────────────────────────────────────────────────
function RelatorioMotoristas({ apiFetch, addToast, motoristas: motoristasList, locacoes: allLocacoes, empresaNome, setPage, setLocDetail }) {
  const hoje = new Date();
  const [de, setDe] = useState(hoje.toISOString().split("T")[0].substring(0,7)+"-01");
  const [ate, setAte] = useState(new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().split("T")[0]);
  const [motorista, setMotorista] = useState("todos");
  const [operacao, setOperacao] = useState("todos");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [excluirCanceladas, setExcluirCanceladas] = useState(true);
  const [somenteEncerradas, setSomenteEncerradas] = useState(false);
  const [clienteFiltro, setClienteFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [atalho, setAtalho] = useState("mes");

  const setAtalhoAndDates = (key) => {
    setAtalho(key);
    const h = new Date();
    if (key === "mes") { setDe(h.toISOString().split("T")[0].substring(0,7)+"-01"); setAte(new Date(h.getFullYear(), h.getMonth()+1, 0).toISOString().split("T")[0]); }
    else if (key === "trim") { const m = h.getMonth(); const qi = m < 3 ? 0 : m < 6 ? 3 : m < 9 ? 6 : 9; setDe(h.getFullYear()+"-"+String(qi+1).padStart(2,"0")+"-01"); setAte(new Date(h.getFullYear(), qi+3, 0).toISOString().split("T")[0]); }
    else if (key === "ano") { setDe(h.getFullYear()+"-01-01"); setAte(h.getFullYear()+"-12-31"); }
  };

  // Unique motoristas from props
  const motoristasUnicos = useMemo(() => {
    const set = new Set();
    (motoristasList||[]).forEach(m => { if(m.nome) set.add(m.nome); });
    (allLocacoes||[]).forEach(l => { if(l.motorista) set.add(l.motorista); if(l.motoristaRetirada||l.motorista_retirada) set.add(l.motoristaRetirada||l.motorista_retirada); });
    return [...set].sort();
  }, [motoristasList, allLocacoes]);

  const load = async () => {
    setLoading(true);
    try {
      let q = `de=${de}&ate=${ate}&motorista=${motorista}&operacao=${operacao}`;
      if (statusFiltro !== "todos") q += `&status=${statusFiltro}`;
      if (excluirCanceladas) q += "&excluirCanceladas=1";
      if (somenteEncerradas) q += "&somenteEncerradas=1";
      if (clienteFiltro) q += `&cliente=${encodeURIComponent(clienteFiltro)}`;
      const r = await apiFetch("/api/locacoes/relatorio/motoristas?" + q);
      setData(r);
    } catch(e) { addToast("Erro: " + e.message, "error"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [de, ate, motorista, operacao, statusFiltro, excluirCanceladas, somenteEncerradas, clienteFiltro]);

  const inp = { padding:"7px 10px", border:"1px solid #e0e0e0", borderRadius:6, fontSize:12, fontFamily:FONT, background:"#fff" };
  const sel = { ...inp, cursor:"pointer", minWidth:100 };

  const isSingle = motorista && motorista !== "todos";
  const res = data?.resumo || {};
  const mots = data?.motoristas || [];
  const ops = data?.operacoes || [];
  const evol = data?.evolucaoDiaria || [];

  // ─── PDF Export (Landscape A4) ───
  const handlePDF = () => {
    if (!data) return addToast("Sem dados para exportar","error");
    const doc = new jsPDF({ unit:"mm", format:"a4", orientation:"landscape" });
    const W = 297, H = 210, mg = 14, cW = W - mg*2;
    let y = 0;
    const checkPage = (n) => { if (y+n > H-16) { doc.addPage(); y = mg; } };
    const fV = (v) => finFmt(v||0);
    const txt = (s,max) => { const t = String(s||"—"); return max && t.length>max ? t.substring(0,max)+"..." : t; };

    // ── Header ──
    doc.setFillColor(22,27,40); doc.rect(0,0,W,32,"F");
    doc.setFillColor(26,174,158); doc.rect(0,32,W,1.2,"F");
    doc.setFont("helvetica","bold"); doc.setFontSize(14); doc.setTextColor(255,255,255);
    doc.text("Relatorio por Motorista", mg, 12);
    doc.setFont("helvetica","normal"); doc.setFontSize(9); doc.setTextColor(180,195,210);
    doc.text("Periodo: " + fmtDate(de) + " a " + fmtDate(ate), mg, 20);
    doc.setFontSize(8); doc.setTextColor(140,155,170);
    doc.text("Motorista: " + (isSingle ? motorista : "Todos") + "  |  " + (isSingle ? ops.length + " operacoes" : mots.length + " motoristas"), mg, 27);
    doc.text((empresaNome||"Controla Cacambas") + "  |  Emitido " + new Date().toLocaleDateString("pt-BR") + " " + new Date().toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"}), W-mg, 12, {align:"right"});
    y = 38;

    // ── Cards de resumo ──
    const cardW = (cW - 15) / 5;
    const cards = [
      {l:"SERVICOS",v:String(res.totalServicos||0),fg:[26,174,158],bg:[240,253,250],bd:[167,243,208]},
      {l:"COLOCACOES",v:String(res.totalColocacoes||0),fg:[34,197,94],bg:[240,253,244],bd:[187,247,208]},
      {l:"RETIRADAS",v:String(res.totalRetiradas||0),fg:[139,92,246],bg:[245,243,255],bd:[196,181,253]},
      {l:"TROCAS",v:String(res.totalTrocas||0),fg:[249,115,22],bg:[255,247,237],bd:[253,186,116]},
      {l:"TOTAL COMISSOES",v:fV(res.totalComissao),fg:[37,99,235],bg:[239,246,255],bd:[147,197,253]}
    ];
    cards.forEach((c,i) => {
      const x = mg + i*(cardW+3.75);
      doc.setFillColor(...c.bg); doc.roundedRect(x,y,cardW,16,2,2,"F");
      doc.setDrawColor(...c.bd); doc.setLineWidth(0.3); doc.roundedRect(x,y,cardW,16,2,2,"S");
      doc.setFont("helvetica","bold"); doc.setFontSize(5.5); doc.setTextColor(...c.fg);
      doc.text(c.l, x+3, y+5.5);
      doc.setFontSize(10);
      doc.text(c.v, x+3, y+12.5);
    });
    y += 22;

    if (!isSingle && mots.length > 0) {
      // ── Ranking bars (lado esquerdo ~60% da página) ──
      const rankW = cW * 0.58;
      const statsX = mg + rankW + 8;
      const statsW = cW - rankW - 8;

      checkPage(14 + Math.min(mots.length,8) * 7);
      doc.setFont("helvetica","bold"); doc.setFontSize(9); doc.setTextColor(40,40,40);
      doc.text("Ranking por Servicos", mg, y+4);
      doc.text("Resumo", statsX, y+4);
      y += 8;
      const rankY = y;

      const maxServ = mots[0]?.total || 1;
      const maxBar = rankW * 0.42;
      const barColors = [[26,174,158],[34,197,94],[139,92,246],[37,99,235],[249,115,22],[236,72,153],[6,182,212],[239,68,68]];
      mots.slice(0,8).forEach((m,i) => {
        const pct = m.total / maxServ;
        doc.setFont("helvetica","normal"); doc.setFontSize(7); doc.setTextColor(60,60,60);
        doc.text((i+1) + ". " + txt(m.nome,22), mg, y+3.5);
        const bx = mg + 48;
        doc.setFillColor(240,240,240); doc.roundedRect(bx,y,maxBar,4.5,1.5,1.5,"F");
        doc.setFillColor(...barColors[i%barColors.length]); doc.roundedRect(bx,y,Math.max(2,pct*maxBar),4.5,1.5,1.5,"F");
        doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(50,50,50);
        doc.text(String(m.total), bx+maxBar+3, y+3.5);
        doc.setFont("helvetica","normal"); doc.setFontSize(6); doc.setTextColor(130,130,130);
        doc.text(fV(m.comissao), mg+rankW-2, y+3.5, {align:"right"});
        y += 7;
      });

      // ── Stats box (lado direito) ──
      let sy = rankY;
      const drawStat = (label, val, color) => {
        doc.setFillColor(...color,15); doc.roundedRect(statsX,sy,statsW,10,1.5,1.5,"F");
        doc.setDrawColor(...color,60); doc.setLineWidth(0.2); doc.roundedRect(statsX,sy,statsW,10,1.5,1.5,"S");
        doc.setFont("helvetica","normal"); doc.setFontSize(6); doc.setTextColor(120,120,120);
        doc.text(label, statsX+3, sy+4);
        doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(...color);
        doc.text(val, statsX+3, sy+8.5);
        sy += 12;
      };
      drawStat("Media por Motorista", String(res.mediaServicos||0) + " servicos", [26,174,158]);
      if (res.topServicos) drawStat("Top Servicos", txt(res.topServicos.nome,18) + " (" + res.topServicos.total + ")", [34,197,94]);
      if (res.topComissao) drawStat("Top Comissao", txt(res.topComissao.nome,18) + " (" + fV(res.topComissao.comissao) + ")", [37,99,235]);
      drawStat("Total Motoristas", String(res.totalMotoristas||0), [139,92,246]);

      y = Math.max(y, sy) + 5;

      // ── Tabela detalhada (todos motoristas) ──
      checkPage(14);
      doc.setFillColor(22,27,40); doc.roundedRect(mg,y,cW,7,1.5,1.5,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(255,255,255);
      doc.text("Detalhamento por Motorista", mg+4, y+5);
      doc.text(mots.length + " motoristas", W-mg-4, y+5, {align:"right"});
      y += 10;

      doc.setFillColor(245,247,250); doc.rect(mg,y,cW,6,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(6); doc.setTextColor(100,110,120);
      const cols = [{t:"#",x:mg+2,a:"left"},{t:"Motorista",x:mg+8,a:"left"},{t:"Colocacoes",x:mg+80,a:"center"},{t:"Retiradas",x:mg+105,a:"center"},{t:"Trocas",x:mg+130,a:"center"},{t:"Total",x:mg+155,a:"center"},{t:"Comissao",x:W-mg-2,a:"right"}];
      cols.forEach(c => doc.text(c.t, c.x, y+4, {align:c.a}));
      y += 7;

      mots.forEach((m,i) => {
        checkPage(6);
        if (i%2===0) { doc.setFillColor(250,251,252); doc.rect(mg,y,cW,5.5,"F"); }
        doc.setFont("helvetica","normal"); doc.setFontSize(6.5); doc.setTextColor(140,140,140);
        doc.text(String(i+1), mg+2, y+3.8);
        doc.setTextColor(40,40,40); doc.setFont("helvetica","bold");
        doc.text(txt(m.nome,35), mg+8, y+3.8);
        doc.setFont("helvetica","normal"); doc.setTextColor(34,197,94);
        doc.text(String(m.colocacoes), mg+80, y+3.8, {align:"center"});
        doc.setTextColor(139,92,246);
        doc.text(String(m.retiradas), mg+105, y+3.8, {align:"center"});
        doc.setTextColor(249,115,22);
        doc.text(String(m.trocas), mg+130, y+3.8, {align:"center"});
        doc.setFont("helvetica","bold"); doc.setTextColor(30,30,30);
        doc.text(String(m.total), mg+155, y+3.8, {align:"center"});
        doc.setTextColor(22,163,74);
        doc.text(fV(m.comissao), W-mg-2, y+3.8, {align:"right"});
        y += 5.8;
      });

      // Total row
      checkPage(7);
      doc.setFillColor(240,253,244); doc.rect(mg,y,cW,6,"F");
      doc.setDrawColor(26,174,158); doc.setLineWidth(0.4); doc.line(mg,y,mg+cW,y);
      doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(30,30,30);
      doc.text("TOTAL", mg+8, y+4.2);
      doc.setTextColor(34,197,94); doc.text(String(res.totalColocacoes), mg+80, y+4.2, {align:"center"});
      doc.setTextColor(139,92,246); doc.text(String(res.totalRetiradas), mg+105, y+4.2, {align:"center"});
      doc.setTextColor(249,115,22); doc.text(String(res.totalTrocas), mg+130, y+4.2, {align:"center"});
      doc.setTextColor(30,30,30); doc.text(String(res.totalServicos), mg+155, y+4.2, {align:"center"});
      doc.setTextColor(22,163,74); doc.text(fV(res.totalComissao), W-mg-2, y+4.2, {align:"right"});
      y += 10;

    } else if (isSingle && ops.length > 0) {
      // ── Tabela de operações (motorista individual) com endereço ──
      checkPage(14);
      doc.setFillColor(22,27,40); doc.roundedRect(mg,y,cW,7,1.5,1.5,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(8); doc.setTextColor(255,255,255);
      doc.text("Operacoes de " + motorista, mg+4, y+5);
      doc.text(ops.length + " operacoes  |  Comissao Total: " + fV(res.totalComissao), W-mg-4, y+5, {align:"right"});
      y += 10;

      // Header
      doc.setFillColor(245,247,250); doc.rect(mg,y,cW,6,"F");
      doc.setFont("helvetica","bold"); doc.setFontSize(5.5); doc.setTextColor(100,110,120);
      const sCols = [
        {t:"Data",x:mg+2},{t:"Locacao",x:mg+20},{t:"Cliente",x:mg+40},{t:"Endereco",x:mg+95},
        {t:"Operacao",x:mg+175,a:"center"},{t:"Cacamba",x:mg+205,a:"center"},{t:"Status",x:mg+235,a:"center"},{t:"Comissao",x:W-mg-2,a:"right"}
      ];
      sCols.forEach(c => doc.text(c.t, c.x, y+4, {align:c.a||"left"}));
      y += 7;

      const opClr = { "Colocação":[34,197,94], "Retirada":[139,92,246], "Troca":[249,115,22] };
      ops.forEach((o,i) => {
        checkPage(6);
        if (i%2===0) { doc.setFillColor(250,251,252); doc.rect(mg,y,cW,5.5,"F"); }
        doc.setFont("helvetica","normal"); doc.setFontSize(6); doc.setTextColor(80,80,80);
        doc.text(fmtDate(o.data)||"—", mg+2, y+3.8);
        doc.setFont("helvetica","bold"); doc.setTextColor(26,174,158);
        doc.text(String(o.locacao_id||""), mg+20, y+3.8);
        doc.setFont("helvetica","normal"); doc.setTextColor(40,40,40);
        doc.text(txt(o.cliente,25), mg+40, y+3.8);
        doc.setFontSize(5.5); doc.setTextColor(110,110,110);
        doc.text(txt(o.endereco,40), mg+95, y+3.8);
        doc.setFontSize(6);
        doc.setTextColor(...(opClr[o.tipo_op]||[50,50,50]));
        doc.setFont("helvetica","bold");
        doc.text(o.tipo_op||"", mg+175, y+3.8, {align:"center"});
        doc.setFont("helvetica","normal"); doc.setTextColor(80,80,80);
        doc.text(txt(o.cacamba,10) + (o.tipo_cacamba?" "+o.tipo_cacamba:""), mg+205, y+3.8, {align:"center"});
        doc.setFontSize(5.5);
        doc.text(o.status||"", mg+235, y+3.8, {align:"center"});
        doc.setFontSize(6); doc.setTextColor(22,163,74); doc.setFont("helvetica","bold");
        doc.text(fV(o.comissao), W-mg-2, y+3.8, {align:"right"});
        y += 5.8;
      });

      // Total row
      checkPage(7);
      doc.setFillColor(240,253,244); doc.rect(mg,y,cW,6,"F");
      doc.setDrawColor(26,174,158); doc.setLineWidth(0.4); doc.line(mg,y,mg+cW,y);
      doc.setFont("helvetica","bold"); doc.setFontSize(7); doc.setTextColor(30,30,30);
      doc.text("TOTAL: " + ops.length + " operacoes", mg+4, y+4.2);
      doc.setTextColor(22,163,74);
      doc.text(fV(res.totalComissao), W-mg-2, y+4.2, {align:"right"});
      y += 10;
    }

    // ── Footer em todas as páginas ──
    const np = doc.internal.getNumberOfPages();
    for (let p=1; p<=np; p++) {
      doc.setPage(p);
      doc.setDrawColor(220,225,230); doc.setLineWidth(0.2); doc.line(mg,H-12,W-mg,H-12);
      doc.setFont("helvetica","normal"); doc.setFontSize(6); doc.setTextColor(160,165,170);
      doc.text((empresaNome||"Controla Cacambas") + "  |  Relatorio de Motoristas  |  " + fmtDate(de) + " a " + fmtDate(ate), mg, H-7);
      doc.text("Pagina " + p + " de " + np, W-mg, H-7, {align:"right"});
    }
    doc.save("relatorio-motoristas-" + de + "-a-" + ate + ".pdf");
    addToast("PDF exportado!","success");
  };

  // ─── Bar chart helper (horizontal) ───
  const HBar = ({ items, maxVal, colorFn, labelFn, valueFn, subtextFn }) => (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {items.map((item,i) => {
        const pct = maxVal > 0 ? (valueFn(item)/maxVal)*100 : 0;
        return (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ width:120, fontSize:12, fontWeight:600, color:"#333", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{labelFn(item)}</span>
            <div style={{ flex:1, height:20, background:"#f3f4f6", borderRadius:10, overflow:"hidden", position:"relative" }}>
              <div style={{ width:pct+"%", height:"100%", background:colorFn(i), borderRadius:10, transition:"width .4s", minWidth:pct>0?4:0 }} />
            </div>
            <span style={{ fontSize:12, fontWeight:700, color:"#333", minWidth:50, textAlign:"right" }}>{subtextFn ? subtextFn(item) : valueFn(item)}</span>
          </div>
        );
      })}
    </div>
  );

  const OP_COLORS = ["#22c55e","#8b5cf6","#f97316"];
  const MOT_COLORS = ["#1aae9e","#3b82f6","#8b5cf6","#f97316","#ec4899","#eab308","#06b6d4","#ef4444"];

  return (
    <div>
      {/* Barra de filtros */}
      <div style={{ background:"#fff", borderRadius:10, padding:"14px 18px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,.06)", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
        {[{k:"mes",l:"Este Mês"},{k:"trim",l:"Trimestre"},{k:"ano",l:"Este Ano"}].map(a => (
          <button key={a.k} onClick={()=>setAtalhoAndDates(a.k)} style={{ padding:"6px 14px", fontSize:11, fontWeight:atalho===a.k?700:500, border:"1px solid "+(atalho===a.k?"#1aae9e":"#e0e0e0"), borderRadius:18, background:atalho===a.k?"#1aae9e":"#fff", color:atalho===a.k?"#fff":"#555", cursor:"pointer", fontFamily:FONT }}>{a.l}</button>
        ))}
        <input type="date" style={inp} value={de} onChange={e=>{setDe(e.target.value);setAtalho("");}} />
        <span style={{ color:"#bbb", fontSize:11 }}>até</span>
        <input type="date" style={inp} value={ate} onChange={e=>{setAte(e.target.value);setAtalho("");}} />
        <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
          <button onClick={handlePDF} style={{ background:"linear-gradient(135deg,#dc2626,#ef4444)", color:"#fff", border:"none", borderRadius:18, padding:"7px 16px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:5 }}>📄 Exportar PDF</button>
        </div>
      </div>

      {/* Filtros expandidos */}
      <div style={{ background:"#fff", borderRadius:10, padding:"14px 18px", marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,.06)", display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <label style={{ fontSize:9, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Motorista</label>
          <select value={motorista} onChange={e=>setMotorista(e.target.value)} style={sel}>
            <option value="todos">Todos os Motoristas</option>
            {motoristasUnicos.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <label style={{ fontSize:9, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Operação</label>
          <select value={operacao} onChange={e=>setOperacao(e.target.value)} style={sel}>
            <option value="todos">Todas</option>
            <option value="colocacao">Colocação</option>
            <option value="retirada">Retirada</option>
            <option value="troca">Troca</option>
          </select>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <label style={{ fontSize:9, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Status</label>
          <select value={statusFiltro} onChange={e=>setStatusFiltro(e.target.value)} style={sel}>
            <option value="todos">Todos</option>
            <option value="Para Colocar">Para Colocar</option>
            <option value="Colocada">Colocada</option>
            <option value="Para Retirar">Para Retirar</option>
            <option value="Para Trocar">Para Trocar</option>
            <option value="Encerrada">Encerrada</option>
          </select>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          <label style={{ fontSize:9, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Cliente</label>
          <input placeholder="Buscar cliente..." value={clienteFiltro} onChange={e=>setClienteFiltro(e.target.value)} style={{...inp, minWidth:140}} />
        </div>
        <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#555", cursor:"pointer" }}>
          <input type="checkbox" checked={excluirCanceladas} onChange={e=>setExcluirCanceladas(e.target.checked)} /> Excluir canceladas
        </label>
        <label style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#555", cursor:"pointer" }}>
          <input type="checkbox" checked={somenteEncerradas} onChange={e=>setSomenteEncerradas(e.target.checked)} /> Somente encerradas
        </label>
      </div>

      {loading && <div style={{ textAlign:"center", padding:60, color:"#aaa" }}>Carregando dados...</div>}

      {data && !loading && (
        <div>
          {/* Cards resumo */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(150px, 1fr))", gap:12, marginBottom:20 }}>
            {[
              { label:"Total de Serviços", val:res.totalServicos, icon:"📊", color:"#1aae9e" },
              { label:"Colocações", val:res.totalColocacoes, icon:"📦", color:"#22c55e" },
              { label:"Retiradas", val:res.totalRetiradas, icon:"🔄", color:"#8b5cf6" },
              { label:"Trocas", val:res.totalTrocas, icon:"🔀", color:"#f97316" },
              { label:"Total Comissões", val:finFmt(res.totalComissao), icon:"💰", color:"#3b82f6" },
              { label:isSingle?"Serviços/Dia":"Média/Motorista", val:isSingle?(evol.length>0?Math.round(res.totalServicos/evol.length*10)/10:0):res.mediaServicos, icon:"📈", color:"#06b6d4" },
              ...(!isSingle && res.topServicos ? [{ label:"Top Serviços", val:res.topServicos.nome + " (" + res.topServicos.total + ")", icon:"🏆", color:"#eab308" }] : []),
              ...(!isSingle && res.topComissao ? [{ label:"Top Comissão", val:res.topComissao.nome + " (" + finFmt(res.topComissao.comissao) + ")", icon:"💎", color:"#ec4899" }] : [])
            ].map((c,i) => (
              <div key={i} style={{ background:"#fff", borderRadius:10, padding:"14px 16px", boxShadow:"0 1px 4px rgba(0,0,0,.06)", borderLeft:"4px solid "+c.color }}>
                <div style={{ fontSize:10, color:"#888", fontWeight:600, marginBottom:4 }}>{c.icon} {c.label}</div>
                <div style={{ fontSize:typeof c.val==="string" && c.val.length>10?14:20, fontWeight:800, color:"#1a1a1a" }}>{c.val}</div>
              </div>
            ))}
          </div>

          {/* Gráficos */}
          <div style={{ display:"grid", gridTemplateColumns:isSingle?"1fr 1fr":"1fr 1fr", gap:16, marginBottom:20 }}>
            {/* Gráfico: Distribuição por tipo */}
            <div style={{ background:"#fff", borderRadius:10, padding:18, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
              <h3 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#333" }}>Distribuição por Operação</h3>
              <HBar
                items={[{l:"Colocações",v:res.totalColocacoes},{l:"Retiradas",v:res.totalRetiradas},{l:"Trocas",v:res.totalTrocas}].filter(x=>x.v>0)}
                maxVal={Math.max(res.totalColocacoes,res.totalRetiradas,res.totalTrocas,1)}
                colorFn={i=>OP_COLORS[i%3]}
                labelFn={x=>x.l}
                valueFn={x=>x.v}
                subtextFn={x=>String(x.v)}
              />
              {res.totalServicos===0 && <div style={{ color:"#ccc", fontSize:12, padding:10 }}>Nenhum serviço no período</div>}
            </div>

            {!isSingle ? (
              /* Ranking por motorista */
              <div style={{ background:"#fff", borderRadius:10, padding:18, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
                <h3 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#333" }}>Ranking de Motoristas</h3>
                <HBar
                  items={mots.slice(0,10)}
                  maxVal={mots[0]?.total||1}
                  colorFn={i=>MOT_COLORS[i%MOT_COLORS.length]}
                  labelFn={m=>m.nome}
                  valueFn={m=>m.total}
                  subtextFn={m=>m.total + " serv."}
                />
                {mots.length===0 && <div style={{ color:"#ccc", fontSize:12, padding:10 }}>Nenhum motorista no período</div>}
              </div>
            ) : (
              /* Evolução diária */
              <div style={{ background:"#fff", borderRadius:10, padding:18, boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
                <h3 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#333" }}>Evolução Diária</h3>
                {evol.length > 0 ? (
                  <HBar
                    items={evol.slice(-15)}
                    maxVal={Math.max(...evol.map(e=>e.total),1)}
                    colorFn={()=>"#1aae9e"}
                    labelFn={e=>fmtDate(e.data)}
                    valueFn={e=>e.total}
                    subtextFn={e=>e.total + " serv."}
                  />
                ) : <div style={{ color:"#ccc", fontSize:12, padding:10 }}>Nenhum dado no período</div>}
              </div>
            )}
          </div>

          {/* Gráficos extras */}
          {!isSingle && mots.length > 0 && (
            <div style={{ background:"#fff", borderRadius:10, padding:18, boxShadow:"0 1px 4px rgba(0,0,0,.06)", marginBottom:20 }}>
              <h3 style={{ margin:"0 0 14px", fontSize:13, fontWeight:700, color:"#333" }}>Ranking por Comissão</h3>
              <HBar
                items={[...mots].sort((a,b)=>b.comissao-a.comissao).slice(0,10)}
                maxVal={Math.max(...mots.map(m=>m.comissao),1)}
                colorFn={i=>MOT_COLORS[(i+2)%MOT_COLORS.length]}
                labelFn={m=>m.nome}
                valueFn={m=>m.comissao}
                subtextFn={m=>finFmt(m.comissao)}
              />
            </div>
          )}

          {/* Tabela principal */}
          <div style={{ background:"#fff", borderRadius:10, overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
            <div style={{ background:"linear-gradient(135deg, #1e2330, #2a3142)", padding:"14px 20px", color:"#fff" }}>
              <h3 style={{ margin:0, fontSize:14, fontWeight:700 }}>{isSingle ? "Operações de " + motorista : "Resumo por Motorista"}</h3>
              <div style={{ fontSize:11, color:"rgba(255,255,255,.5)", marginTop:2 }}>{fmtDate(de)} até {fmtDate(ate)} — {isSingle ? ops.length + " operações" : mots.length + " motoristas"}</div>
            </div>
            <div style={{ overflowX:"auto" }}>
              {!isSingle ? (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead><tr style={{ background:"#f8f9fa", borderBottom:"2px solid #e8e8e8" }}>
                    <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#555", fontSize:11 }}>Motorista</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Colocações</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Retiradas</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Trocas</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Total</th>
                    <th style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, color:"#555", fontSize:11 }}>Comissão</th>
                    <th style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, color:"#555", fontSize:11 }}>Última Op.</th>
                  </tr></thead>
                  <tbody>{mots.map((m,i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #f0f0f0", background:i%2===0?"#fff":"#fafbfc", cursor:"pointer" }}
                      onClick={()=>setMotorista(m.nome)}
                      onMouseEnter={e=>e.currentTarget.style.background="#f0fdf4"} onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafbfc"}>
                      <td style={{ padding:"10px 14px", fontWeight:700, color:"#1a1a1a" }}>{m.nome}</td>
                      <td style={{ padding:"10px 8px", textAlign:"center", color:"#22c55e", fontWeight:600 }}>{m.colocacoes}</td>
                      <td style={{ padding:"10px 8px", textAlign:"center", color:"#8b5cf6", fontWeight:600 }}>{m.retiradas}</td>
                      <td style={{ padding:"10px 8px", textAlign:"center", color:"#f97316", fontWeight:600 }}>{m.trocas}</td>
                      <td style={{ padding:"10px 8px", textAlign:"center", fontWeight:800, color:"#1a1a1a" }}>{m.total}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, color:"#16a34a" }}>{finFmt(m.comissao)}</td>
                      <td style={{ padding:"10px 14px", textAlign:"right", fontSize:11, color:"#888" }}>{fmtDate(m.ultimaData)}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot><tr style={{ background:"#f0fdf4", borderTop:"2px solid #1aae9e" }}>
                    <td style={{ padding:"10px 14px", fontWeight:800, color:"#1a1a1a" }}>TOTAL</td>
                    <td style={{ padding:"10px 8px", textAlign:"center", fontWeight:800, color:"#22c55e" }}>{res.totalColocacoes}</td>
                    <td style={{ padding:"10px 8px", textAlign:"center", fontWeight:800, color:"#8b5cf6" }}>{res.totalRetiradas}</td>
                    <td style={{ padding:"10px 8px", textAlign:"center", fontWeight:800, color:"#f97316" }}>{res.totalTrocas}</td>
                    <td style={{ padding:"10px 8px", textAlign:"center", fontWeight:900, color:"#1a1a1a" }}>{res.totalServicos}</td>
                    <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:800, color:"#16a34a" }}>{finFmt(res.totalComissao)}</td>
                    <td />
                  </tr></tfoot>
                </table>
              ) : (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead><tr style={{ background:"#f8f9fa", borderBottom:"2px solid #e8e8e8" }}>
                    <th style={{ padding:"10px 14px", textAlign:"left", fontWeight:700, color:"#555", fontSize:11 }}>Data</th>
                    <th style={{ padding:"10px 8px", textAlign:"left", fontWeight:700, color:"#555", fontSize:11 }}>Locação</th>
                    <th style={{ padding:"10px 8px", textAlign:"left", fontWeight:700, color:"#555", fontSize:11 }}>Cliente</th>
                    <th style={{ padding:"10px 8px", textAlign:"left", fontWeight:700, color:"#555", fontSize:11 }}>Endereço</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Operação</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Caçamba</th>
                    <th style={{ padding:"10px 14px", textAlign:"right", fontWeight:700, color:"#555", fontSize:11 }}>Comissão</th>
                    <th style={{ padding:"10px 8px", textAlign:"center", fontWeight:700, color:"#555", fontSize:11 }}>Status</th>
                  </tr></thead>
                  <tbody>{ops.map((o,i) => {
                    const opColor = o.tipo_op==="Colocação"?"#22c55e":o.tipo_op==="Retirada"?"#8b5cf6":"#f97316";
                    return (
                      <tr key={i} style={{ borderBottom:"1px solid #f0f0f0", background:i%2===0?"#fff":"#fafbfc" }}>
                        <td style={{ padding:"8px 14px", fontSize:11, color:"#555" }}>{fmtDate(o.data)}</td>
                        <td style={{ padding:"8px 8px" }}><span onClick={()=>{ if(setLocDetail) setLocDetail(o.locacao_id); }} style={{ color:"#1aae9e", fontWeight:700, cursor:"pointer", textDecoration:"underline" }}>#{o.locacao_id}</span></td>
                        <td style={{ padding:"8px 8px", maxWidth:150, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.cliente}</td>
                        <td style={{ padding:"8px 8px", fontSize:11, color:"#777", maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{o.endereco}</td>
                        <td style={{ padding:"8px 8px", textAlign:"center" }}><span style={{ background:opColor+"18", color:opColor, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700 }}>{o.tipo_op}</span></td>
                        <td style={{ padding:"8px 8px", textAlign:"center", fontSize:11 }}>{o.cacamba} {o.tipo_cacamba?"("+o.tipo_cacamba+")":""}</td>
                        <td style={{ padding:"8px 14px", textAlign:"right", fontWeight:700, color:"#16a34a" }}>{finFmt(o.comissao)}</td>
                        <td style={{ padding:"8px 8px", textAlign:"center" }}><StatusBadge status={o.status} /></td>
                      </tr>
                    );
                  })}</tbody>
                  <tfoot><tr style={{ background:"#f0fdf4", borderTop:"2px solid #1aae9e" }}>
                    <td colSpan={3} style={{ padding:"10px 14px", fontWeight:800, color:"#1a1a1a" }}>TOTAL — {ops.length} operações</td>
                    <td colSpan={3} />
                    <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:800, color:"#16a34a" }}>{finFmt(res.totalComissao)}</td>
                    <td />
                  </tr></tfoot>
                </table>
              )}
              {((isSingle && ops.length===0) || (!isSingle && mots.length===0)) && (
                <div style={{ padding:40, textAlign:"center" }}><EmptyState icon="🚛" title="Nenhum dado encontrado" subtitle="Ajuste os filtros para visualizar dados de motoristas" /></div>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && !data && <EmptyState icon="🚛" title="Relatório por Motorista" subtitle="Selecione os filtros acima para gerar o relatório" />}
    </div>
  );
}

// ─── Ícone Serviços (Água/Fossa) ──────────────────────────────────────────────
const IcoServicos = (props) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M12 2v6"/><path d="M12 22c-4.2 0-7-1.8-7-4 0-2.2 2.8-4.5 5-7.2C11.2 9.2 12 8 12 8s.8 1.2 2 2.8c2.2 2.7 5 5 5 7.2 0 2.2-2.8 4-7 4z"/></svg>;

// ═══════════════════════════════════════════════════════════════════════════════
// MÓDULO: SERVIÇOS (FOSSA E ÁGUA) — Totalmente isolado do módulo de caçambas
// ═══════════════════════════════════════════════════════════════════════════════

const OS_STATUS = [
  { key:"Aguardando", label:"Aguardando", color:"#f59e0b" },
  { key:"Agendado",   label:"Agendado",   color:"#3b82f6" },
  { key:"Em rota",    label:"Em rota",    color:"#8b5cf6" },
  { key:"Em execução",label:"Em execução",color:"#f97316" },
  { key:"Finalizado", label:"Finalizado", color:"#22c55e" },
  { key:"Cancelado",  label:"Cancelado",  color:"#ef4444" },
];
const osStatusColor = (s) => (OS_STATUS.find(x=>x.key===s)||{}).color || "#888";

// Mapa de próximo status (fluxo linear)
const OS_NEXT_STATUS = {
  "Aguardando": { label:"Agendar", next:"Agendado", color:"#3b82f6" },
  "Agendado":   { label:"Enviar em Rota", next:"Em rota", color:"#8b5cf6" },
  "Em rota":    { label:"Iniciar Execução", next:"Em execução", color:"#f97316" },
  "Em execução":{ label:"Finalizar", next:"Finalizado", color:"#22c55e" },
};

// ─── Menu de ações por linha (genérico para OS) ─────────────────────────────
function OSRowMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top:"auto", right:"auto" });
  const menuRef = useRef();
  const btnRef  = useRef();

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const rightVal = (window.innerWidth - r.right) + "px";
      const menuHeight = 200;
      const spaceBelow = window.innerHeight - r.bottom;
      if (spaceBelow < menuHeight && r.top > menuHeight) {
        setMenuPos({ bottom: (window.innerHeight - r.top + 4) + "px", top: "auto", right: rightVal });
      } else {
        setMenuPos({ top: r.bottom + 4 + "px", bottom: "auto", right: rightVal });
      }
    }
  }, [open]);

  if (!items || !items.length) return null;
  return (
    <div ref={menuRef} style={{ position:"relative", display:"flex", gap:4, alignItems:"center" }}>
      <button ref={btnRef} onClick={() => setOpen(o => !o)} style={{ background:"#f5f5f5", border:"none", borderRadius:3, padding:"5px 10px", cursor:"pointer", color:"#555", fontSize:16, fontWeight:800, lineHeight:1 }}>☰</button>
      {open && (
        <div style={{ position:"fixed", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, boxShadow:"0 6px 28px rgba(0,0,0,.18)", zIndex:9999, minWidth:200, overflow:"hidden", maxHeight:"80vh", overflowY:"auto", top:menuPos.top, bottom:menuPos.bottom, right:menuPos.right }}>
          {items.map((it,i) => (
            <button key={i} onClick={() => { it.action(); setOpen(false); }}
              style={{ width:"100%", textAlign:"left", padding:"9px 14px", background:"none", border:"none", borderTop:i?"1px solid #f5f5f5":"none", cursor:"pointer", fontSize:13, color:it.color||"#333", fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}
              onMouseEnter={e => e.currentTarget.style.background="#f5f8ff"}
              onMouseLeave={e => e.currentTarget.style.background="none"}>
              {it.emoji && <span style={{ fontSize:14 }}>{it.emoji}</span>}{it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Modal: Novo/Editar Atendimento ──────────────────────────────────────────
function OSFormModal({ ordem, clientes, motoristas, frota, tiposServico, categorias, onClose, onSave, apiFetch, addToast }) {
  const isEdit = !!ordem;
  const [form, setForm] = useState({
    cliente_id: ordem?.cliente_id || "",
    cliente_nome: ordem?.cliente_nome || "",
    categoria: ordem?.categoria || "",
    tipo_servico_id: ordem?.tipo_servico_id || "",
    tipo_servico_nome: ordem?.tipo_servico_nome || "",
    status: ordem?.status || "Aguardando",
    endereco: ordem?.endereco || "",
    end_logradouro: ordem?.end_logradouro || "",
    end_numero: ordem?.end_numero || "",
    end_bairro: ordem?.end_bairro || "",
    end_cidade: ordem?.end_cidade || "",
    end_uf: ordem?.end_uf || "",
    end_cep: ordem?.end_cep || "",
    contato_nome: ordem?.contato_nome || "",
    contato_tel: ordem?.contato_tel || "",
    motorista_id: ordem?.motorista_id || "",
    motorista_nome: ordem?.motorista_nome || "",
    veiculo_id: ordem?.veiculo_id || "",
    veiculo_placa: ordem?.veiculo_placa || "",
    data_agendamento: ordem?.data_agendamento || "",
    hora_agendamento: ordem?.hora_agendamento || "",
    hora_inicio: ordem?.hora_inicio || "",
    hora_fim: ordem?.hora_fim || "",
    valor: ordem?.valor || "",
    forma_pagamento: ordem?.forma_pagamento || "dinheiro",
    volume_estimado: ordem?.volume_estimado || "",
    volume_realizado: ordem?.volume_realizado || "",
    taxa_descarte: ordem?.taxa_descarte || "",
    mangueira_extra: !!ordem?.mangueira_extra,
    tipo_local: ordem?.tipo_local || "",
    litragem_solicitada: ordem?.litragem_solicitada || "",
    litragem_entregue: ordem?.litragem_entregue || "",
    destino_agua: ordem?.destino_agua || "",
    ponto_abastecimento: ordem?.ponto_abastecimento || "",
    urgencia: ordem?.urgencia || "normal",
    observacoes: ordem?.observacoes || "",
    obs_tecnica: ordem?.obs_tecnica || "",
    pago: !!ordem?.pago,
    data_pagamento: ordem?.data_pagamento || "",
  });
  const [saving, setSaving] = useState(false);
  const [showClienteSelector, setShowClienteSelector] = useState(false);
  const [enderecos, setEnderecos] = useState([]);

  const upd = (k,v) => setForm(p => ({...p,[k]:v}));

  const handleTipoServico = (tsId) => {
    const ts = tiposServico.find(t => String(t.id) === String(tsId));
    if (ts) {
      upd("tipo_servico_id", ts.id);
      upd("tipo_servico_nome", ts.nome);
      upd("categoria", ts.categoria);
      if (!form.valor || form.valor === "0") upd("valor", ts.valor_padrao || "");
    } else {
      upd("tipo_servico_id", "");
      upd("tipo_servico_nome", "");
    }
  };

  const handleSelectCliente = async (cli) => {
    upd("cliente_id", cli.id);
    upd("cliente_nome", cli.nome);
    upd("contato_tel", cli.tel || "");
    setShowClienteSelector(false);
    try {
      const ends = await apiFetch("/api/clientes/" + cli.id + "/enderecos");
      setEnderecos(ends || []);
      if (ends?.length === 1) {
        const e = ends[0];
        upd("end_logradouro", e.logradouro || "");
        upd("end_numero", e.numero || "");
        upd("end_bairro", e.bairro || "");
        upd("end_cidade", e.cidade || "");
        upd("end_uf", e.uf || "");
        upd("end_cep", e.cep || "");
        upd("endereco", [e.logradouro, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(", "));
      }
    } catch(e){}
  };

  const handleSelectEndereco = (e) => {
    upd("end_logradouro", e.logradouro || "");
    upd("end_numero", e.numero || "");
    upd("end_bairro", e.bairro || "");
    upd("end_cidade", e.cidade || "");
    upd("end_uf", e.uf || "");
    upd("end_cep", e.cep || "");
    upd("endereco", [e.logradouro, e.numero, e.bairro, e.cidade, e.uf].filter(Boolean).join(", "));
  };

  const handleMotorista = (mId) => {
    const m = motoristas.find(x => String(x.id) === String(mId));
    upd("motorista_id", m ? m.id : "");
    upd("motorista_nome", m ? m.nome : "");
  };

  const handleVeiculo = (vId) => {
    const v = frota.find(x => String(x.id) === String(vId));
    upd("veiculo_id", v ? v.id : "");
    upd("veiculo_placa", v ? v.placa : "");
  };

  const handleSave = async () => {
    if (!form.cliente_id) return addToast("Selecione um cliente","error");
    if (!form.categoria) return addToast("Selecione o tipo de serviço","error");
    if (!form.data_agendamento) return addToast("Data de agendamento obrigatória","error");
    setSaving(true);
    try { await onSave(form); } catch(e) { addToast("Erro: "+e.message,"error"); }
    setSaving(false);
  };

  const inpOS = { width:"100%", padding:"7px 10px", border:"1px solid #ccc", borderRadius:3, fontSize:13, color:"#1a1a1a", outline:"none", boxSizing:"border-box", fontFamily:FONT, background:"#fff" };

  return (
    <LargeModal title={isEdit ? "Editar Atendimento" : "Novo Atendimento"} onClose={onClose} maxWidth={760} onSave={handleSave} saveLabel={saving?"Salvando...":isEdit?"Atualizar":"Salvar"}>
      <div style={{ paddingTop:14 }}>
        {/* Cliente */}
        <SectionHeader icon="👤" label="Cliente" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 160px", gap:12 }}>
            <Field label="Cliente *"><input style={{...inpOS, borderColor:!form.cliente_id?"#1aae9e":"#ccc", background:!form.cliente_id?"#f0f4ff":"#fff"}} value={form.cliente_nome} readOnly placeholder="Selecione um cliente" /></Field>
            <div style={{ display:"flex", alignItems:"end" }}><button onClick={()=>setShowClienteSelector(true)} style={{ background:"#1a9e8f", color:"#fff", border:"none", borderRadius:3, padding:"0 12px", fontSize:12, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:FONT, height:33 }}>Selecionar</button></div>
          </div>
          {enderecos.length > 1 && (
            <div style={{ marginTop:10 }}>
              <Field label="Endereço do cliente">
                <select style={inpOS} onChange={e => { const idx = parseInt(e.target.value); if (enderecos[idx]) handleSelectEndereco(enderecos[idx]); }}>
                  <option value="">Selecione...</option>
                  {enderecos.map((ed, i) => <option key={i} value={i}>{[ed.logradouro, ed.numero, ed.bairro, ed.cidade].filter(Boolean).join(", ")}</option>)}
                </select>
              </Field>
            </div>
          )}
        </SectionBody>

        {/* Tipo de Serviço */}
        <SectionHeader icon="📋" label="Serviço" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <Field label="Tipo de Serviço *">
              <select style={{...inpOS, borderColor:!form.tipo_servico_id?"#1aae9e":"#ccc", background:!form.tipo_servico_id?"#f0f4ff":"#fff"}} value={form.tipo_servico_id} onChange={e=>handleTipoServico(e.target.value)}>
                <option value="">-- Selecione --</option>
                {tiposServico.filter(t=>t.ativo!==0).map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </Field>
            <Field label="Categoria">
              <select style={inpOS} value={form.categoria} onChange={e=>upd("categoria",e.target.value)}>
                {(categorias||[]).filter(c=>c.ativo!==0).map(c => <option key={c.id} value={c.nome}>{c.icone} {c.nome}</option>)}
                {!(categorias||[]).some(c=>c.nome===form.categoria) && form.categoria && <option value={form.categoria}>{form.categoria}</option>}
              </select>
            </Field>
          </div>
        </SectionBody>

        {/* Endereço */}
        <SectionHeader icon="📍" label="Endereço" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"3fr 1fr", gap:12, marginBottom:10 }}>
            <Field label="Logradouro"><input style={inpOS} value={form.end_logradouro} onChange={e=>upd("end_logradouro",e.target.value)} /></Field>
            <Field label="Número"><input style={inpOS} value={form.end_numero} onChange={e=>upd("end_numero",e.target.value)} /></Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px", gap:12 }}>
            <Field label="Bairro"><input style={inpOS} value={form.end_bairro} onChange={e=>upd("end_bairro",e.target.value)} /></Field>
            <Field label="Cidade"><input style={inpOS} value={form.end_cidade} onChange={e=>upd("end_cidade",e.target.value)} /></Field>
            <Field label="UF"><input style={inpOS} value={form.end_uf} onChange={e=>upd("end_uf",e.target.value)} maxLength={2} /></Field>
          </div>
        </SectionBody>

        {/* Contato + Agendamento */}
        <SectionHeader icon="📞" label="Contato e Agendamento" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12 }}>
            <Field label="Contato"><input style={inpOS} value={form.contato_nome} onChange={e=>upd("contato_nome",e.target.value)} /></Field>
            <Field label="Telefone"><input style={inpOS} value={form.contato_tel} onChange={e=>upd("contato_tel",e.target.value)} /></Field>
            <Field label="Data Agendamento *"><input type="date" style={{...inpOS, borderColor:!form.data_agendamento?"#1aae9e":"#ccc", background:!form.data_agendamento?"#f0f4ff":"#fff"}} value={form.data_agendamento} onChange={e=>upd("data_agendamento",e.target.value)} /></Field>
            <Field label="Hora Agendamento"><input type="time" style={inpOS} value={form.hora_agendamento} onChange={e=>upd("hora_agendamento",e.target.value)} /></Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginTop:10 }}>
            <Field label="Hora Iniciada"><input type="time" style={inpOS} value={form.hora_inicio} onChange={e=>upd("hora_inicio",e.target.value)} /></Field>
            <Field label="Hora Finalizada"><input type="time" style={inpOS} value={form.hora_fim} onChange={e=>upd("hora_fim",e.target.value)} /></Field>
            <Field label="Tempo de Serviço">
              <div style={{ padding:"7px 10px", background:"#f0f4ff", borderRadius:6, fontSize:13, fontWeight:700, color: form.hora_inicio && form.hora_fim ? "#1aae9e" : "#ccc", border:"1px solid #e5e7eb" }}>
                {(() => {
                  if (!form.hora_inicio || !form.hora_fim) return "—";
                  const [hi,mi] = form.hora_inicio.split(":").map(Number);
                  const [hf,mf] = form.hora_fim.split(":").map(Number);
                  let diff = (hf*60+mf) - (hi*60+mi);
                  if (diff < 0) diff += 1440;
                  const h = Math.floor(diff/60);
                  const m = diff%60;
                  return (h > 0 ? h+"h " : "") + m + "min";
                })()}
              </div>
            </Field>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginTop:10 }}>
            <Field label="Motorista">
              <select style={{...inpOS, appearance:"none"}} value={form.motorista_id} onChange={e=>handleMotorista(e.target.value)}>
                <option value="">-- Selecione --</option>
                {motoristas.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
              </select>
            </Field>
            <Field label="Veículo">
              <select style={{...inpOS, appearance:"none"}} value={form.veiculo_id} onChange={e=>handleVeiculo(e.target.value)}>
                <option value="">-- Selecione --</option>
                {frota.filter(v=>v.status==="Ativo").map(v => <option key={v.id} value={v.id}>{v.placa} - {v.modelo}</option>)}
              </select>
            </Field>
            <Field label="Urgência">
              <select style={inpOS} value={form.urgencia} onChange={e=>upd("urgencia",e.target.value)}>
                <option value="normal">Normal</option>
                <option value="urgente">Urgente</option>
              </select>
            </Field>
          </div>
        </SectionBody>

        {/* Campos de FOSSA */}
        {(form.categoria||"").toLowerCase().includes("fossa") && <>
          <SectionHeader icon="🪣" label="Dados da Fossa" />
          <SectionBody>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              <Field label="Volume Estimado (m³) *"><input type="number" step="0.1" style={{...inpOS, borderColor:!form.volume_estimado?"#1aae9e":"#ccc", background:!form.volume_estimado?"#f0f4ff":"#fff"}} value={form.volume_estimado} onChange={e=>upd("volume_estimado",e.target.value)} /></Field>
              <Field label="Volume Realizado (m³)"><input type="number" step="0.1" style={inpOS} value={form.volume_realizado} onChange={e=>upd("volume_realizado",e.target.value)} /></Field>
              <Field label="Taxa Descarte (R$)"><input type="number" step="0.01" style={inpOS} value={form.taxa_descarte} onChange={e=>upd("taxa_descarte",e.target.value)} /></Field>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:10 }}>
              <Field label="Tipo de Local">
                <select style={inpOS} value={form.tipo_local} onChange={e=>upd("tipo_local",e.target.value)}>
                  <option value="">-- Selecione --</option>
                  <option>Residencial</option><option>Comercial</option><option>Industrial</option><option>Condomínio</option><option>Rural</option>
                </select>
              </Field>
              <div style={{ display:"flex", alignItems:"end", paddingBottom:4 }}>
                <label style={{ fontSize:12, display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                  <input type="checkbox" checked={form.mangueira_extra} onChange={e=>upd("mangueira_extra",e.target.checked)} style={{ accentColor:"#1aae9e" }} /> Mangueira Extra
                </label>
              </div>
            </div>
          </SectionBody>
        </>}

        {/* Campos de ÁGUA */}
        {(form.categoria||"").toLowerCase().includes("agua") && <>
          <SectionHeader icon="💧" label="Dados do Abastecimento" />
          <SectionBody>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <Field label="Litragem Solicitada (L) *"><input type="number" style={{...inpOS, borderColor:!form.litragem_solicitada?"#1aae9e":"#ccc", background:!form.litragem_solicitada?"#f0f4ff":"#fff"}} value={form.litragem_solicitada} onChange={e=>upd("litragem_solicitada",e.target.value)} /></Field>
              <Field label="Litragem Entregue (L)"><input type="number" style={inpOS} value={form.litragem_entregue} onChange={e=>upd("litragem_entregue",e.target.value)} /></Field>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:10 }}>
              <Field label="Destino da Água">
                <select style={inpOS} value={form.destino_agua} onChange={e=>upd("destino_agua",e.target.value)}>
                  <option value="">-- Selecione --</option>
                  <option>Piscina</option><option>Caixa d'água</option><option>Obra</option><option>Navio</option><option>Caminhão</option><option>Outro</option>
                </select>
              </Field>
              <Field label="Ponto de Abastecimento"><input style={inpOS} value={form.ponto_abastecimento} onChange={e=>upd("ponto_abastecimento",e.target.value)} /></Field>
            </div>
          </SectionBody>
        </>}

        {/* Valor + Pagamento */}
        <SectionHeader icon="💰" label="Financeiro" />
        <SectionBody>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr"+(isEdit?" 1fr":""), gap:12 }}>
            <Field label="Valor (R$)"><input type="number" step="0.01" style={inpOS} value={form.valor} onChange={e=>upd("valor",e.target.value)} /></Field>
            <Field label="Forma de Pagamento">
              <select style={inpOS} value={form.forma_pagamento} onChange={e=>upd("forma_pagamento",e.target.value)}>
                <option value="dinheiro">Dinheiro</option><option value="pix">PIX</option><option value="cartao">Cartão</option><option value="boleto">Boleto</option><option value="transferencia">Transferência</option>
              </select>
            </Field>
            {isEdit && <Field label="Status">
              <select style={inpOS} value={form.status} onChange={e=>upd("status",e.target.value)}>
                {OS_STATUS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>}
          </div>
        </SectionBody>

        {/* Observações */}
        <SectionHeader icon="📝" label="Observações" />
        <SectionBody>
          <Field label="Observações"><textarea style={{...inpOS, height:50, resize:"vertical"}} value={form.observacoes} onChange={e=>upd("observacoes",e.target.value)} /></Field>
          <div style={{ marginTop:10 }}><Field label="Observação Técnica"><textarea style={{...inpOS, height:50, resize:"vertical"}} value={form.obs_tecnica} onChange={e=>upd("obs_tecnica",e.target.value)} /></Field></div>
        </SectionBody>
      </div>

      {showClienteSelector && <ClienteSelectorModal clientes={clientes} onSelect={handleSelectCliente} onClose={()=>setShowClienteSelector(false)} />}
    </LargeModal>
  );
}

// ─── Modal: Visualizar Atendimento ───────────────────────────────────────────
function OSViewModal({ ordem, onClose, onEdit, onChangeStatus, onDelete, addToast, apiFetch, categorias }) {
  const [tab, setTab] = useState("info");
  const [historico, setHistorico] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  if (!ordem) return null;
  const catObj = (categorias||[]).find(c => c.nome === ordem.categoria);
  const cat = catObj ? (catObj.icone + " " + catObj.nome) : ordem.categoria;
  const catCor = catObj?.cor || "#888";
  const isFossa = (ordem.categoria||"").toLowerCase().includes("fossa");
  const isAgua = (ordem.categoria||"").toLowerCase().includes("agua") || (ordem.categoria||"").toLowerCase().includes("água");
  const addr = [ordem.end_logradouro, ordem.end_numero, ordem.end_complemento, ordem.end_bairro, ordem.end_cidade, ordem.end_uf].filter(Boolean).join(", ");
  const lbS = { fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase", letterSpacing:.3, marginBottom:2 };
  const vlS = { fontSize:13, color:"#333", fontWeight:600 };

  const calcTempoServico = () => {
    if (!ordem.hora_inicio || !ordem.hora_fim) return null;
    const hi = String(ordem.hora_inicio).substring(0,5);
    const hf = String(ordem.hora_fim).substring(0,5);
    const [hih,him] = hi.split(":").map(Number);
    const [hfh,hfm] = hf.split(":").map(Number);
    let diff = (hfh*60+hfm) - (hih*60+him);
    if (diff < 0) diff += 1440;
    const h = Math.floor(diff/60);
    const m = diff%60;
    return (h > 0 ? h+"h " : "") + m + "min";
  };

  const loadHistorico = async () => {
    setHistLoading(true);
    try {
      const data = await apiFetch("/api/os-historico/" + ordem.id);
      setHistorico(data || []);
    } catch(e) { console.error("Erro histórico OS:", e); }
    setHistLoading(false);
  };

  const gerarOSPDF = () => {
    const w = window.open("","_blank","width=800,height=600");
    if (!w) return addToast("Popup bloqueado","error");
    const tempo = calcTempoServico();
    w.document.write(`<!DOCTYPE html><html><head><title>OS ${ordem.numero}</title><style>body{font-family:Arial,sans-serif;padding:20px;font-size:13px}h2{text-align:center;margin:0 0 4px}table{width:100%;border-collapse:collapse;margin:10px 0}td{padding:6px 8px;border:1px solid #ddd}td:first-child{font-weight:bold;width:180px;background:#f9f9f9}.header{text-align:center;margin-bottom:16px}.footer{margin-top:30px;text-align:center;font-size:11px;color:#888}@media print{body{padding:10px}}</style></head><body>`);
    w.document.write(`<div class="header"><h2>ORDEM DE SERVIÇO</h2><p style="font-size:11px;color:#666">Nº ${ordem.numero}</p></div>`);
    w.document.write(`<table><tr><td>Cliente</td><td>${ordem.cliente_nome||''}</td></tr>`);
    w.document.write(`<tr><td>Endereço</td><td>${addr}</td></tr>`);
    w.document.write(`<tr><td>Contato</td><td>${ordem.contato_nome||''} ${ordem.contato_tel?'- '+ordem.contato_tel:''}</td></tr>`);
    w.document.write(`<tr><td>Tipo</td><td>${cat} — ${ordem.tipo_servico_nome||''}</td></tr>`);
    w.document.write(`<tr><td>Data Agendamento</td><td>${fmtDate(ordem.data_agendamento)} ${ordem.hora_agendamento||''}</td></tr>`);
    if (ordem.hora_inicio) w.document.write(`<tr><td>Hora Iniciada</td><td>${String(ordem.hora_inicio).substring(0,5)}</td></tr>`);
    if (ordem.hora_fim) w.document.write(`<tr><td>Hora Finalizada</td><td>${String(ordem.hora_fim).substring(0,5)}</td></tr>`);
    if (tempo) w.document.write(`<tr><td>Tempo de Serviço</td><td>${tempo}</td></tr>`);
    w.document.write(`<tr><td>Motorista</td><td>${ordem.motorista_nome||'—'}</td></tr>`);
    w.document.write(`<tr><td>Veículo</td><td>${ordem.veiculo_placa||'—'}</td></tr>`);
    w.document.write(`<tr><td>Status</td><td>${ordem.status}</td></tr>`);
    if (isFossa) {
      w.document.write(`<tr><td>Volume Estimado</td><td>${ordem.volume_estimado||'—'} m³</td></tr>`);
      w.document.write(`<tr><td>Volume Realizado</td><td>${ordem.volume_realizado||'—'} m³</td></tr>`);
      w.document.write(`<tr><td>Tipo de Local</td><td>${ordem.tipo_local||'—'}</td></tr>`);
      w.document.write(`<tr><td>Mangueira Extra</td><td>${ordem.mangueira_extra?'Sim':'Não'}</td></tr>`);
      w.document.write(`<tr><td>Taxa Descarte</td><td>R$ ${parseFloat(ordem.taxa_descarte||0).toFixed(2)}</td></tr>`);
    } else {
      w.document.write(`<tr><td>Litragem Solicitada</td><td>${ordem.litragem_solicitada||'—'} L</td></tr>`);
      w.document.write(`<tr><td>Litragem Entregue</td><td>${ordem.litragem_entregue||'—'} L</td></tr>`);
      w.document.write(`<tr><td>Destino</td><td>${ordem.destino_agua||'—'}</td></tr>`);
      w.document.write(`<tr><td>Ponto Abastecimento</td><td>${ordem.ponto_abastecimento||'—'}</td></tr>`);
    }
    w.document.write(`<tr><td>Valor</td><td>R$ ${parseFloat(ordem.valor||0).toFixed(2)}</td></tr>`);
    w.document.write(`<tr><td>Pagamento</td><td>${ordem.forma_pagamento||'—'} ${ordem.pago?'(Pago)':'(Pendente)'}</td></tr>`);
    if (ordem.observacoes) w.document.write(`<tr><td>Observações</td><td>${ordem.observacoes}</td></tr>`);
    if (ordem.obs_tecnica) w.document.write(`<tr><td>Obs. Técnica</td><td>${ordem.obs_tecnica}</td></tr>`);
    w.document.write(`</table>`);
    w.document.write(`<div style="margin-top:40px;display:flex;justify-content:space-between"><div style="border-top:1px solid #333;width:200px;text-align:center;padding-top:4px;font-size:11px">Assinatura do Cliente</div><div style="border-top:1px solid #333;width:200px;text-align:center;padding-top:4px;font-size:11px">Assinatura do Responsável</div></div>`);
    w.document.write(`<div class="footer">Documento gerado em ${new Date().toLocaleString("pt-BR",{timeZone:"America/Sao_Paulo"})}</div>`);
    w.document.write(`</body></html>`);
    w.document.close();
    setTimeout(()=>w.print(), 300);
  };

  const tempo = calcTempoServico();

  return (
    <div className="modal-overlay" style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div className="modal-box" style={{ background:"#fff", width:"100%", maxWidth:760, maxHeight:"90vh", display:"flex", flexDirection:"column", borderRadius:4, boxShadow:"0 12px 48px rgba(0,0,0,.22)", fontFamily:FONT, overflow:"hidden" }}>
        {/* Header */}
        <div style={{ padding:"14px 22px", borderBottom:"1px solid #e4e4e4", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontWeight:800, fontSize:16, color:"#1a1a1a" }}>OS {ordem.numero}</div>
            <div style={{ fontSize:12, color:"#888", marginTop:2 }}>
              {ordem.cliente_nome} — <span style={{ fontWeight:600, color:ordem.pago?"#2ecc71":"#e74c3c" }}>{ordem.pago?"PAGO":"NÃO PAGO"}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#aaa", fontSize:20 }}>✕</button>
        </div>

        {/* Status badges */}
        <div style={{ padding:"10px 22px", display:"flex", gap:8, alignItems:"center", borderBottom:"1px solid #f0f0f0" }}>
          <span style={{ padding:"3px 9px", borderRadius:3, background:osStatusColor(ordem.status), color:"#fff", fontSize:11, fontWeight:600, display:"inline-flex", alignItems:"center", gap:5 }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:"#fff", flexShrink:0 }}></span>{ordem.status}
          </span>
          <span style={{ padding:"3px 9px", borderRadius:3, background:catCor+"22", color:catCor, fontSize:11, fontWeight:600 }}>{cat}</span>
          {ordem.urgencia==="urgente" && <span style={{ padding:"3px 9px", borderRadius:3, background:"#fde8e8", color:"#c0392b", fontSize:11, fontWeight:600 }}>URGENTE</span>}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:"2px solid #ebebeb", padding:"0 22px" }}>
          {[{k:"info",l:"Informações"},{k:"historico",l:"Linha do Tempo"},{k:"acoes",l:"Ações"}].map(t => (
            <button key={t.k} onClick={()=>{setTab(t.k);if(t.k==="historico"&&historico.length===0)loadHistorico();}} style={{ padding:"10px 16px", border:"none", background:"none", cursor:"pointer", fontWeight:tab===t.k?700:400, color:tab===t.k?"#1aae9e":"#888", borderBottom:tab===t.k?"2px solid #1aae9e":"2px solid transparent", marginBottom:-2, fontFamily:FONT, fontSize:13 }}>{t.l}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex:1, overflowY:"auto", padding:"18px 22px" }}>
          {tab === "info" && <>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Cliente</div><div style={vlS}>{ordem.cliente_nome}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Tipo de Serviço</div><div style={vlS}>{ordem.tipo_servico_nome || "—"}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Endereço</div><div style={vlS}>{addr || "—"}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Contato</div><div style={vlS}>{ordem.contato_nome || "—"} {ordem.contato_tel ? "— "+ordem.contato_tel : ""}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Data Agendamento</div><div style={vlS}>{fmtDate(ordem.data_agendamento)} {ordem.hora_agendamento || ""}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Motorista</div><div style={vlS}>{ordem.motorista_nome || "—"}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Veículo</div><div style={vlS}>{ordem.veiculo_placa || "—"}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Valor</div><div style={vlS}>R$ {parseFloat(ordem.valor||0).toFixed(2)}</div></div>
              <div style={{ borderBottom:"1px solid #f5f5f5", paddingBottom:8 }}><div style={lbS}>Forma Pagamento</div><div style={vlS}>{ordem.forma_pagamento || "—"}</div></div>
            </div>

            {/* Bloco Hora Iniciada / Finalizada / Tempo */}
            {(ordem.hora_inicio || ordem.hora_fim) && (
              <div style={{ border:"1px solid #d1fae5", borderRadius:8, padding:14, background:"#f0fdf4", marginTop:16 }}>
                <div style={{ fontWeight:800, fontSize:12, color:"#166534", display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>⏱️ Tempo de Serviço</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                  <div><div style={lbS}>Hora Iniciada</div><div style={vlS}>{ordem.hora_inicio ? String(ordem.hora_inicio).substring(0,5) : "—"}</div></div>
                  <div><div style={lbS}>Hora Finalizada</div><div style={vlS}>{ordem.hora_fim ? String(ordem.hora_fim).substring(0,5) : "—"}</div></div>
                  <div><div style={lbS}>Duração</div><div style={{ ...vlS, color:"#1aae9e" }}>{tempo || "—"}</div></div>
                </div>
              </div>
            )}

            {/* Bloco específico fossa/agua */}
            {isFossa && (
              <div style={{ gridColumn:"1/-1", border:"1px solid #e8e8e8", borderRadius:8, padding:14, background:"#fafafa", marginTop:16 }}>
                <div style={{ fontWeight:800, fontSize:12, color:"#92400e", display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>🪣 Dados da Fossa</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                  <div><div style={lbS}>Vol. Estimado</div><div style={vlS}>{ordem.volume_estimado || "—"} m³</div></div>
                  <div><div style={lbS}>Vol. Realizado</div><div style={vlS}>{ordem.volume_realizado || "—"} m³</div></div>
                  <div><div style={lbS}>Taxa Descarte</div><div style={vlS}>R$ {parseFloat(ordem.taxa_descarte||0).toFixed(2)}</div></div>
                  <div><div style={lbS}>Tipo Local</div><div style={vlS}>{ordem.tipo_local || "—"}</div></div>
                  <div><div style={lbS}>Mangueira Extra</div><div style={vlS}>{ordem.mangueira_extra ? "Sim" : "Não"}</div></div>
                </div>
              </div>
            )}
            {isAgua && (
              <div style={{ gridColumn:"1/-1", border:"1px solid #e8e8e8", borderRadius:8, padding:14, background:"#fafafa", marginTop:16 }}>
                <div style={{ fontWeight:800, fontSize:12, color:"#1e40af", display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>💧 Dados do Abastecimento</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div><div style={lbS}>Litragem Solicitada</div><div style={vlS}>{ordem.litragem_solicitada || "—"} L</div></div>
                  <div><div style={lbS}>Litragem Entregue</div><div style={vlS}>{ordem.litragem_entregue || "—"} L</div></div>
                  <div><div style={lbS}>Destino</div><div style={vlS}>{ordem.destino_agua || "—"}</div></div>
                  <div><div style={lbS}>Ponto Abastecimento</div><div style={vlS}>{ordem.ponto_abastecimento || "—"}</div></div>
                </div>
              </div>
            )}

            {(ordem.observacoes || ordem.obs_tecnica) && (
              <div style={{ marginTop:16, borderTop:"1px solid #f0f0f0", paddingTop:12 }}>
                {ordem.observacoes && <div><div style={lbS}>Observações</div><div style={{ fontSize:12, color:"#555" }}>{ordem.observacoes}</div></div>}
                {ordem.obs_tecnica && <div style={{ marginTop:8 }}><div style={lbS}>Obs. Técnica</div><div style={{ fontSize:12, color:"#555" }}>{ordem.obs_tecnica}</div></div>}
              </div>
            )}
          </>}

          {tab === "historico" && (
            <div>
              {histLoading ? (
                <div style={{ textAlign:"center", padding:30, color:"#aaa", fontSize:13 }}>Carregando histórico...</div>
              ) : historico.length === 0 ? (
                <EmptyState icon="🕐" title="Nenhuma atividade registrada" subtitle="O histórico de alterações aparecerá aqui" />
              ) : (
                <div style={{ position:"relative", paddingLeft:24 }}>
                  <div style={{ position:"absolute", left:8, top:0, bottom:0, width:2, background:"#e8e8e8" }} />
                  {historico.map(h => (
                    <div key={h.id} style={{ position:"relative", marginBottom:18, paddingLeft:16 }}>
                      <div style={{ position:"absolute", left:-20, top:4, width:12, height:12, borderRadius:"50%", background: h.acao?.includes("Criação")?"#1aae9e": h.acao?.includes("Status")?"#2ecc71": h.acao?.includes("Edição")?"#f39c12": h.acao?.includes("Pagamento")?"#3b82f6":"#888", border:"2px solid #fff", boxShadow:"0 0 0 2px #e8e8e8" }} />
                      <div style={{ fontSize:12, fontWeight:700, color:"#1a1a1a" }}>{h.acao}</div>
                      <div style={{ fontSize:12, color:"#555", marginTop:2 }}>{h.detalhes||""}</div>
                      <div style={{ fontSize:11, color:"#aaa", marginTop:4 }}>
                        {h.usuario} · {new Date(h.created_at).toLocaleString("pt-BR", { timeZone:"America/Sao_Paulo" })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "acoes" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <button onClick={gerarOSPDF} style={{ padding:"12px 16px", background:"#f0f4ff", color:"#1a5fa8", border:"1px solid #c7d7fd", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>📄 Gerar OS / PDF</button>
              <button onClick={()=>window.print()} style={{ padding:"12px 16px", background:"#f0f4ff", color:"#7c3aed", border:"1px solid #d4c7fd", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>🖨️ Imprimir</button>
              {ordem.status !== "Finalizado" && ordem.status !== "Cancelado" && (
                <button onClick={()=>onEdit(ordem)} style={{ padding:"12px 16px", background:"#fff3e0", color:"#e65100", border:"1px solid #ffcc80", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>✏️ Editar</button>
              )}
              {OS_NEXT_STATUS[ordem.status] && (
                <button onClick={()=>onChangeStatus(ordem.id,OS_NEXT_STATUS[ordem.status].next)} style={{ padding:"12px 16px", background:"#e6f4ea", color:"#1a7a38", border:"1px solid #a8d5ba", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>➡️ {OS_NEXT_STATUS[ordem.status].label}</button>
              )}
              {!ordem.pago && (ordem.status === "Finalizado" || ordem.status === "Em execução") && (
                <button onClick={()=>onChangeStatus(ordem.id,"pago")} style={{ padding:"12px 16px", background:"#e6f4ea", color:"#16a34a", border:"1px solid #a8d5ba", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>💰 Marcar Pago</button>
              )}
              {ordem.status !== "Cancelado" && ordem.status !== "Finalizado" && (
                <button onClick={()=>{if(window.confirm("Cancelar este atendimento?"))onChangeStatus(ordem.id,"Cancelado");}} style={{ padding:"12px 16px", background:"#fde8e8", color:"#c0392b", border:"1px solid #f5a8a8", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>❌ Cancelar OS</button>
              )}
              {ordem.status !== "Cancelado" && ordem.status !== "Finalizado" && (
                <button onClick={()=>onDelete(ordem.id)} style={{ padding:"12px 16px", background:"#fde8e8", color:"#c0392b", border:"1px solid #f5a8a8", borderRadius:3, cursor:"pointer", fontWeight:600, fontSize:13, fontFamily:FONT, display:"flex", alignItems:"center", gap:8 }}>🗑️ Excluir</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Página: Serviços (Fossa e Água) ─────────────────────────────────────────
function ServicosPage({ clientes, motoristas, frota, addToast, apiFetch }) {
  const [ordens, setOrdens] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("Aguardando");
  const [busca, setBusca] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editOrdem, setEditOrdem] = useState(null);
  const [viewOrdem, setViewOrdem] = useState(null);
  const [selecionadas, setSelecionadas] = useState([]);
  const [acaoOpen, setAcaoOpen] = useState(false);

  const loadOrdens = useCallback(async () => {
    setLoading(true);
    try {
      const [os, ts, cats] = await Promise.all([
        apiFetch("/api/ordens-servico"),
        apiFetch("/api/tipos-servico"),
        apiFetch("/api/categorias-servico"),
      ]);
      setOrdens(os || []);
      setTiposServico(ts || []);
      setCategorias(cats || []);
    } catch(e) { console.error("Erro carregar serviços:", e); }
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { loadOrdens(); }, [loadOrdens]);

  const filtered = ordens.filter(o => {
    if (o.status !== statusTab) return false;
    if (busca) {
      const s = busca.toLowerCase();
      return (o.cliente_nome||'').toLowerCase().includes(s) || (o.numero||'').toLowerCase().includes(s) || (o.endereco||'').toLowerCase().includes(s) || (o.tipo_servico_nome||'').toLowerCase().includes(s) || (o.contato_tel||'').includes(s);
    }
    return true;
  });

  const handleSave = async (form) => {
    if (editOrdem) {
      await apiFetch("/api/ordens-servico/" + editOrdem.id, { method:"PUT", body:form });
      addToast("Atendimento atualizado!","success");
    } else {
      await apiFetch("/api/ordens-servico", { method:"POST", body:form });
      addToast("Atendimento criado!","success");
    }
    setShowForm(false);
    setEditOrdem(null);
    loadOrdens();
  };

  const handleChangeStatus = async (id, newStatus) => {
    try {
      if (newStatus === "pago") {
        await apiFetch("/api/ordens-servico/" + id + "/pagamento", { method:"PATCH", body:{ pago:true, data_pagamento: new Date().toISOString().split('T')[0] } });
        addToast("Marcado como pago!","Atendimento atualizado","💰","#16a34a");
      } else {
        await apiFetch("/api/ordens-servico/" + id + "/status", { method:"PATCH", body:{ status: newStatus } });
        addToast("Status → " + newStatus,"Atendimento movido com sucesso","✅",osStatusColor(newStatus));
      }
      loadOrdens();
      setViewOrdem(null);
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const handleBatchAction = async (newStatus) => {
    if (!selecionadas.length) return;
    const label = newStatus === "pago" ? "Marcar Pago" : OS_NEXT_STATUS[statusTab]?.label || newStatus;
    if (!window.confirm(`${label} para ${selecionadas.length} atendimento(s)?`)) return;
    try {
      for (const id of selecionadas) {
        if (newStatus === "pago") {
          await apiFetch("/api/ordens-servico/" + id + "/pagamento", { method:"PATCH", body:{ pago:true, data_pagamento: new Date().toISOString().split('T')[0] } });
        } else {
          await apiFetch("/api/ordens-servico/" + id + "/status", { method:"PATCH", body:{ status: newStatus } });
        }
      }
      addToast("Status → " + (newStatus==="pago"?"Pago":newStatus), selecionadas.length+" atendimento(s) atualizado(s)","✅",newStatus==="pago"?"#16a34a":osStatusColor(newStatus));
      setSelecionadas([]);
      loadOrdens();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
    setAcaoOpen(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este atendimento?")) return;
    try {
      await apiFetch("/api/ordens-servico/" + id, { method:"DELETE" });
      addToast("Atendimento excluído","success");
      loadOrdens();
      setViewOrdem(null);
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const handleEdit = (o) => { setViewOrdem(null); setEditOrdem(o); setShowForm(true); };

  const toggleSel = (id) => setSelecionadas(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelecionadas(selecionadas.length === filtered.length ? [] : filtered.map(o => o.id));

  const nextAction = OS_NEXT_STATUS[statusTab];
  const canPay = statusTab === "Finalizado";

  // ─── Aba de gerenciamento de Categorias ───
  const [pageTab, setPageTab] = useState("atendimentos");
  const [catForm, setCatForm] = useState(null);
  const [catEdit, setCatEdit] = useState(null);
  const [catSaving, setCatSaving] = useState(false);

  const ICONES_DISPONIVEIS = ["🪣","💧","🚿","🔧","🧹","🚰","💦","🧪","📦","🔩","🏗️","♻️"];

  const handleSaveCat = async () => {
    const f = catEdit || catForm;
    if (!f.nome) return addToast("Nome da categoria obrigatório","error");
    setCatSaving(true);
    try {
      if (catEdit) {
        await apiFetch("/api/categorias-servico/"+catEdit.id, { method:"PUT", body:f });
        addToast("Categoria atualizada!","success");
      } else {
        await apiFetch("/api/categorias-servico", { method:"POST", body:f });
        addToast("Categoria criada!","success");
      }
      setCatForm(null); setCatEdit(null); loadOrdens();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
    setCatSaving(false);
  };
  const handleDeleteCat = async (id) => {
    if (!window.confirm("Excluir esta categoria?")) return;
    try {
      await apiFetch("/api/categorias-servico/"+id, { method:"DELETE" });
      addToast("Categoria excluída","success");
      loadOrdens();
    } catch(e) { addToast(e.message||"Erro ao excluir","error"); }
  };

  return (
    <div>
      {/* Page Tabs: Atendimentos | Categorias */}
      <div style={{ display:"flex", gap:0, marginBottom:16, borderBottom:"2px solid #ebebeb" }}>
        {[{k:"atendimentos",l:"Atendimentos",icon:"📋"},{k:"categorias",l:"Categorias",icon:"🏷️"}].map(t => (
          <button key={t.k} onClick={()=>setPageTab(t.k)} style={{ padding:"10px 20px", border:"none", background:"none", cursor:"pointer", fontWeight:pageTab===t.k?700:400, color:pageTab===t.k?"#1aae9e":"#888", borderBottom:pageTab===t.k?"3px solid #1aae9e":"3px solid transparent", marginBottom:-2, fontFamily:FONT, fontSize:14, display:"flex", alignItems:"center", gap:6 }}>{t.icon} {t.l}</button>
        ))}
      </div>

      {pageTab === "categorias" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:"#222" }}>{categorias.length} categoria(s)</div>
              <div style={{ fontSize:11, color:"#aaa", marginTop:2 }}>Crie categorias como Fossa, Água, Sucção, Desentupimento, etc.</div>
            </div>
            {!catForm && !catEdit && (
              <button onClick={()=>setCatForm({ nome:"", icone:"📦", cor:"#888888" })} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>+ Nova Categoria</button>
            )}
          </div>

          {/* Form nova/editar categoria */}
          {(catForm || catEdit) && (
            <div style={{ background:"#f8f9fa", border:"1px solid #e5e7eb", borderRadius:8, padding:16, marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:"#1aae9e", marginBottom:12 }}>{catEdit ? "Editar Categoria" : "Nova Categoria"}</div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12 }}>
                <div>
                  <label style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase" }}>Nome da Categoria</label>
                  <input value={(catEdit||catForm).nome} onChange={e=>{const v=e.target.value;catEdit?setCatEdit(f=>({...f,nome:v})):setCatForm(f=>({...f,nome:v}));}} placeholder="Ex: Fossa, Água, Sucção, Desentupimento..." style={{ width:"100%", padding:"8px 10px", borderRadius:4, border:"1px solid #ddd", fontSize:13, fontFamily:FONT, marginTop:4, boxSizing:"border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase" }}>Cor</label>
                  <input type="color" value={(catEdit||catForm).cor||"#888888"} onChange={e=>{const v=e.target.value;catEdit?setCatEdit(f=>({...f,cor:v})):setCatForm(f=>({...f,cor:v}));}} style={{ width:"100%", height:37, borderRadius:4, border:"1px solid #ddd", cursor:"pointer", marginTop:4, padding:2 }} />
                </div>
              </div>
              <div style={{ marginTop:10 }}>
                <label style={{ fontSize:10, fontWeight:700, color:"#aaa", textTransform:"uppercase" }}>Ícone</label>
                <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
                  {ICONES_DISPONIVEIS.map(ic => (
                    <button key={ic} onClick={()=>{catEdit?setCatEdit(f=>({...f,icone:ic})):setCatForm(f=>({...f,icone:ic}));}} style={{ width:36, height:36, borderRadius:6, border:(catEdit||catForm).icone===ic?"2px solid #1aae9e":"1px solid #e0e0e0", background:(catEdit||catForm).icone===ic?"#f0fdf4":"#fff", cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}>{ic}</button>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
                <button onClick={()=>{setCatForm(null);setCatEdit(null);}} style={{ padding:"7px 16px", background:"#f4f4f4", border:"1px solid #e0e0e0", borderRadius:4, color:"#666", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Cancelar</button>
                <button onClick={handleSaveCat} disabled={catSaving} style={{ padding:"7px 16px", background:"#1aae9e", border:"none", borderRadius:4, color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>{catSaving?"Salvando...":"Salvar"}</button>
              </div>
            </div>
          )}

          {/* Lista de categorias */}
          {categorias.length === 0 ? (
            <EmptyState icon="🏷️" title="Nenhuma categoria cadastrada" subtitle="Crie categorias para organizar seus serviços" />
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              {categorias.map(c => {
                const qtd = ordens.filter(o => o.categoria === c.nome).length;
                return (
                  <div key={c.id} style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:10, padding:16, position:"relative", borderLeft:"4px solid "+(c.cor||"#888") }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                      <span style={{ fontSize:24 }}>{c.icone||"📦"}</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:"#222" }}>{c.nome}</div>
                        <div style={{ fontSize:11, color:"#aaa" }}>{qtd} atendimento(s)</div>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                      <span style={{ width:12, height:12, borderRadius:3, background:c.cor||"#888", display:"inline-block" }}></span>
                      <span style={{ fontSize:11, color:"#888" }}>{c.cor||"#888"}</span>
                      <span style={{ marginLeft:"auto", fontSize:11, fontWeight:700, color:c.ativo!==0?"#2ecc71":"#e74c3c" }}>{c.ativo!==0?"Ativo":"Inativo"}</span>
                    </div>
                    <div style={{ display:"flex", gap:6, marginTop:10 }}>
                      <button onClick={()=>{setCatForm(null);setCatEdit({...c});}} style={{ flex:1, padding:"6px 0", background:"#f0f4ff", color:"#3b82f6", border:"1px solid #c7d7fd", borderRadius:4, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Editar</button>
                      <button onClick={()=>handleDeleteCat(c.id)} style={{ flex:1, padding:"6px 0", background:"#fde8e8", color:"#e74c3c", border:"1px solid #f5a8a8", borderRadius:4, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>Excluir</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {pageTab === "atendimentos" && <>
      {/* Action Bar */}
      <div className="page-action-bar" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
          {selecionadas.length > 0 && (nextAction || canPay) && (
            <div style={{ position:"relative" }}>
              <button onClick={() => setAcaoOpen(o => !o)} style={{ background:"#f0f4ff", color:"#1aae9e", border:"1px solid #c7d7fd", borderRadius:3, padding:"8px 14px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
                ⚡Ações ({selecionadas.length}) <span style={{ fontSize:10 }}>▾</span>
              </button>
              {acaoOpen && (
                <div style={{ position:"absolute", top:"100%", left:0, marginTop:3, background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, boxShadow:"0 4px 16px rgba(0,0,0,.1)", zIndex:100, minWidth:240, overflow:"hidden" }}>
                  {nextAction && <button onClick={()=>handleBatchAction(nextAction.next)} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:13, color:nextAction.color, fontFamily:FONT, fontWeight:600 }} onMouseEnter={e=>e.currentTarget.style.background="#f5f5f5"} onMouseLeave={e=>e.currentTarget.style.background="none"}>➡️ {nextAction.label}</button>}
                  {canPay && <button onClick={()=>handleBatchAction("pago")} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#16a34a", fontFamily:FONT, fontWeight:600 }} onMouseEnter={e=>e.currentTarget.style.background="#f5f5f5"} onMouseLeave={e=>e.currentTarget.style.background="none"}>💰 Marcar Pago</button>}
                  <div style={{ borderTop:"1px solid #f0f0f0" }}></div>
                  <button onClick={()=>{handleBatchAction("Cancelado");}} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#e74c3c", fontFamily:FONT, fontWeight:600 }} onMouseEnter={e=>e.currentTarget.style.background="#f5f5f5"} onMouseLeave={e=>e.currentTarget.style.background="none"}>❌ Cancelar</button>
                </div>
              )}
            </div>
          )}
          <button onClick={()=>{setEditOrdem(null);setShowForm(true);}} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
            <IcoPlus /> Novo Atendimento
          </button>
          {selecionadas.length > 0 && (
            <span style={{ fontSize:12, color:"#1aae9e", fontWeight:700, padding:"4px 10px", background:"#f0f4ff", borderRadius:3 }}>{selecionadas.length} selecionada(s)</span>
          )}
        </div>
        <div style={{ position:"relative" }}>
          <input value={busca} onChange={e=>setBusca(e.target.value)} placeholder="Buscar por cliente, nº, telefone..." style={{ padding:"8px 14px 8px 34px", borderRadius:4, border:"1px solid #e0e0e0", fontSize:13, fontFamily:FONT, width:260, background:"#fafafa" }} />
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:14, color:"#aaa", pointerEvents:"none" }}>🔍</span>
          {busca && <button onClick={()=>setBusca("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:14, color:"#aaa", padding:0 }}>✕</button>}
        </div>
      </div>

      {/* Status Tabs — estilo Locações */}
      <div className="status-tabs-row" style={{ display:"flex", gap:0, marginBottom:0, borderBottom:"2px solid #ebebeb", overflowX:"auto", position:"relative" }}>
        {OS_STATUS.map(t => {
          const count = ordens.filter(o => o.status === t.key).length;
          const active = statusTab === t.key;
          return (
            <button key={t.key} onClick={() => { setStatusTab(t.key); setSelecionadas([]); setAcaoOpen(false); }} style={{
              padding:"10px 16px", fontSize:12, fontWeight:active?700:500, border:"none",
              background: active ? t.color+"14" : "none",
              cursor:"pointer", color: active ? t.color : "#888",
              borderBottom: active ? "3px solid "+t.color : "3px solid transparent",
              borderTop: active ? "3px solid "+t.color : "3px solid transparent",
              borderRadius: active ? "6px 6px 0 0" : "0",
              marginBottom:-2, whiteSpace:"nowrap", fontFamily:FONT,
              display:"flex", alignItems:"center", gap:5,
              transition:"all 0.25s ease",
            }}>
              {t.label}
              <span style={{ background:active?t.color:"#eee", color:active?"#fff":"#888", borderRadius:10, padding:"2px 8px", fontSize:10, fontWeight:700, transition:"all 0.25s ease" }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Tabela — estilo Locações */}
      <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderTop:"none", borderRadius:"0 0 4px 4px", overflowX:"auto" }}>
        <table className="tbl-loc" style={{ width:"100%", borderCollapse:"collapse", fontSize:13, tableLayout:"auto" }}>
          <thead style={{ background:"#fafafa" }}>
            <tr>
              <th style={{ padding:"9px 13px", borderBottom:"1px solid #ebebeb", width:40 }}>
                <input type="checkbox" checked={selecionadas.length===filtered.length && filtered.length>0} onChange={toggleAll} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
              </th>
              {["Nº OS","Cliente / Contato / Endereço","Tipo","Data","Motorista","Vol/Litros","Valor","Pago?",""].map(h => (
                <th key={h} style={{ textAlign:h==="Pago?"?"center":"left", padding:"9px 13px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #ebebeb" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10}><div style={{ textAlign:"center", padding:40, color:"#aaa" }}>Carregando...</div></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10}><EmptyState icon={statusTab==="Aguardando"?"📋":"📦"} title="Nenhum atendimento encontrado" subtitle="Clique em 'Novo Atendimento' para cadastrar" /></td></tr>
            )}
            {!loading && filtered.map(o => {
              const sel = selecionadas.includes(o.id);
              const tel = o.contato_tel || "";
              return (
                <tr key={o.id} style={{ borderBottom:"2px solid #e0e0e0", background:sel?"#fff8f4":"" }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background="#fafafa"; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background=""; }}>
                  <td style={{ padding:"10px 13px" }}>
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(o.id)} style={{ accentColor:"#1aae9e", cursor:"pointer" }} />
                  </td>
                  <td style={{ padding:"10px 13px" }}>
                    <span onClick={() => setViewOrdem(o)} style={{ color:"#1a9e8f", fontWeight:600, fontSize:12, cursor:"pointer", textDecoration:"underline" }}>{o.numero}</span>
                  </td>
                  <td style={{ padding:"10px 13px", maxWidth:260 }}>
                    <div style={{ fontWeight:700, color:"#1a1a1a", fontSize:13 }}>{o.cliente_nome}</div>
                    {tel && <div style={{ fontSize:11, color:"#888", display:"flex", alignItems:"center", gap:4 }}>
                      <a href={"https://wa.me/"+(()=>{const t=tel.replace(/\D/g,"");return (t.length<=11?"55":"")+t;})()+"?text="+encodeURIComponent("Olá "+(o.cliente_nome||"").split(" ")[0]+"!")} target="_blank" rel="noopener noreferrer" title="Abrir WhatsApp" style={{ display:"inline-flex", cursor:"pointer" }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.347 0-4.518-.809-6.234-2.163l-.353-.29-3.661 1.228 1.228-3.661-.29-.353A9.96 9.96 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                      </a>
                      {formatPhone(tel)}
                    </div>}
                    <div style={{ fontSize:11, color:"#888" }}>{[o.end_logradouro,o.end_numero,o.end_cidade,o.end_uf].filter(Boolean).join(", ") || o.endereco || ""}</div>
                  </td>
                  <td style={{ padding:"10px 13px" }}>{(() => { const cc = categorias.find(c=>c.nome===o.categoria); return <span style={{ padding:"2px 8px", borderRadius:3, fontSize:10, fontWeight:600, background:(cc?.cor||"#888")+"22", color:cc?.cor||"#888" }}>{cc?.icone||""} {o.categoria}</span>; })()}</td>
                  <td style={{ padding:"10px 13px", whiteSpace:"nowrap" }}>{fmtDate(o.data_agendamento)}</td>
                  <td style={{ padding:"10px 13px" }}>{o.motorista_nome||"—"}</td>
                  <td style={{ padding:"10px 13px" }}>{(o.categoria||"").toLowerCase().includes("fossa")?(o.volume_estimado||"—")+" m³":(o.litragem_solicitada||"—")+" L"}</td>
                  <td style={{ padding:"10px 13px", fontWeight:600 }}>R$ {parseFloat(o.valor||0).toFixed(2)}</td>
                  <td style={{ padding:"10px 13px", textAlign:"center" }}>
                    <span style={{ fontSize:11, fontWeight:700, color:o.pago?"#2ecc71":"#e74c3c" }}>{o.pago?"Sim":"Não"}</span>
                  </td>
                  <td style={{ padding:"10px 13px" }} onClick={e=>e.stopPropagation()}>
                    <OSRowMenu items={[
                      { label:"Ver detalhes", action:()=>setViewOrdem(o), emoji:"👁️" },
                      o.status!=="Finalizado"&&o.status!=="Cancelado"?{ label:"Editar", action:()=>handleEdit(o), color:"#e65100", emoji:"✏️" }:null,
                      OS_NEXT_STATUS[o.status]?{ label:OS_NEXT_STATUS[o.status].label, action:()=>handleChangeStatus(o.id,OS_NEXT_STATUS[o.status].next), color:OS_NEXT_STATUS[o.status].color, emoji:"▶️" }:null,
                      !o.pago&&(o.status==="Finalizado"||o.status==="Em execução")?{ label:"Marcar Pago", action:()=>handleChangeStatus(o.id,"pago"), color:"#16a34a", emoji:"💰" }:null,
                      o.status!=="Cancelado"&&o.status!=="Finalizado"?{ label:"Cancelar", action:()=>{if(window.confirm("Cancelar este atendimento?"))handleChangeStatus(o.id,"Cancelado");}, color:"#e74c3c", emoji:"❌" }:null,
                    ].filter(Boolean)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modais */}
      {showForm && <OSFormModal ordem={editOrdem} clientes={clientes} motoristas={motoristas} frota={frota} tiposServico={tiposServico} categorias={categorias} onClose={()=>{setShowForm(false);setEditOrdem(null);}} onSave={handleSave} apiFetch={apiFetch} addToast={addToast} />}
      {viewOrdem && <OSViewModal ordem={viewOrdem} onClose={()=>setViewOrdem(null)} onEdit={handleEdit} onChangeStatus={handleChangeStatus} onDelete={handleDelete} addToast={addToast} apiFetch={apiFetch} categorias={categorias} />}
      </>}
    </div>
  );
}

// ─── Página: Relatórios de Serviços (isolado) ────────────────────────────────
function RelatoriosServicos({ apiFetch, addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catsFiltro, setCatsFiltro] = useState([]);
  const [dataIni, setDataIni] = useState(() => { const d = new Date(); d.setMonth(d.getMonth()-1); return d.toISOString().split('T')[0]; });
  const [dataFim, setDataFim] = useState(() => new Date().toISOString().split('T')[0]);
  const [categoria, setCategoria] = useState("");

  useEffect(() => { apiFetch("/api/categorias-servico").then(r=>setCatsFiltro(r||[])).catch(()=>{}); }, [apiFetch]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/api/ordens-servico/relatorios/resumo?data_ini="+dataIni+"&data_fim="+dataFim;
      if (categoria) url += "&categoria="+categoria;
      const d = await apiFetch(url);
      setData(d);
    } catch(e) { addToast("Erro ao carregar relatório","error"); }
    setLoading(false);
  }, [apiFetch, addToast, dataIni, dataFim, categoria]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const filtInp = { padding:"8px 12px", border:"1px solid #e0e0e0", borderRadius:3, fontSize:12, fontFamily:FONT };

  return (
    <div>
      <div style={{ display:"flex", gap:10, marginBottom:20, alignItems:"center", flexWrap:"wrap" }}>
        <input type="date" value={dataIni} onChange={e=>setDataIni(e.target.value)} style={filtInp} />
        <input type="date" value={dataFim} onChange={e=>setDataFim(e.target.value)} style={filtInp} />
        <select value={categoria} onChange={e=>setCategoria(e.target.value)} style={filtInp}>
          <option value="">Todas categorias</option>
          {catsFiltro.map(c => <option key={c.id} value={c.nome}>{c.icone} {c.nome}</option>)}
        </select>
        <button onClick={loadReport} style={{ padding:"8px 16px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, cursor:"pointer", fontWeight:700, fontSize:12, fontFamily:FONT }}>Atualizar</button>
      </div>

      {loading && <div style={{ textAlign:"center", padding:40, color:"#aaa" }}>Carregando...</div>}

      {data && !loading && <>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20 }}>
          {[
            { label:"Total Atendimentos", value:data.total, color:"#1aae9e" },
            { label:"Finalizados", value:data.finalizados, color:"#22c55e" },
            { label:"Cancelados", value:data.cancelados, color:"#ef4444" },
            { label:"Faturamento", value:"R$ "+parseFloat(data.faturamento||0).toLocaleString("pt-BR",{minimumFractionDigits:2}), color:"#3b82f6" },
            { label:"Recebido", value:"R$ "+parseFloat(data.recebido||0).toLocaleString("pt-BR",{minimumFractionDigits:2}), color:"#16a34a" },
            { label:"Pendente", value:"R$ "+parseFloat(data.pendente||0).toLocaleString("pt-BR",{minimumFractionDigits:2}), color:"#f59e0b" },
          ].map((c,i) => (
            <div key={i} style={{ background:"#fff", borderRadius:4, padding:"16px 18px", border:"1px solid #e8e8e8", borderLeft:`4px solid ${c.color}` }}>
              <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3 }}>{c.label}</div>
              <div style={{ fontSize:20, fontWeight:800, color:c.color, marginTop:4 }}>{c.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
          <div style={{ background:"#fff", borderRadius:4, padding:20, border:"1px solid #e8e8e8" }}>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Por Categoria</div>
            {(data.porCategoria||[]).map((c,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f5f5f5", fontSize:12 }}>
                <span style={{ fontWeight:600 }}>{(() => { const cc = catsFiltro.find(ct=>ct.nome===c.categoria); return (cc?.icone||"") + " " + (c.categoria||"—"); })()}</span>
                <span>{c.total} atend. — R$ {parseFloat(c.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</span>
              </div>
            ))}
            {(!data.porCategoria||data.porCategoria.length===0) && <div style={{ color:"#aaa", fontSize:12 }}>Sem dados</div>}
          </div>

          <div style={{ background:"#fff", borderRadius:4, padding:20, border:"1px solid #e8e8e8" }}>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Por Status</div>
            {(data.porStatus||[]).map((s,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f5f5f5", fontSize:12 }}>
                <span><span style={{ display:"inline-block", width:8, height:8, borderRadius:4, background:osStatusColor(s.status), marginRight:6 }}></span>{s.status}</span>
                <span style={{ fontWeight:600 }}>{s.total}</span>
              </div>
            ))}
            {(!data.porStatus||data.porStatus.length===0) && <div style={{ color:"#aaa", fontSize:12 }}>Sem dados</div>}
          </div>
        </div>

        <div style={{ background:"#fff", borderRadius:4, padding:20, border:"1px solid #e8e8e8", marginBottom:20 }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Top Clientes</div>
          <table className="tbl-loc" style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr style={{ borderBottom:"2px solid #ebebeb" }}>
              <th style={{ textAlign:"left", padding:"8px 6px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Cliente</th>
              <th style={{ textAlign:"center", padding:"8px 6px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Atendimentos</th>
              <th style={{ textAlign:"right", padding:"8px 6px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase" }}>Valor Total</th>
            </tr></thead>
            <tbody>
              {(data.topClientes||[]).map((c,i) => (
                <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                  <td style={{ padding:"8px 6px" }}>{c.cliente_nome}</td>
                  <td style={{ padding:"8px 6px", textAlign:"center" }}>{c.total}</td>
                  <td style={{ padding:"8px 6px", textAlign:"right", fontWeight:600 }}>R$ {parseFloat(c.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}</td>
                </tr>
              ))}
              {(!data.topClientes||data.topClientes.length===0) && <tr><td colSpan={3} style={{ padding:20, textAlign:"center", color:"#aaa" }}>Sem dados</td></tr>}
            </tbody>
          </table>
        </div>

        <div style={{ background:"#fff", borderRadius:4, padding:20, border:"1px solid #e8e8e8" }}>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Faturamento por Mês</div>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", height:120 }}>
            {(data.porMes||[]).map((m,i) => {
              const maxVal = Math.max(...(data.porMes||[]).map(x=>parseFloat(x.valor||0)),1);
              const h = Math.max((parseFloat(m.valor||0)/maxVal)*100, 4);
              return (
                <div key={i} style={{ flex:1, textAlign:"center" }}>
                  <div style={{ background:"linear-gradient(180deg,#1aae9e,#0d9488)", borderRadius:"4px 4px 0 0", height:h, minWidth:20, margin:"0 auto" }}></div>
                  <div style={{ fontSize:9, color:"#888", marginTop:4 }}>{m.mes}</div>
                  <div style={{ fontSize:9, fontWeight:600, color:"#333" }}>R$ {parseFloat(m.valor||0).toLocaleString("pt-BR",{minimumFractionDigits:0})}</div>
                </div>
              );
            })}
            {(!data.porMes||data.porMes.length===0) && <div style={{ color:"#aaa", fontSize:12, padding:20 }}>Sem dados</div>}
          </div>
        </div>
      </>}
    </div>
  );
}

// ─── Página: Tipos de Serviço ────────────────────────────────────────────────
function TiposServicoPage({ apiFetch, addToast }) {
  const [tipos, setTipos] = useState([]);
  const [catsList, setCatsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTipo, setEditTipo] = useState(null);
  const [form, setForm] = useState({ nome:"", categoria:"", valor_padrao:"" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, cats] = await Promise.all([apiFetch("/api/tipos-servico"), apiFetch("/api/categorias-servico")]);
      setTipos(r||[]);
      setCatsList(cats||[]);
    } catch(e){}
    setLoading(false);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditTipo(null); setForm({ nome:"", categoria:catsList.length>0?catsList[0].nome:"", valor_padrao:"" }); setShowForm(true); };
  const openEdit = (t) => { setEditTipo(t); setForm({ nome:t.nome, categoria:t.categoria, valor_padrao:t.valor_padrao||"" }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.nome) return addToast("Nome obrigatório","error");
    try {
      if (editTipo) {
        await apiFetch("/api/tipos-servico/"+editTipo.id, { method:"PUT", body:form });
        addToast("Tipo atualizado!","success");
      } else {
        await apiFetch("/api/tipos-servico", { method:"POST", body:form });
        addToast("Tipo criado!","success");
      }
      setShowForm(false);
      load();
    } catch(e) { addToast("Erro: "+e.message,"error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir tipo de serviço?")) return;
    try { await apiFetch("/api/tipos-servico/"+id, { method:"DELETE" }); addToast("Excluído","success"); load(); } catch(e) { addToast("Erro","error"); }
  };

  const inpTS = { width:"100%", padding:"7px 10px", border:"1px solid #ccc", borderRadius:3, fontSize:13, color:"#1a1a1a", outline:"none", boxSizing:"border-box", fontFamily:FONT, background:"#fff" };

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
        <button onClick={openNew} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:3, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontFamily:FONT }}>
          <IcoPlus /> Novo Tipo
        </button>
      </div>

      {loading ? <div style={{ textAlign:"center", padding:40, color:"#aaa" }}>Carregando...</div> : (
        <div className="tbl-wrap" style={{ background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, overflowX:"auto" }}>
          <table className="tbl-loc" style={{ width:"100%", borderCollapse:"collapse", fontSize:13, tableLayout:"auto" }}>
            <thead style={{ background:"#fafafa" }}>
              <tr>
                {["Nome","Categoria","Valor Padrão",""].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"9px 13px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.3, borderBottom:"1px solid #ebebeb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tipos.length===0 && <tr><td colSpan={4}><EmptyState icon="📋" title="Nenhum tipo cadastrado" subtitle="Clique em 'Novo Tipo' para adicionar" /></td></tr>}
              {tipos.map(t => (
                <tr key={t.id} style={{ borderBottom:"2px solid #e0e0e0" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#fafafa"}
                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <td style={{ padding:"10px 13px", fontWeight:700 }}>{t.nome}</td>
                  <td style={{ padding:"10px 13px" }}>{(() => { const cc = catsList.find(c=>c.nome===t.categoria); return <span style={{ padding:"2px 8px", borderRadius:3, fontSize:10, fontWeight:600, background:(cc?.cor||"#888")+"22", color:cc?.cor||"#888" }}>{cc?.icone||""} {t.categoria}</span>; })()}</td>
                  <td style={{ padding:"10px 13px", fontWeight:600 }}>R$ {parseFloat(t.valor_padrao||0).toFixed(2)}</td>
                  <td style={{ padding:"10px 13px" }}>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>openEdit(t)} style={{ padding:"5px 12px", background:"#f0f4ff", color:"#1a5fa8", border:"1px solid #c7d7fd", borderRadius:3, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:FONT }}>Editar</button>
                      <button onClick={()=>handleDelete(t.id)} style={{ padding:"5px 12px", background:"#fde8e8", color:"#c0392b", border:"1px solid #f5a8a8", borderRadius:3, cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:FONT }}>Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <LargeModal title={editTipo?"Editar Tipo":"Novo Tipo de Serviço"} onClose={()=>setShowForm(false)} maxWidth={420} onSave={handleSave} saveLabel="Salvar">
          <div style={{ paddingTop:14 }}>
            <Field label="Nome *"><input style={{...inpTS, borderColor:!form.nome?"#1aae9e":"#ccc", background:!form.nome?"#f0f4ff":"#fff"}} value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))} /></Field>
            <div style={{ marginTop:12 }}><Field label="Categoria">
              <select style={inpTS} value={form.categoria} onChange={e=>setForm(p=>({...p,categoria:e.target.value}))}>
                {catsList.filter(c=>c.ativo!==0).map(c => <option key={c.id} value={c.nome}>{c.icone} {c.nome}</option>)}
              </select>
            </Field></div>
            <div style={{ marginTop:12 }}><Field label="Valor Padrão (R$)"><input type="number" step="0.01" style={inpTS} value={form.valor_padrao} onChange={e=>setForm(p=>({...p,valor_padrao:e.target.value}))} /></Field></div>
          </div>
        </LargeModal>
      )}
    </div>
  );
}


// ─── Ícone de Auditoria (shield/log) ──────────────────────────────────
const IcoAudit = () => <Icon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4" />;

// ─── Helpers de Auditoria ────────────────────────────────────────────
const AUDIT_LABELS = {
  cliente_nome:"Cliente", cliente:"Cliente", clienteId:"ID Cliente",
  cacamba_codigo:"Caçamba", cacamba:"Caçamba",
  tipo_cacamba:"Tipo de Caçamba", tipoCacamba:"Tipo de Caçamba",
  valor:"Valor", status:"Status", pago:"Pago",
  motorista:"Motorista", motoristaRetirada:"Motorista Retirada", motorista_retirada:"Motorista Retirada",
  pagamento:"Forma de Pagamento", modalidadePagamento:"Modalidade Pgto", modalidade_pagamento:"Modalidade Pgto",
  data_entrega:"Data de Entrega", dataEntrega:"Data de Entrega",
  data_retirada:"Data de Retirada", dataRetirada:"Data de Retirada",
  data_retirada_real:"Data Retirada Real", dataRetiradaReal:"Data Retirada Real",
  data_colocacao_real:"Data Colocação Real", dataColocacaoReal:"Data Colocação Real",
  data_pagamento:"Data Pagamento", dataPagamento:"Data Pagamento",
  data_troca_prevista:"Data Troca Prevista", dataTrocaPrevista:"Data Troca Prevista",
  data_pedido_troca:"Data Pedido Troca", dataPedidoTroca:"Data Pedido Troca",
  data_pedido_retirada:"Data Pedido Retirada", dataPedidoRetirada:"Data Pedido Retirada",
  obs:"Observações", obs_pagamento:"Obs. Pagamento", obsPagamento:"Obs. Pagamento",
  operacao:"Operação", comissao:"Comissão", comissaoRetirada:"Comissão Retirada", comissao_retirada:"Comissão Retirada",
  nome:"Nome", tipo:"Tipo", cpfCnpj:"CPF/CNPJ", cpf_cnpj:"CPF/CNPJ",
  tel:"Telefone", tel2:"Telefone 2", email:"E-mail",
  cnh:"CNH", categoriaCnh:"Categoria CNH", categoria_cnh:"Categoria CNH",
  codigo:"Código", volume:"Volume", fabricante:"Fabricante",
  em_manutencao:"Em Manutenção", motivo_manutencao:"Motivo Manutenção",
  placa:"Placa", modelo:"Modelo", ano:"Ano", cor:"Cor",
  id:"ID", gerar_mtr:"Gerar MTR", gerarMtr:"Gerar MTR", mtr:"MTR",
};
const AUDIT_EQUIVALENTS = {
  cliente:["cliente_nome","cliente"], cacamba:["cacamba_codigo","cacamba"],
  tipo_cacamba:["tipo_cacamba","tipoCacamba"], motorista_ret:["motorista_retirada","motoristaRetirada"],
  data_entrega:["data_entrega","dataEntrega"], data_retirada:["data_retirada","dataRetirada"],
  data_pagamento:["data_pagamento","dataPagamento"], modalidade:["modalidade_pagamento","modalidadePagamento"],
  obs_pag:["obs_pagamento","obsPagamento"], comissao_ret:["comissao_retirada","comissaoRetirada"],
  cpf:["cpf_cnpj","cpfCnpj"], cat_cnh:["categoria_cnh","categoriaCnh"],
};
const auditIsEmpty = (v) => v === null || v === undefined || v === "" || v === "\u2014";
const auditFmtBool = (v) => { if (v === true || v === 1 || v === "1") return "Sim"; if (v === false || v === 0 || v === "0") return "Não"; return null; };
const auditFmtDate = (v) => {
  if (!v || typeof v !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    const dt = new Date(v); if (isNaN(dt.getTime())) return null;
    const d = dt.getUTCDate().toString().padStart(2,"0"), m = (dt.getUTCMonth()+1).toString().padStart(2,"0"), y = dt.getUTCFullYear();
    if (v.includes("T") && !v.endsWith("00:00:00.000Z") && !v.endsWith("T00:00:00Z")) {
      return d+"/"+m+"/"+y+" "+dt.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit",timeZone:"America/Sao_Paulo"});
    }
    return d+"/"+m+"/"+y;
  }
  return null;
};
const auditFmtMoney = (v, campo) => {
  if (typeof v === "number" || (typeof v === "string" && /^\d+(\.\d+)?$/.test(v))) {
    if (["valor","comissao","comissaoRetirada","comissao_retirada"].includes(campo)) return "R$ "+parseFloat(v).toFixed(2).replace(".",",");
  }
  return null;
};
const auditFmtVal = (v, campo) => {
  if (auditIsEmpty(v)) return "Não informado";
  const b = auditFmtBool(v); if (b) return b;
  const d = auditFmtDate(v); if (d) return d;
  const mon = auditFmtMoney(v, campo); if (mon) return mon;
  return String(v);
};
const auditAcaoLabel = (a) => ({ criar:"Criação", editar:"Edição", excluir:"Exclusão", status:"Alteração de Status", pagamento:"Pagamento", cancelar:"Cancelamento" }[a] || a);
const auditEntidadeLabel = (e) => ({ locacao:"Locação", cliente:"Cliente", cacamba:"Caçamba", motorista:"Motorista", veiculo:"Veículo" }[e] || e);
// ═══════════════════════════════════════════════════════════
// MÓDULO DE ATERROS / DESTINAÇÃO
// ═══════════════════════════════════════════════════════════

const IcoAterro = () => <Icon d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6M9 9h.01M15 9h.01M9 13h.01M15 13h.01" />;

function AterroModal({ onClose, onSave, initialData, editMode, motoristas, frota, aterros }) {
  const [form, setForm] = useState(initialData ? {
    id: initialData.id,
    nome: initialData.nome||"",
    cnpj: initialData.cnpj||"",
    endereco: initialData.endereco||"",
    cidade: initialData.cidade||"",
    uf: initialData.uf||"",
    telefone: initialData.telefone||"",
    contato: initialData.contato||"",
    valor_tonelada: initialData.valor_tonelada||"",
    licenca_numero: initialData.licenca_numero||"",
    licenca_validade: initialData.licenca_validade ? String(initialData.licenca_validade).slice(0,10) : "",
    classes_aceitas: initialData.classes_aceitas||"A,B,C,D",
    observacoes: initialData.observacoes||"",
    ativo: initialData.ativo !== undefined ? initialData.ativo : 1,
  } : { nome:"", cnpj:"", endereco:"", cidade:"", uf:"", telefone:"", contato:"", valor_tonelada:"", licenca_numero:"", licenca_validade:"", classes_aceitas:"A,B,C,D", observacoes:"", ativo:1 });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const CLASSES = ["A","B","C","D"];
  const toggleClasse = (c) => {
    const arr = form.classes_aceitas ? form.classes_aceitas.split(",").filter(Boolean) : [];
    const idx = arr.indexOf(c);
    if (idx >= 0) arr.splice(idx, 1); else arr.push(c);
    setF("classes_aceitas", arr.join(","));
  };
  const save = () => {
    if (!form.nome?.trim()) { alert("Nome do aterro é obrigatório."); return; }
    onSave(form);
  };
  const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:12, fontFamily:FONT, boxSizing:"border-box", outline:"none" };
  const UFS = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];
  return (
    <LargeModal title={editMode ? "Editar Aterro" : "Novo Aterro"} subtitle="Cadastro de aterro / destinatário" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar" : "Salvar"} maxWidth={700}>
      <SectionHeader icon="🏭" label="Dados do Aterro" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:12 }}>
          <Field label="Nome *"><input style={inp} value={form.nome} onChange={e => setF("nome", e.target.value)} placeholder="Nome do aterro / destinatário" /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
          <Field label="CNPJ"><input style={inp} value={form.cnpj} onChange={e => setF("cnpj", formatCpfCnpj(e.target.value))} placeholder="00.000.000/0000-00" /></Field>
          <Field label="Telefone"><input style={inp} value={form.telefone} onChange={e => setF("telefone", formatPhone(e.target.value))} placeholder="(00) 00000-0000" /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:12, marginTop:12 }}>
          <Field label="Endereço"><input style={inp} value={form.endereco} onChange={e => setF("endereco", e.target.value)} placeholder="Rua, número" /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:12, marginTop:12 }}>
          <Field label="Cidade"><input style={inp} value={form.cidade} onChange={e => setF("cidade", e.target.value)} /></Field>
          <Field label="UF"><select style={{ ...inp, appearance:"none" }} value={form.uf} onChange={e => setF("uf", e.target.value)}><option value="">--</option>{UFS.map(u => <option key={u} value={u}>{u}</option>)}</select></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
          <Field label="Contato"><input style={inp} value={form.contato} onChange={e => setF("contato", e.target.value)} placeholder="Nome do contato" /></Field>
          <Field label="Valor por Tonelada (R$)"><input type="number" style={inp} value={form.valor_tonelada} onChange={e => setF("valor_tonelada", e.target.value)} placeholder="0,00" step="0.01" /></Field>
        </div>
      </SectionBody>
      <SectionHeader icon="📜" label="Licença Ambiental" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Field label="Nº da Licença"><input style={inp} value={form.licenca_numero} onChange={e => setF("licenca_numero", e.target.value)} /></Field>
          <Field label="Validade"><input type="date" style={inp} value={form.licenca_validade} onChange={e => setF("licenca_validade", e.target.value)} /></Field>
        </div>
        <div style={{ marginTop:12 }}>
          <Field label="Classes de Resíduo Aceitas">
            <div style={{ display:"flex", gap:14, marginTop:4 }}>
              {CLASSES.map(c => (
                <label key={c} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, cursor:"pointer" }}>
                  <input type="checkbox" checked={(form.classes_aceitas||"").split(",").includes(c)} onChange={() => toggleClasse(c)} style={{ accentColor:"#1aae9e" }} />
                  Classe {c}
                </label>
              ))}
            </div>
          </Field>
        </div>
      </SectionBody>
      <SectionHeader icon="📝" label="Observações" />
      <SectionBody>
        <textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={form.observacoes} onChange={e => setF("observacoes", e.target.value)} placeholder="Observações sobre o aterro..." />
        {editMode && (
          <div style={{ marginTop:12 }}>
            <Field label="Status">
              <select style={{ ...inp, appearance:"none" }} value={form.ativo} onChange={e => setF("ativo", parseInt(e.target.value))}>
                <option value={1}>Ativo</option>
                <option value={0}>Inativo</option>
              </select>
            </Field>
          </div>
        )}
      </SectionBody>
    </LargeModal>
  );
}

function ViagemAterroModal({ onClose, onSave, initialData, editMode, motoristas, frota, aterros }) {
  const [form, setForm] = useState(initialData ? {
    id: initialData.id,
    aterro_id: initialData.aterro_id||"",
    motorista_id: initialData.motorista_id||"",
    veiculo_id: initialData.veiculo_id||"",
    data: initialData.data ? String(initialData.data).slice(0,10) : hojeLocal(),
    peso_bruto: initialData.peso_bruto||"",
    tara: initialData.tara||"",
    peso_liquido: initialData.peso_liquido||"",
    valor: initialData.valor||"",
    forma_pagamento: initialData.forma_pagamento||"",
    pago: initialData.pago ? 1 : 0,
    data_pagamento: initialData.data_pagamento ? String(initialData.data_pagamento).slice(0,10) : "",
    ticket_numero: initialData.ticket_numero||"",
    observacao: initialData.observacao||"",
    tipo_veiculo: initialData.tipo_veiculo||"",
    volume_m3: (initialData.volume_m3 === 0 || initialData.volume_m3) ? initialData.volume_m3 : "",
    modalidade: initialData.modalidade||"",
  } : { aterro_id:"", motorista_id:"", veiculo_id:"", data:hojeLocal(), peso_bruto:"", tara:"", peso_liquido:"", valor:"", forma_pagamento:"", pago:0, data_pagamento:"", ticket_numero:"", observacao:"", tipo_veiculo:"", volume_m3:"", modalidade:"" });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // Tipo de veículo: presets + "Outro" (texto livre)
  const TIPOS_VEICULO = ["Traçado", "Basculante", "Poliguindaste"];
  const [tipoSel, setTipoSel] = useState(() => {
    const tv = initialData?.tipo_veiculo || "";
    return tv ? (TIPOS_VEICULO.includes(tv) ? tv : "Outro") : "";
  });

  // Auto-calcular peso líquido
  useEffect(() => {
    const bruto = parseFloat(form.peso_bruto) || 0;
    const tara = parseFloat(form.tara) || 0;
    if (bruto > 0 && tara > 0) setF("peso_liquido", (bruto - tara).toFixed(2));
  }, [form.peso_bruto, form.tara]);

  // Auto-calcular valor baseado no peso e valor/ton do aterro selecionado
  useEffect(() => {
    if (!form.aterro_id || !form.peso_liquido) return;
    const at = (aterros||[]).find(a => a.id == form.aterro_id);
    if (at && parseFloat(at.valor_tonelada) > 0 && parseFloat(form.peso_liquido) > 0) {
      const valorCalc = (parseFloat(form.peso_liquido) / 1000) * parseFloat(at.valor_tonelada);
      setF("valor", valorCalc.toFixed(2));
    }
  }, [form.aterro_id, form.peso_liquido]);

  const save = () => {
    if (!form.aterro_id) { alert("Selecione o aterro."); return; }
    if (!form.data) { alert("Data é obrigatória."); return; }
    onSave(form);
  };
  const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:4, fontSize:12, fontFamily:FONT, boxSizing:"border-box", outline:"none" };
  const FORMAS = ["Dinheiro","PIX","Cartão","Boleto","Transferência","Faturado","Outro"];
  return (
    <LargeModal title={editMode ? "Editar Viagem" : "Nova Viagem ao Aterro"} subtitle="Registro de viagem / descarte" onClose={onClose} onSave={save} saveLabel={editMode ? "Atualizar" : "Salvar"} maxWidth={750}>
      <SectionHeader icon="🚛" label="Dados da Viagem" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <Field label="Aterro *">
            <select style={{ ...inp, appearance:"none", borderColor:!form.aterro_id?"#1aae9e":"#e0e0e0" }} value={form.aterro_id} onChange={e => setF("aterro_id", e.target.value)}>
              <option value="">-- Selecione --</option>
              {(aterros||[]).filter(a => a.ativo).map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
            </select>
          </Field>
          <Field label="Data *"><input type="date" style={{ ...inp, borderColor:!form.data?"#1aae9e":"#e0e0e0" }} value={form.data} onChange={e => setF("data", e.target.value)} /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
          <Field label="Motorista">
            <select style={{ ...inp, appearance:"none" }} value={form.motorista_id} onChange={e => setF("motorista_id", e.target.value)}>
              <option value="">-- Selecione --</option>
              {(motoristas||[]).map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
          </Field>
          <Field label="Veículo">
            <select style={{ ...inp, appearance:"none" }} value={form.veiculo_id} onChange={e => setF("veiculo_id", e.target.value)}>
              <option value="">-- Selecione --</option>
              {(frota||[]).filter(v => !v.status || v.status === "Ativo").map(v => <option key={v.id} value={v.id}>{v.placa} — {v.modelo}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginTop:12 }}>
          <Field label="Tipo de Veículo">
            <select style={{ ...inp, appearance:"none" }} value={tipoSel} onChange={e => { const v = e.target.value; setTipoSel(v); setF("tipo_veiculo", v === "Outro" ? "" : v); }}>
              <option value="">-- Selecione --</option>
              {TIPOS_VEICULO.map(t => <option key={t} value={t}>{t}</option>)}
              <option value="Outro">Outro</option>
            </select>
            {tipoSel === "Outro" && <input style={{ ...inp, marginTop:6 }} value={form.tipo_veiculo} onChange={e => setF("tipo_veiculo", e.target.value)} placeholder="Especifique o tipo" />}
          </Field>
          <Field label="Modalidade">
            <select style={{ ...inp, appearance:"none" }} value={form.modalidade} onChange={e => setF("modalidade", e.target.value)}>
              <option value="">-- Selecione --</option>
              <option value="Caçamba">Caçamba</option>
              <option value="Caminhão">Caminhão</option>
            </select>
          </Field>
          <Field label="Volume (m³)"><input type="number" style={inp} value={form.volume_m3} onChange={e => setF("volume_m3", e.target.value)} placeholder="0" step="0.01" /></Field>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:12 }}>
          <Field label="Nº do Ticket"><input style={inp} value={form.ticket_numero} onChange={e => setF("ticket_numero", e.target.value)} placeholder="Número do ticket de pesagem" /></Field>
        </div>
      </SectionBody>
      <SectionHeader icon="⚖️" label="Pesagem" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
          <Field label="Peso Bruto (kg)"><input type="number" style={inp} value={form.peso_bruto} onChange={e => setF("peso_bruto", e.target.value)} placeholder="0" step="0.01" /></Field>
          <Field label="Tara (kg)"><input type="number" style={inp} value={form.tara} onChange={e => setF("tara", e.target.value)} placeholder="0" step="0.01" /></Field>
          <Field label="Peso Líquido (kg)"><input type="number" style={{ ...inp, background:"#f8f9fa", fontWeight:700 }} value={form.peso_liquido} onChange={e => setF("peso_liquido", e.target.value)} placeholder="Calculado automaticamente" step="0.01" /></Field>
        </div>
      </SectionBody>
      <SectionHeader icon="💰" label="Financeiro" />
      <SectionBody>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
          <Field label="Valor (R$)"><input type="number" style={{ ...inp, fontWeight:700 }} value={form.valor} onChange={e => setF("valor", e.target.value)} placeholder="0,00" step="0.01" /></Field>
          <Field label="Forma de Pagamento">
            <select style={{ ...inp, appearance:"none" }} value={form.forma_pagamento} onChange={e => setF("forma_pagamento", e.target.value)}>
              <option value="">--</option>
              {FORMAS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <div style={{ display:"flex", flexDirection:"column", justifyContent:"center" }}>
            <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, cursor:"pointer", paddingTop:18 }}>
              <input type="checkbox" checked={!!form.pago} onChange={e => { const v = e.target.checked ? 1 : 0; setForm(p => ({ ...p, pago: v, data_pagamento: v && !p.data_pagamento ? hojeLocal() : p.data_pagamento })); }} style={{ accentColor:"#2ecc71", width:15, height:15 }} />
              <span style={{ fontWeight:form.pago?600:400, color:form.pago?"#2ecc71":"#444" }}>Pago</span>
            </label>
          </div>
        </div>
        {form.pago ? (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginTop:12 }}>
            <Field label="Data do Pagamento"><input type="date" style={inp} value={form.data_pagamento} onChange={e => setF("data_pagamento", e.target.value)} /></Field>
          </div>
        ) : null}
        <div style={{ marginTop:12 }}>
          <Field label="Observação"><textarea style={{ ...inp, minHeight:50, resize:"vertical" }} value={form.observacao} onChange={e => setF("observacao", e.target.value)} placeholder="Observações da viagem..." /></Field>
        </div>
      </SectionBody>
    </LargeModal>
  );
}

function AterrosPage({ motoristas, frota, addToast, apiFetch }) {
  const [aba, setAba] = useState("viagens");
  const [aterros, setAterros] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalAterro, setModalAterro] = useState(false);
  const [editAterro, setEditAterro] = useState(null);
  const [modalViagem, setModalViagem] = useState(false);
  const [editViagem, setEditViagem] = useState(null);
  // Relatório
  const hoje = new Date();
  const mesAtual = hoje.toISOString().split("T")[0].substring(0,7);
  const [relDe, setRelDe] = useState(mesAtual+"-01");
  const [relAte, setRelAte] = useState(new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().split("T")[0]);
  const [relAterroId, setRelAterroId] = useState("");
  const [relModalidade, setRelModalidade] = useState("");
  const [relatorio, setRelatorio] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, v] = await Promise.all([
        apiFetch("/api/aterros"),
        apiFetch("/api/aterros/viagens/list"),
      ]);
      setAterros(a || []);
      setViagens(v || []);
    } catch(e) { addToast("Erro ao carregar dados de aterros", "error"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // CRUD Aterros
  const saveAterro = async (data) => {
    try {
      if (data.id) {
        const upd = await apiFetch("/api/aterros/" + data.id, { method: "PUT", body: data });
        setAterros(p => p.map(a => a.id === upd.id ? upd : a));
        addToast("Aterro atualizado!", "success");
      } else {
        const novo = await apiFetch("/api/aterros", { method: "POST", body: data });
        setAterros(p => [...p, novo]);
        addToast("Aterro cadastrado!", "success");
      }
      setModalAterro(false); setEditAterro(null);
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };
  const removeAterro = async (id) => {
    if (!window.confirm("Excluir este aterro?")) return;
    try {
      await apiFetch("/api/aterros/" + id, { method: "DELETE" });
      setAterros(p => p.filter(a => a.id !== id));
      addToast("Aterro excluído", "success");
    } catch(err) { addToast(err.message || "Erro ao excluir", "error"); }
  };

  // CRUD Viagens
  const saveViagem = async (data) => {
    try {
      if (data.id) {
        const upd = await apiFetch("/api/aterros/viagens/" + data.id, { method: "PUT", body: data });
        setViagens(p => p.map(v => v.id === upd.id ? upd : v));
        addToast("Viagem atualizada!", "success");
      } else {
        const novo = await apiFetch("/api/aterros/viagens", { method: "POST", body: data });
        setViagens(p => [novo, ...p]);
        addToast("Viagem registrada!", "success");
      }
      setModalViagem(false); setEditViagem(null);
    } catch(err) { addToast("Erro: " + err.message, "error"); }
  };
  const removeViagem = async (id) => {
    if (!window.confirm("Excluir esta viagem?")) return;
    try {
      await apiFetch("/api/aterros/viagens/" + id, { method: "DELETE" });
      setViagens(p => p.filter(v => v.id !== id));
      addToast("Viagem excluída", "success");
    } catch(err) { addToast("Erro ao excluir", "error"); }
  };
  const marcarPago = async (v) => {
    try {
      const upd = await apiFetch("/api/aterros/viagens/" + v.id, { method: "PUT", body: { ...v, pago: 1, data_pagamento: hojeLocal() } });
      setViagens(p => p.map(x => x.id === upd.id ? upd : x));
      addToast("Marcado como pago!", "success");
    } catch(err) { addToast("Erro", "error"); }
  };

  // Relatório
  const loadRelatorio = async () => {
    try {
      let url = `/api/aterros/relatorio/resumo?de=${relDe}&ate=${relAte}`;
      if (relAterroId) url += `&aterro_id=${relAterroId}`;
      if (relModalidade) url += `&modalidade=${encodeURIComponent(relModalidade)}`;
      const r = await apiFetch(url);
      setRelatorio(r);
    } catch(e) { addToast("Erro ao gerar relatório", "error"); }
  };

  // Cards resumo (viagens)
  const mesViagens = viagens.filter(v => { const d = String(v.data).slice(0,7); return d === mesAtual; });
  const totalMes = mesViagens.reduce((s, v) => s + parseFloat(v.valor||0), 0);
  const pesoMes = mesViagens.reduce((s, v) => s + parseFloat(v.peso_liquido||0), 0);
  const pendenteMes = mesViagens.filter(v => !v.pago).reduce((s, v) => s + parseFloat(v.valor||0), 0);

  // Licenças vencendo
  const licencasVencendo = aterros.filter(a => {
    if (!a.licenca_validade || !a.ativo) return false;
    const diff = Math.ceil((new Date(a.licenca_validade) - new Date()) / (1000*60*60*24));
    return diff <= 30 && diff >= 0;
  });
  const licencasVencidas = aterros.filter(a => {
    if (!a.licenca_validade || !a.ativo) return false;
    return new Date(a.licenca_validade) < new Date();
  });

  const inp = { padding:"8px 10px", border:"1px solid #e0e0e0", borderRadius:6, fontSize:12, fontFamily:FONT, background:"#fff", outline:"none" };
  const ABAS = [
    { key:"viagens", label:"Viagens", icon:"🚛" },
    { key:"cadastro", label:"Aterros", icon:"🏭" },
    { key:"relatorio", label:"Relatório", icon:"📊" },
  ];

  return (
    <div>
      {/* Alerta licenças */}
      {licencasVencidas.length > 0 && (
        <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <span style={{ fontSize:12, color:"#dc2626", fontWeight:600 }}>Licença vencida: {licencasVencidas.map(a => a.nome).join(", ")}</span>
        </div>
      )}
      {licencasVencendo.length > 0 && (
        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 16px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:16 }}>⏰</span>
          <span style={{ fontSize:12, color:"#d97706", fontWeight:600 }}>Licença vencendo em breve: {licencasVencendo.map(a => `${a.nome} (${fmtDate(a.licenca_validade)})`).join(", ")}</span>
        </div>
      )}

      {/* Abas */}
      <div style={{ display:"flex", gap:4, marginBottom:18, borderBottom:"2px solid #f0f0f0", paddingBottom:0 }}>
        {ABAS.map(a => (
          <button key={a.key} onClick={() => setAba(a.key)} style={{ padding:"10px 20px", background:"none", border:"none", borderBottom: aba===a.key ? "2px solid #1aae9e" : "2px solid transparent", cursor:"pointer", fontFamily:FONT, fontSize:13, fontWeight: aba===a.key ? 700 : 500, color: aba===a.key ? "#1aae9e" : "#888", display:"flex", alignItems:"center", gap:6, marginBottom:-2 }}>
            <span>{a.icon}</span> {a.label}
          </button>
        ))}
      </div>

      {/* ── ABA VIAGENS ── */}
      {aba === "viagens" && (
        <div>
          {/* Cards resumo */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:18 }}>
            {[
              { label:"Viagens (mês)", val: mesViagens.length, color:"#3498db" },
              { label:"Peso Total (mês)", val: (pesoMes/1000).toFixed(1) + " ton", color:"#1aae9e" },
              { label:"Gasto (mês)", val: "R$ " + totalMes.toFixed(2).replace(".",","), color:"#e74c3c" },
              { label:"Pendente", val: "R$ " + pendenteMes.toFixed(2).replace(".",","), color: pendenteMes > 0 ? "#f39c12" : "#2ecc71" },
            ].map((c,i) => (
              <div key={i} style={{ background:"#fff", borderRadius:10, padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,.06)", borderLeft:`4px solid ${c.color}` }}>
                <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", marginBottom:4 }}>{c.label}</div>
                <div style={{ fontSize:20, fontWeight:800, color:c.color }}>{c.val}</div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <div style={{ position:"relative", flex:1, maxWidth:280 }}>
              <input style={{ ...inp, width:"100%", paddingLeft:32 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar viagem..." />
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#bbb" }}>🔍</span>
            </div>
            <div style={{ flex:1 }} />
            <button onClick={() => exportToExcel(viagens, [
                { label:"Data", key: v => fmtDate(v.data) },
                { label:"Aterro", key:"aterro_nome" },
                { label:"Motorista", key:"motorista_nome" },
                { label:"Veículo", key: v => v.veiculo_placa ? (v.veiculo_placa + (v.veiculo_modelo ? " - " + v.veiculo_modelo : "")) : "" },
                { label:"Modalidade", key:"modalidade" },
                { label:"Tipo Veículo", key:"tipo_veiculo" },
                { label:"Volume (m³)", key: v => (v.volume_m3 === 0 || v.volume_m3) ? v.volume_m3 : "" },
                { label:"Ticket", key:"ticket_numero" },
                { label:"Peso Liq (kg)", key: v => v.peso_liquido||0 },
                { label:"Valor (R$)", key: v => v.valor||0 },
                { label:"Pago", key: v => v.pago ? "Sim" : "Não" },
              ], "Viagens_Aterro")}
              style={{ padding:"8px 14px", background:"#fff", border:"1px solid #e0e0e0", borderRadius:6, fontSize:11, cursor:"pointer", fontFamily:FONT, fontWeight:600, color:"#555" }}>
              📥 Exportar
            </button>
            <button onClick={() => { setEditViagem(null); setModalViagem(true); }}
              style={{ padding:"8px 18px", background:"linear-gradient(135deg,#1aae9e,#17c4b0)", color:"#fff", border:"none", borderRadius:6, fontSize:12, cursor:"pointer", fontFamily:FONT, fontWeight:700 }}>
              + Nova Viagem
            </button>
          </div>

          {/* Tabela viagens */}
          <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
              <thead style={{ background:"#fafafa" }}>
                <tr>{["#","Data","Aterro","Motorista","Veículo","Modalidade","Tipo Veíc.","m³","Ticket","Peso (kg)","Valor","Pago","Ações"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb", whiteSpace:"nowrap" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {viagens.filter(v => {
                  if (!search) return true;
                  const s = norm(search);
                  return norm(v.aterro_nome||"").includes(s) || norm(v.motorista_nome||"").includes(s) || (v.ticket_numero||"").includes(search);
                }).map(v => (
                  <tr key={v.id} style={{ borderBottom:"1px solid #f5f5f5" }} onMouseEnter={e=>e.currentTarget.style.background="#fafafa"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <td style={{ padding:"10px 12px", color:"#bbb", fontSize:11 }}>{String(v.id).padStart(4,"0")}</td>
                    <td style={{ padding:"10px 12px", fontWeight:600 }}>{fmtDate(v.data)}</td>
                    <td style={{ padding:"10px 12px", fontWeight:600, color:"#1a1a1a" }}>{v.aterro_nome||"—"}</td>
                    <td style={{ padding:"10px 12px" }}>{v.motorista_nome||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11 }}>{v.veiculo_placa ? `${v.veiculo_placa} — ${v.veiculo_modelo||""}` : "—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11 }}>{v.modalidade ? <span style={{ background: v.modalidade==="Caçamba" ? "#eaf4ff" : "#fff4e6", color: v.modalidade==="Caçamba" ? "#2563eb" : "#d97706", padding:"2px 8px", borderRadius:12, fontSize:10, fontWeight:700, whiteSpace:"nowrap" }}>{v.modalidade}</span> : "—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:"#666" }}>{v.tipo_veiculo||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11 }}>{(v.volume_m3 === 0 || v.volume_m3) ? parseFloat(v.volume_m3).toLocaleString("pt-BR") : "—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:"#666" }}>{v.ticket_numero||"—"}</td>
                    <td style={{ padding:"10px 12px", fontWeight:700 }}>{parseFloat(v.peso_liquido||0).toLocaleString("pt-BR")}</td>
                    <td style={{ padding:"10px 12px", fontWeight:700, color:"#e74c3c" }}>R$ {parseFloat(v.valor||0).toFixed(2).replace(".",",")}</td>
                    <td style={{ padding:"10px 12px" }}>
                      {v.pago ? (
                        <span style={{ background:"#eafaf1", color:"#2ecc71", padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700 }}>Pago</span>
                      ) : (
                        <span onClick={() => marcarPago(v)} style={{ background:"#fef6e7", color:"#f39c12", padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, cursor:"pointer" }} title="Clique para marcar como pago">Pendente</span>
                      )}
                    </td>
                    <td style={{ padding:"10px 12px", whiteSpace:"nowrap" }}>
                      <button onClick={() => { setEditViagem(v); setModalViagem(true); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, marginRight:6 }} title="Editar">✏️</button>
                      <button onClick={() => removeViagem(v.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13 }} title="Excluir">🗑️</button>
                    </td>
                  </tr>
                ))}
                {viagens.length === 0 && (
                  <tr><td colSpan={13} style={{ textAlign:"center", padding:40, color:"#bbb", fontSize:13 }}>Nenhuma viagem registrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ textAlign:"right", fontSize:11, color:"#aaa", marginTop:8 }}>{viagens.length} viagem(ns)</div>
        </div>
      )}

      {/* ── ABA CADASTRO ── */}
      {aba === "cadastro" && (
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <div style={{ position:"relative", flex:1, maxWidth:280 }}>
              <input style={{ ...inp, width:"100%", paddingLeft:32 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar aterro..." />
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", fontSize:13, color:"#bbb" }}>🔍</span>
            </div>
            <div style={{ flex:1 }} />
            <button onClick={() => { setEditAterro(null); setModalAterro(true); }}
              style={{ padding:"8px 18px", background:"linear-gradient(135deg,#1aae9e,#17c4b0)", color:"#fff", border:"none", borderRadius:6, fontSize:12, cursor:"pointer", fontFamily:FONT, fontWeight:700 }}>
              + Novo Aterro
            </button>
          </div>
          <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
              <thead style={{ background:"#fafafa" }}>
                <tr>{["#","Nome","CNPJ","Cidade/UF","Telefone","R$/Ton","Licença","Validade","Status","Ações"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb", whiteSpace:"nowrap" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {aterros.filter(a => {
                  if (!search) return true;
                  const s = norm(search);
                  return norm(a.nome||"").includes(s) || (a.cnpj||"").includes(search) || norm(a.cidade||"").includes(s);
                }).map(a => {
                  const licVenc = a.licenca_validade ? new Date(a.licenca_validade) < new Date() : false;
                  const licProx = a.licenca_validade ? Math.ceil((new Date(a.licenca_validade) - new Date()) / (1000*60*60*24)) <= 30 && !licVenc : false;
                  return (
                    <tr key={a.id} style={{ borderBottom:"1px solid #f5f5f5" }} onMouseEnter={e=>e.currentTarget.style.background="#fafafa"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{ padding:"10px 12px", color:"#bbb", fontSize:11 }}>{String(a.id).padStart(4,"0")}</td>
                      <td style={{ padding:"10px 12px", fontWeight:700, color:"#1a1a1a" }}>{a.nome}</td>
                      <td style={{ padding:"10px 12px", fontSize:11 }}>{a.cnpj||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11 }}>{a.cidade ? `${a.cidade}/${a.uf}` : "—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11 }}>{a.telefone||"—"}</td>
                      <td style={{ padding:"10px 12px", fontWeight:600 }}>{parseFloat(a.valor_tonelada||0) > 0 ? `R$ ${parseFloat(a.valor_tonelada).toFixed(2).replace(".",",")}` : "—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11 }}>{a.licenca_numero||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>
                        {a.licenca_validade ? (
                          <span style={{ background: licVenc ? "#fdecea" : licProx ? "#fef6e7" : "#eafaf1", color: licVenc ? "#e74c3c" : licProx ? "#f39c12" : "#2ecc71", padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700 }}>
                            {fmtDate(a.licenca_validade)}
                          </span>
                        ) : "—"}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <span style={{ background: a.ativo ? "#eafaf1" : "#f5f5f5", color: a.ativo ? "#2ecc71" : "#999", padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700 }}>
                          {a.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td style={{ padding:"10px 12px", whiteSpace:"nowrap" }}>
                        <button onClick={() => { setEditAterro(a); setModalAterro(true); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, marginRight:6 }} title="Editar">✏️</button>
                        <button onClick={() => removeAterro(a.id)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13 }} title="Excluir">🗑️</button>
                      </td>
                    </tr>
                  );
                })}
                {aterros.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign:"center", padding:40, color:"#bbb", fontSize:13 }}>Nenhum aterro cadastrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ textAlign:"right", fontSize:11, color:"#aaa", marginTop:8 }}>{aterros.length} aterro(s)</div>
        </div>
      )}

      {/* ── ABA RELATÓRIO ── */}
      {aba === "relatorio" && (
        <div>
          <div style={{ display:"flex", alignItems:"flex-end", gap:12, marginBottom:18, flexWrap:"wrap" }}>
            <Field label="De"><input type="date" style={inp} value={relDe} onChange={e => setRelDe(e.target.value)} /></Field>
            <Field label="Até"><input type="date" style={inp} value={relAte} onChange={e => setRelAte(e.target.value)} /></Field>
            <Field label="Aterro">
              <select style={{ ...inp, appearance:"none", minWidth:180 }} value={relAterroId} onChange={e => setRelAterroId(e.target.value)}>
                <option value="">Todos</option>
                {aterros.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
            </Field>
            <Field label="Modalidade">
              <select style={{ ...inp, appearance:"none", minWidth:150 }} value={relModalidade} onChange={e => setRelModalidade(e.target.value)}>
                <option value="">Todas</option>
                <option value="Caçamba">Caçamba</option>
                <option value="Caminhão">Caminhão</option>
              </select>
            </Field>
            <button onClick={loadRelatorio}
              style={{ padding:"8px 18px", background:"linear-gradient(135deg,#1aae9e,#17c4b0)", color:"#fff", border:"none", borderRadius:6, fontSize:12, cursor:"pointer", fontFamily:FONT, fontWeight:700, height:36 }}>
              Gerar Relatório
            </button>
          </div>

          {relatorio && (
            <div>
              {/* Cards totais */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:20 }}>
                {[
                  { label:"Total Viagens", val: relatorio.totais?.total_viagens || 0, color:"#3498db" },
                  { label:"Peso Total", val: ((parseFloat(relatorio.totais?.peso_total||0))/1000).toFixed(1) + " ton", color:"#1aae9e" },
                  { label:"Valor Total", val: "R$ " + parseFloat(relatorio.totais?.valor_total||0).toFixed(2).replace(".",","), color:"#e74c3c" },
                  { label:"Pendente", val: "R$ " + parseFloat(relatorio.totais?.valor_pendente||0).toFixed(2).replace(".",","), color:"#f39c12" },
                ].map((c,i) => (
                  <div key={i} style={{ background:"#fff", borderRadius:10, padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,.06)", borderLeft:`4px solid ${c.color}` }}>
                    <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", marginBottom:4 }}>{c.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:c.color }}>{c.val}</div>
                  </div>
                ))}
              </div>

              {/* Cards Caçamba x Caminhão */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:14, marginBottom:20 }}>
                {[
                  { label:"Caçambas no período", val: relatorio.totais?.total_cacambas || 0, color:"#2563eb", bg:"#eaf4ff" },
                  { label:"Caminhões no período", val: relatorio.totais?.total_caminhoes || 0, color:"#d97706", bg:"#fff4e6" },
                ].map((c,i) => (
                  <div key={i} style={{ background:c.bg, borderRadius:10, padding:"16px 18px", boxShadow:"0 1px 4px rgba(0,0,0,.06)" }}>
                    <div style={{ fontSize:11, fontWeight:700, color:c.color, textTransform:"uppercase", marginBottom:4 }}>{c.label}</div>
                    <div style={{ fontSize:26, fontWeight:800, color:c.color }}>{c.val} <span style={{ fontSize:13, fontWeight:600 }}>viagem(ns)</span></div>
                  </div>
                ))}
              </div>

              {/* Tabela por modalidade */}
              <div style={{ marginBottom:20 }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:10 }}>Por Modalidade (Caçamba x Caminhão)</h3>
                <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
                    <thead style={{ background:"#fafafa" }}>
                      <tr>{["Modalidade","Viagens","Volume (m³)","Peso Total","Valor Total"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(relatorio.porModalidade||[]).map((r,i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{r.modalidade||"—"}</td>
                          <td style={{ padding:"10px 12px" }}>{r.viagens}</td>
                          <td style={{ padding:"10px 12px" }}>{parseFloat(r.volume_total||0).toLocaleString("pt-BR")} m³</td>
                          <td style={{ padding:"10px 12px" }}>{(parseFloat(r.peso_total||0)/1000).toFixed(1)} ton</td>
                          <td style={{ padding:"10px 12px", fontWeight:700, color:"#e74c3c" }}>R$ {parseFloat(r.valor_total||0).toFixed(2).replace(".",",")}</td>
                        </tr>
                      ))}
                      {(relatorio.porModalidade||[]).length === 0 && <tr><td colSpan={5} style={{ textAlign:"center", padding:30, color:"#bbb" }}>Sem dados no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tabela por tipo de veículo */}
              <div style={{ marginBottom:20 }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:10 }}>Por Tipo de Veículo</h3>
                <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
                    <thead style={{ background:"#fafafa" }}>
                      <tr>{["Tipo de Veículo","Viagens","Volume (m³)","Peso Total","Valor Total"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(relatorio.porTipoVeiculo||[]).map((r,i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{r.tipo_veiculo||"—"}</td>
                          <td style={{ padding:"10px 12px" }}>{r.viagens}</td>
                          <td style={{ padding:"10px 12px" }}>{parseFloat(r.volume_total||0).toLocaleString("pt-BR")} m³</td>
                          <td style={{ padding:"10px 12px" }}>{(parseFloat(r.peso_total||0)/1000).toFixed(1)} ton</td>
                          <td style={{ padding:"10px 12px", fontWeight:700, color:"#e74c3c" }}>R$ {parseFloat(r.valor_total||0).toFixed(2).replace(".",",")}</td>
                        </tr>
                      ))}
                      {(relatorio.porTipoVeiculo||[]).length === 0 && <tr><td colSpan={5} style={{ textAlign:"center", padding:30, color:"#bbb" }}>Sem dados no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tabela por aterro */}
              <div style={{ marginBottom:20 }}>
                <h3 style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:10 }}>Por Aterro</h3>
                <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
                    <thead style={{ background:"#fafafa" }}>
                      <tr>{["Aterro","Viagens","Peso Total","Valor Total","Pago","Pendente"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(relatorio.porAterro||[]).map((r,i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{r.aterro||"—"}</td>
                          <td style={{ padding:"10px 12px" }}>{r.viagens}</td>
                          <td style={{ padding:"10px 12px" }}>{(parseFloat(r.peso_total||0)/1000).toFixed(1)} ton</td>
                          <td style={{ padding:"10px 12px", fontWeight:700, color:"#e74c3c" }}>R$ {parseFloat(r.valor_total||0).toFixed(2).replace(".",",")}</td>
                          <td style={{ padding:"10px 12px", color:"#2ecc71", fontWeight:600 }}>R$ {parseFloat(r.valor_pago||0).toFixed(2).replace(".",",")}</td>
                          <td style={{ padding:"10px 12px", color:"#f39c12", fontWeight:600 }}>R$ {parseFloat(r.valor_pendente||0).toFixed(2).replace(".",",")}</td>
                        </tr>
                      ))}
                      {(relatorio.porAterro||[]).length === 0 && <tr><td colSpan={6} style={{ textAlign:"center", padding:30, color:"#bbb" }}>Sem dados no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tabela por motorista */}
              <div>
                <h3 style={{ fontSize:14, fontWeight:700, color:"#1a1a2e", marginBottom:10 }}>Por Motorista</h3>
                <div style={{ background:"#fff", borderRadius:10, boxShadow:"0 1px 4px rgba(0,0,0,.06)", overflow:"hidden" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, fontFamily:FONT }}>
                    <thead style={{ background:"#fafafa" }}>
                      <tr>{["Motorista","Viagens","Peso Total","Valor Total"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase", borderBottom:"1px solid #ebebeb" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {(relatorio.porMotorista||[]).map((r,i) => (
                        <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                          <td style={{ padding:"10px 12px", fontWeight:700 }}>{r.motorista||"Sem motorista"}</td>
                          <td style={{ padding:"10px 12px" }}>{r.viagens}</td>
                          <td style={{ padding:"10px 12px" }}>{(parseFloat(r.peso_total||0)/1000).toFixed(1)} ton</td>
                          <td style={{ padding:"10px 12px", fontWeight:700, color:"#e74c3c" }}>R$ {parseFloat(r.valor_total||0).toFixed(2).replace(".",",")}</td>
                        </tr>
                      ))}
                      {(relatorio.porMotorista||[]).length === 0 && <tr><td colSpan={4} style={{ textAlign:"center", padding:30, color:"#bbb" }}>Sem dados no período</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {relatorio.totais?.valor_medio_ton > 0 && (
                <div style={{ marginTop:16, background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:"10px 16px", fontSize:12, color:"#0369a1" }}>
                  <strong>Custo médio por tonelada:</strong> R$ {parseFloat(relatorio.totais.valor_medio_ton).toFixed(2).replace(".",",")}
                </div>
              )}
            </div>
          )}
          {!relatorio && (
            <div style={{ textAlign:"center", padding:60, color:"#bbb", fontSize:13 }}>Selecione o período e clique em "Gerar Relatório"</div>
          )}
        </div>
      )}

      {/* Modais */}
      {modalAterro && <AterroModal onClose={() => { setModalAterro(false); setEditAterro(null); }} onSave={saveAterro} initialData={editAterro} editMode={!!editAterro} motoristas={motoristas} frota={frota} aterros={aterros} />}
      {modalViagem && <ViagemAterroModal onClose={() => { setModalViagem(false); setEditViagem(null); }} onSave={saveViagem} initialData={editViagem} editMode={!!editViagem} motoristas={motoristas} frota={frota} aterros={aterros} />}
    </div>
  );
}

const auditAcaoColor = (a) => ({ criar:"#2ecc71", editar:"#3498db", excluir:"#e74c3c", status:"#9b59b6", pagamento:"#f39c12", cancelar:"#e74c3c" }[a] || "#888");
const auditAcaoBg = (a) => ({ criar:"#eafaf1", editar:"#eaf2fb", excluir:"#fdecea", status:"#f4ecfb", pagamento:"#fef6e7", cancelar:"#fdecea" }[a] || "#f5f5f5");

// ─── Página de Auditoria ─────────────────────────────────────────────
function AuditoriaPage({ apiFetch, addToast }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pg, setPg] = useState(1);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [filtroEntidade, setFiltroEntidade] = useState("");
  const [filtroAcao, setFiltroAcao] = useState("");
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroDe, setFiltroDe] = useState("");
  const [filtroAte, setFiltroAte] = useState("");
  const [filtroBusca, setFiltroBusca] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [techId, setTechId] = useState(null);
  const LIM = 30;

  const carregarLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIM });
      if (filtroEntidade) params.set("entidade", filtroEntidade);
      if (filtroAcao) params.set("acao", filtroAcao);
      if (filtroUsuario) params.set("usuario_id", filtroUsuario);
      if (filtroDe) params.set("de", filtroDe);
      if (filtroAte) params.set("ate", filtroAte);
      if (filtroBusca) params.set("busca", filtroBusca);
      const data = await apiFetch("/api/audit-logs?" + params.toString());
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPg(p);
      if (data.usuarios) setUsuarios(data.usuarios);
    } catch (e) { addToast("Erro ao carregar logs: " + e.message, "error"); }
    setLoading(false);
  }, [filtroEntidade, filtroAcao, filtroUsuario, filtroDe, filtroAte, filtroBusca, apiFetch, addToast]);

  useEffect(() => { carregarLogs(1); }, [carregarLogs]);

  const totalPages = Math.ceil(total / LIM);
  const ENTIDADES = [{ value:"", label:"Todos" },{ value:"locacao", label:"Locações" },{ value:"cliente", label:"Clientes" },{ value:"cacamba", label:"Caçambas" },{ value:"motorista", label:"Motoristas" },{ value:"veiculo", label:"Veículos" }];
  const ACOES_F = [{ value:"", label:"Todas" },{ value:"criar", label:"Criação" },{ value:"editar", label:"Edição" },{ value:"excluir", label:"Exclusão" },{ value:"status", label:"Status" },{ value:"pagamento", label:"Pagamento" },{ value:"cancelar", label:"Cancelamento" }];

  const fmtDtAmigavel = (d) => {
    if (!d) return ""; const dt = new Date(d);
    return dt.toLocaleDateString("pt-BR")+(" às ")+dt.toLocaleTimeString("pt-BR",{hour:"2-digit",minute:"2-digit"});
  };

  // Gerar diffs amigáveis (consolidando equivalentes)
  const gerarDiffsAmigaveis = (antes, depois) => {
    if (!antes && !depois) return [];
    const a = antes || {}, dep = depois || {};
    const allKeys = new Set([...Object.keys(a), ...Object.keys(dep)]);
    const usado = new Set();
    const diffs = [];
    // Consolidar equivalentes
    for (const [, group] of Object.entries(AUDIT_EQUIVALENTS)) {
      const vals = group.map(k => ({ k, antes: a[k], depois: dep[k] })).filter(v => allKeys.has(v.k));
      if (!vals.length) continue;
      vals.forEach(v => usado.add(v.k));
      const antesVal = vals.map(v => v.antes).find(v => !auditIsEmpty(v));
      const depoisVal = vals.map(v => v.depois).find(v => !auditIsEmpty(v));
      if (JSON.stringify(antesVal) === JSON.stringify(depoisVal)) continue;
      if (auditIsEmpty(antesVal) && auditIsEmpty(depoisVal)) continue;
      const label = AUDIT_LABELS[vals[0].k] || vals[0].k;
      const campo = vals[0].k;
      diffs.push({ campo, label, antes: antesVal, depois: depoisVal });
    }
    // Campos restantes
    for (const k of allKeys) {
      if (usado.has(k)) continue;
      if (k === "id" || k === "clienteId") continue;
      if (JSON.stringify(a[k]) === JSON.stringify(dep[k])) continue;
      if (auditIsEmpty(a[k]) && auditIsEmpty(dep[k])) continue;
      diffs.push({ campo: k, label: AUDIT_LABELS[k] || k, antes: a[k], depois: dep[k] });
    }
    return diffs;
  };

  const diffBadge = (antes, depois) => {
    const ae = auditIsEmpty(antes), de = auditIsEmpty(depois);
    if (ae && !de) return { text:"Adicionado", bg:"#eafaf1", color:"#27ae60" };
    if (!ae && de) return { text:"Removido", bg:"#fdecea", color:"#e74c3c" };
    return { text:"Alterado", bg:"#eaf2fb", color:"#2980b9" };
  };

  const inputSt = { padding:"7px 10px", borderRadius:6, border:"1px solid #e0e0e0", fontSize:13, fontFamily:FONT, outline:"none", minWidth:0, background:"#fff" };
  const selectSt = { ...inputSt, cursor:"pointer" };

  return (
    <div style={{ fontFamily:FONT, maxWidth:1100 }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
        <div style={{ background:"#1a9e8f", borderRadius:10, width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <IcoAudit />
        </div>
        <div>
          <div style={{ fontSize:20, fontWeight:700, color:"#1a1a2e" }}>Auditoria do Sistema</div>
          <div style={{ fontSize:12, color:"#999" }}>Registro completo de todas as ações realizadas</div>
        </div>
        <span style={{ marginLeft:"auto", fontSize:13, color:"#1a9e8f", fontWeight:600, background:"#eafaf1", padding:"4px 12px", borderRadius:20 }}>{total} registros</span>
      </div>

      {/* Filtros */}
      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #eee", padding:"14px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"flex-end" }}>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>Módulo</label>
            <select value={filtroEntidade} onChange={e => setFiltroEntidade(e.target.value)} style={selectSt}>
              {ENTIDADES.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>Ação</label>
            <select value={filtroAcao} onChange={e => setFiltroAcao(e.target.value)} style={selectSt}>
              {ACOES_F.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>Usuário</label>
            <select value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)} style={selectSt}>
              <option value="">Todos</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>De</label>
            <input type="date" value={filtroDe} onChange={e => setFiltroDe(e.target.value)} style={inputSt} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>Até</label>
            <input type="date" value={filtroAte} onChange={e => setFiltroAte(e.target.value)} style={inputSt} />
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:3, flex:1, minWidth:140 }}>
            <label style={{ fontSize:11, color:"#888", fontWeight:600 }}>Busca</label>
            <input type="text" placeholder="Buscar..." value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)} style={inputSt} onKeyDown={e => { if (e.key === "Enter") carregarLogs(1); }} />
          </div>
          <button onClick={() => carregarLogs(1)} style={{ padding:"7px 18px", borderRadius:6, border:"none", background:"#1a9e8f", color:"#fff", fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:FONT, height:34 }}>Filtrar</button>
          <button onClick={() => { setFiltroEntidade(""); setFiltroAcao(""); setFiltroUsuario(""); setFiltroDe(""); setFiltroAte(""); setFiltroBusca(""); }} style={{ padding:"7px 14px", borderRadius:6, border:"1px solid #e0e0e0", background:"#fff", color:"#999", fontWeight:500, fontSize:13, cursor:"pointer", fontFamily:FONT, height:34 }}>Limpar</button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign:"center", padding:50, color:"#aaa" }}>
          <div style={{ fontSize:14 }}>Carregando registros...</div>
        </div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, color:"#bbb" }}>
          <div style={{ fontSize:36, marginBottom:8 }}>Nenhum registro</div>
          <div style={{ fontSize:13 }}>Ajuste os filtros ou aguarde novas ações no sistema</div>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {logs.map(log => {
            const isOpen = expandedId === log.id;
            const isTech = techId === log.id;
            const diffs = gerarDiffsAmigaveis(log.dados_antes, log.dados_depois);
            return (
            <div key={log.id} style={{ background:"#fff", borderRadius:10, border: isOpen ? "1px solid #d0d0d0" : "1px solid #eee", overflow:"hidden", transition:"border .2s" }}>
              {/* Row compacta */}
              <div onClick={() => { setExpandedId(isOpen ? null : log.id); if (isOpen) setTechId(null); }} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", cursor:"pointer" }}>
                <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:30, height:30, borderRadius:8, background:auditAcaoBg(log.acao), fontSize:14 }}>
                  {{ locacao:"\u{1F4CB}", cliente:"\u{1F464}", cacamba:"\u{1F5D1}", motorista:"\u{1F697}", veiculo:"\u{1F69B}" }[log.entidade] || "\u{1F4DD}"}
                </span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                    <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:700, color:"#fff", background:auditAcaoColor(log.acao), textTransform:"uppercase", letterSpacing:.5 }}>{auditAcaoLabel(log.acao)}</span>
                    <span style={{ fontSize:13, color:"#333", fontWeight:500 }}>{auditEntidadeLabel(log.entidade)}{log.entidade_id ? " #"+log.entidade_id : ""}</span>
                  </div>
                  <div style={{ fontSize:12, color:"#999", marginTop:2 }}>{log.descricao}</div>
                </div>
                <div style={{ textAlign:"right", flexShrink:0 }}>
                  <div style={{ fontSize:12, color:"#1a9e8f", fontWeight:600 }}>{log.usuario_nome}</div>
                  <div style={{ fontSize:11, color:"#bbb" }}>{fmtDtAmigavel(log.created_at)}</div>
                </div>
                <span style={{ fontSize:11, color:"#ccc", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition:"transform .15s", marginLeft:4 }}>{"\u25B6"}</span>
              </div>

              {/* Expandido */}
              {isOpen && (
                <div style={{ borderTop:"1px solid #f0f0f0" }}>
                  {/* Resumo da alteração */}
                  <div style={{ padding:"14px 20px", background:"#fafcfe" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12, flexWrap:"wrap" }}>
                      <div style={{ fontSize:12, color:"#777" }}>
                        <span style={{ fontWeight:600, color:"#555" }}>{auditAcaoLabel(log.acao)}</span> realizada em <span style={{ fontWeight:600, color:"#555" }}>{fmtDtAmigavel(log.created_at)}</span>
                      </div>
                      <div style={{ fontSize:12, color:"#777" }}>
                        Por: <span style={{ fontWeight:600, color:"#1a9e8f" }}>{log.usuario_nome}</span>
                      </div>
                    </div>

                    {/* Campos alterados - Visão Amigável */}
                    {diffs.length > 0 ? (
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:8 }}>Campos alterados</div>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          {diffs.map((d, i) => {
                            const badge = diffBadge(d.antes, d.depois);
                            return (
                              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background: i % 2 === 0 ? "#fff" : "#f9fafb", borderRadius:6, border:"1px solid #f0f0f0" }}>
                                <span style={{ minWidth:120, fontSize:12, fontWeight:600, color:"#444" }}>{d.label}</span>
                                <span style={{ display:"inline-block", padding:"1px 6px", borderRadius:3, fontSize:10, fontWeight:600, color:badge.color, background:badge.bg }}>{badge.text}</span>
                                <div style={{ flex:1, display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
                                  {!auditIsEmpty(d.antes) && (
                                    <span style={{ color:"#c0392b", textDecoration:"line-through", opacity:.8 }}>{auditFmtVal(d.antes, d.campo)}</span>
                                  )}
                                  {!auditIsEmpty(d.antes) && !auditIsEmpty(d.depois) && (
                                    <span style={{ color:"#ccc" }}>{"\u2192"}</span>
                                  )}
                                  {!auditIsEmpty(d.depois) && (
                                    <span style={{ color:"#27ae60", fontWeight:600 }}>{auditFmtVal(d.depois, d.campo)}</span>
                                  )}
                                  {auditIsEmpty(d.antes) && auditIsEmpty(d.depois) && (
                                    <span style={{ color:"#bbb" }}>Não informado</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (log.dados_antes || log.dados_depois) ? (
                      <div style={{ fontSize:12, color:"#999" }}>Sem campos alterados detectados</div>
                    ) : null}

                    {/* Botão detalhes técnicos */}
                    {(log.dados_antes || log.dados_depois) && (
                      <div style={{ marginTop:12 }}>
                        <button onClick={(e) => { e.stopPropagation(); setTechId(isTech ? null : log.id); }} style={{ background:"none", border:"1px solid #ddd", borderRadius:5, padding:"4px 12px", fontSize:11, color:"#888", cursor:"pointer", fontFamily:FONT, fontWeight:500 }}>
                          {isTech ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
                          <span style={{ marginLeft:4, fontSize:9, transform: isTech ? "rotate(90deg)" : "rotate(0deg)", display:"inline-block", transition:"transform .15s" }}>{"\u25B6"}</span>
                        </button>
                      </div>
                    )}

                    {/* Visão Técnica */}
                    {isTech && (
                      <div style={{ marginTop:10, padding:"12px 14px", background:"#f7f8fa", borderRadius:8, border:"1px solid #eee" }}>
                        <div style={{ fontSize:11, fontWeight:700, color:"#888", textTransform:"uppercase", letterSpacing:.5, marginBottom:8 }}>Dados técnicos (payload original)</div>
                        <div style={{ display:"flex", gap:16, fontSize:11, color:"#888", marginBottom:10, flexWrap:"wrap" }}>
                          <span>ID Registro: <b style={{ color:"#555" }}>{log.entidade_id || "\u2014"}</b></span>
                          <span>Entidade: <b style={{ color:"#555" }}>{log.entidade}</b></span>
                          <span>Ação: <b style={{ color:"#555" }}>{log.acao}</b></span>
                          <span>IP: <b style={{ color:"#555" }}>{log.ip || "\u2014"}</b></span>
                          <span>Log ID: <b style={{ color:"#555" }}>{log.id}</b></span>
                        </div>
                        {log.dados_antes && (
                          <div style={{ marginBottom:8 }}>
                            <div style={{ fontSize:11, fontWeight:600, color:"#c0392b", marginBottom:3 }}>dados_antes:</div>
                            <pre style={{ fontSize:11, color:"#555", background:"#fff", padding:8, borderRadius:4, border:"1px solid #eee", overflow:"auto", maxHeight:200, margin:0, whiteSpace:"pre-wrap", wordBreak:"break-all" }}>{JSON.stringify(log.dados_antes, null, 2)}</pre>
                          </div>
                        )}
                        {log.dados_depois && (
                          <div>
                            <div style={{ fontSize:11, fontWeight:600, color:"#27ae60", marginBottom:3 }}>dados_depois:</div>
                            <pre style={{ fontSize:11, color:"#555", background:"#fff", padding:8, borderRadius:4, border:"1px solid #eee", overflow:"auto", maxHeight:200, margin:0, whiteSpace:"pre-wrap", wordBreak:"break-all" }}>{JSON.stringify(log.dados_depois, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:18, alignItems:"center" }}>
          <button disabled={pg <= 1} onClick={() => carregarLogs(pg - 1)} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #e0e0e0", background:"#fff", cursor: pg > 1 ? "pointer" : "not-allowed", fontSize:12, fontFamily:FONT, opacity: pg <= 1 ? .4 : 1, fontWeight:500 }}>{"\u2190"} Anterior</button>
          <span style={{ fontSize:12, color:"#888", padding:"0 8px" }}>Página {pg} de {totalPages}</span>
          <button disabled={pg >= totalPages} onClick={() => carregarLogs(pg + 1)} style={{ padding:"6px 14px", borderRadius:6, border:"1px solid #e0e0e0", background:"#fff", cursor: pg < totalPages ? "pointer" : "not-allowed", fontSize:12, fontFamily:FONT, opacity: pg >= totalPages ? .4 : 1, fontWeight:500 }}>Próxima {"\u2192"}</button>
        </div>
      )}
    </div>
  );
}


// ─── Main App ─────────────────────────────────────────────────────────────────
const PAGES = [
  { key:"dashboard",    label:"Dashboard",         Icon:IcoDashboard },
  { key:"tutorial",     label:"Tutorial",          Icon:IcoTutorial  },
  { key:"mapa",         label:"Mapa Operacional",  Icon:IcoMap       },
  { key:"locacoes",     label:"Locações",           Icon:IcoList      },
  { key:"clientes",     label:"Clientes",           Icon:IcoUsers      },
  { key:"cacambas",     label:"Caçambas",           Icon:IcoContainer, group:"cacambas" },
  { key:"tiposCacamba", label:"Tipos de Caçamba",   Icon:IcoContainer, group:"cacambas" },
  { key:"itinerarios",  label:"Itinerários",        Icon:IcoTruck     },
  { key:"finComissoes",  label:"Comissões",          Icon:IcoDriver    },
  { key:"aterros",      label:"Aterros",             Icon:IcoAterro    },
  { key:"frota",        label:"Veículos",           Icon:IcoCar,       group:"frota" },
  { key:"motoristas",   label:"Motoristas",         Icon:IcoDriver,    group:"frota" },
  { key:"manutencao",   label:"Manutenção",         Icon:IcoWrench,    group:"frota" },
  { key:"combustivel",  label:"Combustível",        Icon:IcoFuel,      group:"frota" },
  { key:"relatorios",   label:"Relatórios",         Icon:IcoChart,     group:"financeiro" },
  { key:"relLocacoes",  label:"Relatório Locações", Icon:IcoChart,     group:"financeiro" },
  { key:"relDetalhado", label:"Locações (Detalhado)", Icon:IcoChart,    group:"financeiro" },
  { key:"relMensal",    label:"Relatório Mensal",    Icon:IcoChart,    group:"financeiro" },
  { key:"relMotoristas",label:"Relatório Motoristas", Icon:IcoDriver,   group:"financeiro" },
  { key:"orcamentos",   label:"Orçamentos",             Icon:IcoDoc,         group:"comercial" },
  { key:"calculadora",  label:"Calculadora de Caçambas", Icon:IcoCalculator, group:"comercial" },
  { key:"finDashboard",  label:"Resumo",               Icon:IcoFinanceiro, group:"gestao_fin" },
  { key:"finLancamentos",label:"Lançamentos",         Icon:IcoFinanceiro, group:"gestao_fin" },
  { key:"finContasPagar",label:"Contas a Pagar",      Icon:IcoFinanceiro, group:"gestao_fin" },
  { key:"finContasReceber",label:"Contas a Receber",  Icon:IcoFinanceiro, group:"gestao_fin" },
  { key:"finPlanoContas",label:"Categorias",           Icon:IcoFinanceiro, group:"gestao_fin" },
  { key:"finRelatorios", label:"Resultado do Período", Icon:IcoChart,     group:"gestao_fin" },
  { key:"servicos",       label:"Fossa e Água",           Icon:IcoServicos,  group:"servicos_liquidos", modulo:"servicos_liquidos" },
  { key:"tiposServico",   label:"Tipos de Serviço",       Icon:IcoServicos,  group:"servicos_liquidos", modulo:"servicos_liquidos" },
  { key:"relServicos",    label:"Relatórios Serviços",    Icon:IcoChart,     group:"servicos_liquidos", modulo:"servicos_liquidos" },
  { key:"ia",           label:"Controla.AI",         Icon:IcoBot       },
  { key:"auditoria",    label:"Auditoria",           Icon:IcoAudit,     group:"sistema" },
  { key:"configuracoes",label:"Configurações",      Icon:IcoGear,      group:"sistema" },
];

const pageDesc = {
  boasvindas:"Página inicial do usuário operacional",
  dashboard:"Visão geral da saúde da empresa",
  tutorial:"Aprenda a usar o Controla com guias interativos e animações",
  mapa:"Localização geográfica das locações",
  locacoes:"Gerenciamento de locações de caçambas",
  clientes:"Cadastro e gerenciamento de clientes",
  motoristas:"Cadastro e gerenciamento de motoristas",
  tiposCacamba:"Cadastro de tipos de caçamba (entulho, resíduos, etc.)",
  cacambas:"Cadastro e gerenciamento de caçambas",
  itinerarios:"Roteiros de atendimento por motorista",
  aterros:"Controle de aterros, viagens e destinação de resíduos",
  frota:"Cadastro e gestão de veículos da frota",
  manutencao:"Registro e histórico de manutenções da frota",
  combustivel:"Controle de abastecimentos e eficiência de combustível",
  relatorios:"Análises e relatórios financeiros",
  relLocacoes:"Relatório detalhado de locações por cliente",
  relDetalhado:"Relatório detalhado com seleção de colunas (Excel)",
  relMensal:"Resumo diário de entregas, retiradas, trocas e pagamentos do mês",
  relMotoristas:"Produção e desempenho por motorista com gráficos e PDF",
  orcamentos:"Crie e gerencie orçamentos comerciais para seus clientes",
  calculadora:"Calcule o volume e descubra a caçamba ideal para seu cliente",
  finDashboard:"Quanto entrou, quanto saiu e o saldo do mês",
  finPlanoContas:"Organize suas categorias de receitas e despesas",
  finLancamentos:"Registre entradas e saídas de dinheiro",
  finContasPagar:"Veja o que precisa pagar",
  finContasReceber:"Veja o que precisa receber",
  finComissoes:"Controle e pague as comissões dos motoristas",
  finRelatorios:"Veja o resultado financeiro de um período",
  servicos:"Atendimentos de limpeza de fossa e abastecimento de água",
  tiposServico:"Cadastre os tipos de serviço oferecidos (fossa, água, etc.)",
  relServicos:"Relatórios isolados do módulo de serviços",
  ia:"Inteligência artificial para análise do seu negócio",
  configuracoes:"Dados da empresa, logo, QR Code PIX e condições do recibo",
};


// ─── Users & Session ──────────────────────────────────────────────────────────
// ─── API ─────────────────────────────────────────────────────────────────────
const API = process.env.REACT_APP_API || "http://localhost:3001";

function getToken() { try { return sessionStorage.getItem("controla_token"); } catch(e){ return null; } }
function setToken(t){ try { sessionStorage.setItem("controla_token", t); } catch(e){} }
function delToken() { try { sessionStorage.removeItem("controla_token"); sessionStorage.removeItem("controla_session"); } catch(e){} }

async function apiFetch(path, opts = {}) {
  const token = getToken();
  const res = await fetch(API + path, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    method: opts.method || "GET",
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Token expirado ou inválido — redirecionar para login
    if (res.status === 401) {
      clearSession();
      window.location.reload();
      throw new Error("Sessão expirada. Faça login novamente.");
    }
    throw new Error(data.error || "Erro na requisição");
  }
  return data;
}

// Simple in-memory session
let _memSession = null;
function getSession()        { return _memSession; }
function saveSession(user)   { _memSession = user; try { sessionStorage.setItem("controla_session", JSON.stringify(user)); } catch(e){} }
function clearSession()      { _memSession = null; delToken(); }

// ─── Caçamba SVG Illustration ─────────────────────────────────────────────────
const CacambaSVG = ({ size=120, opacity=0.18, color="#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity }}>
    {/* Truck body */}
    <rect x="4" y="52" width="72" height="34" rx="4" fill={color}/>
    <rect x="4" y="44" width="44" height="20" rx="3" fill={color} opacity="0.7"/>
    {/* Cabin windshield */}
    <rect x="8" y="48" width="34" height="12" rx="2" fill={color} opacity="0.35"/>
    {/* Container (caçamba) */}
    <path d="M52 26 L100 26 L108 56 L44 56 Z" fill={color} opacity="0.85"/>
    <path d="M52 26 L100 26 L100 30 L52 30 Z" fill={color} opacity="0.5"/>
    {/* Wheels */}
    <circle cx="22" cy="88" r="10" fill={color} opacity="0.9"/>
    <circle cx="22" cy="88" r="5" fill={color} opacity="0.4"/>
    <circle cx="68" cy="88" r="10" fill={color} opacity="0.9"/>
    <circle cx="68" cy="88" r="5" fill={color} opacity="0.4"/>
    {/* Exhaust */}
    <rect x="74" y="38" width="4" height="16" rx="2" fill={color} opacity="0.5"/>
    <ellipse cx="76" cy="36" rx="4" ry="3" fill={color} opacity="0.3"/>
  </svg>
);

const TrashSVG = ({ size=80, opacity=0.13, color="#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity }}>
    <path d="M10 20 L16 68 L64 68 L70 20 Z" fill={color}/>
    <rect x="6" y="14" width="68" height="8" rx="3" fill={color}/>
    <rect x="28" y="6" width="24" height="10" rx="3" fill={color} opacity="0.7"/>
    <line x1="28" y1="32" x2="26" y2="60" stroke={color} opacity="0.4" strokeWidth="3" strokeLinecap="round"/>
    <line x1="40" y1="32" x2="40" y2="60" stroke={color} opacity="0.4" strokeWidth="3" strokeLinecap="round"/>
    <line x1="52" y1="32" x2="54" y2="60" stroke={color} opacity="0.4" strokeWidth="3" strokeLinecap="round"/>
  </svg>
);

// ─── Login Page ───────────────────────────────────────────────────────────────
// ─── Modal de Boa Segunda-Feira ──────────────────────────────────────────────
// ─── Novidades / Changelog ──────────────────────────────────────────────────
// Ao mudar esta versão, o popup reaparece pra todos os usuários.
const NOVIDADES_VERSAO = "2026.05.20";
const NOVIDADES_LISTA = [
  { icon: "🎨", titulo: "Orçamento personalizável", desc: "Ao gerar o PDF do orçamento, escolha a cor e inclua a logo da sua empresa." },
  { icon: "🚚", titulo: "Editar motorista do itinerário", desc: "Agora dá pra trocar o motorista, a data e as paradas de um itinerário já criado." },
  { icon: "📍", titulo: "Busca de endereços melhorada", desc: "Sugestões mais rápidas e com mais fontes — encontra ruas e pontos com mais facilidade." },
  { icon: "⏰", titulo: "Hora de entrega na locação", desc: "Campo opcional de horário previsto de entrega — aparece no recibo quando preenchido." },
  { icon: "🔍", titulo: "Filtros por coluna nos Clientes", desc: "Botão de funil pra filtrar a lista de clientes por nome, telefone, endereço e mais." },
  { icon: "📊", titulo: "Exportar Contas em Excel e PDF", desc: "Contas a Pagar e Receber agora exportam relatórios formatados em Excel e PDF." },
];

function NovidadesModal({ onClose }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.6)", zIndex:10001, display:"flex", alignItems:"center", justifyContent:"center", padding:20, animation:"fadeIn .25s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"#fff", borderRadius:18, maxWidth:520, width:"100%", maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,.3)", fontFamily:FONT, animation:"fadeSlideUp .35s cubic-bezier(.2,.9,.3,1.2)" }}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg, #1aae9e 0%, #0f7a6e 100%)", padding:"26px 28px 22px", color:"#fff", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-40, right:-30, width:150, height:150, borderRadius:"50%", background:"rgba(255,255,255,.08)" }} />
          <div style={{ position:"relative", zIndex:1 }}>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:2, textTransform:"uppercase", opacity:.85, marginBottom:6 }}>✨ Novidades</div>
            <div style={{ fontSize:24, fontWeight:800, lineHeight:1.15 }}>O que há de novo no Controla</div>
            <div style={{ fontSize:13, opacity:.9, marginTop:6 }}>Algumas melhorias que preparamos pra você:</div>
          </div>
        </div>
        {/* Lista */}
        <div style={{ padding:"18px 24px 8px" }}>
          {NOVIDADES_LISTA.map((n, i) => (
            <div key={i} style={{ display:"flex", gap:14, padding:"12px 0", borderBottom: i < NOVIDADES_LISTA.length-1 ? "1px solid #f2f2f2" : "none" }}>
              <div style={{ flexShrink:0, width:42, height:42, borderRadius:10, background:"#f0faf8", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{n.icon}</div>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:"#1e2d4f" }}>{n.titulo}</div>
                <div style={{ fontSize:12.5, color:"#777", marginTop:2, lineHeight:1.5 }}>{n.desc}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div style={{ padding:"12px 24px 24px", display:"flex", justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"#1aae9e", color:"#fff", border:"none", borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
            Entendi, vamos lá! 🚀
          </button>
        </div>
      </div>
    </div>
  );
}

function BoaSemanaModal({ onClose, locacoes = [], cacambas = [], userName = "" }) {
  // Calcular métricas pra mostrar pro usuário
  const ativas      = locacoes.filter(l => ["Para Colocar","Colocada","Para Retirar","Para Trocar"].includes(l.status)).length;
  const paraColocar = locacoes.filter(l => l.status === "Para Colocar").length;
  const colocadas   = locacoes.filter(l => l.status === "Colocada").length;
  const paraRetirar = locacoes.filter(l => l.status === "Para Retirar").length;
  const paraCobrar  = locacoes.filter(l => l.status === "Para Cobrar").length;
  const cacAlocadas = cacambas.filter(c => c.status === "Alocada").length;
  const cacDisponiveis = cacambas.filter(c => c.status === "Disponível").length;

  // Atrasadas (data de retirada prevista < hoje e ainda colocada)
  const hoje = new Date(); hoje.setHours(0,0,0,0);
  const atrasadas = locacoes.filter(l => {
    if (!["Colocada","Para Retirar","Para Trocar"].includes(l.status)) return false;
    const dr = l.dataRetiraPrevista || l.dataRetirada || l.data_retirada;
    if (!dr) return false;
    const d = new Date(dr); d.setHours(0,0,0,0);
    return d < hoje;
  }).length;

  const primeiroNome = (userName || "").split(" ")[0] || "";

  // Frase motivacional rotativa baseada no dia do mês
  const frases = [
    "Cada caçamba colocada hoje é mais um cliente atendido.",
    "Semana nova, oportunidades novas. Vamos com tudo!",
    "Operação fluida começa com planejamento na segunda.",
    "Disciplina supera talento. Bora pra cima!",
    "O sucesso é a soma de pequenos esforços repetidos todo dia.",
    "Quem chega mais cedo na segunda termina mais cedo na sexta.",
  ];
  const fraseDoDia = frases[new Date().getDate() % frases.length];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
        zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "fadeIn .25s ease"
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 18, maxWidth: 540, width: "100%",
          maxHeight: "92vh", overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,.3)",
          fontFamily: FONT,
          animation: "fadeSlideUp .35s cubic-bezier(.2,.9,.3,1.2)"
        }}
      >
        {/* Header gradiente */}
        <div style={{
          background: "linear-gradient(135deg, #1aae9e 0%, #0f7a6e 100%)",
          padding: "28px 28px 22px",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Detalhe decorativo */}
          <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
          <div style={{ position: "absolute", bottom: -50, right: 80, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", opacity: 0.85, marginBottom: 6 }}>
              ☀️ Segunda-feira
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.15 }}>
              Boa semana{primeiroNome ? `, ${primeiroNome}` : ""}!
            </div>
            <div style={{ fontSize: 14, opacity: 0.92, marginTop: 8, fontStyle: "italic", lineHeight: 1.5 }}>
              "{fraseDoDia}"
            </div>
          </div>
        </div>

        {/* Conteúdo — briefing operacional */}
        <div style={{ padding: "22px 28px 8px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#666", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 }}>
            Briefing da operação
          </div>

          {/* 4 cards de métricas */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <BriefingCard label="Locações ativas" valor={ativas} cor="#1aae9e" />
            <BriefingCard label="Caçambas alocadas" valor={cacAlocadas} sub={`${cacDisponiveis} disponíveis`} cor="#3498db" />
            <BriefingCard label="Para colocar hoje" valor={paraColocar} cor="#16a085" />
            <BriefingCard label="Para retirar" valor={paraRetirar} cor="#9b59b6" />
          </div>

          {/* Alertas se tiver */}
          {(atrasadas > 0 || paraCobrar > 0) && (
            <div style={{ background: "#fff8e1", border: "1px solid #ffd47a", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#b8651b", marginBottom: 6 }}>
                ⚠️ Demanda atenção
              </div>
              <div style={{ fontSize: 12, color: "#8a4a14", lineHeight: 1.6 }}>
                {atrasadas > 0 && <div>• {atrasadas} caçamba{atrasadas === 1 ? "" : "s"} com retirada atrasada</div>}
                {paraCobrar > 0 && <div>• {paraCobrar} locaç{paraCobrar === 1 ? "ão" : "ões"} para cobrar</div>}
              </div>
            </div>
          )}

          {atrasadas === 0 && paraCobrar === 0 && (
            <div style={{ background: "#e8f5e9", border: "1px solid #a8d8aa", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1e7c2c", display: "flex", alignItems: "center", gap: 6 }}>
                ✅ Operação em ordem — nada urgente pra resolver
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "8px 28px 24px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              background: "#1aae9e", color: "#fff", border: "none",
              borderRadius: 10, padding: "12px 28px",
              fontSize: 14, fontWeight: 700, cursor: "pointer",
              fontFamily: FONT, transition: "transform .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.03)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          >
            Bora começar 🚀
          </button>
        </div>
      </div>
    </div>
  );
}

function BriefingCard({ label, valor, sub, cor }) {
  return (
    <div style={{ background: "#fafafa", border: "1px solid #ececec", borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: cor, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 10, color: "#aaa", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [login, setLogin]   = useState("");
  const [senha, setSenha]   = useState("");
  const [erro, setErro]     = useState("");
  const [sucesso, setSucesso] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot" | "reset"
  const [emailReset, setEmailReset] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [shakeLogin, setShakeLogin] = useState(false);
  const [loginPopup, setLoginPopup] = useState(null); // null | { tipo: "erro" | "bloqueado" | "suspenso", msg: string }
  const [tentativasLogin, setTentativasLogin] = useState(0);

  // Detecta token de reset na URL (?reset=TOKEN)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset");
    if (token) {
      setResetToken(token);
      setMode("reset");
      // Limpa a URL sem recarregar
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleLogin = async () => {
    if (loginPopup) return;
    setErro(""); setSucesso("");
    if (!login || !senha) { setErro("Preencha usuário e senha."); return; }
    setLoading(true);
    try {
      const res = await fetch(API + "/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: login.trim().toLowerCase(), senha })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msgBackend = data.error || "";
        const codeBackend = data.code || "";
        // Empresa suspensa/cancelada — não incrementa tentativas, não mostra erro de credenciais
        if (codeBackend === "TENANT_SUSPENDED" || codeBackend === "TENANT_CANCELLED") {
          setLoginPopup({ tipo: "suspenso", msg: msgBackend || "Seu acesso está temporariamente suspenso.\nEntre em contato com o suporte para regularizar a situação." });
          setLoading(false);
          return;
        }
        const novas = tentativasLogin + 1;
        setTentativasLogin(novas);
        setShakeLogin(true);
        setTimeout(() => setShakeLogin(false), 600);
        if (msgBackend.toLowerCase().includes("muitas tentativas") || novas >= 6) {
          setLoginPopup({ tipo: "bloqueado", msg: "Você excedeu o limite de tentativas de login. Sua conta foi temporariamente bloqueada por segurança. Aguarde alguns minutos e tente novamente." });
        } else {
          setLoginPopup({ tipo: "erro", msg: "Usuário ou senha incorretos." });
        }
        setLoading(false);
        return;
      }
      setTentativasLogin(0);
      setToken(data.token);
      saveSession(data.user);
      onLogin(data.user, data.boletoVencido || null);
    } catch(err) {
      setLoginPopup({ tipo: "erro", msg: "Erro de conexão. Verifique sua internet." });
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    setErro(""); setSucesso("");
    if (!emailReset) { setErro("Informe seu email."); return; }
    setLoading(true);
    try {
      const data = await apiFetch("/api/auth/forgot-password", { method: "POST", body: { email: emailReset.trim() } });
      setSucesso(data.message || "Se o email estiver cadastrado, você receberá um link de redefinição.");
      setErro("");
    } catch(err) {
      setSucesso("Se o email estiver cadastrado, você receberá um link de redefinição.");
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setErro(""); setSucesso("");
    if (!novaSenha || !confirmarSenha) { setErro("Preencha os dois campos."); return; }
    if (novaSenha.length < 6) { setErro("A senha deve ter no mínimo 6 caracteres."); return; }
    if (novaSenha !== confirmarSenha) { setErro("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      const data = await apiFetch("/api/auth/reset-password", { method: "POST", body: { token: resetToken, senha: novaSenha } });
      setSucesso(data.message || "Senha redefinida com sucesso!");
      setErro("");
      setTimeout(() => { setMode("login"); setSucesso(""); setNovaSenha(""); setConfirmarSenha(""); }, 3000);
    } catch(err) {
      setErro(err.message || "Erro ao redefinir senha.");
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter") {
      if (mode === "login") handleLogin();
      else if (mode === "forgot") handleForgot();
      else if (mode === "reset") handleReset();
    }
  };

  const goToMode = (m) => { setMode(m); setErro(""); setSucesso(""); };

  const inputStyle = { width:"100%", padding:"13px 16px", fontSize:14, border:"1.5px solid #e5e7eb", borderRadius:8, fontFamily:FONT, background:"#f9fafb", color:"#111", transition:"all .2s", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:11, fontWeight:700, color:"#374151", textTransform:"uppercase", letterSpacing:.5, marginBottom:6 };
  const focusIn = (e) => { e.target.style.borderColor="#1aae9e"; e.target.style.boxShadow="0 0 0 3px rgba(26,174,158,.12)"; };
  const focusOut = (e) => { e.target.style.borderColor="#e5e7eb"; e.target.style.boxShadow="none"; };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden", fontFamily:FONT, background:"#061018" }}>
      <div style={{ position:"absolute", inset:0, backgroundImage:"url(/login-bg.png)", backgroundSize:"cover", backgroundPosition:"center center", backgroundRepeat:"no-repeat" }} />

      {shakeLogin && <style>{`@keyframes shakeLogin { 0%,100%{transform:translateX(0)} 15%{transform:translateX(-8px)} 30%{transform:translateX(8px)} 45%{transform:translateX(-6px)} 60%{transform:translateX(6px)} 75%{transform:translateX(-3px)} 90%{transform:translateX(3px)} }`}</style>}
      <div className="login-box" style={{ position:"relative", zIndex:1, width:"100%", maxWidth:440, margin:"20px", background:"rgba(255,255,255,.97)", borderRadius:16, boxShadow: shakeLogin ? "0 25px 80px rgba(220,38,38,.35), 0 0 60px rgba(220,38,38,.15)" : "0 25px 80px rgba(0,0,0,.4), 0 0 60px rgba(26,174,158,.12)", overflow:"hidden", backdropFilter:"blur(10px)", animation: shakeLogin ? "shakeLogin 0.5s ease" : "none", transition:"box-shadow .3s" }}>

        {/* Logo */}
        <div style={{ padding:"48px 40px 28px", textAlign:"center" }}>
          <img src="/logo-full.png" alt="Controla Caçambas" style={{ width:220, maxWidth:"75%", margin:"0 auto 18px", display:"block" }} />
          <div style={{ fontSize:13, color:"#6b7280", fontWeight:500, letterSpacing:.5 }}>{"Gestão Inteligente de Caçambas"}</div>
        </div>

        <div style={{ margin:"0 40px", height:2, background:"linear-gradient(90deg, transparent, #1aae9e, #4ecdc4, #1aae9e, transparent)", borderRadius:1 }} />

        <div style={{ padding:"28px 40px 36px" }}>

          {/* ═══ MODO LOGIN ═══ */}
          {mode === "login" && <>
            <div style={{ marginBottom:20 }}>
              <label style={labelStyle}>{"Usuário"}</label>
              <input autoFocus value={login} onChange={e => { setLogin(e.target.value); setErro(""); }} onKeyDown={handleKey} placeholder="Digite seu usuário" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom:4, position:"relative" }}>
              <label style={labelStyle}>{"Senha"}</label>
              <input type={showSenha ? "text" : "password"} value={senha} onChange={e => { setSenha(e.target.value); setErro(""); }} onKeyDown={handleKey} placeholder="Digite sua senha" style={{ ...inputStyle, paddingRight:44 }} onFocus={focusIn} onBlur={focusOut} />
              <button type="button" onClick={() => setShowSenha(s => !s)} style={{ position:"absolute", right:14, bottom:11, background:"none", border:"none", cursor:"pointer", color:"#9ca3af", fontSize:18, padding:2 }}>
                {showSenha ? "\uD83D\uDE48" : "\uD83D\uDC41"}
              </button>
            </div>
            <div style={{ textAlign:"right", marginBottom:16 }}>
              <button type="button" onClick={() => goToMode("forgot")} style={{ background:"none", border:"none", color:"#1aae9e", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT, padding:0 }}>
                {"Esqueceu sua senha?"}
              </button>
            </div>
          </>}

          {/* ═══ MODO FORGOT ═══ */}
          {mode === "forgot" && <>
            <div style={{ marginBottom:6 }}>
              <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:700, color:"#1a1a1a" }}>{"Recuperar Senha"}</h3>
              <p style={{ margin:"0 0 18px", fontSize:12, color:"#6b7280", lineHeight:1.5 }}>{"Informe o email cadastrado. Enviaremos um link para redefinir sua senha."}</p>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={labelStyle}>{"Email"}</label>
              <input autoFocus type="email" value={emailReset} onChange={e => { setEmailReset(e.target.value); setErro(""); setSucesso(""); }} onKeyDown={handleKey} placeholder="seu@email.com" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
          </>}

          {/* ═══ MODO RESET ═══ */}
          {mode === "reset" && <>
            <div style={{ marginBottom:6 }}>
              <h3 style={{ margin:"0 0 6px", fontSize:16, fontWeight:700, color:"#1a1a1a" }}>{"Nova Senha"}</h3>
              <p style={{ margin:"0 0 18px", fontSize:12, color:"#6b7280", lineHeight:1.5 }}>{"Crie uma nova senha para sua conta. Mínimo de 6 caracteres."}</p>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={labelStyle}>{"Nova senha"}</label>
              <input autoFocus type="password" value={novaSenha} onChange={e => { setNovaSenha(e.target.value); setErro(""); }} onKeyDown={handleKey} placeholder="Mínimo 6 caracteres" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={labelStyle}>{"Confirmar senha"}</label>
              <input type="password" value={confirmarSenha} onChange={e => { setConfirmarSenha(e.target.value); setErro(""); }} onKeyDown={handleKey} placeholder="Repita a nova senha" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
          </>}

          {/* Mensagem de sucesso */}
          {sucesso && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"10px 14px", marginBottom:16, color:"#15803d", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
              {"\u2705"} {sucesso}
            </div>
          )}

          {/* Mensagem de erro (forgot/reset apenas — login usa popup) */}
          {erro && mode !== "login" && (
            <div style={{ background:"linear-gradient(135deg, #fef2f2, #fee2e2)", border:"1.5px solid #fca5a5", borderRadius:10, padding:"14px 16px", marginBottom:16, color:"#dc2626", fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:10, boxShadow:"0 4px 16px rgba(220,38,38,.12)", animation:"fadeInDown .3s ease" }}>
              <span style={{ fontSize:22, flexShrink:0 }}>{"\u26A0\uFE0F"}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700 }}>Erro</div>
                <div style={{ fontSize:12, fontWeight:500, color:"#b91c1c", marginTop:2 }}>{erro}</div>
              </div>
            </div>
          )}
          {/* Erro inline do login — campos vazios */}
          {erro && mode === "login" && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px", marginBottom:16, color:"#dc2626", fontSize:13, fontWeight:600 }}>
              ⚠️ {erro}
            </div>
          )}
          <style>{`@keyframes fadeInDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }`}</style>

          {/* Botão principal */}
          {mode === "login" && (
            <button onClick={handleLogin} disabled={loading} style={{ width:"100%", padding:"14px", background: loading ? "#7ee8df" : "linear-gradient(135deg, #1aae9e 0%, #158a7d 100%)", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor: loading?"wait":"pointer", fontFamily:FONT, boxShadow:"0 4px 20px rgba(26,174,158,.35)", transition:"all .3s", letterSpacing:.3 }}>
              {loading ? "Verificando..." : "Entrar no Sistema"}
            </button>
          )}
          {mode === "forgot" && !sucesso && (
            <button onClick={handleForgot} disabled={loading} style={{ width:"100%", padding:"14px", background: loading ? "#7ee8df" : "linear-gradient(135deg, #1aae9e 0%, #158a7d 100%)", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor: loading?"wait":"pointer", fontFamily:FONT, boxShadow:"0 4px 20px rgba(26,174,158,.35)", transition:"all .3s", letterSpacing:.3 }}>
              {loading ? "Enviando..." : "Enviar link de redefinição"}
            </button>
          )}
          {mode === "reset" && !sucesso && (
            <button onClick={handleReset} disabled={loading} style={{ width:"100%", padding:"14px", background: loading ? "#7ee8df" : "linear-gradient(135deg, #1aae9e 0%, #158a7d 100%)", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor: loading?"wait":"pointer", fontFamily:FONT, boxShadow:"0 4px 20px rgba(26,174,158,.35)", transition:"all .3s", letterSpacing:.3 }}>
              {loading ? "Redefinindo..." : "Redefinir Senha"}
            </button>
          )}

          {/* Link voltar ao login */}
          {mode !== "login" && (
            <div style={{ textAlign:"center", marginTop:16 }}>
              <button type="button" onClick={() => goToMode("login")} style={{ background:"none", border:"none", color:"#1aae9e", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
                {"\u2190 Voltar ao login"}
              </button>
            </div>
          )}
        </div>

        <div style={{ padding:"14px 40px", borderTop:"1px solid #f0f0f0", textAlign:"center", fontSize:11, color:"#9ca3af" }}>
          {"Controla Caçambas v1.61 \u00B7 \u00A9 2026 Controla Caçambas"}
        </div>
      </div>

      {/* POPUP de erro de login */}
      {loginPopup && (
        <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,.55)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:14, width:380, maxWidth:"90vw", boxShadow:"0 20px 60px rgba(0,0,0,.25)", overflow:"hidden" }}>
            {/* Header do popup */}
            <div style={{ padding:"24px 24px 0", textAlign:"center" }}>
              <div style={{ width:56, height:56, borderRadius:"50%", background: loginPopup.tipo === "suspenso" ? "#fef2f2" : loginPopup.tipo === "bloqueado" ? "#fef2f2" : "#fff7ed", margin:"0 auto 16px", display:"flex", alignItems:"center", justifyContent:"center", border: loginPopup.tipo === "suspenso" ? "2px solid #fecaca" : loginPopup.tipo === "bloqueado" ? "2px solid #fecaca" : "2px solid #fed7aa" }}>
                <span style={{ fontSize:28 }}>{loginPopup.tipo === "suspenso" ? "\uD83D\uDEAB" : loginPopup.tipo === "bloqueado" ? "\uD83D\uDEAB" : "\u26A0\uFE0F"}</span>
              </div>
              <div style={{ fontSize:17, fontWeight:700, color: loginPopup.tipo === "suspenso" ? "#dc2626" : loginPopup.tipo === "bloqueado" ? "#dc2626" : "#ea580c", marginBottom:8 }}>
                {loginPopup.tipo === "suspenso" ? "Empresa suspensa" : loginPopup.tipo === "bloqueado" ? "Acesso Bloqueado" : "Falha no Login"}
              </div>
              <div style={{ fontSize:14, color:"#555", lineHeight:1.6, marginBottom:4, whiteSpace:"pre-line" }}>
                {loginPopup.msg}
              </div>
              {loginPopup.tipo === "erro" && tentativasLogin >= 3 && (
                <div style={{ fontSize:12, color:"#dc2626", fontWeight:600, marginTop:8 }}>
                  {`Tentativa ${tentativasLogin} de 6 — após 6 erros o acesso será bloqueado.`}
                </div>
              )}
            </div>
            {/* Botão OK */}
            <div style={{ padding:"20px 24px 24px" }}>
              <button onClick={() => { setLoginPopup(null); setSenha(""); }} style={{
                width:"100%", padding:"13px", background: (loginPopup.tipo === "bloqueado" || loginPopup.tipo === "suspenso") ? "#dc2626" : "linear-gradient(135deg, #1aae9e, #158a7d)",
                color:"#fff", border:"none", borderRadius:8, fontSize:14, fontWeight:700, cursor:"pointer", fontFamily:FONT,
                boxShadow: (loginPopup.tipo === "bloqueado" || loginPopup.tipo === "suspenso") ? "0 4px 16px rgba(220,38,38,.3)" : "0 4px 16px rgba(26,174,158,.3)"
              }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(() => {
    try {
      const saved = sessionStorage.getItem("controla_session");
      const token = sessionStorage.getItem("controla_token");
      if (saved && token) return JSON.parse(saved);
    } catch(e) {}
    return null;
  });
  const [page, setPage] = useState("dashboard");
  const [locacoesInitFilter, setLocacoesInitFilter] = useState(null);
  const goToLocacoes = (filter) => { setLocacoesInitFilter(filter || null); setPage("locacoes"); };
  const [clientes, setClientes] = useState([]);
  const [locacoes, setLocacoes] = useState([]);
  const [itinerarios, setItinerarios] = useState([]);
  const [frota, setFrota]       = useState([]);
  const [cacambas, setCacambas] = useState([]);
  const [tiposCacamba, setTiposCacamba] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [novaLocModal, setNovaLocModal] = useState(false);
  const [novoCliModal, setNovoCliModal] = useState(false);
  const [mapaDetailLoc, setMapaDetailLoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manutencoes, setManutencoes] = useState([]);
  const [abastecimentos, setAbastecimentos] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAlterarSenha, setShowAlterarSenha] = useState(false);
  const [showSuporteModal, setShowSuporteModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showBoaSemana, setShowBoaSemana] = useState(false);

  // Segunda-feira: mostrar popup de boas-vindas (só uma vez por dia)
  useEffect(() => {
    if (!session) return;
    const hoje = new Date();
    const isSegunda = hoje.getDay() === 1; // 1 = segunda
    if (!isSegunda) return;
    const hojeISO = hoje.toISOString().split("T")[0];
    const ultimoMostradoISO = localStorage.getItem("controla_boa_semana_ultimo");
    if (ultimoMostradoISO === hojeISO) return; // já mostrou hoje
    // Atrasa 1.2s pra não atropelar o load inicial
    const t = setTimeout(() => setShowBoaSemana(true), 1200);
    return () => clearTimeout(t);
  }, [session]);

  const fecharBoaSemana = () => {
    const hojeISO = new Date().toISOString().split("T")[0];
    localStorage.setItem("controla_boa_semana_ultimo", hojeISO);
    setShowBoaSemana(false);
  };

  // Popup de novidades — mostra 1x quando a versão do changelog muda
  const [showNovidades, setShowNovidades] = useState(false);
  useEffect(() => {
    if (!session) return;
    const vistaVersao = localStorage.getItem("controla_novidades_versao");
    if (vistaVersao === NOVIDADES_VERSAO) return;
    const t = setTimeout(() => setShowNovidades(true), 1600); // após o load
    return () => clearTimeout(t);
  }, [session]);
  const fecharNovidades = () => {
    localStorage.setItem("controla_novidades_versao", NOVIDADES_VERSAO);
    setShowNovidades(false);
  };

  const [empresaNome, setEmpresaNome] = useState("");
  const [empresaEndereco, setEmpresaEndereco] = useState("");
  const [empresaCfg, setEmpresaCfg] = useState(null);
  const [boletoVencido, setBoletoVencido] = useState(null);
  const [showBoletoPopup, setShowBoletoPopup] = useState(false);
  const [boletoPopupConfirmed, setBoletoPopupConfirmed] = useState(false);
  const [modulosAtivos, setModulosAtivos] = useState([]);

  const toast = useToast();

  // Carregar dados do backend após login
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cls, locs, frt, cacs, its, mots, tipCacs, manuts, abasts] = await Promise.all([
        apiFetch("/api/clientes"),
        apiFetch("/api/locacoes"),
        apiFetch("/api/frota"),
        apiFetch("/api/cacambas"),
        apiFetch("/api/itinerarios"),
        apiFetch("/api/motoristas"),
        apiFetch("/api/tipos-cacamba").catch(() => []),
        apiFetch("/api/manutencoes").catch(() => []),
        apiFetch("/api/abastecimentos").catch(() => []),
      ]);
      setClientes((cls || []).map(c => ({ ...c, cpfCnpj: c.cpf_cnpj || c.cpfCnpj || '', criadoEm: fmtDate(c.created_at) || c.criadoEm || '' })));
      setMotoristas(mots || []);
      // Mapa clienteId -> cpf_cnpj para enriquecer locações
      const cliMap = {};
      (cls || []).forEach(c => { cliMap[c.id] = c.cpf_cnpj || c.cpfCnpj || ''; });
      setLocacoes((locs || []).map(r => {
        const e = r.endereco || {};
        return {
        ...r,
        cpfCnpj: cliMap[r.cliente_id] || '',
        dataEntrega: r.data_entrega || r.dataEntrega || '',
        dataRetirada: r.data_retirada || r.dataRetirada || '',
        dataRetiraPrevista: r.data_retirada || r.dataRetiraPrevista || '',
        dataPagamento: r.data_pagamento || r.dataPagamento || '',
        dataRetiradaReal: r.data_retirada_real || r.dataRetiradaReal || '',
        dataColocacaoReal: r.data_colocacao_real || r.dataColocacaoReal || '',
        dataSolicitacao: r.data_solicitacao || r.dataSolicitacao || '',
        dataTrocaPrevista: r.data_troca_prevista || r.dataTrocaPrevista || '',
        dataPedidoTroca: r.data_pedido_troca || r.dataPedidoTroca || '',
        dataPedidoRetirada: r.data_pedido_retirada || r.dataPedidoRetirada || '',
        motoristaRetirada: r.motorista_retirada || r.motoristaRetirada || '',
        prazoPagamento: r.prazo_pagamento || r.prazoPagamento || '',
        cacamba: r.cacamba_codigo || r.cacamba || '',
        cliente: r.cliente_nome || r.cliente || '',
        tipoCacamba: r.tipo_cacamba || r.tipoCacamba || '',
        contatoTel: r.contato_tel || r.contatoTel || '',
        contatoNome: r.contato_nome || r.contatoNome || '',
        logradouro: r.logradouro || e.logradouro || r.end_logradouro || '',
        numero: r.numero || e.numero || r.end_numero || '',
        bairro: r.bairro || e.bairro || r.end_bairro || '',
        cidade: r.cidade || e.cidade || r.end_cidade || '',
        uf: r.uf || e.uf || r.end_uf || '',
        pago: !!r.pago,
        gerarMtr: !!r.gerar_mtr,
        previsao: r.data_entrega || r.dataEntrega || '—',
        status: r.status || 'Para Colocar',
        valor: parseFloat(r.valor) || 0,
        endereco: e,
      };}));
      // Normalize DB snake_case to camelCase for frota
      setFrota((frt || []).map(v => ({
        ...v,
        vencCNH: v.venc_cnh || v.vencCNH || '',
        vencCRLV: v.venc_crlv || v.vencCRLV || '',
        vencSeguro: v.venc_seguro || v.vencSeguro || '',
        motorista: v.motorista || '',
        modelo: v.modelo || '',
        placa: v.placa || '',
      })));
      setCacambas(cacs || []);
      setTiposCacamba(tipCacs || []);
      setItinerarios((its || []).map(it => {
        const paradas = (it.locIds || []).map((id, i) => {
          return { locId: id, status: it.paradaStatuses?.[i] || "Pendente", motivo: it.paradaMotivos?.[i] || "", obs: "" };
        });
        const atendidos = paradas.filter(p => p.status === "Atendido").length;
        const pendentes = paradas.length - atendidos;
        // Respeitar status do backend — não auto-fechar
        const status = it.status || (atendidos === 0 ? "Em Aberto" : "Em Andamento");
        return { ...it, paradas, atendidos, pendentes, total: paradas.length, status };
      }));
      setManutencoes(manuts || []);
      setAbastecimentos(abasts || []);
    } catch(err) {
      console.error("Erro ao carregar dados:", err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (session) loadData();
  }, [session, loadData]);

  // === AUTO-REFRESH: apenas ao CLICAR em Locações ou Mapa Operacional ===
  // Não dispara em alt-tab / volta de aba — apenas ao navegar vindo de outra página.
  const AUTO_REFRESH_PAGES = ["locacoes", "mapa"];
  const prevPageRef = useRef(null);
  useEffect(() => {
    if (!session) { prevPageRef.current = null; return; }
    if (
      prevPageRef.current !== null &&
      prevPageRef.current !== page &&
      AUTO_REFRESH_PAGES.includes(page)
    ) {
      loadData();
    }
    prevPageRef.current = page;
  }, [page, session, loadData]);

  useEffect(() => {
    if (session) apiFetch("/api/config").then(d => {
      setEmpresaCfg(d || null);
      setEmpresaNome(d?.nome_empresa || d?.nome || d?.empresa || "");
      const endParts = [d?.endereco, d?.bairro, d?.cidade, d?.uf].filter(Boolean);
      if (endParts.length > 0) setEmpresaEndereco(endParts.join(", "));
    }).catch(()=>{});
    // Carregar módulos ativos do tenant
    if (session) apiFetch("/api/modulos/meus").then(d => {
      setModulosAtivos((d || []).map(m => m.slug));
    }).catch(()=>setModulosAtivos([]));
    // Checar boleto vencido (para sessões que já estavam logadas)
    if (session && !boletoVencido) {
      apiFetch("/api/auth/boleto-status").then(d => {
        if (d?.boletoVencido) setBoletoVencido(d.boletoVencido);
      }).catch(()=>{});
    }
  }, [session]);

  const handleLogin  = (user, boleto) => {
    setSession(user);
    if (boleto) {
      setBoletoVencido(boleto);
      setShowBoletoPopup(true);
      setBoletoPopupConfirmed(false);
    }
  };
  const handleLogout = () => {
    // Enviar log de logout para o backend
    try {
      const sid = session?.sessionId || '';
      const tk = getToken() || '';
      navigator.sendBeacon && navigator.sendBeacon(API + '/api/auth/logout', new Blob([JSON.stringify({ sessionId: sid, token: tk })], { type: 'application/json' }));
      // Fallback com fetch
      fetch(API + '/api/auth/logout', { method: 'POST', headers: { Authorization: 'Bearer ' + tk, 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId: sid }), keepalive: true }).catch(() => {});
    } catch(e) {}
    clearSession(); setSession(null); setPage("dashboard"); setBoletoVencido(null); setShowBoletoPopup(false); setBoletoPopupConfirmed(false);
  };

  // Tracking de páginas visitadas (heartbeat) para monitoramento admin
  useEffect(() => {
    if (!session || !page) return;
    const sid = session?.sessionId || '';
    const sendHeartbeat = () => {
      fetch(API + '/api/auth/heartbeat', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + getToken(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pagina: page, sessionId: sid }),
        keepalive: true,
      }).catch(() => {});
    };
    sendHeartbeat();
    // Heartbeat a cada 5 minutos para manter "online" status
    const iv = setInterval(sendHeartbeat, 300000);
    return () => clearInterval(iv);
  }, [page, session]);

  // Enviar logout ao fechar a aba
  useEffect(() => {
    if (!session) return;
    const onUnload = () => {
      const sid = session?.sessionId || '';
      const tk = getToken() || '';
      try {
        navigator.sendBeacon && navigator.sendBeacon(API + '/api/auth/logout', new Blob([JSON.stringify({ sessionId: sid, token: tk })], { type: 'application/json' }));
      } catch(e) {}
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [session]);

  // ─── Controle de acesso por cargo ─────────────────────────────────────────
  const rawRole = (session?.role || "admin").toLowerCase();
  const userRole = ["admin", "administrador"].includes(rawRole) ? "admin" : rawRole;
  const isOperacional = ["operacional", "user", "usuario"].includes(userRole);
  const isFinanceiro  = userRole === "financeiro";

  // Páginas permitidas para Operacional
  const OPERACIONAL_PAGES = new Set([
    "mapa", "locacoes", "clientes", "cacambas", "tiposCacamba",
    "itinerarios", "frota", "motoristas", "manutencao", "combustivel",
    "orcamentos", "calculadora", "configuracoes"
  ]);

  // Páginas permitidas para Financeiro — APENAS Plano de Contas (sem Resumo nem Resultado do Período)
  const FINANCEIRO_PAGES = new Set([
    "finLancamentos", "finContasPagar", "finContasReceber", "finComissoes", "finPlanoContas"
  ]);

  // Página inicial por cargo
  useEffect(() => {
    if (isOperacional && page === "dashboard") setPage("boasvindas");
    if (isFinanceiro && !FINANCEIRO_PAGES.has(page)) setPage("finLancamentos");
  }, [isOperacional, isFinanceiro, page]);

  if (!session) return <LoginPage onLogin={handleLogin} />;

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontFamily:FONT, flexDirection:"column", gap:12, color:"#555" }}>
      <div style={{ fontSize:48, animation:"bounce 1s infinite" }}>🚛</div>
      <div style={{ fontWeight:700, fontSize:16 }}>Carregando Controla CAÇAMBA...</div>
      <div style={{ fontSize:12, color:"#aaa" }}>Conectando ao banco de dados</div>
    </div>
  );

  // motoristas agora vem do state (carregado da API /api/motoristas)

  const normLoc = (r) => ({
    ...r,
    dataEntrega: r.dataEntrega || r.data_entrega || '',
    dataRetirada: r.dataRetirada || r.data_retirada || '',
    dataSolicitacao: r.dataSolicitacao || r.data_solicitacao || '',
    cacamba: r.cacamba || r.cacamba_codigo || '',
    cliente: r.cliente || r.cliente_nome || '',
    pago: !!r.pago,
    gerarMtr: !!r.gerar_mtr,
    previsao: r.dataEntrega || r.data_entrega || '—',
    status: r.status || 'Para Colocar',
    valor: parseFloat(r.valor) || 0,
    endereco: r.endereco || {
      logradouro: r.end_logradouro || '',
      numero: r.end_numero || '',
      bairro: r.end_bairro || '',
      cidade: r.end_cidade || '',
      uf: r.end_uf || '',
      cep: r.end_cep || '',
    },
  });

  const addLocacao = async (loc) => {
    try {
      const nova = await apiFetch("/api/locacoes", { method: "POST", body: loc });
      setLocacoes(p => [...p, normLoc(nova)]);
      toast.add("Locação criada com sucesso!", "success");
    } catch(err) {
      toast.add("Erro ao criar locação: " + err.message, "error");
    }
  };

  const current = PAGES.find(p => p.key === page) || (page === "boasvindas" ? { key:"boasvindas", label:"Início", Icon:IcoDashboard } : null);

  return (
    <>
    <GlobalStyles />
    <ToastContainer toasts={toast.toasts} removeToast={toast.remove} />

    {/* ─── Popup Boleto Vencido (aparece no login) ─── */}
    {showBoletoPopup && boletoVencido && (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:99999, backdropFilter:"blur(4px)" }}>
        <div style={{ background:"#fff", borderRadius:16, width:440, padding:0, boxShadow:"0 20px 60px rgba(0,0,0,.3)", animation:"slideIn .3s ease", overflow:"hidden" }}>
          <div style={{ background:"linear-gradient(135deg, #e74c3c, #c0392b)", padding:"28px 24px", textAlign:"center" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:"rgba(255,255,255,.2)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
              <span style={{ fontSize:36, color:"#fff" }}>!</span>
            </div>
            <h2 style={{ color:"#fff", fontSize:20, fontWeight:800, margin:"0 0 4px" }}>Boleto Vencido</h2>
            <p style={{ color:"rgba(255,255,255,.85)", fontSize:13, margin:0 }}>Referência: {boletoVencido.referencia}</p>
          </div>
          <div style={{ padding:"24px" }}>
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"16px", marginBottom:16, textAlign:"center" }}>
              <div style={{ fontSize:42, fontWeight:900, color:"#dc2626", lineHeight:1 }}>{boletoVencido.diasVencido}</div>
              <div style={{ fontSize:13, color:"#991b1b", fontWeight:600, marginTop:4 }}>{boletoVencido.diasVencido === 1 ? "dia vencido" : "dias vencido(s)"}</div>
            </div>
            <p style={{ fontSize:13, color:"#555", lineHeight:1.6, textAlign:"center", margin:"0 0 8px" }}>
              Seu boleto no valor de <strong style={{ color:"#dc2626" }}>R$ {parseFloat(boletoVencido.valor).toFixed(2).replace(".",",")}</strong> está vencido
              {boletoVencido.diasVencido >= 1 ? ` há ${boletoVencido.diasVencido} dia(s)` : ""}.
            </p>
            {boletoVencido.bloqueioEm > 0 ? (
              <p style={{ fontSize:12, color:"#b91c1c", fontWeight:700, textAlign:"center", background:"#fef2f2", padding:"8px 12px", borderRadius:6, margin:"0 0 16px" }}>
                Seu acesso será bloqueado em {boletoVencido.bloqueioEm} dia(s) caso o pagamento não seja efetuado.
              </p>
            ) : (
              <p style={{ fontSize:12, color:"#b91c1c", fontWeight:700, textAlign:"center", background:"#fef2f2", padding:"8px 12px", borderRadius:6, margin:"0 0 16px" }}>
                Seu acesso pode ser bloqueado a qualquer momento. Regularize o pagamento.
              </p>
            )}
            <button onClick={() => { setShowBoletoPopup(false); setBoletoPopupConfirmed(true); }} style={{ width:"100%", padding:"14px", background:"#dc2626", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    )}

    <div style={{ display:"flex", height:"100vh", fontFamily:FONT, background:"#f5f5f5", fontSize:14 }}>
      {/* Mobile overlay */}
      {sidebarOpen && <div className="mobile-overlay" onClick={()=>setSidebarOpen(false)} />}
      {/* Sidebar */}
      <div className={`desktop-sidebar app-sidebar${sidebarOpen?" mobile-open":""}`} style={{ width:214, background:"#1e2330", display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div onClick={()=>setPage("dashboard")} style={{ height:56, boxSizing:"border-box", display:"flex", alignItems:"center", padding:"0 12px", borderBottom:"1px solid #2a3040", cursor:"pointer", background:"#fff", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:7 }}>
            <img src="/logo-icon.png" alt="CC" style={{ width:32, height:32, objectFit:"contain", flexShrink:0 }} />
            <div style={{ minWidth:0 }}>
              <div style={{ color:"#1e2d4f", fontWeight:800, fontSize:13, lineHeight:1.2, whiteSpace:"nowrap" }}>{"Controla"} <span style={{ color:"#1aae9e" }}>{"Caçambas"}</span></div>
              <div style={{ color:"#9ca3af", fontSize:9, fontWeight:500, lineHeight:1.2, marginTop:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{"Gestão Inteligente de Caçambas"}</div>
            </div>
          </div>
        </div>
        <nav style={{ flex:1, padding:"10px 0", overflowY:"auto" }}>
          {(() => {
            // Filtrar páginas: módulos + cargo
            const visiblePages = PAGES.filter(p => {
              if (p.modulo && !modulosAtivos.includes(p.modulo)) return false;
              if (isOperacional && !OPERACIONAL_PAGES.has(p.key)) return false;
              if (isFinanceiro  && !FINANCEIRO_PAGES.has(p.key))  return false;
              return true;
            });
            const groups = {};
            const topLevel = [];
            visiblePages.forEach(p => {
              if (p.group) {
                if (!groups[p.group]) groups[p.group] = [];
                groups[p.group].push(p);
              } else {
                topLevel.push(p);
              }
            });
            const items = [];
            const groupLabels = { frota:"Frota", cacambas:"Caçambas", financeiro:"Relatórios Locações", comercial:"Comercial", gestao_fin:"Plano de Contas", servicos_liquidos:"Fossa e Água", sistema:"Sistema" };
            const groupIcons = { frota:IcoTruck, cacambas:IcoContainer, financeiro:IcoChart, comercial:IcoComercial, gestao_fin:IcoFinanceiro, servicos_liquidos:IcoServicos, sistema:IcoGear };
            const rendered = new Set();

            const renderGroup = (gk) => {
              if (rendered.has(gk) || !groups[gk]) return;
              rendered.add(gk);
              const GIcon = groupIcons[gk] || IcoCar;
              const isExpanded = expandedGroups[gk];
              const isGroupActive = groups[gk].some(c => c.key === page);
              items.push(
                <div key={"g_"+gk}>
                  <button onClick={() => setExpandedGroups(prev => ({...prev, [gk]: !prev[gk]}))} style={{ width:"100%", textAlign:"left", padding:"9px 16px", background:isGroupActive&&!isExpanded?"rgba(26,174,158,.18)":"transparent", border:"none", cursor:"pointer", color:isGroupActive?"#4ecdc4":"rgba(255,255,255,.45)", fontSize:13, fontWeight:isGroupActive?700:400, display:"flex", alignItems:"center", gap:9, borderLeft:"3px solid "+(isGroupActive&&!isExpanded?"#4ecdc4":"transparent"), fontFamily:FONT }}>
                    <GIcon /> {groupLabels[gk]||gk}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft:"auto", transform:isExpanded?"rotate(90deg)":"rotate(0)", transition:"transform .15s" }}><path d="M9 18l6-6-6-6"/></svg>
                  </button>
                  {isExpanded && groups[gk].map(cp => (
                    <button key={cp.key} onClick={() => {setPage(cp.key);setSidebarOpen(false);}} style={{ width:"100%", textAlign:"left", padding:"7px 16px 7px 40px", background:page===cp.key?"rgba(26,174,158,.18)":"transparent", border:"none", cursor:"pointer", color:page===cp.key?"#4ecdc4":"rgba(255,255,255,.35)", fontSize:12, fontWeight:page===cp.key?700:400, display:"flex", alignItems:"center", gap:8, borderLeft:"3px solid "+(page===cp.key?"#4ecdc4":"transparent"), fontFamily:FONT }}>
                      <cp.Icon /> {cp.label}
                    </button>
                  ))}
                </div>
              );
            };

            // Render in order: top-level items, and when we encounter the first item that has a group peer, render that group
            const processedTopLevel = new Set();

            visiblePages.forEach(p => {
              if (p.group) {
                renderGroup(p.group);
              } else if (!processedTopLevel.has(p.key)) {
                processedTopLevel.add(p.key);
                items.push(
                  <button key={p.key} onClick={() => {setPage(p.key);setSidebarOpen(false);}} style={{ width:"100%", textAlign:"left", padding:"9px 16px", background:page===p.key?"rgba(26,174,158,.18)":"transparent", border:"none", cursor:"pointer", color:page===p.key?"#4ecdc4":"rgba(255,255,255,.45)", fontSize:13, fontWeight:page===p.key?700:400, display:"flex", alignItems:"center", gap:9, borderLeft:"3px solid "+(page===p.key?"#4ecdc4":"transparent"), fontFamily:FONT }}>
                    <p.Icon /> {p.label}
                  </button>
                );
              }
            });
            return items;
          })()}
        </nav>
        <div style={{ padding:14, borderTop:"1px solid rgba(255,255,255,.06)", fontSize:11, color:"rgba(255,255,255,.25)" }}>
          v1.61 · © 2026 Controla CAÇAMBA
        </div>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0, overflow:"hidden" }}>
        {/* Topbar */}
        <div className="app-topbar" style={{ background:"#fff", borderBottom:"1px solid #e4e4e4", padding:"0 16px", height:56, boxSizing:"border-box", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0, boxShadow:"0 1px 4px rgba(0,0,0,.06)", zIndex:2, position:"relative" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <button className="mobile-hamburger" onClick={()=>setSidebarOpen(p=>!p)} style={{ display:"none", background:"none", border:"none", cursor:"pointer", color:"#555", fontSize:20, padding:4, alignItems:"center", justifyContent:"center" }}>
              ☰
            </button>
            <span style={{ fontSize:13, color:"#555", fontWeight:600 }}>{current?.label || "Dashboard"}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button className="desktop-topbar-btns" onClick={()=>setNovaLocModal(true)} style={{ background:"linear-gradient(135deg,#1aae9e,#17c4b0)", color:"#fff", border:"none", borderRadius:20, padding:"7px 15px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:5, boxShadow:"0 2px 8px rgba(26,174,158,.3)", transition:"transform .15s, box-shadow .15s" }} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 4px 12px rgba(26,174,158,.4)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 8px rgba(26,174,158,.3)";}}>📦 Nova Locação</button>
            <button className="desktop-topbar-btns" onClick={()=>setNovoCliModal(true)} style={{ background:"linear-gradient(135deg,#0ea5e9,#38bdf8)", color:"#fff", border:"none", borderRadius:20, padding:"7px 15px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:5, boxShadow:"0 2px 8px rgba(14,165,233,.3)", transition:"transform .15s, box-shadow .15s" }} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 4px 12px rgba(14,165,233,.4)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 8px rgba(14,165,233,.3)";}}>👤 Novo Cliente</button>
            <div className="desktop-topbar-btns" style={{ width:1, height:24, background:"#e4e4e4", margin:"0 2px" }} />
            <button className="desktop-topbar-btns" onClick={()=>setPage("mapa")} style={{ background:page==="mapa"?"linear-gradient(135deg,#059669,#10b981)":"#fff", color:page==="mapa"?"#fff":"#059669", border:page==="mapa"?"none":"1.5px solid #a7f3d0", borderRadius:20, padding:"7px 16px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:6, boxShadow:page==="mapa"?"0 3px 10px rgba(5,150,105,.35)":"0 1px 4px rgba(0,0,0,.06)", transition:"all .2s" }} onMouseEnter={e=>{if(page!=="mapa"){e.currentTarget.style.background="#ecfdf5";e.currentTarget.style.borderColor="#6ee7b7";}e.currentTarget.style.transform="translateY(-1px)";}} onMouseLeave={e=>{if(page!=="mapa"){e.currentTarget.style.background="#fff";e.currentTarget.style.borderColor="#a7f3d0";}e.currentTarget.style.transform="translateY(0)";}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg> Mapa Operacional</button>
            <button className="ai-pulse-btn" onClick={()=>setPage("ia")} style={{ background:page==="ia"?"linear-gradient(135deg,#7c3aed,#6d28d9)":"linear-gradient(135deg,#8b5cf6,#a78bfa)", color:"#fff", border:"none", borderRadius:20, padding:"7px 15px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:5, boxShadow:"0 2px 8px rgba(139,92,246,.3)", transition:"all .15s" }} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-1px)";e.currentTarget.style.boxShadow="0 4px 12px rgba(139,92,246,.4)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 8px rgba(139,92,246,.3)";}}>🤖 Controla.AI</button>
            <div style={{ display:"flex", alignItems:"center", gap:8, position:"relative" }}>
              <div onClick={()=>setShowUserDropdown(p=>!p)} style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", padding:"4px 8px", borderRadius:6, transition:"background .15s" }}
                onMouseEnter={e=>e.currentTarget.style.background="#f3f4f6"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:"#1aae9e", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:12 }}>{session.avatar}</div>
                <span className="topbar-user-name" style={{ fontSize:13, fontWeight:600, color:"#333" }}>{empresaNome ? empresaNome + " — " : ""}{session.nome}</span>
                ▼
              </div>
              {showUserDropdown && <>
                <div onClick={()=>setShowUserDropdown(false)} style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:998 }} />
                <div style={{ position:"absolute", top:"100%", right:0, marginTop:4, background:"#fff", border:"1px solid #e4e4e4", borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,.12)", minWidth:200, zIndex:999, overflow:"hidden" }}>
                  <div style={{ padding:"12px 16px", borderBottom:"1px solid #f0f0f0" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:"#1a1a1a" }}>{session.nome}</div>
                    <div style={{ fontSize:11, color:"#999", marginTop:2 }}>{session.email || empresaNome}</div>
                  </div>
                  <button onClick={()=>{setPage(isFinanceiro ? "finLancamentos" : isOperacional ? "boasvindas" : "dashboard");setShowUserDropdown(false);}} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:500, color:"#555", display:"flex", alignItems:"center", gap:10, fontFamily:FONT }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    🏠 {isFinanceiro ? "Plano de Contas" : isOperacional ? "Início" : "Dashboard"}
                  </button>
                  {!isFinanceiro && (
                    <button onClick={()=>{setPage("configuracoes");setShowUserDropdown(false);}} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:500, color:"#555", display:"flex", alignItems:"center", gap:10, fontFamily:FONT }}
                      onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      ⚙️ Configurações
                    </button>
                  )}
                  <button onClick={()=>{setShowAlterarSenha(true);setShowUserDropdown(false);}} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:500, color:"#555", display:"flex", alignItems:"center", gap:10, fontFamily:FONT }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    🔑 Alterar Senha
                  </button>
                  <button onClick={()=>{setShowSuporteModal(true);setShowUserDropdown(false);}} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:500, color:"#555", display:"flex", alignItems:"center", gap:10, fontFamily:FONT }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    🎧 Suporte
                  </button>
                  <div style={{ borderTop:"1px solid #f0f0f0" }}>
                    <button onClick={handleLogout} style={{ width:"100%", textAlign:"left", padding:"10px 16px", background:"none", border:"none", cursor:"pointer", fontSize:12, fontWeight:600, color:"#e74c3c", display:"flex", alignItems:"center", gap:10, fontFamily:FONT }}
                      onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      🚪 Sair
                    </button>
                  </div>
                </div>
              </>}
            </div>
          </div>
        </div>

        {/* Banner boleto vencido (persistente) */}
        {boletoVencido && boletoPopupConfirmed && (
          <div className="boleto-banner" style={{ background:"linear-gradient(90deg, #dc2626, #b91c1c)", padding:"8px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:16, background:"rgba(255,255,255,.2)", width:26, height:26, borderRadius:"50%", display:"inline-flex", alignItems:"center", justifyContent:"center", fontWeight:900, color:"#fff" }}>!</span>
              <span style={{ color:"#fff", fontSize:12, fontWeight:600 }}>
                Boleto vencido há {boletoVencido.diasVencido} dia(s) — R$ {parseFloat(boletoVencido.valor).toFixed(2).replace(".",",")} (Ref: {boletoVencido.referencia})
                {boletoVencido.bloqueioEm > 0 ? ` · Bloqueio em ${boletoVencido.bloqueioEm} dia(s)` : " · Acesso pode ser bloqueado a qualquer momento"}
              </span>
            </div>
            <span style={{ color:"rgba(255,255,255,.6)", fontSize:11 }}>Entre em contato com o suporte</span>
          </div>
        )}

        {/* Banner trial */}
        {empresaCfg?.tenant_status === "trial" && (() => {
          const trialRaw = empresaCfg.trial_expira ? String(empresaCfg.trial_expira).slice(0,10) : null;
          const trialExp = trialRaw ? new Date(trialRaw + "T23:59:59") : null;
          const hoje = new Date();
          const diasRestantes = trialExp ? Math.max(0, Math.ceil((trialExp - hoje) / (1000*60*60*24))) : 0;
          const expiraStr = trialExp ? trialExp.toLocaleDateString("pt-BR") : "—";
          return (
            <div style={{ background:"linear-gradient(90deg, #2563eb, #1d4ed8)", padding:"8px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:16, background:"rgba(255,255,255,.2)", width:26, height:26, borderRadius:"50%", display:"inline-flex", alignItems:"center", justifyContent:"center", fontWeight:900, color:"#fff" }}>i</span>
                <span style={{ color:"#fff", fontSize:12, fontWeight:600 }}>
                  {diasRestantes > 0
                    ? `Seu período de teste expira em ${expiraStr} (${diasRestantes} dia${diasRestantes !== 1 ? "s" : ""} restante${diasRestantes !== 1 ? "s" : ""})`
                    : "Seu período de teste expirou. Entre em contato para ativar seu plano."}
                </span>
              </div>
              <span style={{ color:"rgba(255,255,255,.6)", fontSize:11 }}>Período de avaliação</span>
            </div>
          );
        })()}

        {/* Content */}
        <div className="app-content" style={{ flex:1, overflow:"auto", padding:"24px 22px 22px 22px", background:"#f0f1f3" }}>
          <div key={page} className="page-content">
            <div style={{ marginBottom:16 }}>
              <h1 style={{ margin:0, fontSize:19, fontWeight:700, color:"#1a1a1a" }}>{current?.label}</h1>
              <p style={{ margin:"2px 0 0", fontSize:12, color:"#aaa" }}>{pageDesc[page]}</p>
            </div>
            {page === "dashboard" && !isOperacional && !isFinanceiro && <Dashboard locacoes={locacoes} clientes={clientes} cacambas={cacambas} motoristas={motoristas} frota={frota} manutencoes={manutencoes} goToLocacoes={goToLocacoes} userName={session?.nome} />}
            {page === "boasvindas" && isOperacional && (() => {
              const hora = new Date().getHours();
              const saudacao = hora < 12 ? "Bom dia" : hora < 18 ? "Boa tarde" : "Boa noite";
              const colocadas = locacoes.filter(l => l.status === "Colocada").length;
              const paraColocar = locacoes.filter(l => l.status === "Para Colocar").length;
              const paraRetirar = locacoes.filter(l => l.status === "Para Retirar").length;
              const paraTrocar = locacoes.filter(l => l.status === "Para Trocar").length;
              return (
                <div style={{ padding:"30px 0", maxWidth:700, margin:"0 auto" }}>
                  <div style={{ textAlign:"center", marginBottom:32 }}>
                    <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg,#1aae9e,#17c4b0)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:14, boxShadow:"0 4px 16px rgba(26,174,158,.3)" }}>
                      <span style={{ fontSize:28, color:"#fff" }}>{session?.avatar || "👤"}</span>
                    </div>
                    <h1 style={{ margin:"0 0 6px", fontSize:24, fontWeight:800, color:"#1a1a2e" }}>{saudacao}, {session?.nome?.split(" ")[0]}!</h1>
                    <p style={{ margin:0, fontSize:14, color:"#888" }}>Painel Operacional — {empresaNome || "Controla Caçambas"}</p>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:28 }}>
                    {[
                      { label:"Para Colocar", val:paraColocar, color:"#1a9e8f", page:"locacoes" },
                      { label:"Colocadas", val:colocadas, color:"#2ecc71", page:"locacoes" },
                      { label:"Para Retirar", val:paraRetirar, color:"#9b59b6", page:"locacoes" },
                      { label:"Para Trocar", val:paraTrocar, color:"#f39c12", page:"locacoes" },
                    ].map((c,i) => (
                      <div key={i} onClick={() => { setLocacoesInitFilter(c.label); setPage("locacoes"); }} style={{ background:"#fff", borderRadius:12, padding:"18px 20px", boxShadow:"0 2px 8px rgba(0,0,0,.06)", cursor:"pointer", borderLeft:`4px solid ${c.color}`, transition:"all .15s" }}
                        onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,.12)"} onMouseLeave={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,.06)"}>
                        <div style={{ fontSize:28, fontWeight:800, color:c.color }}>{c.val}</div>
                        <div style={{ fontSize:12, color:"#888", fontWeight:600 }}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
                    {[
                      { label:"Mapa Operacional", icon:"🗺️", key:"mapa", color:"#059669" },
                      { label:"Locações", icon:"📦", key:"locacoes", color:"#1a9e8f" },
                      { label:"Itinerários", icon:"🚛", key:"itinerarios", color:"#7c3aed" },
                    ].map((btn,i) => (
                      <button key={i} onClick={() => setPage(btn.key)} style={{ background:"#fff", border:"1.5px solid #e5e7eb", borderRadius:12, padding:"20px 14px", textAlign:"center", cursor:"pointer", fontFamily:FONT, transition:"all .15s" }}
                        onMouseEnter={e=>{e.currentTarget.style.borderColor=btn.color;e.currentTarget.style.boxShadow=`0 4px 16px ${btn.color}22`;}} onMouseLeave={e=>{e.currentTarget.style.borderColor="#e5e7eb";e.currentTarget.style.boxShadow="none";}}>
                        <div style={{ fontSize:26, marginBottom:6 }}>{btn.icon}</div>
                        <div style={{ fontSize:12, fontWeight:700, color:"#333" }}>{btn.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {page === "mapa"         && <MapaOperacional locacoes={locacoes} cacambas={cacambas} clientes={clientes} empresaNome={empresaNome} goToLocacoes={goToLocacoes} onViewLocacao={(loc) => setMapaDetailLoc(loc)} addToast={toast.add} onReloadLocacoes={loadData} />}
            {page === "locacoes"     && <Locacoes locacoes={locacoes} setLocacoes={setLocacoes} clientes={clientes} setClientes={setClientes} motoristas={motoristas} cacambas={cacambas} tiposCacamba={tiposCacamba} frota={frota} addToast={toast.add} apiFetch={apiFetch} itinerarios={itinerarios} setItinerarios={setItinerarios} initFilter={locacoesInitFilter} clearInitFilter={() => setLocacoesInitFilter(null)} empresaNome={empresaNome} />}
            {page === "clientes"     && <Clientes clientes={clientes} setClientes={setClientes} locacoes={locacoes} setLocacoes={setLocacoes} addToast={toast.add} />}
            {page === "motoristas"   && <MotoristasPage motoristas={motoristas} setMotoristas={setMotoristas} addToast={toast.add} apiFetch={apiFetch} empresaCfg={empresaCfg} />}
            {page === "tiposCacamba" && <TiposCacambaPage tiposCacamba={tiposCacamba} setTiposCacamba={setTiposCacamba} addToast={toast.add} apiFetch={apiFetch} />}
            {page === "cacambas"     && <CacambasPage cacambas={cacambas} setCacambas={setCacambas} tiposCacamba={tiposCacamba} addToast={toast.add} apiFetch={apiFetch} API={API} goToLocacoes={goToLocacoes} />}
            {page === "itinerarios"  && <Itinerarios itinerarios={itinerarios} setItinerarios={setItinerarios} locacoes={locacoes} setLocacoes={setLocacoes} motoristas={motoristas} apiFetch={apiFetch} addToast={toast.add} empresaNome={empresaNome} empresaEndereco={empresaEndereco} empresaCfg={empresaCfg} />}
            {page === "aterros"      && <AterrosPage motoristas={motoristas} frota={frota} addToast={toast.add} apiFetch={apiFetch} />}
            {page === "frota"        && <Frota frota={frota} setFrota={setFrota} motoristas={motoristas} addToast={toast.add} apiFetch={apiFetch} empresaCfg={empresaCfg} />}
            {page === "manutencao"   && <ManutencaoPage manutencoes={manutencoes} setManutencoes={setManutencoes} frota={frota} addToast={toast.add} apiFetch={apiFetch} empresaCfg={empresaCfg} />}
            {page === "combustivel"  && <CombustivelPage abastecimentos={abastecimentos} setAbastecimentos={setAbastecimentos} frota={frota} addToast={toast.add} apiFetch={apiFetch} />}
            {page === "relatorios"   && <Relatorios locacoes={locacoes} clientes={clientes} apiFetch={apiFetch} />}
            {page === "relLocacoes" && <RelatorioLocacoes locacoes={locacoes} clientes={clientes} empresaNome={empresaNome} />}
            {page === "relDetalhado" && <RelatorioDetalhado locacoes={locacoes} clientes={clientes} empresaNome={empresaNome} />}
            {page === "relMensal" && <RelatorioMensal locacoes={locacoes} clientes={clientes} empresaNome={empresaNome} />}
            {page === "relMotoristas" && <RelatorioMotoristas apiFetch={apiFetch} addToast={toast.add} motoristas={motoristas} locacoes={locacoes} empresaNome={empresaNome} setLocDetail={(id)=>{setMapaDetailLoc(locacoes.find(l=>l.id===id)||null);}} />}
            {page === "orcamentos"   && <OrcamentosPage clientes={clientes} locacoes={locacoes} tiposCacamba={tiposCacamba} addToast={toast.add} apiFetch={apiFetch} empresaCfg={empresaCfg} />}
            {page === "calculadora"  && <CalculadoraCacambas tiposCacamba={tiposCacamba} />}
            {page === "finDashboard"  && <FinDashboard apiFetch={apiFetch} addToast={toast.add} />}
            {page === "finPlanoContas" && <FinPlanoContas apiFetch={apiFetch} addToast={toast.add} />}
            {page === "finLancamentos" && <FinLancamentos apiFetch={apiFetch} addToast={toast.add} clientes={clientes} />}
            {page === "finContasPagar" && <FinContasPagar apiFetch={apiFetch} addToast={toast.add} clientes={clientes} />}
            {page === "finContasReceber" && <FinContasReceber apiFetch={apiFetch} addToast={toast.add} clientes={clientes} />}
            {page === "finComissoes" && <FinComissoes apiFetch={apiFetch} addToast={toast.add} motoristas={motoristas} empresaNome={empresaNome} empresaCfg={empresaCfg} verLocacao={(id)=>{ const l = locacoes.find(x=>String(x.id)===String(id)); if (l) setMapaDetailLoc(l); else toast.add("Locação #"+id+" não está carregada (pode ser antiga ou encerrada)", "info"); }} />}
            {page === "finRelatorios" && <FinRelatorios apiFetch={apiFetch} addToast={toast.add} />}
            {page === "ia"           && <ControlaAI apiFetch={apiFetch} addToast={toast.add} empresaCfg={empresaCfg} />}
            {page === "servicos" && modulosAtivos.includes("servicos_liquidos") && <ServicosPage clientes={clientes} motoristas={motoristas} frota={frota} addToast={toast.add} apiFetch={apiFetch} />}
            {page === "tiposServico" && modulosAtivos.includes("servicos_liquidos") && <TiposServicoPage apiFetch={apiFetch} addToast={toast.add} />}
            {page === "relServicos" && modulosAtivos.includes("servicos_liquidos") && <RelatoriosServicos apiFetch={apiFetch} addToast={toast.add} />}
            {page === "auditoria" && !isOperacional && <AuditoriaPage apiFetch={apiFetch} addToast={toast.add} />}
            {page === "configuracoes" && <ConfiguracoesPage apiFetch={apiFetch} addToast={toast.add} userRole={userRole} />}
            {page === "tutorial" && <TutorialPage />}
          </div>
        </div>
      </div>

      {novaLocModal && <NovaLocacaoModal clientes={clientes} locacoes={locacoes} motoristas={motoristas} cacambas={cacambas} tiposCacamba={tiposCacamba} apiFetch={apiFetch} onClose={()=>setNovaLocModal(false)} onSave={addLocacao} onClienteCreated={async (c)=>{
        try { const novo = await apiFetch("/api/clientes", { method:"POST", body:c }); setClientes(p=>[...p,{...novo, cpfCnpj: novo.cpf_cnpj||novo.cpfCnpj||'', criadoEm: fmtDate(novo.created_at)||''}]); return novo.id; } catch(e){ return null; }
      }} onEnderecoAdded={(cid, novoEnd) => setClientes(prev => prev.map(cli => cli.id === cid ? { ...cli, enderecos: [...(cli.enderecos||[]), novoEnd] } : cli))} />}
      {novoCliModal && <NovoClienteModal onClose={()=>setNovoCliModal(false)} onSave={async c=>{
        try { const novo = await apiFetch("/api/clientes", { method:"POST", body:c }); setClientes(p=>[...p,{...novo, cpfCnpj: novo.cpf_cnpj||novo.cpfCnpj||'', criadoEm: fmtDate(novo.created_at)||''}]); setNovoCliModal(false); toast.add("Cliente cadastrado!","success"); } catch(err){ toast.add("Erro: "+err.message,"error"); }
      }} />}
      {mapaDetailLoc && <LocacaoDetailModal locacao={mapaDetailLoc} onClose={() => setMapaDetailLoc(null)} apiFetch={apiFetch} onUpdate={updated => { setLocacoes(p => p.map(l => l.id===updated.id ? updated : l)); setMapaDetailLoc(updated); }} />}

      {/* Popup de Boa Segunda-feira */}
      {showBoaSemana && <BoaSemanaModal onClose={fecharBoaSemana} locacoes={locacoes} cacambas={cacambas} userName={session?.nome} />}
      {showNovidades && !showBoaSemana && <NovidadesModal onClose={fecharNovidades} />}

      {/* Modal Alterar Senha */}
      {showAlterarSenha && (() => {
        const AlterarSenhaModal = () => {
          const [senhaAtual, setSenhaAtual] = useState("");
          const [novaSenha, setNovaSenha] = useState("");
          const [confirmar, setConfirmar] = useState("");
          const [saving, setSaving] = useState(false);
          const [erro, setErro] = useState("");
          const [sucesso, setSucesso] = useState(false);
          const handleSave = async () => {
            setErro("");
            if (!senhaAtual) return setErro("Digite a senha atual.");
            if (novaSenha.length < 6) return setErro("Nova senha deve ter no mínimo 6 caracteres.");
            if (novaSenha !== confirmar) return setErro("As senhas não conferem.");
            setSaving(true);
            try {
              const res = await fetch(API + "/api/auth/change-password", {
                method:"POST", headers:{ "Content-Type":"application/json", Authorization:"Bearer "+getToken() },
                body: JSON.stringify({ senhaAtual, novaSenha })
              });
              const data = await res.json();
              if (!res.ok) { setErro(data.error || "Erro ao alterar senha."); setSaving(false); return; }
              setSucesso(true);
              setTimeout(() => setShowAlterarSenha(false), 1500);
            } catch(e) { setErro("Erro de conexão."); }
            setSaving(false);
          };
          const inp = { width:"100%", padding:"10px 12px", border:"1px solid #e0e0e0", borderRadius:6, fontSize:13, fontFamily:FONT, outline:"none" };
          return (
            <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setShowAlterarSenha(false)}>
              <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:10, padding:28, width:380, maxWidth:"90vw", boxShadow:"0 16px 48px rgba(0,0,0,.15)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                  <div style={{ fontSize:16, fontWeight:700, color:"#1a1a1a" }}>🔑 Alterar Senha</div>
                  <button onClick={()=>setShowAlterarSenha(false)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:"#999" }}>✕</button>
                </div>
                {sucesso ? (
                  <div style={{ textAlign:"center", padding:"20px 0" }}>
                    ✅
                    <div style={{ fontSize:14, fontWeight:600, color:"#2ecc71" }}>Senha alterada com sucesso!</div>
                  </div>
                ) : (
                  <>
                    {erro && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"8px 12px", marginBottom:14, fontSize:12, color:"#dc2626" }}>⚠️{erro}</div>}
                    <div style={{ marginBottom:12 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:4, display:"block" }}>Senha Atual</label>
                      <input type="password" value={senhaAtual} onChange={e=>setSenhaAtual(e.target.value)} style={inp} placeholder="Digite sua senha atual" />
                    </div>
                    <div style={{ marginBottom:12 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:4, display:"block" }}>Nova Senha</label>
                      <input type="password" value={novaSenha} onChange={e=>setNovaSenha(e.target.value)} style={inp} placeholder="Mínimo 6 caracteres" />
                    </div>
                    <div style={{ marginBottom:18 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:4, display:"block" }}>Confirmar Nova Senha</label>
                      <input type="password" value={confirmar} onChange={e=>setConfirmar(e.target.value)} style={inp} placeholder="Repita a nova senha" onKeyDown={e=>e.key==="Enter"&&handleSave()} />
                    </div>
                    <button onClick={handleSave} disabled={saving} style={{ width:"100%", padding:"11px", background:"#1aae9e", color:"#fff", border:"none", borderRadius:6, fontSize:13, fontWeight:700, cursor:saving?"wait":"pointer", fontFamily:FONT, opacity:saving?.7:1 }}>
                      {saving ? "Salvando..." : "Alterar Senha"}
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        };
        return <AlterarSenhaModal />;
      })()}

      {/* Modal Suporte */}
      {showSuporteModal && (() => {
        const ASSUNTOS_SUPORTE = [
          "Problema ao criar locação",
          "Erro no mapa operacional",
          "Problema com cobrança/pagamento",
          "Erro ao gerar PDF/recibo",
          "Problema com itinerário/rota",
          "Erro no login ou acesso",
          "Problema com cadastro de cliente",
          "Erro no dashboard ou relatórios",
          "Dúvida sobre funcionalidade",
          "Sugestão de melhoria",
          "Outro"
        ];
        const CRITICIDADES = [
          { key:"alta", label:"Alta", color:"#e74c3c", bg:"#fef2f2" },
          { key:"media", label:"Média", color:"#f59e0b", bg:"#fffbeb" },
          { key:"baixa", label:"Baixa", color:"#10b981", bg:"#f0fdf4" }
        ];
        const SuporteModal = () => {
          const [assunto, setAssunto] = useState("");
          const [assuntoOutro, setAssuntoOutro] = useState("");
          const [criticidade, setCriticidade] = useState("");
          const [descricao, setDescricao] = useState("");
          const [sending, setSending] = useState(false);
          const [erro, setErro] = useState("");
          const [enviado, setEnviado] = useState(false);

          const handleEnviar = async () => {
            setErro("");
            if (!assunto) return setErro("Selecione o assunto.");
            if (assunto === "Outro" && !assuntoOutro.trim()) return setErro("Descreva o assunto.");
            if (!criticidade) return setErro("Selecione a criticidade.");
            if (!descricao.trim()) return setErro("Descreva o problema.");
            setSending(true);
            try {
              const res = await fetch(API + "/api/suporte", {
                method:"POST",
                headers:{ "Content-Type":"application/json", Authorization:"Bearer "+getToken() },
                body: JSON.stringify({
                  assunto: assunto === "Outro" ? assuntoOutro.trim() : assunto,
                  criticidade,
                  descricao: descricao.trim()
                })
              });
              const data = await res.json();
              if (!res.ok) { setErro(data.error || "Erro ao enviar."); setSending(false); return; }
              setEnviado(true);
              setTimeout(() => { setShowSuporteModal(false); }, 2500);
            } catch(e) { setErro("Erro de conexão."); }
            setSending(false);
          };

          const inp = { width:"100%", padding:"10px 12px", border:"1px solid #e0e0e0", borderRadius:6, fontSize:13, fontFamily:FONT, outline:"none", boxSizing:"border-box" };

          return (
            <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,.5)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setShowSuporteModal(false)}>
              <div onClick={e=>e.stopPropagation()} style={{ background:"#fff", borderRadius:12, width:440, maxWidth:"92vw", maxHeight:"90vh", overflow:"auto", boxShadow:"0 16px 48px rgba(0,0,0,.18)" }}>
                {/* Header */}
                <div style={{ padding:"20px 24px 16px", borderBottom:"1px solid #f0f0f0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:10, background:"linear-gradient(135deg, #1aae9e, #0d9488)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🎧</div>
                    <div>
                      <div style={{ fontSize:15, fontWeight:700, color:"#1a1a1a" }}>Suporte Controla</div>
                      <div style={{ fontSize:11, color:"#999" }}>Envie sua solicitação</div>
                    </div>
                  </div>
                  <button onClick={()=>setShowSuporteModal(false)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:"#999", padding:4 }}>✕</button>
                </div>

                {enviado ? (
                  <div style={{ padding:"48px 24px", textAlign:"center" }}>
                    <style>{`@keyframes suporteCheck { 0% { transform:scale(0) rotate(-45deg); opacity:0; } 50% { transform:scale(1.2) rotate(0deg); opacity:1; } 100% { transform:scale(1) rotate(0deg); opacity:1; } } @keyframes suporteFade { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
                    <div style={{ width:72, height:72, borderRadius:"50%", background:"linear-gradient(135deg, #10b981, #059669)", margin:"0 auto 16px", display:"flex", alignItems:"center", justifyContent:"center", animation:"suporteCheck .6s ease forwards" }}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                    </div>
                    <div style={{ fontSize:18, fontWeight:700, color:"#10b981", marginBottom:6, animation:"suporteFade .5s ease .3s both" }}>Enviado com sucesso!</div>
                    <div style={{ fontSize:13, color:"#6b7280", animation:"suporteFade .5s ease .5s both" }}>Nossa equipe receberá sua solicitação em breve.</div>
                  </div>
                ) : (
                  <div style={{ padding:"20px 24px 24px" }}>
                    {erro && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"8px 12px", marginBottom:14, fontSize:12, color:"#dc2626", fontWeight:600 }}>⚠️ {erro}</div>}

                    {/* Assunto */}
                    <div style={{ marginBottom:16 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:6, display:"block" }}>Assunto</label>
                      <select value={assunto} onChange={e=>{setAssunto(e.target.value);setErro("");}} style={{ ...inp, cursor:"pointer", background:"#fff", appearance:"auto" }}>
                        <option value="">Selecione o assunto...</option>
                        {ASSUNTOS_SUPORTE.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                      {assunto === "Outro" && (
                        <input value={assuntoOutro} onChange={e=>setAssuntoOutro(e.target.value)} placeholder="Descreva o assunto..." style={{ ...inp, marginTop:8 }} />
                      )}
                    </div>

                    {/* Criticidade */}
                    <div style={{ marginBottom:16 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:8, display:"block" }}>Criticidade</label>
                      <div style={{ display:"flex", gap:8 }}>
                        {CRITICIDADES.map(c => (
                          <button key={c.key} onClick={()=>{setCriticidade(c.key);setErro("");}} style={{
                            flex:1, padding:"10px 8px", borderRadius:8, border: criticidade===c.key ? `2px solid ${c.color}` : "2px solid #e5e7eb",
                            background: criticidade===c.key ? c.bg : "#fff", cursor:"pointer", fontFamily:FONT, transition:"all .15s",
                            display:"flex", flexDirection:"column", alignItems:"center", gap:4
                          }}>
                            <div style={{ width:10, height:10, borderRadius:"50%", background:c.color }} />
                            <span style={{ fontSize:12, fontWeight: criticidade===c.key ? 700 : 500, color: criticidade===c.key ? c.color : "#666" }}>{c.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Descrição */}
                    <div style={{ marginBottom:20 }}>
                      <label style={{ fontSize:12, fontWeight:600, color:"#555", marginBottom:6, display:"block" }}>Descrição do problema</label>
                      <textarea value={descricao} onChange={e=>{setDescricao(e.target.value);setErro("");}} rows={5} placeholder="Descreva detalhadamente o problema ou dúvida..." style={{ ...inp, resize:"vertical", minHeight:100 }} />
                    </div>

                    {/* Enviar */}
                    <button onClick={handleEnviar} disabled={sending} style={{
                      width:"100%", padding:"12px", background: sending ? "#7ee8df" : "linear-gradient(135deg, #1aae9e, #0d9488)", color:"#fff", border:"none", borderRadius:8,
                      fontSize:14, fontWeight:700, cursor:sending?"wait":"pointer", fontFamily:FONT, boxShadow:"0 4px 16px rgba(26,174,158,.3)", transition:"all .2s", letterSpacing:.3
                    }}>
                      {sending ? "Enviando..." : "Enviar Solicitação"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        };
        return <SuporteModal />;
      })()}

    </div>
    </>
  );
}
